import { DiscordSDK } from '@discord/embedded-app-sdk';
import { episodeEstVu, listeApresBascule, marqueDEpisode } from './episodes-vus.js';
import { creerLecteur, phraseVariante } from './lecteur.js';
import { drapeauxDe, DRAPEAUX } from './drapeaux.js';
import { appliquerBlason, lireBlason, BLASON_DEFAUTS } from './blason.js';
import { creerSeance } from './seance.js';
import { brancherCases, brancherRetraits } from './addons-ecran.js';
import { echelleDuCadre, zoomPage, rectPage, largeurPage, hauteurPage, enPage } from './echelle.js';
// L'écran d'administration vit à part : ce fichier passe les seize mille
// lignes, et un écran entier de plus n'y serait plus relisible. Il ne connaît
// rien du lecteur — on lui donne ses outils au montage (voir plus bas).
import { monterEcranAdmin } from './admin.js';
// ⚠️ LA LOGIQUE DES POINTS EST UN MODULE À PART, ET C'EST LA SEULE FAÇON DE NE
// PAS LA RECOPIER. `src/admin.js` n'importe rien (il reçoit ses outils) ;
// `src/points.js`, lui, importe les DEUX fichiers que le bot exécute pour de
// vrai — les bornes des réglages et le calcul d'un prix. Ce qui s'affiche dans
// l'aperçu du barème est donc ce qui sera débité, par construction.
import * as reglagesDesPoints from './points.js';
import {
  arcDeLEpisode, entreesArcs, cheminDeDepart, voisinsCatalogue,
  partiesDeLArc, indexerCatalogue, chercherDansCatalogue,
} from './menu-episodes.js';
// Le catalogue d'œuvres : la fin de « une seule série ». Tout ce qui traduit le
// contrat du serveur vers la forme que ce fichier sait lire vit là-bas, hors du
// DOM et hors du réseau, donc éprouvable depuis Node (essais/oeuvres.mjs).
import {
  normaliserCatalogue, normaliserOeuvre, enSerie, dureeLisible,
  chercherOeuvres, genresDuCatalogue, anneesDuCatalogue, sousTrisAnime, trimestresDuCatalogue, decenniesDe, nouveautes, normaliserReprises,
  typeLisible,
  sousTitreDeReprise, etatDEpisode, nomCourt, estUnFilm, fenetreDeGrille, positionDeReprise,
  pastilleDeReprise, miroirADire, MIROIR_TARDE,
} from './oeuvres.js';
// Les langues de sous-titres : la table, le filtre et la traduction entre les
// codes des fichiers (« fre », « fra », « fr ») et les langues qu on choisit.
import {
  LANGUES_ST, LANGUES_PAR_DEFAUT, codesDesLangues, clesDepuisCodes, pistesRetenues,
} from './langues-st.js';
// Les sous-titres riches (ASS avec ses styles, PGS) : ce qu'on sait rendre
// ici, et les adresses à donner aux bibliothèques. Pur, éprouvé par
// essais/st-riches.mjs ; le rendu lui-même vit dans le lecteur.
import {
  formatDeRendu, capacitesDuNavigateur, urlsDePiste, urlsDesPolices,
} from './st-riches.js';
// Le vocabulaire des goûts vit hors du client : il est partagé mot pour mot
// avec le serveur, qui s'en sert pour lire les rôles Discord ET pour valider
// les réglages enregistrés sur le compte. Le fichier ne dépend de rien, ni de
// Node ni du navigateur — c'est ce qui lui permet de servir aux deux.
// ⚠️ `ORDRE_SOUSTITRES` VIENT D'ICI, ON NE LE RECOPIE PAS. C'est la table qui
// traduit un choix de sous-titres (`fr`, `en`, `aucun`) en ordre de codes
// (`fre, fra, fr`) — la même que les rôles Discord posent déjà dans
// `st-langues`. En écrire une seconde pour les préférences par type aurait
// donné deux vocabulaires pour une seule chose, et celui qui dérive se
// remarque au pire moment : quand quelqu'un regarde.
import { reglagesDuGout, CLES_DU_GOUT, ORDRE_SOUSTITRES } from '../gouts.js';
import { dateFr, anneeDe, joursAvant, pluriel } from './dates.js';
import { surveillerVersion } from './maj-auto.js';
import { monterCaisse } from './caisse.js';
import { imageDeTaille, densiteUtile } from './images-taille.js';

export { imageDeTaille, densiteUtile };

const APP_ID = document.body.dataset.appId;

// ---------------------------------------------------------------------------
// LE NOM DU PRODUIT NE S'ÉCRIT PLUS DANS LE CODE
//
// « 14ème Division » et « The Almighty » étaient écrits en dur à cinq endroits :
// le titre de l'onglet, le nom de l'activité Discord, le texte de survol de la
// présence, le repli du bandeau de vignette. C'est le nom d'UN serveur Discord,
// posé sur un lecteur qui sert maintenant à n'importe quelle communauté — et
// une présence riche est justement lue par des gens de l'extérieur.
//
// La marque vient donc de la page, qui la tient du serveur. Le repli est
// neutre : mieux vaut « Séance » que le nom de quelqu'un d'autre.
// ---------------------------------------------------------------------------
const MARQUE = document.body.dataset.marque || 'Séance';
const params = new URLSearchParams(location.search);
const OUVRIR = params.get('id') || null;
// Le retour vers une proposition de la refonte : seulement `/v/<a-d>/…`, jamais une adresse libre.
const RETOUR_VERSION = /^\/v\/[a-d]\/[\w\-/?=&%.]*$/.test(params.get('retour') ?? '') ? params.get('retour') : null;
// Le lecteur reste UN, mais chaque proposition l'habille : `html[data-version]` + sa feuille `lecteur.css`.
const VERSION = RETOUR_VERSION ? RETOUR_VERSION[3] : null;
if (VERSION) {
  document.documentElement.dataset.version = VERSION;
  document.head.append(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: `/v/${VERSION}/lecteur.css` }));
}
// ⚠️ D'OÙ VENAIT-ON DANS L'ACTIVITÉ ? (Loki 21/10, trace mviivbrq-sl1a)
// La lecture ouverte depuis l'activité arrive ici par le billet, onglet neuf :
// chemin vide, donc « arrivé par un lien de séance », donc la liste des séances
// au retour. L'activité écrit l'écran d'origine dans le FRAGMENT (`#depuis=…&
// oeuvre=…`, aucun jeton) : le fragment survit aux redirections de `/s/<code>`,
// et `marquerPosition` l'efface à la première navigation. Lu ici, avant elle.
const DEPUIS_ACTIVITE = (() => {
  try {
    const h = new URLSearchParams(location.hash.replace(/^#/, ''));
    return h.has('depuis') ? { depuis: h.get('depuis') ?? '', oeuvre: h.get('oeuvre') ?? '' } : null;
  } catch { return null; }
})();
// L'épisode que l'adresse demande d'ouvrir, quand elle en porte un. C'est ce
// que reçoit la page ouverte dans un nouvel onglet : sans lui, elle atterrirait
// sur la fiche et il faudrait recliquer l'épisode qu'on venait de lancer.
const EPISODE_VOULU = (() => {
  const m = /^(\d{1,3}):(\d{1,4})$/.exec(params.get('ep') ?? '');
  return m ? { saison: Number(m[1]), episode: Number(m[2]) } : null;
})();

// Dans l'iframe, tout passe par le proxy de Discord. Le prefixe /.proxy est
// accepte en production comme en retrocompatibilite, contrairement au chemin nu.
const DANS_DISCORD = location.hostname.endsWith('.discordsays.com');
const base = (chemin) => (DANS_DISCORD ? `/.proxy${chemin}` : chemin);
// ⚠️ DANS DISCORD, `sessionStorage` SURVIT À LA FERMETURE DE L'ACTIVITÉ : il
// appartient à la fenêtre de Discord, pas à l'iframe. Relancer l'activité
// passerait donc pour un rechargement. Ce qui les départage, c'est `frame_id` :
// Discord en donne un neuf à chaque lancement, et un rechargement garde le sien.
// Lu ici, avant tout `replaceState`.
const CADRE_DISCORD = DANS_DISCORD ? (new URLSearchParams(location.search).get('frame_id') ?? '') : '';

// ⚠️ Le constructeur du SDK exige frame_id dans l'URL et LEVE hors de Discord :
// l'instancier au chargement du module tuerait tout le script.
const sdk = DANS_DISCORD ? new DiscordSDK(APP_ID) : null;

let session = null;
// ⚠️ `serie` N'EST PLUS « LA » SÉRIE : C'EST L'ŒUVRE OUVERTE.
//
// Le nom reste — quarante lectures s'en servent, et les renommer une à une
// aurait fait quarante occasions d'en oublier une. Ce qu'il porte, en revanche,
// a changé de nature : ce n'est plus la seule série que le lecteur sache
// regarder, c'est la forme héritée (`arcs[].episodes[].season`…) de l'œuvre que
// l'on vient d'ouvrir, quelle qu'elle soit. La traduction se fait dans
// `oeuvres.js`, à un seul endroit.
let serie = null;
// L'œuvre courante telle que le SERVEUR la décrit — titre, type, genres, note,
// saisons, collections. C'est elle qu'on annonce (présence, salon) et elle qui
// peint la fiche ; `serie` n'est que sa traduction pour le vieux code.
let oeuvre = null;
// ---------------------------------------------------------------------------
// CE QUI JOUE, RAMENÉ À SON TYPE — `anime`, `serie` ou `film`.
//
// ⚠️ DÉFAUT PAYÉ : LES SOUS-TITRES NE SAVAIENT PAS CE QU'ILS ACCOMPAGNENT.
//
// `attacherPistes` choisit la piste à allumer, et elle n'a que `ref` — une
// référence de FICHIER. Elle lisait donc `st-langues`, unique pour tout le
// catalogue : régler « français toujours » pour les films, c'était l'imposer
// aux animes qu'on regarde sans rien lire. Or les deux seuls endroits qui
// lancent une lecture (`lancerFilm`, `lancer`) connaissent parfaitement
// l'œuvre : ils posent le type ici, une fois, au même moment que
// `lectureCourante`.
//
// ⚠️ ET PAS DANS `lectureCourante` : cet objet part tel quel dans le corps de
// `/api/reprendre` (`{ ...lectureCourante, position }`). Y glisser un champ de
// plus, c'est changer un contrat de route pour un besoin d'affichage.
//
// `null` tant que rien n'a été lancé : les réglages généraux s'appliquent
// alors, ce qui est exactement le comportement d'avant.
let typeEnLecture = null;
// Le catalogue entier, une fois chargé. Vide tant qu'il n'a pas répondu : on ne
// peint jamais une grille inventée, on peint une grille vide et on le dit.
let catalogue = [];
let role = null;

// Preferences du poste. localStorage peut lever selon le contexte : on ne se
// laisse jamais bloquer par un reglage de confort.
//
// Le stockage local reste la SOURCE DE LECTURE de tout le lecteur : les
// quarante endroits qui appellent `prefs.lire` n'ont pas à savoir qu'un compte
// existe, et surtout ils n'ont pas à attendre le réseau pour peindre l'écran.
// Ce qui change, c'est qu'une écriture est désormais aussi ANNONCÉE au compte
// (voir `reglagesDuCompte` juste en dessous) : c'est le seul point de passage
// commun, et l'y brancher évite d'aller ajouter un envoi derrière chaque
// curseur — l'un aurait fini par être oublié.
const prefs = {
  lire(cle, defaut) { try { return localStorage.getItem(`14d.${cle}`) ?? defaut; } catch { return defaut; } },
  ecrire(cle, val) { prefs.poser(cle, val); reglagesDuCompte.noter(cle); },
  // Écrire SANS annoncer : ce que le compte vient de nous dire ne doit pas
  // repartir vers le compte. Sans cette porte de service, chaque chargement
  // renverrait au serveur ce qu'il vient d'envoyer.
  poser(cle, val) { try { localStorage.setItem(`14d.${cle}`, val); } catch { /* sans effet */ } },
};

// ---------------------------------------------------------------------------
// ⚠️ DÉFAUT PAYÉ LE 14/09 : LE GUIDE A ÉCRIT UN MOT QUE LA CLÉ NE PARLE PAS.
//
// Les questions « Séries / Films / Animes » de l'écran d'accueil émettaient
// `vostfr` dans `langue-serie` et `langue-anime`, dont le vocabulaire est
// `auto | vo | vf` — celui de `langueVoulueDe`, celui des segments de réglages
// et celui du serveur. Résultat : la réponse ne servait à rien, puis
// `lierSegment` la remplaçait par `auto` au premier passage dans les réglages.
// Le guide est corrigé à la source (public/index.html), mais tous les comptes
// qui ont déjà répondu portent la valeur morte : on la TRADUIT, une fois, au
// lieu de la laisser se faire effacer.
//
// ⚠️ NE JAMAIS ÉTENDRE CETTE TRADUCTION À LA CLÉ `langue`. Elle, elle parle
// bien `vostfr` — c'est la valeur enregistrée dans les navigateurs de tout le
// monde, et `LANGUE_DU_GOUT` (gouts.js) la produit encore. Traduire `langue`
// ici casserait la préférence générale de tous les comptes.
// ---------------------------------------------------------------------------
const CLES_LANGUE_PAR_TYPE = ['langue-serie', 'langue-anime'];

/**
 * ⚠️ APPELÉE APRÈS `reglagesDuCompte.charger()`, ET PAS PLUS TÔT.
 *
 * À ce moment le compte vient de poser ses valeurs par-dessus le local et
 * l'envoi est armé : la correction vaut pour cet appareil ET remonte au
 * compte. Lancée au chargement du module, elle aurait corrigé un local que le
 * compte réécrivait deux secondes plus tard avec la même valeur morte.
 */
function migrerVocabulaireDesLangues() {
  for (const cle of CLES_LANGUE_PAR_TYPE) {
    if (prefs.lire(cle, '') !== 'vostfr') continue;
    // `ecrire` et non `poser` : la valeur morte est aussi dans le compte, et
    // c'est de là qu'elle redescendrait au prochain appareil ouvert.
    prefs.ecrire(cle, 'vo');
    tracer('reglages', 'vocabulaire du guide traduit', { cle, avant: 'vostfr', apres: 'vo' });
  }
}

// ---------------------------------------------------------------------------
// Les réglages suivent le compte
//
// Avant : tout vivait dans le `localStorage` de CE navigateur. Régler la
// taille des sous-titres sur le PC puis ouvrir le lecteur sur le téléphone,
// c'était tout retrouver d'origine et tout refaire — et vider son cache
// revenait à perdre ses réglages.
//
// Maintenant : le compte garde ce qui est PERSONNEL (voir `reglages.js` côté
// serveur pour la liste et pour ce qui, au contraire, reste sur l'appareil).
// Trois règles tiennent ce module :
//
//   · LE LOCAL RESTE LE CACHE ET LE REPLI. On peint avec ce qu'on a, tout de
//     suite ; le compte ne fait que corriger ensuite. Serveur muet, hors
//     ligne, session expirée : le lecteur se règle exactement comme avant.
//   · ON N'ENVOIE QUE CE QUI A CHANGÉ. Une retouche, jamais l'état complet :
//     un téléphone resté ouvert une heure ne doit pas pouvoir écraser ce qui a
//     été réglé entre-temps sur le PC.
//   · L'ENVOI NE S'ARME QU'APRÈS LA PREMIÈRE LECTURE. `appliquerGout` écrit
//     des réglages déduits des rôles Discord pendant la connexion ; s'ils
//     partaient vers le compte, ils écraseraient les choix de la personne à
//     chaque ouverture, sur chaque appareil.
// ---------------------------------------------------------------------------
const reglagesDuCompte = {
  arme: false,
  disponible: false,
  voyagent: new Set(),
  polices: [],
  catalogue: null,
  version: 0,
  // Les clés touchées depuis le dernier envoi. Un Set, pas une liste : bouger
  // un curseur écrit à chaque image, et on n'envoie la valeur qu'une fois.
  enAttente: new Set(),
  minuterie: null,

  noter(cle) {
    if (!this.arme || !this.voyagent.has(cle)) return;
    this.enAttente.add(cle);
    clearTimeout(this.minuterie);
    // Assez long pour qu'un curseur relâché ne fasse qu'un seul appel, assez
    // court pour qu'un onglet fermé dans la foulée ait déjà envoyé.
    this.minuterie = setTimeout(() => this.pousser(), 700);
  },

  async pousser() {
    if (!this.arme || this.enAttente.size === 0) return;
    const patch = {};
    for (const cle of this.enAttente) patch[cle] = prefs.lire(cle, '');
    this.enAttente.clear();
    try {
      const r = await api('/api/reglages', { method: 'PUT', body: JSON.stringify(patch) });
      this.version = r.version ?? this.version;
      this.disponible = true;
      if (r.refuses?.length) tracer('reglages', 'refusés par le serveur', r.refuses);
    } catch (err) {
      // Le réglage est déjà appliqué et déjà écrit en local : l'échec ne se
      // voit pas à l'écran, et c'est voulu. On ne rejoue pas la retouche —
      // elle repartira à la prochaine modification, et un lecteur hors ligne
      // n'a pas à empiler des envois qui n'aboutiront pas.
      this.disponible = false;
      tracer('reglages', 'envoi impossible', { erreur: err.message });
    }
  },

  /**
   * Va chercher les réglages du compte et les pose par-dessus le cache local.
   *
   * Ne lève jamais : sans compte joignable, on garde ce qu'on a. Le seul
   * effet visible est alors que rien ne suivra sur l'autre appareil, ce qui
   * est très exactement le comportement d'avant.
   */
  async charger() {
    let r = null;
    try {
      r = await api('/api/reglages');
      this.disponible = true;
    } catch (err) {
      tracer('reglages', 'compte injoignable, réglages locaux conservés', { erreur: err.message });
    }

    if (r) {
      this.catalogue = r.catalogue ?? null;
      this.voyagent = new Set(r.catalogue?.voyagent ?? []);
      this.polices = Array.isArray(r.polices) ? r.polices : [];
      this.version = r.version ?? 0;
      for (const [cle, valeur] of Object.entries(r.reglages ?? {})) prefs.poser(cle, valeur);

      // PREMIÈRE FOIS SUR CE COMPTE : on fait monter ce que ce navigateur
      // avait déjà. Sans cela, quelqu'un qui a tout réglé avant cette
      // fonctionnalité verrait son compte rester vide jusqu'à ce qu'il
      // retouche chaque curseur un par un — et le premier appareil ouvert
      // ensuite lui rendrait un lecteur d'origine.
      if (this.version === 0) this.monterLExistant();
    }

    // On arme dans tous les cas — l'envoi est une retouche clé par clé, il ne
    // peut rien écraser d'autre que ce qu'on vient de régler, donc rien à
    // craindre à essayer.
    //
    // À NOTER quand même : sans réponse du serveur, `voyagent` reste vide et
    // plus rien ne part jusqu'au prochain chargement de page. C'est assumé.
    // La liste de ce qui voyage vient du serveur, et la deviner ici en
    // recopiant le catalogue donnerait deux listes à tenir à jour — celle qui
    // dérive finirait par envoyer un réglage que l'autre refuse. Un lecteur
    // resté ouvert pendant une coupure garde donc ses réglages en local et les
    // fera monter à la prochaine ouverture, ce qui est le pire cas acceptable.
    this.arme = true;
    return r;
  },

  /** Les réglages déjà présents sur ce poste, poussés en une fois. */
  monterLExistant() {
    // Ce que la personne a décidé elle-même, et seulement cela : la marque
    // existe justement pour distinguer les deux.
    const aLaMain = new Set(prefs.lire(MARQUE_MAIN, '').split(',').filter(Boolean));
    for (const cle of this.voyagent) {
      // Une valeur posée par un rôle Discord n'est pas un choix : elle se
      // recalcule partout à chaque connexion. La faire monter reviendrait à
      // graver le rôle du jour dans le compte, et le prochain changement de
      // rôle n'aurait plus d'effet — voir `appliquerGout`.
      if (CLES_DU_GOUT.includes(cle) && !aLaMain.has(cle)) continue;
      const valeur = prefs.lire(cle, null);
      if (valeur !== null && valeur !== '') this.enAttente.add(cle);
    }
    if (this.enAttente.size === 0) return;
    // `pousser` se garde de partir tant que rien n'est armé : ici on n'est
    // pas encore armé, donc on arme le temps de l'envoi. C'est le seul
    // endroit qui a le droit de le faire.
    this.arme = true;
    this.pousser();
  },

  /**
   * Retient une police sur le compte. C'est tout ce que « récupérer la
   * police » veut dire ici : un NOM, jamais un fichier ni une adresse — voir
   * l'avertissement en tête de `reglages.js`.
   */
  async retenirPolice(nom) {
    if (!nom || this.polices.some((p) => p.toLowerCase() === nom.toLowerCase())) return;
    try {
      const r = await api('/api/reglages/polices', { method: 'POST', body: JSON.stringify({ nom }) });
      if (Array.isArray(r.polices)) this.polices = r.polices;
    } catch (err) {
      // Le choix reste utilisable ici — il est déjà dans `st-police`. Seul le
      // partage vers les autres appareils est perdu.
      tracer('reglages', 'police non retenue sur le compte', { nom, erreur: err.message });
    }
  },
};

const POLICES = {
  zen: '"Zen Kaku Gothic New", "Trebuchet MS", system-ui, sans-serif',
  system: 'system-ui, "Segoe UI", sans-serif',
  inter: '"Inter", system-ui, sans-serif',
  trebuchet: '"Trebuchet MS", system-ui, sans-serif',
  verdana: 'Verdana, Geneva, sans-serif',
  serif: '"Shippori Mincho", Georgia, serif',
  mono: 'ui-monospace, "Cascadia Mono", Consolas, monospace',
};

const CONTOURS = {
  aucun: 'none',
  leger: '0 1px 2px rgba(0,0,0,.9)',
  marque: '0 0 3px #000, 0 0 3px #000, 0 1px 2px #000',
};

// ---------------------------------------------------------------------------
// Les polices retenues sur le compte
//
// Le réglage `st-police` valait jusqu'ici une des sept clés ci-dessus. Il peut
// maintenant valoir aussi un NOM de famille retenu sur le compte. Les deux
// cohabitent parce que les sept clés sont déjà enregistrées dans le navigateur
// de tout le monde : les remplacer effacerait leur choix au premier
// chargement.
// ---------------------------------------------------------------------------

/**
 * Le nom d'une police, s'il est acceptable.
 *
 * Cette vérification EXISTE DÉJÀ SUR LE SERVEUR, et elle est refaite ici — ce
 * n'est pas de la redondance décorative. La valeur relue vient du
 * `localStorage`, que n'importe quelle console ouverte peut remplir : elle
 * finit dans une déclaration `font-family`, où un guillemet fermerait la
 * chaîne et un point-virgule ouvrirait une autre propriété.
 */
function nomDePolice(nom) {
  const s = String(nom ?? '').trim().replace(/\s+/g, ' ');
  if (s.length < 1 || s.length > 40) return null;
  return /^[A-Za-z0-9]([A-Za-z0-9 -]*[A-Za-z0-9])?$/.test(s) ? s : null;
}

/**
 * Cette police est-elle réellement disponible ICI ?
 *
 * On mesure, on ne demande pas. `document.fonts.check()` semble fait pour ça
 * mais ne répond que des règles @font-face de la page : pour une police
 * installée sur la machine, il répond « oui » quoi qu'il arrive, y compris
 * pour une famille qui n'existe nulle part. La mesure, elle, ne ment pas : on
 * écrit la même chaîne dans la police demandée puis dans trois piles
 * génériques, et une largeur qui change prouve qu'une vraie famille a répondu.
 *
 * Aucun octet ne sort du navigateur — c'est justement ce qui rend la chose
 * acceptable : reconnaître les polices de quelqu'un est une empreinte, elle ne
 * doit pas quitter son poste.
 */
const policeInstallee = (() => {
  const cache = new Map();
  const BASES = ['monospace', 'serif', 'sans-serif'];
  const TEMOIN = 'mmmmmmmmmmlliWwMm0123';
  let pinceau = null;

  return (nom) => {
    const propre = nomDePolice(nom);
    if (!propre) return false;
    if (cache.has(propre)) return cache.get(propre);

    let present = false;
    try {
      pinceau ??= document.createElement('canvas').getContext('2d');
      const largeur = (famille) => { pinceau.font = `72px ${famille}`; return pinceau.measureText(TEMOIN).width; };
      present = BASES.some((base) => largeur(base) !== largeur(`"${propre}", ${base}`));
    } catch {
      // Pas de canvas (contexte restreint, mémoire) : on ne prétend pas
      // savoir. Répondre « absente » ferait disparaître de la liste une police
      // qui marche peut-être très bien.
      present = true;
    }
    cache.set(propre, present);
    return present;
  };
})();

/** La pile CSS d'un réglage `st-police`, quel que soit sa forme. */
function pileDePolice(valeur) {
  if (POLICES[valeur]) return POLICES[valeur];
  const nom = nomDePolice(valeur);
  // Le repli n'est pas décoratif : sur l'appareil qui n'a pas la police, c'est
  // lui qui s'affiche, et il doit rester lisible en sous-titre.
  return nom ? `"${nom}", "Zen Kaku Gothic New", system-ui, sans-serif` : POLICES.zen;
}

/**
 * Remplit la liste des polices : les sept d'origine (écrites dans la page),
 * puis celles retenues sur le compte, puis celles installées sur CET appareil.
 *
 * C'est la réponse concrète à « on choisit une police et le système l'ajoute à
 * la liste » : choisir une police de l'appareil la retient sur le compte, donc
 * elle apparaît ensuite dans la liste sur le téléphone, la tablette et
 * l'ordinateur du bureau — même quand ceux-là ne l'ont pas installée, auquel
 * cas elle est marquée comme telle plutôt que de mentir sur ce qu'on verra.
 */
function garnirListeDePolices() {
  const liste = $('r-st-police');
  if (!liste) return;
  for (const groupe of [...liste.querySelectorAll('optgroup[data-ajoute]')]) groupe.remove();

  // Le catalogue vient du serveur : on ne suppose pas sa forme. Un serveur
  // revenu à une version précédente doit dégrader la liste, pas faire tomber
  // l'ouverture des réglages.
  const servies = new Set((reglagesDuCompte.catalogue?.servies ?? []).map((p) => String(p?.famille ?? '').toLowerCase()));
  const retenues = reglagesDuCompte.polices ?? [];
  const dejaLa = new Set(retenues.map((p) => p.toLowerCase()));

  const groupe = (titre, noms, decorer) => {
    if (noms.length === 0) return;
    const bloc = document.createElement('optgroup');
    bloc.label = titre;
    bloc.setAttribute('data-ajoute', '1');
    for (const nom of noms) {
      const option = document.createElement('option');
      option.value = nom;
      option.textContent = decorer(nom);
      bloc.appendChild(option);
    }
    liste.appendChild(bloc);
  };

  groupe('Retenues sur ton compte', retenues, (nom) =>
    // Une police servie par ce domaine s'affiche partout ; les autres dépendent
    // de ce que l'appareil a. Le dire ici évite le « pourquoi ça ne marche pas
    // sur mon téléphone » qui n'a aucune réponse visible.
    servies.has(nom.toLowerCase()) || policeInstallee(nom) ? nom : `${nom} — indisponible ici`);

  const surLAppareil = (reglagesDuCompte.catalogue?.candidates ?? [])
    .filter((nom) => !dejaLa.has(nom.toLowerCase()) && policeInstallee(nom));
  groupe('Sur cet appareil', surLAppareil, (nom) => nom);
}

function styleSousTitres() {
  const p = (cle, defaut) => prefs.lire(cle, defaut) || defaut;
  const epaisseur = Number(p('st-contour-epaisseur', '1.5'));
  const couleurContour = p('st-contour-couleur', '#000000');
  // Le contour est simulé par des ombres portées croisées : ::cue n'accepte
  // pas -webkit-text-stroke.
  const contour = epaisseur > 0
    ? [`-${epaisseur}px 0 ${couleurContour}`, `${epaisseur}px 0 ${couleurContour}`,
       `0 -${epaisseur}px ${couleurContour}`, `0 ${epaisseur}px ${couleurContour}`,
       `0 ${epaisseur}px ${epaisseur * 1.6}px rgba(0,0,0,.7)`].join(', ')
    : 'none';

  return {
    taille: Number(p('st-taille', '88')),
    police: pileDePolice(p('st-police', 'zen')),
    graisse: p('st-graisse', '400'),
    couleur: p('st-couleur', '#ffffff'),
    fond: `rgba(${p('st-fond-couleur', '0,0,0')}, ${Number(p('st-fond-opacite', '62')) / 100})`,
    contour,
    interligne: Number(p('st-interligne', '135')) / 100,
  };
}

function appliquerSousTitres() {
  const s = styleSousTitres();
  let regle = document.getElementById('cue-style');
  if (!regle) {
    regle = document.createElement('style');
    regle.id = 'cue-style';
    document.head.appendChild(regle);
  }
  regle.textContent = `video::cue {
    font-family: ${s.police};
    font-size: ${s.taille}%;
    font-weight: ${s.graisse};
    color: ${s.couleur};
    background: ${s.fond};
    text-shadow: ${s.contour};
    line-height: ${s.interligne};
  }`;

  const demo = document.getElementById('st-demo');
  if (demo) {
    Object.assign(demo.style, {
      fontFamily: s.police,
      fontSize: `${(s.taille / 100) * 16}px`,
      fontWeight: s.graisse,
      color: s.couleur,
      background: s.fond,
      textShadow: s.contour,
      lineHeight: String(s.interligne),
      padding: '4px 10px',
      borderRadius: '3px',
    });
  }
}

/**
 * Les deux préférences en paramètres d'URL, ou une chaîne vide s'il n'y en a
 * aucune. Écrit une fois ici parce que les deux appels à `play-now` — la
 * lecture et le préchargement de l'épisode suivant — doivent porter EXACTEMENT
 * la même chose : sinon on précharge un fichier qu'on rejettera à l'ouverture.
 */
function parametresDeGout(audio, sousTitres) {
  const q = new URLSearchParams();
  if (audio) q.set('langue', audio);
  if (sousTitres) q.set('soustitres', sousTitres);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * La langue audio voulue pour CETTE œuvre.
 *
 * ⚠️ UN ANIME ET UNE SÉRIE NE SE REGARDENT PAS DANS LA MÊME LANGUE.
 *
 * Toji : « quand je suis sur un animé ça va être du japonais, sur des séries
 * standards ça sera plutôt de l'anglais ». Le réglage unique « langue préférée »
 * obligeait à changer d'avis à la main entre deux titres — donc à ne jamais le
 * changer, et à regarder la moitié de ses séries dans la mauvaise langue.
 *
 * Le réglage du type prime quand il dit quelque chose ; « auto » retombe sur le
 * réglage général, qui retombe lui-même sur « ce que la source a de mieux ».
 * Personne n'a donc à remplir deux écrans pour que rien ne change.
 */
function langueVoulueDe(oeuvreOuType) {
  // ⚠️ LE DERNIER CHOIX EXPLICITE DE CETTE ŒUVRE PASSE AVANT TOUT RÉGLAGE.
  //
  // Toji : « je rafraîchis, ça me remet la langue de base, pas celle que j'avais définie » et « il était en
  // VO, il est passé à l'épisode suivant, ça l'a remis en VF ». Le choix fait dans le menu « Voix » ne vivait
  // que dans le lecteur, le temps d'une source : au rechargement comme à l'épisode suivant (préchargé ou non),
  // seul le réglage général parlait. Les trois chemins (épisode, film, préchargement) passent par ici.
  const choisie = choixDeLangueDe(typeof oeuvreOuType === 'object' ? oeuvreOuType?.id : null);
  if (choisie) return choisie;
  const parType = prefs.lire(cleDeLangue(oeuvreOuType), 'auto');
  if (parType === 'vo' || parType === 'vf' || /^[a-z]{2}(-[a-z]{2})?$/.test(parType)) return parType; // ou une langue précise (ja, en…)
  // « auto » : la langue du registre commun, servie avec la fiche (reglagesDe côté serveur, jamais le Manager).
  return { vf: 'vf', vostfr: 'vo' }[prefs.lire('langue', 'auto')] ?? LANGUE_DU_REGISTRE[reglagesFiche?.langue];
}
const LANGUE_DU_REGISTRE = { fr: 'vf', en: 'vo' };

// Les choix explicites, par œuvre, sur cet appareil. `poser` et non `ecrire` : ce ne sont pas des clés du compte.
function choixDeLangueDe(id) {
  const v = id == null ? '' : prefs.lire(`choix-langue.${id}`, '');
  return v === 'vf' || v === 'vo' ? v : null;
}
function retenirChoixDeLangue(id, langue) {
  if (id == null || (langue !== 'vf' && langue !== 'vo')) return;
  prefs.poser(`choix-langue.${id}`, langue);
  // L'épisode suivant déjà préparé l'a été avec l'ancienne préférence : on le refera.
  prepare = null;
}
/** Sous-titres : 'aucun' ou un code de langue ('fre', 'eng'…), ou null. */
function choixSTDe(id) {
  const v = id == null ? '' : prefs.lire(`choix-st.${id}`, '');
  return v || null;
}
function retenirChoixST(langue) {
  const id = lectureCourante?.oeuvreId;
  if (id != null && langue) prefs.poser(`choix-st.${id}`, String(langue).toLowerCase());
}
window.__retenirST = retenirChoixST;

/**
 * Le type d'une œuvre, ramené aux TROIS que les réglages distinguent.
 *
 * Tout ce qui n'est ni un anime ni un film se règle comme une série : c'est ce
 * que le lecteur faisait déjà, et inventer ici un quatrième comportement pour
 * une forme du catalogue (une collection, un arc) le rendrait invisible —
 * aucun écran ne saurait le montrer ni le corriger.
 *
 * Accepte l'œuvre entière ou son seul type, comme `langueVoulueDe` : les
 * appelants ont tantôt la fiche (`lancerFilm`), tantôt le mot (`typeEnLecture`).
 */
function typeDOeuvre(oeuvreOuType) {
  const t = typeof oeuvreOuType === 'string' ? oeuvreOuType : oeuvreOuType?.type;
  return t === 'anime' || t === 'film' ? t : 'serie';
}

/**
 * Une clé de réglage, mais SEULEMENT si le serveur la connaît.
 *
 * ⚠️ LE DÉFAUT QU'ON REFUSE DE PAYER : ÉCRIRE DANS UNE CLÉ REFUSÉE.
 *
 * `reglages.js` valide chaque clé et écarte en silence ce qu'il ne connaît pas
 * (`refuses`) : la valeur reste alors dans le seul `localStorage`, et au
 * chargement suivant `charger()` repose par-dessus ce que le COMPTE porte,
 * c'est-à-dire rien — donc `auto`. Le réglage disparaît du jour au lendemain,
 * sans trace, et personne ne peut relier ça au clic de la veille.
 *
 * Tant que la clé n'est pas dans le catalogue annoncé par le serveur, on reste
 * donc sur celle d'hier, qui marche. `voyagent` est également vide quand
 * `/api/reglages` n'a pas répondu (hors ligne, session expirée) : la retombée
 * est alors le comportement d'aujourd'hui, ce qui est le bon sens du doute.
 */
function cleServie(cle, repli) {
  return reglagesDuCompte.voyagent.has(cle) ? cle : repli;
}

/**
 * La clé qui porte la langue AUDIO de ce type d'œuvre.
 *
 * ⚠️ LES FILMS N'AVAIENT PAS DE CLÉ À EUX.
 *
 * Le guide d'accueil pose trois questions — séries, films, animes — mais il
 * n'existait que deux clés : « Films » écrivait `langue-serie`, et la dernière
 * réponse effaçait l'autre. L'écran posait une question dont il jetait la
 * réponse. Toji : « pour les animés c'est du japonais, pour les séries de
 * l'anglais, pour les films de l'anglais avec des sous-titres français tout le
 * temps » — trois habitudes, donc trois clés.
 */
function cleDeLangue(oeuvreOuType) {
  const type = typeDOeuvre(oeuvreOuType);
  if (type === 'anime') return 'langue-anime';
  // `langue-serie` n'est pas un pis-aller arbitraire : c'est LA clé que les
  // films suivaient jusqu'ici. Tant que le serveur ne sert pas `langue-film`,
  // rien ne change — ni à l'écran, ni dans le compte.
  if (type === 'film') return cleServie('langue-film', 'langue-serie');
  return 'langue-serie';
}

/**
 * Le choix de SOUS-TITRES propre à ce type d'œuvre : `fr`, `en`, `aucun` —
 * ou `null` quand il n'y en a pas, et que les réglages généraux tranchent.
 *
 * ⚠️ `auto` REND `null`, ET C'EST TOUT L'INTÉRÊT. « auto » veut dire « ce que
 * disent les réglages généraux » : le traduire ici en une liste de langues
 * figerait la valeur du jour et le curseur de l'onglet sous-titres n'aurait
 * plus d'effet sur ce type-là.
 *
 * Une valeur inconnue rend `null` elle aussi — même raison que pour `vostfr`
 * dans `langue-serie` : mieux vaut retomber sur le général que d'inventer un
 * sens à un mot qu'on ne comprend pas.
 */
function sousTitresDuType(oeuvreOuType) {
  if (oeuvreOuType == null) return null;
  const cle = cleServie(`st-${typeDOeuvre(oeuvreOuType)}`, null);
  if (!cle) return null;
  const choix = prefs.lire(cle, 'auto');
  // `Object.hasOwn` et jamais `in` : `'constructor' in ORDRE_SOUSTITRES` vaut
  // `true`, et une valeur bizarre restée dans un navigateur passerait le test
  // pour rendre ensuite une liste indéchiffrable.
  // Une langue précise (clé de LANGUES_ST, choisie par le « + » des réglages) vaut aussi.
  return Object.hasOwn(ORDRE_SOUSTITRES, choix) || codesDesLangues([choix]).length ? choix : null;
}

/**
 * L'adresse IMDb d'une oeuvre, ou `null`.
 *
 * Le catalogue n'accepte que des identifiants IMDb — « pas d'IMDb, pas de
 * flux » — donc l'adresse se deduit, elle ne se stocke pas. Le controle reste :
 * un identifiant venu d'ailleurs ne doit pas fabriquer un lien qui ne mene nulle
 * part.
 */
function lienImdb(id) {
  return /^ttd+$/.test(String(id ?? '')) ? `https://www.imdb.com/title/${id}/` : null;
}

/**
 * Ordre de préférence des langues de sous-titres.
 *
 * ⚠️ LE FRANCAIS SEUL, PAS LE FRANCAIS PUIS L'ANGLAIS.
 *
 * Toji : « de base, on va fournir que les francais. Techniquement, les anglais
 * sont dispo aussi, mais il faut aller dans les reglages et les activer. » Ce
 * defaut-ci decide de ce que le menu du lecteur MONTRE depuis que les pistes
 * sont filtrees : y laisser l'anglais reviendrait a le fournir d'office.
 *
 * ⚠️ `pour` EST LE TYPE DE CE QU'ON REGARDE, ET IL PASSE DEVANT.
 *
 * Toji : « pour les films de l'anglais avec des sous-titres français tout le
 * temps ». C'est une règle par TYPE, et `st-langues` est unique pour tout le
 * catalogue : sans ce paramètre, la satisfaire sur les films l'imposait aussi
 * aux animes. Sans `pour` — l'écran des réglages, qui règle le général — rien
 * ne change.
 */
function languesPreferees(pour = null) {
  const parType = sousTitresDuType(pour);
  const liste = parType === null ? prefs.lire('st-langues', 'fre, fra, fr')
    : Object.hasOwn(ORDRE_SOUSTITRES, parType) ? ORDRE_SOUSTITRES[parType] : codesDesLangues([parType]).join(', ');
  return liste
    .split(',')
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * « Pas de sous-titres du tout », pour ce type d'œuvre.
 *
 * Deux façons de le dire, et elles ne sont pas interchangeables : la case
 * générale `st-sans`, et le choix `aucun` d'un type. La seconde gagne quand
 * elle existe — on ne demande pas « aucun sous-titre sur les animes » pour se
 * voir répondre par un réglage qui parle de tout le catalogue.
 *
 * ⚠️ ET « aucun » N'EST PAS UNE LISTE VIDE. Une liste de langues vide fait
 * retomber `pistesRetenues` sur « on garde tout » : c'est un drapeau à part, ici
 * comme dans `gouts.js`.
 */
function sansSousTitres(pour = null) {
  const parType = sousTitresDuType(pour);
  if (parType !== null) return parType === 'aucun';
  return prefs.lire('st-sans', 'non') === 'oui';
}

/**
 * La préférence de sous-titres, dans le vocabulaire du serveur : fr, en, ou
 * « aucun ».
 *
 * Le serveur sait choisir un fichier selon les sous-titres depuis ce lot, mais
 * personne ne le lui disait : les deux appels à `play-now` ne portaient que la
 * langue audio. Deux personnes qui ne diffèrent que par les sous-titres
 * chargeaient donc le MÊME fichier, et le réglage passait pour ignoré.
 *
 * Les réglages, eux, s'expriment autrement : une liste ordonnée de codes
 * (`fre, fra, fr, eng, en`) parce qu'un fichier annonce la même langue de trois
 * façons, et un « sans sous-titres » à part — « aucun » n'est pas une langue,
 * et une liste vide ferait simplement retomber sur la première piste venue.
 * On traduit donc ici plutôt que d'imposer ce vocabulaire au serveur.
 */
function sousTitresVoulus(pour = null) {
  const memo = choixSTDe(typeof pour === 'object' ? pour?.id : null);
  if (memo === 'aucun') return 'aucun';
  if (/^(fre|fra|fr)/.test(memo ?? '')) return 'fr';
  if (/^(eng|en)/.test(memo ?? '')) return 'en';
  // ⚠️ LE CHOIX PAR TYPE PART TEL QUEL, SANS ALLER-RETOUR PAR LES CODES.
  //
  // `st-film = 'fr'` est DÉJÀ le mot que le serveur attend — c'est le
  // vocabulaire de `gouts.js`, celui des rôles Discord. Le traduire en liste de
  // codes pour le retraduire en `fr` deux lignes plus bas n'ajouterait qu'un
  // endroit où se tromper, et c'est ce qui décide quel FICHIER est servi : un
  // film choisi sans sous-titres français ne se rattrape pas côté lecteur.
  const parType = sousTitresDuType(pour);
  if (parType !== null) return parType;

  if (prefs.lire('st-sans', 'non') === 'oui') return 'aucun';
  const premiere = languesPreferees()[0] ?? '';
  if (/^(fre|fra|fr)/.test(premiere)) return 'fr';
  if (/^(eng|en)/.test(premiere)) return 'en';
  return null;
}

// Langues annoncées par les add-ons actifs. Sert aux pastilles des vignettes
// tant que le pré-scan par épisode n'a pas tourné.
let languesAddons = [];
function languesDeclarees() {
  return languesAddons;
}

const $ = (id) => document.getElementById(id);

// ⚠️ Un seul élément absent suffisait à interrompre tout le script au
// chargement : l'interface restait figée sur « Vérification de la session »,
// sans clic ni profil. Ces trois aides rendent chaque branchement indépendant
// des autres — un élément manquant n'emporte plus que sa propre ligne.
const surClic = (id, fn) => { const el = $(id); if (el) el.onclick = fn; };
const surChangement = (id, fn) => { const el = $(id); if (el) el.onchange = fn; };
const texte = (id, t) => { const el = $(id); if (el) el.textContent = t; };
const valeur = (id, v) => { const el = $(id); if (el) el.value = v; return el ? el.value : null; };
const esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);

// ---------------------------------------------------------------------------
// OU L'ON EN EST — RETENU, MAIS PAS DANS L'ADRESSE
//
// Toji : « je viens de penser à l'URL … `/#oeuvre=tt26545992&arc=0`. Ça, je
// veux que tu l'enlèves complètement. Ça doit être une gestion interne. Par
// contre, quand on reload, je veux que ça remette la page en question. »
//
// Les deux vont ensemble, et c'est ce qui rendait le hash tentant : il servait
// à la fois d'adresse et de mémoire. Mais il n'a jamais été une bonne adresse —
// `#oeuvre=tt26545992&arc=0` ne se lit pas, ne se raconte pas, et se copie dans
// un message où il ne veut plus rien dire. Et il n'a pas besoin d'être une
// adresse pour être une mémoire.
//
// `sessionStorage` fait exactement la différence voulue : il survit au
// rechargement de l'onglet, pas à sa fermeture. Recharger, c'est retrouver sa
// page ; rouvrir le site, c'est arriver là où l'administration a décidé (voir
// `accueilPar`). Le hash, lui, disparaît de la barre.
const CLE_POSITION = 'tsudoi.position';

function marquerPosition(params) {
  // ⚠️ RIEN N'EST ÉCRIT SUR UNE ADRESSE DE SÉANCE.
  //
  // Une séance n'a qu'une adresse et elle ne bouge pas — « c'est une seule URL
  // liée à la session, et tout le reste est géré en interne ».
  if (/^\/lecteur\//.test(location.pathname)) return;
  try {
    const utiles = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v != null && v !== ''));
    if (Object.keys(utiles).length) sessionStorage.setItem(CLE_POSITION, JSON.stringify(utiles));
    else sessionStorage.removeItem(CLE_POSITION);
  } catch { /* sans effet sur la navigation */ }

  // Le hash s'efface s'il en reste un : un ancien lien, ou une page ouverte
  // avant ce changement. On ne touche pas au chemin, seulement au fragment.
  try {
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
  } catch { /* sans effet sur la navigation */ }
}

function positionMemorisee() {
  try {
    const garde = JSON.parse(sessionStorage.getItem(CLE_POSITION) ?? 'null');
    if (garde && typeof garde === 'object') return garde;
  } catch { /* on tente le fragment */ }
  // Repli : un lien ouvert avec l'ancien fragment doit encore mener au bon
  // endroit. Il sera efface a la premiere navigation.
  try {
    return Object.fromEntries(new URLSearchParams(location.hash.replace(/^#/, '')));
  } catch {
    return {};
  }
}

/**
 * Sur quel écran ouvrir au démarrage — PURE, pour être éprouvée sans navigateur.
 *
 * ⚠️ TOUTE LA DIFFICULTÉ TIENT EN UNE PHRASE : « j'arrive sur le site » et « je
 * recharge cet onglet » se présentent avec la MÊME adresse `/`. L'adresse ne
 * peut donc pas les départager, et c'est en essayant de le faire avec elle que
 * ce chemin s'est trompé trois fois.
 *
 * Ce qui les départage, c'est `sessionStorage` : il survit à un rechargement et
 * meurt avec l'onglet. S'il porte un écran, c'est un rechargement — on rend cet
 * écran. Sinon c'est une arrivée — et la réponse à « rien demandé » est
 * l'accueil.
 *
 * Rend `{ ecran, raison }` : la raison part dans la trace de démarrage, parce
 * que ce chemin ne laisse aucune requête derrière lui et qu'on ne peut donc pas
 * le diagnostiquer depuis le serveur.
 */
export function ecranDeDemarrage({
  dansDiscord = false, ouvrir = null, chemin = '/',
  memoireOnglet = '', memoireDurable = '', ecransRetenus = null, existe = () => true,
} = {}) {
  const connus = ecransRetenus ?? ECRANS_RETENUS;
  const retenu = memoireOnglet || memoireDurable || '';
  // Le domaine nu mène à l'accueil — mais seulement si RIEN ne dit qu'on
  // revient. Sans `!memoireOnglet`, cette règle emportait tout : un F5 depuis
  // l'administration, la console ou la grille rendait l'accueil, puisque toutes
  // s'ouvrent sans changer l'adresse.
  if (!dansDiscord && !ouvrir && chemin === '/' && !memoireOnglet) {
    return { ecran: 'v-accueil', raison: 'domaine nu, aucune memoire d onglet : on arrive' };
  }
  // Toji (23/10, remplace le 21/10) : l'activité ouvre l'ACCUEIL réel, pas les
  // séances. La mémoire durable n'y change rien ; seul un rechargement de cet
  // onglet rend l'écran qu'on avait. Une séance en cours se rejoint toujours.
  if (dansDiscord && !memoireOnglet) {
    return { ecran: 'v-accueil', raison: 'activite lancee : l accueil' };
  }
  // `v-lecture` fait exception : l'écran seul ne sert à rien sans son fichier.
  // La reprise de séance sait quel épisode et à quelle seconde ; un simple
  // changement de vue rendrait une balise vide.
  if (retenu === 'v-lecture') {
    return { ecran: 'v-accueil', raison: 'ecran retenu v-lecture : laisse a la reprise de seance' };
  }
  if (retenu && connus.has(retenu) && existe(retenu)) {
    return {
      ecran: retenu,
      raison: memoireOnglet ? `rechargement de cet onglet : ${retenu}` : `memoire durable : ${retenu}`,
    };
  }
  return { ecran: 'v-accueil', raison: retenu ? `ecran ${retenu} inconnu ou absent de la page` : 'aucun ecran retenu' };
}

// ⚠️ RECHARGER REND L'ÉCRAN OÙ L'ON ÉTAIT, PAS L'ACCUEIL.
//
// Toji : « je suis parti dans la page de gestion, j'ai reload. Ça m'a affiché
// l'accueil du système, et après ça m'a lancé le lecteur. Ça ne m'a pas
// réaffiché la page sur laquelle j'étais. On doit pouvoir reload dans la page
// exacte de là où on était de base. »
//
// Un rechargement repartait toujours de l'accueil, puis la reprise de séance
// ouvrait le lecteur par-dessus : deux écrans de trop, et jamais le bon.
//
// Seules les vues qui sont des PAGES se retiennent. Le chargement n'en est pas
// une — c'est un passage, et y revenir au démarrage ferait attendre devant un
// écran qui n'attend rien.
// L'écran où l'on se trouve. Sert à savoir d'où l'on VIENT quand on en change :
// certains gestes n'ont de sens qu'au moment où l'on quitte un écran précis, et
// non chaque fois qu'on en peint un autre.
let ecranCourant = null;
// Vrai tant que l'activité reste sur les séances posées à son lancement
// (voir `rejoindreCeQuiEstDejaOuvert`) ; le premier autre écran l'éteint.
let seancesDArrivee = false;

const ECRANS_RETENUS = new Set([
  // Les trois écrans du catalogue d'œuvres. `v-oeuvre` remplace `v-serie` (une
  // fiche par œuvre, et non plus l'unique série) et `v-catalogue` porte
  // désormais la grille des œuvres — voir `ECRAN_PHOTOS` pour le catalogue
  // d'images, qui portait ce même identifiant avant.
  'v-accueil', 'v-oeuvre', 'v-catalogue', 'v-recherche',
  'v-serie', 'v-episodes', 'v-collection',
  'v-gestion', 'v-sessions', 'v-lecture', 'v-reglages',
  // L'administration des accès est une PAGE, pas un panneau : recharger depuis
  // elle doit la rendre, comme la console de séance ou la liste des séances.
  'v-admin',
]);

// Posé par `suivreRotation()` au démarrage, et nul partout ailleurs qu'au
// doigt. `vue()` s'en sert pour rattraper l'orientation à l'ouverture de la
// lecture : quelqu'un qui lance un épisode téléphone DÉJÀ couché ne produira
// aucune rotation, et resterait donc dans la mise en page de la page d'avant.
let verifierRotation = null;

// Posé par `basculeFinale` le temps de son fondu : annule le rangement différé
// de l'écran d'attente. Déclaré ICI, avant `vue()`, qui l'appelle.
let interrompreRangement = null;

// ⚠️ PAS DE SURVOL PENDANT QU'ON DÉFILE (Toji, 20/10 : « le scroll […] pas
// méga fluide »). MESURÉ au banc `essais/parcours/defilement.mjs` (CPU ×4) :
// sous la souris immobile, chaque carte qui passe déclenche son `:hover`
// (soulèvement + liseré, transition .18 s) — c'était la moitié de la peinture
// et la quasi-totalité du raster du catalogue. Le survol reprend 150 ms après
// le dernier cran. Écouteur unique, en capture (le `scroll` ne remonte pas),
// passif. La classe se pose sur la boîte QUI défile, pas sur `<html>` : sur la
// racine, chaque bascule recalculait le style de tout le document (MESURÉ).
{
  let fin = null;
  let boite = null;
  document.addEventListener('scroll', (e) => {
    const cible = e.target instanceof Element ? e.target : document.documentElement;
    if (cible !== boite) {
      boite?.classList.remove('defile-en-cours');
      boite = cible;
      boite.classList.add('defile-en-cours');
    }
    clearTimeout(fin);
    fin = setTimeout(() => { boite?.classList.remove('defile-en-cours'); boite = null; }, 150);
  }, { capture: true, passive: true });
}

// ⚠️ UNE ENTRÉE SE JOUE QUAND ON ARRIVE, PAS QUAND ON REPEINT.
//
// `vue()` est appelée sans arrêt, y compris pour repeindre l'écran où l'on est
// déjà, et elle relançait l'animation à chaque fois. Avec un fondu de 180 ms
// ça passait ; avec une cascade, l'écran rejouerait son entrée sous les yeux.
// L'entrée se joue donc au CHANGEMENT d'écran, et `.entre` est retirée une fois
// la cascade jouée : une rangée ou une liste d'épisodes repeinte plus tard ne
// rejoue rien. L'écran d'attente et le lecteur gardent leur classe à vie,
// comme avant — la bascule de lecture joue sur leur opacité.
const DUREE_D_ENTREE = 1200;
function entrerDans(cible) {
  cible.classList.remove('entre');
  void cible.offsetWidth; // retirer puis reposer : sans ça, l'animation ne rejoue pas
  cible.classList.add('entre');
  // La fenêtre d'entrée des écrans qui attendent leur contenu (accueil,
  // grille) : elle ne s'ouvre qu'à leur première peinture — voir `enEntree`.
  cible.dataset.entree = 'attend';
  // La place de chaque épisode dans la cascade de la fiche (plafonnée à 12).
  if (cible.id === 'v-oeuvre') {
    [...($('s-episodes')?.children ?? [])].slice(0, 36).forEach((ep, i) => {
      ep.classList.add('ent-ep');
      ep.style.setProperty('--ent-ep', String(Math.min(i, 12)));
    });
  }
  clearTimeout(cible._finEntree);
  if (cible.id !== 'v-chargement' && cible.id !== 'v-lecture') {
    cible._finEntree = setTimeout(() => {
      cible.classList.remove('entre');
      for (const ep of cible.querySelectorAll('.ent-ep')) ep.classList.remove('ent-ep');
    }, DUREE_D_ENTREE);
  }
}

/**
 * Vrai pendant la fenêtre d'entrée d'un écran qui attend son contenu : de sa
 * PREMIÈRE peinture avec du contenu jusqu'à `DUREE_D_ENTREE` plus tard.
 * L'accueil et la grille arrivent vides puis se remplissent quand le serveur
 * répond — c'est cette peinture-là qui doit entrer en cascade, pas l'écran vide.
 */
function enEntree(ecran) {
  const f = ecran?.dataset.entree;
  if (!f) return false;
  const maintenant = performance.now();
  if (f === 'attend') { ecran.dataset.entree = String(maintenant + DUREE_D_ENTREE); return true; }
  if (maintenant < Number(f)) return true;
  delete ecran.dataset.entree;
  return false;
}

function vue(id) {
  // La sonnette ne se quitte pas : l'accueil, chargé juste après le premier 403,
  // la recouvrait, et la demande d'accès devenait introuvable (cenoakuma 21/10).
  // L'autorisation recharge la page ; seule la connexion peut encore passer.
  if (ecranCourant === 'v-attente' && id !== 'v-attente' && id !== 'v-login') return;
  // Ni la connexion ni la sonnette n'ouvrent le catalogue : la recherche de
  // l'en-tête n'y mènerait qu'à un refus (matrice des menus, 21/10).
  const recherche = $('recherche-tete');
  if (recherche) recherche.hidden = !session || role === 'attente' || id === 'v-login';
  // La place tenue au démarrage (voir `[data-reserve]` dans la feuille) se rend
  // quand on SAIT qu'il n'y aura pas de recherche — pas sur le simple « pas
  // encore de session » de l'écran d'attente du démarrage.
  if (recherche && (role === 'attente' || id === 'v-login' || !recherche.hidden)) delete recherche.dataset.reserve;
  noterEtape(id);
  if (id !== 'v-sessions' && id !== 'v-chargement') seancesDArrivee = false;
  if (ECRANS_RETENUS.has(id)) {
    try { prefs.ecrire('ecran', id); } catch { /* un écran retenu n'est qu'un confort */ }
    // ⚠️ DEUX MEMOIRES, PARCE QU'IL Y A DEUX QUESTIONS.
    //
    // `localStorage` retient l'écran d'une fois sur l'autre. Mais « je recharge
    // cet onglet » et « j'arrive sur le site » ne demandent pas la même
    // réponse : le premier veut retrouver sa page, le second veut la page que
    // l'administration a choisie pour lui. `sessionStorage` fait exactement
    // cette différence — il survit au rechargement, pas à la fermeture.
    try { sessionStorage.setItem('14d.ecran-onglet', id); sessionStorage.setItem('14d.ecran-cadre', CADRE_DISCORD); } catch { /* sans effet */ }
  }

  // ⚠️ LE MOT D'ATTENTE MEURT AVEC L'ÉCRAN QUI LE PORTE.
  //
  // Il était posé à quatre endroits — « Recherche d'une source pour S17E46… »,
  // « Connexion à Discord… » — et effacé à AUCUN. Tant que l'élément n'existait
  // pas, ça ne se voyait pas ; depuis qu'il existe, le vieux message survivait
  // à l'écran de chargement et réapparaissait plus tard, sur fond noir, alors
  // que l'épisode jouait déjà. « Chargement en cours » sur une lecture en
  // route, c'est le genre de chose qui fait douter de tout le reste.
  //
  // Quitter l'écran d'attente l'efface : un état s'écrit quand il commence et
  // s'efface quand il finit, jamais seulement quand il commence.
  if (id !== 'v-chargement') {
    const mot = $('chargement-txt');
    if (mot) mot.textContent = '';
    // Quitter l'attente retire la sortie : elle n'appartient qu'à cet écran.
    const sortie = $('retour-attente');
    if (sortie) sortie.hidden = true;
  } else {
    // ⚠️ UN NOUVEL ÉCRAN D'ATTENTE N'EST PAS RANGÉ PAR LE FONDU DU PRÉCÉDENT
    // (audit 19/10). `basculeFinale` range l'écran `total + 60` ms plus tard :
    // ouvert entre-temps, le nouveau se faisait cacher en plein chargement.
    interrompreRangement?.();
    // ⚠️ LA SORTIE ET LE MOT SE POSENT ICI, PAS AILLEURS.
    //
    // Toji : « le bouton de retour est planté quand il y a le chargement, on ne
    // le voit même pas … y a pas de texte non plus. »
    //
    // Mesuré : le bouton était `hidden` précisément quand l'écran de chargement
    // était visible. On le réaffichait bien au lancement, mais `basculeFinale()`
    // — la transition de l'épisode PRÉCÉDENT — le remasquait juste après, parce
    // qu'elle passe en dernier. Deux fonctions se disputaient le même drapeau,
    // et celle qui parlait la dernière avait tort.
    //
    // Entrer dans l'écran d'attente est le seul instant où la réponse est
    // certaine : on y est, donc la sortie doit être là et le mot aussi. Le poser
    // ici met fin à la dispute — quel que soit le chemin emprunté, quelle que
    // soit la transition en cours.
    const sortie = $('retour-attente');
    if (sortie) sortie.hidden = false;
    const mot = $('chargement-txt');
    // Un écran d'attente qui n'annonce rien laisse croire qu'il ne se passe
    // rien. Ce qui suit écrira mieux dès qu'on saura quel épisode.
    if (mot && !mot.textContent) mot.textContent = 'Ouverture de la séance…';
  }
  // Le bandeau de séance ne vit que sur l'écran de lecture : ailleurs il
  // flotterait au-dessus d'une page qui n'a rien à voir.
  setTimeout(() => { try { peindreSeance(); } catch { /* séance absente */ } }, 0);
  // ⚠️ QUITTER LA LECTURE MET LE LECTEUR DE CÔTÉ. IL NE LE JETTE PLUS.
  //
  // Ces quatre lignes coupaient la source à CHAQUE sortie de l'écran de
  // lecture, quel que soit le chemin — et c'est ici que se perdait tout le
  // travail fait ailleurs : le bouton de retour avait beau mettre le lecteur de
  // côté proprement, `vue()` détruisait la source une ligne plus loin.
  //
  // Toji : « ça kick le lecteur … le lecteur est tout le temps ouvert en fond,
  // comme ça quand l'utilisateur revient, c'est instantané. »
  //
  // Le motif d'origine reste valable et reste traité : une balise EN PAUSE ne
  // joue plus de son sur la page suivante, et cesse de tirer dès son tampon
  // plein. Ce qu'on ne fait plus, c'est jeter le fichier, la position et le
  // tampon — c'est-à-dire tout ce qui rend le retour instantané.
  //
  // La pause passe par le lecteur, jamais par la balise en direct : c'est un
  // geste de navigation, et sans cette précaution il partirait à la séance,
  // mettant tout le monde en pause parce que quelqu'un a changé d'écran.
  // ⚠️ ON NE MET DE CÔTÉ QU'EN QUITTANT RÉELLEMENT LA LECTURE.
  //
  // C'était « si l'écran demandé n'est pas le lecteur ». Or `vue()` est appelée
  // sans arrêt, y compris pour repeindre l'écran où l'on est déjà : chaque appel
  // remettait donc la balise en pause. Toji : « il me dit que j'arrête pas de
  // mettre en pause et d'arrêter … le logo clignote ».
  //
  // Une pause répétée n'est pas anodine : l'état poussé chaque seconde porte
  // `lecture: !video.paused`, et il se mettait à osciller entre vrai et faux au
  // rythme des repeintures. La console recevait donc une pause, une reprise, une
  // pause — et l'affichait fidèlement, en clignotant.
  //
  // La bonne question n'est pas « où va-t-on ? » mais « d'où vient-on ? ». On ne
  // met de côté qu'au moment précis où l'on sort de l'écran de lecture, une seule
  // fois, et jamais si l'on n'y était pas.
  if (id !== 'v-lecture' && ecranCourant === 'v-lecture') {
    try { lecteur().mettreDeCote(); } catch { /* le lecteur n'existe pas encore */ }
    // Quitter l'écran est LE moment où l'on veut avoir noté : c'est souvent le
    // dernier avant que l'onglet ne se ferme. Le battement des quinze secondes
    // ne suffit pas — il aurait laissé passer jusqu'à quinze secondes de
    // lecture, et l'on reprendrait avant l'endroit où l'on s'est arrêté.
    try { noterOuOnEnEst({ force: true }); } catch { /* une note ne casse pas une navigation */ }
    // Sortir du lecteur, c'est sortir du plein écran (pas en changeant d'épisode),
    // et oublier l'épisode pour le rechargement.
    if (id !== 'v-chargement') {
      quitterPleinEcran(); oublierLectureDeLOnglet();
      // Sorti pendant la bascule : le voile `garde` survivait à la boucle
      // ci-dessous et restait posé sur l'écran d'arrivée.
      $('v-chargement')?.classList.remove('garde');
    }
    // Venu d'une des quatre propositions (`&retour=/v/x/…`) : on y retourne,
    // après la note ci-dessus (laissée partir avant la navigation).
    if (id !== 'v-chargement' && RETOUR_VERSION) { setTimeout(() => location.assign(RETOUR_VERSION), 150); }
  }
  // Le retour devant l'écran rend le jeton du lecteur (voir `horsEcran`,
  // lecteur.js) : sans lui, une source préparée en fond resterait muette.
  if (id === 'v-lecture' && ecranCourant !== 'v-lecture') {
    try { _lecteur?.reprendreDeCote(false); } catch { /* le lecteur n'existe pas encore */ }
  }
  const precedent = ecranCourant;
  ecranCourant = id;
  // Seule la lecture impose le plein écran. Le chargement le CONSERVE s'il l'a
  // déjà — lancer() le pose avant de chercher une source — mais ne se le donne
  // jamais tout seul : au démarrage, il sert aussi à vérifier la session, et la
  // barre doit alors rester en place.
  if (id === 'v-lecture') document.body.classList.add('plein');
  // Le plein cadre appartient au lecteur : ailleurs, l'en-tete doit revenir,
  // sinon on quitte la lecture sur une page sans repere ni retour.
  if (id !== 'v-lecture') document.body.classList.remove('plein-cadre');
  // ⚠️ PAS UN `else` : il rattachait ce bloc à `id === 'v-lecture'`, donc
  // `plein` (posé par lancer() pendant l'attente) ne partait JAMAIS en quittant
  // vers un autre écran. Lancement en échec → v-erreur → retour : l'en-tête
  // restait masqué (`body.plein header`) sur la fiche et l'accueil, marque
  // comprise (Toji, 22/10).
  if (id !== 'v-chargement') {
    document.body.classList.remove('plein');
    // Le blason d'attente appartient au lecteur : hors de là, il disparaît.
    $('v-chargement')?.classList.remove('lecture');
  }
  // Une vue marquée « garde » recouvre volontairement la suivante — le temps
  // d'une transition — et ne doit donc pas être masquée ici.
  for (const v of document.querySelectorAll('.view')) {
    if (!v.classList.contains('garde')) v.hidden = true;
  }
  const cible = $(id);
  if (!cible) return;
  // Lu AVANT la boucle ci-dessus n'aurait rien changé : elle vient de tout
  // masquer. On compare donc à l'écran précédent, pas à l'attribut.
  const arrive = precedent !== id;
  // Les réglages partagent leur bloc avec la pop-up : ouverts comme page juste
  // après sa fermeture, ils le reprennent sans attendre la fin du fondu.
  if (id === 'v-reglages') rendreLesReglages();
  cible.hidden = false;
  if (arrive) entrerDans(cible);

  // ⚠️ LE PREMIER VRAI ÉCRAN FERME LE DÉMARRAGE. Jusque-là `<html>` porte
  // `amorce` : le blason se révèle seul, l'en-tête attend. Au premier écran
  // qui n'est pas l'attente, l'en-tête descend à sa place (voir la feuille,
  // « LES APPARITIONS »).
  const racine = document.documentElement;
  if (id !== 'v-chargement' && racine.classList.contains('amorce')) {
    racine.classList.replace('amorce', 'entree');
    setTimeout(() => racine.classList.remove('entree'), DUREE_D_ENTREE);
  }

  // L'orientation se rattrape à l'arrivée sur le lecteur — et à la sortie, pour
  // ne pas laisser la page suivante sans en-tête si l'on quittait en plein
  // cadre. Après la bascule d'affichage : la fonction lit `hidden` pour savoir
  // si l'on regarde, et l'aurait lu à l'envers depuis le haut de `vue()`.
  // `true` : l'arrivée sur le lecteur compte comme une bascule, même sans
  // rotation. Quelqu'un qui lance un épisode téléphone DÉJÀ couché ne produira
  // aucun `orientationchange` — et resterait dans la mise en page de la page
  // d'avant, en-tête compris.
  if (verifierRotation) verifierRotation(id === 'v-lecture');
}

function erreur(titre, essais = []) {
  // Une erreur met fin a la transition, quoi qu'il arrive.
  //
  // Le blanc de la bascule n'etait leve que par la lecture. Echouer laissait
  // donc l'ecran d'erreur s'afficher PAR-DESSUS un fond blanc — ou pire, sur
  // une page entierement blanche quand l'ecran d'erreur lui-meme ne prenait
  // pas la main. On rend la main ici : c'est le seul endroit par ou passent
  // tous les echecs.
  document.body.classList.remove('bascule');
  for (const b of document.querySelectorAll('.bande-blanche')) b.remove();

  texte('e-titre', titre);
  $('e-essais').innerHTML = essais.length
    ? essais
        .map((e) => `<div>${e.ok ? '✓' : '<span class="ko">✕</span>'} ${esc(e.source)} · ${esc(e.nom)}${e.raison ? ` — <span class="ko">${esc(e.raison)}</span>` : ''} <span class="muted">${e.ms} ms</span></div>`)
        .join('')
    : '';
  $('e-essais').hidden = essais.length === 0;
  // Sans session (accès refusé), il n'y a pas d'écran où revenir.
  $('retour3').hidden = !session;
  vue('v-erreur');
}

// ---------------------------------------------------------------------------
// Trace de lecture
//
// Une lecture traverse le client, le serveur, les add-ons, le lien debrid et
// parfois ffmpeg. Quand elle échoue dans l'activité Discord, il n'y a pas de
// console pour le dire. Un identifiant commun relie donc toutes les étapes,
// des deux côtés, et la section admin les relit dans l'ordre.
// ---------------------------------------------------------------------------
let traceCourante = null;

function nouvelleTrace(quoi) {
  traceCourante = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  tracer('depart', quoi);
  return traceCourante;
}

/** Dépose une ligne côté serveur. Silencieux : tracer ne doit jamais gêner. */
function tracer(ou, message, detail) {
  if (!traceCourante) return;
  fetch(base('/api/journal'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-trace': traceCourante,
      ...(session ? { authorization: `Bearer ${session}` } : {}),
    },
    body: JSON.stringify({ ou, message: String(message).slice(0, 400), detail: detail ?? null }),
  }).catch(() => {});
}

// LES LECTURES DU DÉMARRAGE PARTENT ENSEMBLE (Toji 22/10 : chargement trop long).
// La chaîne les faisait l'une après l'autre (session → réglages → catalogue →
// accueil) quand elles ne dépendent que de la session. `amorcer` les lance dès
// qu'elle est là ; l'écran qui les demande ensuite reprend la réponse en route,
// une fois, et seulement fraîche — passé 20 s, il refait l'appel.
const amorces = new Map();
function amorcer(chemin) {
  if (!amorces.has(chemin)) {
    amorces.set(chemin, { t: Date.now(), p: api(chemin).then((v) => ({ v }), (e) => ({ e })) });
  }
}

/**
 * `delai` (ms) et `reprise` sont OPT-IN : sans eux, l'appel est exactement celui
 * d'avant (aucun délai par défaut — certaines routes attendent jusqu'à 45 s).
 * `reprise: true` rejoue UNE fois un GET après une erreur réseau ou un 502/503/504.
 */
async function api(chemin, { delai, reprise, ...options } = {}) {
  const amorce = amorces.get(chemin);
  if (amorce && delai === undefined && reprise === undefined && !Object.keys(options).length) {
    amorces.delete(chemin);
    if (Date.now() - amorce.t < 20_000) {
      const { v, e } = await amorce.p;
      if (e) throw e;
      return v;
    }
  }
  const lancer = () => fetch(base(chemin), {
    ...options,
    signal: options.signal ?? (delai ? AbortSignal.timeout(delai) : undefined),
    headers: {
      'content-type': 'application/json',
      ...(session ? { authorization: `Bearer ${session}` } : {}),
      ...(traceCourante ? { 'x-trace': traceCourante } : {}),
      ...options.headers,
    },
  });
  const rejouable = reprise === true && (options.method ?? 'GET') === 'GET';
  let r;
  try {
    r = await lancer();
    if (rejouable && [502, 503, 504].includes(r.status)) r = await lancer();
  } catch (err) {
    if (!rejouable || err?.name === 'AbortError' || err?.name === 'TimeoutError') throw err;
    r = await lancer();
  }
  const corps = await r.json().catch(() => ({}));
  if (!r.ok) {
    // ⚠️ « EN ATTENTE » N'EST PAS UNE ERREUR DE PLUS.
    //
    // Le serveur refuse TOUT a un compte connecte qui n'a pas encore ete
    // autorise, et il le dit d'une seule facon : 403 avec `attente`. L'ecouter
    // ICI plutot qu'a chaque appel evite que la personne voie passer dix
    // messages d'erreur differents avant de comprendre qu'elle attend juste
    // une autorisation.
    //
    // ⚠️ MAIS CET ECRAN ETAIT SYSTEMATIQUEMENT ECRASE JUSTE APRES.
    //
    // `ouvrirAttente()` bascule bien sur `v-attente` ICI — mais la fonction
    // leve quand meme, et TOUS les appelants font `erreur(err.message)` dans
    // leur `catch`, ce qui rebascule aussitot sur l'ecran d'erreur generique
    // (`v-erreur`) par-dessus. Le message « en attente d'autorisation » ne
    // restait donc jamais a l'ecran plus d'une fraction de seconde.
    //
    // `silencieux` est le drapeau deja utilise ailleurs (voir la connexion,
    // qui leve `{ silencieux: true }` quand elle a deja bascule sur
    // `v-login`) pour dire aux appelants « la vue a deja ete choisie, ne la
    // repeins pas ». On l'attache ici pour la meme raison, et `status` suit
    // avec — `changerLangue` juste plus bas en a besoin pour distinguer
    // « pas de source dans cette langue » d'une vraie panne.
    const enAttente = r.status === 403 && !!corps.attente;
    if (enAttente) ouvrirAttente();
    // `erreurs` : la liste de validation du registre commun. Sans elle dans le
    // message, la personne ne voyait qu'un « HTTP 400 » nu.
    const erreurs = Array.isArray(corps.erreurs) ? corps.erreurs : [];
    const base = corps.error || `HTTP ${r.status}`;
    throw Object.assign(new Error(erreurs.length ? `${base} : ${erreurs.join(' ; ')}` : base), {
      erreurs, essais: corps.essais, silencieux: enAttente, status: r.status,
      // La caisse lit son refus (`motif`, `manque`, `devis`) dans le corps.
      corps,
    });
  }
  return corps;
}

// ---------------------------------------------------------------------------
// LA SONNETTE — ce que voit quelqu'un qui n'a pas encore le droit d'entrer
//
// Trois etats, et l'ecran ne montre que celui du moment : sonner, attendre, ou
// entrer. On ne demande PAS son identifiant Discord : la connexion l'a deja
// etabli. On demande seulement le nom sous lequel on le reconnaitra dans la
// liste — et il est prerempli avec son pseudo Discord.
// ---------------------------------------------------------------------------
let attenteMontree = false;
let minuteurAttente = null;

function peindreAttente(etat) {
  // Autorise pendant qu'il attendait : on recharge, et il arrive sur l'accueil
  // comme n'importe qui. Pas de message « c'est bon », pas de bouton a cliquer.
  if (etat?.etat === 'autorise') {
    if (minuteurAttente) clearInterval(minuteurAttente);
    location.reload();
    return;
  }
  const aSonne = etat?.etat === 'demande';
  const demander = $('attente-demander');
  const patiente = $('attente-patiente');
  if (demander) demander.hidden = aSonne;
  if (patiente) patiente.hidden = !aSonne;
  if (aSonne && etat?.pseudo) texte('attente-titre', `Demande envoyée au nom de « ${etat.pseudo} ».`);

  // Une seule verification en cours a la fois, et seulement quand il y a
  // quelque chose a attendre.
  if (aSonne && !minuteurAttente) {
    minuteurAttente = setInterval(() => {
      api('/api/acces/etat').then(peindreAttente).catch(() => {});
    }, 8000);
  }
}

async function ouvrirAttente() {
  if (attenteMontree) return;
  attenteMontree = true;
  vue('v-attente');
  const champ = $('attente-pseudo');
  if (champ && !champ.value) champ.value = moi?.pseudo ?? moi?.username ?? '';
  try {
    peindreAttente(await api('/api/acces/etat'));
  } catch {
    // Meme l'etat est refuse : on laisse l'ecran tel quel plutot que de
    // renvoyer sur une page blanche. Le bouton, lui, marchera peut-etre.
  }
}

function brancherAttente() {
  surClic('attente-envoyer', async () => {
    const champ = $('attente-pseudo');
    texte('attente-erreur', 'envoi…');
    try {
      const etat = await api('/api/acces/demande', {
        method: 'POST',
        body: JSON.stringify({ pseudo: champ ? champ.value.trim() : '' }),
      });
      texte('attente-erreur', '');
      peindreAttente(etat);
    } catch (err) {
      texte('attente-erreur', err.message);
    }
  });
}

// ---------------------------------------------------------------------------
// Connexion
// ---------------------------------------------------------------------------
// L'identite du serveur Discord remplace le titre generique : icone a gauche,
// nom a cote. Silencieux si le bot n'y a pas acces.
async function appliquerIdentite() {
  // ⚠️ LA MARQUE DE L'INSTANCE N'EST PAS UN REPLI : ELLE PRIME.
  //
  // Cette fonction écrasait l'en-tête avec le nom du serveur Discord, quel que
  // soit ce que le serveur avait injecté — l'instance Tsudoi affichait donc
  // « 14ÈME DIVISION », le nom du serveur depuis lequel on l'essayait. Quand
  // l'instance porte un nom (`SEANCE_NOM`, posé dans `data-marque`), il n'y a
  // rien à compléter : on s'arrête là.
  //
  // Sans marque d'instance, le nom du serveur reste le meilleur libellé
  // disponible, et cet appel garde son rôle de filet quand le serveur n'a pas
  // pu le résoudre au démarrage (bot hors du serveur, API Discord lente).
  if (document.body.dataset.marque) return;
  const logo = $('logo-instance');
  if (logo && logo.getAttribute('src')) return;
  try {
    const b = await api('/api/branding');
    if (b.icone && logo) {
      logo.src = b.icone;
      logo.hidden = false;
      // Les deux occupent la même place : poser l'un cache l'autre.
      const embleme = $('embleme-defaut');
      if (embleme) embleme.hidden = true;
    }
    if (b.nom) texte('nom-serveur', b.nom);
  } catch { /* l'entete garde son libelle par defaut */ }
}

/**
 * Suit la disposition que Discord donne à l'activité.
 *
 * Le SDK n'offre aucun moyen de forcer le plein écran ni de redimensionner le
 * cadre — c'est Discord qui décide. Mais il prévient à chaque bascule, et
 * c'est ce signal qui manquait : sans lui, l'application garde la mise en page
 * du mode précédent, et passer en grand laisse le lecteur dans la largeur
 * qu'il occupait avant.
 */
const MODES = { 0: 'concentre', 1: 'vignette', 2: 'grille' };

/**
 * Suit la taille réelle du cadre, en continu.
 *
 * `100dvh` et `100%` se fient à ce que le navigateur annonce. Dans une iframe
 * dont l'hôte change la taille sans rien dire — une activité Discord qui
 * bascule en plein écran — cette annonce peut rester en retard d'un mode : la
 * mise en page garde alors les dimensions d'avant, et le lecteur reste dans la
 * largeur qu'il occupait.
 *
 * On mesure donc soi-même. La feuille de style s'appuie sur la mesure, jamais
 * sur la déclaration.
 */
function suivreTaille() {
  let dernier = '';

  // Dans Discord seulement : le site, dans un navigateur, garde sa taille.
  // `?echelle` l'y allume pour un aperçu (et pour l'essai).
  const aLEchelle = DANS_DISCORD || new URLSearchParams(location.search).has('echelle');
  const racine = document.documentElement;

  const poser = () => {
    // Le cadre RÉEL, en pixels d'écran : `innerWidth` ignore le `zoom`, là où
    // `clientWidth` parlerait en pixels de page et ferait boucler l'échelle sur
    // elle-même. Les deux sont égaux ici (la page ne défile pas) — relevé dans
    // le journal : « fenetre » et « mesure » toujours identiques.
    const l = innerWidth;
    const h = innerHeight;
    if (!l || !h) return;
    const e = aLEchelle ? echelleDuCadre(l, h) : 1;
    if (zoomPage() !== e) racine.style.zoom = e === 1 ? '' : String(e);
    // La page se met en page sur le cadre divisé par l'échelle. Les unités
    // `vh`/`vw` de la feuille passent par --vh1/--vw1 : sous `zoom`, le
    // navigateur les agrandirait une seconde fois.
    racine.style.setProperty('--vw', `${l / e}px`);
    racine.style.setProperty('--vh', `${h / e}px`);
    for (const [nom, v] of [['--vw1', l / e / 100], ['--vh1', h / e / 100]]) {
      if (e === 1) racine.style.removeProperty(nom);
      else racine.style.setProperty(nom, `${v}px`);
    }

    // Le mode reduit se reconnait a ses dimensions, sans attendre un evenement
    // qui n arrive pas toujours : bas ET etroit, ce n est ni un telephone ni
    // une fenetre.
    document.body.classList.toggle('mode-vignette', h <= 340 && l <= 560);

    // Une même taille annoncée deux fois n'apprend rien : on ne note que les
    // vrais changements, sinon le journal se remplit de doublons.
    const signature = `${l}x${h}`;
    if (signature !== dernier) {
      dernier = signature;
      if (!$('v-lecture')?.hidden) tracer('taille', `cadre ${signature} · échelle ${e}`);
    }
  };

  poser();
  try {
    new ResizeObserver(poser).observe(document.documentElement);
  } catch {
    // Navigateur sans ResizeObserver : les deux écouteurs suivants suffisent.
  }
  addEventListener('resize', poser);
  addEventListener('orientationchange', poser);
}

function suivreDisposition() {
  if (!DANS_DISCORD || !sdk) return;

  const appliquer = (mode) => {
    const nom = MODES[mode] ?? 'concentre';
    for (const m of Object.values(MODES)) {
      document.body.classList.toggle(`mode-${m}`, m === nom);
    }
    // Le cadre vient de changer de taille sans que rien ne l'annonce au reste
    // du code : on force la relecture des dimensions, puis on prévient tout ce
    // qui écoute déjà le redimensionnement.
    void document.body.offsetHeight;
    dispatchEvent(new Event('resize'));
    peindreVignette();
    tracer('disposition', `mode ${nom}`, {
      fenetre: [innerWidth, innerHeight],
      mesure: [document.documentElement.clientWidth, document.documentElement.clientHeight],
    });
  };

  try {
    sdk.subscribe('ACTIVITY_LAYOUT_MODE_UPDATE', ({ layout_mode }) => appliquer(layout_mode));
    sdk.subscribe('ORIENTATION_UPDATE', ({ screen_orientation }) => {
      document.body.classList.toggle('portrait', screen_orientation === 0);
      void document.body.offsetHeight;
      dispatchEvent(new Event('resize'));
      tracer('disposition', `orientation ${screen_orientation === 0 ? 'portrait' : 'paysage'}`);
    });
  } catch (err) {
    // Un abonnement refusé ne doit pas empêcher l'activité de démarrer : on
    // perd l'adaptation, pas la lecture.
    console.warn('[disposition] abonnement impossible :', err.message);
  }
}

// ---------------------------------------------------------------------------
// Le téléphone
// ---------------------------------------------------------------------------
//
// Trois comportements que la feuille de style ne peut pas porter, parce qu'ils
// répondent à des ÉVÉNEMENTS et pas à une largeur : la rotation, le plein
// écran qui va avec, et la première image d'une lecture que le navigateur
// refuse de démarrer tout seul.

/**
 * Un appareil qu'on tient dans la main, et pas une fenêtre étroite.
 *
 * `hover: none` seul dirait oui à une tablette et à un écran tactile de
 * bureau ; la plus petite dimension physique tranche. 560 px laisse passer
 * tous les téléphones (le plus large fait 440 px de côté court) et arrête les
 * tablettes, qui commencent à 768.
 */
function auDoigt() {
  try {
    return matchMedia('(hover: none) and (pointer: coarse)').matches
      && Math.min(innerWidth, innerHeight) <= 560;
  } catch {
    return false;
  }
}

const enPaysage = () => innerWidth > innerHeight;

/**
 * Tourner le téléphone met la lecture en plein écran, et le remettre droit l'en
 * sort.
 *
 * Toji : « quand la personne tourne le téléphone, tu saurais faire en sorte que
 * ça mette en plein écran automatiquement, comme ça c'est fait pour la
 * personne. »
 *
 * ⚠️ ON NE PEUT PAS APPELER `requestFullscreen()` ICI, et c'est le point dur.
 * L'API exige une activation transitoire — un appui récent. Une rotation n'en
 * est pas une : sur Android la promesse est rejetée, et sur iPhone la méthode
 * n'existe même pas sur un élément (seule la balise vidéo sait s'ouvrir, dans
 * le lecteur natif du système, ce qui ferait perdre les sous-titres tracés et
 * toute la séance).
 *
 * D'où le détour : on ACTIONNE LE BOUTON de plein écran plutôt que l'API. Le
 * lecteur a déjà tout prévu derrière ce bouton — il tente le vrai plein écran,
 * et bascule sur le « plein cadre » applicatif (l'en-tête disparaît, le lecteur
 * prend toute la surface) dès que le navigateur refuse. Ce repli-là ne demande
 * aucune permission et rend exactement ce qu'on veut sur un téléphone couché,
 * où la barre du navigateur se rétracte de toute façon. Et il garde l'icône du
 * bouton en accord avec l'état, ce qu'une bascule faite à la main ici aurait
 * cassé.
 *
 * Le bouton BASCULE : on ne l'actionne donc que si l'état n'est pas déjà celui
 * qu'on veut, sinon tourner deux fois sortirait du plein écran.
 */
function suivreRotation() {
  if (!auDoigt()) return;

  const plein = () => !!document.fullscreenElement
    || document.body.classList.contains('plein-cadre');
  const enLecture = () => !$('v-lecture')?.hidden;

  // L'invitation ne se montre qu'une fois par session : une personne qui a déjà
  // tourné son téléphone sait qu'elle peut le faire.
  let inviteVue = false;
  let minuteurInvite = 0;

  const cacherInvite = () => {
    clearTimeout(minuteurInvite);
    const n = $('l-tourner');
    if (n) n.hidden = true;
  };

  const inviter = () => {
    const n = $('l-tourner');
    if (!n || inviteVue || enPaysage() || !enLecture()) return;
    // Rien à conseiller tant que rien ne joue : quand le panneau d'amorce est
    // là, l'écran pose déjà une question, et deux panneaux qui se répondent
    // l'un l'autre ne se lisent ni l'un ni l'autre.
    if ($('l-amorce')?.hidden === false) return;
    inviteVue = true;
    n.hidden = false;
    // Elle s'efface d'elle-même : c'est un conseil, pas une consigne, et un
    // panneau qui reste finit par cacher l'image qu'il commente.
    minuteurInvite = setTimeout(() => { n.hidden = true; }, 6000);
  };

  // ⚠️ ON RÉAGIT À UNE ROTATION, PAS À UN REDIMENSIONNEMENT.
  //
  // Sur un téléphone, `resize` part sans arrêt : la barre du navigateur se
  // rétracte au défilement, le clavier s'ouvre, la barre d'onglets revient.
  // Agir à chaque fois, c'est reprendre le plein écran à quelqu'un qui vient
  // d'en sortir — on lui reposerait le plein écran au premier pixel de
  // défilement, sans qu'il puisse jamais y échapper. On ne bouge donc que
  // lorsque l'orientation CHANGE vraiment, ou quand l'écran de lecture s'ouvre
  // (`force`), cas où il n'y a par définition aucune rotation à observer.
  let derniere = null;

  const suivre = (force = false) => {
    const sens = enPaysage() ? 'paysage' : 'portrait';
    const bascule = force || (derniere !== null && sens !== derniere);
    derniere = sens;
    if (!enLecture()) {
      cacherInvite();
      // Le « plein cadre » applicatif est déjà retiré par `vue()` pour toute
      // vue autre que la lecture. Le VRAI plein écran, lui, ne l'est pas : on
      // se retrouvait sur le catalogue dans un plein écran système dont plus
      // rien n'expliquait la sortie. On ne passe pas par le bouton ici — il
      // bascule, et l'état qu'il lit vient d'être remis à zéro sous lui.
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      return;
    }
    if (sens === 'paysage') {
      cacherInvite();
      if (bascule && !plein()) $('l-plein')?.click();
      return;
    }
    // Retour à la verticale : on rend la page à sa forme normale, sinon on
    // reste enfermé dans un lecteur sans en-tête sur un écran haut et étroit.
    if (bascule && plein()) $('l-plein')?.click();
    inviter();
  };

  // `orientationchange` part parfois AVANT que les dimensions ne soient à jour :
  // `resize` ferme la marche, et le double appel ne coûte rien puisque `suivre`
  // ne fait rien quand l'état est déjà le bon.
  addEventListener('orientationchange', () => setTimeout(suivre, 120));
  // ⚠️ `addEventListener('resize', suivre)` — SANS l'enveloppe — passe l'ÉVÉNEMENT
  // en premier argument, donc un objet toujours vrai dans `force`. Chaque
  // redimensionnement devenait alors une bascule forcée, et le garde ci-dessus
  // ne servait plus à rien : mesuré, quelqu'un qui sortait du plein écran en
  // paysage se le voyait reposer au redimensionnement suivant — c'est-à-dire au
  // premier pixel de défilement, la barre du navigateur se rétractant.
  addEventListener('resize', () => suivre());
  try {
    screen.orientation?.addEventListener('change', () => setTimeout(suivre, 120));
  } catch { /* API absente : les deux écouteurs ci-dessus suffisent */ }

  // Et au moment où la lecture s'ouvre, sans attendre une rotation : quelqu'un
  // qui lance un épisode téléphone déjà couché doit passer en plein écran tout
  // de suite.
  return suivre;
}

/**
 * Débloque la première image sur un téléphone.
 *
 * ⚠️ C'EST L'ÉCRAN NOIR SIGNALÉ, ET IL N'A RIEN À VOIR AVEC LA SOURCE.
 *
 * Mesuré (Chrome, politique `document-user-activation-required`, celle de tout
 * appareil réel) : la source est complètement chargée — `readyState` vaut 4,
 * une image est décodable — et pourtant `video.play()` est rejeté avec
 * `NotAllowedError`. La balise reste à `currentTime = 0`, en pause, et n'a
 * jamais peint la moindre image. D'où le rectangle noir.
 *
 * Le lecteur traite bien ce cas, mais il le traite par une PHRASE : « Touche
 * l'écran pour lancer la lecture », en 12 px, en haut à droite, dans un bandeau
 * qui s'efface au bout de trois secondes avec le reste des commandes. Et
 * l'appui qu'elle réclame est intercepté, quand les commandes sont masquées,
 * pour les révéler : il en faut deux, à l'aveugle, sur un écran noir.
 *
 * Le repli en sourdine a été vérifié dans la même mesure : `muted = true` puis
 * `play()` démarre, l'image apparaît. On l'offre après l'appui, pas à la place,
 * parce qu'une salle qui regarde ensemble n'a aucune raison de se retrouver
 * sans son sans l'avoir demandé.
 */
function debloquerAmorce() {
  if (!auDoigt()) return;
  const video = $('video');
  const panneau = $('l-amorce');
  const rappel = $('l-son-coupe');
  if (!video || !panneau) return;

  // A-t-on déjà vu cette source jouer ? Tant que non, une pause à zéro n'est
  // pas un choix : c'est un blocage.
  let aJoue = false;
  video.addEventListener('playing', () => { aJoue = true; panneau.hidden = true; });
  // Une nouvelle source remet le compteur : changer d'épisode redonne lieu au
  // même blocage, et le panneau doit pouvoir revenir.
  for (const ev of ['loadstart', 'emptied']) {
    video.addEventListener(ev, () => { aJoue = false; });
  }

  const lancer = () => {
    panneau.hidden = true;
    video.play()
      .catch(() => {
        // Refusé même avec l'appui : il ne reste que la sourdine, qui elle
        // n'est jamais bloquée. L'image part, et le son se rétablit d'un
        // second appui — sur un bouton qui, lui, se voit.
        //
        // ⚠️ CE DRAPEAU EMPÊCHE DE RETENIR UNE COUPURE QU'ON N'A PAS
        // CHOISIE. Sans lui, ce `muted` partait dans le réglage de son avec
        // le `volumechange` qui suit, et qui ne cliquait pas ensuite sur
        // « rétablir le son » revenait muet à toutes ses séances suivantes
        // (voir `sonARetenir`, dans `lecteur.js`).
        video.dataset.sourdineTechnique = '1';
        video.muted = true;
        return video.play().then(() => { if (rappel) rappel.hidden = false; });
      })
      .catch(() => { panneau.hidden = false; });
  };

  panneau.addEventListener('click', lancer);
  if (rappel) {
    rappel.addEventListener('click', () => {
      // Le geste est explicite : a partir d'ici, le son redevient un choix,
      // et se retient comme tel.
      delete video.dataset.sourdineTechnique;
      video.muted = false;
      rappel.hidden = true;
    });
  }

  // Un sondage plutôt qu'un événement : le refus d'`amorcer()` est traité dans
  // le lecteur, qui ne prévient personne. Deux fois par seconde suffisent, et
  // la boucle ne coûte rien tant qu'on n'est pas sur l'écran de lecture.
  setInterval(() => {
    const surLecture = !$('v-lecture')?.hidden;
    if (!surLecture) { panneau.hidden = true; return; }
    // `readyState >= 2` : la balise tient au moins l'image courante. En dessous
    // elle charge encore, et c'est le blason d'attente qui parle.
    const bloque = !aJoue && video.paused && video.currentTime === 0
      && video.readyState >= 2 && !!video.currentSrc;
    if (bloque) panneau.hidden = false;
    else if (!bloque && aJoue) panneau.hidden = true;
  }, 500);
}

// Origine SSO (pop-up Discord), donnee par /api/auth/me ; vide = redirection pleine page.
let ssoOrigine = '';
function connexionDiscord() {
  const pleinePage = () => { location.href = '/api/auth/login'; };
  if (!ssoOrigine) return pleinePage();
  // Pop-up bloquee : on retombe sur la redirection pleine page.
  if (!window.open(`${ssoOrigine}/api/auth/login?popup=1`, 'discord', 'width=500,height=750')) pleinePage();
}
// ponytail: rechargement complet apres connexion, le demarrage n'est pas rejouable a part.
addEventListener('message', (e) => {
  if (ssoOrigine && e.origin === ssoOrigine && e.data?.type === 'discord-connecte') location.reload();
});

async function connecter() {
  if (!DANS_DISCORD) {
    // Pas api() : elle ajoute `authorization` et ouvre l'écran d'attente, hors de propos avant la connexion.
    // La page l'a demandée dès son en-tête (index.html) : on reprend cette réponse, une fois.
    const etat = await (window.__authMe ?? fetch('/api/auth/me').then((r) => (r.ok ? r.json() : null)).catch(() => null))
      ?? { authenticated: false };
    window.__authMe = null;
    if (!etat.authenticated) {
      ssoOrigine = etat.sso || '';
      // Le lien de rejoint n a de sens que s il a ete configure : un bouton qui
      // renvoie vers rien vaut moins que pas de bouton.
      const rejoindre = $('acces-rejoindre');
      if (rejoindre) rejoindre.hidden = !/^https?:/.test(rejoindre.getAttribute('href') || '');
      // L'icône du serveur est écrite dans la page par le serveur : cet écran
      // est celui d'AVANT la connexion, et toute route protégée y échouerait.
      vue('v-login');
      throw { silencieux: true };
    }
    session = etat.session;
    role = etat.role;
    appliquerGout(etat.gout);
    poserProfil(etat.username, etat.username, null);
    return;
  }

  // sdk.ready() ne se resout jamais si le mapping d'URL est absent : sans borne,
  // l'ecran resterait en chargement sans le moindre message.
  await Promise.race([
    sdk.ready(),
    new Promise((_, rej) => setTimeout(() => rej(new Error("Discord n'a pas répondu. Vérifie le mapping d'URL de l'activité.")), 12_000)),
  ]);

  // `rpc.activities.write` s'ajoute à `identify` : c'est lui, et lui seul, qui
  // autorise `setActivity` — donc toute la présence riche. Discord le documente
  // avec `prompt: 'none'` pour une activité, mais un refus ici emporterait la
  // connexion entière : on retombe alors sur `identify` seul et l'on se prive
  // de la présence, pas du lecteur.
  const demandeAcces = (scope) => sdk.commands.authorize({
    client_id: APP_ID, response_type: 'code', state: '', prompt: 'none', scope,
  });
  let code;
  try {
    ({ code } = await demandeAcces(['identify', 'rpc.activities.write']));
  } catch (err) {
    presenceAutorisee = false;
    console.warn('[presence] rpc.activities.write refusé :', err?.message ?? err);
    ({ code } = await demandeAcces(['identify']));
  }
  const r = await fetch(base('/api/token'), {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code, guildId: sdk?.guildId }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'accès refusé');
  await sdk.commands.authenticate({ access_token: data.access_token });
  suivreDisposition();
  session = data.session;
  role = data.role;
  appliquerGout(data.gout);
  poserProfil(data.user.global_name ?? data.user.username, data.user.username, data.user.avatar
    ? `https://cdn.discordapp.com/avatars/${data.user.id}/${data.user.avatar}.png?size=64`
    : null);
}

const CLE_PROFIL = '14d:profil';

/** Le dernier profil affiche, relu tel quel au chargement suivant. */
function profilRetenu() {
  try {
    const brut = localStorage.getItem(CLE_PROFIL);
    return brut ? JSON.parse(brut) : null;
  } catch {
    return null; // navigation privee, stockage refuse : ce n'est pas une erreur
  }
}

function retenirProfil(p) {
  try {
    localStorage.setItem(CLE_PROFIL, JSON.stringify(p));
  } catch { /* rien a retenir, l'affichage reste correct */ }
}

/**
 * Peint le dernier profil connu AVANT toute attente reseau.
 *
 * L'en-tete restait vide le temps de l'authentification — pastille grise, pas
 * de pseudo, pas de rang — puis tout apparaissait d'un coup. Les informations
 * etaient pourtant deja la, en memoire locale : il suffisait de s'en servir
 * tout de suite. Le reseau ne fait plus que confirmer, et ne repeint que ce
 * qui a change.
 */
function peindreProfilRetenu() {
  const retenu = profilRetenu();
  if (!retenu) return;
  moi = retenu;
  rafraichirProfil();
}

function poserProfil(pseudo, identifiant, avatar) {
  // Ce qui est deja connu est pose tout de suite : le bloc ne part jamais vide,
  // donc il ne se remplit pas sous les yeux a chaque rechargement.
  const retenu = profilRetenu();
  moi = retenu && retenu.username === identifiant
    ? { ...retenu, pseudo: pseudo ?? retenu.pseudo, avatar: avatar ?? retenu.avatar }
    : { pseudo, username: identifiant, avatar, rang: null };
  rafraichirProfil();
  retenirProfil(moi);

  // Le rang et l'avatar de serveur ne sont connus que du backend.
  api('/api/moi')
    .then((d) => {
      // `rangPosition` est la HAUTEUR du rôle dans la hiérarchie du serveur,
      // pas son nom : c'est elle, et elle seule, qui permet à la console de
      // ranger les participants comme Discord les range. Le client ne peut pas
      // la déduire d'un nom de rôle.
      // ⚠️ CE QUI N'EST PAS RECOPIE ICI EST PERDU.
      //
      // Cette ligne reconstruit `moi` champ par champ. Tout ce que le serveur
      // rend et qui n'y figure pas disparait — silencieusement, et pour toute
      // la session. C'est ce qui est arrive a `categorie` et a `droits` : le
      // bouton « Reglages » restait invisible pour le PROPRIETAIRE lui-meme,
      // puisque la page ne savait plus qu'il l'etait. Rien ne le signalait :
      // `moi.droits?.sources` sur `undefined` ne leve pas, il repond « non ».
      const frais = {
        ...moi,
        id: d.id ?? moi.id ?? null,
        pseudo: d.pseudo ?? moi.pseudo,
        rang: d.rang,
        rangCouleur: d.rangCouleur ?? null,
        rangPosition: d.rangPosition ?? null,
        avatar: d.avatar ?? moi.avatar,
        // Qui est cette personne pour le service, et ce qu'elle a le droit de
        // faire : deux reponses qui ne se devinent pas cote client.
        categorie: d.categorie ?? moi.categorie ?? null,
        accueilPar: d.accueilPar ?? moi.accueilPar ?? null,
        droits: d.droits ?? moi.droits ?? null,
      };
      // Ne repeindre que si quelque chose a reellement change : un rendu
      // identique suffit a faire clignoter l'image.
      if (JSON.stringify(frais) !== JSON.stringify(moi)) {
        moi = frais;
        rafraichirProfil();
        // La fiche a peut-etre ete peinte AVANT cette reponse : le bouton des
        // reglages depend des droits, et il resterait cache jusqu'a la
        // prochaine ouverture. On le remet a jour ici, ou l'on vient justement
        // d'apprendre ce qu'il faut savoir.
        try { if (oeuvre && !$(ECRAN_OEUVRE())?.hidden) peindreFicheOeuvre(); } catch { /* pas de fiche ouverte */ }
      }
      retenirProfil(frais);
      poserBoutonsVersions(frais);
    })
    .catch(() => {});
}

/**
 * Les quatre propositions de la refonte (Toji, 23/10) : quatre boutons dans la
 * barre du haut, pour les admins seulement. Le serveur refuse `/v/*` aux autres
 * (`versionAdmin`) : ces boutons ne sont qu'un raccourci.
 */
function poserBoutonsVersions(qui) {
  if (!(qui?.categorie === 'proprietaire' || qui?.droits?.admin || qui?.testeurVersions) || $('st-versions')) return;
  const nav = document.createElement('nav');
  nav.id = 'st-versions';
  nav.className = 'st-versions';
  nav.setAttribute('aria-label', 'Propositions de la refonte');
  nav.innerHTML = ['a', 'b', 'c', 'd']
    .map((l) => `<a href="/v/${l}/" title="Proposition ${l.toUpperCase()}">${l.toUpperCase()}</a>`).join('');
  document.querySelector('header .tete-droite')?.prepend(nav);
}

// Les trois planches de l'accueil. Leur visuel vient du catalogue : on résout
// l'affiche de la référence associée plutôt que d'embarquer une image.
const PLANCHES = {
  films: { libelle: 'Films' },
  anime: { libelle: 'Anime' },
  live: { libelle: 'Live action' },
};

let collections = (typeof window !== 'undefined' && window.__COLLECTIONS__) || {};
let descriptions = {};
let collectionCourante = null;

// Anime : on entre dans la série, triée par arc. Films et live action : la
// collection, quel que soit le nombre de titres.
function ouvrirPlanche(cle) {
  const fiches = collections[cle] ?? [];
  if (!fiches.length) return;
  if (cle === 'anime') return ouvrirSerie(fiches[0].id);
  return ouvrirCollection(cle, PLANCHES[cle]?.libelle ?? cle);
}

function detailPlanche(cle, fiches) {
  if (!fiches.length) return '';
  if (cle === 'anime') {
    const s = collections.anime?.[0];
    const bouts = [];
    if (s?.arcs) bouts.push(`${s.arcs} arcs`);
    if (s?.episodes) bouts.push(`${s.episodes} épisodes`);
    return bouts.join(' · ') || 'série complète';
  }
  return `${fiches.length} ${pluriel(fiches.length, 'film')}`;
}

function preparerPlanches() {
  // Les collections sont déjà dans la page. L'appel réseau ne sert que de
  // rattrapage si le service n'avait rien résolu à son démarrage.
  api('/api/collections')
    .then((d) => {
      descriptions = d.descriptions ?? {};
      if (!Object.keys(collections).length && d.collections) {
        collections = d.collections;
        preparerPlanches();
      }
    })
    .catch(() => {});

  for (const [cle, def] of Object.entries(PLANCHES)) {
    const carte = document.querySelector(`.planche[data-cat="${cle}"]`);
    if (!carte) continue;

    const fiches = collections[cle] ?? [];
    // ⚠️ Un enfant absent ne doit pas emporter le démarrage. `preparerPlanches`
    // est appelée DANS la chaîne de `connecter()` : une exception ici rejette la
    // chaîne entière, et l'on affiche l'écran d'erreur au lieu du lecteur. Le
    // balisage de l'accueil est justement en train d'être réécrit.
    const det = carte.querySelector('.det');
    if (det) det.textContent = detailPlanche(cle, fiches);


    // Le visuel tourne quand la collection en compte plusieurs.
    const visuels = (window.__VISUELS__ ?? {})[cle] ?? [];
    const img = carte.querySelector('.planche-img img');
    if (visuels.length > 1 && img) faireTourner(img, visuels);
  }
}

// Glissement latéral : le ruban avance d'une affiche à chaque pas, du premier
// au dernier, puis revient au début. Une seule propriété animée, `transform`,
// donc le mouvement reste fluide.
function faireTourner(img, visuels) {
  const cadre = img.parentElement;
  if (!cadre || visuels.length < 2) return;

  const ruban = document.createElement('span');
  ruban.className = 'ruban';
  ruban.innerHTML = visuels
    .map((u) => `<span><img src="${esc(u)}" alt="" /></span>`)
    .join('');
  img.remove();
  cadre.appendChild(ruban);

  let i = 0;
  let sens = 1;
  setInterval(() => {
    // Aller au dernier, puis revenir : pas de saut sec en fin de course.
    if (i + sens >= visuels.length || i + sens < 0) sens = -sens;
    i += sens;
    ruban.style.transform = `translateX(-${i * 100}%)`;
  }, 5200);
}

function ouvrirTitre(fiche) {
  if (fiche.type === 'movie') return lancerFilm(fiche);
  return ouvrirSerie(fiche.id);
}

function ouvrirCollection(cle, libelle) {
  const fiches = collections[cle] ?? [];
  collectionCourante = cle;

  texte('col-titre', libelle);

  // L'affiche du bloc : celle du titre le plus récent, la plus parlante pour
  // représenter l'ensemble.
  const vedette = [...fiches].sort((a, b) => String(b.annee ?? '').localeCompare(String(a.annee ?? '')))[0];
  const img = $('col-img');
  if (img) img.src = vedette?.affiche ?? vedette?.fond ?? '';

  // ⚠️ UN NŒUD ABSENT NE DOIT PAS EMPORTER TOUTE LA VUE.
  //
  // La ligne au-dessus se gardait (`if (img)`), les quatre suivantes non — et
  // c'est ce qui cassait : `#col-meta` avait disparu du balisage, donc
  // `$('col-meta').innerHTML` levait « Cannot set properties of null » AVANT
  // que la rangée de films ne soit peinte. « Films » et « Live action » ne
  // s'ouvraient donc PAS DU TOUT, sur téléphone comme sur ordinateur, et rien
  // ne le disait à l'écran : la vue restait simplement vide.
  //
  // Une vue qui décore doit survivre à la perte de sa décoration. Ce qui compte
  // ici, c'est la liste des films ; le compte, les années et le résumé sont des
  // agréments, et leur absence ne vaut pas un écran noir.
  const annees = fiches.map((f) => String(f.annee ?? '').slice(0, 4)).filter(Boolean).sort();
  const meta = $('col-meta');
  if (meta) {
    meta.innerHTML = [
      `${fiches.length} ${pluriel(fiches.length, 'film')}`,
      ...(annees.length > 1 ? [`${annees[0]} – ${annees[annees.length - 1]}`] : annees),
    ]
      .map((t) => `<span class="tag">${esc(t)}</span>`)
      .join('');
  }
  const desc = $('col-desc');
  if (desc) {
    desc.textContent = descriptions[cle] ?? fiches.find((f) => f.resume)?.resume ?? '';
  }
  const editer = $('col-editer');
  if (editer) editer.hidden = role !== 'owner';

  // La rangée, elle, EST la vue : sans elle il n'y a rien à montrer. On se garde
  // quand même, pour échouer sur une page vide plutôt que sur une exception qui
  // emporte le reste de l'application.
  const rangee = $('col-rangee');
  if (!rangee) {
    tracer('collection', 'rangee absente du balisage', cle);
    vue('v-collection');
    return;
  }

  rangee.innerHTML = fiches
    .map(
      (f, i) => `<button class="film" type="button" data-i="${i}">
        <span class="vis">${f.affiche ? `<img src="${esc(f.affiche)}" alt="" />` : ''}</span>
        <span class="nom">${esc(f.nom)}</span>
        <span class="an">${esc(f.annee ?? '')}</span>
      </button>`,
    )
    .join('');

  for (const el of rangee.querySelectorAll('.film')) {
    el.onclick = () => lancerFilm(fiches[Number(el.dataset.i)]);
  }
  vue('v-collection');
}

// La description se modifie sur place : pas de formulaire séparé pour un champ.
function basculerEdition() {
  const zone = $('col-desc');
  const enEdition = zone.getAttribute('contenteditable') === 'true';

  if (!enEdition) {
    zone.setAttribute('contenteditable', 'true');
    zone.focus();
    $('col-editer').textContent = 'Enregistrer';
    return;
  }

  zone.removeAttribute('contenteditable');
  $('col-editer').textContent = 'Modifier la description';
  const texte = zone.textContent.trim();
  descriptions[collectionCourante] = texte;
  api(`/api/collections/${encodeURIComponent(collectionCourante)}/description`, {
    method: 'PUT',
    body: JSON.stringify({ texte }),
  }).catch(() => { $('col-editer').textContent = "Échec de l'enregistrement"; });
}

// Un film n'a pas d'épisodes// Un film n'a pas d'épisodes : on va droit à la résolution.
/**
 * @param {number} reprise seconde où reprendre. Vient de « Reprendre » sur
 *   l'accueil, comme pour `lancer()` — voir `reprendreLecture`.
 * @param {boolean} relance appel interne, déclenché par `video.onerror`
 *   ci-dessous pour retenter UNE fois. Jamais posé par un appelant.
 */
// ---------------------------------------------------------------------------
// Une source qui meurt en cours de lecture : on en redemande une, UNE fois.
//
// Deux voix remontent la panne, et elles ne se recouvrent pas :
//   · `v.onerror` — la balise elle-même : lien debrid expiré, réseau coupé,
//     format refusé. C'est la seule qu'on écoutait.
//   · `surPanneSource` du lecteur — le démuxeur MKV, qui lit le fichier dans
//     la page et peut mourir SANS que la balise en dise rien (voir
//     `creerSignalDePanne` dans lecteur.js). Ce rappel était exposé depuis le
//     correctif de l'écran noir, mais personne ne le branchait : ce fichier ne
//     l'appelait nulle part, si bien qu'une source MKV qui lâchait affichait
//     « Source perdue — reviens en arrière » et ne retentait jamais. Mesuré à
//     l'audit du 10/09.
//
// Le traitement était écrit deux fois — films, épisodes — à l'identique. Il
// n'est plus écrit qu'ici, et posé aux DEUX endroits par `brancherReprise`. Le
// garde-fou anti-boucle ne change pas : une seule tentative par ouverture,
// portée par `v.dataset.reprise` — remis à '0' à chaque ouverture qui n'est
// pas elle-même la relance (voir `lancer` et `lancerFilm`, dont la relance
// passe `relance: true`).
//
// Et depuis l'audit du 10/09, une TROISIÈME voix, qui ne redemande rien :
// l'erreur 4 de la balise — format non pris en charge. Elle ne dit pas que la
// source est morte, elle dit que cette machine ne décode pas ce flux tel qu'on
// le lui donne. Un .mp4 en HEVC, en Dolby Vision, en E-AC3 ou en DTS, déclaré
// « lisible tel quel » par le serveur sur la foi de son extension, finissait
// ici — et repartait en résolution d'une AUTRE source, aux mêmes codecs. Le
// lecteur tient désormais un escalier sur la MÊME ref (relais direct →
// démuxeur → remux, voir « L'ESCALIER » dans lecteur.js) : `reprendre` le
// laisse descendre tant qu'il a un cran, et dit « source indisponible » quand
// il n'en a plus — sans re-résoudre pour ce motif-là. `dataset.reprise` reste
// intact : c'est un second garde-fou, pas un contournement du premier.
// ---------------------------------------------------------------------------

// Comment on nomme, à l'écran, le cran où l'escalier vient de descendre.
const CRANS_DITS = { mse: 'par le démuxeur', remux: 'par le serveur' };

/**
 * Pose la reprise sur la balise ET sur le lecteur, et rend le rappel posé.
 *
 * Exportée pour `essais/panne-source.mjs`, qui l'éprouve sans navigateur.
 *
 * @param {HTMLVideoElement} v la balise — son `dataset.reprise` porte le compte.
 * @param {{surPanneSource?: Function, descendre?: Function}|null} lecteur celui
 *   de `lecteur()` ; nul quand la balise est seule — aucun démuxeur ne parlera
 *   alors, et aucun escalier ne descendra : une erreur 4 y suit le chemin de
 *   toujours.
 * @param {object} o
 * @param {() => void} o.relancer redemande une source : l'appel qui relance
 *   `lancer`/`lancerFilm` avec `relance: true`.
 * @param {boolean} [o.enFond] en préparation, personne ne lit la ligne d'infos.
 * @param {object} [o.contexte] ce que la trace doit porter (film, saison…).
 */
export function brancherReprise(v, lecteur, { relancer, enFond = false, contexte = null }) {
  const reprendre = (panne) => {
    // ⚠️ HORS DE L'ÉCRAN, UNE SOURCE QUI MEURT NE RAPPELLE PAS LE LECTEUR (bug
    // SEANCE-2610222121-pws). Une balise mise de côté dont le flux se coupe
    // relançait `lancer()`, qui rouvrait l'écran et rejouait l'épisode derrière
    // la personne partie. On vide la balise : au retour elle n'est plus prête
    // (`estPret`), et le chemin complet refait la source.
    if (!enFond && lecteur?.estHorsEcran?.()) {
      tracer('video', 'source perdue hors de l’écran — pas de relance', contexte ?? {});
      try { v.removeAttribute('src'); v.load(); } catch { /* déjà vide */ }
      return;
    }
    // D'où vient le cri. La balise ne dit rien de plus qu'un code ; le
    // démuxeur, lui, arrive avec `{ ref, raison }` (voir `creerSignalDePanne`).
    // La trace le garde : c'est ce qui permet, dans l'admin, de compter les
    // sources mortes par chemin.
    const duDemuxeur = Boolean(panne) && typeof panne.raison === 'string';
    // Le code d'erreur du média dit ce qu'aucun log serveur ne peut dire :
    // format refusé, réseau coupé, flux tronqué, ou lecture interrompue.
    const codes = { 1: 'lecture interrompue', 2: 'erreur réseau', 3: 'décodage impossible', 4: 'format non pris en charge' };
    // ⚠️ UN FORMAT REFUSÉ NE SE RÉSOUT PAS AILLEURS : IL SE REJOUE AUTREMENT.
    //
    // L'erreur 4 ne dit rien du lien : elle dit que CETTE machine ne décode
    // pas ce flux tel qu'on le lui donne. Re-résoudre rendait le même fichier,
    // ou un autre aux mêmes codecs, et l'on retombait dans le même trou avec
    // une résolution de moins. Le lecteur tient un escalier pour ça (voir
    // « L'ESCALIER » dans lecteur.js) : la même ref, un cran plus bas, à la
    // même seconde. Tant qu'il a un cran, on le laisse descendre ; épuisé,
    // c'est « source indisponible » — et PAS de re-résolution pour ce
    // motif-là. Le marqueur `dataset.reprise` n'est pas touché : il garde son
    // unique tentative pour ce qui la mérite, une source qui meurt.
    //
    // Le démuxeur, lui, ne parle jamais en code 4 : sa panne est une source qui
    // meurt en cours de route, et suit le chemin d'en dessous. Un lecteur sans
    // `descendre` (la balise seule) n'a pas d'escalier : même chemin.
    // ⚠️ L'ERREUR SE LIT AVANT DE DESCENDRE. `descendre` vide la balise
    // (`video.load()`), et l'algorithme de chargement remet `error` à null :
    // relire `v.error` après coup tracerait « erreur ? — inconnue » pour
    // exactement le cas qu'on veut compter. Trouvé par la vérification
    // adversariale du 10/09.
    const codeErreur = v.error?.code ?? null;
    const messageErreur = v.error?.message ?? null;
    // ⚠️ AU CRAN REMUX, UNE ERREUR PEUT ETRE UN REFUS DU SERVEUR, PAS DU FICHIER.
    //
    // Le plafond des conversions (conversions.js) rend 503 quand toutes les
    // places sont prises — et la balise ne lit pas un corps JSON : elle ne voit
    // qu'un code 2 ou 4. Relancer aveuglement retomberait sur la meme source,
    // se ferait refuser a nouveau et finirait sur « source indisponible » pour
    // une soiree simplement trop chargee. On demande donc au serveur, par un
    // endpoint qui ne lance AUCUN ffmpeg, si c'est une saturation ; si oui, on
    // le dit a la personne et on ne depense ni cran ni relance pour ca.
    // Le chemin ordinaire part TOUT DE SUITE, comme avant : descente ou
    // relance ne doivent pas attendre un aller-retour. La question de la
    // saturation se pose en parallele, et ne fait que corriger le MOT si la
    // reponse est oui — c'est la partie qui manquait : sans elle, une soiree
    // trop chargee se lisait « source indisponible ».
    const auCranRemux = !duDemuxeur && typeof lecteur?.cran === 'function' && lecteur.cran() === 'remux';
    reprendreSansSaturation();
    if (auCranRemux && (codeErreur === 2 || codeErreur === 4) && typeof fetch === 'function') {
      (async () => {
        let etat = null;
        try {
          const r = await fetch('/api/conversions', { credentials: 'include' });
          if (r.ok) etat = await r.json();
        } catch { /* le serveur ne repond pas : le mot ordinaire reste */ }
        if (!etat?.sature) return;
        tracer('video', 'conversions saturées — le serveur a refusé la conversion', {
          origine: 'serveur', enCours: etat.enCours, max: etat.max, ...(contexte ?? {}),
        });
        lecteur?.noterChemin?.('remux', 'conversions-saturees');
        if (!enFond) texte('l-infos', `les conversions sont saturées (${etat.enCours}/${etat.max}) — essaie une autre source`);
      })();
    }
    return;

    function reprendreSansSaturation() {
    const formatRefuse = !duDemuxeur && codeErreur === 4 && typeof lecteur?.descendre === 'function';
    const marche = formatRefuse ? lecteur.descendre({ enFond }) : null;
    tracer('video', duDemuxeur
      ? `source perdue — ${panne.raison}`
      : `erreur ${codeErreur ?? '?'} — ${codes[codeErreur] ?? 'inconnue'}`, {
      origine: duDemuxeur ? 'demuxeur' : 'balise',
      message: messageErreur,
      reprise: v.dataset.reprise === '1',
      // Ce que l'escalier a fait de l'erreur 4 : le cran pris, ou « épuisé ».
      escalier: formatRefuse ? (marche ? `${marche.de} → ${marche.vers}` : 'épuisé') : null,
      readyState: v.readyState,
      networkState: v.networkState,
      ...(contexte ?? {}),
    });
    if (marche) {
      if (!enFond) texte('l-infos', `format refusé — nouvel essai ${CRANS_DITS[marche.vers] ?? marche.vers}…`);
      return;
    }
    if (formatRefuse) {
      // Cran 3 : trois façons de lire la même source, trois refus. Une autre
      // source aux mêmes codecs n'y changerait rien — on ne la cherche pas.
      if (!enFond) direIndisponible('Cette source ne se lit pas : ni directement, ni convertie par le serveur.');
      return;
    }
    if (v.dataset.reprise === '1') {
      // En fond, personne ne lit ce message : la préparation renonce, et le
      // clic sur « Revenir au lecteur » refera le chemin complet.
      if (!enFond) direIndisponible('La source ne répond plus, et la nouvelle tentative non plus.');
      return;
    }
    v.dataset.reprise = '1';
    if (!enFond) texte('l-infos', 'source perdue, nouvelle tentative…');
    relancer();
    }

    // L'écran d'erreur (étude UI 22/10, point 6). La ligne d'infos garde son
    // mot pour qui a l'habitude de la lire ; l'écran porte les gestes.
    // « Réessayer » rend sa tentative automatique : `dataset.reprise` à '0'.
    // Le code : d'où vient le cri (MED = la balise, DMX = le démuxeur) et le
    // code de la balise — de quoi retrouver la trace sans la console.
    function direIndisponible(message) {
      texte('l-infos', 'source indisponible');
      // ⚠️ L'ÉCRAN D'ATTENTE ATTEND UNE IMAGE QUI NE VIENDRA PAS : il recouvrait
      // l'écran d'erreur jusqu'à `ATTENTE_MAX_IMAGE`. On le lève ici.
      try { if ($('v-chargement') && !$('v-chargement').hidden) basculeFinale(); } catch { /* sans page */ }
      lecteur?.montrerErreur?.({
        message,
        code: duDemuxeur ? 'DMX' : `MED-${codeErreur ?? 0}`,
        reessayer: () => { v.dataset.reprise = '0'; relancer(); },
      });
    }
  };
  v.onerror = reprendre;
  // Le MÊME rappel des deux côtés : un cri du démuxeur et une erreur de la
  // balise pour la même source se partagent le même compte — `dataset.reprise`
  // — et ne peuvent donc pas déclencher deux résolutions.
  lecteur?.surPanneSource?.(reprendre);
  return reprendre;
}

/**
 * Les rappels du menu « Voix » et du panneau « Changement de source », pour
 * UNE cible de lecture (`series`/`<id>:<s>:<e>` ou `movie`/`<id>`).
 *
 * ⚠️ UNE SEULE FABRIQUE POUR LES SÉRIES ET LES FILMS. Le `poser` des épisodes
 * recevait `changerLangue` et `typeOeuvre` depuis toujours ; celui des films
 * partait nu — c'est le défaut « VF/VO inerte sur les films » : le bouton
 * existait, le clic ne faisait rien. Tout rappel qui s'ajoute ici arrive donc
 * aux deux, ou à aucun.
 *
 * Le lecteur ne connaît aucune route : il reçoit des fonctions. `voixDe` ne
 * résout JAMAIS de sources (le serveur lit son mapping et ses sondes), c'est ce
 * qui permet de l'appeler à chaque ouverture sans faire payer la salle.
 */
function rappelsDeVoixEtSources(type, id) {
  const cible = `${encodeURIComponent(type)}/${encodeURIComponent(id)}`;
  return {
    proprietaire: role === 'owner',
    voixDe: (ref) => api(`/api/voix/${cible}?depuis=${encodeURIComponent(ref ?? '')}`),
    sourcesDe: (ref, frais) => api(`/api/sources-episode/${cible}?depuis=${encodeURIComponent(ref ?? '')}&frais=${frais ? 1 : 0}`),
    // `depuis` : la référence en cours, pour que le serveur mesure l'écart
    // entre les deux montages en fond (comme /api/langue) — et le lecteur le
    // réclame ensuite par `surSourceOuverte`, exactement comme la bascule VF/VO.
    ouvrirSourceDe: (url) => api(`/api/sources-episode/${cible}/ouvrir`, {
      method: 'POST', body: JSON.stringify({ url, depuis: lecteur().ref() ?? '' }),
    }),
    surSourceOuverte: (avant, apres, seconde) => reclamerAlignement(avant, apres, seconde),
  };
}

/**
 * Retient un lancement, tout de suite (épisode OU film).
 *
 * ⚠️ LANCER SUFFIT A RETENIR. Toji : « j'ai cliqué, hop, c'est en retenue dans
 * tous les cas ». La note de position n'arrive qu'au bout de trente secondes de
 * lecture (le plancher qui protege des coups d'oeil) : jusque-la, revenir a
 * l'accueil ne montrait rien. `demarrage` le dit au serveur, qui garde son
 * plancher pour tout le reste. Un film manquait a l'appel (SEANCE-2610200713-7sg).
 *
 * Detache et silencieux : une memoire de reprise ne retarde pas une lecture,
 * et son echec ne la regarde pas (il est seulement trace).
 */
function retenirLeLancement(reprise = 0) {
  if (!lectureCourante?.oeuvreId) return;
  api('/api/reprendre', {
    method: 'POST',
    body: JSON.stringify({ ...lectureCourante, position: Math.max(6, Math.floor(reprise || 0)), demarrage: true }),
  }).then(() => chargerReprises())
    .catch((err) => tracer('reprendre', 'note de départ refusée', err?.message ?? String(err)));
}

async function lancerFilm(fiche, { reprise = 0, relance = false } = {}) {
  nouvelleTrace(`film ${fiche.nom}`);
  // Ouvrir un groupe se paie ici, AVANT la résolution (rejoindre : rien à payer).
  if (!relance && !(await caisseUI.porteDuPrix('movie', fiche.id, { titre: fiche.nom, qualite: prefs.lire('qualite', 'auto') }))) return;
  if (await lireDehors({ film: fiche.id }, fiche.nom)) return;
  texte('l-titre', fiche.nom);
  texte('chargement-txt', `Recherche d'une source pour ${fiche.nom}…`);
  appliquerBlason(lireBlason(prefs));
  // Un film n'a ni saison ni épisode : `imageEpisode` ne peut rien pour lui.
  // Son propre visuel tient lieu de vignette — le fond d'abord, l'affiche
  // sinon : un fond est cadré large, une affiche verticale se recadre mal.
  // Le grand palier devant, celui des cartes derrière — voir `peindreFondAttente`.
  const decorFilm = fondDeLOeuvre(fiche);
  peindreFondAttente(
    decorFilm ? fondDeBanniere(decorFilm, window.innerWidth || 1280) : null,
    decorFilm || null,
  );
  peindreTitreAttente({ titre: fiche.nom });
  retourDepuisAttente = null; // le retour suit le chemin (voir `revenir`)
  const bRetour = $('retour-attente');
  if (bRetour) bRetour.hidden = false;
  document.body.classList.add('plein');
  $('v-chargement')?.classList.add('lecture');
  debutAttente = Date.now();
  vue('v-chargement');
  // La sortie doit être là DÈS le premier instant : c'est un écran qui peut durer
  // dix secondes, et il n'en a pas d'autre. `vue()` peut l'avoir remasquée en
  // repeignant, on la repose donc après elle et non avant.
  if (bRetour) bRetour.hidden = false;

  // ⚠️ UN FILM ETAIT LE PARENT PAUVRE : AUCUNE PREFERENCE N'ETAIT ENVOYEE.
  //
  // `lancer()` (les épisodes) part avec `parametresDeGout(...)` depuis
  // toujours ; cet appel-ci partait nu. Le serveur recevait « langue : rien,
  // sous-titres : rien » et ne pouvait pas choisir le bon fichier parmi
  // plusieurs — deux personnes qui ne differaient que par leurs reglages
  // recevaient donc TOUJOURS le meme fichier pour le meme film.
  //
  // `oeuvre` (la fiche globale) n'est pas fiable ici : `lancerFilm` est
  // aussi appelee directement depuis une grille ou une collection, sans
  // etre passee par `ouvrirOeuvre` d'abord — `oeuvre` peut alors pointer sur
  // tout autre chose, ou etre nul. `fiche`, lui, EST le film qu'on lance :
  // c'est lui qui doit decider, comme le fait `oeuvre` pour `lancer()`.
  const voulue = langueVoulueDe(fiche);
  let choix;
  try {
    // `fiche` aux DEUX, et pour la meme raison : c'est le film qu'on lance qui
    // decide de sa langue comme de ses sous-titres.
    choix = await api(`/api/play-now/movie/${encodeURIComponent(fiche.id)}`
      + parametresDeGout(voulue, sousTitresVoulus(fiche)));
  } catch (err) {
    // ⚠️ Voir le commentaire dans `api()` : une « attente d'autorisation »
    // a deja pose son propre ecran (`v-attente`) — le rebasculer ici sur
    // l'ecran d'erreur generique l'effacerait aussitot.
    if (err.silencieux) return;
    return erreur(err.message, err.essais ?? []);
  }
  // Retour pressé pendant la recherche : la réponse arrive quand même, mais
  // elle ne doit plus rien déclencher.
  if ($('v-chargement').hidden) return;

  // La bascule pose son ecran blanc de facon synchrone : au retour, il couvre
  // deja tout. On enchaine donc sans attendre — le chargement du flux part
  // pendant que la transition se joue par-dessus, au lieu d'apres.
  // La transition attend l'image : voir `basculerQuandPret`.
  // Un film se reprend comme un épisode : sans saison ni numéro, c'est l'œuvre
  // entière qui porte la position.
  lectureCourante = {
    oeuvreId: fiche.id, saison: 0, episode: 0,
    titre: fiche.nom, episodeTitre: null,
    vignette: fiche.fond || fiche.affiche || null,
  };
  // ⚠️ EN DEHORS de `lectureCourante`, qui part telle quelle dans le corps de
  // `/api/reprendre` : c'est ce que les sous-titres liront pour savoir a quel
  // type d'oeuvre ils appartiennent (voir `typeEnLecture`). `lancerFilm` ne
  // lance que des films — d'ou le mot ecrit en clair.
  typeEnLecture = 'film';
  const cible = `movie/${fiche.id}`;
  lecteur().ouvrir({
    ref: choix.ref,
    direct: choix.direct,
    duree: choix.duree,
    titre: fiche.nom,
    // Pour l'écran verrouillé et le casque (Media Session, voir lecteur.js).
    affiche: fiche.affiche || fiche.fond || null,
    // Sans cette ligne, aucun apercu n'etait JAMAIS demande pour un film : la
    // barre de progression n'en montrait pas, et le serveur n'en fabriquait
    // donc jamais. La cible d'un film est son seul identifiant — il n'a ni
    // saison ni episode.
    episode: fiche.id,
    // ⚠️ AUCUN SOUS-TITRE, JAMAIS. Sans ce rappel, changer de langue en
    // cours de lecture (VF -> VO) ne relancait jamais la recherche de
    // sous-titres pour le NOUVEAU fichier — voir `etat.surSousTitres?.(...)`
    // dans lecteur.js, qui l'appelle a chaque bascule de langue.
    surSousTitres: (ref) => chargerSousTitres(ref, cible),
    // ⚠️ UN FILM N'AVAIT NI `changerLangue` NI `typeOeuvre` : le bouton de
    // langue s'affichait et ne faisait RIEN (« VF/VO inerte sur les films »).
    // La cible est la même que celle des sous-titres et de la reprise.
    changerLangue: (vers, position) => changerLangue(cible, vers, position),
    langueVoulue: voulue,
    surChoixLangue: (l) => retenirChoixDeLangue(fiche.id, l),
    // `oeuvre` n'est fiable que si c'est bien CE film qui est ouvert sur la
    // fiche (voir plus haut) ; une fiche de film ne porte pas le type
    // « anime » elle-même. Sans le savoir, la VO est dite « Anglais ».
    typeOeuvre: oeuvre?.id === fiche.id ? (oeuvre?.type ?? null) : null,
    // Sans échelle approximative, la barre d'un film restait sans repère
    // jusqu'au retour de la durée mesurée (audit du câblage, 10/09).
    dureeApprox: secondesDe(fiche.runtime ?? oeuvre?.runtime) || null,
    ...rappelsDeVoixEtSources('movie', fiche.id),
  });
  retenirLeLancement(reprise);
  annoncerVariante();
  retenirLeLancement(reprise);
  basculerQuandPret();

  // ⚠️ UNE SEULE TENTATIVE DE REPRISE PAR OUVERTURE, PAS PAR SESSION.
  //
  // Meme marqueur, meme piege, que dans `lancer()` (voir le commentaire au
  // debut de cette fonction-la) : `v.dataset.reprise` vit sur la balise
  // <video>, qui vit toute la session, et sert aussi bien aux films qu'aux
  // episodes. On le remet a zero a l'ouverture d'un film, sauf si CET appel
  // est lui-meme la relance interne declenchee par `onerror` juste en
  // dessous — sinon la relance se retrouverait avec un marqueur tout juste
  // efface, et pourrait boucler sur elle-meme sans fin.
  const v = $('video');
  if (v) {
    if (!relance) v.dataset.reprise = '0';

    // ⚠️ REPRENDRE : ON SE PLACE DES QUE LA BALISE TIENT QUELQUE CHOSE.
    // Meme repere que `lancer()` — voir son commentaire detaille : la duree
    // et le `readyState` restent coinces sur un MKV demuxe dans la page, et
    // `imagePrete` est le seul repere qui marche dans les deux cas.
    if (reprise > 0) {
      const placer = () => {
        try { lecteur().recaler(reprise); } catch (err) { tracer('reprise', 'recalage refusé — balise pas déplaçable', err?.message ?? String(err)); }
        tracer('reprise', `reprise à ${Math.round(reprise)} s`, { film: fiche.id });
      };
      if (imagePrete(v)) placer();
      else v.addEventListener('loadedmetadata', placer, { once: true });
    }

    surveillerVideo(v);
    // ⚠️ PAS DE `surveillerFin` ICI. Elle prepare l'EPISODE SUIVANT — une
    // notion qui n'existe pas pour un film, qui n'a ni saison ni numero.
    // Copier cet appel aurait appele `preparerSuivant` avec des arguments
    // absurdes pour rien de plus qu'un film n'a a preparer.

    // ⚠️ AUCUN FILET, JAMAIS. `lancer()` pose sa reprise, pas cette
    // fonction : un lien debrid qui expire en cours de lecture laissait
    // un film fige, sans un mot et sans trace, la ou un episode aurait
    // tente une seconde source. Meme mecanique qu'en face — c'est le meme
    // `brancherReprise` —, adaptee : pas de saison/episode a repasser, juste
    // la fiche et la seconde ou l'on en etait.
    //
    // La seconde ou l'on reprenait voyage avec la nouvelle tentative, comme
    // pour un episode : sans elle, une source qui lache a la reprise
    // ramenerait au generique celui qui venait justement de demander a
    // reprendre.
    brancherReprise(v, lecteur(), {
      relancer: () => lancerFilm(fiche, { reprise, relance: true }),
      contexte: { film: fiche.id },
    });
  }

  // Les sous-titres arrivent apres : les chercher d'abord retarderait la
  // lecture pour une piste qui n'existe peut-etre pas. Meme ordre que dans
  // `lancer()`.
  chargerSousTitres(choix.ref, cible);
}

// ---------------------------------------------------------------------------
// Recherche
// ---------------------------------------------------------------------------
let minuteur = null;

async function chercher() {
  const champ = $('q');
  const q = champ ? champ.value.trim() : '';
  if (q.length < 2) return;

  // ⚠️ CETTE RECHERCHE-CI INTERROGE LES ADD-ONS, PAS LE CATALOGUE.
  //
  // Elle sert à trouver un titre qui n'est PAS encore chez nous — c'est l'outil
  // d'administration qui ajoute une œuvre. La recherche que voit tout le monde
  // est celle de la grille (`#oeuvres-q`), qui filtre le catalogue en mémoire,
  // sans réseau. Deux besoins, deux chemins ; les confondre ferait attendre un
  // aller-retour à chaque touche pour filtrer ce qu'on a déjà.
  //
  // Ses quatre nœuds appartiennent à l'ancien accueil : sans eux, on renvoie
  // vers la grille plutôt que de lever.
  const zone = $('resultats');
  if (!zone) return ouvrirCatalogueOeuvres();
  texte('accueil-titre', `Résultats pour « ${q} »`);
  zone.innerHTML = '';
  const cadre = $('zone-resultats');
  if (cadre) cadre.hidden = false;
  $('v-accueil')?.classList.add('libre');
  vue('v-accueil');

  let res;
  try {
    ({ results: res } = await api(`/api/search?q=${encodeURIComponent(q)}`));
  } catch (err) {
    zone.innerHTML = `<p class="muted">${esc(err.message)}</p>`;
    return;
  }
  if (!res.length) { zone.innerHTML = '<p class="muted">Aucun résultat.</p>'; return; }

  zone.innerHTML = res
    .map(
      (m) => `<button class="poster" data-id="${esc(m.id)}" type="button">
        <span class="art">${m.poster ? `<img src="${esc(afficheDeCarte(m.poster, densiteEcran()))}" alt="" decoding="async" loading="lazy" />` : ''}</span>
        <span class="nom">${esc(m.name)}</span>
        <span class="an">${esc(m.year ?? '')}</span>
      </button>`,
    )
    .join('');
  for (const el of zone.querySelectorAll('.poster')) el.onclick = () => ouvrirOeuvre(el.dataset.id);
}

// ===========================================================================
// LE CATALOGUE D'ŒUVRES
//
// Trois écrans, et c'est tout le chantier :
//
//   · `#v-accueil`   — trois rangées : « Reprendre », « Nouveautés »,
//                      « Tout le catalogue ».
//   · `#v-catalogue` — la grille complète, avec recherche et filtres.
//   · `#v-oeuvre`    — la fiche d'UNE œuvre, et ses épisodes.
//
// ⚠️ LE BALISAGE EST ÉCRIT PAR QUELQU'UN D'AUTRE, EN MÊME TEMPS QUE CE FICHIER.
//
// D'où la règle qui gouverne tout ce qui suit : on ne présume RIEN de la
// structure intérieure d'une vue. On cherche le nœud attendu ; s'il n'est pas
// là, on le crée dans la vue et on peint dedans. Une page qui n'a pas encore
// reçu sa mise en page rend donc un contenu utilisable plutôt qu'un écran vide,
// et le jour où le balisage arrive avec les bons identifiants, ce code cesse
// simplement de créer quoi que ce soit.
//
// L'incident derrière cette règle est déjà consigné plus haut, au-dessus de
// `surClic` : « un seul élément absent suffisait à interrompre tout le script
// au chargement ». Ici le risque est le même, en pire — les vues sont en train
// d'être réécrites pendant qu'on lit ce fichier.
//
// LES IDENTIFIANTS QUE CE FICHIER PEUPLE (contrat pour l'agent de l'interface) :
//   accueil   : #acc-reprendre, #acc-nouveautes, puis une `.st-rangee` par
//               rayon (films, séries, animés) créée par `zoneDeLaRangee`
//   catalogue : #oeuvres-q, #oeuvres-filtres, #oeuvres-compte, #oeuvres-grille
//   fiche     : #oeu-fond, #oeu-affiche, #oeu-titre, #oeu-meta, #oeu-resume,
//               #oeu-lire (film), .choix-arc + #s-episodes (série)
// Les classes émises sont préfixées `st-`, comme le reste de la nouvelle
// interface.
// ===========================================================================

/**
 * L'écran d'une fiche d'œuvre.
 *
 * `v-serie` est l'ancien nom du même écran, du temps où il n'y avait qu'une
 * série. On le garde en repli le temps que le balisage bascule : sans lui, une
 * page pas encore réécrite n'afficherait plus AUCUNE fiche, et le lecteur
 * n'aurait plus par où lancer un épisode.
 */
const ECRAN_OEUVRE = () => ($('v-oeuvre') ? 'v-oeuvre' : 'v-serie');

/**
 * ⚠️ COLLISION D'IDENTIFIANTS, ET ELLE EST RÉELLE.
 *
 * `#v-catalogue` désignait le catalogue de PHOTOS — l'écran d'administration
 * où l'on choisit le visuel d'un arc. Il désigne désormais la grille des
 * œuvres, qui est ce que « catalogue » veut dire pour tout le monde sauf pour
 * ce coin d'admin. Le catalogue d'images déménage donc sous `#v-photos`.
 *
 * On résout au moment du clic et non au chargement : les deux fichiers changent
 * en même temps, et une constante figée ici se tromperait selon lequel des deux
 * a été enregistré en premier.
 */
const ECRAN_PHOTOS = () => ($('v-photos') ? 'v-photos' : 'v-catalogue');

/**
 * L'écran vers lequel on revient quand on quitte une attente ou une lecture.
 *
 * ⚠️ IL DOIT EXISTER. `vue()` sur un identifiant absent masque toutes les vues
 * puis sort sans rien montrer : on reste sur un écran noir, et le bouton
 * « retour » paraît cassé alors qu'il a fait exactement ce qu'on lui a demandé.
 * La grille d'un arc d'abord (on en vient), la fiche ensuite, l'accueil en
 * dernier recours — le seul écran dont l'existence ne dépend de rien.
 */
export function ecranDeRetourLecture() {
  // Le chemin réellement parcouru passe avant toute déduction : c'est la seule
  // chose qui sache si l'on vient de l'accueil, d'un rayon ou d'une fiche.
  const venu = derniereEtape();
  if (venu && $(venu)) return venu;
  if (collectionCourante && collectionCourante !== 'anime' && $('v-collection')) return 'v-collection';
  if ($('ep-grille')?.innerHTML) return 'v-episodes';
  return $(ECRAN_OEUVRE()) ? ECRAN_OEUVRE() : 'v-accueil';
}

// ---------------------------------------------------------------------------
// LE CHEMIN DE L'UTILISATEUR
//
// Toji : « le retour, faut vraiment le mapper par rapport à là où on était de
// base. On enregistre le chemin utilisateur. On est dans l'accueil, on clique
// sur un anime, on clique sur une saison. Peu importe ce qu'on fait, dans tous
// les cas, on sait d'où vient l'utilisateur. »
//
// Ce qu'il y avait : sortir du lecteur ouvrait la liste des séances, TOUJOURS,
// sur tout ce qui n'est pas l'activité Discord (`if (!DANS_DISCORD)`). C'était
// juste du temps où le site n'était qu'un lecteur et un choix de séance ; depuis
// qu'il porte un catalogue entier, ça renvoie systématiquement quelqu'un qui
// venait de l'accueil vers un écran qu'il n'avait pas demandé.
//
// Ce qu'il y a : on retient les écrans de CONTENU traversés. Le lecteur, le
// chargement et l'attente n'en font pas partie — ce sont des passages, pas des
// destinations, et revenir sur eux serait revenir sur ses pas au lieu de sortir.
//
// La liste survit au rechargement (`sessionStorage`) mais pas à la fermeture de
// l'onglet : rouvrir le site, c'est un nouveau chemin.
// Les écrans de l'activité qu'un onglet du navigateur sait rendre au retour.
const DEPUIS_RENDUS = new Set(['v-oeuvre', 'v-catalogue', 'v-accueil']);

/**
 * Où le retour du lecteur mène quand la lecture vient de l'ACTIVITÉ — PURE.
 * `null` : pas venu de l'activité (un vrai lien de séance Discord → la liste).
 * Écran inconnu ou intransportable (les séances, la console) : la fiche de
 * l'œuvre si on la connaît, sinon l'accueil (Toji, 21/10 : « on doit revenir
 * sur la fiche de l'œuvre »).
 */
export function ecranDeRetourDepuisActivite(depuis, oeuvreConnue) {
  if (!depuis) return null;
  if (DEPUIS_RENDUS.has(depuis.depuis) && (depuis.depuis !== 'v-oeuvre' || oeuvreConnue)) return depuis.depuis;
  return oeuvreConnue ? 'v-oeuvre' : 'v-accueil';
}

/** L'écran de contenu de l'activité d'où part une lecture, pour `#depuis=`. */
function ecranDOrigineDansLActivite() {
  return [ecranCourant, ...[...chemin].reverse()].find((e) => DEPUIS_RENDUS.has(e)) ?? '';
}

/**
 * Quitte le plein écran, qu'il soit sur la vidéo ou sur son conteneur
 * (`document.exitFullscreen` sort de l'un comme de l'autre), repli webkit
 * compris ; le plein cadre de l'iframe se rend par le bouton du lecteur.
 */
function quitterPleinEcran(doc = document) {
  try {
    if (doc.fullscreenElement) doc.exitFullscreen?.()?.catch?.(() => {});
    else if (doc.webkitFullscreenElement) doc.webkitExitFullscreen?.();
    if (doc.body?.classList.contains('plein-cadre')) doc.getElementById('l-plein')?.click();
  } catch { /* rien à quitter */ }
}

const ETAPES_IGNOREES = new Set(['v-lecture', 'v-chargement', 'v-attente', 'v-login', 'v-erreur']);
const CLE_CHEMIN = 'tsudoi.chemin';
let chemin = [];
try { chemin = JSON.parse(sessionStorage.getItem(CLE_CHEMIN) ?? '[]'); } catch { chemin = []; }

function noterEtape(id) {
  if (ETAPES_IGNOREES.has(id)) return;
  // Revenir sur un écran déjà traversé RACCOURCIT le chemin au lieu de
  // l'allonger : sans ça, accueil → fiche → accueil → fiche laisserait quatre
  // étapes, et il faudrait quatre retours pour ressortir d'un aller-retour.
  const deja = chemin.lastIndexOf(id);
  if (deja >= 0) chemin.length = deja;
  chemin.push(id);
  // Un chemin ne sert qu'à revenir en arrière : au-delà d'une dizaine d'étapes,
  // ce qui est au fond ne sera jamais atteint et n'a plus à être gardé.
  if (chemin.length > 12) chemin.shift();
  try { sessionStorage.setItem(CLE_CHEMIN, JSON.stringify(chemin)); } catch { /* confort */ }
}

/** L'écran de contenu précédent — celui d'où l'on vient vraiment. */
function derniereEtape() {
  // Le dernier de la liste est l'écran COURANT quand on est encore dessus ;
  // pendant une lecture il n'y figure pas, et c'est bien lui qu'on veut.
  for (let i = chemin.length - 1; i >= 0; i -= 1) {
    if (chemin[i] !== ecranCourant && $(chemin[i])) return chemin[i];
  }
  return null;
}

/** Vrai si la personne a traversé le site avant d'arriver ici. */
function venuDuSite() {
  return chemin.some((e) => e !== 'v-sessions' && e !== 'v-seances');
}

/**
 * Quitter les réglages : en modale, la fermer ; en PAGE (« Mes réglages »
 * depuis la liste), fermer une modale absente ne changeait pas d'écran (audit
 * 14e 21/10, P8 ; même défaut sur le retour de « Mes sources »).
 */
function quitterLesReglages() {
  if ($('voile') && !$('voile').hidden) return fermerModale();
  return revenir('v-accueil');
}

/**
 * LE RETOUR REND L'ÉCRAN D'OÙ L'ON VIENT (audit 14e 21/10, P6/P9).
 *
 * Un seul geste pour tous les boutons de retour, au lieu d'une destination
 * écrite en dur sur chacun (la console et l'erreur rendaient la fiche, même à
 * qui venait de la liste). Sur le site, la liste des séances seulement si l'on
 * en vient — ou si l'on n'a traversé aucun écran sur une adresse de séance
 * (`/lecteur/<alias>`) : c'est l'arrivée par un lien Discord. Les écrans peints à
 * l'ouverture se rouvrent par leur fonction : `vue()` seul les rendrait figés.
 */
function revenir(repli = 'v-accueil') {
  const venu = derniereEtape();
  // Rien traversé sur une adresse de séance : on est arrivé par le lien Discord.
  const parUnLienDeSeance = !venu && /^\/lecteur\//.test(location.pathname);
  if (parUnLienDeSeance && !DANS_DISCORD) return ouvrirLesSeances();
  const cible = venu ?? repli;
  if (cible === 'v-sessions' || cible === 'v-seances') return ouvrirLesSeances();
  if (cible === 'v-gestion') { vue('v-gestion'); return peindreGestion(); }
  if (cible === 'v-accueil') return ouvrirAccueil();
  if (cible === 'v-catalogue' && $('v-photos')) return ouvrirCatalogueOeuvres();
  return vue($(cible) ? cible : 'v-accueil');
}

/**
 * Le nœud voulu, ou celui qu'on vient de créer pour lui.
 *
 * Rend `null` seulement si la VUE elle-même manque : à ce moment-là il n'y a
 * plus rien à faire, et l'appelant le sait.
 */
function zoneOuCreee(hote, id, { balise = 'div', classe = '', avant = null } = {}) {
  if (!hote) return null;
  const deja = $(id);
  // Déjà dans la bonne vue : rien à faire. Ailleurs — le balisage l'a laissé
  // sur l'écran d'avant — on le DÉMÉNAGE plutôt que d'en créer un jumeau : deux
  // nœuds portant le même identifiant, c'est `getElementById` qui tranche au
  // hasard, et la moitié des peintures qui partent dans un écran masqué.
  if (deja) {
    if (!hote.contains(deja)) hote.appendChild(deja);
    return deja;
  }
  const el = document.createElement(balise);
  el.id = id;
  if (classe) el.className = classe;
  if (avant && avant.parentElement === hote) hote.insertBefore(el, avant);
  else hote.appendChild(el);
  return el;
}

/** Le catalogue, chargé une fois. Ne lève jamais : sans lui, la grille est vide. */
// ---------------------------------------------------------------------------
// LE CATALOGUE ARRIVE PAR PAGES, ET L'ECRAN N'ATTEND PAS LA DERNIERE
//
// L'add-on pagine ses catalogues et il pagine loin : le nôtre grossit à chaque
// tour d'amorçage. Tout demander d'un coup, c'était une réponse qui grandit
// sans limite — et un accueil qui attend qu'elle soit entière avant d'afficher
// la première affiche.
//
// La première page suffit à peindre : trois cents œuvres, c'est déjà plus que
// ce que les rangées montrent. Le reste arrive derrière, et l'accueil se
// repeint à chaque page — on voit le catalogue se remplir au lieu d'attendre
// devant un écran vide.
// ---------------------------------------------------------------------------
let catalogueComplet = false;
let chargementEnCours = null;

let dernierePeinture = 0;

/**
 * Le catalogue, charge une fois.
 *
 * `tout` dit si l'on veut la TOTALITE — c'est le cas de la grille, qui cherche
 * et filtre, et jamais celui de l'accueil, qui montre dix titres par rangee.
 */
async function chargerCatalogue(force = false, { tout: toutEstVoulu = false } = {}) {
  if (catalogue.length && !force && (catalogueComplet || !toutEstVoulu)) return catalogue;
  // Deux écrans peuvent le demander en même temps (l'accueil et la grille) :
  // sans ce partage, les deux tireraient le catalogue entier, chacun de son
  // côté.
  if (chargementEnCours && !force) return chargementEnCours;

  chargementEnCours = (async () => {
    try {
      const premiere = await api('/api/catalogue?limite=300');
      catalogue = normaliserCatalogue(premiere?.oeuvres);
      catalogueComplet = !premiere?.suite;
      tracer('catalogue', `${catalogue.length}${premiere?.total ? ` sur ${premiere.total}` : ''} œuvres`);

      // ⚠️ L'ACCUEIL N'A PAS BESOIN DES DOUZE MILLE.
      //
      // Toji : « pourquoi ça met autant de temps à recharger quand je suis sur
      // l'accueil ? » Le catalogue a atteint son plafond — douze mille titres,
      // dix mégaoctets de JSON — et le rechargement les tirait TOUS : vingt-cinq
      // requêtes pour peindre quarante vignettes, en repeignant l'accueil entier
      // (quatre rangées, un tri sur la liste complète) après chacune.
      //
      // Ce qui suit ne part donc que si quelqu'un demande le catalogue ENTIER,
      // c'est-à-dire s'il ouvre la grille. L'accueil se contente de la première
      // page : trois cents titres, quand chaque rangée en montre dix.
      if (!toutEstVoulu) {
        tracer('catalogue', 'accueil servi avec la première page — la suite attendra la grille');
        return catalogue;
      }

      // La suite, page par page, sans bloquer ce qui vient d'être peint.
      let suite = premiere?.suite ?? null;
      // ⚠️ CE GARDE-FOU DOIT RESTER AU-DESSUS DU PLAFOND DU SERVEUR.
      //
      // Vingt pages de trois cents couvraient six mille œuvres, « bien au-delà
      // du plafond que l'amorçage s'impose » — ce qui était vrai quand ce
      // plafond valait deux mille. Il en vaut douze mille : à vingt pages, le
      // catalogue se serait arrêté à six mille sans que rien ne le dise, et
      // c'est exactement le genre de limite qu'on ne trouve qu'en descendant.
      // Cinquante pages laissent quinze mille titres, une marge sur le plafond.
      for (let page = 0; suite !== null && page < 50; page += 1) {
        const lot = await api(`/api/catalogue?depuis=${suite}&limite=500`);
        const neuves = normaliserCatalogue(lot?.oeuvres);
        if (!neuves.length) break;
        catalogue = [...catalogue, ...neuves];
        suite = lot?.suite ?? null;
        tracer('catalogue', `${catalogue.length}${lot?.total ? ` sur ${lot.total}` : ''} œuvres`);
        // L'écran se remplit à mesure, mais PAS à chaque page : repeindre quatre
        // rangées trie la liste entière, et le faire vingt-quatre fois de suite
        // sur douze mille titres bloque la page plus longtemps que le
        // téléchargement lui-même. Une fois par seconde suffit à voir la grille
        // grossir.
        const maintenant = Date.now();
        if (maintenant - dernierePeinture > 1000) {
          dernierePeinture = maintenant;
          try {
            if (!$('v-accueil')?.hidden) peindreAccueil();
            else if (!$('v-catalogue')?.hidden) peindreCatalogueOeuvres();
          } catch { /* pas encore prêt */ }
        }
      }
      catalogueComplet = suite === null;
      // La dernière page vient d'arriver : on peint une fois de plus, sinon la
      // limite d'une par seconde peut avoir mange la derniere.
      try {
        if (!$('v-accueil')?.hidden) peindreAccueil();
        else if (!$('v-catalogue')?.hidden) peindreCatalogueOeuvres();
      } catch { /* pas encore prêt */ }
    } catch (err) {
      // Une grille vide se voit et se dit (« Le catalogue est injoignable »).
      // Une exception, elle, emporterait l'écran entier.
      tracer('catalogue', 'injoignable', err.message);
    } finally {
      chargementEnCours = null;
    }
    return catalogue;
  })();

  return chargementEnCours;
}

// Ce que le compte a commencé et pas fini. Rangé ici plutôt que redemandé à
// chaque peinture : la rangée se repeint à chaque retour à l'accueil, et
// l'appel, lui, n'a de raison de repartir qu'au changement de lecture.
let reprises = [];
// Épisodes vus par œuvre (`{ "S1E4": horodatage }`), même pour une œuvre masquée de la rangée.
let vusServeur = {};

/** Va chercher « Reprendre ». Silencieux : une rangée absente n'est pas une panne. */
// ⚠️ « REPRENDRE » DOIT ETRE LA A L'ARRIVEE, PAS UNE MINUTE APRES.
//
// Toji : « il disparaît pendant un temps et il apparaît au bout de quelques
// minutes. Je ne suis pas fan de cette idée. Je veux vraiment qu'il soit déjà
// présent. »
//
// La rangee attendait un aller-retour reseau — apres celui du catalogue — pour
// se peindre. Sur un ecran qui s'ouvre en un dixieme de seconde, cela se voit :
// l'accueil apparait sans elle, puis elle s'insere et pousse tout le reste vers
// le bas.
//
// Ce que le navigateur a vu la derniere fois est donc garde ici. Ce n'est pas
// un cache de donnees — le serveur reste seul juge — c'est de quoi PEINDRE tout
// de suite, en attendant sa reponse, qui remplacera l'affichage sans que
// personne ne l'ait vu vide.
const CLE_REPRISES = 'tsudoi.reprises';

function reprisesDeLaDerniereFois() {
  try {
    const brut = JSON.parse(localStorage.getItem(CLE_REPRISES) ?? '[]');
    return Array.isArray(brut) ? normaliserReprises(brut, catalogue) : [];
  } catch {
    return [];
  }
}

async function chargerReprises() {
  try {
    const r = await api('/api/reprendre');
    reprises = normaliserReprises(r?.entrees, catalogue);
    vusServeur = r?.vus && typeof r.vus === 'object' ? r.vus : {};
    try { localStorage.setItem(CLE_REPRISES, JSON.stringify(r?.entrees ?? [])); } catch { /* confort */ }
  } catch (err) {
    // ⚠️ On ne VIDE pas la rangee sur un echec reseau : ce qu'on a regarde n'a
    // pas disparu parce que la requete a rate, et l'effacer sous les yeux
    // donnerait a croire le contraire. La memoire du navigateur sert ICI, et
    // seulement ici — en repli, pas en avance.
    if (!reprises.length) reprises = reprisesDeLaDerniereFois();
    tracer('reprendre', 'indisponible', err.message);
  }
  return reprises;
}

// ---------------------------------------------------------------------------
// LES IMAGES : LA BONNE TAILLE, ET LA BONNE PRIORITE
//
// Toji : « quand on reload l'interface, je veux vraiment qu'on voie directement
// toutes les images chargées. Je veux pas avoir des images noires, puis qu'elles
// pop d'un coup. Ça se voit qu'en fait c'est mal foutu. »
//
// TROIS DEFAUTS MESURES DANS LE CODE, ET C'EST LEUR ADDITION QUI SE VOIT.
//
//   1. ON DEMANDAIT L'AFFICHE EN PLEINE RESOLUTION POUR UNE VIGNETTE DE 172 px.
//      Cote serveur, `hauteResolution()` reecrit toute adresse TMDB en
//      `/t/p/original/`, et `meilleureAffiche()` va chercher la PLUS GRANDE des
//      affiches du titre — « souvent 2000x3000 », dit son propre commentaire.
//      Une carte de la grille fait 124 a 172 points de large : on telechargeait
//      donc, au mieux, onze fois plus de pixels que l'ecran n'en pose. Quatorze
//      lignes montees, c'est une centaine de cartes dans ce regime.
//
//   2. TOUTES CES IMAGES PARTAIENT EN `fetchpriority="high"`. La grille monte
//      quatorze lignes — deux visibles, douze en reserve — et `glisserLaFenetre`
//      les fabriquait TOUTES avec `avance: true`, donc toutes prioritaires.
//      Une priorite que tout le monde a n'est plus une priorite : les douze
//      lignes qu'on ne regarde pas se disputaient la bande passante avec les
//      deux qu'on regarde. D'ou l'effet decrit — tout reste noir, puis tout
//      apparait ensemble quand le lot entier finit par arriver.
//
//   3. `decoding="async"` sur les cartes visibles. Il dit au navigateur : peins
//      la frame SANS l'image, tu la poseras plus tard. C'est exactement le
//      « pop » : meme une image deja dans le cache HTTP arrivait apres coup.
//
// Ce qui suit corrige les trois, avec des fonctions PURES — des chaines
// entrent, des chaines sortent — pour que `essais/images.mjs` puisse les
// eprouver sans navigateur.
// ---------------------------------------------------------------------------

/** Une vignette d'épisode assez grande pour une carte paysage, sinon null. */
export function vignetteNette(adresse) {
  if (!adresse) return null;
  return /thetvdb\.com(%2F|\/)banners(%2F|\/)episodes(%2F|\/)/i.test(adresse) ? null : adresse;
}

// La largeur d'une carte, en points CSS : `--st-aff` vaut
// `clamp(124px, 13vw, 172px)`. On prend le haut de la fourchette — une carte
// plus etroite se contentera tres bien d'une image un peu large, l'inverse
// serait flou.
const LARGEUR_CARTE = 172;

/** L'affiche d'une carte, taillee pour la carte. */
export function afficheDeCarte(adresse, dpr = 1) {
  // Six cartes de front (« Séries par ligne ») en font de bien plus larges que
  // 172 px : l'image suit la largeur réelle, jamais en dessous du palier d'avant.
  const reelle = Math.round((Number(globalThis.innerWidth) || 0) / cartesParRangee());
  return imageDeTaille(adresse, Math.max(LARGEUR_CARTE, reelle) * densiteUtile(dpr), 'affiche');
}

/**
 * Le fond d'une banniere, dans le plus grand palier qui serve encore a quelque
 * chose.
 *
 * Toji : « les fonds au niveau des séries, films, etc. Je t'avais demandé de
 * mettre une image de plus grande taille. »
 *
 * ⚠️ LA DENSITE N'EST PAS PRISE EN COMPTE ICI, ET C'EST VOULU. Le fond est
 * agrandi de 4 %, desature, assombri de 28 % et recouvert de deux degrades
 * (`.st-hero-fond` + `.st-hero-voile`) : personne n'en lit les pixels. Le
 * doubler pour un ecran a deux points par pixel couterait deux megaoctets pour
 * une difference que la feuille de style efface elle-meme.
 */
export function fondDeBanniere(adresse, largeurEcran = 1280) {
  return imageDeTaille(adresse, Math.max(1280, Number(largeurEcran) || 0), 'fond');
}

/**
 * L'adresse du fond d'une oeuvre, dans l'ordre ou on la cherche.
 *
 * ⚠️ LA PORTE EST OUVERTE POUR LE FOND D'ARC. Un autre chantier pose, cote
 * serveur, un fond propre a l'arc qu'on regarde. Le jour ou la fiche le
 * portera, il se substituera ici SANS que rien d'autre ne bouge : c'est le
 * premier champ lu, et il est deja lu.
 */
export function fondDeLOeuvre(o) {
  return o?.fondArc || o?.fond || o?.affiche || '';
}

/**
 * Les deux couches d'un fond de banniere, dans l'ordre CSS.
 *
 * ⚠️ « PLUS GRAND » NE DOIT PAS VOULOIR DIRE « PLUS TARD ».
 *
 * Un fond en 1920 px pese quelques centaines de kilo-octets : entre le clic et
 * son arrivee, la banniere resterait noire, puis le fond « poperait » — le
 * defaut meme qu'on corrige ailleurs, reintroduit ici par le remede.
 *
 * `background-image` accepte PLUSIEURS couches, et peint la premiere par-dessus
 * les suivantes. On pose donc le grand devant et le petit derriere : tant que
 * le grand n'est pas la, c'est le petit qu'on voit — deja cadre, de la bonne
 * couleur, et arrive bien avant lui. Le grand le recouvre ensuite, sans
 * transition a jouer et sans mesure a refaire. Et si le grand palier n'existe
 * pas chez ce fournisseur, son 404 ne laisse aucun trou : le petit est dessous.
 *
 * CE QUE CELA COUTE : un second telechargement, celui du petit — une centaine
 * de kilo-octets. C'est le prix d'une banniere qui n'est jamais noire, et il se
 * paie une fois : les deux adresses sont servies par `/api/img` en
 * `immutable`, donc jamais redemandees. Pour l'ecran d'attente d'un episode, le
 * petit est meme deja dans le cache — c'est la vignette de la grille.
 *
 * Rendue a part, et pure, pour que le banc puisse verifier l'ordre des couches
 * — l'inverser rendrait le remede invisible.
 */
export function couchesDeFond(adresse, largeurEcran = 1280) {
  if (!adresse) return [];
  const grand = fondDeBanniere(adresse, largeurEcran);
  return grand === adresse ? [adresse] : [grand, adresse];
}

/** Une adresse posee dans une valeur CSS `url("…")`, sans pouvoir en sortir. */
const urlCss = (a) => `url("${String(a).replace(/["\\]/g, '\\$&')}")`;

/** Pose le fond d'une banniere : le grand palier devant, le petit derriere. */
function poserFondDeBanniere(el, adresse) {
  if (!el) return;
  const couches = couchesDeFond(adresse, window.innerWidth || 1280);
  if (el.tagName === 'IMG') {
    // Un `<img>` ne sait pas empiler deux sources. On pose ce qu'on a tout de
    // suite, et on n'echange que lorsque le grand est REELLEMENT pret : mettre
    // `src` sur le grand d'emblee viderait la balise pendant son chargement.
    const [grand, petit] = couches.length > 1 ? couches : [couches[0], couches[0]];
    if (!couches.length) { el.removeAttribute('src'); return; }
    if (el.dataset.fondVoulu === grand) return;
    el.dataset.fondVoulu = grand;
    el.src = petit;
    if (grand === petit) return;
    const avance = new Image();
    avance.decoding = 'async';
    // La fiche a pu changer entre-temps : on ne remplace que si c'est toujours
    // ce fond-la qu'on attend.
    avance.onload = () => { if (el.dataset.fondVoulu === grand) el.src = grand; };
    avance.src = grand;
    return;
  }
  const voulu = couches.length ? couches.map(urlCss).join(', ') : 'none';
  // Reposer la meme valeur relancerait le chargement dans certains navigateurs :
  // la fiche se repeint a chaque changement d'arc, ce n'est pas rare.
  if (el.style.backgroundImage !== voulu) el.style.backgroundImage = voulu;
}

/**
 * De combien d'avance une rangee doit-elle etre peinte, en pixels.
 *
 * Toji : « voir tout, c'est surtout, en fonction du viewport, afficher de facon
 * a ce qu'on voie tous les elements, avec la marge de gestion. »
 *
 * ⚠️ C'ETAIT 400 px POUR L'OBSERVATEUR, ET 200 px POUR LE PREMIER RENDU. Moins
 * d'un quart d'ecran : un coup de molette le mangeait avant que l'observateur
 * n'ait rendu la main, et l'on tombait sur une rangee vide qui se remplissait
 * sous les yeux.
 *
 * Pourquoi une fraction du viewport et non « six lignes » comme la grille : une
 * rangee d'accueil, ce sont DIX cartes. Six rangees d'avance, ce serait sonner
 * soixante images avant que personne n'ait defile. Une fois et demie la hauteur
 * de l'ecran donne trois a quatre rangees de reserve — de quoi absorber un
 * defilement franc — pour le cout d'un ecran et demi.
 */
export function avanceDeChargement(hauteurVue = 800) {
  const h = Number(hauteurVue) || 800;
  return Math.round(Math.min(2400, Math.max(600, h * 1.5)));
}

/**
 * Les attributs de chargement d'une image de carte.
 *
 * Trois regimes, et c'est la distinction qui manquait :
 *
 *   · `dans` — la carte est a l'ecran. Elle est prioritaire, et son image est
 *     decodee AVEC la frame (`decoding="sync"`) : c'est la seule facon qu'une
 *     image deja en cache soit peinte du premier coup au lieu d'apparaitre une
 *     frame plus tard. Le cout est reel mais borne — une vingtaine de vignettes
 *     de 342 px, pas une centaine d'affiches 2000x3000.
 *
 *   · `reserve` — la carte est montee d'avance, six lignes plus bas. Elle part
 *     TOUT DE SUITE (`eager`), parce que c'est exactement ce que Toji demande
 *     — « toutes les icônes, quand elles apparaissent, elles doivent être déjà
 *     chargées 6 lignes en dessous » — mais en priorite BASSE, pour ne pas
 *     retarder celles qu'on regarde. `loading="lazy"` ne conviendrait pas :
 *     le seuil natif du navigateur est de l'ordre de 1 250 px, soit moins de
 *     six lignes, et la reserve ne serait donc pas chargee.
 *
 *   · `plus tard` — la carte n'est ni vue ni en reserve (une rangee d'accueil
 *     encore loin). Le navigateur decide, `lazy`.
 */
// ⚠️ UNE IMAGE SE MONTRE UNE FOIS DÉCODÉE (voir `.prete` dans la feuille).
// Un seul écouteur, en capture sur le document : `load` ne remonte pas, mais
// se capture — et il vaut pour toutes les cartes, qu'elles soient posées par
// `innerHTML`, un gabarit ou la fenêtre glissante de la grille.
globalThis.document?.addEventListener?.('load', (e) => {
  const img = e.target;
  if (img?.tagName !== 'IMG' || !img.closest('.st-carte-img, .cadre-img, .film .vis')) return;
  (img.decode ? img.decode() : Promise.resolve()).catch(() => {}).then(() => img.classList.add('prete'));
}, true);

export function chargementDImage(regime) {
  if (regime === 'dans') return { loading: 'eager', fetchpriority: 'high', decoding: 'sync' };
  if (regime === 'reserve') return { loading: 'eager', fetchpriority: 'low', decoding: 'async' };
  return { loading: 'lazy', fetchpriority: 'auto', decoding: 'async' };
}

// ---------------------------------------------------------------------------
// LA MARQUE POSEE SUR UNE AFFICHE — « UN TAG EN HAUT A GAUCHE »
//
// Toji, 13/09 : « on peut par exemple noter qu'on l'a déjà vu, et comme ça on a
// un tag en haut à gauche, peut-être avec le "noté vu" ». Puis, sur le visuel :
// « juste "tu as vu", genre ou un œil barré, tu vois ? »
//
// C'EST L'ŒIL BARRÉ, ET EN SVG POSÉ DANS LA PAGE. Un emoji aurait été plus
// court à écrire, mais il change de dessin d'une plateforme à l'autre — et sa
// boîte change avec lui, donc l'alignement en haut à gauche de l'affiche aussi.
// Un tracé écrit ici fait le même trait partout, prend la couleur du texte, et
// se met à l'échelle sans se troubler.
//
// ⚠️ DEUX ÉTATS, DEUX DESSINS — SINON ILS SE CONFONDENT. « Je l'ai déjà vu » et
// « je l'ai gardé pour plus tard » sont deux choses opposées : la même pastille
// pour les deux ferait lire « vu » sur une pile à regarder. Le signet est aussi
// plus discret que l'œil — on garde beaucoup, on marque vu rarement, et une
// grille entière de pastilles vives ne se lit plus.
//
// ⚠️ LE TITRE EST DIT EN TOUTES LETTRES. Une icône seule se devine ; « Déjà vu »
// écrit dans `title` et dans `aria-label` se lit — à la souris comme au lecteur
// d'écran. C'est ce que Toji demandait derrière « peut-être avec le "noté vu" ».
// ---------------------------------------------------------------------------

// Les deux tracés, en coordonnees 24x24. L'œil est BRISÉ la où la barre le
// traverse : une barre posée par-dessus un œil entier fait une bouillie de
// trois traits à 14 px, alors que l'interruption se lit d'un coup.
const MARQUES = {
  'oeil-barre': '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
    + '<path d="M10.6 5.9A9.6 9.6 0 0 1 12 5.8c5.7 0 9.8 6.2 9.8 6.2a17 17 0 0 1-2.5 3.1" />'
    + '<path d="M16.3 16.5A9.8 9.8 0 0 1 12 18.2C6.3 18.2 2.2 12 2.2 12a17.6 17.6 0 0 1 4.4-4.6" />'
    + '<path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />'
    + '<path d="M3 3 21 21" /></svg>',
  signet: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
    + '<path d="M7 3h10a1 1 0 0 1 1 1v17l-6-4.4L6 21V4a1 1 0 0 1 1-1Z" /></svg>',
};

/**
 * Ce qu'une affiche porte en haut a gauche, ou rien.
 *
 * PURE, et c'est voulu : le banc `essais/menu-oeuvre.mjs` l'éprouve sans
 * navigateur. Elle ne reçoit que l'ENTRÉE de bibliothèque du titre — telle que
 * `GET /api/bibliotheque` la rend — parce que c'est la seule chose qui décide.
 *
 * ⚠️ UN TITRE INCONNU NE PORTE RIEN. `undefined` (pas dans la bibliothèque),
 * `null`, un tableau, une chaîne : tout ce qui n'est pas une entrée rend `null`.
 * Un état malformé qui jetterait ici ferait tomber la peinture de TOUTE la
 * grille — une carte est fabriquée par cette fonction, pas décorée après coup.
 */
export function badgeDeLOeuvre(etat) {
  if (!etat || typeof etat !== 'object' || Array.isArray(etat)) return null;
  // ⚠️ `=== true` ET PAS UNE VÉRITÉ MOLLE. Une entrée d'avant le 13/09 n'a pas
  // le champ : `undefined` doit valoir « pas vu », jamais « on ne sait pas ».
  if (etat.vu === true) return { marque: 'oeil-barre', titre: 'Déjà vu' };
  // Présent dans la bibliothèque, mais pas encore vu : le signet, plus discret.
  return { marque: 'signet', titre: 'Dans ma bibliothèque' };
}

/** L'entrée de bibliothèque d'un titre, ou `null` s'il n'y est pas. */
function etatDuTitre(oeuvreId) {
  return maBiblio.get(String(oeuvreId ?? '')) ?? null;
}

/**
 * Cet épisode est-il marqué vu ? Lit la bibliothèque locale.
 *
 * Les règles elles-mêmes vivent dans `episodes-vus.js`, sans DOM : c'est ce qui
 * les rend éprouvables sans monter une page.
 */
function episodeVuIci(oeuvreId, saison, episode) {
  return episodeEstVu(maBiblio.get(String(oeuvreId ?? '')), saison, episode);
}

/**
 * Marque ou démarque un épisode, et repeint sa ligne.
 *
 * ⚠️ LA LIGNE SE REPEINT AVANT L'ALLER-RETOUR. Un clic droit qui ne montre rien
 * pendant une seconde se lit comme un clic perdu, et on recommence — ce qui
 * annule ce qu'on venait de faire. Si le serveur refuse, `poserSurLeTitre`
 * remet l'état d'avant et le dit.
 */
async function basculerEpisodeVu(ep) {
  const marque = marqueDEpisode(ep.saison, ep.episode);
  const avant = etatDuTitre(ep.oeuvreId);
  const eps = listeApresBascule(avant?.eps, marque);
  // L'entrée locale d'abord, pour que la ligne change tout de suite.
  maBiblio.set(String(ep.oeuvreId), { ...(avant ?? { oeuvreId: ep.oeuvreId }), eps });
  marquerLaLigneDEpisode(ep.ligne, eps.includes(marque));
  await poserSurLeTitre(ep.oeuvreId, { eps }, oeuvre);
}

/**
 * Pose ou retire la marque « vu » sur une ligne d'épisode.
 *
 * Un attribut plutôt qu'une classe : le CSS le lit aussi bien, et un attribut
 * de données se voit dans l'inspecteur sans avoir à connaître le nom de la
 * classe qu'on cherche.
 */
function marquerLaLigneDEpisode(ligne, vu) {
  if (!ligne) return;
  if (vu) ligne.dataset.vu = '1';
  else delete ligne.dataset.vu;
}

/** Le balisage de la marque d'une carte — chaîne vide s'il n'y en a pas. */
function marqueDeCarte(oeuvreId) {
  const badge = badgeDeLOeuvre(etatDuTitre(oeuvreId));
  const dessin = badge && MARQUES[badge.marque];
  if (!dessin) return '';
  return `<span class="st-marque" data-marque="${esc(badge.marque)}" role="img"`
    + ` title="${esc(badge.titre)}" aria-label="${esc(badge.titre)}">${dessin}</span>`;
}

/**
 * Les pastilles posées DANS l'affiche (étude UI du 22/10, point 4) : « Nouvel
 * épisode » / « Nouvelle saison » quand la reprise de ce titre est revenue
 * d'elle-même, « Vu » (avec un voile) quand le titre est marqué vu. Lues sur
 * l'affiche sans ouvrir la fiche — sur TOUTES les cartes du titre, pas
 * seulement « Reprendre ». Chaîne vide s'il n'y a rien à dire.
 */
function pastillesDeCarte(oeuvreId) {
  const neuf = pastilleDeReprise(reprises.find((r) => r.oeuvreId === oeuvreId));
  const vu = badgeDeLOeuvre(etatDuTitre(oeuvreId))?.marque === 'oeil-barre';
  if (!neuf && !vu) return '';
  return `${vu ? '<span class="st-voile" aria-hidden="true"></span>' : ''}<span class="st-pastilles">${
    neuf ? `<span class="st-pastille st-pastille--neuf">${neuf}</span>` : ''}${
    vu ? '<span class="st-pastille st-pastille--vu">Vu</span>' : ''}</span>`;
}

/**
 * Ce que le survol d'une affiche révèle (point 3) : année, nombre d'épisodes ou
 * « Film », drapeaux de langue — SEULEMENT ce que la page sait déjà (la fiche
 * déjà ouverte, `fichesEnMemoire`) : un survol ne déclenche aucun appel.
 */
function infosDeSurvol(o) {
  const brut = fichesEnMemoire.get(o.id)?.fiche;
  const eps = o.episodes?.length ? o.episodes : (brut?.saisons ?? []).flatMap((s) => s?.episodes ?? []);
  const n = eps.length;
  return [
    o.annee ? esc(String(o.annee)) : '',
    estUnFilm(o) ? 'Film' : (n ? `${n} ${pluriel(n, 'épisode')}` : ''),
    drapeauxDe(eps.flatMap((e) => e?.langues ?? []), { type: o.type }),
  ].filter(Boolean).join('<i aria-hidden="true">·</i>');
}

/**
 * Remet la marque de ce titre a jour PARTOUT ou il est affiche, tout de suite.
 *
 * ⚠️ SANS ELLE, IL FAUDRAIT REPEINDRE LA GRILLE POUR VOIR SON PROPRE GESTE.
 *
 * Le même titre peut être à l'écran plusieurs fois — dans « Ma bibliothèque »,
 * dans un rayon, et dans les résultats de recherche en même temps. On les
 * reprend donc toutes, pas seulement celle qu'on a cliquée : une carte qui
 * garde l'ancienne marque pendant que sa jumelle porte la nouvelle est
 * exactement le genre de désaccord qui fait douter du reste.
 */
function rafraichirMarques(oeuvreId) {
  const id = String(oeuvreId ?? '');
  if (!id) return;
  const html = marqueDeCarte(id);
  // ⚠️ PAS DE SELECTEUR FABRIQUE AVEC L'IDENTIFIANT. Un identifiant porteur de
  // guillemets casserait `querySelectorAll` et la peinture avec — on compare
  // la valeur, ce qui ne peut pas casser.
  for (const carte of document.querySelectorAll('.st-carte[data-oeuvre]')) {
    if (carte.dataset.oeuvre !== id) continue;
    const visuel = carte.querySelector('.st-carte-img');
    if (!visuel) continue;
    visuel.querySelector('.st-marque')?.remove();
    if (html) visuel.insertAdjacentHTML('afterbegin', html);
    // Le voile et la pastille « Vu » suivent la même marque, au même geste.
    visuel.querySelector('.st-voile')?.remove();
    visuel.querySelector('.st-pastilles')?.remove();
    visuel.insertAdjacentHTML('beforeend', pastillesDeCarte(id));
  }
  // La fiche ouverte porte le même état sur son cœur et sa cloche : la laisser
  // en retard ferait mentir deux écrans l'un sur l'autre.
  if (oeuvre?.id === id) {
    try { peindreBoutonsDeBiblio(oeuvre); } catch (err) { tracer('biblio', 'fiche non repeinte', err.message); }
  }
}

/**
 * Une carte d'œuvre.
 *
 * Le visuel d'abord, le titre en clair DESSOUS et non par-dessus : c'est la
 * leçon déjà tirée des vignettes d'épisodes (voir `vignetteEpisode`), où trois
 * informations superposées sur six centimètres ne se lisaient plus au doigt.
 *
 * `data-oeuvre` porte l'identifiant : le branchement des clics se fait ensuite
 * sur toute la grille d'un coup, sans refaire le tour des cartes une à une.
 *
 * ⚠️ `avance` N'EST PLUS UN OUI OU NON. C'etait un booleen qui valait « eager +
 * fetchpriority high », et `glisserLaFenetre` le posait sur les quatorze lignes
 * montees — voir le pave « LES IMAGES » ci-dessus. Il porte maintenant le
 * REGIME de chargement : `'dans'` (a l'ecran), `'reserve'` (monte d'avance),
 * ou rien du tout. `true` reste accepte et vaut `'dans'`, pour que les appels
 * qui n'ont pas de fenetre a consulter continuent de dire ce qu'ils voulaient
 * dire.
 *
 * `dpr` : la densite de l'ecran. Elle decide du palier d'image demande, et elle
 * est passee explicitement pour que la fonction reste pure — donc eprouvable.
 */
export function carteOeuvre(o, {
  sous = '', part = 0, avance = false, retirable = false, dpr = 1, paysage = false,
} = {}) {
  // Le seul point de passage de toutes les vignettes du produit — accueil,
  // grille, recherche, bibliothèque, reprises. Retenir ici, et nulle part
  // ailleurs, garantit qu'aucun écran n'oublie de le faire.
  retenirOeuvreVue(o);
  // Paysage (« Reprendre ») : une image large (vignette d'épisode, fond), deux
  // fois plus large qu'une affiche — la rangée en montre deux fois moins.
  const visuel = paysage
    ? imageDeTaille(o.affiche || o.fond || '', Math.max(2 * LARGEUR_CARTE, Math.round((Number(globalThis.innerWidth) || 0) * 2 / cartesParRangee())) * densiteUtile(dpr), 'fond')
    : afficheDeCarte(o.affiche || o.fond || '', dpr);
  const detail = sous || [
    typeLisible(o),
    o.annee ? String(o.annee) : '',
  ].filter(Boolean).join(' · ');
  const chargement = chargementDImage(avance === true ? 'dans' : avance);
  // Le bloc de survol n'a sa place que sur une affiche : « Reprendre » (paysage)
  // porte déjà sa barre et sa pastille à cet endroit.
  const survol = paysage ? '' : infosDeSurvol(o);
  return `<button class="st-carte${retirable ? ' st-carte--deux' : ''}${paysage ? ' st-carte--paysage' : ''}" type="button" data-oeuvre="${esc(o.id)}" title="${esc(o.titre)}">
    <span class="st-carte-img">${visuel ? `<img src="${esc(visuel)}" alt="" width="${paysage ? 2 * LARGEUR_CARTE : LARGEUR_CARTE}" height="${Math.round(paysage ? LARGEUR_CARTE * 9 / 8 : LARGEUR_CARTE * 1.5)}" decoding="${chargement.decoding}" loading="${chargement.loading}" fetchpriority="${chargement.fetchpriority}" />` : ''}
      ${marqueDeCarte(o.id)}
      ${pastillesDeCarte(o.id)}
      ${survol ? `<span class="st-survol" aria-hidden="true">${survol}</span>` : ''}
      ${part > 0 ? `<span class="st-jauge"><span class="st-jauge-barre" style="width:${Math.round(part * 100)}%"></span></span>` : ''}
      ${retirable ? `<span class="st-carte-croix" role="button" tabindex="0" data-retirer-reprise="${esc(o.id)}" title="Retirer de « Reprendre »" aria-label="Retirer ${esc(o.titre)} de la reprise">✕</span>` : ''}
    </span>
    <span class="st-carte-titre">${esc(o.titre)}</span>
    <span class="st-carte-sous">${esc(detail)}</span>
    ${o.note && !retirable ? `<span class="st-carte-note">${esc(String(o.note).replace('.', ','))}</span>` : ''}
  </button>`;
}

/** Branche les cartes d'une zone. Un seul écouteur poserait le même geste. */
function brancherCartes(zone, surChoix, surTexte = null) {
  if (!zone) return;
  for (const el of zone.querySelectorAll('[data-oeuvre]')) {
    el.onclick = (e) => {
      // ⚠️ DEUX ZONES, DEUX INTENTIONS.
      //
      // Toji : « quand on clique sur l'image, ça va lancer directement
      // l'épisode ; par contre, quand on clique sur le texte en dessous, ça va
      // mettre le choix d'épisode. »
      //
      // Sur « Reprendre », les deux gestes sont légitimes et opposés : reprendre
      // où l'on s'est arrêté, ou aller voir la liste des épisodes. Une carte qui
      // n'en propose qu'un force à passer par la fiche pour l'autre — ou pire,
      // relance une lecture quand on voulait juste regarder ce qui suit.
      //
      // L'affiche garde l'action principale : c'est elle qu'on vise, et c'est le
      // geste le plus fréquent.
      // ⚠️ TOUT CE QUI N'EST PAS L'AFFICHE MENE AU CHOIX D'EPISODE.
      //
      // Toji : « c'est pas uniquement le titre qui redirige : c'est aussi S1,
      // épisode 1. Les deux, dans tous les cas, ça redirige sur le choix de
      // l'épisode. » Viser les deux lignes une par une laissait des trous — la
      // gouttiere entre elles, la marge sous la derniere — ou le clic
      // retombait sur le bouton et lancait la lecture. La regle se dit dans
      // l'autre sens : l'affiche reprend, le reste choisit.
      if (surTexte && !e.target.closest('.st-carte-img')) {
        surTexte(el.dataset.oeuvre, el);
        return;
      }
      surChoix(el.dataset.oeuvre, el);
    };
  }
}

/**
 * Une rangée de l'accueil : un titre, une liste qui défile de côté.
 *
 * Une rangée VIDE disparaît, elle ne s'affiche pas vide. « Reprendre » sans
 * rien dedans annonce une reprise qui n'existe pas, et c'est la même faute que
 * le drapeau français posé sur un épisode qui n'a pas de VF.
 */
// ---------------------------------------------------------------------------
// CE QU'UNE RANGÉE PEINT : CE QUI TIENT À L'ÉCRAN, ET RIEN DE PLUS.
//
// Toji : « tu mets, en fonction du viewport, les éléments qui sont visibles
// directement — là par exemple les dix premiers pour les films en 1920 × 1080 ».
//
// Une rangée qui porte cent soixante-huit cartes construit cent soixante-huit
// boutons et autant de balises d'image, pour n'en montrer que dix : c'est du
// travail de rendu payé comptant, à l'ouverture, sur le chemin le plus visible
// de l'application. On mesure donc la place réelle et on peint ce qui s'y voit.
// Le reste n'est pas caché : il est derrière « Voir tout ».
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// DIX CARTES PAR RANGEE, PARTOUT.
//
// Toji : « en HD, en 2K ou en 4K, je veux que l'interface garde la proportion
// qu'on a définie : c'est 10 de largeur. Sur téléphone, techniquement tu peux
// en mettre deux, mais après il y a un slider, et tu mets sur 10. »
//
// Ce n'est donc pas un calcul, c'est une PROPORTION. Le nombre ne dépend plus
// de la largeur : dix cartes sont peintes, et c'est la feuille de style qui
// décide de ce qu'on en voit — les dix côte à côte sur un écran, deux et un
// glissement du doigt sur un téléphone.
//
// C'est aussi ce qui a fait disparaître la barre de défilement sur ordinateur :
// il n'y a plus rien derrière le bord, donc plus rien à faire défiler. Le doigt
// fait glisser une rangée naturellement ; la souris, non.
//
// ⚠️ DEPUIS LE 22/10, LE NOMBRE EST UN RÉGLAGE (« Séries par ligne » : 6, 10,
// 12). Une clé pour le site, une pour l'activité Discord : chacun garde son
// choix. La proportion reste une proportion : la feuille lit
// `--series-par-ligne`, la rangée peint autant de cartes.
// ⚠️ 10 PAR DÉFAUT PARTOUT (Toji 22/10) : 6 sur le site donnait des cartes de
// 290 px au lieu de 167 à 1920 px — « t'as tout agrandi ». Clé à part de
// `par-ligne` (les épisodes) : jamais lue ici.
// ---------------------------------------------------------------------------
const CHOIX_PAR_LIGNE = ['6', '10', '12'];
const CLE_PAR_LIGNE = DANS_DISCORD ? 'series-par-ligne-discord' : 'series-par-ligne-site';
const DEFAUT_PAR_LIGNE = '10';
function cartesParRangee() {
  const v = prefs.lire(CLE_PAR_LIGNE, DEFAUT_PAR_LIGNE);
  return Number(CHOIX_PAR_LIGNE.includes(v) ? v : DEFAUT_PAR_LIGNE);
}
function appliquerSeriesParLigne() {
  document.documentElement.style.setProperty('--series-par-ligne', String(cartesParRangee()));
}
appliquerSeriesParLigne();


// ---------------------------------------------------------------------------
// QUAND UN TITRE REMONTE EN TETE, ON DOIT LE VOIR ARRIVER
//
// Toji : « imaginons qu'il y ait un episode qui vient de sortir pour tel anime
// (…) des qu'on a l'info qu'il est dispo, bam, on le remet a la premiere ligne.
// Il apparait de la gauche, et apres il y a toute la premiere ligne qui se
// decale vers la droite. Le dernier, il est remis a la ligne, il est cache. »
//
// C'est un FLIP, la seule technique qui donne ce mouvement sans faire sauter la
// page : on releve la position de chaque carte AVANT de repeindre, on repeint,
// on remet chaque carte a son ancienne place par `transform` — donc rien n'a
// bouge a l'ecran — et on relache. Le navigateur anime alors le retour, sur le
// compositeur, sans recalculer la mise en page une seule fois.
//
// ⚠️ JAMAIS AU PREMIER RENDU. Sans cartes precedentes il n'y a rien a comparer,
// et animer l'arrivee de toute une rangee ferait un feu d'artifice a chaque
// ouverture de l'accueil. Le mouvement doit dire « ca vient de changer » : il
// perd tout son sens s'il se produit aussi quand rien n'a change.
// ---------------------------------------------------------------------------
const GLISSEMENT_MS = 420;

/** Ou se trouve chaque carte, avant que la rangee ne se repeigne. */
function positionsDesCartes(bloc) {
  const vues = new Map();
  for (const carte of bloc.querySelectorAll('.st-rangee-liste > .st-carte')) {
    const id = carte.dataset.oeuvre;
    if (id) vues.set(id, rectPage(carte).left);
  }
  return vues;
}

/** Ramene chaque carte de son ancienne place a la nouvelle, en glissant. */
function glisserLesCartes(bloc, avant) {
  if (!avant?.size) return;
  // Un mouvement qu'on n'a pas demande, c'est un mouvement qui gene : le
  // systeme sait le dire, et on l'ecoute.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

  const mouvements = [];
  for (const carte of bloc.querySelectorAll('.st-rangee-liste > .st-carte')) {
    const id = carte.dataset.oeuvre;
    if (!id) continue;
    const ou = rectPage(carte).left;
    const ancienne = avant.get(id);
    if (ancienne === undefined) {
      // Elle n'etait pas la : elle entre par la gauche, en apparaissant.
      mouvements.push({ carte, dx: -(carte.offsetWidth + 40), neuve: true });
    } else if (Math.abs(ancienne - ou) > 1) {
      mouvements.push({ carte, dx: ancienne - ou, neuve: false });
    }
  }
  if (!mouvements.length) return;

  for (const { carte, dx, neuve } of mouvements) {
    carte.style.transition = 'none';
    carte.style.transform = `translateX(${Math.round(dx)}px)`;
    if (neuve) carte.style.opacity = '0';
  }
  // Deux battements : le premier pose la position de depart, le second la
  // relache. En un seul, le navigateur regrouperait les deux ecritures et
  // n'animerait rien du tout.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    for (const { carte, neuve } of mouvements) {
      carte.style.transition = `transform ${GLISSEMENT_MS}ms cubic-bezier(.22,.61,.36,1)`
        + (neuve ? `, opacity ${GLISSEMENT_MS}ms ease` : '');
      carte.style.transform = '';
      if (neuve) carte.style.opacity = '';
    }
    // Les styles en ligne repartent : la carte doit retrouver son agrandissement
    // au survol, qui passe lui aussi par `transform`.
    setTimeout(() => {
      for (const { carte } of mouvements) { carte.style.transition = ''; carte.style.transform = ''; }
    }, GLISSEMENT_MS + 80);
  }));
}

function peindreRangee(hote, id, titre, cartes, { tout = null, surCarte = null, surCroix = null, surTexte = null, paysage = false } = {}) {
  const bloc = zoneOuCreee(hote, id, { classe: 'st-rangee' });
  if (!bloc) return null;
  // `cartes` peut être une FONCTION : la rangée sous le pli ne fabrique alors
  // son HTML qu'au moment où elle approche de l'écran.
  const toutes = typeof cartes === 'function' ? null : cartes;
  if (toutes && !toutes.length) { bloc.hidden = true; bloc.innerHTML = ''; return bloc; }
  bloc.hidden = false;

  const peindre = () => {
    // Le REGIME de chargement des images de cette rangee, decide au moment ou
    // elle se peint et pas avant : une rangee peinte d'avance n'est pas encore
    // regardee, et ses vignettes ne doivent pas passer devant celles qu'on a
    // sous les yeux. Voir `chargementDImage`.
    const regime = estSousLesYeux(bloc) ? 'dans' : 'reserve';
    const liste = typeof cartes === 'function' ? cartes(regime) : cartes;
    if (!liste.length) { bloc.hidden = true; bloc.innerHTML = ''; return; }
    // Releve AVANT de tout remplacer : c'est la moitie du FLIP, et elle doit se
    // faire pendant que les anciennes cartes sont encore a l'ecran.
    const placesAvant = bloc.hidden ? null : positionsDesCartes(bloc);
    bloc.hidden = false;
    const html = `<p class="st-rangee-titre">
        <span>${esc(titre)}</span>
        ${tout ? '<button type="button" class="st-rangee-tout" data-tout="1">Voir tout</button>' : ''}
      </p>
      ${paysage
    // Paysage : la rangée défile, tout est peint ; `--tient` quand tout tient
    // sans couper (moitié moins de cartes que d'affiches), sinon la dernière
    // est coupée net pour dire qu'il y a une suite.
    ? `<div class="st-rangee-liste st-rangee-liste--paysage${liste.length <= cartesParRangee() / 2 ? ' st-rangee-liste--tient' : ''}">${liste.join('')}</div>`
    : `<div class="st-rangee-liste">${liste.slice(0, cartesParRangee()).join('')}</div>`}`;
    // ⚠️ REPEINDRE A L'IDENTIQUE FAISAIT CLIGNOTER TOUTE LA RANGEE.
    //
    // L'accueil se repeint a chaque ouverture et a chaque retour de fiche (plus
    // jamais sur horloge, depuis le 12/09), et `peindreAccueil` repose
    // `innerHTML` sur CHAQUE rangee — meme quand rien n'a bouge. Les dix
    // `<img>` etaient donc detruites et refabriquees : le
    // navigateur les reprend au cache, mais il doit les RE-DECODER, et cela se
    // voit — l'accueil scintille tout seul, sans que personne n'ait rien fait.
    //
    // Comparer la chaine coute une comparaison de chaines. C'est la mesure
    // exacte de « il ne s'est rien passe ».
    // ⚠️ `bloc.firstElementChild` FAIT PARTIE DE LA CONDITION. Une rangee videe
    // entre-temps — masquee dans les reglages, puis reaffichee — garde son
    // empreinte alors qu'elle n'a plus rien dedans : sans ce garde-fou, elle
    // resterait vide pour toujours.
    if (bloc.dataset.empreinte === html && bloc.firstElementChild) {
      // Les noeuds n'ont pas bouge, mais les fermetures branchees dessus, si :
      // `surCarte` peut venir d'un rendu precedent. On rebranche, on ne
      // reconstruit pas.
      if (surCarte) brancherCartes(bloc, surCarte, surTexte);
      return;
    }
    bloc.dataset.empreinte = html;
    bloc.innerHTML = html;
    const bouton = bloc.querySelector('[data-tout]');
    if (bouton && tout) bouton.onclick = tout;
    // ⚠️ LE BRANCHEMENT SUIT LE RENDU, IL NE LE PRÉCÈDE PAS. Une rangée peinte
    // plus tard — quand on descend jusqu'à elle — n'existait pas encore au
    // moment où l'accueil branchait ses cartes : ses vignettes seraient restées
    // muettes au clic.
    if (surCarte) brancherCartes(bloc, surCarte, surTexte);
    // ⚠️ LA CROIX EST DANS LA CARTE, ET LA CARTE EST UN BOUTON.
    //
    // Sans `stopPropagation`, cliquer la croix lancerait l'épisode qu'on est en
    // train de retirer — le clic remonte au bouton qui l'entoure. C'est aussi
    // pourquoi la croix est un `<span>` et non un `<button>` : un bouton dans un
    // bouton n'est pas du HTML valide, et les navigateurs le réparent chacun à
    // leur façon.
    if (surCroix) {
      for (const croix of bloc.querySelectorAll('[data-retirer-reprise]')) {
        const partir = (e) => {
          e.stopPropagation();
          e.preventDefault();
          surCroix(croix.dataset.retirerReprise);
        };
        croix.onclick = partir;
        croix.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') partir(e); };
      }
    }
    glisserLesCartes(bloc, placesAvant);
  };

  // La première rangée est déjà là : la peindre tout de suite, sans attendre un
  // battement de l'observateur — c'est elle qu'on voit en arrivant.
  if (estDejaVisible(bloc)) peindre();
  else differerJusquAuRegard(bloc, peindre);
  return bloc;
}

/**
 * Ce bloc est-il dans l'écran, ou assez près pour qu'on le peigne déjà ?
 *
 * ⚠️ C'ETAIT 200 px, ET C'EST LA MOITIE DE LA PANNE. Une rangee posee a
 * 210 px sous le bord n'etait pas peinte : elle attendait l'observateur, qui
 * lui-meme n'avait que 400 px d'avance. Un simple coup de molette arrivait donc
 * AVANT la peinture, et l'on voyait la rangee se fabriquer. Les deux seuils
 * viennent maintenant du meme endroit — `avanceDeChargement` — et se comptent
 * en fractions d'ecran, pas en pixels ecrits en dur.
 */
function estDejaVisible(el) {
  const r = el.getBoundingClientRect();
  const hauteur = window.innerHeight || 800;
  return r.top < hauteur + avanceDeChargement(hauteur);
}

/** Ce bloc est-il vraiment SOUS LES YEUX ? Ses images passent alors devant. */
function estSousLesYeux(el) {
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < (window.innerHeight || 800);
}

/** La densité de l'écran, telle que la carte doit la demander. */
const densiteEcran = () => densiteUtile(window.devicePixelRatio || 1);

// Un seul observateur pour toutes les rangées : en créer un par rangée les
// ferait tous vivre en parallèle pour la même question.
const regardeurDeRangees = ('IntersectionObserver' in window)
  ? new IntersectionObserver((entrees, obs) => {
    for (const e of entrees) {
      if (!e.isIntersecting) continue;
      obs.unobserve(e.target);
      // ⚠️ APRÈS le rendu du navigateur, pas pendant le défilement : peindre
      // dans le fil du scroll fait sauter l'image. `requestAnimationFrame` rend
      // la main d'abord, et la rangée apparaît sans à-coup.
      requestAnimationFrame(() => e.target.__peindre?.());
    }
    // ⚠️ L'AVANCE SE COMPTE EN ECRANS, PLUS EN PIXELS FIXES.
    //
    // 400 px, c'etait moins d'un demi-ecran sur un portable et moins d'un quart
    // sur un 27 pouces : la rangee etait declaree « proche » alors qu'un seul
    // coup de molette l'amenait deja sous les yeux, et l'on voyait ses dix
    // vignettes arriver. `avanceDeChargement` en donne une fois et demie —
    // trois a quatre rangees de reserve, ce que Toji appelle « la marge de
    // gestion ».
  }, { rootMargin: `${avanceDeChargement(window.innerHeight || 800)}px 0px` })
  : null;

// ---------------------------------------------------------------------------
// LE PASSAGE ENTRE ORDINATEUR ET TELEPHONE
//
// Le nombre de cartes ne dépend plus de la largeur — dix, partout. Mais la
// FEUILLE DE STYLE, elle, change de régime au seuil du téléphone : dix cartes
// qui se partagent la largeur d'un côté, dix cartes de largeur fixe qu'on fait
// glisser de l'autre. Une fenêtre qu'on réduit franchit ce seuil, et les
// rangées différées — celles qui attendent qu'on descende jusqu'à elles —
// n'auraient alors jamais été peintes dans le bon régime.
//
// On repeint donc au changement de largeur, mais pas à chaque pixel de la
// poignée : le temps que la main s'arrête.
// ---------------------------------------------------------------------------
let largeurConnue = 0;
let minuteurTaille = null;

function suivreLaTailleDesRangees() {
  addEventListener('resize', () => {
    clearTimeout(minuteurTaille);
    minuteurTaille = setTimeout(() => {
      const large = largeurPage() || 0;
      if (large === largeurConnue) return;
      largeurConnue = large;
      // Seul l'accueil est concerné : la grille, elle, est en `grid` et se
      // réajuste toute seule.
      if (!$('v-accueil')?.hidden) peindreAccueil();
    }, 180);
  });
}

function differerJusquAuRegard(bloc, peindre) {
  bloc.__peindre = peindre;
  if (!regardeurDeRangees) { peindre(); return; }
  // Une hauteur d'attente pour que la barre de défilement ne saute pas quand la
  // rangée se remplit : elle occupe déjà la place qu'elle prendra.
  if (!bloc.innerHTML) bloc.innerHTML = '<div class="st-rangee-attente" aria-hidden="true"></div>';
  regardeurDeRangees.observe(bloc);
}


// ---------------------------------------------------------------------------
// LA RECHERCHE DU HAUT
//
// Toji : « une barre de recherche tout en haut, dans la barre du header,
// centrée au milieu … quand on a la recherche, ça ouvre une sorte de menu
// secondaire où on a les propositions, un peu comme c'est noté sur Stremio …
// et que ça cherche directement dans toute la liste complète. »
//
// ⚠️ ELLE CHERCHE CÔTÉ SERVEUR, ET C'EST TOUT LE POINT.
//
// La grille cherchait dans ce que le navigateur avait déjà téléchargé : elle
// répondait donc « aucun résultat » pour un titre qui est au catalogue mais pas
// encore dans la page. Toji : « je cherche des lanterns … s'il n'y est pas dans
// la pagination, il va me noter qu'il n'y a rien, alors que dans l'idée il y
// est. » Douze mille œuvres ne se téléchargent pas pour répondre à trois
// lettres ; le serveur, lui, les a toutes en mémoire.
//
// Les résultats sont groupés par type et suivent l'ORDRE DES RAYONS choisi
// dans les réglages — le même qu'à l'accueil. Deux ordres différents pour les
// mêmes catégories, ce serait deux endroits à apprendre.
let minuteurTete = null;
let requeteTete = 0;
let visee = -1;

function fermerRecherche() {
  const menu = $('resultats-tete');
  if (menu) { menu.hidden = true; menu.innerHTML = ''; }
  visee = -1;
}

function peindreResultats(resultats, q) {
  const menu = $('resultats-tete');
  if (!menu) return;
  visee = -1;

  if (!resultats.length) {
    menu.innerHTML = `<p class="res-vide">Rien qui ressemble à « ${esc(q)} ».</p>`;
    menu.hidden = false;
    return;
  }

  // Le même ordre qu'à l'accueil — mais RIEN N'EST MASQUÉ ici.
  //
  // ⚠️ Ce code supposait que « quelqu'un qui a caché les animes ne les cherche
  // pas non plus ». C'est faux, et c'est ce qui a fait diverger deux écrans sur
  // la même requête (Toji, 13/09). Masquer une rangée range son accueil ; taper
  // un titre, c'est demander ce qui existe. Même règle que `peindreRecherche`,
  // sans quoi le menu et la page ne montreraient pas la même chose.
  const groupes = ordreDesRayons()
    .map((nom) => ({ nom: RAYONS[nom].titre, type: RAYONS[nom].type }))
    .map((g) => ({ ...g, lignes: resultats.filter((o) => o.type === g.type) }))
    .filter((g) => g.lignes.length);

  menu.innerHTML = groupes.map((g) => `<p class="res-groupe">${esc(g.nom)}</p>`
    + g.lignes.map((o) => `<button type="button" class="res-ligne" data-oeuvre="${esc(o.id)}">
        ${o.affiche
    // ⚠️ 34x51 A L'ECRAN, ET ON DEMANDAIT DU 2000x3000. Le serveur sert les
    // affiches en `/t/p/original/` : chaque frappe dans la recherche lancait
    // douze telechargements de plusieurs centaines de kilo-octets pour des
    // vignettes de la taille d'un ongle. `w92` suffit, et arrive tout de suite.
    ? `<img src="${esc(imageDeTaille(o.affiche, 34 * densiteEcran(), 'affiche'))}" alt="" width="34" height="51" decoding="async" loading="lazy" />`
    : '<span class="vide"></span>'}
        <span class="res-txt">
          <span class="res-titre">${esc(o.titre)}</span>
          <span class="res-detail">${esc([
    o.annee || '',
    o.genres?.[0] ?? '',
    o.horsCatalogue ? 'pas encore au catalogue' : '',
  ].filter(Boolean).join(' · '))}</span>
        </span>
        ${o.note ? `<span class="res-note">${esc(String(o.note).replace('.', ','))}</span>` : ''}
      </button>`).join('')).join('');
  menu.hidden = false;

  for (const b of menu.querySelectorAll('[data-oeuvre]')) {
    b.onclick = async () => {
      const id = b.dataset.oeuvre;
      const trouve = resultats.find((o) => o.id === id);
      // ⚠️ UN TITRE HORS CATALOGUE N'A PAS DE FICHE A OUVRIR.
      //
      // Il vient de l'add-on de metadonnees, pas de chez nous : il faut
      // l'inscrire avant que la fiche ait quoi que ce soit a lire. L'inscription
      // demande le droit de gerer les add-ons — le catalogue est commun, et
      // laisser chacun y verser ce qu'il croise en ferait le journal des
      // recherches de tout le monde.
      if (trouve?.horsCatalogue) {
        b.disabled = true;
        try {
          await api('/api/catalogue/ajouter', { method: 'POST', body: JSON.stringify(trouve) });
          await chargerCatalogue(true);
        } catch (err) {
          b.disabled = false;
          const detail = b.querySelector('.res-detail');
          if (detail) detail.textContent = err.message.includes('403') || err.message.includes('droit')
            ? 'pas encore au catalogue — demande à un administrateur de l’ajouter'
            : err.message;
          return;
        }
      }
      fermerRecherche();
      const champ = $('q-tete');
      if (champ) champ.value = '';
      ouvrirOeuvre(id);
    };
  }
}

async function chercherDepuisLaTete(q) {
  const mien = ++requeteTete;
  try {
    const r = await api(`/api/recherche?q=${encodeURIComponent(q)}`);
    // Deux frappes, deux réponses : la seconde arrive parfois la première. On
    // écarte tout ce qui ne concerne plus la dernière demande.
    if (mien !== requeteTete) return;
    peindreResultats(normaliserCatalogue(r?.resultats), q);
  } catch (err) {
    if (mien !== requeteTete) return;
    const menu = $('resultats-tete');
    if (menu) {
      menu.innerHTML = `<p class="res-vide">Recherche indisponible — ${esc(err.message)}</p>`;
      menu.hidden = false;
    }
  }
}


// ---------------------------------------------------------------------------
// LA PAGE DE RESULTATS
//
// Toji : « ça met une page où c'est pas noté Catalogue, c'est noté Résultat de
// la recherche … on a la liste complète de tous les résultats. On peut
// regrouper par catégorie. Quand on regroupe par catégorie, on trie les films
// ensemble, les séries ensemble, les animés ensemble. »
//
// ⚠️ ELLE NE PASSE PAS PAR LA GRILLE DU CATALOGUE. Celle-ci filtre ce que le
// navigateur a déjà chargé : elle annonçait « 1 sur 12000 » là où la recherche
// en trouvait vingt. Ici, tout vient du serveur — catalogue et add-on de
// métadonnées confondus.
let resultatsCourants = [];
let requeteCourante = '';
let typeRecherche = 'tout';

async function ouvrirRecherche(q) {
  requeteCourante = String(q ?? '').trim();
  if (!requeteCourante) return ouvrirAccueil();
  quitterLAdresseDeSeance();
  vue('v-recherche');
  // Ce qu'on cherchait fait partie de la page : un rechargement doit la rendre
  // telle quelle, pas vide.
  marquerPosition({ q: requeteCourante });
  texte('rech-compte', 'recherche…');
  const corps = $('rech-corps');
  if (corps) corps.innerHTML = '';

  try {
    const r = await api(`/api/recherche?large=1&q=${encodeURIComponent(requeteCourante)}`);
    resultatsCourants = normaliserCatalogue(r?.resultats);
  } catch (err) {
    resultatsCourants = [];
    if (corps) corps.innerHTML = `<p class="st-vide"><b>La recherche n'a pas abouti</b>${esc(err.message)}</p>`;
    texte('rech-compte', '');
    return;
  }
  peindreRecherche();
}

function peindreRecherche() {
  const corps = $('rech-corps');
  if (!corps) return;

  // ⚠️ UNE RECHERCHE NE SE FILTRE PAS SUR LES GOÛTS D'ACCUEIL.
  //
  // Toji, 13/09 : « quand je l'ai vue chercher, elle n'avait pas tout à fait les
  // mêmes résultats que moi. » La route `/api/recherche` rend pourtant la même
  // chose à tout le monde — elle ne lit pas la session, vérifié. L'écart venait
  // d'ICI : on écartait les rayons masqués sur l'accueil DE LA PERSONNE. Qui a
  // rangé « Animes » hors de sa page d'accueil ne trouvait donc plus un anime en
  // le CHERCHANT, et « 11 résultats » annonçait ce qui restait après ce tri, pas
  // ce que la recherche avait trouvé.
  //
  // Masquer une rangée range l'ACCUEIL ; chercher, c'est demander ce qui existe.
  // L'ordre personnel des rayons reste, lui : il dit dans quel ordre on aime
  // voir les groupes, il n'en retire aucun.
  const groupes = ordreDesRayons()
    .map((nom) => ({ nom: RAYONS[nom].titre, type: RAYONS[nom].type }))
    .filter((g) => typeRecherche === 'tout' || g.type === typeRecherche)
    .map((g) => ({ ...g, lignes: resultatsCourants.filter((o) => o.type === g.type) }))
    .filter((g) => g.lignes.length);

  const total = groupes.reduce((n, g) => n + g.lignes.length, 0);
  texte('rech-compte', total
    ? `${total} ${pluriel(total, 'résultat')} pour « ${requeteCourante} »`
    : `rien pour « ${requeteCourante} »`);

  // Le filtre par type est le seul qui ait un sens ici : on vient de chercher un
  // titre, pas de parcourir un rayon. Genre et année appartiennent à la grille.
  const barre = $('rech-filtres');
  if (barre) {
    // Les mêmes rayons que les groupes ci-dessus, masquage compris — c'est-à-dire
    // aucun : un onglet qui manque ferait disparaître des résultats sans le dire,
    // et c'est exactement ce qui différenciait deux écrans sur la même requête.
    const choix = [['tout', 'Tout'], ...ordreDesRayons()
      .map((nom) => [RAYONS[nom].type, RAYONS[nom].titre])];
    barre.innerHTML = `<div class="st-filtre">${choix.map(([v, mot]) => `<button type="button" data-t="${esc(v)}"
        aria-pressed="${typeRecherche === v}">${esc(mot)}</button>`).join('')}</div>`;
    for (const b of barre.querySelectorAll('[data-t]')) {
      b.onclick = () => { typeRecherche = b.dataset.t; peindreRecherche(); };
    }
  }

  if (!total) {
    corps.innerHTML = `<p class="st-vide"><b>Rien qui ressemble à « ${esc(requeteCourante)} »</b>
      Essaie moins de mots, ou le titre d'origine.</p>`;
    return;
  }

  corps.innerHTML = groupes.map((g) => `<section class="st-groupe">
      <h2 class="st-groupe-titre">${esc(g.nom)}<span class="st-groupe-compte">${g.lignes.length}</span></h2>
      <div class="st-grille">${g.lignes.map((o, rang) => carteOeuvre(o, {
    // La page de resultats n'est pas virtualisee : elle pose tout d'un coup.
    // Les premieres cartes de chaque groupe sont celles qu'on voit, les
    // suivantes attendent leur tour — sans quoi trente vignettes prioritaires
    // se disputeraient a nouveau la bande passante.
    avance: rang < 12 ? 'dans' : 'reserve', dpr: densiteEcran(),
  })).join('')}</div>
    </section>`).join('');

  brancherCartes(corps, async (id) => {
    const trouve = resultatsCourants.find((o) => o.id === id);
    // Même règle que dans le menu : un titre qui n'est pas encore au catalogue
    // doit y entrer avant qu'une fiche puisse l'ouvrir.
    if (trouve?.horsCatalogue) {
      try {
        await api('/api/catalogue/ajouter', { method: 'POST', body: JSON.stringify(trouve) });
        await chargerCatalogue(true);
      } catch (err) {
        texte('rech-compte', err.message);
        return;
      }
    }
    ouvrirOeuvre(id);
  });
}

function preparerRechercheTete() {
  const bloc = $('recherche-tete');
  const champ = $('q-tete');
  if (!bloc || !champ) return;
  bloc.hidden = false;

  champ.oninput = () => {
    const q = champ.value.trim();
    clearTimeout(minuteurTete);
    if (q.length < 2) { fermerRecherche(); return; }
    // On laisse le mot se finir : une requête par touche ferait douze appels
    // pour « lanterns », et la réponse utile serait la dernière de toute façon.
    minuteurTete = setTimeout(() => chercherDepuisLaTete(q), 220);
  };

  champ.onkeydown = (e) => {
    const lignes = [...($('resultats-tete')?.querySelectorAll('.res-ligne') ?? [])];
    if (e.key === 'Escape') { fermerRecherche(); champ.blur(); return; }
    if (e.key === 'Enter') {
      // Entrée sans sélection : la grille, avec la recherche déjà posée. C'est
      // là qu'on filtre par genre et par année quand la liste est trop longue.
      if (visee >= 0 && lignes[visee]) { lignes[visee].click(); return; }
      const q = champ.value.trim();
      if (!q) return;
      fermerRecherche();
      // ⚠️ PAS LA GRILLE DU CATALOGUE. Elle ne filtre que ce que le navigateur a
      // charge — « 1 sur 12000 » la ou la recherche en trouve vingt. La page de
      // resultats montre ce que le SERVEUR a trouve, groupe par type.
      ouvrirRecherche(q);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!lignes.length) return;
    e.preventDefault();
    visee = e.key === 'ArrowDown'
      ? Math.min(lignes.length - 1, visee + 1)
      : Math.max(0, visee - 1);
    lignes.forEach((l, i) => l.classList.toggle('vise', i === visee));
    lignes[visee]?.scrollIntoView({ block: 'nearest' });
  };

  // Cliquer ailleurs referme : un menu qui reste ouvert derrière l'écran suivant
  // est un menu qu'on retrouve plus tard sans comprendre d'où il sort.
  document.addEventListener('click', (e) => {
    if (!bloc.contains(e.target)) fermerRecherche();
  });
  champ.onfocus = () => { if (champ.value.trim().length >= 2) champ.oninput(); };
}

// ---------------------------------------------------------------------------
// LES TROIS RAYONS DE L'ACCUEIL
//
// Une seule rangée « Tout le catalogue » mélangeait un film, une série et un
// anime dans le même ruban : pour trouver un anime il fallait faire défiler
// tout le reste. Trois rangées répondent à la question qu'on se pose vraiment
// en arrivant — « qu'est-ce qu'on regarde ce soir, un film ou une série ? ».
//
// L'ordre est un réglage : celui qui vient pour les animes ne veut pas les
// voir en troisième position tous les soirs.
// ---------------------------------------------------------------------------
const RAYONS = {
  films: { id: 'acc-films', titre: 'Films', type: 'film' },
  series: { id: 'acc-series', titre: 'Séries', type: 'serie' },
  animes: { id: 'acc-animes', titre: 'Animes', type: 'anime' },
};
const ORDRE_RAYONS = ['films', 'series', 'animes'];

// Les quatre rangées réglables, « Nouveautés » comprise : elle se montre, se
// cache et se déplace comme les autres — c'est une rangée, pas une exception.
const RANGEES_REGLABLES = ['bibliotheque', 'nouveautes', ...ORDRE_RAYONS];
const NOM_DE_RANGEE = {
  bibliotheque: 'Ma bibliothèque',
  nouveautes: 'Nouveautés', films: 'Films', series: 'Séries', animes: 'Animes',
};

/** La zone de page d'une rangée. Une seule table : trois `if` divergeaient. */
function zoneDeLaRangee(nom) {
  if (nom === 'bibliotheque') return $('acc-biblio');
  if (nom === 'nouveautes') return $('acc-nouveautes');
  return RAYONS[nom] ? $(RAYONS[nom].id) : null;
}

/** L'ordre choisi, complété par ce qui n'a jamais été déplacé. */
function ordreDesRangees() {
  const pose = prefs.lire('rayons-ordre', '').split(',').filter((r) => RANGEES_REGLABLES.includes(r));
  // Une rangée ajoutée au produit après le réglage de quelqu'un doit apparaître
  // quand même — à la fin, mais visible. Sans cela, elle resterait invisible
  // pour tous ceux qui ont touché à ce réglage une fois.
  return [...pose, ...RANGEES_REGLABLES.filter((r) => !pose.includes(r))];
}

/** Les rangées que la personne a cachées. */
function rangeesCachees() {
  return new Set(prefs.lire('rayons-caches', '').split(',').filter(Boolean));
}

// ---------------------------------------------------------------------------
// MA BIBLIOTHEQUE
//
// Toji : « il faudrait pouvoir mettre un like et pouvoir l'ajouter a la
// bibliotheque, comme ca on peut le regarder plus tard, un peu comme ce que
// fait Stremio, litteralement. »
//
// Les FICHES viennent avec la liste, comme pour l'accueil : un titre garde il y
// a un mois n'est pas forcement dans les trois cents oeuvres que la page a en
// memoire, et c'est justement celui auquel on tient. Sans elles, la rangee
// afficherait des cadres vides.
// ---------------------------------------------------------------------------
let maBiblio = new Map();
const fichesBiblio = new Map();

/**
 * Toute la bibliothèque, EN UNE REQUÊTE, au démarrage.
 *
 * ⚠️ UNE PAR CARTE SERAIT INTENABLE. Une grille en montre une vingtaine et le
 * catalogue en compte douze mille : demander l'état titre par titre ferait
 * vingt allers-retours pour peindre un écran, et autant à chaque cran de
 * molette. Ce qu'on garde tient dans quelques kilo-octets — on prend tout.
 *
 * ⚠️ LA RÉPONSE PORTE DEUX CHOSES, ET `oeuvres` N'EST PAS UNE TABLE D'ÉTATS :
 * `{ entrees: [{ oeuvreId, ajoute, notifs, dernierPrevenu, vu, vuLe }],
 * oeuvres: [FICHES] }`. C’est ce que sert `installerCatalogue` (oeuvres.js),
 * et c'est la seule porte de lecture du lecteur — il n'y en a pas d'autre.
 *
 * ⚠️ LIRE `d.oeuvres` COMME UNE TABLE D'ÉTATS REND UNE BIBLIOTHÈQUE VIDE, SANS
 * UNE SEULE ERREUR. `Array.isArray` est alors vrai, la table retenue vaut `{}`,
 * et `maBiblio` reste vide pour toujours : plus une marque sur les affiches, le
 * menu du clic droit propose « Ajouter » sur un titre déjà gardé, et la rangée
 * « Ma bibliothèque » ne montre rien. Le `catch` ne voit rien passer : c'est une
 * réponse 200 parfaitement valide, lue de travers. Le banc
 * `essais/bibliotheque-personnelle.mjs` (section K) rejoue donc la vraie réponse
 * de la route dans CE code, pour que le désaccord ne puisse plus être silencieux.
 *
 * ⚠️ ET LES FICHES ARRIVENT AVEC. Un titre gardé il y a un mois n’est plus
 * forcément dans le mix : la route le résout par `configOuSource` et l’envoie
 * ici. Les jeter, c’est rafficher les cadres vides que cette route existe
 * précisément pour éviter.
 */
async function chargerBiblio() {
  try {
    const d = await api('/api/bibliotheque');
    maBiblio = new Map((d?.entrees ?? [])
      .filter((e) => e && typeof e === 'object' && e.oeuvreId)
      .map((e) => [String(e.oeuvreId), { ...e, oeuvreId: String(e.oeuvreId) }]));
    fichesBiblio.clear();
    for (const o of normaliserCatalogue(d?.oeuvres)) fichesBiblio.set(o.id, o);
  } catch (err) {
    // Une bibliotheque injoignable ne doit pas emporter l'accueil : la rangee
    // se cache, le reste vit.
    tracer('biblio', 'injoignable', err.message);
  }
  return maBiblio;
}

/**
 * La fiche d'un titre, prise à la meilleure source qu'on ait sous la main.
 *
 * ⚠️ LA BIBLIOTHÈQUE NE REND QUE DES ÉTATS, PLUS DES FICHES. La route dit ce
 * qu'on garde et depuis quand ; elle ne renvoie plus les affiches, qui pèsent
 * cent fois plus et que la page a déjà. On les reprend donc dans l'ordre de
 * confiance : le catalogue chargé et les rangées de l'accueil portent la fiche
 * ENTIÈRE (format d'anime compris) ; `oeuvresVues` n'en porte qu'un calque,
 * assez pour peindre une carte, et c'est le dernier recours.
 *
 * Un titre qu'on ne sait peindre nulle part rend `null` : la rangée le saute
 * plutôt que d'afficher une carte vide — un cadre sans titre ni affiche ne dit
 * pas s'il manque une image ou une œuvre.
 */
function ficheConnue(oeuvreId) {
  const id = String(oeuvreId ?? '');
  if (!id) return null;
  return fichesBiblio.get(id)
    ?? fichesDAccueil.get(id)
    ?? catalogue.find((o) => o.id === id)
    // Les résultats de recherche portent des titres que le catalogue n'a pas
    // encore : c'est la seule source qui sache qu'ils sont `horsCatalogue`.
    ?? resultatsCourants.find((o) => o.id === id)
    ?? oeuvresVues.get(id)
    ?? null;
}

/** Les oeuvres gardees, de la plus recemment ajoutee a la plus ancienne. */
function oeuvresDeLaBiblio() {
  return [...maBiblio.values()]
    // ⚠️ L'ORDRE SE CALCULE ICI, IL NE SE REÇOIT PLUS. La table de la route
    // n'a pas d'ordre garanti — c'est un objet — et la rangée promet
    // « la plus récemment ajoutée d'abord » : la promesse se tient donc ici.
    .sort((a, b) => (Number(b.ajoute) || 0) - (Number(a.ajoute) || 0))
    .map((e) => ficheConnue(e.oeuvreId))
    .filter(Boolean);
}

/**
 * Le coeur et la cloche de la fiche ouverte.
 *
 * La cloche ne s'affiche que si le titre est garde : demander a etre prevenu
 * pour quelque chose qu'on n'a pas garde n'aurait nulle part ou etre range.
 * Elle ne s'affiche pas non plus pour un film — il n'y aura pas de prochain
 * episode, et une cloche qui ne sonnera jamais est une promesse en trop.
 */
function peindreBoutonsDeBiblio(oeuvre) {
  const bGarder = $('oeu-garder');
  const bCloche = $('oeu-cloche');
  if (!bGarder || !oeuvre) return;
  const dedans = maBiblio.has(oeuvre.id);
  const entree = maBiblio.get(oeuvre.id);

  bGarder.hidden = false;
  bGarder.setAttribute('aria-pressed', String(dedans));
  texte('oeu-garder-mot', dedans ? 'Dans ma bibliothèque' : 'Bibliothèque');
  bGarder.onclick = async () => {
    bGarder.disabled = true;
    const r = await basculerBiblio(oeuvre.id);
    bGarder.disabled = false;
    direMotDeCloche(null);
    peindreBoutonsDeBiblio(oeuvre);
    // Le refus du serveur se dit (comme pour la cloche), il ne s'avale pas.
    if (r.erreur) direMotDeCloche(`Impossible pour l'instant : ${r.erreur}`);
    // L'accueil porte la rangee : la laisser en retard obligerait a recharger
    // pour voir ce qu'on vient de garder.
    try { if (!$('v-accueil')?.hidden) peindreAccueil(); } catch { /* pas peint */ }
  };

  if (!bCloche) return;
  // ⚠️ ETRE PREVENU ET GARDER POUR PLUS TARD SONT DEUX ENVIES DIFFERENTES.
  //
  // Toji : « actuellement, le bouton de notifs n'apparait que quand j'ai le
  // liker pour la bibliotheque. Ce qu'il faudrait, c'est qu'il soit
  // independant. » On peut vouloir etre prevenu d'un nouvel episode sans
  // ranger le titre, et l'inverse. Cocher la cloche ajoute donc le titre en
  // silence — il faut bien ranger l'abonnement quelque part — mais on n'a plus
  // a le faire soi-meme d'abord.
  //
  // Un FILM garde son exception : il n'aura pas de prochain episode, et une
  // cloche qui ne sonnera jamais est une promesse en trop.
  const cloche = !estUnFilm(oeuvre);
  bCloche.hidden = !cloche;
  if (!cloche) { direMotDeCloche(null); return; }
  bCloche.setAttribute('aria-pressed', String(entree?.notifs === true));
  bCloche.setAttribute('aria-label', entree?.notifs
    ? 'Ne plus recevoir les nouveaux épisodes en message privé'
    : 'Recevoir les nouveaux épisodes en message privé');
  bCloche.onclick = async () => {
    bCloche.disabled = true;
    const r = await basculerCloche(oeuvre.id);
    bCloche.disabled = false;
    peindreBoutonsDeBiblio(oeuvre);
    if (r.erreur) { direMotDeCloche(`Impossible pour l'instant : ${r.erreur}`); return; }
    // ⚠️ LE CAS QUI DEMANDE UNE EXPLICATION, ET PAS UN MESSAGE D'ERREUR.
    //
    // Discord n'a pas de demande d'autorisation : le bot essaie d'ecrire, et la
    // personne peut avoir ferme ses messages prives pour ce serveur. Ce n'est
    // pas une panne, c'est un reglage — et il se change en trois clics, a
    // condition de savoir lesquels.
    // ⚠️ DEUX REFUS, DEUX CONSEILS OPPOSES.
    //
    // « Messages fermés » se règle en trois clics par la personne elle-même.
    // « Aucun serveur en commun » ne se règle PAS de son côté : c'est le bot
    // qu'il faut inviter. Donner le premier conseil dans le second cas fait
    // chercher un réglage qui ne changera rien.
    if (r.notification?.raison === 'aucun-serveur-commun') {
      direMotDeCloche('<b>Le bot ne partage aucun serveur Discord avec toi.</b> Discord '
        + 'interdit à un bot d’écrire à quelqu’un qu’il ne croise nulle part. Rejoins le '
        + 'serveur de l’instance — ou fais-y inviter le bot — puis reclique sur la cloche.');
      return;
    }
    if (r.notification?.ferme) {
      direMotDeCloche('<b>Discord ne laisse pas le bot t’écrire.</b> Ouvre le serveur, '
        + 'clic droit sur son nom, <b>Paramètres de confidentialité</b>, puis autorise les '
        + 'messages privés des membres. Reclique ensuite sur la cloche.');
      return;
    }
    if (r.notification && !r.notification.ok) {
      direMotDeCloche(`<b>Le message de confirmation n’est pas parti.</b> ${esc(r.notification.detail
        ?? r.notification.raison ?? 'raison inconnue')}`);
      return;
    }
    if (r.notifs) direMotDeCloche('Un message de confirmation vient de t’être envoyé sur Discord.', true);
    else direMotDeCloche(null);
  };
}

/** Le mot sous les boutons. `null` le retire. */
function direMotDeCloche(html, bref = false) {
  const haut = document.querySelector('#v-oeuvre .st-hero');
  if (!haut) return;
  let mot = $('oeu-mot-cloche');
  if (!html) { mot?.remove(); return; }
  if (!mot) {
    mot = document.createElement('p');
    mot.id = 'oeu-mot-cloche';
    mot.className = 'st-mot-cloche';
    haut.appendChild(mot);
  }
  mot.innerHTML = html;
  // Une confirmation se lit et s'oublie ; une explication reste tant qu'on n'a
  // pas agi.
  clearTimeout(direMotDeCloche.minuteur);
  if (bref) direMotDeCloche.minuteur = setTimeout(() => mot.remove(), 6000);
}

/** Garde ou relache un titre. Rend l'etat apres le geste. */
async function basculerBiblio(oeuvreId) {
  const dedans = maBiblio.has(oeuvreId);
  try {
    if (dedans) {
      await api(`/api/bibliotheque/${encodeURIComponent(oeuvreId)}`, { method: 'DELETE' });
      maBiblio.delete(oeuvreId);
    } else {
      const d = await api(`/api/bibliotheque/${encodeURIComponent(oeuvreId)}`, {
        method: 'PUT', body: JSON.stringify({ notifs: false }),
      });
      maBiblio.set(oeuvreId, { oeuvreId, ...(d?.entree ?? {}) });
      // La fiche qu'on vient de garder n'etait peut-etre pas dans la liste :
      // on la prend de ce qu'on a sous la main pour que la rangee la peigne.
      const connue = fichesDAccueil.get(oeuvreId) ?? catalogue.find((o) => o.id === oeuvreId);
      if (connue && !fichesBiblio.has(oeuvreId)) fichesBiblio.set(oeuvreId, connue);
    }
  } catch (err) {
    tracer('biblio', 'refuse', err.message);
    return { erreur: err.message };
  }
  // ⚠️ LES CARTES PORTENT LE MÊME ÉTAT QUE CE BOUTON. Sans cela, revenir de la
  // fiche à la grille montrerait l'ancienne marque sur l'affiche qu'on vient
  // tout juste de garder — deux écrans qui se contredisent sur le même titre.
  rafraichirMarques(oeuvreId);
  return { dedans: maBiblio.has(oeuvreId) };
}

/**
 * Allume ou eteint la cloche d'un titre.
 *
 * ⚠️ LA REPONSE PEUT DIRE « NON ». Discord n'a pas de demande d'autorisation :
 * cocher la case envoie un message de confirmation, et s'il ne part pas — la
 * personne a ferme ses messages prives — le serveur remet la cloche a zero. On
 * rend alors de quoi le DIRE, plutot que de promettre des notifications qui
 * n'arriveront jamais.
 */
async function basculerCloche(oeuvreId) {
  const entree = maBiblio.get(oeuvreId);
  // Pas encore garde : la cloche le range au passage. C'est le seul endroit ou
  // l'abonnement peut vivre, et l'expliquer serait demander un geste de plus
  // pour rien.
  const veut = !entree?.notifs;
  try {
    const d = await api(`/api/bibliotheque/${encodeURIComponent(oeuvreId)}`, {
      method: 'PUT', body: JSON.stringify({ notifs: veut }),
    });
    maBiblio.set(oeuvreId, { oeuvreId, ...(d?.entree ?? {}) });
    // La cloche peut AJOUTER le titre au passage : la carte doit alors porter
    // son signet, sans attendre un rechargement.
    rafraichirMarques(oeuvreId);
    return { notifs: d?.entree?.notifs === true, notification: d?.notification ?? null };
  } catch (err) {
    return { erreur: err.message };
  }
}

// ---------------------------------------------------------------------------
// LE MENU AU CLIC DROIT SUR UNE AFFICHE
//
// Toji, 13/09 : « ce que j'aimerais, c'est aussi que tu implémentes des options
// par rapport au clic droit sur chacun des épisodes. Par exemple, on clic droit
// sur une série, on peut l'ajouter à sa watchlist, à la bibliothèque, je veux
// dire. On peut par exemple noter qu'on l'a déjà vu. »
//
// ⚠️ LE CLIC DROIT DU NAVIGATEUR RESTE ACCESSIBLE PARTOUT AILLEURS.
//
// Un menu maison qui prend `contextmenu` sur toute la page confisque « ouvrir
// dans un nouvel onglet », « copier l'image », « inspecter » — et il les
// confisque en silence : rien ne dit à la personne pourquoi son clic droit ne
// répond plus comme d'habitude. On ne le prend donc QUE sur une carte d'œuvre,
// et seulement dans les trois grilles qui en portent (accueil, catalogue,
// recherche). Sur le reste de la page, le clic droit est celui du navigateur.
//
// ⚠️ ET IL S'OUVRE AUSSI AU CLAVIER. La touche Menu (ou Maj+F10) fait la même
// chose sur la carte qui a le focus : un menu qui n'existe qu'à la souris
// n'existe pas pour tout le monde, et les cartes sont déjà des boutons —
// elles reçoivent donc le focus sans qu'on ait rien à ajouter.
// ---------------------------------------------------------------------------

/**
 * Ce que le menu propose pour ce titre, dans cet etat.
 *
 * PURE, et c'est voulu : elle porte toutes les decisions du menu, et le banc
 * `essais/menu-oeuvre.mjs` les eprouve sans navigateur.
 *
 * `etat` est l'entree de bibliotheque du titre, ou `null`/`undefined` s'il n'y
 * est pas. `actif` dit que l'attribut est DEJA pose — c'est ce que lit le
 * gestionnaire pour savoir dans quel sens basculer, et c'est ce que la coche
 * affiche pour les notifications.
 *
 * ⚠️ PAS DE BASCULE DE NOTIFICATIONS POUR UN FILM. Il n'aura pas de prochain
 * episode : proposer d'en etre prevenu serait promettre une notification qui
 * n'arrivera jamais. C'est la meme regle que la cloche de la fiche, et elle se
 * dit ici une seule fois pour les deux.
 */
export function actionsDuMenu(oeuvre, etat) {
  const entree = etat && typeof etat === 'object' && !Array.isArray(etat) ? etat : null;
  const dedans = entree !== null;
  const vu = entree?.vu === true;

  const actions = [
    dedans
      // Retirer est le seul geste de ce menu qui defait quelque chose : il se
      // signale, sans etre mis a part — c'est une option comme les autres.
      ? { cle: 'biblio', libelle: 'Retirer de ma bibliothèque', danger: true, actif: true }
      : { cle: 'biblio', libelle: 'Ajouter à ma bibliothèque', actif: false },
    vu
      ? { cle: 'vu', libelle: 'Marquer comme non vu', actif: true }
      : { cle: 'vu', libelle: 'Marquer comme déjà vu', actif: false },
  ];

  if (!estUnFilm(oeuvre)) {
    // Le libelle ne bascule PAS ici — « ne plus me prevenir » se lit mal a cote
    // de deux lignes qui disent deja ce qu'elles font. C'est la coche qui porte
    // l'etat, et c'est pour elle que `actif` existe.
    actions.push({
      cle: 'notifs',
      libelle: 'Me prévenir des nouveaux épisodes',
      actif: entree?.notifs === true,
    });
  }

  actions.push({ cle: 'fiche', libelle: 'Ouvrir la fiche' });
  return actions;
}

/**
 * Ou poser le menu pour qu'il ne sorte JAMAIS de l'ecran.
 *
 * PURE, et eprouvee — parce que c'est exactement ce qu'on ne peut pas deviner.
 * Les dimensions sont MESUREES sur le menu deja construit et sur la fenetre
 * reelle ; cette fonction ne fait que la geometrie.
 *
 * Trois cas, dans cet ordre :
 *   · il tient tel quel — on ne touche a rien ;
 *   · il deborde, mais l'autre cote du curseur tient — il s'ouvre de l'autre
 *     cote, exactement comme le fait le menu du systeme ;
 *   · ni l'un ni l'autre (un menu plus grand que la fenetre, un telephone en
 *     paysage) — on le plaque contre le bord, la marge gagne, et le haut du
 *     menu reste visible plutot que de deborder des deux cotes a la fois.
 */
export function placerLeMenu({
  x = 0, y = 0, largeur = 0, hauteur = 0, ecranL = 0, ecranH = 0, marge = 8,
} = {}) {
  const surUnAxe = (pos, taille, ecran) => {
    const p = Number(pos) || 0;
    const t = Math.max(0, Number(taille) || 0);
    const e = Number(ecran) || 0;
    // Sans fenetre mesurable, on ne peut rien decider : on garde la position
    // demandee plutot que d'inventer une bascule.
    if (!(e > 0)) return { valeur: Math.max(marge, p), bascule: false };
    const tient = (v) => v >= marge && v + t + marge <= e;
    // ⚠️ LE COTE RETOURNE DOIT TENIR, LUI AUSSI — ON NE LE SUPPOSE PAS.
    //
    // Mesure du banc : curseur à y = 1090 sur une fenêtre de 1080 (le pointeur
    // sort du cadre — un glisser qui dépasse, un écran secondaire), menu de
    // 170 px. Le retournement donnait 920, et 920 + 170 = 1090 : le menu
    // dépassait de dix pixels par le bas. Retourner ne suffit donc pas ; il
    // faut vérifier que le retournement tient, et rabattre contre le bord
    // sinon. Le bord gagne toujours, c'est la seule position qu'on sache vraie.
    // Le rabattement vise le point VALIDE LE PLUS PROCHE DU CURSEUR, et non
    // le bord opposé.
    //
    // ⚠️ MESURÉ : curseur à x = 2 (on vise l'affiche la plus à gauche de la
    // rangée), menu de 240 px, fenêtre de 1920. Rabattre « contre le bord »
    // donnait 1672 : le menu traversait tout l'écran pour aller s'ouvrir à
    // l'autre bout, à deux mille pixels de l'affiche qu'on venait de viser.
    // Il n'y avait pourtant aucun problème à résoudre : il manquait six
    // pixels de marge à gauche.
    const pres = (v) => Math.min(Math.max(v, marge), Math.max(marge, e - t - marge));
    const valeur = tient(p) ? p : (tient(p - t) ? p - t : pres(p));
    // La bascule se LIT sur le résultat plutôt qu'elle ne se décide en chemin :
    // après un rabattement, le menu peut se retrouver entièrement du côté
    // opposé au curseur sans qu'aucune branche ne l'ait « décidé ».
    return { valeur, bascule: valeur + t <= p };
  };
  const h = surUnAxe(x, largeur, ecranL);
  const v = surUnAxe(y, hauteur, ecranH);
  return {
    x: Math.round(h.valeur), y: Math.round(v.valeur),
    versLaGauche: h.bascule, versLeHaut: v.bascule,
  };
}

// Le menu ouvert, et de quoi le defaire proprement. Un seul a la fois : un
// second clic droit ferme le premier avant d'ouvrir le suivant.
let menuDOeuvre = null;

function fermerLeMenu({ rendreLeFocus = false } = {}) {
  if (!menuDOeuvre) return;
  const { boite, ecouteurs, venuDe } = menuDOeuvre;
  // ⚠️ REMIS A `null` AVANT DE DEBRANCHER : retirer les ecouteurs peut
  // declencher un `blur`, qui rappellerait cette meme fonction.
  menuDOeuvre = null;
  for (const [cible, type, fn, options] of ecouteurs) cible.removeEventListener(type, fn, options);
  boite.remove();
  // Le focus revient d'ou il venait : sinon il retombe sur `<body>` et la
  // navigation au clavier repart du haut de la page.
  if (rendreLeFocus && venuDe?.isConnected) {
    try { venuDe.focus({ preventScroll: true }); } catch (err) { tracer('menu', 'focus non rendu', err.message); }
  }
}

function ouvrirLeMenuDOeuvre(o, {
  x, y, venuDe = null,
  // ⚠️ DEUX JEUX D'ACTIONS, UN SEUL MENU. Le placement, le retournement pres
  // d'un bord, les fleches, `Escape`, le retour du focus : tout cela a coute
  // du soin et ne doit exister qu'une fois. Un menu d'episode qui se
  // redessinerait a cote finirait par se comporter autrement — et c'est
  // toujours le second qui oublie une touche.
  actions = actionsDuMenu(o, etatDuTitre(o.id)),
  etiquette = `Options pour ${o.titre ?? 'ce titre'}`,
  executer = (cle) => lancerLActionDuMenu(cle, o, actions),
} = {}) {
  fermerLeMenu();

  const boite = document.createElement('div');
  boite.className = 'st-menu-oeuvre';
  boite.setAttribute('role', 'menu');
  boite.setAttribute('aria-label', etiquette);
  boite.innerHTML = actions.map((a) => {
    // ⚠️ LA COCHE NE VA QUE LA OU LE LIBELLE NE DIT PAS DEJA L'ETAT.
    //
    // « Retirer de ma bibliothèque » coché se lirait « déjà retiré », et
    // « Marquer comme non vu » coché, l'inverse de ce qu'il fait. Ces deux-là
    // portent leur état DANS leur libellé — c'est la forme la plus courte.
    // Les notifications, elles, gardent un libellé fixe : sans coche, rien ne
    // dirait qu'elles sont déjà allumées.
    // La coche va aux actions dont le libelle NE DIT PAS l'etat. « Marquer
    // comme non vu » le dit ; « Notifications » ne le dit pas. Chaque action
    // peut desormais le declarer elle-meme (`coche: true`), sinon on garde la
    // regle historique.
    const coche = a.coche === true || a.cle === 'notifs';
    return `<button type="button" class="st-menu-ligne${a.danger ? ' st-menu-ligne--danger' : ''}"`
      + ` role="${coche ? 'menuitemcheckbox' : 'menuitem'}"`
      + (coche ? ` aria-checked="${a.actif === true}"` : '')
      + ` data-action="${esc(a.cle)}">`
      // Une gouttiere de largeur fixe, cochee ou non : sans elle, la ligne
      // cochee se decalerait d'un cran et la colonne de texte ferait un pas.
      + `<span class="st-menu-coche" aria-hidden="true">${coche && a.actif === true ? '✓' : ''}</span>`
      + `<span class="st-menu-mot">${esc(a.libelle)}</span></button>`;
  }).join('');
  document.body.appendChild(boite);
  // ⚠️ LE MENU EST « OUVERT » DÈS QU'IL EST DANS LA PAGE, PAS QUAND IL EST
  // FINI. Tout ce qui suit peut échouer — une mesure sur une page en train
  // d'être démontée, un focus refusé — et une boîte qu'on ne sait plus fermer
  // resterait par-dessus l'écran pour de bon. La déclarer ici rend
  // `fermerLeMenu()` capable de la reprendre à tout moment.
  const ecouteurs = [];
  menuDOeuvre = { boite, ecouteurs, venuDe };

  // ⚠️ MESURE, PAS ESTIMATION. La hauteur depend du nombre d'entrees (un film
  // n'en a que trois) et la largeur du plus long libelle, qui depend de la
  // police reellement chargee. Le menu est donc construit, mesure, PUIS place —
  // il est invisible (`visibility`) jusqu'a ce que `data-pret` soit pose, ce
  // qui evite de le voir apparaitre une frame en haut a gauche.
  const taille = boite.getBoundingClientRect();
  const place = placerLeMenu({
    x, y, largeur: taille.width, hauteur: taille.height,
    ecranL: window.innerWidth || document.documentElement?.clientWidth || 0,
    ecranH: window.innerHeight || document.documentElement?.clientHeight || 0,
  });
  // Placé en pixels d’écran (clic, cadre), écrit en pixels de page.
  boite.style.left = `${enPage(place.x)}px`;
  boite.style.top = `${enPage(place.y)}px`;
  boite.dataset.pret = '1';

  const lignes = () => [...boite.querySelectorAll('[data-action]')];
  for (const ligne of lignes()) {
    ligne.onclick = (e) => {
      e.preventDefault();
      const cle = ligne.dataset.action;
      // Ouvrir la fiche change d'ecran : y ramener le focus le reprendrait a
      // une carte qui n'est plus a l'ecran.
      fermerLeMenu({ rendreLeFocus: cle !== 'fiche' });
      executer(cle);
    };
  }

  const surTouche = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      // `Escape` est aussi le raccourci du lecteur : on l'arrete ici, sinon
      // fermer le menu sortirait du plein ecran par la meme occasion.
      if (e.key === 'Escape') e.preventDefault();
      e.stopPropagation();
      fermerLeMenu({ rendreLeFocus: true });
      return;
    }
    const l = lignes();
    if (!l.length) return;
    const ou = l.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const pas = e.key === 'ArrowDown' ? 1 : -1;
      // La liste tourne : quatre entrees se parcourent en rond, pas en butee.
      const depart = ou < 0 ? (pas > 0 ? -1 : 0) : ou;
      l[(depart + pas + l.length) % l.length]?.focus({ preventScroll: true });
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      (e.key === 'Home' ? l[0] : l[l.length - 1])?.focus({ preventScroll: true });
    }
  };

  // ⚠️ `preventScroll` : amener le focus sur la premiere ligne ne doit pas
  // faire defiler la grille — le defilement ferme le menu, il se refermerait
  // donc tout seul a l'ouverture.
  lignes()[0]?.focus({ preventScroll: true });

  const brancher = (cible, type, fn, options) => {
    cible.addEventListener(type, fn, options);
    ecouteurs.push([cible, type, fn, options]);
  };
  // Un clic ailleurs ferme, en capture : une carte qui avale son propre clic
  // laisserait le menu ouvert par-dessus l'ecran suivant.
  brancher(document, 'pointerdown', (e) => { if (!boite.contains(e.target)) fermerLeMenu(); }, true);
  // Le menu est en position fixe : il ne suit pas la grille. Le laisser ouvert
  // pendant qu'on defile le ferait pointer une autre affiche que celle qu'on
  // avait choisie — et c'est le pire mensonge possible pour un menu.
  brancher(window, 'scroll', () => fermerLeMenu(), { capture: true, passive: true });
  brancher(window, 'resize', () => fermerLeMenu());
  brancher(window, 'blur', () => fermerLeMenu());
  brancher(document, 'keydown', surTouche, true);
}

/** Ce que fait chaque entree du menu. */
function lancerLActionDuMenu(cle, o, actions) {
  const action = actions.find((a) => a.cle === cle);
  if (!action) return;
  if (cle === 'fiche') { ouvrirDepuisLeMenu(o); return; }
  // `actif` dit ou l'on en est ; on va donc a l'inverse. C'est la seule facon
  // de basculer sans redemander l'etat au moment du clic — il n'a pas pu
  // changer entre l'ouverture du menu et le choix d'une ligne.
  const patch = cle === 'biblio' ? { suit: action.actif !== true }
    : cle === 'vu' ? { vu: action.actif !== true }
      : cle === 'notifs' ? { notifs: action.actif !== true }
        : null;
  if (!patch) return;
  poserSurLeTitre(o.id, patch, o);
}

/**
 * Les actions du menu d'un episode.
 *
 * ⚠️ ELLES SONT COURTES, ET C'EST VOULU. Un menu contextuel qu'on ouvre sur une
 * ligne parmi trois cents doit se lire d'un coup : trois entrees se parcourent
 * sans bouger les yeux, huit se lisent. Ce qui n'est pas ici se trouve sur la
 * fiche, qui est a un clic.
 */
function actionsDeLEpisode({ saison, episode }, vu) {
  return [
    {
      cle: 'ep-vu',
      // Le libelle PORTE l'etat, comme celui de l'oeuvre : « Marquer comme vu »
      // et « Marquer comme non vu » se distinguent sans coche ni couleur.
      libelle: vu ? 'Marquer comme non vu' : 'Marquer comme vu',
      actif: vu,
    },
    { cle: 'ep-lire', libelle: `Lire l’épisode ${episode}` },
    ...(peutRegler() ? [{ cle: 'ep-reglages', libelle: 'Réglages du lien' }] : []),
  ];
}

/**
 * Le menu d'un episode. Toji, 14/09 : « pour les episodes, on doit pouvoir clic
 * droit pour noter ce qu'on a vu ».
 */
function ouvrirLeMenuDEpisode(ep, { x, y }) {
  const vu = episodeVuIci(ep.oeuvreId, ep.saison, ep.episode);
  const actions = actionsDeLEpisode(ep, vu);
  ouvrirLeMenuDOeuvre(oeuvre ?? { id: ep.oeuvreId }, {
    x, y, venuDe: ep.ligne, actions,
    etiquette: `Options pour l’épisode ${ep.episode}`,
    executer: (cle) => {
      if (cle === 'ep-vu') { basculerEpisodeVu(ep); return; }
      if (cle === 'ep-lire') { ep.ligne.click(); return; }
      if (cle === 'ep-reglages' && !modeMapping) basculerModeMapping();
    },
  });
}

/**
 * « Ouvrir la fiche » depuis le menu.
 *
 * ⚠️ UN RESULTAT DE RECHERCHE HORS CATALOGUE DOIT Y ENTRER D'ABORD. C'est la
 * meme regle que le clic gauche sur la meme carte (voir `peindreResultats`) :
 * sans elle, le menu ouvrirait une fiche que le serveur ne connait pas, et
 * l'ecran repondrait « introuvable » sur un titre qu'on a sous les yeux.
 */
async function ouvrirDepuisLeMenu(o) {
  if (o?.horsCatalogue) {
    try {
      await api('/api/catalogue/ajouter', { method: 'POST', body: JSON.stringify(o) });
      await chargerCatalogue(true);
    } catch (err) {
      tracer('menu', 'entree au catalogue refusee', err.message);
      if (!err.silencieux) direMot(`Impossible d’ouvrir la fiche : ${err.message}`);
      return;
    }
  }
  ouvrirOeuvre(o.id);
}

/** L'accueil porte la rangee « Ma bibliotheque » : elle suit le geste. */
function repeindreLAccueilSiVisible() {
  const ecran = $('v-accueil');
  if (!ecran || ecran.hidden) return;
  try { peindreAccueil(); } catch (err) { tracer('biblio', 'accueil non repeint', err.message); }
}

/** Ce qu'on dit quand le serveur refuse — en nommant le geste, pas le code. */
function refusLisible(patch) {
  if (patch.suit === false) return 'Le titre n’a pas été retiré de ta bibliothèque';
  if (patch.suit === true) return 'Le titre n’a pas été ajouté à ta bibliothèque';
  if (patch.vu === true) return 'Le « déjà vu » n’a pas été enregistré';
  if (patch.vu === false) return 'Le « déjà vu » n’a pas été retiré';
  if (patch.notifs !== undefined) return 'Les notifications n’ont pas changé';
  return 'Le changement n’a pas été enregistré';
}

/**
 * Dit le sort de la cloche, et seulement quand il y a quelque chose a dire.
 *
 * ⚠️ LE SERVEUR PEUT AVOIR REMIS LA CLOCHE A ZERO. Discord n'a pas de demande
 * d'autorisation : le bot essaie d'ecrire, et si la personne a ferme ses
 * messages prives, l'abonnement ne sert a rien — le serveur le refuse donc, et
 * `entree.notifs` revient a `false`. On le DIT, parce qu'une cloche qui
 * s'eteint toute seule sans un mot se lit comme une panne.
 */
function direLeSortDeLaCloche(patch, reponse) {
  if (patch.notifs !== true || !reponse) return;
  const n = reponse.notification;
  if (reponse.entree?.notifs === true) {
    direMot('C’est noté : tu seras prévenu des nouveaux épisodes.');
    return;
  }
  if (n?.raison === 'aucun-serveur-commun') {
    direMot('Le bot ne partage aucun serveur Discord avec toi : il ne peut pas t’écrire.');
    return;
  }
  if (n?.ferme) {
    direMot('Discord ne laisse pas le bot t’écrire : ouvre les messages privés du serveur, puis réessaie.');
    return;
  }
  direMot(`Les notifications n’ont pas pu être activées${n?.detail ? ` : ${n.detail}` : ''}.`);
}

/**
 * Pose un changement sur un titre. L'ECRAN REPOND AVANT LE SERVEUR.
 *
 * Toji : le geste doit se voir tout de suite — cliquer « déjà vu » et attendre
 * un aller-retour pour voir l'œil apparaître donne l'impression que le clic
 * n'a pas été pris, et on reclique.
 *
 * ⚠️ MAIS PAS D'UI FAUSSE : si le serveur refuse, l'etat REVIENT EXACTEMENT
 * comme il etait — y compris la rangee de l'accueil — et un mot dit pourquoi.
 * Une coche laissee posee sur un refus est pire que pas de coche du tout :
 * elle se croit enregistree et on la retrouve absente au rechargement suivant.
 *
 * ⚠️ ET LE SERVEUR A LE DERNIER MOT. Sa reponse ecrase l'etat optimiste, meme
 * quand elle reussit : c'est lui qui date l'ajout, et c'est lui qui peut
 * refuser une cloche tout en acceptant le reste.
 */
async function poserSurLeTitre(oeuvreId, patch, o = null) {
  const id = String(oeuvreId ?? '');
  if (!id) return { ok: false };
  const avant = maBiblio.get(id) ?? null;
  const etaitDedans = avant !== null;
  const retire = patch.suit === false;

  if (retire) maBiblio.delete(id);
  else {
    const vu = patch.vu === undefined ? avant?.vu === true : patch.vu === true;
    maBiblio.set(id, {
      oeuvreId: id,
      // La date d'ajout appartient au premier geste : marquer « vu » un titre
      // garde il y a six mois ne doit pas le faire remonter en tete.
      ajoute: Number(avant?.ajoute) || Date.now(),
      notifs: patch.notifs === undefined ? avant?.notifs === true : patch.notifs === true,
      dernierPrevenu: avant?.dernierPrevenu ?? null,
      vu,
      vuLe: vu ? (Number(avant?.vuLe) || Date.now()) : null,
    });
    // Le titre entre peut-etre dans la bibliotheque a l'instant : la rangee doit
    // savoir le peindre, et c'est ici qu'on a sa fiche sous la main.
    if (o && !fichesBiblio.has(id)) fichesBiblio.set(id, o);
  }
  rafraichirMarques(id);
  const changeDAppartenance = etaitDedans !== maBiblio.has(id);
  if (changeDAppartenance) repeindreLAccueilSiVisible();

  try {
    let reponse = null;
    if (retire) await api(`/api/bibliotheque/${encodeURIComponent(id)}`, { method: 'DELETE' });
    else {
      reponse = await api(`/api/bibliotheque/${encodeURIComponent(id)}`, {
        method: 'PUT', body: JSON.stringify(patch),
      });
      if (reponse?.entree) {
        maBiblio.set(id, { oeuvreId: id, ...reponse.entree });
        rafraichirMarques(id);
      } else {
        // ⚠️ `entree: null` NE VEUT PAS DIRE « RIEN A SIGNALER » : IL VEUT DIRE
        // « JE N'AI RIEN ECRIT ».
        //
        // Le serveur repond cela quand il refuse d'agir sans le dire par une
        // erreur — decocher « deja vu » sur un titre qui n'est pas dans la
        // bibliotheque, par exemple : l'y faire entrer pour l'en ressortir
        // aussitot n'aurait aucun sens, donc il ne fait rien (server.js, la
        // porte du « vu »). L'ecran, lui, avait deja pose l'entree en optimiste.
        // La garder laissait un SIGNET sur une affiche qui n'est dans aucune
        // bibliotheque — une marque qu'on retrouve absente au rechargement
        // suivant, sans que rien n'ait jamais dit non.
        if (avant) maBiblio.set(id, avant); else maBiblio.delete(id);
        rafraichirMarques(id);
        if (changeDAppartenance) repeindreLAccueilSiVisible();
      }
    }
    direLeSortDeLaCloche(patch, reponse);
    return { ok: true, entree: maBiblio.get(id) ?? null };
  } catch (err) {
    if (avant) maBiblio.set(id, avant); else maBiblio.delete(id);
    rafraichirMarques(id);
    if (changeDAppartenance) repeindreLAccueilSiVisible();
    tracer('biblio', 'geste refuse', { id, patch, raison: err.message });
    // ⚠️ `silencieux` veut dire « un ecran a deja ete pose » — c'est le cas de
    // l'attente d'autorisation. Un mot de plus par-dessus le brouillerait.
    if (!err.silencieux) direMot(`${refusLisible(patch)} — ${err.message}`);
    return { ok: false, erreur: err.message };
  }
}

// Les trois grilles qui portent des cartes d'oeuvre. Ailleurs — la fiche, la
// console de seance, l'administration — le clic droit reste celui du navigateur.
/**
 * Les ecrans ou le clic droit NE DOIT PAS ouvrir notre menu.
 *
 * ⚠️ C'ETAIT UNE LISTE BLANCHE, ET C'EST CE QUI PRODUISAIT LE DEFAUT.
 *
 * Toji, 14/09 : « pour les episodes, on doit pouvoir clic droit pour noter ce
 * qu'on a vu. La, ce n'est pas le cas, PAS TOUT LE TEMPS. C'est un peu bugue. »
 *
 * Trois ecrans etaient nommes — accueil, catalogue, recherche — et le menu se
 * taisait partout ailleurs : sur la fiche d'une oeuvre, dans une collection,
 * dans une serie. Une carte d'oeuvre y ressemble pourtant trait pour trait a
 * celle de l'accueil, donc rien ne laissait deviner pourquoi le clic droit
 * repondait ici et pas la. Une liste blanche oublie chaque ecran qu'on ajoute
 * ensuite, en silence, et c'est exactement ce qui s'est passe.
 *
 * On nomme donc l'INVERSE : les trois endroits ou un menu d'oeuvre n'a aucun
 * sens. Un ecran neuf herite du menu sans qu'on y pense, ce qui est le bon
 * defaut — et s'il ne doit pas l'avoir, il s'ajoute ici, une ligne.
 */
const ECRANS_SANS_MENU = [
  // Pendant une lecture, le clic droit sert a la video (plein ecran, vitesse,
  // « enregistrer sous ») : le confisquer serait une regression, pas un ajout.
  'v-lecture',
  // L'administration manipule des lignes, pas des oeuvres.
  'v-admin',
  // Avant d'etre entre, il n'y a rien a marquer comme vu.
  'v-login',
];

/** Vrai si ce noeud est dans un ecran ou notre menu ne doit pas s'ouvrir. */
function dansUnEcranSansMenu(noeud) {
  for (const id of ECRANS_SANS_MENU) {
    const ecran = $(id);
    if (ecran?.contains(noeud)) return true;
  }
  return false;
}

/** La carte d'oeuvre sous ce noeud, ou `null`. */
function carteAMenu(noeud) {
  const carte = noeud?.closest?.('.st-carte[data-oeuvre]');
  if (!carte || dansUnEcranSansMenu(carte)) return null;
  return carte;
}

/**
 * L'episode sous ce noeud, ou `null`.
 *
 * ⚠️ UN EPISODE N'EST PAS UNE CARTE D'OEUVRE, et c'est la seconde moitie du
 * defaut. Les lignes d'episode sont des `<button class="ep" data-s data-e>` :
 * elles ne portent pas `data-oeuvre`, ne sont pas des `.st-carte`, et le menu
 * ne les a donc JAMAIS vues — pas « pas tout le temps », jamais. C'est
 * pourtant la, sur la fiche d'une serie, que Toji veut marquer ce qu'il a vu.
 *
 * L'oeuvre vient de la fiche ouverte (`oeuvre`), la seule chose qui la sache :
 * la ligne, elle, ne connait que sa saison et son numero.
 */
function episodeAMenu(noeud) {
  const ligne = noeud?.closest?.('.ep[data-s][data-e]');
  if (!ligne || dansUnEcranSansMenu(ligne)) return null;
  if (!oeuvre?.id) return null;
  const saison = Number(ligne.dataset.s);
  const episode = Number(ligne.dataset.e);
  if (!Number.isFinite(saison) || !Number.isFinite(episode)) return null;
  return { ligne, oeuvreId: oeuvre.id, saison, episode };
}

/**
 * Branche le menu, une fois, sur toute la page.
 *
 * UN SEUL ECOUTEUR, pas un par carte : les grilles se repeignent en
 * permanence — fenetre virtuelle, tri, recherche — et rebrancher carte par
 * carte laisserait muettes toutes celles peintes apres coup. C'est deja la
 * regle de `brancherCartes`, pour la meme raison.
 */
function brancherMenuDesCartes() {
  document.addEventListener('contextmenu', (e) => {
    // Les episodes d'abord : une ligne d'episode peut se trouver dans une fiche
    // qui contient aussi des cartes d'oeuvre, et c'est la plus precise qui gagne.
    const ep = episodeAMenu(e.target);
    if (ep) {
      e.preventDefault();
      ouvrirLeMenuDEpisode(ep, { x: e.clientX, y: e.clientY });
      return;
    }

    const carte = carteAMenu(e.target);
    // ⚠️ ON NE PREND RIEN : le menu du navigateur s'ouvre comme d'habitude.
    if (!carte) return;
    const o = ficheConnue(carte.dataset.oeuvre);
    // Un titre dont on ne sait rien n'a rien a proposer — et surtout, on ne
    // saurait pas s'il s'agit d'un film. Le menu du navigateur reprend la main.
    if (!o) return;
    e.preventDefault();
    ouvrirLeMenuDOeuvre(o, { x: e.clientX, y: e.clientY, venuDe: carte });
  });

  document.addEventListener('keydown', (e) => {
    // La touche Menu, et son equivalent sur les claviers qui ne l'ont pas.
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
    // Au clavier aussi : la touche Menu sur un episode focalise doit faire la
    // meme chose que le clic droit, sinon le raccourci ment sur la moitie des
    // elements de la page.
    const epFocal = episodeAMenu(document.activeElement);
    if (epFocal) {
      e.preventDefault();
      const ou = epFocal.ligne.getBoundingClientRect();
      ouvrirLeMenuDEpisode(epFocal, { x: Math.round(ou.left + 12), y: Math.round(ou.top + 12) });
      return;
    }
    const carte = carteAMenu(document.activeElement);
    if (!carte) return;
    const o = ficheConnue(carte.dataset.oeuvre);
    if (!o) return;
    e.preventDefault();
    // Sans curseur, le menu part du coin haut-gauche de l'affiche : c'est la
    // ou le regard est deja, et `placerLeMenu` le retourne si le bord approche.
    const ou = carte.getBoundingClientRect();
    ouvrirLeMenuDOeuvre(o, { x: Math.round(ou.left + 12), y: Math.round(ou.top + 12), venuDe: carte });
  });
}

// ---------------------------------------------------------------------------
// L'ACCUEIL NE SE TRIE PAS — DEPUIS LE 12/09.
//
// Il y avait un reglage personnel « Ordre des rangees de l'accueil » (recent,
// note, titre, cumulables). Toji : « je ne touche a rien et je vois les
// affiches changer de place, ca change en continu — ce n'etait pas du tout
// l'objectif ». Les rangees montrent l'ordre du catalogue tel qu'il est ecrit
// — un MIX recompose a heure fixe, le meme pour tout le monde. Le tri ne vit
// plus que dans la grille, en un seul menu deroulant. L'ancienne preference
// de tri de l'accueil encore posee dans un navigateur est inerte : ni lue, ni
// ecrite.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LES RANGEES VIENNENT DU SERVEUR, QUI VOIT TOUT LE CATALOGUE
//
// ⚠️ LA PAGE N'EN VOYAIT QUE TROIS CENTS.
//
// L'accueil triait `catalogue`, qui ne contient que la premiere page tant que
// la grille n'a pas ete ouverte — et cette page est l'ordre du FICHIER, pas un
// classement. Une serie qui sort un episode ce soir et qui se trouve plus loin
// dans le fichier ne pouvait pas remonter, quoi qu'il arrive.
//
// Le serveur classe les douze mille et ne renvoie que ce qui s'affiche. La
// page garde le repli local : tant que la reponse n'est pas la, mieux vaut une
// rangee approximative qu'un ecran nu.
// ---------------------------------------------------------------------------
let rangeesAccueil = null;
const fichesDAccueil = new Map();

async function chargerAccueil() {
  try {
    // Pas de `tri=` : l'accueil montre l'ordre du catalogue, et rien d'autre.
    const d = await api(`/api/accueil?dispo=${queDuDispo() ? 'oui' : 'non'}`);
    const fiches = normaliserCatalogue(d?.oeuvres);
    if (!fiches.length) return rangeesAccueil;
    fichesDAccueil.clear();
    for (const o of fiches) fichesDAccueil.set(o.id, o);
    rangeesAccueil = d?.rangees ?? null;
    tracer('accueil', `${fiches.length} fiche(s) classees sur ${d?.total ?? '?'}`);
  } catch (err) {
    // L'accueil se peint quand meme, avec ce que la page connait : une rangee
    // approchee vaut mieux qu'une page vide.
    tracer('accueil', 'classement injoignable', err.message);
  }
  return rangeesAccueil;
}

/**
 * Les cartes d'une rangee : celles du serveur, ou ce que la page sait deja.
 *
 * `regime` vient de `peindreRangee`, qui seul sait si la rangee est sous les
 * yeux ou en reserve. Sans lui, toutes les cartes de l'accueil partaient en
 * `loading="lazy"` — un SECOND filtrage par-dessus l'observateur, avec son
 * propre seuil, que personne ne pouvait regler : la rangee etait peinte parce
 * qu'elle approchait, et ses images attendaient quand meme.
 */
function cartesDeLaRangee(nom, type, regime = 'reserve') {
  const options = { avance: regime, dpr: densiteEcran() };
  const ids = rangeesAccueil?.[nom];
  if (Array.isArray(ids) && ids.length) {
    const retenues = ids.map((id) => fichesDAccueil.get(id)).filter(Boolean);
    if (retenues.length) return retenues.map((o) => carteOeuvre(o, options));
  }
  if (nom === 'nouveautes') return nouveautes(catalogue, 20).map((o) => carteOeuvre(o, options));
  return chercherOeuvres(catalogue, { type, tri: 'mix', dispoSeulement: queDuDispo() })
    .map((o) => carteOeuvre(o, options));
}

/**
 * Vrai quand on ne veut voir que ce qui se lit vraiment.
 *
 * Éteint par défaut : l'examen de fond met des heures à faire le tour d'un
 * catalogue de deux mille titres, et l'allumer d'office masquerait, sur une
 * installation neuve, tout ce qu'il n'a pas encore regardé.
 */
function queDuDispo() {
  return prefs.lire('dispo-seulement', 'non') === 'oui';
}

/**
 * Les rayons, dans l'ordre choisi — les VRAIS rayons, ceux qui ont un type.
 *
 * ⚠️ IL EXCLUAIT « Nouveautés » PAR SON NOM, ET RIEN D'AUTRE.
 *
 * « Ma bibliothèque » est arrivée dans les rangées réglables, et elle passait
 * donc ce filtre — alors qu'elle n'a pas d'entrée dans `RAYONS`. Le menu de
 * recherche lisait `RAYONS[nom].titre` sur `undefined` et tombait sur
 * « Recherche indisponible — Cannot read properties of undefined ». La
 * recherche entière était morte, et rien ne disait pourquoi.
 *
 * On ne liste plus par exclusion mais par APPARTENANCE : est un rayon ce qui
 * figure dans la table des rayons. Une rangée ajoutée demain ne pourra plus
 * casser cet écran par mégarde.
 */
function ordreDesRayons() {
  return ordreDesRangees().filter((r) => Boolean(RAYONS[r]));
}

/**
 * La grille, restreinte a ce qu'on a garde.
 *
 * `dans` est un ensemble d'identifiants : la grille garde tous ses filtres et
 * tous ses tris, elle ne voit simplement plus le reste du catalogue. Une vue de
 * plus, avec sa propre recherche et son propre tri, aurait diverge de celle-ci
 * a la premiere correction.
 */
function ouvrirLaBiblio() {
  filtresOeuvres.q = '';
  filtresOeuvres.genre = 'tout';
  filtresOeuvres.annee = 'tout';
  filtresOeuvres.type = 'tout';
  filtresOeuvres.format = 'tout';
  filtresOeuvres.saison = 'tout';
  filtresOeuvres.statut = 'tout';
  filtresOeuvres.dans = 'bibliotheque';
  triChoisi = 'mix';
  filtresOeuvres.tri = triChoisi;
  ouvrirCatalogueOeuvres({ garderLaSource: true });
}

/** Ouvre la grille sur un rayon. `tout` ouvre le catalogue entier. */
function ouvrirRayon(type) {
  filtresOeuvres.dans = null;
  filtresOeuvres.q = '';
  filtresOeuvres.genre = 'tout';
  filtresOeuvres.annee = 'tout';
  filtresOeuvres.type = type;
  // La grille s'ouvre dans l'ordre de la rangee dont elle vient : cliquer
  // « Voir tout » ne doit pas rebattre les cartes sous les yeux.
  triChoisi = 'mix';
  filtresOeuvres.tri = triChoisi;
  // Les sous-tris n'existent que pour les animes : les laisser posés en passant
  // aux films viderait la grille sans qu'aucun menu visible ne l'explique.
  if (type !== 'anime') {
    filtresOeuvres.format = 'tout';
    filtresOeuvres.saison = 'tout';
    filtresOeuvres.statut = 'tout';
  }
  ouvrirCatalogueOeuvres();
}

/** L'accueil : reprendre, nouveautés, tout le catalogue. */
/**
 * La première rangée AFFICHÉE touche le haut de l'écran.
 *
 * Toji : « je voudrais que "Nouveauté" soit vraiment collée tout en haut. Une
 * fois qu'on a regardé quelque chose, "Reprendre" se colle tout en haut à la
 * place, et "Nouveauté" se place à l'élément en dessous. »
 *
 * ⚠️ LE CSS SEUL NE PEUT PAS RÉPONDRE. « Reprendre » reste dans le document
 * quand il n'y a rien à reprendre — il est masqué, pas retiré — et
 * `:first-of-type` désigne donc toujours cette rangée-là, y compris invisible.
 * La première rangée visible se désigne ici, où on la connaît.
 */
function collerLaPremiereRangee(hote) {
  let premiere = true;
  for (const rangee of hote.querySelectorAll('.st-rangee')) {
    const visible = !rangee.hidden && rangee.innerHTML !== '';
    rangee.classList.toggle('st-rangee--tete', visible && premiere);
    if (visible) premiere = false;
  }
}

/**
 * Ce que le hero de l'accueil met en avant : la reprise la plus récente, sinon
 * la première sortie récente du catalogue. `null` sans rien des deux.
 */
function sujetDuHero() {
  const entree = reprises.reduce((a, r) => ((r.quand ?? 0) > (a?.quand ?? -1) ? r : a), null);
  if (entree) {
    const o = entree.oeuvre ?? { id: entree.oeuvreId, titre: entree.titre, fond: entree.vignette, resume: '' };
    return { o, entree };
  }
  const ids = rangeesAccueil?.nouveautes;
  const o = (Array.isArray(ids) ? ids.map((id) => fichesDAccueil.get(id)).find(Boolean) : null)
    ?? nouveautes(catalogue, 1, { dispoSeulement: queDuDispo() })[0];
  return o ? { o, entree: null } : null;
}

/**
 * LE HERO DE L'ACCUEIL (étude UI 22/10, point 1) : la décision en haut.
 *
 * La bannière était prête et éteinte depuis la refonte (voir `#st-acc-hero`) :
 * on la remplit. Le fond est celui de la fiche (`fondDeLOeuvre`, grand palier
 * devant, petit derrière — `poserFondDeBanniere`), la vignette d'épisode à
 * défaut si elle est nette. Le titre en serif : la source ne fournit aucun
 * logo-titre (MESURÉ : aucun champ `logo` au catalogue), on n'en invente pas.
 *
 * ⚠️ PAS DE TEMPS RESTANT DANS LE BOUTON (Toji, 21/10 : retiré de « Reprendre »,
 * la décision prime sur l'étude UI) : « Reprendre S1E4 », et la seconde voyage
 * jusqu'au lecteur par `reprendreLecture`, comme depuis la carte.
 *
 * ⚠️ AUCUNE VIDÉO : une image de fond et deux boutons. L'aperçu vidéo
 * automatique est refusé par l'étude (bande passante, phase 4).
 */
function peindreHeroAccueil() {
  const hero = $('st-acc-hero');
  if (!hero) return;
  const sujet = sujetDuHero();
  hero.classList.remove('st-squelette');
  if (!sujet) { hero.hidden = true; return; }
  const { o, entree } = sujet;
  poserFondDeBanniere($('st-acc-fond'), fondDeLOeuvre(o) || vignetteNette(entree?.vignette) || '');
  texte('st-acc-surtitre', entree ? 'Reprendre' : 'Sortie récente');
  texte('st-acc-titre', o.titre ?? entree?.titre ?? '');
  texte('st-acc-reperes', entree ? sousTitreDeReprise(entree) : [typeLisible(o), o.annee].filter(Boolean).join(' · '));
  texte('st-acc-resume', o.resume ?? '');
  const code = entree?.saison > 0 && entree?.episode > 0 ? ` S${entree.saison}E${entree.episode}` : '';
  texte('st-acc-regarder-mot', entree ? `Reprendre${code}` : 'Regarder');
  // Sans reprise, « Regarder » ouvre la fiche : c'est elle qui choisit l'épisode
  // (un second bouton « Fiche » y mènerait aussi — il se retire).
  $('st-acc-regarder').onclick = () => (entree ? reprendreLecture(entree) : ouvrirOeuvre(o.id));
  const fiche = $('st-acc-fiche');
  fiche.hidden = !entree;
  fiche.onclick = () => ouvrirOeuvre(o.id);
  hero.hidden = false;
}

/**
 * LES SQUELETTES DE L'ACCUEIL (étude UI 22/10, point 5).
 *
 * Avant les données, l'accueil était VIDE : un fond noir, puis tout d'un coup.
 * On pose le hero en squelette (sa vraie boîte, des barres à la place du texte)
 * et trois rangées d'affiches aux classes des vraies (`.st-carte`,
 * `.st-carte-img`…) : mêmes tailles par construction, et `peindreAccueil` les
 * retire au moment de peindre. Seulement à froid — un accueil déjà peint se
 * repeint par-dessus lui-même, sans clignoter.
 */
function squelettesDAccueil() {
  const hote = $('v-accueil')?.querySelector('.st-rangees');
  if (!hote || hote.querySelector('.st-carte')) return;
  const hero = $('st-acc-hero');
  if (hero) {
    hero.classList.add('st-squelette');
    for (const id of ['st-acc-surtitre', 'st-acc-titre', 'st-acc-reperes', 'st-acc-resume']) texte(id, ' ');
    $('st-acc-fiche').hidden = true;
    hero.hidden = false;
  }
  const barre = '<i class="st-sq-barre"></i>';
  const carte = `<span class="st-carte"><span class="st-carte-img"></span><span class="st-carte-titre">${barre}</span><span class="st-carte-sous">${barre}</span></span>`;
  const rangee = `<div class="st-rangee"><p class="st-rangee-titre"><span>${barre}</span></p><div class="st-rangee-liste">${carte.repeat(cartesParRangee())}</div></div>`;
  const zone = zoneOuCreee(hote, 'acc-squelettes', { classe: 'st-squelette' });
  zone.setAttribute('aria-hidden', 'true');
  zone.innerHTML = rangee.repeat(3);
  zone.firstElementChild.classList.add('st-rangee--tete');
  hote.prepend(zone);
}

function peindreAccueil() {
  const vue_accueil = $('v-accueil');
  if (!vue_accueil) return;
  $('acc-squelettes')?.remove();
  peindreHeroAccueil();
  peindreEnCeMomentAccueil();
  // ⚠️ LES RANGEES VIVENT DANS `.st-rangees`, PAS DANS LA VUE.
  //
  // Le réordonnancement plus bas faisait `hote.appendChild(zone)` avec la VUE
  // pour hôte : chaque rangée réordonnée sortait donc de son conteneur pour
  // atterrir après lui. « Reprendre », jamais réordonné, restait dedans — d'où
  // un accueil coupé en deux, avec le padding de fin de bloc (72 px) glissé au
  // milieu, entre la première rangée et toutes les autres.
  const hote = vue_accueil.querySelector('.st-rangees') ?? vue_accueil;

  // ⚠️ Les planches de l'ancien accueil (Films / Anime / Live action) sont un
  // découpage en trois catégories fixes : elles disaient « il y a une série et
  // deux collections ». Elles n'ont plus de sens face à un catalogue, et on les
  // cache plutôt que d'attendre que le balisage les retire — deux accueils
  // superposés ne se lisent pas.
  const planches = $('planches');
  if (planches) planches.hidden = true;

  // ⚠️ UNE FONCTION, PLUS UN TABLEAU. Le regime de chargement des images n'est
  // connu qu'au moment de la peinture — c'est la rangee, une fois posee, qui
  // sait si elle est sous les yeux. Fabriquer le HTML ici le figeait avant de
  // le savoir. `peindreRangee` hide toujours une rangee vide, la liste soit-elle
  // fabriquee plus tard : « Reprendre » sans reprise disparait comme avant.
  const enCours = (regime) => reprises.map((r) => {
    const o = r.oeuvre ?? { id: r.oeuvreId, titre: r.titre, affiche: r.vignette, fond: r.vignette, note: null };
    // `type` : une œuvre hors catalogue n'en a pas, et la carte d'un film
    // disait « Série » (La Fin d'Oak Street, 22/10).
    // Les vieilles vignettes TVDB (`/banners/episodes/`) font 400×225 : floues
    // sur une carte de 320 px (Toji, 22/10). Le fond HD de la série les remplace.
    const vignette = vignetteNette(r.vignette) ?? o.fond ?? r.vignette;
    return carteOeuvre({ ...o, type: o.type ?? r.type, affiche: vignette ?? o.affiche }, {
      paysage: true,
      sous: sousTitreDeReprise(r),
      part: r.part,
      avance: regime,
      dpr: densiteEcran(),
      // Toji : « pour le reprendre, ça serait bien qu'on ait une croix en haut à
      // droite, de façon à ce qu'on puisse enlever quelque chose qu'on ne veut
      // plus avoir. » Une reprise qu'on ne veut plus reste sinon en tête de
      // l'accueil jusqu'à expiration — la seule façon de s'en débarrasser était
      // de finir l'épisode.
      retirable: true,
    });
  });
  peindreRangee(hote, 'acc-reprendre', 'Reprendre', enCours, {
    paysage: true,
    surCroix: async (id) => {
      // On retire d'abord de la liste en mémoire : l'écran répond au clic sans
      // attendre le serveur, et la carte ne réapparaît pas le temps d'un
      // aller-retour. Un échec la ramènera au prochain chargement, ce qui est
      // le bon comportement — elle n'aura pas été retirée.
      reprises = reprises.filter((r) => r.oeuvreId !== id);
      peindreAccueil();
      try {
        await api(`/api/reprendre/${encodeURIComponent(id)}`, { method: 'DELETE' });
      } catch (err) {
        tracer('reprendre', 'retrait refusé', err.message);
      }
    },
    // L'affiche reprend là où l'on s'est arrêté.
    surCarte: (id) => {
      const entree = reprises.find((r) => r.oeuvreId === id);
      if (entree) reprendreLecture(entree);
      else ouvrirOeuvre(id);
    },
    // Le titre et la ligne d'épisode ouvrent la fiche, donc le choix d'épisode :
    // c'est ce qu'on vient chercher quand on lit le texte plutôt que l'image.
    surTexte: (id) => ouvrirOeuvre(id),
  });
  // Les rangées dans l'ordre voulu, et seulement celles qu'on veut voir. Une
  // rangée cachée est VIDÉE, pas seulement masquée : laisser son contenu dans
  // la page ferait payer un rendu que personne ne regarde.
  const cachees = rangeesCachees();
  for (const nom of ordreDesRangees()) {
    const zone = zoneDeLaRangee(nom);
    if (cachees.has(nom)) {
      if (zone) { zone.hidden = true; zone.innerHTML = ''; }
      continue;
    }
    if (nom === 'bibliotheque') {
      // Elle se cache toute seule quand elle est vide : une rangee « Ma
      // bibliotheque » sans rien dedans occupe la place sans rien dire.
      peindreRangee(hote, 'acc-biblio', 'Ma bibliothèque',
        (regime) => oeuvresDeLaBiblio().map((o) => carteOeuvre(o, { avance: regime, dpr: densiteEcran() })),
        { tout: () => ouvrirLaBiblio(), surCarte: ouvrirOeuvre });
      continue;
    }
    if (nom === 'nouveautes') {
      peindreRangee(hote, 'acc-nouveautes', 'Nouveautés',
        (regime) => cartesDeLaRangee('nouveautes', null, regime),
        { tout: () => ouvrirRayon('tout'), surCarte: ouvrirOeuvre });
      continue;
    }
    const { id, titre, type } = RAYONS[nom];
    // La liste n'est fabriquée qu'au moment où la rangée se peint : sur cent
    // soixante-huit œuvres, construire tout le HTML à l'ouverture coûterait
    // plus cher que ce qu'on en montre.
    // ⚠️ DU PLUS RECENT AU PLUS ANCIEN, PAS DE A A Z.
    //
    // Le tri alphabetique mettait « 009-1 The End of the Beginning », un anime
    // de 2013, en tete du rayon des animes — les chiffres passent avant les
    // lettres. Une rangee d'accueil montre ce qui sort, pas le debut de
    // l'annuaire : c'est la grille, avec ses filtres, qui sert a chercher un
    // titre precis.
    peindreRangee(hote, id, titre,
      (regime) => cartesDeLaRangee(nom, type, regime),
      { tout: () => ouvrirRayon(type), surCarte: ouvrirOeuvre });
  }

  // ⚠️ L'ORDRE DU DOM SUIT L'ORDRE CHOISI. `zoneOuCreee` ajoute une rangée à la
  // fin la première fois : sans ce replacement, réordonner ne changerait rien
  // pour qui a déjà ouvert l'accueil une fois dans cette page.
  for (const nom of ordreDesRangees()) {
    const zone = zoneDeLaRangee(nom);
    if (zone && !zone.hidden) hote.appendChild(zone);
  }
  collerLaPremiereRangee(hote);

  // Le catalogue n'a pas répondu : on le DIT. Un accueil vide sans un mot se
  // lit comme « il n'y a rien à regarder », ce qui est une autre information.
  const mot = zoneOuCreee(hote, 'acc-vide', { balise: 'p', classe: 'st-vide' });
  if (mot) {
    mot.hidden = catalogue.length > 0 || reprises.length > 0;
    mot.textContent = 'Le catalogue est vide ou injoignable.';
  }
}

/**
 * Quitter l'adresse d'une séance quand on quitte la séance.
 *
 * ⚠️ L'ADRESSE DOIT DIRE OU L'ON EST, SINON LE RECHARGEMENT MENT.
 *
 * `/lecteur/<alias>` restait dans la barre après être revenu à l'accueil : on
 * pouvait donc être sur le catalogue avec, écrite au-dessus, l'adresse d'une
 * séance. Recharger relisait cette adresse — la seule chose qui survive à un
 * rechargement — et l'application faisait ce qu'elle promet : elle ouvrait la
 * séance. Toji : « je reload dans l'accueil et ça me revient dans l'onglet
 * séance ».
 *
 * La séance, elle, ne se perd pas : elle vit dans le service, le bandeau la
 * montre, et la liste des séances y ramène. Ce qu'on retire, c'est une adresse
 * qui ne décrit plus ce qu'on regarde.
 */
function quitterLAdresseDeSeance() {
  if (!/^\/lecteur\//.test(location.pathname)) return;
  try { history.replaceState(null, '', '/'); } catch { /* sans effet sur la navigation */ }
}

// ---------------------------------------------------------------------------
// OU L'ON ARRIVE — UNE REGLE, PLUS UNE LIGNE ECRITE EN DUR
//
// Toji : « je peux définir que pour mon utilisateur à moi ça m'accède à
// l'accueil ; les gens à qui je donne l'accès au site, ça leur donne accès à
// l'accueil ; les gens qui viennent depuis Discord, ça les met sur la session ».
//
// Trois populations, parce qu'elles n'arrivent pas pour la même chose. Le
// serveur donne la table et la catégorie de la personne ; le client ajoute la
// seule chose que le serveur ne peut pas savoir — est-on dans l'activité
// Discord ou sur le site.
//
// ⚠️ CETTE REGLE NE VAUT QU'A L'ARRIVEE. Recharger un onglet n'est pas arriver :
// on rend alors la page qu'on avait sous les yeux (`sessionStorage`), et la
// règle ne se réapplique qu'à la prochaine ouverture.
// ---------------------------------------------------------------------------
const ECRAN_DE = {
  accueil: () => ouvrirAccueil(),
  catalogue: () => ouvrirCatalogueOeuvres(),
  seances: () => ouvrirLesSeances(),
};

function ecranRetenuDeLOnglet() {
  try {
    // Un autre `frame_id` : l'activité a été relancée, ce n'est pas un rechargement.
    if (DANS_DISCORD && (sessionStorage.getItem('14d.ecran-cadre') ?? '') !== CADRE_DISCORD) return '';
    return sessionStorage.getItem('14d.ecran-onglet') || '';
  } catch { return ''; }
}

async function ouvrirLaPorte() {
  // Un rechargement : on rend ce qu'on avait, sans consulter la règle.
  const rouvert = ecranRetenuDeLOnglet();
  if (rouvert && rouvert !== 'v-lecture' && ECRANS_RETENUS.has(rouvert) && $(rouvert)) {
    tracer('demarrage', `rechargement · ${rouvert}`);
    if (rouvert === 'v-gestion') { vue('v-gestion'); peindreGestion(); return; }
    if (rouvert === 'v-sessions') return ouvrirLesSeances();
    if (rouvert === 'v-catalogue') return ouvrirCatalogueOeuvres();
    if (rouvert === 'v-oeuvre' || rouvert === 'v-serie') {
      if (oeuvre) { vue(rouvert); peindreFicheOeuvre(); return; }
      // ⚠️ APRES UN RECHARGEMENT, `oeuvre` EST VIDE — c'est une variable de la
      // page, et la page vient de repartir de zero. On retombait donc sur
      // l'accueil, ce qui est precisement ce que Toji ne veut pas : « quand on
      // reload, je veux que ça remette la page en question ». La position
      // retenue porte l'identifiant : il suffit de la rouvrir.
      const ou = positionMemorisee();
      if (ou?.oeuvre) return ouvrirOeuvre(ou.oeuvre);
      return ouvrirAccueil();
    }
    // La page de resultats se retrouve avec sa requete : sans elle, elle
    // s'ouvrirait vide et il faudrait retaper ce qu'on venait de chercher.
    if (rouvert === 'v-recherche') {
      const ou = positionMemorisee();
      if (ou?.q) return ouvrirRecherche(ou.q);
      return ouvrirAccueil();
    }
    return ouvrirAccueil();
  }

  // Une arrivée : la règle décide. `moi` est déjà là le plus souvent ; sinon on
  // le demande, plutôt que de deviner une catégorie.
  let qui = moi;
  if (!qui) { try { qui = await api('/api/moi'); } catch { /* on prendra le défaut */ } }
  const population = DANS_DISCORD ? 'discord' : (qui?.categorie ?? 'invite');
  // L'activité arrive sur l'accueil (Toji, 23/10) : voir `ecranDeDemarrage`.
  const voulu = DANS_DISCORD ? 'accueil' : (qui?.accueilPar?.[population] ?? 'accueil');
  tracer('demarrage', `arrivée · ${population} → ${voulu}`);
  return (ECRAN_DE[voulu] ?? ECRAN_DE.accueil)();
}

/** L'accueil, chargé puis peint. Le seul chemin d'entrée. */
async function ouvrirAccueil() {
  quitterLAdresseDeSeance();
  vue('v-accueil');
  squelettesDAccueil();

  // ⚠️ LES DEUX APPELS PARTENT ENSEMBLE, MAIS ON NE PEINT QU'UNE FOIS PRET.
  //
  // Le catalogue et les reprises ne dependent pas l'un de l'autre : les
  // enchainer, c'etait additionner deux attentes pour un seul ecran. Ils
  // partent donc ensemble, et c'est ce parallelisme qui rend la rangee presque
  // immediate.
  //
  // Ce qu'on ne fait PAS : peindre avant que les deux soient la. Essaye le
  // 08/09 avec les reprises gardees en memoire du navigateur — la rangee
  // apparaissait bien plus tot, mais dans un etat a moitie construit : une
  // seule carte, plus large que les autres, sans sa ligne d'episode, puis tout
  // se replacait. Une rangee qui se corrige sous les yeux est pire qu'une
  // rangee qui arrive.
  await Promise.all([chargerCatalogue(), chargerReprises(), chargerAccueil(), chargerBiblio()]);
  peindreAccueil();
  // L'entrée en cascade ne part qu'avec des cartes : un accueil vide n'a rien
  // à faire entrer, et la fenêtre reste ouverte pour la peinture suivante.
  const vueAccueil = $('v-accueil');
  if (vueAccueil?.querySelector('.st-carte') && !vueAccueil.hidden && enEntree(vueAccueil)) {
    // Les rangs de la vague, posés une fois (voir la feuille : pas de
    // `:nth-child`, qui alourdirait la grille virtuelle).
    [...vueAccueil.querySelectorAll('.st-rangee:not([hidden])')].forEach((rangee, r) => {
      rangee.style.setProperty('--ent-rang', String(Math.min(r, 3)));
      [...rangee.querySelectorAll('.st-rangee-liste > .st-carte')].slice(0, 8)
        .forEach((carte, c) => carte.style.setProperty('--ent-place', String(c)));
    });
    vueAccueil.classList.add('cascade');
    setTimeout(() => vueAccueil.classList.remove('cascade'), DUREE_D_ENTREE);
  }
  // ⚠️ PLUS DE RAPPEL SUR HORLOGE NI AU RETOUR SUR L'ONGLET — COUPES LE 12/09.
  //
  // Un minuteur de trois minutes et un ecouteur de changement de visibilite
  // redemandaient l'accueil et le repeignaient. Tant que le classement bougeait
  // a chaque constat de l'examen de fond, c'etait « des qu'il est sorti, on le
  // remet a la premiere ligne » ; c'est devenu « je ne touche a rien et je vois
  // les affiches changer de place ». L'accueil se charge a l'ouverture et au
  // retour d'une fiche (ce chemin-ci), jamais tout seul. Entre deux versions
  // du catalogue, rien ne bouge.
}

// ---------------------------------------------------------------------------
// La grille : recherche et filtres
// ---------------------------------------------------------------------------

// Ce que la grille montre en ce moment. Un objet plutôt que quatre variables :
// il part tel quel dans `chercherOeuvres`, qui est la fonction éprouvée.
// UN SEUL critere de tri — `mix` (Sélection, l'ordre du catalogue), `titre`,
// `annee` ou `note` — jamais une combinaison (Toji, 12/09 : « à la place du
// multi-tri, fais simplement un tri avec un menu dropdown »).
let triChoisi = 'mix';

const filtresOeuvres = {
  q: '', type: 'tout', genre: 'tout', tri: 'mix', annee: 'tout',
  // Le sous-tri des animes. Il vit ici avec les autres, mais ne s'affiche et
  // n'agit que sur le rayon des animes — voir `monterBarreOeuvres`.
  format: 'tout', saison: 'tout', statut: 'tout',
  // Tous rayons : la saison d'anime (« Automne 2026 ») et la diffusion
  // (`saison-en-cours`, `en-diffusion`, `simulcasts`, `a-venir`).
  trimestre: 'tout', diffusion: 'tout',
};

let minuteurRecherche = null;

/**
 * La barre de recherche et de filtres, montée une seule fois.
 *
 * ⚠️ UNE SEULE FOIS, et c'est important : reconstruire le champ à chaque
 * peinture le remplacerait sous les doigts, ce qui perd le curseur à chaque
 * touche. La grille, elle, se repeint autant qu'on veut — elle n'a pas de
 * focus à préserver.
 */

// ---------------------------------------------------------------------------
// LES RANGEES DE L'ACCUEIL : LES MONTRER, LES CACHER, LES DEPLACER
//
// Toji : « chaque onglet on a un œil, et en fait c'est un drag and drop pas
// géré depuis le navigateur mais interne au système, qu'on puisse déplacer de
// haut en bas simplement, avec une petite animation bien gérée. »
//
// ⚠️ PAS L'API HTML5 DU NAVIGATEUR. `dragstart`/`drop` ne se déclenche pas au
// doigt sans rustine, dessine une image fantôme qu'on ne contrôle pas, et se
// comporte différemment dans la webview de Discord. Les événements de pointeur
// donnent un seul code pour la souris et le doigt, et une animation à nous.
//
// Le mouvement ne déplace RIEN dans le document tant qu'on n'a pas lâché : les
// lignes se contentent de glisser par `transform`. Réordonner le DOM pendant le
// geste ferait sauter la ligne sous le doigt à chaque franchissement.
// ---------------------------------------------------------------------------
const OEIL_OUVERT = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z"/><circle cx="12" cy="12" r="3"/></svg>';
const OEIL_FERME = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M2 12s3.5-6 10-6c1.6 0 3 .36 4.24.94M22 12s-3.5 6-10 6c-1.6 0-3-.36-4.24-.94"/><path d="m4 4 16 16"/></svg>';
const POIGNEE = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>';

function monterReglageDesRangees() {
  const zone = $('r-rangees');
  if (!zone) return;

  const ecrire = (ordre, cachees) => {
    prefs.ecrire('rayons-ordre', ordre.join(','));
    prefs.ecrire('rayons-caches', [...cachees].join(','));
    // L'accueil se repeint derrière : un réglage qu'il faut aller vérifier en
    // fermant le panneau ne se règle pas, il se devine.
    try { peindreAccueil(); } catch { /* l'accueil n'est pas encore chargé */ }
  };

  const peindre = () => {
    const ordre = ordreDesRangees();
    const cachees = rangeesCachees();
    zone.innerHTML = ordre.map((cle) => `<div class="rangee-ligne${cachees.has(cle) ? ' masquee' : ''}" role="listitem" data-rangee="${cle}">
        <span class="rangee-poignee" aria-hidden="true">${POIGNEE}</span>
        <span class="rangee-nom">${esc(NOM_DE_RANGEE[cle] ?? cle)}</span>
        <button type="button" class="rangee-oeil" data-oeil="${cle}"
          aria-label="${cachees.has(cle) ? 'Afficher' : 'Masquer'} la rangée ${esc(NOM_DE_RANGEE[cle] ?? cle)}"
          aria-pressed="${cachees.has(cle) ? 'false' : 'true'}">${cachees.has(cle) ? OEIL_FERME : OEIL_OUVERT}</button>
      </div>`).join('');

    for (const bouton of zone.querySelectorAll('[data-oeil]')) {
      bouton.onclick = () => {
        const cle = bouton.dataset.oeil;
        const suite = rangeesCachees();
        if (suite.has(cle)) suite.delete(cle); else suite.add(cle);
        ecrire(ordreDesRangees(), suite);
        peindre();
      };
    }
    for (const ligne of zone.querySelectorAll('.rangee-ligne')) brancherDeplacement(ligne, zone, peindre, ecrire);
  };

  peindre();
}

function brancherDeplacement(ligne, zone, peindre, ecrire) {
  const poignee = ligne.querySelector('.rangee-poignee');
  if (!poignee) return;

  poignee.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const lignes = [...zone.querySelectorAll('.rangee-ligne')];
    const depart = lignes.indexOf(ligne);
    const hauteur = rectPage(ligne).height + 6; // + la gouttière
    const y0 = e.clientY;
    let cible = depart;

    ligne.classList.add('attrapee');
    poignee.setPointerCapture(e.pointerId);

    const bouger = (ev) => {
      const dy = enPage(ev.clientY - y0);
      ligne.style.transform = `translateY(${dy}px)`;
      // Où la ligne tomberait si on lâchait maintenant. `round` plutôt que
      // `floor` : on bascule à la moitié d'une ligne, pas au bout — c'est ce
      // qui donne l'impression que les autres « s'écartent » au bon moment.
      const suite = Math.max(0, Math.min(lignes.length - 1, depart + Math.round(dy / hauteur)));
      if (suite === cible) return;
      cible = suite;
      // Les autres s'écartent, par translation : le document n'a pas bougé.
      lignes.forEach((autre, i) => {
        if (autre === ligne) return;
        let decalage = 0;
        if (depart < cible && i > depart && i <= cible) decalage = -hauteur;
        if (depart > cible && i < depart && i >= cible) decalage = hauteur;
        autre.style.transform = decalage ? `translateY(${decalage}px)` : '';
      });
    };

    const lacher = () => {
      poignee.removeEventListener('pointermove', bouger);
      poignee.removeEventListener('pointerup', lacher);
      poignee.removeEventListener('pointercancel', lacher);
      ligne.classList.remove('attrapee');
      for (const autre of lignes) autre.style.transform = '';

      if (cible !== depart) {
        const ordre = ordreDesRangees();
        const [deplacee] = ordre.splice(depart, 1);
        ordre.splice(cible, 0, deplacee);
        ecrire(ordre, rangeesCachees());
      }
      // On repeint dans tous les cas : c'est ce qui rebranche les écouteurs sur
      // les lignes à leur nouvelle place, et ce qui remet l'ordre du document
      // d'accord avec ce qu'on voit.
      peindre();
    };

    poignee.addEventListener('pointermove', bouger);
    poignee.addEventListener('pointerup', lacher);
    poignee.addEventListener('pointercancel', lacher);
  });
}

function monterBarreOeuvres(hote) {
  const champ = zoneOuCreee(hote, 'oeuvres-q', { balise: 'input', classe: 'st-recherche' });
  if (champ && !champ.dataset.branche) {
    champ.type = 'search';
    champ.placeholder = 'Chercher une œuvre…';
    champ.setAttribute('aria-label', 'Chercher une œuvre');
    champ.dataset.branche = '1';
    champ.oninput = () => {
      // La frappe ne repeint pas à chaque touche : on laisse le mot se finir.
      // Le filtrage est local — aucun appel réseau — mais une grille de deux
      // cents cartes reconstruite six fois par seconde saute sous le doigt.
      clearTimeout(minuteurRecherche);
      minuteurRecherche = setTimeout(() => {
        filtresOeuvres.q = champ.value.trim();
        peindreCatalogueOeuvres();
      }, 220);
    };
  }
  // ⚠️ JAMAIS SOUS LES DOIGTS. Chaque réponse de la grille repasse ici : si
  // l'on tapait encore, le champ était remis à la valeur de la frappe
  // PRÉCÉDENTE (`filtresOeuvres.q` ne suit qu'après l'anti-rebond) et les
  // lettres tapées entre-temps disparaissaient — MESURÉ au banc : « Série
  // Témoin » tapé à 110 ms/touche devenait « Srie Témoin ». C'était le « ça
  // plante au niveau de la frappe ». Le champ n'est resynchronisé que quand il
  // n'a pas le focus (retour sur l'écran, rayon ouvert depuis l'accueil).
  if (champ && document.activeElement !== champ && champ.value !== filtresOeuvres.q) champ.value = filtresOeuvres.q;

  const barre = zoneOuCreee(hote, 'oeuvres-filtres', { classe: 'st-filtres' });
  if (!barre) return;

  // Les genres viennent du catalogue lui-même : une liste écrite en dur
  // vieillirait au premier titre ajouté, et proposerait des filtres qui ne
  // rendent rien.
  // ⚠️ VENUES DU SERVEUR, PAS DE LA PAGE.
  //
  // La grille ne detient plus que la tranche affichee : deduire les genres de
  // ce qu'elle a sous la main donnerait un menu qui change a chaque
  // defilement. Le serveur les calcule sur les douze mille et les envoie avec
  // la premiere page ; tant qu'elles ne sont pas la, on retombe sur ce que la
  // page connait — c'est un menu incomplet, pas un menu vide.
  const genres = facettesGrille?.genres ?? genresDuCatalogue(catalogue);
  // Les années réellement présentes, de la plus récente à la plus ancienne.
  const annees = facettesGrille?.annees ?? anneesDuCatalogue(catalogue);
  const trimestres = facettesGrille?.trimestres ?? trimestresDuCatalogue(catalogue);

  // DES MENUS DÉROULANTS, PAS DES RANGÉES DE PASTILLES.
  //
  // Toji : « à la place de tous, c'est un menu dropdown avec : 1. Série,
  // 2. Film, 3. Anime. Ensuite, à droite de lui, on a un menu dropdown avec les
  // genres. »
  //
  // Ce que les pastilles coûtaient : neuf genres, cinq formats et quatre
  // saisons faisaient quatre lignes de boutons au-dessus de la grille, et le
  // catalogue commençait sous la ligne de flottaison. Un menu occupe la place
  // d'un seul bouton quel que soit le nombre de valeurs — et sur téléphone il
  // ouvre le sélecteur du système au lieu d'une rangée qui déborde.
  //
  // `<select>` natif, et non un menu dessiné : il se pilote au clavier, il
  // s'annonce aux lecteurs d'écran, et il ne demande aucun code pour se fermer
  // quand on clique ailleurs — trois choses qu'un menu maison redemande à
  // chaque fois.
  const menu = (cle, libelle, valeurs) => `<label class="st-menu">
      <span class="st-menu-nom">${esc(libelle)}</span>
      <select data-f="${esc(cle)}" aria-label="${esc(libelle)}">${valeurs
    .map(([v, t]) => `<option value="${esc(v)}"${filtresOeuvres[cle] === v ? ' selected' : ''}>${esc(t)}</option>`)
    .join('')}</select>
    </label>`;

  // TROIS RAYONS, ET RIEN D'AUTRE au premier étage.
  //
  // Toji : « à la place d'avoir tous les types, genre Netflix, on a simplement
  // les séries, mais on a vraiment le tri des séries. On n'a pas d'anime dans
  // le tri "séries" ».
  //
  // Un anime n'est donc pas une série animée : c'est son propre rayon. Le
  // classement fin — format, saison de diffusion, statut — n'apparaît qu'une
  // fois dans ce rayon-là, comme le fait AIOMetadata : poser ces trois
  // groupes en permanence donnerait six lignes de filtres au-dessus d'une
  // grille de films, dont trois qui ne peuvent rien changer.
  const rayonAnime = filtresOeuvres.type === 'anime';
  const sous = rayonAnime ? (facettesGrille?.animes ?? sousTrisAnime(catalogue)) : null;
  const menuSi = (liste, cle, libelle, tout) => (liste?.length
    ? menu(cle, libelle, [['tout', tout], ...liste.map((v) => [v, v])])
    : '');

  // Le type d'abord, les genres à sa droite. Le reste suit dans le même
  // vocabulaire — mélanger un menu et des pastilles sur la même ligne ferait
  // deux gestes différents pour la même chose.
  const html = menu('type', 'Type', [
    ['tout', 'Tout'], ['serie', 'Série'], ['film', 'Film'], ['anime', 'Anime'],
  ])
    + (genres.length
      ? menu('genre', 'Genre', [['tout', 'Tous les genres'], ...genres.map((g) => [g, g])])
      : '')
    // Le classement fin des animes ne s'ouvre que dans leur rayon : trois menus
    // de plus au-dessus d'une grille de films ne pourraient rien y changer.
    // Le sous-tri Séries / Films des animés : DEUX formes, toujours les mêmes
    // (Toji, 12/09 : « les animés séparés avec dedans le sous-tri séries /
    // films »). Une OVA, un spécial tombent côté séries — voir `formeAnime`.
    + (rayonAnime
      ? menu('format', 'Format', [['tout', 'Séries et films'], ['serie', 'Séries'], ['film', 'Films']])
        // La saison déclarée par la source ne s'affiche que s'il n'y a pas la
        // saison calculée (plus bas, tous rayons) : deux menus « Saison ».
        + (trimestres.length ? '' : menuSi(sous.saisons, 'saison', 'Saison', 'Toutes les saisons'))
        + menuSi(sous.statuts, 'statut', 'Statut', 'Tous les statuts')
      : '')
    // ⚠️ L'ANNEE EST UN FILTRE, PAS UN TRI.
    //
    // Toji : « ce qui serait bien, c'est que tu puisses rajouter une gestion
    // d'année : tu peux choisir l'année, de 2026 à très vieux ». « Cette année
    // et l'an dernier » vient en tête parce que c'est la question qu'on se pose
    // le plus souvent — ce qui sort en ce moment — et elle se calcule, pour ne
    // pas figer 2025 et 2026 dans du code qui leur survivra.
    + (annees.length
      ? menu('annee', 'Année', [
        ['tout', 'Toutes les années'],
        ['recentes', 'Cette année et l’an dernier'],
        // Une liste longue se parcourt aussi par décennie (Toji, 20/10).
        ...(annees.length > 12 ? decenniesDe(annees).map((d) => [`d${d}`, `Années ${d}`]) : []),
        ...annees.map((a) => [String(a), String(a)]),
      ])
      : '')
    // ⚠️ LA SAISON D'ANIME ET LA DIFFUSION (Toji, 20/10 : « par sortie de
    // saison », « Simulcasts », « tous ceux qui sortent actuellement »). Tous
    // rayons, cumulables avec le type et l'année. Les dates viennent
    // d'AIOMetadata (`periode`/`sortie` du catalogue, épisodes datés des fiches).
    + (trimestres.length
      ? menu('trimestre', 'Saison', [['tout', 'Toutes les saisons'], ...trimestres.map((t) => [t, t])])
      : '')
    + menu('diffusion', 'Diffusion', [
      ['tout', 'Tout'], ['saison-en-cours', 'Saison en cours'], ['en-diffusion', 'En cours de diffusion'],
      ['simulcasts', 'Simulcasts'], ['a-venir', 'À venir'],
    ])
    // ⚠️ « Disponible » N'EST PAS ICI. C'était un interrupteur en fin de barre ;
    // Toji : « le slider disponible, ça serait bien que tu le mettes plutôt
    // directement dans les réglages ». Ce n'est pas un critère de recherche de
    // plus — c'est ce que la personne veut voir partout, accueil compris, et un
    // réglage posé dans une barre de filtres se reperd à chaque écran.
    // ⚠️ UN SEUL TRI, EN MENU DEROULANT — PLUS DE PASTILLES CUMULABLES.
    //
    // Toji (12/09) : « à la place du multi-tri, fais simplement un tri avec un
    // menu dropdown ». Le meme bloc `.st-menu` que Type, Genre et Annee — la
    // feuille le connait deja, aucune regle nouvelle. « Sélection » est l'ordre
    // du catalogue tel qu'il est ecrit (le MIX) ; les trois autres sont des
    // classements simples, jamais combines. Le Genre reste un FILTRE, a part.
    + `<div class="st-menu">
        <span class="st-menu-nom">Tri</span>
        <select data-tri aria-label="Tri">${[
    ['mix', 'Sélection'], ['titre', 'A→Z'], ['annee', 'Année'], ['note', 'Note'],
    ['sorties', 'Dernières sorties'],
  ].map(([v, mot]) => `<option value="${esc(v)}"${triChoisi === v ? ' selected' : ''}>${esc(mot)}</option>`)
    .join('')}</select>
      </div>`;

  if (barre.innerHTML !== html) {
    barre.innerHTML = html;
    const tri = barre.querySelector('select[data-tri]');
    if (tri) {
      tri.onchange = () => {
        triChoisi = tri.value;
        filtresOeuvres.tri = triChoisi;
        peindreCatalogueOeuvres();
      };
    }
    for (const s of barre.querySelectorAll('select[data-f]')) {
      s.onchange = () => {
        filtresOeuvres[s.dataset.f] = s.value;
        // Quitter le rayon des animes remet son classement fin à plat : sinon
        // « OVA » reste posé dans le dos, invisible puisque son menu n'est plus
        // affiché, et vide la grille au retour sans qu'on voie pourquoi.
        if (s.dataset.f === 'type' && filtresOeuvres.type !== 'anime') {
          filtresOeuvres.format = 'tout';
          filtresOeuvres.saison = 'tout';
          filtresOeuvres.statut = 'tout';
        }
        peindreCatalogueOeuvres();
      };
    }
  }
}

// ---------------------------------------------------------------------------
// LA GRILLE NE PEINT QUE CE QU'ON REGARDE
//
// Toji : « tu te bases sur le viewport de la personne. Tu regardes où tombe le
// dernier élément, tu prends une marge de trois lignes, et les trois lignes
// doivent rester affichées tout le temps. Au-dessus pareil. De façon que
// l'utilisateur ne voie pas que ça charge progressivement. »
//
// ⚠️ DEUX MILLE CARTES, C'EST DEUX MILLE BOUTONS ET DEUX MILLE IMAGES.
//
// Le catalogue grossit à chaque tour d'amorçage. Peindre la grille entière,
// c'est fabriquer tout ce balisage à chaque frappe dans la recherche et à
// chaque changement de filtre — et le laisser dans le document, où il coûte à
// chaque mise en page. `loading="lazy"` ne sauve que le téléchargement des
// images ; les nœuds, eux, restent.
//
// On ne rend donc que la fenêtre visible, plus trois lignes de chaque côté. La
// place de ce qui n'est pas rendu est réservée par un rembourrage : la barre de
// défilement garde la bonne longueur, et rien ne saute.
// ---------------------------------------------------------------------------
// ⚠️ SIX LIGNES DE PART ET D'AUTRE, PAS TROIS.
//
// Toji : « on met 6 lignes en dessous et 6 lignes au-dessus … il y a tout le
// temps 14 lignes affichées, dont les 2 lignes en viewport … même quand on
// descend très vite, on va tout le temps afficher pour sûr les 14 lignes.
// Pour un utilisateur, il ne doit pas voir qu'il y a du chargement. »
//
// Trois lignes, c'était un demi-écran d'avance : un coup de molette le mangeait
// avant la peinture suivante, et l'on rattrapait des cases vides.
const MARGE_LIGNES = 6;
// ⚠️ ON DEMANDE BIEN PLUS TÔT QU'ON NE PEINT.
//
// Toji, 14/09 : « ça doit charger suffisamment vite pour qu'on ne voie pas
// qu'il y a du chargement. » `MARGE_LIGNES` dit combien de rangées sont MONTÉES
// autour de l'écran — la monter alourdirait le DOM pour rien. Ce qu'il faut,
// c'est demander la page suivante beaucoup plus bas : trente rangées d'avance
// laissent le temps d'un aller-retour même en défilement rapide, sans qu'une
// seule carte de plus soit dessinée.
const MARGE_DEMANDE = 30;
// Ce que la grille montre en ce moment, et la fenêtre qu'elle a peinte. Gardés
// ici pour que le défilement ne refasse ni la recherche ni le tri.
let grilleListe = [];
let grilleFenetre = { premiere: -1, derniere: -1 };

// ---------------------------------------------------------------------------
// « VOIR TOUT », CE N'EST PAS TOUT TELECHARGER
//
// Toji : « voir tout, c'est pas reellement voir tout. Voir tout, c'est surtout,
// en fonction du viewport, afficher de facon a ce qu'on voie tous les elements
// (…) avec la marge de gestion. »
//
// ⚠️ LA GRILLE TIRAIT LES DOUZE MILLE OEUVRES. Vingt-cinq requetes, plusieurs
// megaoctets de JSON — et le tri restait FAUX jusqu'a la derniere page, puisque
// la page ne pouvait trier que ce qu'elle avait deja recu.
//
// Elle demande maintenant des pages, et chaque page est taillee sur la
// geometrie reelle : le nombre de colonnes multiplie par les quatorze lignes
// qu'on tient montees. La suivante part des que la reserve descend sous six
// lignes — celles qui doivent etre chargees AVANT d'entrer dans l'ecran.
// ---------------------------------------------------------------------------
const LIGNES_MONTEES = 2 * MARGE_LIGNES + 2;   // les six du dessus, les six du dessous, l'ecran
let grilleSuite = 0;        // le curseur de la page suivante ; `null` quand il n'y a plus rien
// ⚠️ LA PANNE EST UN ETAT A PART, PAS UN CURSEUR EFFACE. « Je n'ai pas pu
// demander » et « il n'y a plus rien » se traitent differemment : le premier se
// reessaie, le second est une fin. Les confondre donne un NON que personne ne
// peut lever — c'est ce qui a coute quatre-vingt-cinq series a Toji.
let grillePanne = null;     // le motif du dernier echec, ou `null`
let grilleTotal = 0;
let grilleEnRoute = null;   // une seule page a la fois : deux en parallele se doubleraient
let grilleCriteres = '';    // l'empreinte des filtres qui ont produit `grilleListe`
let facettesGrille = null;  // genres, annees et sous-tris, calcules sur tout le catalogue

/** Les criteres, tels qu'ils partent au serveur. */
function criteresDeLaGrille() {
  const p = new URLSearchParams();
  if (filtresOeuvres.q) p.set('q', filtresOeuvres.q);
  for (const nom of ['type', 'genre', 'annee', 'format', 'saison', 'statut', 'trimestre', 'diffusion']) {
    const v = filtresOeuvres[nom];
    if (v && v !== 'tout') p.set(nom, v);
  }
  // Une valeur, jamais une liste : le serveur ne cumule plus rien.
  p.set('tri', filtresOeuvres.tri || 'mix');
  if (queDuDispo()) p.set('dispo', 'oui');
  return p;
}

/**
 * Va chercher la page suivante de la grille.
 *
 * `combien` vient de la geometrie : le client sait combien de cartes tiennent
 * dans son ecran, le serveur ne le sait pas. Demander un nombre rond aurait
 * donne trop peu sur un 4K et trop sur un portable.
 */
async function chargerPageDeGrille(combien, { reset = false, depuis = null } = {}) {
  // ⚠️ UN RESET NE FAIT PAS LA QUEUE DERRIERE UNE PAGE PERIMEE.
  //
  // Rendre la promesse en cours quand les criteres viennent de changer, c'est
  // repeindre avec la reponse de la recherche PRECEDENTE — et laisser la
  // nouvelle sans effet. On laisse donc la page en vol se terminer dans le
  // vide : `grilleCriteres` a change, sa reponse sera ignoree.
  if (grilleEnRoute && !reset) return grilleEnRoute;
  if (!reset && depuis === null && grilleSuite === null) return null;

  const p = criteresDeLaGrille();
  const a = reset ? 0 : (depuis ?? grilleSuite);
  p.set('depuis', String(a));
  p.set('limite', String(Math.max(24, Math.min(1000, Math.round(combien)))));

  const empreinteDemandee = grilleCriteres;
  const enRoute = (async () => {
    try {
      const d = await api(`/api/oeuvres?${p}`);
      if (d?.facettes) facettesGrille = d.facettes;
      const neuves = normaliserCatalogue(d?.oeuvres);
      // Les criteres ont change pendant l'aller-retour : cette reponse decrit
      // une recherche qu'on ne fait plus.
      if (grilleCriteres !== empreinteDemandee) return null;
      // ⚠️ LA LISTE A LA LONGUEUR DU TOTAL, PAS DE CE QUI EST DESCENDU.
      //
      // Toji, 20/10 : « quand on descend très rapidement, c'est buggé ». La
      // grille se dimensionnait sur les œuvres déjà reçues : on butait au bas
      // de la page chargée, puis la hauteur grandissait et la barre sautait
      // sous la main (MESURÉ : jusqu'à cinq fois par geste, Fin arrêtée à la
      // 517e œuvre sur 4 500). La liste est maintenant creuse : chaque tranche
      // se pose à sa place, la hauteur est juste dès la première page, et ce
      // qui manque s'affiche en case d'attente le temps d'arriver.
      if (reset) grilleListe = [];
      neuves.forEach((o, k) => { grilleListe[a + k] = o; });
      grilleTotal = d?.total ?? grilleListe.length;
      if (grilleListe.length < grilleTotal) grilleListe.length = grilleTotal;
      grilleSuite = premierManquant(0, grilleListe.length);
      // Une page qui arrive lève la panne : c'est le seul signal fiable que le
      // serveur répond de nouveau.
      grillePanne = null;
      return neuves;
    } catch (err) {
      tracer('grille', 'page injoignable', err.message);
      // ⚠️ ON N'EFFACE PLUS LE CURSEUR. `grilleSuite = null` veut dire « il n'y
      // a plus rien » — le MEME etat que « on est arrive au bout ». Une seule
      // page en echec arretait donc le defilement POUR TOUJOURS, en silence :
      // Toji s'est arrete a la 215e serie sur 300 en croyant etre au bout.
      //
      // L'intention d'origine etait juste — « une grille qui rejoue la meme
      // requete a chaque cran de molette martelerait le serveur en panne ». Ce
      // qu'il fallait, c'est ne plus redemander TOUT SEUL, pas oublier qu'il
      // reste des pages. Le curseur reste, le drapeau dit pourquoi ca s'arrete,
      // et l'ecran offre une porte de sortie.
      grillePanne = String(err?.message ?? err) || 'page injoignable';
      return null;
    } finally {
      // ⚠️ SEULEMENT SI C'EST ENCORE ELLE. Une frappe lance un reset pendant
      // que la page précédente est en vol : quand celle-ci finissait, elle
      // effaçait la promesse de la NOUVELLE, et deux pages partaient de front.
      if (grilleEnRoute === enRoute) grilleEnRoute = null;
    }
  })();
  grilleEnRoute = enRoute;
  return enRoute;
}



/** Le premier index de [de, a[ encore à recevoir, ou `null`. */
function premierManquant(de, a) {
  for (let i = de; i < a; i += 1) if (!grilleListe[i]) return i;
  return null;
}

/** Combien de colonnes la grille pose, et quelle hauteur fait une ligne. */
function mesuresDeLaGrille(grille) {
  const style = getComputedStyle(grille);
  const colonnes = Math.max(1, style.gridTemplateColumns.split(' ').filter(Boolean).length);
  const carte = grille.querySelector('.st-carte');
  const gap = parseFloat(style.rowGap) || 22;
  // Sans carte rendue, on ne peut pas mesurer : l'affiche est en 2/3, plus le
  // titre et la ligne de détail. C'est une estimation, et elle ne sert qu'au
  // tout premier rendu — la mesure vraie arrive dès qu'une carte existe.
  const hauteurCarte = carte ? rectPage(carte).height
    : (grille.clientWidth / colonnes) * 1.5 + 46;
  return { colonnes, hauteurLigne: hauteurCarte + gap };
}

/** Peint la fenêtre visible, si elle a changé. */
function peindreFenetreGrille(grille, defileur, { force = false } = {}) {
  if (!grilleListe.length) return;
  const { colonnes, hauteurLigne } = mesuresDeLaGrille(grille);
  const lignes = Math.ceil(grilleListe.length / colonnes);

  // Où commence la grille dans le conteneur qui défile : le compte et la barre
  // de filtres sont au-dessus, et les ignorer décalerait la fenêtre d'autant.
  const hautGrille = grille.offsetTop;
  const hauteurVue = defileur.clientHeight || window.innerHeight || 800;

  const geometrie = {
    scrollTop: defileur.scrollTop, hauteurVue, hautGrille, hauteurLigne, lignes,
  };
  const { premiere, derniere } = fenetreDeGrille({ ...geometrie, marge: MARGE_LIGNES });
  // ⚠️ LA MEME FENETRE, SANS LA MARGE : LES LIGNES REELLEMENT SOUS LES YEUX.
  //
  // C'est la distinction qui manquait, et c'est elle qui faisait « tout noir,
  // puis tout d'un coup ». `glisserLaFenetre` fabriquait les QUATORZE lignes en
  // `fetchpriority="high"` : les douze de reserve partaient en meme temps que
  // les deux qu'on regarde, sur le meme nombre de connexions, et rien
  // n'arrivait avant que tout n'arrive. Les deux visibles passent maintenant
  // devant ; la reserve suit, en priorite basse, sans jamais etre retardee au
  // point d'arriver trop tard — elle a six lignes d'avance pour cela.
  const vue = fenetreDeGrille({ ...geometrie, marge: 0 });
  if (!force && premiere === grilleFenetre.premiere && derniere === grilleFenetre.derniere) return;
  grilleFenetre = { premiere, derniere };

  const debut = premiere * colonnes;
  const fin = Math.min(grilleListe.length, (derniere + 1) * colonnes);
  const sousLesYeux = { debut: vue.premiere * colonnes, fin: (vue.derniere + 1) * colonnes };
  grille.style.paddingTop = `${premiere * hauteurLigne}px`;
  grille.style.paddingBottom = `${Math.max(0, (lignes - 1 - derniere) * hauteurLigne)}px`;
  // ⚠️ UN CHANGEMENT DE LISTE N'EST PAS UN DEFILEMENT.
  //
  // Toji : « ça trie simplement de A à Z. Le bouton tri ne fonctionne pas. »
  //
  // `glisserLaFenetre` garde les cartes dont l'index est deja monte — c'est ce
  // qui rend le defilement fluide, et c'est exactement ce qu'il ne faut pas
  // faire ici. Apres un tri, la liste a change d'ORDRE mais pas de longueur :
  // les index 0 a N sont toujours la, le recouvrement est total, et pas une
  // seule carte n'etait remplacee. La grille affichait donc l'ancien ordre,
  // indefiniment, alors que le tri avait parfaitement fonctionne derriere.
  //
  // `force` dit precisement « ce n'est plus la meme liste » : on vide, et on
  // repart.
  if (force) grille.replaceChildren();
  // Seule une peinture COMPLÈTE dans la fenêtre d'entrée de l'écran fait
  // entrer ses cartes. Un défilement n'est jamais `force` : les cartes qu'il
  // fabrique arrivent sans animation (voir `.apparait` dans la feuille).
  glisserLaFenetre(grille, debut, fin, sousLesYeux, force && enEntree(defileur) ? colonnes : 0);
  brancherCartes(grille, ouvrirOeuvre);

  // ⚠️ LA RESERVE SE REMPLIT AVANT D'ETRE VUE, PAS QUAND ELLE MANQUE.
  //
  // Toji : « toutes les icones, quand elles apparaissent, elles doivent etre
  // deja chargees 6 lignes en dessous ». Attendre d'arriver au bout de la liste
  // pour demander la suite, c'est garantir le contraire : la page partirait au
  // moment ou la carte doit s'afficher. On demande donc des que ce qui reste
  // sous la derniere ligne peinte descend sous six lignes.
  //
  // Ce qui manque se cherche là où l'on EST : de la fenêtre montée jusqu'à
  // trente rangées sous elle. Un saut de barre ou la touche Fin demandent donc
  // directement la tranche d'arrivée, au lieu d'attendre que tout ce qui est
  // au-dessus soit descendu page après page.
  const manque = grillePanne ? null
    : premierManquant(debut, Math.min(grilleListe.length, fin + MARGE_DEMANDE * colonnes));
  if (manque !== null) {
    // On demande de quoi tenir soixante rangées : le double de l'avance qu'on
    // veut garder, pour que la page suivante ne soit jamais réclamée dans la
    // foulée de celle-ci.
    // Une seule suite par aller-retour : si une page est déjà en vol, c'est
    // son retour qui repeindra et redemandera.
    const deja = grilleEnRoute;
    const enRoute = chargerPageDeGrille(colonnes * MARGE_DEMANDE * 2, { depuis: manque });
    if (enRoute && enRoute !== deja) enRoute.then((neuves) => {
      if (!neuves?.length) return;
      remplirLesAttentes(grille);
      // Une autre tranche peut manquer (on a bougé pendant l'aller-retour) :
      // la fenêtre se repeint, et redemande ce qui est sous les yeux.
      grilleFenetre = { premiere: -1, derniere: -1 };
      peindreFenetreGrille(grille, defileur);
      majCompteDeLaGrille();
    });
  }
}

/** Le compte au-dessus de la grille : ce qu'on a, sur ce qu'il y a. */
function majCompteDeLaGrille() {
  const compte = $('oeuvres-compte');
  if (!compte) return;
  if (filtresOeuvres.dans === 'bibliotheque') {
    const n = grilleListe.length;
    compte.textContent = `${n} ${pluriel(n, 'titre')}`;
    return;
  }
  // On dit ce qui correspond, pas ce qui est descendu : « 40 sur 12 000 »
  // laisserait croire que la recherche n'a trouve que quarante titres.
  const total = grilleTotal || grilleListe.length;
  // ⚠️ UNE PANNE SE DIT, ET ELLE SE RATTRAPE.
  //
  // Sans ça, on a juste déplacé le silence : le défilement s'arrête, l'écran
  // affiche « 300 œuvres » et on en voit 215, sans que rien n'explique l'écart.
  // C'est exactement ce que Toji a vécu — il a cru être arrivé au bout.
  if (grillePanne) {
    compte.innerHTML = `${esc(String(total))} ${pluriel(total, 'œuvre')}`
      + ` · <span class="grille-panne">la suite n’est pas arrivée</span>`
      + ` <button type="button" id="grille-reessayer" class="ghost">Réessayer</button>`;
    const bouton = $('grille-reessayer');
    if (bouton) {
      bouton.onclick = () => {
        // On relance à la main, jamais tout seul : une grille qui rejoue la
        // même requête à chaque cran de molette martèlerait un serveur en
        // panne. C'est l'intention d'origine du `catch`, et elle est gardée.
        grillePanne = null;
        const enRoute = chargerPageDeGrille(60);
        if (enRoute) enRoute.then(() => peindreCatalogueOeuvres({ rechargee: true }));
      };
    }
    return;
  }
  compte.textContent = `${total} ${pluriel(total, 'œuvre')}`;
}

/**
 * Monter ce qui entre, démonter ce qui sort — et NE PAS TOUCHER au reste.
 *
 * ⚠️ C'ÉTAIT `innerHTML = …` À CHAQUE PAS DE DÉFILEMENT.
 *
 * Toji : « quand je descends progressivement, les icônes ne s'affichent pas
 * suffisamment rapidement … pour un utilisateur, il ne doit pas voir qu'il y a
 * du chargement. »
 *
 * Remplacer tout le contenu détruisait AUSSI les cartes qui restaient à
 * l'écran : leurs `<img>` repartaient de zéro, à re-décoder, parfois à
 * re-télécharger. Autrement dit, la grille se vidait et se remplissait sous les
 * yeux à chaque cran de molette — précisément ce qu'on cherchait à éviter en
 * gardant six lignes d'avance.
 *
 * Ici, une carte déjà montée reste le MÊME nœud : son image est déjà décodée,
 * elle ne clignote pas. Seules les lignes qui entrent réellement dans la
 * fenêtre coûtent quelque chose.
 */
const CARTE_EN_ATTENTE = '<div class="st-carte st-carte--attente" aria-hidden="true">'
  + '<span class="st-carte-img"></span><span class="st-carte-titre">&nbsp;</span>'
  + '<span class="st-carte-sous">&nbsp;</span></div>';

/** Une tranche arrivée : ses cases d'attente montées deviennent des cartes. */
function remplirLesAttentes(grille) {
  const densite = densiteEcran();
  for (const el of grille.querySelectorAll(':scope > .st-carte--attente')) {
    const o = grilleListe[Number(el.dataset.i)];
    if (!o) continue;
    const gabarit = document.createElement('template');
    gabarit.innerHTML = carteOeuvre(o, { avance: 'dans', dpr: densite });
    const carte = gabarit.content.firstElementChild;
    carte.dataset.i = el.dataset.i;
    el.replaceWith(carte);
  }
  brancherCartes(grille, ouvrirOeuvre);
}

function glisserLaFenetre(grille, debut, fin, sousLesYeux = null, entreeSurColonnes = 0) {
  const enfants = grille.children;
  const premierMonte = enfants.length ? Number(enfants[0].dataset.i) : NaN;
  const dernierMonte = enfants.length ? Number(enfants[enfants.length - 1].dataset.i) : NaN;
  const densite = densiteEcran();

  const fabriquer = (i) => {
    const gabarit = document.createElement('template');
    // Deux regimes, et un seul avant : voir `chargementDImage`. Une carte de la
    // reserve part quand meme tout de suite — c'est la regle des six lignes —
    // mais elle ne prend pas la place de celles qu'on regarde.
    const dans = !sousLesYeux || (i >= sousLesYeux.debut && i < sousLesYeux.fin);
    gabarit.innerHTML = !grilleListe[i] ? CARTE_EN_ATTENTE : carteOeuvre(grilleListe[i], {
      avance: dans ? 'dans' : 'reserve', dpr: densite,
    });
    const el = gabarit.content.firstElementChild;
    el.dataset.i = String(i);
    // L'entrée : en vague diagonale, 40 ms par ligne et 25 par colonne,
    // plafonnée à 320 ms pour qu'aucune carte n'arrive en retard.
    if (entreeSurColonnes && dans && sousLesYeux) {
      const k = i - sousLesYeux.debut;
      const d = Math.min(320, Math.floor(k / entreeSurColonnes) * 40 + (k % entreeSurColonnes) * 25);
      el.classList.add('apparait');
      el.style.setProperty('--ent-d', `${d}ms`);
    }
    return el;
  };

  // Aucun recouvrement — un saut de barre de défilement, un changement de
  // filtre : reconstruire est alors moins cher que de raisonner.
  if (!Number.isFinite(premierMonte) || debut > dernierMonte || fin - 1 < premierMonte) {
    const morceau = document.createDocumentFragment();
    for (let i = debut; i < fin; i += 1) morceau.appendChild(fabriquer(i));
    grille.replaceChildren(morceau);
    return;
  }

  while (grille.firstElementChild && Number(grille.firstElementChild.dataset.i) < debut) {
    grille.firstElementChild.remove();
  }
  while (grille.lastElementChild && Number(grille.lastElementChild.dataset.i) >= fin) {
    grille.lastElementChild.remove();
  }
  // En tête, on insère à l'envers : chaque carte se pose avant la précédente,
  // et l'ordre du document reste celui des index.
  for (let i = Math.min(premierMonte, fin) - 1; i >= debut; i -= 1) {
    grille.insertBefore(fabriquer(i), grille.firstElementChild);
  }
  for (let i = Math.max(dernierMonte, debut - 1) + 1; i < fin; i += 1) {
    grille.appendChild(fabriquer(i));
  }
}

/**
 * ⚠️ CE QUE LA SOURCE TROUVE ET QUE LE CATALOGUE N'A PAS.
 *
 * Toji, 19/10 : VisionQuest, Série, 2026 → « 0 ŒUVRE », alors que Stremio la
 * trouve. La grille ne cherchait que dans NOTRE catalogue — l'amorçage ne ramène
 * que des tendances, et une série sortie la semaine dernière n'y est pas. La
 * page de recherche, elle, interrogeait aussi la source. On complète donc la
 * grille avec ce qu'elle rend, passé aux MÊMES filtres que le reste : un titre
 * ajouté ici respecte le type et l'année choisis.
 */
async function completerParLaSource(empreinte) {
  let r;
  try {
    r = await api(`/api/recherche?q=${encodeURIComponent(filtresOeuvres.q)}`);
  } catch { return; } // la grille locale reste : une recherche partielle vaut mieux qu'une erreur
  if (grilleCriteres !== empreinte || filtresOeuvres.dans === 'bibliotheque') return;
  const connus = new Set(grilleListe.map((o) => o.id));
  const ailleurs = chercherOeuvres(
    normaliserCatalogue(r?.resultats).filter((o) => !connus.has(o.id)),
    { ...filtresOeuvres, q: '', dispoSeulement: false },
  );
  if (!ailleurs.length) return;
  grilleListe = [...grilleListe, ...ailleurs];
  grilleTotal = (grilleTotal || 0) + ailleurs.length;
  peindreCatalogueOeuvres({ rechargee: true });
}

/** La grille des œuvres, filtrée par ce que porte `filtresOeuvres`. */
function peindreCatalogueOeuvres({ rechargee = false } = {}) {
  const hote = $('v-catalogue');
  if (!hote) return;
  monterBarreOeuvres(hote);

  zoneOuCreee(hote, 'oeuvres-compte', { balise: 'p', classe: 'st-compte' });
  const grille = zoneOuCreee(hote, 'oeuvres-grille', { classe: 'st-grille' });
  if (!grille) return;

  // ⚠️ DEUX SOURCES, DEUX REGIMES — ET C'EST VOULU.
  //
  // La bibliotheque tient en memoire : quelques dizaines de titres, deja
  // charges avec leurs fiches. La trier ici coute moins qu'un aller-retour.
  // Le catalogue, lui, en compte douze mille : c'est le serveur qui filtre et
  // qui trie, et la page ne recoit que la tranche qu'elle affiche.
  if (filtresOeuvres.dans === 'bibliotheque') {
    grilleSuite = null;
    grilleListe = chercherOeuvres(oeuvresDeLaBiblio(),
      { ...filtresOeuvres, dispoSeulement: queDuDispo() });
    grilleTotal = grilleListe.length;
  } else {
    // Les criteres ont-ils bouge ? Si oui, la liste recue ne les decrit plus :
    // on repart de la premiere page. Sinon on garde ce qui est deja descendu —
    // revenir a la grille ne doit pas tout redemander.
    const empreinte = String(criteresDeLaGrille());
    if (!rechargee && empreinte !== grilleCriteres) {
      // ⚠️ LA FRAPPE SACCADAIT (Toji, 19/10 : « ça plante un peu au niveau de
      // la frappe »). Trois causes, toutes ici : la liste était vidée AVANT la
      // réponse — la grille clignotait à chaque mot malgré le commentaire
      // ci-dessous ; la réponse d'une frappe précédente, arrivée entre-temps,
      // repeignait « Aucune œuvre pour … » sur la liste vide ; et sa fin
      // effaçait la promesse de la nouvelle. La liste reste donc jusqu'à la
      // réponse (`chargerPageDeGrille` la remplace d'un coup), et seule la
      // réponse de la DERNIÈRE frappe repeint.
      grilleCriteres = empreinte;
      grilleSuite = 0;
      // La premiere page se demande sur la geometrie reelle : quatorze lignes
      // de cartes, soit exactement ce qui est monte en permanence.
      const { colonnes } = mesuresDeLaGrille(grille);
      // ⚠️ `chargerPageDeGrille` peut rendre `null` — une page deja en route,
      // ou plus rien a demander. Enchainer un `.then` dessus leverait, et la
      // grille resterait vide SANS que rien ne reprenne la main.
      const enRoute = chargerPageDeGrille(Math.max(1, colonnes) * LIGNES_MONTEES, { reset: true });
      const repeindre = () => {
        if (grilleCriteres !== empreinte) return;
        peindreCatalogueOeuvres({ rechargee: true });
        if (filtresOeuvres.q && grilleSuite === null) completerParLaSource(empreinte);
      };
      if (enRoute) enRoute.then(repeindre);
      else repeindre();
      // On ne vide pas l'ecran en attendant : les cartes de la recherche
      // precedente restent jusqu'a ce que les nouvelles arrivent. Une grille
      // qui se vide a chaque frappe clignote plus qu'elle n'informe.
      if (!grilleListe.length) {
        grille.style.paddingTop = '';
        grille.style.paddingBottom = '';
        grille.replaceChildren();
      }
      return;
    }
  }

  majCompteDeLaGrille();

  if (!grilleListe.length) {
    grille.style.paddingTop = '';
    grille.style.paddingBottom = '';
    // Dire ce qui a été cherché : « aucun résultat » tout court laisse penser
    // que le catalogue est vide, alors que c'est le filtre qui l'est.
    grille.innerHTML = `<p class="st-vide">Aucune œuvre${filtresOeuvres.q ? ` pour « ${esc(filtresOeuvres.q)} »` : ''}.</p>`;
    return;
  }

  // Un nouveau filtre, une nouvelle liste : on repart du haut, et la fenêtre
  // est forcément à repeindre même si ses bornes tombent aux mêmes nombres.
  grilleFenetre = { premiere: -1, derniere: -1 };
  peindreFenetreGrille(grille, hote, { force: true });

  // ⚠️ UN SEUL ÉCOUTEUR, POSÉ UNE FOIS. `peindreCatalogueOeuvres` est appelée à
  // chaque frappe dans la recherche : en poser un ici à chaque fois, c'est la
  // fuite qu'on vient de corriger ailleurs.
  if (!hote.dataset.virtuelle) {
    hote.dataset.virtuelle = '1';
    // L'entrée jouée, la carte rend sa classe : elle ne garde aucune animation
    // (même finie) pendant le défilement qui suit.
    grille.addEventListener('animationend', (e) => {
      if (e.target.parentNode === grille) e.target.classList.remove('apparait');
    });
    // `passive` : on ne bloque jamais le défilement, on le suit.
    // Une seule frame en attente : un cran de molette émet plusieurs `scroll`
    // par image, et chacun relançait la mesure de la grille (MESURÉ : jusqu'à
    // trois `peindreFenetreGrille` par frame).
    let enAttente = false;
    hote.addEventListener('scroll', () => {
      // Le rendu attend la frame suivante : peindre dans le fil du défilement
      // fait sauter l'image, et c'est exactement ce que la marge de trois
      // lignes existe pour éviter.
      if (enAttente) return;
      enAttente = true;
      requestAnimationFrame(() => { enAttente = false; peindreFenetreGrille(grille, hote); });
    }, { passive: true });
    addEventListener('resize', () => {
      // La largeur change le nombre de colonnes, donc toute la géométrie.
      grilleFenetre = { premiere: -1, derniere: -1 };
      peindreFenetreGrille(grille, hote, { force: true });
    });
  }

  // La première mesure se fait sur une grille vide : dès qu'une carte existe,
  // on refait le calcul avec la vraie hauteur.
  requestAnimationFrame(() => peindreFenetreGrille(grille, hote, { force: true }));
}

/** Ouvre la grille. Charge le catalogue si c'est la première fois. */
/**
 * ⚠️ « CATALOGUE » VEUT DIRE LE CATALOGUE, PAS CE QU'ON REGARDAIT AVANT.
 *
 * `filtresOeuvres.dans` restait pose sur « bibliotheque » : revenir au
 * catalogue par le menu reaffichait donc la bibliotheque, souvent vide, et
 * l'ecran annoncait « Aucune oeuvre » sur un catalogue de douze mille titres.
 * Toji : « dans le catalogue, j'ai rien du tout ».
 *
 * Seule `ouvrirLaBiblio` pose ce filtre, et elle passe donc par
 * `ouvrirCatalogueOeuvres({ garderLaSource: true })`.
 */
async function ouvrirCatalogueOeuvres({ garderLaSource = false } = {}) {
  if (!garderLaSource && filtresOeuvres.dans) {
    filtresOeuvres.dans = null;
    // Les criteres ont change de source : la liste en memoire ne les decrit
    // plus, il faut redemander.
    grilleCriteres = '';
  }
  // Même raison qu'à l'accueil : parcourir le catalogue n'est pas être dans une
  // séance, et l'adresse ne doit pas prétendre le contraire.
  quitterLAdresseDeSeance();
  vue('v-catalogue');
  // ⚠️ ELLE N'ATTEND PLUS RIEN. La grille demandait le catalogue ENTIER avant
  // de peindre sa premiere carte : douze mille oeuvres pour en montrer trente.
  // Elle se peint maintenant tout de suite, et sa premiere page part avec.
  peindreCatalogueOeuvres();
}

// ---------------------------------------------------------------------------
// La fiche d'une œuvre
// ---------------------------------------------------------------------------

/**
 * La zone qui porte le menu des arcs et la grille des épisodes.
 *
 * `construireMenuArcs` et `rendreArc` sont deux fonctions éprouvées qui
 * peignent dans `.choix-arc` et `#s-episodes`. Plutôt que d'en écrire une
 * seconde paire pour la nouvelle fiche — donc deux rendus d'épisodes à tenir à
 * jour, dont un finirait par diverger — on leur fournit leurs nœuds à
 * l'intérieur de la nouvelle vue. Le rendu des vignettes, les drapeaux, le
 * réglage « épisodes par ligne », la recherche dans le menu : tout continue de
 * marcher sans une ligne de plus.
 */
function zoneEpisodesFiche(hote) {
  const zone = zoneOuCreee(hote, 'oeu-episodes', { classe: 'st-oeuvre-eps' });
  if (!zone) return null;
  let tete = zone.querySelector('.choix-arc');
  if (!tete) {
    tete = document.createElement('div');
    tete.className = 'choix-arc';
    zone.appendChild(tete);
  }
  zoneOuCreee(zone, 's-episodes', { classe: 'eps-grille' });
  return zone;
}

// ---------------------------------------------------------------------------
// LA CAISSE DES GROUPES (phase 4) — l'interface vit dans `caisse.js`
//
// Ici, seulement les branchements : les séances en cours (une réponse partagée
// par l'accueil et la fiche, gardée 10 s) et la dernière interaction, qui fait
// partie de la preuve de présence du bonus de groupe.
// ---------------------------------------------------------------------------
const caisseUI = monterCaisse({ api, direMot, tracer });
let derniereInteraction = Date.now();
for (const t of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
  addEventListener(t, () => { derniereInteraction = Date.now(); }, { passive: true, capture: true });
}
let seancesEnCache = { quand: 0, liste: null };
async function seancesEnCours() {
  if (seancesEnCache.liste && Date.now() - seancesEnCache.quand < 10_000) return seancesEnCache.liste;
  const r = await api('/api/seances').catch(() => null);
  seancesEnCache = { quand: Date.now(), liste: r?.seances ?? [] };
  return seancesEnCache.liste;
}
function peindreEnCeMomentAccueil() {
  const zone = $('acc-encemoment');
  if (!zone) return;
  seancesEnCours().then((l) => caisseUI.peindreEnCeMoment(zone, l)).catch(() => {});
}
function peindreCaisseDeLaFiche() {
  const hote = document.querySelector(`#${ECRAN_OEUVRE()} .st-hero-actions`);
  const zone = zoneOuCreee(hote, 'oeu-caisse', { classe: 'caisse-actions-fiche', avant: hote?.firstElementChild ?? null });
  if (!zone || !oeuvre) return;
  const cible = oeuvre;
  seancesEnCours()
    .then((l) => (cible === oeuvre ? caisseUI.peindreActionsFiche(zone, oeuvre, l, { estUnFilm: estUnFilm(oeuvre) }) : null))
    .catch(() => {});
}

/** La fiche : bandeau, repères, résumé, puis les épisodes ou le bouton « Lire ». */
function peindreFicheOeuvre() {
  const hote = $(ECRAN_OEUVRE());
  if (!hote || !oeuvre) return;

  // Le retour dit où il mène. Un bouton qui annonce « Catalogue » et rend
  // l'accueil est un petit mensonge qu'on ne remarque qu'après avoir cliqué —
  // et l'inverse ferait atterrir dans une grille de douze mille titres.
  const venu = derniereEtape();
  texte('oeu-retour-mot', { 'v-accueil': 'Accueil', 'v-catalogue': 'Catalogue', 'v-recherche': 'Recherche' }[venu ?? 'v-catalogue'] ?? 'Retour');

  // ⚠️ LE BOUTON DES REGLAGES N'EXISTE QUE POUR QUI GERE LES ADD-ONS.
  //
  // Il ouvre le mapping — les liens bruts, leur source, leur poids. Ce n'est
  // pas une information de spectateur, et le montrer a tout le monde
  // proposerait un reglage que la plupart ne peuvent pas appliquer.
  // ⚠️ LE COEUR ET LA CLOCHE SE PEIGNENT ICI, PAS AU BRANCHEMENT.
  //
  // Leur etat depend de l'oeuvre AFFICHEE : les brancher une fois au demarrage
  // laisserait le coeur plein d'une fiche sur la fiche suivante. C'est ce qui
  // arrive a tout bouton dont l'etat suit le contenu, et c'est pour cela qu'ils
  // sont ici, dans la peinture.
  peindreBoutonsDeBiblio(oeuvre);

  const bMap = $('oeu-mapping');
  if (bMap) {
    bMap.hidden = !peutRegler();
    // Toji : « à la place de Réglages, on a Retour ». Le mot dit ou l'on est,
    // pas ce que le bouton fait : en mode reglages, la seule chose qu'on veut
    // encore de lui, c'est en sortir.
    texte('oeu-mapping-mot', modeMapping ? 'Retour' : 'Réglages');
    bMap.setAttribute('aria-pressed', String(modeMapping));
    // Un FILM n'a pas de grille ou cliquer : le bouton ouvre directement ses
    // liens, puisqu'il n'y a qu'un episode a regler.
    bMap.onclick = estUnFilm(oeuvre)
      ? () => ouvrirMapping(0, 0)
      : basculerModeMapping;
  }

  // Le fond : celui de l'œuvre, l'affiche à défaut. Un `<img>` déjà présent
  // dans le balisage est réutilisé tel quel — c'est lui qui porte le cadrage.
  const fond = $('oeu-fond') ?? $('s-fond');
  if (fond) poserFondDeBanniere(fond, fondDeLOeuvre(oeuvre));
  const affiche = $('oeu-affiche');
  if (affiche && affiche.tagName === 'IMG') {
    affiche.src = afficheDeCarte(oeuvre.affiche || oeuvre.fond || '', densiteEcran());
    // L'affiche de la bannière est le premier repère de l'écran : elle passe
    // devant le fond, qui n'est qu'une ambiance.
    affiche.setAttribute('fetchpriority', 'high');
    affiche.setAttribute('decoding', 'sync');
  }

  const titre = zoneOuCreee(hote, 'oeu-titre', { balise: 'h1', classe: 'st-oeuvre-titre' });
  if (titre) titre.textContent = oeuvre.titre;
  texte('s-nom', oeuvre.titre);

  // Les repères, dans l'ordre où on les cherche : ce que c'est, quand, la
  // note, puis les genres. La note se dit « 8,4 ★ » et pas « 8.4 » : c'est un
  // texte français.
  // ⚠️ LA NOTE EST UNE INFORMATION D'IMDB : ELLE DOIT Y MENER.
  //
  // Toji : « quand on clique sur la note, ça redirige vers la page en
  // question. » L'identifiant du catalogue EST l'identifiant IMDb — c'est la
  // seule chose qu'on exige d'une œuvre pour l'accepter — donc l'adresse se
  // fabrique sans rien demander à personne.
  const combien = oeuvre.episodes.length;
  const etiquettes = [
    typeLisible(oeuvre),
    ...(oeuvre.annee ? [String(oeuvre.annee)] : []),
    // La durée d'un film — la vraie, mesurée sur le fichier. C'est la deuxième
    // question qu'on se pose devant un film, et la fiche n'y répondait pas.
    ...(oeuvre.duree > 0 ? [dureeLisible(oeuvre.duree)] : []),
    ...(oeuvre.note ? [{ mot: `${String(oeuvre.note).replace('.', ',')} ★`, vers: lienImdb(oeuvre.id) }] : []),
    ...(combien ? [`${combien} ${pluriel(combien, 'épisode')}`] : []),
    ...(serie?.arcs?.length > 1 ? [`${serie.arcs.length} arcs`] : []),
    ...oeuvre.genres.slice(0, 3),
  ];
  const meta = $('oeu-meta') ?? $('s-meta');
  // ⚠️ UNE ETIQUETTE PEUT MENER QUELQUE PART.
  //
  // Toji : « quand on clique sur la note, ca redirige vers la page en
  // question. » L'identifiant du catalogue EST celui d'IMDb — c'est la seule
  // chose qu'on exige d'une oeuvre pour l'accepter — donc l'adresse se fabrique
  // sans rien demander a personne. Les autres etiquettes restent du texte : une
  // page ou tout est cliquable ne dit plus ce qui l'est.
  if (meta) {
    meta.innerHTML = etiquettes.map((t) => {
      if (typeof t === 'string') return `<span class="tag">${esc(t)}</span>`;
      if (!t?.vers) return `<span class="tag">${esc(t.mot)}</span>`;
      return `<a class="tag" href="${esc(t.vers)}" target="_blank" rel="noopener noreferrer"`
        + ` title="Voir la fiche IMDb">${esc(t.mot)}</a>`;
    }).join('');
  }

  const resume = $('oeu-resume') ?? $('s-resume');
  if (resume) resume.textContent = oeuvre.resume || '';

  // « Rejoindre » d'abord quand un groupe regarde déjà l'œuvre, puis la série
  // complète (caisse des groupes, phase 4). Détaché : la fiche n'attend rien.
  peindreCaisseDeLaFiche();

  // --- un film : un bouton, pas une grille ---------------------------------
  if (estUnFilm(oeuvre)) {
    const zone = $('oeu-episodes');
    if (zone) zone.hidden = true;
    const bouton = zoneOuCreee(hote, 'oeu-lire', { balise: 'button', classe: 'st-lire' });
    if (bouton) {
      bouton.hidden = false;
      bouton.type = 'button';
      bouton.textContent = 'Lire le film';
      bouton.onclick = () => lancerFilm({
        id: oeuvre.id, nom: oeuvre.titre, fond: oeuvre.fond, affiche: oeuvre.affiche,
      });
    }
    return;
  }

  const bouton = $('oeu-lire');
  if (bouton) bouton.hidden = true;

  // --- une série : le menu des arcs et la grille ----------------------------
  const zone = zoneEpisodesFiche(hote);
  if (zone) zone.hidden = false;
  // ⚠️ UNE LISTE GARDÉE SE DIT. Servie depuis le miroir, elle s'arrête à la
  // dernière fois que la source a répondu : un mois de panne muette a fait
  // dire « un seul épisode » à VisionQuest, qui en a huit (19/10/2026).
  let avis = zone?.querySelector('.st-miroir') ?? null;
  // Une source seulement LENTE, servie par un miroir confirmé récemment, ne se
  // dit pas (22/10 : bandeau sur un miroir du jour même) — `miroirADire`.
  if (zone && miroirADire(oeuvre?.miroir)) {
    if (!avis) {
      avis = document.createElement('p');
      avis.className = 'st-miroir';
      zone.prepend(avis);
    }
    const quand = oeuvre.miroir.depuis
      ? ` le ${dateFr(oeuvre.miroir.depuis)}` : '';
    avis.textContent = `Liste d’épisodes enregistrée${quand} : la base de métadonnées ne répond pas pour l’instant, un épisode sorti depuis peut manquer.`;
  } else avis?.remove();
  const arcs = serie?.arcs ?? [];
  if (!arcs.length) {
    const grille = $('s-episodes');
    // ⚠️ DIRE POURQUOI. « Aucun épisode listé » sur une série qui en a quatre
    // cents envoie chercher la panne du mauvais côté — on a cru pendant une
    // nuit que la fiche était cassée alors que la source ne répondait pas.
    // `sansSource` vient du serveur : il sait, lui, s'il a servi de mémoire.
    if (grille) {
      grille.innerHTML = oeuvre?.sansSource
        ? '<p class="st-vide">La base de métadonnées ne répond pas : les épisodes ne peuvent pas être listés pour l’instant. Le reste de la fiche vient du catalogue.</p>'
        : '<p class="st-vide">Aucun épisode listé pour cette œuvre.</p>';
    }
    return;
  }

  // L'arc où l'on était la dernière fois, quand c'est la même œuvre. Repartir
  // du premier arc d'une série de quatre cents épisodes, à chaque retour, c'est
  // refaire le chemin à chaque fois.
  const memo = positionMemorisee();
  const depart = arcDeDepart(arcs, {
    memoire: memo.oeuvre === oeuvre.id ? memo.arc : null,
    retenu: prefs.lire(`saison.${oeuvre.id}`, ''),
    reprise: repriseConnueDe(oeuvre.id),
  });
  presenterLesArcs(arcs, depart);
  rendreArc(depart);
}

// ⚠️ RECHARGER PENDANT UN ÉPISODE REND CET ÉPISODE (Toji, 21/10).
// Sur le site : `?id=<œuvre>&s=<saison>&e=<épisode>` (l'ancienne forme, déjà
// lue au démarrage ; aucun jeton). Dans l'activité, l'adresse appartient à
// Discord : sessionStorage, lié au `frame_id` comme la mémoire d'écran. Une
// adresse de séance (`/lecteur/…`) a sa propre reprise : on n'y touche pas.
const CLE_LECTURE = 'tsudoi.lecture';

function retenirLectureDeLOnglet(saison, episode) {
  const id = oeuvre?.id ?? serie?.id;
  if (!id || /^\/lecteur\//.test(location.pathname)) return;
  try {
    if (DANS_DISCORD) sessionStorage.setItem(CLE_LECTURE, JSON.stringify({ oeuvre: id, s: saison, e: episode, cadre: CADRE_DISCORD }));
    else history.replaceState(null, '', `/?id=${encodeURIComponent(id)}&s=${saison}&e=${episode}`);
  } catch { /* un rechargement retombera sur la fiche */ }
}

/** Sortir du lecteur, c'est oublier l'épisode : un F5 sur la fiche rend la fiche. */
function oublierLectureDeLOnglet() {
  try { sessionStorage.removeItem(CLE_LECTURE); } catch { /* rien à oublier */ }
  try {
    if (!DANS_DISCORD && !/^\/lecteur\//.test(location.pathname) && /[?&]e=/.test(location.search)) history.replaceState(null, '', location.pathname);
  } catch { /* sans effet sur la navigation */ }
}

/** L'épisode qu'un rechargement doit rouvrir, ou `null`. */
function lectureARouvrir() {
  if (/^\/lecteur\//.test(location.pathname)) return null;
  if (DANS_DISCORD) {
    try {
      const l = JSON.parse(sessionStorage.getItem(CLE_LECTURE) ?? 'null');
      return l?.oeuvre && l.cadre === CADRE_DISCORD && Number(l.e) > 0 ? { oeuvre: l.oeuvre, saison: Number(l.s) || 0, episode: Number(l.e) } : null;
    } catch { return null; }
  }
  const e = Number(params.get('e'));
  return OUVRIR && params.has('s') && e > 0 ? { oeuvre: OUVRIR, saison: Number(params.get('s')) || 0, episode: e } : null;
}

/** La seconde où reprendre CET épisode : copie locale à la seconde si plus récente (b7564b0), sinon le serveur. */
async function repriseDeLEpisode(oeuvreId, saison, episode) {
  if (!(reprises ?? []).some((r) => r.oeuvreId === oeuvreId)) { try { await chargerReprises(); } catch { /* la copie locale suffit */ } }
  const meme = (x) => x && Number(x.saison) === saison && Number(x.episode) === episode;
  let locale = null;
  try { locale = JSON.parse(prefs.lire(`reprise.${oeuvreId}`, 'null')); } catch { /* copie illisible */ }
  const entree = (reprises ?? []).find((r) => r.oeuvreId === oeuvreId);
  if (meme(entree)) return positionDeReprise(entree, meme(locale) ? locale : null);
  return meme(locale) ? Number(locale.position) || 0 : 0;
}

/**
 * La saison (l'arc) qu'une fiche montre en s'ouvrant — PURE.
 * Toji (21/10) : la dernière CHOISIE pour cette œuvre ; à défaut celle de
 * l'épisode à reprendre ; à défaut la saison 1. `memoire` (cet onglet) passe
 * devant : c'est l'arc qu'on regardait il y a deux secondes.
 */
export function arcDeDepart(arcs, { memoire = null, retenu = '', reprise = null } = {}) {
  const valide = (v) => v !== null && v !== '' && Number.isInteger(Number(v)) && arcs[Number(v)] ? Number(v) : null;
  const choisi = valide(memoire) ?? valide(retenu);
  if (choisi !== null) return choisi;
  const contient = (saison, episode) => arcs.findIndex((a) => (a.episodes ?? [])
    .some((ep) => Number(ep.season ?? ep.saison) === Number(saison) && Number(ep.episode) === Number(episode)));
  const deLaReprise = reprise ? contient(reprise.saison, reprise.episode) : -1;
  if (deLaReprise >= 0) return deLaReprise;
  const saisonUn = arcs.findIndex((a) => (a.episodes ?? []).some((ep) => Number(ep.season ?? ep.saison) === 1));
  return saisonUn >= 0 ? saisonUn : 0;
}

/** Retient la saison choisie À LA MAIN pour cette œuvre (localStorage, aucun réglage de compte n'existe). */
function retenirSaison(index) {
  if (serie?.id) prefs.poser(`saison.${serie.id}`, String(index));
}

/** L'épisode à reprendre de cette œuvre : la copie locale si elle est plus récente, sinon le serveur. */
function repriseConnueDe(oeuvreId) {
  let locale = null;
  try { locale = JSON.parse(prefs.lire(`reprise.${oeuvreId}`, 'null')); } catch { /* copie illisible */ }
  const entree = (reprises ?? []).find((r) => r.oeuvreId === oeuvreId) ?? null;
  if (locale && (!entree || (Number(locale.quand) || 0) >= (Number(entree.quand) || 0))) return locale;
  return entree;
}

/**
 * Ouvre une œuvre : la fiche, ses épisodes, et de quoi en lancer un.
 *
 * ⚠️ C'EST ICI QUE TOMBE L'HYPOTHÈSE « UNE SEULE SÉRIE ». La fonction
 * s'appelait `ouvrirSerie`, allait chercher `/api/meta/series/:id` et posait le
 * résultat dans `serie`, la variable unique. Elle prend maintenant N'IMPORTE
 * QUELLE œuvre du catalogue — série ou film — et pose deux choses : `oeuvre`,
 * ce que le serveur décrit, et `serie`, sa traduction pour le code hérité.
 *
 * L'ancien nom reste exporté juste en dessous : une vingtaine d'appels s'en
 * servent (un billet, une reprise, un lien d'ouverture), et les renommer un à
 * un aurait fait une vingtaine d'occasions d'en oublier un.
 */
// Les fiches deja ouvertes dans cette page : `id -> { fiche, quand }`. Une
// `Map` ordinaire, videe par le plus ancien — voir `ouvrirOeuvre`.
const fichesEnMemoire = new Map();

// ---------------------------------------------------------------------------
// TOUT CE QU'ON A MONTRÉ DOIT SUFFIRE À OUVRIR UNE FICHE
//
// ⚠️ UNE CARTE VISIBLE À L'ÉCRAN POUVAIT MENER À UN MUR.
//
// Toji : « peu importe ce qu'on recherche, dans tous les cas on peut avoir
// l'affiche. Il devrait jamais y avoir un souci de ce type. »
//
// Or la fiche se peignait UNIQUEMENT depuis `/api/oeuvre/:id`. Si cet appel
// échouait — titre absent du catalogue, source muette, réseau qui tousse —
// l'écran affichait une erreur, alors que la carte sur laquelle on venait de
// cliquer portait déjà le titre, l'affiche, l'année et les genres. On avait
// tout ce qu'il faut sous les yeux, et on répondait « introuvable ».
//
// Chaque carte peinte dépose donc ce qu'elle sait ici. C'est un filet, pas un
// cache : il ne sert JAMAIS quand la route répond, et il ne remplace jamais
// une vraie fiche — il évite seulement le mur.
//
// Borné à 400 : c'est plus qu'une grille entière plus une soirée de recherche,
// et ça reste quelques dizaines de kilo-octets.
// ---------------------------------------------------------------------------
const oeuvresVues = new Map();
const PLAFOND_VUES = 400;

function retenirOeuvreVue(o) {
  if (!o?.id || typeof o.id !== 'string') return;
  // On ne garde que de quoi PEINDRE : ni épisodes, ni saisons, ni rien qui
  // puisse se faire passer pour une fiche complète au moment de la relire.
  oeuvresVues.set(o.id, {
    id: o.id,
    titre: o.titre ?? null,
    type: o.type ?? null,
    // ⚠️ LE FORMAT D'UN ANIME FAIT PARTIE DE CE QU'ON PEINT, ET DE CE QU'ON
    // PROPOSE. `typeLisible` l'écrit sous l'affiche (« Anime · OVA »), et
    // `estUnFilm` s'en sert pour savoir s'il y aura un prochain épisode — donc
    // si le menu au clic droit a le droit de proposer d'en être prévenu. Sans
    // lui, un film d'animation relu depuis ce filet se ferait passer pour une
    // série et proposerait une cloche qui ne sonnerait jamais.
    anime: o.anime && typeof o.anime === 'object' ? { ...o.anime } : null,
    affiche: o.affiche ?? null,
    fond: o.fond ?? null,
    annee: o.annee ?? null,
    note: o.note ?? null,
    genres: Array.isArray(o.genres) ? o.genres.slice(0, 6) : [],
    resume: o.resume ?? null,
    horsCatalogue: o.horsCatalogue === true,
  });
  if (oeuvresVues.size > PLAFOND_VUES) {
    oeuvresVues.delete(oeuvresVues.keys().next().value);
  }
}

// `enFond` : la fiche se remplit sans changer d'écran ni entrer dans le chemin
// du retour. C'est le mode des ouvertures AUTOMATIQUES (reprise de séance,
// ordre reçu, billet, démarrage) : personne n'a demandé à voir la fiche
// (audit 14e 21/10, P4/P6).
async function ouvrirOeuvre(id, { enFond = false, relance = false } = {}) {
  if (!id) return;
  // Le mode reglages appartient a l'oeuvre qu'on regardait : le garder en
  // ouvrant la suivante ferait ouvrir une pop-up de liens a qui voulait
  // simplement lancer un episode.
  modeMapping = false;
  chargerSeuilVF(id);
  if (!enFond) {
    texte('chargement-txt', 'Chargement…');
    // Ouvrir une fiche n'est pas lancer un épisode : la vignette du précédent
    // n'aurait plus rien à voir avec ce qu'on affiche.
    peindreFondAttente(null);
    peindreTitreAttente(null);
    vue('v-chargement');
  }

  // ⚠️ UNE FICHE DEJA OUVERTE NE SE REDEMANDE PAS.
  //
  // Toji : « après, on garde les infos et les icônes temporairement ». Le
  // serveur garde deja la SIENNE, mais chaque retour repayait quand meme
  // l'aller-retour reseau et l'ecran de chargement qui va avec — pour une
  // reponse identique. Rouvrir une serie qu'on vient de fermer doit etre
  // instantane, sans le clignotement de l'attente.
  //
  // Cinq minutes, et plus vingt : la liste d'episodes doit suivre AIOMetadata
  // (Toji, 19/10 — « des qu'AIOMetadata est mis a jour, le lecteur doit l'etre
  // aussi »). Le serveur ne refabrique la fiche que si la source a change :
  // revenir sur une fiche reste donc quasi gratuit. Une fiche servie depuis le
  // miroir n'est pas gardee du tout — elle doit ceder sa place des que la
  // source revient.
  let brut = fichesEnMemoire.get(id);
  if (!brut || brut.fiche?.miroir || Date.now() - brut.quand > 5 * 60_000) {
    try {
      brut = { fiche: await api(`/api/oeuvre/${encodeURIComponent(id)}`), quand: Date.now() };
      fichesEnMemoire.set(id, brut);
      // Une trentaine de fiches suffit a une soiree ; au-dela on oublie la plus
      // ancienne plutot que de laisser la page grossir sans fin.
      if (fichesEnMemoire.size > 30) fichesEnMemoire.delete(fichesEnMemoire.keys().next().value);
    } catch (err) {
      // ⚠️ Meme garde qu'ailleurs (voir `api()`) : une « attente
      // d'autorisation » a deja pose son propre ecran, ne pas l'ecraser.
      if (err.silencieux) return;

      // ⚠️ ON A DÉJÀ MONTRÉ CE TITRE. ON NE VA PAS DIRE QU’IL N’EXISTE PAS.
      //
      // La carte cliquée portait son affiche, son titre, son année. Répondre
      // « introuvable » parce qu'un appel a échoué, c'est démentir ce que la
      // personne a sous les yeux — et c'est ce qui s'est produit le 09/09 sur
      // John Wick 4.
      //
      // On peint donc ce qu'on sait, en le DISANT : la fiche s'ouvre, l'affiche
      // est là, et le message explique que la liste des épisodes manque encore.
      // Un titre qu'on n'a jamais vu, lui, garde son message d'erreur — inventer
      // une fiche vide serait pire.
      const vue = oeuvresVues.get(id);
      if (!vue) return erreur(err.message);
      tracer('oeuvre', 'fiche servie de mémoire', { id, raison: err.message });
      brut = { fiche: { ...vue, saisons: [], sansSource: true }, quand: 0 };
    }
  }
  brut = brut.fiche;
  // Variante servie par le serveur pour cette fiche : le lecteur dit discrètement si elle a dû changer.
  varianteFiche = brut.variante ?? null;
  reglagesFiche = brut.reglages ?? null;

  const lue = normaliserOeuvre(brut);
  if (!lue) return erreur('Cette œuvre est introuvable.');
  oeuvre = lue;
  serie = enSerie(lue);
  // Les collections de l'ancien accueil ne décident plus du retour : on vient
  // d'une œuvre, pas d'une planche.
  collectionCourante = null;

  // L'adresse retient l'ŒUVRE, plus « la » série : recharger doit rendre la
  // fiche qu'on regardait, et il y en a maintenant plusieurs.
  marquerPosition({ oeuvre: oeuvre.id });

  peindreFicheOeuvre();
  if (!enFond) vue(ECRAN_OEUVRE());
  // La source a seulement tardé : le serveur la rafraîchit en fond. Une seule
  // relecture quelques secondes plus tard repeint la fiche si l'on y est encore
  // (hors réglages) ; sinon la prochaine ouverture lira la réponse fraîche.
  if (!relance && lue.miroir?.souci === MIROIR_TARDE) {
    setTimeout(() => {
      if (oeuvre?.id === id && ecranCourant === ECRAN_OEUVRE() && !modeMapping) {
        ouvrirOeuvre(id, { enFond: true, relance: true }).catch(() => {});
      }
    }, 6_000);
  }
}

/** @deprecated Ancien nom d'`ouvrirOeuvre`. Conservé pour les appels existants. */
const ouvrirSerie = ouvrirOeuvre;

// ---------------------------------------------------------------------------
// RETENIR OÙ L'ON EN EST
//
// La rangée « Reprendre » de l'accueil lit `/api/reprendre`. Encore faut-il que
// quelqu'un l'écrive : sans cette section, la rangée serait vide pour toujours
// et personne ne saurait dire si c'est la rangée qui ne marche pas ou s'il n'y
// a rien à reprendre.
//
// Trois règles, chacune payée ailleurs dans ce fichier :
//
//   · ON N'ANNONCE PAS UNE LECTURE QUI N'A PAS COMMENCÉ. Une balise préparée
//     mais restée au générique n'est pas une reprise : elle mettrait une carte
//     à 0 % sur l'accueil pour chaque épisode simplement effleuré. Cinq
//     secondes de lecture suffisent à faire la différence.
//   · LE REPÈRE DE « PRÊT » EST `imagePrete`, jamais `readyState >= 3` ni
//     `Number.isFinite(duration)` : sur un MKV démuxé dans la page, la durée
//     reste `Infinity` et `readyState` plafonne à 2. Les deux repères habituels
//     ne sont jamais atteints, et rien ne serait jamais noté.
//   · LA DURÉE NE PART PAS D'ICI. Le serveur la reprend de sa propre mesure —
//     celle que le lecteur affiche peut encore être l'estimation du catalogue,
//     et la figer dans la mémoire de reprise rendrait la jauge fausse pour
//     toujours. C'est écrit noir sur blanc dans la route.
// ---------------------------------------------------------------------------

/** Ce qui joue en ce moment, tel qu'on le noterait sur le compte. */
let lectureCourante = null;

// Un envoi toutes les cinq secondes (Toji, 21/10 : « quasiment à la seconde »),
// plus un envoi immédiat à la pause, au saut, au changement d'épisode et à la
// sortie. La seconde exacte, elle, est gardée sur l'appareil à chaque battement
// (`prefs` « reprise.<œuvre> ») : une sortie brutale ne coûte rien ici.
const ECART_REPRISE = 5_000;
let derniereReprise = { cle: null, quand: 0, position: -1 };
let basse = { cle: null, position: -1 };

function noterOuOnEnEst({ force = false, partant = false } = {}) {
  const v = $('video');
  if (!v || !lectureCourante || !imagePrete(v)) return;
  // ⚠️ LA SECONDE DE L'ŒUVRE, JAMAIS `v.currentTime` — voir `position()`.
  //
  // Au cran remux, le serveur coupe le flux à la seconde demandée et la balise
  // repart de ZÉRO : `currentTime` dit alors où l'on en est dans le SEGMENT, ce
  // qui n'a aucun sens pour une mémoire de reprise. Un film repris à la
  // quarantième minute postait une poignée de secondes, et « Reprendre » le
  // ramenait éternellement au même endroit.
  //
  // `lecteur()` monte le lecteur au premier appel — ici c'est sans effet : on
  // ne passe les gardes ci-dessus que si une lecture est EN COURS, donc si le
  // lecteur joue déjà. Le repli couvre la méthode absente, pas le lecteur
  // absent : aux crans direct et mse le décalage vaut zéro, les deux valeurs
  // sont alors égales, et une note approximative vaut mieux qu'aucune note.
  const position = Math.floor(lecteur()?.position?.() ?? v.currentTime ?? 0);
  if (!(position > 5)) return;
  // Un retour volontaire n'est cru qu'après une vraie lecture : 0-10 s qui AVANCE de 3 s+, hors saut.
  const cleBas = `${lectureCourante.oeuvreId}:${lectureCourante.saison ?? 0}:${lectureCourante.episode ?? 0}`;
  let rembobine = false;
  if (position <= 10 && !v.seeking && !v.paused) {
    rembobine = basse.cle === cleBas && position >= basse.position + 3;
    if (basse.cle !== cleBas || position < basse.position) basse = { cle: cleBas, position };
  } else if (position > 10) basse = { cle: null, position: -1 };

  const cle = `${lectureCourante.oeuvreId}:${lectureCourante.saison ?? 0}:${lectureCourante.episode ?? 0}`;
  const maintenant = Date.now();
  prefs.poser(`reprise.${lectureCourante.oeuvreId}`, JSON.stringify({
    saison: lectureCourante.saison ?? 0, episode: lectureCourante.episode ?? 0, position, quand: maintenant,
  }));
  if (!force) {
    if (v.paused) return;
    if (cle === derniereReprise.cle && maintenant - derniereReprise.quand < ECART_REPRISE) return;
  }
  // Rien n'a bougé depuis le dernier envoi : réécrire la même seconde ne dit
  // rien de neuf, et une sortie d'écran répétée en enverrait autant.
  if (cle === derniereReprise.cle && position === derniereReprise.position) return;
  derniereReprise = { cle, quand: maintenant, position };

  // La durée lue n'est qu'un repli côté serveur (sa sonde prime) : sans elle,
  // un épisode jamais sondé n'était jamais « fini ».
  const totale = Number(lecteur()?.totale?.());
  api('/api/reprendre', {
    method: 'POST',
    // `keepalive` : la requête survit à la fermeture de l'onglet (pagehide).
    keepalive: partant,
    body: JSON.stringify({
      ...lectureCourante, position, rembobine, duree: totale > 0 ? Math.round(totale) : null,
      // La présence réelle, pour le bonus de groupe (caisse) : séance, onglet, dernière interaction.
      seance: instanceCourante() ?? null,
      visible: document.visibilityState === 'visible',
      interactionS: Math.round((Date.now() - derniereInteraction) / 1000),
    }),
  }).then((r) => caisseUI.surBonus(r?.caisse)).catch((err) => {
    // Ne se voit pas et ne doit pas se voir : la lecture continue, seule la
    // reprise depuis un autre appareil est perdue.
    tracer('reprendre', 'note refusée', err.message);
  });
}

/**
 * Reprendre là où l'on s'est arrêté.
 *
 * ⚠️ LA POSITION VOYAGE JUSQU'AU LECTEUR, elle ne se rejoue pas après coup.
 * Ouvrir puis chercher la seconde une fois la lecture partie fait entendre
 * trois secondes du début — et sur un flux remuxé, c'est un aller-retour
 * serveur complet pour rien. `lancer` sait démarrer à une seconde donnée.
 */
async function reprendreLecture(entree) {
  if (!entree?.oeuvreId) return;
  await ouvrirOeuvre(entree.oeuvreId);
  if (!oeuvre) return;
  if (estUnFilm(oeuvre)) {
    // ⚠️ « REPRENDRE » RELANCAIT UN FILM A ZERO.
    //
    // `entree.position` s'arretait ici : `lancerFilm` n'avait meme pas de
    // parametre pour la recevoir. Cliquer « Reprendre » sur un film rouvrait
    // donc TOUJOURS le generique, exactement comme un premier visionnage —
    // la ligne voisine, juste en dessous, le fait deja bien pour un episode.
    return lancerFilm({
      id: oeuvre.id, nom: oeuvre.titre, fond: oeuvre.fond, affiche: oeuvre.affiche,
    }, { reprise: positionDeReprise(entree, (() => { try { return JSON.parse(prefs.lire(`reprise.${entree.oeuvreId}`, 'null')); } catch { return null; } })()) });
  }
  if (!(entree.saison > 0) || !(entree.episode > 0)) return;
  let locale = null;
  try { locale = JSON.parse(prefs.lire(`reprise.${entree.oeuvreId}`, 'null')); } catch { /* copie locale illisible : le serveur suffit */ }
  return lancer(entree.saison, entree.episode, { reprise: positionDeReprise(entree, locale) });
}

// Le résumé d'un épisode tient parfois en dix lignes : on garde la première
// phrase, ce qui suffit à situer sans noyer la grille.
function resumeCourt(texte) {
  if (!texte) return '';
  const propre = String(texte).replace(/\s+/g, ' ').trim();
  const fin = propre.search(/[.!?](\s|$)/);
  return propre.length > 320 ? `${propre.slice(0, 317)}…` : propre;
}

// Vignette d'épisode : image, encoche portant le numéro, titre posé dessus.

const DATE_COURTE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });

/** Titre ramené à une longueur tenable sur une vignette. */
function titreCourt(texte, max = 42) {
  const t = String(texte ?? '').trim();
  if (t.length <= max) return t;
  // Couper au dernier espace évite de trancher un mot en deux.
  const coupe = t.slice(0, max);
  const espace = coupe.lastIndexOf(' ');
  return `${(espace > max * 0.6 ? coupe.slice(0, espace) : coupe).trimEnd()}…`;
}

// Dernier épisode dont le doublage est AVÉRÉ — mesuré par le serveur, jamais
// écrit en dur : un doublage avance au fil des sorties, et une valeur figée
// vieillirait mal.
//
// ⚠️ `null` TANT QUE LE SERVEUR N'A PAS RÉPONDU, et surtout pas une valeur
// plausible. La ligne portait S17E41 en dur, ce qui est deux mensonges à la
// fois : c'est le doublage de Bleach à une date donnée, appliqué à toute série
// ouverte, et `doublageConnu` — que le lecteur interroge pour décider du
// drapeau de langue — répondait « je sais » sur cette valeur inventée avant
// même que `chargerSeuilVF` ait envoyé sa requête. Ne pas savoir se dit :
// `estDouble` répond alors « pas de VF connue », `doublageConnu` répond `null`
// et le lecteur laisse le clic trancher.
let seuilVF = null;

/** L'épisode se situe-t-il en deçà du seuil de doublage ? */
function estDouble(saison, episode) {
  if (!seuilVF) return false;
  return saison < seuilVF.saison
    || (saison === seuilVF.saison && episode <= seuilVF.episode);
}

/**
 * Les langues d'un épisode — LE MAPPING D'ABORD, le seuil ensuite.
 *
 * Toji : « il faudra s'assurer que le mapping permet aussi de mapper la source
 * de vérité. À chaque fois que quelqu'un va cliquer sur un des épisodes, s'il
 * voit français et japonais, il doit se dire : okay, je peux le regarder dans
 * les deux langues. »
 *
 * C'est la bonne hiérarchie, et elle n'était pas respectée. Le drapeau venait
 * d'un SEUIL GLOBAL — « tout ce qui précède S17E41 est doublé » — c'est-à-dire
 * d'une généralité vraie en gros et fausse en détail : un épisode ancien sans
 * VF portait le drapeau, un épisode récent qui en a une ne le portait pas.
 *
 * Le balayage, lui, sait ÉPISODE PAR ÉPISODE : il a vu les liens. Le serveur
 * pose déjà ce qu'il a trouvé dans `v.langues` — c'est cette réponse-là qu'il
 * faut lire, et le seuil ne sert plus qu'à répondre quelque chose là où le
 * balayage n'est pas encore passé.
 *
 * Ni l'un ni l'autre ne remplace la sonde : eux lisent des noms de fichiers,
 * elle lit les pistes. Mais pour un drapeau sur une vignette — « puis-je le
 * regarder en français ? » — la réponse du balayage est la bonne.
 */
function languesDeLEpisode(v) {
  return versionsDeLEpisode(v).langues;
}

/**
 * LES VERSIONS D'UN ÉPISODE, ET D'OÙ ON LE SAIT — PURE, éprouvée sans navigateur.
 *
 * ⚠️ LE DÉFAUT, MOT POUR MOT : « pourquoi ça me note qu'il y a du français
 * alors qu'il n'y a pas de français ? »
 *
 * Il y avait DEUX mensonges empilés dans les trois lignes que cette fonction
 * remplace, et le second était le pire :
 *
 *   1. `v.langues` arrive du serveur sans dire d'où il vient. C'est tantôt une
 *      MESURE (la sonde a ouvert le conteneur et lu ses pistes ; le dossier
 *      porte alors `mesurees: true`), tantôt une SUPPOSITION tirée du NOM des
 *      fichiers — et « MULTI » ou « DUAL AUDIO » promettent une bande-son
 *      française que beaucoup de ces fichiers ne portent pas. Le serveur
 *      transmet désormais `languesMesurees` ; le drapeau l'avoue au survol.
 *
 *   2. ⚠️ ET SURTOUT : QUAND LE BALAYAGE N'ÉTAIT JAMAIS PASSÉ, on posait
 *      quand même un drapeau français — tiré du SEUIL GLOBAL de doublage
 *      (`estDouble`), c'est-à-dire de « tout ce qui précède S17E41 est
 *      doublé ». Une généralité vraie en gros, fausse en détail, appliquée à
 *      un épisode que PERSONNE n'a regardé : ce n'est ni une mesure, ni même
 *      une annonce d'une source. C'est une invention de l'écran.
 *
 * Le seuil ne disparaît pas — il reste ce qu'il a toujours été, un ordre de
 * grandeur utile ailleurs — mais il ne DESSINE plus. Ne pas savoir se dit en
 * ne posant rien : la version originale, elle, est une tautologie et reste.
 *
 * @returns {{langues: string[], mesure: ?boolean, seuil: boolean}}
 */
export function versionsDeLEpisode(v, { double = estDouble } = {}) {
  if (Array.isArray(v?.langues) && v.langues.length) {
    // `languesMesurees` absent = un serveur d'avant ce correctif, ou un
    // épisode dont le dossier ne porte pas le drapeau : « on ne sait pas »,
    // et non « ce n'est pas mesuré » — d'où `null` plutôt que `false`.
    const mesure = typeof v.languesMesurees === 'boolean' ? v.languesMesurees : null;
    return { langues: v.langues, mesure, seuil: false };
  }
  // Le seuil est PASSABLE en parametre pour une seule raison : sans cela,
  // aucun banc ne pourrait prouver que le drapeau francais ne sort plus de lui.
  // Un essai qui ne peut pas voir le defaut ne garde rien.
  return { langues: ['vo'], mesure: null, seuil: !!double(v?.season, v?.episode) };
}

/**
 * Réclame le seuil. Le serveur sonde en arrière-plan : tant qu'il n'a pas
 * conclu, il répond sans valeur et l'on repasse un peu plus tard.
 */
function chargerSeuilVF(serieId) {
  api(`/api/vf/${encodeURIComponent(serieId)}`)
    .then(({ seuil }) => {
      // Sondage en cours, ou sans résultat : on n'invente rien. Ce qui a déjà
      // été mesuré tient ; au premier passage, il n'y a rien et l'on continue
      // de ne pas savoir jusqu'au tour suivant.
      if (!seuil || !seuil.saison) { setTimeout(() => chargerSeuilVF(serieId), 15000); return; }
      const change = JSON.stringify(seuil) !== JSON.stringify(seuilVF);
      seuilVF = seuil;
      // Le seuil arrive souvent après le premier rendu : on redessine plutôt
      // que de retarder tout l'affichage en l'attendant.
      if (change && arcAffiche !== null) rendreArc(arcAffiche);
    })
    // Une panne ici laissait `seuilVF` nul pour toute la session, sans un mot.
    .catch((err) => tracer('vf', 'seuil de doublage indisponible', err?.message ?? String(err)));
}

/**
 * Une vignette d'épisode — et, depuis ce chantier, SA LÉGENDE.
 *
 * ⚠️ LE MÊME CONTENU, MAIS HORS DU CADRE, POUR LE TÉLÉPHONE.
 *
 * La vignette de bureau empile trois choses dans un seul rectangle 16/9 :
 * l'image, le titre en bas à droite, et le résumé qui vient prendre la place du
 * titre au survol. Sans survol, la passe précédente a montré le résumé EN
 * PERMANENCE et masqué le titre. Mesuré à 390 px : le résumé recouvrait l'image
 * entière, le numéro — un chiffre gris posé au même endroit — se lisait
 * DERRIÈRE le texte, et le titre avait purement disparu de la grille. Une liste
 * d'épisodes où l'on ne voit ni la vignette, ni le titre, ni le numéro.
 *
 * Trois informations ne tiennent pas superposées sur six centimètres. Elles
 * sortent donc du cadre, en clair, à côté de l'image — et la vignette redevient
 * une vignette. Le nœud `.prev-legende` est masqué partout sauf dans la couche
 * mobile de la feuille de style : la grille du bureau ne gagne pas un pixel, et
 * pas d'espace de `gap` non plus, un enfant sans boîte n'en produisant aucun.
 *
 * Le résumé y tient en deux lignes : c'est déjà ce que la vignette montrait au
 * survol, et deux lignes suffisent à reconnaître un épisode.
 */
// ---------------------------------------------------------------------------
// LA GRILLE D'EPISODES : ELLE N'EST PAS VIRTUALISEE, ELLE SE RANGE PAR RANG.
//
// Un arc de quatre cents episodes pose quatre cents vignettes d'un coup. On ne
// peut donc pas toutes les demander tout de suite — mais on ne peut pas non
// plus toutes les laisser en `loading="lazy"`, sinon les premieres, celles
// qu'on regarde, attendent le bon vouloir du navigateur.
//
// Trois rangs, la meme regle que partout ailleurs (`chargementDImage`) :
// ce qui est a l'ecran passe devant, ce qui suit se prepare en priorite basse,
// le reste attend qu'on descende.
const VIGNETTES_VUES = 12;    // environ deux lignes sur un ecran d'ordinateur
const VIGNETTES_RESERVE = 36; // quatre lignes de plus, deja chargees

/**
 * @param {number} rang la place de la vignette dans l'arc. `Array.map` le passe
 *   tout seul — c'est bien le rang dans la grille, pas le numero d'episode.
 */
/**
 * L'image d'un épisode que le catalogue ne connaît pas encore (VisionQuest,
 * épisodes 3 et 4 sortis mais hachurés, 22/10) : le fond de l'œuvre, son
 * affiche à défaut. La trame ne dit plus « rien » pour un épisode qu'on peut lire.
 */
function imageDeRepliEpisode() {
  const o = oeuvre?.id && oeuvre.id === serie?.id ? oeuvre : null;
  const src = fondDeLOeuvre(o) || serie?.background || serie?.poster || '';
  return src ? imageDeTaille(src, 320, 'fond') : '';
}

function vignetteEpisode(v, rang = 0) {
  const reste = joursAvant(v.released);
  const chargement = chargementDImage(
    rang < VIGNETTES_VUES ? 'dans' : (rang < VIGNETTES_RESERVE ? 'reserve' : 'plus tard'),
  );
  const etat = reste ? null : etatDeLEpisode(v, { manuel: true });
  return `<button class="prev${reste ? ' a-venir' : ''}" type="button"
    data-s="${v.season}" data-e="${v.episode}"${etat?.vu ? ' data-vu="1"' : ''}
    ${reste ? `disabled aria-disabled="true" title="Diffusion le ${
      esc(dateFr(v.released))}"` : ''}>
    <span class="cadre-img">
      ${(v.thumbnail || imageDeRepliEpisode()) ? `<img src="${esc(v.thumbnail || imageDeRepliEpisode())}"${v.thumbnail ? '' : ' class="repli"'} alt="" width="320" height="180" decoding="${chargement.decoding}" loading="${chargement.loading}" fetchpriority="${chargement.fetchpriority}" />` : ''}
      ${v.resume ? `<span class="desc"><span>${esc(resumeCourt(v.resume))}</span></span>` : ''}
      <svg class="cadre-trait" viewBox="0 0 160 90" preserveAspectRatio="none" aria-hidden="true">
        <path class="piece coin" d="M2,71 Q2,69.5 3.5,70.8 L17.5,86.5 Q19,88 17,88 L4,88 Q2,88 2,86 Z" />
        <path class="piece" d="M5,1 H155 Q159,1 159,5 V85 Q159,89 155,89 H23
                 Q20.5,89 19,87.3 L5.4,71.6 Q4,70 4,67.6 V5 Q4,1 5,1 Z" />
      </svg>
      <span class="num">${v.absolu ?? v.episode}</span>
      ${etat ? etat.html : ''}
      ${reste ? `<span class="a-venir-pastille">
        <b>${DATE_COURTE.format(new Date(v.released))}</b>
        <i>${reste} ${pluriel(reste, 'jour')}</i>
      </span>` : ''}
      ${reste ? '' : `<span class="lang">${drapeauxDe(versionsDeLEpisode(v).langues, { type: oeuvre?.type, mesure: versionsDeLEpisode(v).mesure })}</span>`}
      <span class="tt">${esc(titreCourt(v.title) || `Épisode ${v.episode}`)}</span>
    </span>
    <span class="prev-legende">
      <span class="n">Épisode ${v.absolu ?? v.episode}</span>
      <span class="t">${esc(titreCourt(v.title) || `Épisode ${v.episode}`)}</span>
      ${reste
        ? `<span class="r">Diffusion le ${esc(dateFr(v.released))}</span>`
        : (v.resume ? `<span class="r">${esc(resumeCourt(v.resume))}</span>` : '')}
    </span>
  </button>`;
}

/** Barre de progression + « Vu » d'un épisode de la fiche (vide s'il n'est pas commencé). */
function etatDeLEpisode(v, { manuel = false } = {}) {
  const id = oeuvre?.id;
  const entree = id ? (reprises ?? []).find((r) => r.oeuvreId === id) : null;
  let e = id ? etatDEpisode(vusServeur[id], entree, v.season, v.episode) : { vu: false, part: 0 };
  // La tuile compte aussi la marque posée à la main (clic droit) ; la ligne la lit déjà à part.
  if (manuel && !e.vu && id && episodeVuIci(id, v.season, v.episode)) e = { vu: true, part: 1 };
  const barre = e.vu || e.part > 0.01
    ? `<span class="ep-barre" aria-hidden="true"><i style="width:${Math.round(e.part * 100)}%"></i></span>` : '';
  // Pas de « N min restantes » : Toji l'a retiré le 21/10, la barre le dit déjà.
  // Vu : un voile sur l'image et la coche (posée en CSS devant « Vu »).
  return { vu: e.vu, part: e.part, html: barre + (e.vu ? '<span class="ep-voile" aria-hidden="true"></span><span class="ep-vu">Vu</span>' : '') };
}

function ligneEpisode(v) {
  // ⚠️ LA MARQUE EST POSÉE À LA FABRICATION, pas ajoutée après coup. La fiche
  // se repeint entièrement à chaque changement de saison ou de mode : une
  // décoration appliquée ensuite disparaîtrait au premier repeinte, et
  // l'épisode qu'on vient de marquer redeviendrait vierge sous les yeux.
  const etat = etatDeLEpisode(v);
  const vu = (oeuvre?.id ? episodeVuIci(oeuvre.id, v.season, v.episode) : false) || etat.vu;
  return `<button class="ep" type="button" data-s="${v.season}" data-e="${v.episode}"${vu ? ' data-vu="1"' : ''}
    title="${esc(v.resume || '')}">${etat.html}
    <span class="n">${v.absolu ?? v.episode}</span>
    <span class="t">${esc(v.title || `Épisode ${v.episode}`)}</span>
    <span class="go">${anneeDe(v.released) ?? '▶'}</span>
  </button>`;
}

// ---------------------------------------------------------------------------
// LA BULLE D'UN ÉPISODE AU SURVOL (Toji, 22/10). Le résumé posé sur la vignette
// la salissait : il passe dans une bulle DÉCALÉE, au-dessus de la tuile, sinon
// à côté, sinon dessous. Fixe sur la page, elle n'est jamais coupée par un
// conteneur à `overflow` caché, et reste bornée à l'écran. Souris et focus au
// clavier ; au doigt, la légende sous l'image suffit. `pointer-events: none` et
// `aria-hidden` : elle ne prend jamais le clic (le résumé reste lisible au
// lecteur d'écran dans la tuile, `.desc`).
// ---------------------------------------------------------------------------
const BULLE_DELAI = 350;
const BULLE_ECART = 10;
let bulleEp = null;
let bulleTuile = null;
let bulleMinuteur = null;

function cacherBulleEpisode() {
  clearTimeout(bulleMinuteur);
  bulleTuile = null;
  bulleEp?.classList.remove('visible');
}

function contenuDeBulle(v) {
  const etat = etatDeLEpisode(v, { manuel: true });
  const versions = versionsDeLEpisode(v);
  const drapeaux = drapeauxDe(versions.langues, { type: oeuvre?.type, mesure: versions.mesure });
  // « En cours » sans minutes : Toji a retiré le temps restant le 21/10.
  const statut = etat.vu ? '<b class="eb-vu">Vu</b>' : (etat.part > 0.01 ? '<b>En cours</b>' : '');
  const pied = [statut, v.duree ? esc(dureeLisible(v.duree)) : '', drapeaux ? `<span class="eb-lang">${drapeaux}</span>` : '']
    .filter(Boolean).join('<i aria-hidden="true">·</i>');
  return `<p class="eb-n">Épisode ${v.absolu ?? v.episode}${v.season ? ` · S${v.season}E${v.episode}` : ''}</p>
    <p class="eb-t">${esc(v.title || `Épisode ${v.episode}`)}</p>
    ${v.resume ? `<p class="eb-r">${esc(resumeCourt(v.resume))}</p>` : ''}
    ${pied ? `<p class="eb-pied">${pied}</p>` : ''}`;
}

/** Dessus, sinon à droite, à gauche, dessous — toujours dans l'écran, sous le choix de saison collé en haut. */
function placerBulleEpisode(tuile) {
  const r = rectPage(tuile.querySelector('.cadre-img') ?? tuile);
  const L = largeurPage();
  const H = hauteurPage();
  const barre = tuile.closest('.st-oeuvre-eps')?.querySelector(':scope > .choix-arc');
  const haut = Math.max(8, barre ? rectPage(barre).bottom + 4 : 0);
  const l = bulleEp.offsetWidth;
  const h = bulleEp.offsetHeight;
  const borne = (x, min, max) => Math.min(Math.max(x, min), Math.max(min, max));
  const centreX = borne(r.left + r.width / 2 - l / 2, 8, L - l - 8);
  const centreY = borne(r.top + r.height / 2 - h / 2, haut, H - h - 8);
  let x = centreX;
  let y = r.top - BULLE_ECART - h;
  let cote = 'dessus';
  if (y < haut) {
    if (r.right + BULLE_ECART + l <= L - 8) { x = r.right + BULLE_ECART; y = centreY; cote = 'droite'; }
    else if (r.left - BULLE_ECART - l >= 8) { x = r.left - BULLE_ECART - l; y = centreY; cote = 'gauche'; }
    else { y = borne(r.bottom + BULLE_ECART, haut, H - h - 8); cote = 'dessous'; }
  }
  bulleEp.style.left = `${Math.round(x)}px`;
  bulleEp.style.top = `${Math.round(y)}px`;
  bulleEp.dataset.cote = cote;
}

function brancherBulleEpisode() {
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  bulleEp = document.createElement('div');
  bulleEp.className = 'ep-bulle';
  bulleEp.setAttribute('aria-hidden', 'true');
  document.body.append(bulleEp);
  // Souris (survol) ou clavier (focus au Tab) : même bulle, même délai.
  const suivre = (e) => {
    const tuile = e.target.closest?.('.prev[data-s][data-e]:not(.a-venir)') ?? null;
    if (tuile === bulleTuile) return;
    cacherBulleEpisode();
    if (!tuile || (e.type === 'pointerover' && e.pointerType !== 'mouse')) return;
    bulleTuile = tuile;
    bulleMinuteur = setTimeout(() => {
      const v = serie?.videos?.find((x) => x.season === Number(tuile.dataset.s) && x.episode === Number(tuile.dataset.e));
      if (!v || !tuile.isConnected || bulleTuile !== tuile) return;
      bulleEp.innerHTML = contenuDeBulle(v);
      placerBulleEpisode(tuile);
      bulleEp.classList.add('visible');
    }, BULLE_DELAI);
  };
  document.addEventListener('pointerover', suivre);
  document.addEventListener('focusin', (e) => { if (e.target.matches?.(':focus-visible')) suivre(e); });
  // Sortie de la fenêtre, clic, touche, défilement : elle part tout de suite.
  document.addEventListener('pointerout', (e) => { if (!e.relatedTarget) cacherBulleEpisode(); });
  for (const ev of ['pointerdown', 'keydown']) document.addEventListener(ev, cacherBulleEpisode, true);
  document.addEventListener('scroll', cacherBulleEpisode, { capture: true, passive: true });
}


// ---------------------------------------------------------------------------
// LES REGLAGES DES LIENS — LE MAPPING, VU ET MODIFIABLE
//
// Toji : « il y a un bouton pour les réglages dans chaque série. Quand on
// clique dessus, on peut choisir la saison, et quand on clique sur un épisode,
// à la place d'ouvrir le lecteur, ça ouvre les réglages de l'épisode … un
// pop-up où il y a le listing au niveau des add-ons, et voir par exemple un
// pour le mettre en mapping. Je peux faire du mapping manuel. »
//
// Pourquoi cet écran existe : le classement des liens — correspondance, langue,
// poids, verdict de sonde — est fait de bonnes règles, et elles se trompent. Un
// épisode servi en HD alors qu'une 4K existe ne se voit nulle part, et rien ne
// permettait de le corriger. Ici on voit ce qui a été trouvé, ce que la sonde
// en sait, et on tranche.
let modeMapping = false;

/** Le mode réglages ne s'offre qu'à qui gère les add-ons. */
const peutRegler = () => Boolean(moi?.droits?.sources || moi?.droits?.admin || moi?.categorie === 'proprietaire');

function basculerModeMapping() {
  modeMapping = !modeMapping;
  texte('oeu-mapping-mot', modeMapping ? 'Retour' : 'Réglages');
  $('oeu-mapping')?.setAttribute('aria-pressed', String(modeMapping));
  // La fiche se repeint : c'est elle qui pose les clics des épisodes, et ils ne
  // font pas la même chose selon le mode.
  try { peindreFicheOeuvre(); } catch { /* fiche pas encore montée */ }
  const grille = $('ep-grille');
  if (grille) grille.classList.toggle('en-reglages', modeMapping);
}

/** Taille lisible — c'est le premier indice de qualité, avant même le nom. */
function poidsLisible(octets) {
  const n = Number(octets);
  if (!Number.isFinite(n) || n <= 0) return null;
  const go = n / (1024 ** 3);
  return go >= 1 ? `${go.toFixed(go >= 10 ? 0 : 1)} Go` : `${Math.round(n / (1024 ** 2))} Mo`;
}

/** La résolution telle que le nom du fichier l'annonce. */
function qualiteDuNom(nom) {
  const t = String(nom ?? '');
  if (/\b(2160p|4k|uhd)\b/i.test(t)) return '4K';
  if (/\b1080p\b/i.test(t)) return '1080p';
  if (/\b720p\b/i.test(t)) return '720p';
  if (/\b(480p|360p|sd)\b/i.test(t)) return 'SD';
  return null;
}

/** Le poids annoncé dans la description d'un flux : « 3.4 GB », « 950 MB ». */
function poidsDuTexte(texte) {
  const m = /(\d+(?:[.,]\d+)?)\s*(g|m)b\b/i.exec(String(texte ?? ''));
  if (!m) return null;
  const n = Number(m[1].replace(',', '.'));
  return m[2].toLowerCase() === 'g' ? n * (1024 ** 3) : n * (1024 ** 2);
}

// ---------------------------------------------------------------------------
// LA POP-UP D'UN EPISODE : LES ADD-ONS, PUIS L'APERCU
//
// Toji : « quand on clique sur Épisode 1, ça fait une recherche comme Stremio.
// Donc ça cherche les différents liens … on a un tab Add-on et on a la liste
// des liens. On peut voir en termes de qualité, donc on peut trier par exemple
// pour de la 4K, HD, etc. … Et on a un onglet Aperçu, et on peut directement
// générer un aperçu … télécharger l'image pour vérifier que la qualité soit
// suffisante. »
//
// ⚠️ ELLE CHERCHE EN DIRECT, elle ne relit pas le mapping. Le mapping ne
// contient que ce que le balayage a croisé, un jour, avec les sources d'alors.
// La question posée ici est « qu'est-ce qui existe MAINTENANT », et c'est la
// même que celle que le lecteur pose au moment de lancer.
let mapCible = null;
let mapLiens = [];
let mapOnglet = 'addons';
let mapFiltreQualite = 'tout';
let mapFiltreAddon = 'tout';
let mapChoixApercu = null;
// L'extraction en cours, s'il y en a une : { nom, pourcent }. Un seul travail a
// la fois — c'est le serveur qui l'impose, un ffmpeg par fichier.
let mapExtraction = null;

/** Pose l'avancement sur l'onglet « Aperçu », sans rien repeindre d'autre. */
function majEtatExtraction() {
  const cible = document.querySelector('[data-etat-onglet="apercu"]');
  if (!cible) return;
  cible.textContent = mapExtraction ? ` · ${mapExtraction.pourcent} %` : '';
  cible.classList.toggle('actif', Boolean(mapExtraction));
}

const ORDRE_QUALITE = ['4K', '1080p', '720p', 'SD'];

// Deux pictogrammes, dessines ici : un cone pour VLC, un ecran a fleche pour
// Stremio. Ce ne sont pas les logos officiels — on n'a pas a les redistribuer —
// mais des formes qui se reconnaissent d'un coup d'oeil a cette taille, ce qui
// est tout ce qu'on leur demande.
// Le mot « Aperçu » redevient le mot quand le bouton n'est plus « Validé ».
const ICONE_APERCU = 'Aperçu';
const ICONE_VLC = `<svg viewBox="0 0 24 24" aria-hidden="true" class="ic-appli">
    <path d="M9.4 3h5.2l1.6 5.2H7.8L9.4 3Z" fill="#e8e8ea"/>
    <path d="M7.4 9.6h9.2L20 20a1.4 1.4 0 0 1-1.3 1.9H5.3A1.4 1.4 0 0 1 4 20l3.4-10.4Z" fill="#f28100"/>
  </svg>`;

async function ouvrirMapping(saison, episode) {
  const voile = $('voile-mapping');
  if (!voile || !oeuvre) return;

  const film = estUnFilm(oeuvre);
  mapCible = {
    saison,
    episode,
    film,
    type: film ? 'movie' : 'series',
    id: film ? oeuvre.id : `${oeuvre.id}:${saison}:${episode}`,
  };
  mapLiens = [];
  mapOnglet = 'addons';
  mapFiltreQualite = 'tout';
  mapFiltreAddon = 'tout';
  mapChoixApercu = null;

  texte('map-titre', film ? oeuvre.titre : `${oeuvre.titre} — S${saison}E${episode}`);
  voile.hidden = false;
  peindreMapping();
  await chargerLiensDeLEpisode();
}

async function chargerLiensDeLEpisode() {
  texte('map-etat', 'recherche des liens chez les add-ons…');
  peindreMapping();
  try {
    const [flux, mapping] = await Promise.all([
      api(`/api/streams/${mapCible.type}/${encodeURIComponent(mapCible.id)}`),
      // Le mapping ne sert qu'à savoir lequel est épinglé : la liste, elle,
      // vient de la recherche en direct.
      api(`/api/admin/mapping/${encodeURIComponent(oeuvre.id)}/${mapCible.saison}/${mapCible.episode}`)
        .catch(() => ({ epingle: null })),
    ]);
    const nomEpingle = mapping?.epingle?.filename ?? null;
    mapLiens = (flux?.streams ?? []).map((s) => {
      const complet = `${s.filename ?? ''} ${s.name ?? ''} ${s.description ?? ''}`;
      return {
        ...s,
        qualite: qualiteDuNom(complet),
        poids: poidsDuTexte(s.description ?? s.name),
        // ⚠️ L'ÉPINGLE SE RECONNAÎT AU NOM DE FICHIER, pas à la référence :
        // celle-ci est fabriquée à chaque recherche pour que l'URL réelle ne
        // sorte jamais du serveur, et elle ne vaut donc que le temps d'une
        // page.
        epingle: Boolean(nomEpingle && s.filename && nomEpingle === s.filename),
      };
    });
    texte('map-etat', mapLiens.length
      ? `${mapLiens.length} lien(s) trouvé(s)`
      : "aucun lien : les add-ons n'ont rien pour cet épisode");
  } catch (err) {
    mapLiens = [];
    texte('map-etat', err.message);
  }
  peindreMapping();
}

function peindreMapping() {
  const liste = $('map-liste');
  if (!liste) return;

  // ⚠️ L'ETAT D'EXTRACTION SE LIT SUR L'ONGLET, PAS SEULEMENT DANS LA LIGNE.
  //
  // Toji : « ce que je voudrais, c'est que ça soit noté du côté de l'onglet
  // Aperçu, à droite, dans Aperçu et État d'extraction. » Le pourcentage
  // s'affichait sous la ligne du lien — donc invisible des qu'on remonte la
  // liste pour comparer autre chose, ce qu'on fait justement pendant les
  // trente secondes ou il tourne. Sur l'onglet, il reste sous les yeux ou que
  // l'on soit dans la pop-up.
  const onglets = [['addons', 'Add-ons'], ['apercu', 'Aperçu']];
  liste.innerHTML = `<div class="map-onglets">${onglets.map(([v, mot]) => `<button type="button"
      class="map-onglet${mapOnglet === v ? ' actif' : ''}" data-onglet="${esc(v)}">${esc(mot)}<span
      class="map-onglet-etat" data-etat-onglet="${esc(v)}"></span></button>`).join('')}
    </div><div id="map-corps"></div>`;
  majEtatExtraction();

  for (const b of liste.querySelectorAll('[data-onglet]')) {
    b.onclick = () => { mapOnglet = b.dataset.onglet; peindreMapping(); };
  }
  if (mapOnglet === 'addons') peindreOngletAddons();
  else peindreOngletApercu();
}

function liensFiltres() {
  return mapLiens.filter((l) => (mapFiltreQualite === 'tout' || l.qualite === mapFiltreQualite)
    && (mapFiltreAddon === 'tout' || l.source === mapFiltreAddon));
}

function peindreOngletAddons() {
  const corps = $('map-corps');
  if (!corps) return;

  // Les add-ons qui ont répondu, et les qualités réellement présentes : un
  // filtre qui ne peut rien rendre est un filtre qui ment.
  const addons = [...new Set(mapLiens.map((l) => l.source).filter(Boolean))];
  const qualites = ORDRE_QUALITE.filter((q) => mapLiens.some((l) => l.qualite === q));

  const filtres = `<div class="map-filtres">
      <div class="st-filtre">
        <button type="button" data-q="tout" aria-pressed="${mapFiltreQualite === 'tout'}">Toutes qualités</button>
        ${qualites.map((q) => `<button type="button" data-q="${esc(q)}" aria-pressed="${mapFiltreQualite === q}">${esc(q)}</button>`).join('')}
      </div>
      ${addons.length > 1 ? `<div class="st-filtre">
        <button type="button" data-a="tout" aria-pressed="${mapFiltreAddon === 'tout'}">Tous les add-ons</button>
        ${addons.map((a) => `<button type="button" data-a="${esc(a)}" aria-pressed="${mapFiltreAddon === a}">${esc(a)}</button>`).join('')}
      </div>` : ''}
    </div>`;

  const retenus = liensFiltres();
  // Groupés par add-on : c'est la présentation demandée, et c'est aussi la
  // question qu'on se pose devant un mauvais lien — lequel des trois l'a servi.
  const groupes = [...new Set(retenus.map((l) => l.source))].map((a) => ({
    nom: a,
    lignes: retenus.filter((l) => l.source === a),
  }));

  corps.innerHTML = filtres + (retenus.length
    ? groupes.map((g) => `<p class="map-groupe">${esc(g.nom)} · ${g.lignes.length}</p>`
      + g.lignes.map((l) => ligneDeLien(l)).join('')).join('')
    : '<p class="res-vide">Rien à ce filtre.</p>');

  for (const b of corps.querySelectorAll('[data-q]')) {
    b.onclick = () => { mapFiltreQualite = b.dataset.q; peindreMapping(); };
  }
  for (const b of corps.querySelectorAll('[data-a]')) {
    b.onclick = () => { mapFiltreAddon = b.dataset.a; peindreMapping(); };
  }
  brancherActionsDeLien(corps);
}

/**
 * La description d'un flux, debarrassee de ce qu'on affiche deja.
 *
 * AIOStreams rend plusieurs lignes, et la derniere REPETE le nom du fichier —
 * qui est deja le titre de la ligne. Sur quatre-vingt-treize liens, cela fait
 * quatre-vingt-treize repetitions qui poussent le suivant hors de l'ecran.
 */
function detailDuLien(l) {
  return String(l.description ?? '')
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x && x !== l.filename && x !== l.name)
    .join(' · ');
}

function ligneDeLien(l) {
  const pastilles = [
    l.qualite ? `<span class="map-pastille qualite">${esc(l.qualite)}</span>` : '',
    l.poids ? `<span class="map-pastille">${esc(poidsLisible(l.poids))}</span>` : '',
    l.epingle ? '<span class="map-pastille epingle">épinglé</span>' : '',
  ].filter(Boolean).join('');

  const nom = l.filename || l.name;
  return `<div class="map-lien${l.epingle ? ' choisi' : ''}">
    <span class="map-corps">
      <span class="map-nom" title="${esc(nom)}">${esc(nom)}</span>
      <span class="map-detail" title="${esc(detailDuLien(l))}">${esc(detailDuLien(l))}</span>
      <span class="map-pastilles">${pastilles}</span>
    </span>
    <span class="map-actions">
      <button type="button" class="ghost" data-epingle="${esc(l.ref)}">${l.epingle ? 'Retirer' : 'Épingler'}</button>
      <button type="button" class="ghost" data-apercu="${esc(l.ref)}">${ICONE_APERCU}</button>
      <!-- Deux facons d'aller verifier le fichier ailleurs. En ICONES : sur une
           ligne qui porte deja un nom de release de quatre-vingts caracteres,
           deux mots de plus repoussent le sujet hors de l'ecran. Le nom reste
           dans l'infobulle et dans l'etiquette d'accessibilite. -->
      <button type="button" class="ghost mince" data-vlc="${esc(l.ref)}"
              title="Ouvrir ce fichier dans VLC" aria-label="Ouvrir dans VLC">${ICONE_VLC}</button>
    </span>
  </div>`;
}

function brancherActionsDeLien(zone) {
  for (const b of zone.querySelectorAll('[data-epingle]')) {
    b.onclick = async () => {
      const l = mapLiens.find((x) => x.ref === b.dataset.epingle);
      if (!l) return;
      b.disabled = true;
      texte('map-etat', 'enregistrement…');
      const chemin = `/api/admin/mapping/${encodeURIComponent(oeuvre.id)}/${mapCible.saison}/${mapCible.episode}`;
      try {
        if (l.epingle) await api(chemin, { method: 'DELETE' });
        else {
          // La référence part avec la demande : c'est le SERVEUR qui retrouve
          // l'URL réelle derrière elle, et qui grave celle-là.
          await api(chemin, {
            method: 'POST',
            body: JSON.stringify({ ref: l.ref, lien: { nom: l.name, filename: l.filename, source: l.source } }),
          });
        }
        await chargerLiensDeLEpisode();
      } catch (err) {
        b.disabled = false;
        texte('map-etat', err.message);
      }
    };
  }
  // ⚠️ « APERCU » NE CHANGE PLUS SEULEMENT D'ONGLET, IL LANCE LE TRAVAIL.
  //
  // Toji : « quand on clique sur Aperçu, ça met une validation comme quoi ça va
  // charger, et ça prépare l'aperçu directement de l'épisode en question ».
  // Basculer d'onglet puis demander de recliquer, c'etait deux gestes pour une
  // seule intention — et le second n'etait pas evident, puisque le lien etait
  // deja choisi.
  // ⚠️ UN SEUL GESTE, ET ON NE BOUGE PAS.
  //
  // Toji : « le bouton de validation, c'est direct : je choisis 1080, je
  // choisis un épisode, et ça reste dans le même menu. Il n'y a pas Valider ni
  // Générer : directement, ça valide et ça déclenche la génération. »
  //
  // J'avais mis une confirmation, puis une bascule d'onglet. Les deux partent :
  // on est venu comparer des liens, et perdre la liste — ou repondre a une
  // question — pour voir une image, c'est sortir du travail en cours. La
  // planche s'ouvre SOUS la ligne du lien, la ou on l'a demandee.
  // ⚠️ RIEN NE S'INSERE ENTRE LES LIENS. JAMAIS.
  //
  // Toji : « la petite zone, en fait, entre les deux liens, j'en veux pas ça.
  // Par contre, dans l'aperçu, on affiche l'image, et quand on clique dessus,
  // ça l'affiche en grand. »
  //
  // Un bloc glisse au milieu de la liste pousse tous les liens suivants vers le
  // bas — pendant qu'on est justement en train de les comparer. L'image a un
  // endroit a elle : l'onglet Apercu. Le bouton lance le travail, l'onglet
  // porte l'avancement, et l'image s'y affiche quand elle est prete.
  for (const b of zone.querySelectorAll('[data-apercu]')) {
    b.onclick = async () => {
      const l = mapLiens.find((x) => x.ref === b.dataset.apercu);
      if (!l) return;
      mapChoixApercu = l.ref;

      // Le bouton repond tout de suite : l'extraction met des dizaines de
      // secondes, et sans accuse de reception on reclique.
      b.disabled = true;
      b.classList.add('valide');
      b.innerHTML = '✓ Validé';

      mapExtraction = { nom: l.filename || l.name, pourcent: 0 };
      majEtatExtraction();
      const suivi = setInterval(async () => {
        try {
          const a = await api(`/api/admin/apercu-lien/avancee?ref=${encodeURIComponent(l.ref)}`);
          if (!Number.isFinite(a?.pourcent)) return;
          // On ne repeint pas le menu pour changer un nombre : la liste serait
          // reconstruite toutes les secondes et demie sous les yeux.
          mapExtraction = { ...mapExtraction, pourcent: a.pourcent };
          majEtatExtraction();
        } catch { /* la reponse finale tranchera */ }
      }, 1500);

      try {
        // L'episode voyage avec la demande : la planche sert au lecteur, pas
        // seulement a cet ecran.
        await api('/api/admin/apercu-lien', {
          method: 'POST',
          body: JSON.stringify({ ref: l.ref, episode: mapCible.id }),
        });
        // La planche est prete : l'onglet Apercu la montre. On y bascule, c'est
        // ce qu'on attendait en cliquant.
        clearInterval(suivi);
        mapExtraction = null;
        mapOnglet = 'apercu';
        peindreMapping();
      } catch (err) {
        clearInterval(suivi);
        mapExtraction = null;
        majEtatExtraction();
        texte('map-etat', err.message);
      }
      b.classList.remove('valide');
      b.innerHTML = ICONE_APERCU;
      b.disabled = false;
    };
  }

  for (const b of zone.querySelectorAll('[data-vlc]')) {
    b.onclick = () => {
      // ⚠️ UN FICHIER `.m3u`, PAS LE SCHEMA `vlc://`.
      //
      // Le schema demande que VLC se soit enregistre aupres du systeme, ce
      // qu'il ne fait pas toujours — et quand il ne le fait pas, le navigateur
      // ne dit RIEN : le bouton parait mort. Une liste de lecture se telecharge,
      // s'ouvre avec le lecteur associe, et se retrouve dans les
      // telechargements si rien ne l'ouvre.
      const a = document.createElement('a');
      a.href = base(`/api/admin/lien/${encodeURIComponent(b.dataset.vlc)}.m3u`);
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // ⚠️ LE NAVIGATEUR NE PEUT PAS LANCER VLC A NOTRE PLACE.
      //
      // Toji : « ce qui serait bien, c'est que ça fasse play, un peu comme
      // l'équivalent du play in VLC ». C'est exactement ce que porte ce
      // fichier — mais le dernier geste appartient au navigateur, qui refuse
      // par principe de démarrer une application. En cochant « Toujours ouvrir
      // ce type de fichier » sur le téléchargement, le clic suivant ouvrira VLC
      // tout seul, et ce sera un vrai « play ». On le dit une fois, plutôt que
      // de laisser chercher.
      texte('map-etat', 'liste de lecture téléchargée — ouvre-la et VLC lit ce fichier. '
        + 'Coche « Toujours ouvrir ce type de fichier » dans les téléchargements : '
        + 'les suivantes partiront directement dans VLC.');
    };
  }

}

// ⚠️ CET ONGLET N'EST QU'UN AFFICHEUR.
//
// Toji : « le choix, comme ça, tu me l'enlèves du menu dropdown … Générer
// aussi, c'est vraiment juste pour l'afficher. »
//
// Il portait un menu de liens et un bouton « Générer » — deux façons de plus de
// lancer le même travail, alors qu'on le lance déjà depuis la ligne du lien,
// là où l'on sait de quel fichier on parle. Ici on vient VOIR, et rien d'autre.
function peindreOngletApercu() {
  const corps = $('map-corps');
  if (!corps) return;
  corps.innerHTML = '<p class="map-detail" id="map-apercu-etat">lecture de l’aperçu…</p>'
    + '<div id="map-apercu-image"></div>';
  montrerApercuDeLEpisode();
}

/**
 * Montre une image en grand, par-dessus tout le reste.
 *
 * Une planche fait plusieurs milliers de pixels de large : la voir a la taille
 * de la pop-up ne sert a rien, et c'est pourtant pour la REGARDER qu'on
 * l'extrait. Elle s'affiche donc a sa taille, deplacable, et se referme d'un
 * clic ou d'un Echap — sans quitter l'ecran ou l'on travaille.
 */
function agrandirImage(adresse) {
  const voile = document.createElement('div');
  voile.className = 'visionneuse';
  voile.innerHTML = `<img src="${esc(adresse)}" alt="" />`;
  const fermer = () => { voile.remove(); document.removeEventListener('keydown', surTouche); };
  const surTouche = (e) => { if (e.key === 'Escape') fermer(); };
  voile.onclick = fermer;
  document.addEventListener('keydown', surTouche);
  document.body.appendChild(voile);
}

async function montrerApercuDeLEpisode() {
  const zone = $('map-apercu-image');
  if (!zone || !mapCible) return;
  try {
    const r = await api(`/api/admin/apercu-episode?ep=${encodeURIComponent(mapCible.id)}`);
    if (!r?.existe) {
      texte('map-apercu-etat', 'Aucun aperçu pour cet épisode. '
        + 'Va dans « Add-ons » et clique « Aperçu » sur le lien de ton choix : la planche sera extraite de CE fichier.');
      return;
    }
    const adresse = base(`/api/admin/apercu-lien/${r.cle}.webp`);
    texte('map-apercu-etat', `planche de l’épisode — ${r.colonnes ?? '?'} × ${r.lignes ?? '?'} vignettes · clique pour l’agrandir`);
    // ⚠️ PAS DE NOUVEL ONGLET.
    //
    // Toji : « quand c'est en grand, ça l'ouvre pas dans une nouvelle fenêtre.
    // Ça le met vraiment juste en grand. » Un lien `target="_blank"` sort de
    // l'application pour montrer une image : on perd la pop-up, la liste, et
    // l'onglet ouvert reste derriere. L'agrandissement se fait ICI.
    zone.innerHTML = `<img src="${esc(adresse)}" alt="Aperçu de l’épisode" data-agrandir="${esc(adresse)}" />
      <p class="map-detail"><a href="${esc(adresse)}" download="apercu-${esc(r.cle)}.webp">télécharger l’image</a></p>`;
    zone.querySelector('[data-agrandir]').onclick = () => agrandirImage(adresse);
  } catch (err) {
    texte('map-apercu-etat', err.message);
  }
}


function preparerMapping() {
  surClic('map-fermer', () => { const v = $('voile-mapping'); if (v) v.hidden = true; });
  $('voile-mapping')?.addEventListener('click', (e) => {
    // Cliquer le voile ferme ; cliquer la carte, non — sinon un clic sur un
    // bouton en bord de carte refermerait tout.
    if (e.target === $('voile-mapping')) $('voile-mapping').hidden = true;
  });
  surClic('oeu-mapping', basculerModeMapping);
}

function brancherEpisodes() {
  for (const el of $('s-episodes').querySelectorAll('.ep')) {
    // ⚠️ EN MODE REGLAGES, LE CLIC NE LANCE PLUS RIEN.
    //
    // Toji : « quand on clique sur un épisode, à la place d'ouvrir le lecteur,
    // ça ouvre les réglages de l'épisode ». C'est le meme geste, sur le meme
    // element : ce qui change, c'est ce qu'on est venu faire.
    el.onclick = clicEpisode(el);
  }
}

// Vue par arcs : la numerotation d'AIOMetadata reste la reference, on ne fait
// que la regrouper. Les episodes hors plage restent visibles en fin de liste.
// Menu des arcs, entièrement fait main : un <select> ne laisse styler ni sa
// liste ni son survol.
let surTailleMenuArcs = null;


// ---------------------------------------------------------------------------
// LE CHOIX DE LA SAISON : TROIS PRESENTATIONS, UN REGLAGE
//
// Toji : « on peut par exemple voir directement toutes les saisons en forme de
// card. Après il y aurait plusieurs types de présentations : tu peux reprendre
// ce qu'on a fait pour Bleach avec un menu dropdown, et on choisit l'arc. »
//
// Le menu déroulant existait déjà et reste le défaut : sur une œuvre à dix-huit
// arcs, il tient en une ligne là où les cartes prendraient un écran entier.
// Les cartes servent l'autre cas — trois ou quatre saisons, qu'on veut voir
// d'un coup d'œil, avec ce qu'elles racontent.
//
// ⚠️ LES DEUX PEIGNENT DANS LE MÊME ENDROIT ET APPELLENT LE MÊME `rendreArc`.
// C'est ce qui garde une seule grille d'épisodes, un seul branchement de clic,
// et ce qui fait qu'ajouter une présentation ne duplique pas la fiche.
// ---------------------------------------------------------------------------
function construireCartesArcs(arcs, actif) {
  const hote = document.querySelector('.choix-arc')
    || $('s-saison')?.parentElement
    || document.querySelector('.zone-tete');
  if (!hote) return;

  hote.innerHTML = `<div class="cartes-arcs">${arcs.map((a, i) => {
    const nb = a.episodes.length;
    // Le résumé de la saison quand la source en donne un ; à défaut celui de
    // son premier épisode, qui situe au moins où l'on en est. Rien plutôt
    // qu'une phrase inventée.
    const mot = a.resume || a.episodes[0]?.resume || '';
    return `<button type="button" class="carte-arc${i === actif ? ' actif' : ''}" data-arc="${i}">
      <span class="carte-arc-nom">${esc(a.nom)}</span>
      <span class="carte-arc-cpt">${nb} ${pluriel(nb, 'épisode')}</span>
      ${mot ? `<span class="carte-arc-mot">${esc(String(mot).slice(0, 160))}</span>` : ''}
    </button>`;
  }).join('')}</div>`;

  for (const b of hote.querySelectorAll('[data-arc]')) {
    b.onclick = () => {
      for (const autre of hote.querySelectorAll('[data-arc]')) autre.classList.remove('actif');
      b.classList.add('actif');
      retenirSaison(Number(b.dataset.arc));
      rendreArc(Number(b.dataset.arc));
    };
  }
}

/** La présentation choisie, ou celle qui convient à cette œuvre. */
function presenterLesArcs(arcs, actif) {
  const voulue = prefs.lire('presentation-saisons', 'menu');
  if (voulue === 'cartes') return construireCartesArcs(arcs, actif);
  return construireMenuArcs(arcs, actif);
}

/**
 * Où va le focus quand on tape une touche dans un menu déplié.
 *
 * ⚠️ DÉFAUT PAYÉ LE 14/09 : CHAQUE MENU AVAIT SON PROPRE CLAVIER, QUAND IL EN
 * AVAIT UN.
 *
 * Le menu du clic droit sur une œuvre (`ouvrirLeMenuDOeuvre`) est le seul qui
 * répondait aux flèches ; le menu des arcs, lui, s'annonçait `role="listbox"`
 * et ne bougeait pas d'une ligne — dix-huit tabulations pour atteindre le
 * dernier arc. La règle du déplacement est extraite ici, pure et éprouvable,
 * pour qu'il n'y ait plus qu'UNE réponse à « où va ↓ » dans toute
 * l'application.
 *
 * L'anneau est volontaire — c'est déjà la convention du menu d'œuvre : quatre
 * entrées se parcourent en rond, pas en butée. `null` veut dire « cette touche
 * ne nous regarde pas » : l'appelant la laisse alors filer.
 */
export function cibleDuClavierDeMenu(touche, { index = -1, total = 0 } = {}) {
  if (!(total > 0)) return null;
  if (touche === 'Home') return 0;
  if (touche === 'End') return total - 1;
  if (touche !== 'ArrowDown' && touche !== 'ArrowUp') return null;
  const pas = touche === 'ArrowDown' ? 1 : -1;
  // Rien de focalisé : ↓ prend la première entrée, ↑ la dernière.
  const depart = index < 0 ? (pas > 0 ? -1 : 0) : index;
  return (depart + pas + total) % total;
}

function construireMenuArcs(arcs, actif) {
  const hote =
    document.querySelector('.choix-arc') ||
    $('s-saison')?.parentElement ||
    document.querySelector('.zone-tete');
  if (!hote) {
    console.warn('[menu] aucun conteneur pour le menu des arcs');
    return;
  }

  // Reconstruction complète à chaque appel : le menu est un simple rendu de la
  // liste d'arcs, pas un objet à maintenir dans le temps.
  // Un arc découpé en parties n'occupe qu'une entrée : ses parties sont
  // proposées séparément, sinon le menu se remplit de doublons.
  const entrees = [];
  arcs.forEach((a, i) => {
    const precedent = entrees[entrees.length - 1];
    if (a.partie && precedent && precedent.nom === a.nom) {
      precedent.parties.push({ partie: a.partie, index: i });
      return;
    }
    entrees.push({ nom: a.nom, filler: a.filler, index: i, parties: a.partie ? [{ partie: a.partie, index: i }] : [] });
  });

  // ⚠️ `role="menu"` ET NON `role="listbox"` — défaut payé le 14/09.
  //
  // Le conteneur s'annonçait « liste de sélection » et ses entrées « option »,
  // sans rien de ce qu'une listbox exige : ni `aria-activedescendant`, ni
  // tabindex roulant, ni flèches. Un lecteur d'écran annonçait donc une liste
  // dont aucun élément n'était courant, et le clavier ne descendait pas d'une
  // ligne. Poser `role="option"` sur un `<button>` écrasait en prime sa
  // sémantique de bouton sans rien donner en échange.
  //
  // Le bon rôle ici est `menu` : chaque entrée DÉCLENCHE une action, elle ne
  // compose pas un formulaire. Il se pilote aux flèches avec le focus, ce que
  // ces entrées savent déjà recevoir — et `aria-current`, que le CSS style
  // (`.menu-arc .liste button[aria-current="true"]`), reste valable.
  hote.innerHTML = `
    <div class="menu-arc">
      <button type="button" id="menu-arc-bouton" aria-haspopup="menu" aria-expanded="false" aria-controls="menu-arc-liste">
        <span id="menu-arc-libelle"></span>
      </button>
      <div class="liste" id="menu-arc-liste" role="menu">
        ${entrees
          .map(
            (e) => `<button type="button" role="menuitem" data-i="${e.index}">
              <span>${esc(e.nom)}</span>
              ${e.filler ? '<span class="fil">filler</span>' : ''}
            </button>`,
          )
          .join('')}
      </div>
    </div>`;

  const menu = hote.querySelector('.menu-arc');
  const bouton = hote.querySelector('#menu-arc-bouton');
  const options = [...hote.querySelectorAll('[data-i]')];

  // ⚠️ DÉFAUT PAYÉ LE 14/09 : FERMER LE MENU PERDAIT LE FOCUS.
  //
  // `.menu-arc .liste` repasse en `display:none` dès que la classe `ouvert`
  // tombe. Un élément qui passe en `display:none` rend le focus au `<body>` :
  // on validait un arc au clavier, et la touche suivante n'allait plus nulle
  // part — il fallait retabuler depuis le haut de la page. `lecteur.js` le
  // documente et le corrige depuis longtemps, mais pour ses menus à lui.
  //
  // L'appartenance du focus se lit AVANT de retirer la classe : après, tout le
  // monde est déjà sur `body` et le test arriverait trop tard.
  const fermer = ({ rendreLeFocus = false } = {}) => {
    const dedans = menu.contains(document.activeElement);
    menu.classList.remove('ouvert');
    bouton.setAttribute('aria-expanded', 'false');
    // `preventScroll` : ramener le focus ne doit pas faire défiler la fiche
    // jusqu'au bouton — c'est déjà la convention maison ailleurs dans ce
    // fichier.
    if (rendreLeFocus || dedans) bouton.focus({ preventScroll: true });
  };

  const choisir = (i) => {
    texte('menu-arc-libelle', arcs[i]?.nom ?? 'Choisir un arc');
    const entree = entrees.find((e) => e.index === i || e.parties.some((p) => p.index === i));
    options.forEach((o) => o.setAttribute('aria-current', String(Number(o.dataset.i) === (entree?.index ?? i))));
    rendreParties(entree, i);
    // `choisir` ne sert qu'aux clics : c'est un choix, on le retient pour l'œuvre.
    retenirSaison(i);
    rendreArc(i);
  };

  // Les parties de l'arc courant, en boutons courts.
  function rendreParties(entree, actif) {
    const zone = $('parties');
    if (!zone) return;
    const parties = entree?.parties ?? [];
    zone.hidden = parties.length < 2;
    zone.innerHTML = parties
      .map(
        (p) => `<button type="button" data-partie="${p.index}"
          aria-current="${p.index === actif}">Part ${p.partie}</button>`,
      )
      .join('');
    for (const b of zone.querySelectorAll('[data-partie]')) {
      b.onclick = () => choisir(Number(b.dataset.partie));
    }
  }

  // Le clic sur le bouton bascule l'état. `stopPropagation` empêche le même
  // clic d'atteindre le document et de refermer aussitôt ce qu'il vient d'ouvrir.
  const liste = menu.querySelector('.liste');

  /**
   * La liste est ancrée au bouton par le CSS (`absolute` dans un `.menu-arc`
   * relatif) : il n'y a plus rien à calculer pour la placer, elle le suit.
   *
   * Ne reste que le choix du CÔTÉ. Ouvrir vers le bas est la norme ; on ne
   * bascule au-dessus que lorsqu'il n'y a franchement pas la place en dessous
   * et qu'il y en a plus au-dessus. La hauteur disponible se mesure, elle, à
   * chaque ouverture — une fenêtre redimensionnée entre deux clics change la
   * réponse.
   */
  function placer() {
    const b = rectPage(bouton);
    liste.style.width = `${Math.max(b.width, 320)}px`;

    const dispoBas = hauteurPage() - b.bottom - 16;
    if (dispoBas < 220 && b.top > dispoBas) {
      liste.style.top = 'auto';
      liste.style.bottom = 'calc(100% + 7px)';
      liste.style.maxHeight = `${Math.max(160, b.top - 24)}px`;
    } else {
      liste.style.bottom = 'auto';
      liste.style.top = 'calc(100% + 7px)';
      liste.style.maxHeight = `${Math.max(160, dispoBas)}px`;
    }
  }

  bouton.addEventListener('click', (e) => {
    e.stopPropagation();
    const ouvert = menu.classList.toggle('ouvert');
    bouton.setAttribute('aria-expanded', String(ouvert));
    if (!ouvert) return;
    placer();
    // À l'ouverture, le focus va sur l'arc courant : c'est de là qu'on se
    // déplace, et c'est ce qu'annonce un lecteur d'écran. Sans ça, ↓ repartait
    // de la première entrée quel que soit l'arc affiché.
    (options.find((o) => o.getAttribute('aria-current') === 'true') ?? options[0])
      ?.focus({ preventScroll: true });
  });

  // ⚠️ LE CLAVIER DU MENU — il n'y en avait aucun (défaut payé le 14/09).
  //
  // Sur la liste et non sur le document : le rayon d'action est borné au menu
  // déplié. `stopPropagation` est indispensable — sans lui, Échap traverserait
  // jusqu'aux raccourcis du lecteur et sortirait du plein écran en même temps
  // qu'il ferme le menu, et les flèches iraient régler le volume.
  liste.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      fermer({ rendreLeFocus: true });
      return;
    }
    const cible = cibleDuClavierDeMenu(e.key, {
      index: options.indexOf(document.activeElement),
      total: options.length,
    });
    if (cible === null) return;
    e.preventDefault();
    e.stopPropagation();
    options[cible]?.focus({ preventScroll: true });
  });

  // ⚠️ UN SEUL ÉCOUTEUR, PAS UN PAR OUVERTURE DE FICHE.
  //
  // `construireMenuArcs` est appelée à chaque ouverture d'œuvre. Cette ligne
  // posait donc un écouteur global de plus à chaque fois, et chacun retenait
  // SON menu — un nœud désormais détaché du document. Vingt fiches ouvertes
  // dans la soirée, vingt écouteurs qui se déclenchent au moindre changement de
  // taille, et vingt arbres DOM que le ramasse-miettes ne peut plus reprendre.
  if (surTailleMenuArcs) removeEventListener('resize', surTailleMenuArcs);
  surTailleMenuArcs = () => { if (menu.classList.contains('ouvert')) placer(); };
  addEventListener('resize', surTailleMenuArcs);

  for (const o of options) {
    o.addEventListener('click', (e) => {
      e.stopPropagation();
      // `rendreLeFocus` explicite : tous les navigateurs ne focalisent pas un
      // `<button>` au clic (Safari ne le fait pas), donc le test « le focus
      // était dedans » ne suffirait pas à le ramener.
      fermer({ rendreLeFocus: true });
      choisir(Number(o.dataset.i));
    });
  }

  // Un seul écouteur pour tout le document, quel que soit le nombre de
  // reconstructions du menu.
  if (!window.__menuArcGlobal) {
    window.__menuArcGlobal = true;
    // Le focus se rend AVANT que la classe tombe, sinon `display:none` l'a déjà
    // renvoyé au `<body>` et il n'y a plus rien à constater. Le clic, lui, ne
    // rend rien : il a emmené le focus ailleurs de lui-même.
    const refermerTout = (rendreLeFocus) => {
      document.querySelectorAll('.menu-arc.ouvert').forEach((n) => {
        const dedans = n.contains(document.activeElement);
        n.classList.remove('ouvert');
        const b = n.querySelector('button');
        b?.setAttribute('aria-expanded', 'false');
        if (rendreLeFocus && dedans) b?.focus({ preventScroll: true });
      });
    };
    document.addEventListener('click', () => refermerTout(false));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      refermerTout(true);
    });
  }

  choisirInitial(actif);
  function choisirInitial(i) {
    texte('menu-arc-libelle', arcs[i]?.nom ?? 'Choisir un arc');
    const entree = entrees.find((e) => e.index === i || e.parties.some((p) => p.index === i));
    options.forEach((o) => o.setAttribute('aria-current', String(Number(o.dataset.i) === (entree?.index ?? i))));
    rendreParties(entree, i);
  }
}

// Densité de la grille : le réglage fixe directement le nombre de colonnes,
// au lieu de laisser une largeur minimale en décider.
function appliquerParLigne() {
  const n = Number(prefs.lire('par-ligne', '9')) || 9;
  for (const id of ['s-episodes', 'ep-grille']) {
    const g = $(id);
    if (g) g.style.gridTemplateColumns = `repeat(${n}, minmax(0, 1fr))`;
  }
  texte('o-par-ligne', String(n));
  const c = $('r-par-ligne');
  if (c && c.value !== String(n)) c.value = String(n);
}

// Affiche un arc et un seul, avec son intitulé à côté du menu.
let arcAffiche = null;

function rendreArc(index) {
  const arc = (serie.arcs ?? [])[index];
  if (!arc) return;
  arcAffiche = index;

  // Le menu affiche déjà l'intitulé et le nombre d'épisodes se lit à l'écran :
  // rien à répéter à côté.
  texte('s-arc-courant', '');
  texte('s-cpt', arc.filler ? 'filler' : '');

  // ⚠️ `oeuvre` ET NON `serie` DANS L'ADRESSE. Le fragment sert à retrouver
  // l'écran exact au rechargement ; tant qu'il n'y avait qu'une série, la clé
  // n'avait pas d'importance. Maintenant qu'il y en a plusieurs, `#serie=…`
  // désignerait l'ancienne notion — et `peindreFicheOeuvre`, qui relit ce
  // fragment pour retrouver l'arc, ne s'y reconnaîtrait pas : on repartirait de
  // l'arc 0 à chaque rechargement.
  marquerPosition({ oeuvre: serie.id, arc: String(index) });
  appliquerParLigne();
  // La grille peut ne pas être montée quand la fiche est encore en train de se
  // construire : on ne peint pas dans le vide, et surtout on n'y lève pas.
  const grille = $('s-episodes');
  if (!grille) return;
  grille.innerHTML = arc.episodes.map(vignetteEpisode).join('');
  // ⚠️ TROIS ENDROITS POSENT LE CLIC D'UN EPISODE, ET C'EST CELUI-CI QUI SERT.
  //
  // `brancherEpisodes` et la grille des arcs le posaient aussi — j'avais
  // corrige ces deux-la et pas celui-ci, si bien qu'en mode reglages le clic
  // lancait toujours le lecteur sur la fiche d'une oeuvre : exactement l'ecran
  // ou l'on venait de demander les reglages. Le geste passe donc par une
  // fonction unique, `clicEpisode`, plutot que par trois copies d'une meme
  // ligne — la quatrieme aurait redit la meme erreur.
  for (const el of grille.querySelectorAll('.prev')) el.onclick = clicEpisode(el);
}

/**
 * Ce qu'un clic sur un episode declenche — le lecteur, ou ses reglages.
 *
 * Toji : « quand on clique sur un épisode, à la place d'ouvrir le lecteur, ça
 * ouvre les réglages de l'épisode ». C'est le meme geste, sur le meme element :
 * ce qui change, c'est ce qu'on est venu faire.
 */
const clicEpisode = (el) => () => (modeMapping
  ? ouvrirMapping(Number(el.dataset.s), Number(el.dataset.e))
  : lancer(Number(el.dataset.s), Number(el.dataset.e)));

function rendreArcs() {
  $('s-episodes').innerHTML = serie.arcs
    .map(
      (a) => `<div class="arc-tete">
          <span class="nom">${esc(a.nom)}</span>
          ${a.filler ? '<span class="hs filler">Filler</span>' : ''}
          ${a.debut ? `<span class="plage">${a.saison ? `S${a.saison} · ` : ''}${a.debut}–${a.fin}</span>` : ''}
        </div>` + a.episodes.map(ligneEpisode).join(''),
    )
    .join('');
  brancherEpisodes();
}

function rendreEpisodes(saison) {
  if (saison === 'arcs') return rendreArcs();
  const liste = serie.videos.filter((v) => v.season === saison).sort((a, b) => a.episode - b.episode);
  $('s-episodes').innerHTML = liste
    .map(
      ligneEpisode,
    )
    .join('');
  brancherEpisodes();
}

// Grille d'arcs : chaque carte porte son visuel — celui déposé s'il existe,
// la vignette du premier épisode sinon.
function rendreGrilleArcs() {
  const cible = $('arcs-grille');
  if (!cible) return;
  if (!serie.arcs || !serie.arcs.length) { cible.innerHTML = ''; return; }

  cible.innerHTML = serie.arcs
    .map(
      (a, i) => `<button class="arc-carte" type="button" data-arc="${i}">
        <span class="vis">${a.vignette ? `<img src="${esc(afficheDeCarte(a.vignette, densiteEcran()))}" alt="" decoding="async" loading="lazy" />` : ''}</span>
        <span class="bas">
          <span class="an">${esc(a.nom)}</span>
          <span class="info">
            <span>${a.total} ép.</span>
            ${a.filler ? '<span class="fil">Filler</span>' : ''}
          </span>
        </span>
      </button>`,
    )
    .join('');

  for (const el of cible.querySelectorAll('.arc-carte')) {
    el.onclick = () => ouvrirArc(Number(el.dataset.arc));
  }
}

// Grille dense des épisodes d'un arc : la vignette porte le numéro dans son
// encoche, jamais par-dessus l'image.
function ouvrirArc(index) {
  const arc = serie.arcs[index];
  if (!arc) return;

  texte('ep-arc', arc.nom);
  texte('ep-cpt', `${arc.total} épisodes${arc.filler ? ' · filler' : ''}`);
  $('ep-grille').innerHTML = arc.episodes.map(vignetteEpisode).join('');

  for (const el of $('ep-grille').querySelectorAll('.prev')) {
    el.onclick = clicEpisode(el);
  }
  vue('v-episodes');
}

/**
 * Bloc « Compte » : qui est connecté, et de quoi se déconnecter.
 *
 * Il n'apparaît que HORS de Discord. Dans l'activité, la session vient du SDK
 * et serait refaite dans la seconde : un bouton qui ne fait rien vaut moins
 * que pas de bouton du tout.
 */
/**
 * Sortie vers le navigateur, où le plein écran existe vraiment.
 *
 * Mesuré dans l'activité en plein écran : la fenêtre fait 1919 x 839 et le
 * lecteur l'occupe entièrement. Les 241 px qui manquent pour atteindre 1080
 * sont l'interface de Discord — en-tête du client, barre des commandes
 * vocales. Aucun code de notre côté ne peut les reprendre.
 *
 * Le bouton n'apparaît donc que dans l'activité : ailleurs, on y est déjà.
 */
/**
 * Détache le lecteur dans une fenêtre à part.
 *
 * L'activité vit dans une iframe dont Discord fixe la taille — 1919 x 839
 * mesurés, et rien ne peut les augmenter. Une fenêtre détachée, elle,
 * appartient à l'utilisateur : il l'agrandit, la met en plein écran, la pose
 * sur son second écran.
 *
 * On y déplace le lecteur ENTIER. Le PiP classique ne détache que l'image, ce
 * qui ferait perdre les commandes et les sous-titres — ils sont peints
 * par-dessus la vidéo, pas incrustés dedans. Il sert de repli quand le
 * détachement de document manque.
 */
let fenetreDetachee = null;

// Un onglet a-t-il été ouvert depuis cette activité ? Le voyant de la séance
// met un instant à s'allumer, et pendant cet instant on rouvrirait un
// deuxième onglet. Ce drapeau comble le trou ; la séance le corrige ensuite.
let ongletOuvert = false;

// L'instance de séance, quand elle vient d'un billet plutôt que de l'adresse.
let instanceDeSeance = null;

async function detacherLecteur() {
  const lecteurNode = $('lecteur');
  if (!lecteurNode) return;

  // Déjà détaché : le bouton referme.
  if (fenetreDetachee && !fenetreDetachee.closed) {
    fenetreDetachee.close();
    return;
  }
  if (document.pictureInPictureElement) {
    await document.exitPictureInPicture().catch(() => {});
    return;
  }

  // 1. La fenêtre complète, avec commandes et sous-titres. Interdite depuis
  //    une iframe par la spécification — donc toujours refusée dans une
  //    activité — mais tentée quand même : le lecteur tourne aussi dans un
  //    navigateur, où elle fonctionne.
  if ('documentPictureInPicture' in window) {
    try {
      const fenetre = await documentPictureInPicture.requestWindow({ width: 1280, height: 720 });
      fenetreDetachee = fenetre;

      for (const feuille of document.styleSheets) {
        try {
          const style = fenetre.document.createElement('style');
          style.textContent = [...feuille.cssRules].map((r) => r.cssText).join('\n');
          fenetre.document.head.appendChild(style);
        } catch {
          if (!feuille.href) continue;
          const lien = fenetre.document.createElement('link');
          lien.rel = 'stylesheet';
          lien.href = feuille.href;
          fenetre.document.head.appendChild(lien);
        }
      }
      fenetre.document.body.classList.add('plein', 'detache');

      const place = document.createComment('lecteur détaché');
      lecteurNode.replaceWith(place);
      fenetre.document.body.appendChild(lecteurNode);
      tracer('detacher', 'fenetre complete', { taille: [fenetre.innerWidth, fenetre.innerHeight] });

      fenetre.addEventListener('pagehide', () => {
        place.replaceWith(lecteurNode);
        fenetreDetachee = null;
        tracer('detacher', 'fenetre refermee');
      }, { once: true });
      return;
    } catch (err) {
      // L'API existe mais refuse : c'est le cas dans l'activité. On ne s'arrête
      // pas là, il reste l'image seule.
      fenetreDetachee = null;
      tracer('detacher', 'fenetre complete refusee', err.message);
    }
  }

  // 2. L'image seule. On y perd les commandes et les sous-titres — ils sont
  //    peints par-dessus la vidéo, pas incrustés dedans — mais la fenêtre,
  //    elle, sort de l'iframe et s'agrandit à volonté.
  const v = $('video');
  if (v && document.pictureInPictureEnabled) {
    try {
      await v.requestPictureInPicture();
      tracer('detacher', 'image seule');
      lecteur().signaler('Image détachée — les sous-titres restent ici.');
      return;
    } catch (err) {
      tracer('detacher', 'image seule refusee', err.message);
    }
  }

  // 3. Rien n'est permis : on le dit, et on montre la sortie qui reste.
  tracer('detacher', 'aucun mode disponible', {
    documentPiP: 'documentPictureInPicture' in window,
    videoPiP: document.pictureInPictureEnabled,
  });
  lecteur().signaler('Détachement refusé ici — ouvre le lecteur dans le navigateur.');
}

function preparerNavigateur() {
  const zone = $('zone-navigateur');
  if (!zone) return;
  zone.hidden = !DANS_DISCORD;
  if (!DANS_DISCORD) return;

  const h = window.screen?.height ?? 0;
  texte('aide-navigateur', h
    ? `Discord garde ${Math.max(0, h - innerHeight)} px de haut pour son interface. Dans le navigateur, le lecteur prend l'écran entier.`
    : "Dans le navigateur, le lecteur prend l'écran entier.");

  surClic('ouvrir-navigateur', async () => {
    // L'épisode en cours part avec le lien : rouvrir sur l'accueil obligerait
    // à tout refaire.
    //
    // ⚠️ DEUX DÉFAUTS PAYÉS LE 14/09 SUR CETTE SEULE EXPRESSION.
    //
    // 1. L'HÔTE ÉTAIT INVENTÉ. Dans l'iframe, `location.host` vaut
    //    `<app-id>.discordsays.com` ; retirer le suffixe laissait l'identifiant
    //    d'application NU — `https://1461…/`, un nombre pour nom d'hôte.
    //    Discord affichait sa confirmation, et rien ne s'ouvrait. L'origine
    //    réelle est pourtant déjà dans la page : le serveur l'y injecte
    //    (`ORIGINE_LECTEUR`), précisément parce que « la page ne peut pas
    //    deviner seule son adresse publique ».
    // 2. LA CLÉ LUE N'EXISTE PLUS. `marquerPosition` n'écrit que `oeuvre` ;
    //    `memo.serie` est l'ANCIENNE clé du fragment et valait donc toujours
    //    `undefined` — la branche était morte, et le lien rendait l'accueil.
    //    Même repli qu'au démarrage : `memo.oeuvre || memo.serie`, pour l'onglet
    //    resté ouvert avant le chantier.
    const memo = positionMemorisee();
    const id = memo.oeuvre || memo.serie;
    const racine = ORIGINE_LECTEUR.replace(/\/$/, '');
    const adresse = racine + (id ? `/?id=${encodeURIComponent(id)}` : '/');

    // Une instance sans `LECTEUR_ORIGIN` retombe sur `location.origin`, donc
    // sur l'hôte de l'iframe : on le DIT plutôt que d'envoyer une adresse dont
    // on sait déjà qu'elle n'ouvrira rien.
    // Test sur la CHAÎNE, pas via `new URL` : une valeur mal formée ferait
    // lever le constructeur, et un bouton d'aide n'a pas à pouvoir planter.
    if (/^[a-z]+:\/\/[^/?#]*\.discordsays\.com(?::\d+)?$/i.test(racine)) {
      tracer('navigateur', 'origine publique non configuree', racine);
      texte('aide-navigateur', "L'adresse publique du lecteur n'est pas configurée sur ce serveur.");
      return;
    }

    try {
      tracer('navigateur', 'ouverture demandee', adresse);
      await sdk.commands.openExternalLink({ url: adresse });
    } catch (err) {
      texte('aide-navigateur', `Discord a refusé l'ouverture : ${err.message}`);
    }
  });
}

function preparerCompte() {
  const zone = $('zone-compte');
  if (!zone) return;
  zone.hidden = DANS_DISCORD;
  if (DANS_DISCORD) return;

  texte('compte-qui', moi?.pseudo ? `Connecté en tant que ${moi.pseudo}` : 'Connecté');

  surClic('deconnexion', async () => {
    texte('compte-qui', 'déconnexion…');
    try {
      await fetch(base('/api/auth/logout'), { method: 'POST', credentials: 'same-origin' });
    } catch { /* le cookie sera de toute façon abandonné au rechargement */ }
    // Le profil retenu part avec la session : sans cela, le pseudo et l'avatar
    // du précédent s'afficheraient à la connexion suivante — c'est justement
    // parce qu'ils sont mémorisés qu'il faut penser à les effacer.
    try { localStorage.removeItem(CLE_PROFIL); } catch { /* stockage refusé */ }
    location.reload();
  });
}

// Un groupe de boutons plutôt qu'un menu déroulant : en dessous de quatre
// choix, le menu cache une information qui tenait sur une ligne et coûte deux
// gestes au lieu d'un.
// ---------------------------------------------------------------------------
// Ce que les rôles disent, le lecteur le fait
//
// Quelqu'un qui a cliqué sur « version japonaise » et « sous-titres français »
// en arrivant sur le serveur ne devrait pas avoir à le redire ici. Ses rôles
// règlent donc le lecteur pour lui, dès l'ouverture.
//
// MAIS JAMAIS PAR-DESSUS UN CHOIX EXPLICITE. Le jour où quelqu'un ouvre les
// réglages et met la VF à la main, c'est sa décision qui vaut — sinon le
// réglage reviendrait à son état d'origine à chaque ouverture, sans qu'on
// comprenne pourquoi. Une marque distingue « réglé par les rôles » de « réglé
// par la personne ».
// ---------------------------------------------------------------------------
const MARQUE_MAIN = 'regle-a-la-main';

function appliquerGout(gout) {
  if (!gout) return;

  // Ce que la personne a réglé elle-même l'emporte, toujours.
  const aLaMain = new Set(prefs.lire(MARQUE_MAIN, '').split(',').filter(Boolean));

  // ⚠️ `prefs.poser`, PAS `prefs.ecrire` : ces valeurs ne doivent jamais
  // remonter au compte. Elles se recalculent à chaque connexion, sur chaque
  // appareil, à partir des rôles Discord du moment. Les enregistrer figerait
  // le rôle d'aujourd'hui : le jour où quelqu'un passe de VF à VO, le compte
  // lui rendrait la VF que son ancien rôle avait posée, et le changement de
  // rôle n'aurait plus aucun effet visible.
  const poser = (cle, valeur) => {
    if (!valeur || aLaMain.has(cle)) return;
    if (prefs.lire(cle, null) === valeur) return;
    prefs.poser(cle, valeur);
    tracer('gout', `${cle} → ${valeur} (d'après les rôles)`);
  };

  // La traduction « un goût → des réglages » vivait ici, en dur : la table des
  // ordres de langues et le fait qu'une version japonaise s'écrive `vostfr`
  // dans les réglages alors qu'elle s'appelle `vo` côté rôles. Elle est
  // remontée dans `gouts.js`, qui définit déjà ce vocabulaire, parce qu'un
  // second lecteur en a besoin : le stockage des réglages sur le compte doit
  // savoir quelles valeurs il a le droit d'accepter pour ces trois clés. Deux
  // copies de cette table auraient fini par diverger, et le symptôme aurait
  // été un réglage refusé sans explication.
  for (const [cle, valeur] of Object.entries(reglagesDuGout(gout))) poser(cle, valeur);
}

/**
 * Repeint tout ce qu'un réglage venu du compte peut changer.
 *
 * L'écran a déjà été peint avec le cache local, sans attendre le réseau ;
 * cette passe corrige ce qui diffère. Sans elle, les réglages du compte
 * seraient bien enregistrés mais ne se verraient qu'au rechargement suivant —
 * et la fonctionnalité passerait pour cassée alors qu'elle marche.
 */
function repeindreLesReglages() {
  appliquerSousTitres();
  appliquerCote();
  appliquerAncrage();
  appliquerParLigne();
  appliquerBlason(lireBlason(prefs));
  appliquerHauteurBandeau(prefs.lire('sc-bandeau-h', '125'));
  rafraichirProfil();
}

/** À appeler quand la personne règle elle-même : son choix devient définitif. */
function marquerRegleALaMain(cle) {
  const deja = new Set(prefs.lire(MARQUE_MAIN, '').split(',').filter(Boolean));
  if (deja.has(cle)) return;
  deja.add(cle);
  prefs.ecrire(MARQUE_MAIN, [...deja].join(','));
}

/**
 * Un groupe de pastilles ou PLUSIEURS reponses se cumulent, dans l'ordre ou on
 * les a choisies.
 *
 * C'est le meme geste que dans la barre de la grille, et pour la meme raison :
 * un menu deroulant dit « une seule reponse », alors qu'on veut dire « celui-ci,
 * puis celui-la ». Recliquer retire, ce qui remet le critere suivant en tete.
 */
// ---------------------------------------------------------------------------
// LES LANGUES DE SOUS-TITRES : CE QU'ON GARDE, ET TOUT LE RESTE
//
// ⚠️ LE REGLAGE EXISTAIT, SON INTERFACE N'A JAMAIS ETE ECRITE.
//
// `st-langues` etait lu partout — pour choisir la piste, pour dire au serveur
// quel fichier prendre — mais la zone qui devait le regler est restee VIDE dans
// la page. Personne ne pouvait donc changer l'ordre des langues autrement qu'en
// modifiant le stockage du navigateur a la main.
//
// Deux listes, parce qu'il y a deux questions : ce qu'on veut voir et dans quel
// ordre, puis tout le reste — d'un clic, sans avoir a chercher.
// ---------------------------------------------------------------------------
function monterLanguesDeSousTitres() {
  const retenues = $('st-langues');
  const autres = $('st-langues-autres');
  if (!retenues) return;

  const lire = () => {
    const cles = clesDepuisCodes(languesPreferees());
    return cles.length ? cles : [...LANGUES_PAR_DEFAUT];
  };

  const ecrire = (cles) => {
    // On enregistre des CODES, pas des langues : c'est ce que le reste du
    // lecteur lit deja, et ce qui voyage vers le serveur. La liste de langues
    // n'est qu'une facon de la composer.
    prefs.ecrire('st-langues', codesDesLangues(cles).join(', '));
  };

  const nomDe = (cle) => LANGUES_ST.find((l) => l.cle === cle)?.nom ?? cle;

  const peindre = () => {
    const choisies = lire();

    retenues.innerHTML = choisies.map((cle, i) => `<button type="button" class="actif" data-retirer="${esc(cle)}"
        aria-label="Retirer ${esc(nomDe(cle))} des langues affichées"><span class="rang">${i + 1}</span>${esc(nomDe(cle))} ✕</button>`).join('')
      || '<span class="aide">Aucune langue retenue : le menu du lecteur les montrera toutes.</span>';

    if (autres) {
      const reste = LANGUES_ST.filter((l) => !choisies.includes(l.cle));
      autres.innerHTML = reste.map((l) => `<button type="button" data-ajouter="${esc(l.cle)}"
          aria-label="Afficher aussi ${esc(l.nom)}">${esc(l.nom)} +</button>`).join('');
    }

    for (const b of retenues.querySelectorAll('[data-retirer]')) {
      b.onclick = () => {
        ecrire(lire().filter((c) => c !== b.dataset.retirer));
        peindre();
      };
    }
    for (const b of autres?.querySelectorAll('[data-ajouter]') ?? []) {
      b.onclick = () => {
        // A la fin : la langue qu'on vient d'ajouter est un secours, pas une
        // preference. Toji : « les anglais en ajout potentiel, mais en ajout
        // rapide » — un clic suffit, et l'ordre reste celui qu'on a voulu.
        ecrire([...lire(), b.dataset.ajouter]);
        peindre();
      };
    }
  };

  peindre();
}

function lierSegment(id, cle, defaut, apres) {
  const groupe = $(id);
  if (!groupe) return;
  const boutons = [...groupe.querySelectorAll('button[data-v]')];
  if (boutons.length === 0) return;

  const peindre = (v) => {
    for (const b of boutons) {
      const actif = b.dataset.v === v;
      b.classList.toggle('actif', actif);
      b.setAttribute('aria-pressed', actif ? 'true' : 'false');
    }
  };

  // Toucher un réglage, c'est décider : les rôles ne le remplaceront plus.
  const surChoixManuel = () => marquerRegleALaMain(cle);

  // Une valeur enregistrée qui ne correspond plus à aucun bouton — un choix
  // retiré depuis, comme le 480p — retombe sur le défaut au lieu de laisser le
  // groupe sans rien d'allumé.
  //
  // ⚠️ DÉFAUT PAYÉ LE 14/09 : CETTE RETOMBÉE EFFAÇAIT EN SILENCE.
  //
  // La retombée elle-même est juste et on n'y touche pas. Ce qui ne l'était
  // pas, c'est qu'elle ne laissait AUCUNE trace : `langue-serie = 'vostfr'`,
  // posé par le guide d'accueil avec un vocabulaire que ce segment ne connaît
  // pas (`auto|vo|vf`), devenait `auto` au premier passage dans les réglages —
  // et comme `ecrire` annonce au compte, l'effacement voyageait sur tous les
  // appareils. Plus personne ne pouvait relier ça à un clic fait des semaines
  // plus tôt. Une valeur inconnue qu'on jette se dit.
  const memo = prefs.lire(cle, defaut);
  // Une langue précise (`ja`) n'a pas de bouton ici mais reste un choix valide :
  // la remettre au défaut l'effacerait sur tous les appareils.
  const precise = cle.startsWith('langue-') && /^[a-z]{2}(-[a-z]{2})?$/.test(memo);
  const courante = precise || boutons.some((b) => b.dataset.v === memo) ? memo : defaut;
  if (courante !== memo) {
    tracer('reglages', 'valeur inconnue remise au défaut', { cle, memo, defaut });
    prefs.ecrire(cle, courante);
  }
  peindre(courante);

  for (const b of boutons) {
    b.onclick = () => {
      prefs.ecrire(cle, b.dataset.v);
      surChoixManuel();
      peindre(b.dataset.v);
      if (apres) apres();
    };
  }
}

// Les réglages sont répartis en onglets : le panneau tient sur un écran, et
// les sections d'administration n'existent que pour ceux qui y ont droit.
function preparerOnglets() {
  const barre = $('onglets-reglages');
  if (!barre || !barre.parentElement) return;
  const boutons = [...barre.querySelectorAll('button[data-onglet]')];
  const panneaux = [...barre.parentElement.querySelectorAll(':scope > .panneau-reglages')];

  const montrer = (nom) => {
    for (const b of boutons) {
      const actif = b.dataset.onglet === nom;
      b.classList.toggle('actif', actif);
      b.setAttribute('aria-selected', actif ? 'true' : 'false');
    }
    for (const p of panneaux) p.hidden = p.dataset.onglet !== nom;
  };

  for (const b of boutons) {
    if (b.hasAttribute('data-admin')) b.hidden = role !== 'owner';
    if (b.hasAttribute('data-seance')) b.hidden = !etatSeance?.actif;
    b.onclick = () => {
      montrer(b.dataset.onglet);
      if (b.dataset.onglet === 'points') caisseUI.peindreJournal($('caisse-journal')).catch(() => {});
    };
  }

  // ⚠️ `data-admin` NE VIT PLUS SEULEMENT SUR LES ONGLETS.
  //
  // Les trois onglets d'administration ont quitté ce panneau le 16/09 : la
  // gestion est un tableau de bord à part entière, pas un onglet de 480 px. Il
  // reste donc UN élément à montrer aux propriétaires — l'entrée qui y mène — et
  // il n'est pas dans la barre d'onglets. La boucle ci-dessus ne le voyait pas,
  // et l'entrée serait restée cachée pour tout le monde, y compris pour Toji.
  // Partout où vit le panneau : dans la modale, il a quitté `#v-reglages`, et
  // l'entrée restait cachée au propriétaire lui-même (matrice des menus, 21/10).
  for (const el of document.querySelectorAll('.panneau-reglages [data-admin]')) {
    el.hidden = role !== 'owner';
  }

  // On rouvre sur l'onglet où l'on était, sauf s'il vient de disparaître.
  const encore = boutons.find((b) => !b.hidden && b.classList.contains('actif'));
  const repli = boutons.find((b) => !b.hidden);
  montrer((encore ?? repli)?.dataset.onglet ?? 'general');
}

// Deux ouvertures possibles : au centre de l'écran, ou collée sous l'onglet du
// profil qui l'ouvre. Les deux repères sont mesurés à l'ouverture — une
// constante vieillirait mal, l'en-tête n'ayant pas la même hauteur partout.
function appliquerAncrage() {
  const voile = $('voile');
  if (!voile) return;
  const ancre = prefs.lire('ancrage', 'ancre') === 'ancre';
  voile.classList.toggle('ancre', ancre);
  if (!ancre) return;
  const bouton = $('profil');
  if (!bouton) return;
  const r = rectPage(bouton);
  voile.style.setProperty('--ancre-haut', `${Math.round(r.bottom + 8)}px`);
  voile.style.setProperty('--ancre-droite', `${Math.round(Math.max(12, largeurPage() - r.right))}px`);
}

// La pop-up reprend le contenu de la vue réglages plutôt que de le dupliquer :
// un seul balisage, deux présentations.
// `enPage` : « Mes réglages » depuis la liste des séances montre la VUE des
// réglages, sans pop-up. Elle n'était branchée que par la pop-up : en page, les
// onglets et les réglages restaient morts (matrice des menus, 21/10).
function ouvrirModale({ enPage = false } = {}) {
  const corps = $('modale-corps');
  const source = document.querySelector('#v-reglages .wrap');
  // Si le bloc a déjà été déplacé, il est dans la modale : rien à refaire.
  if (!enPage && corps && source) {
    while (corps.firstChild) corps.removeChild(corps.firstChild);
    corps.appendChild(source);
  }

  // ⚠️ Chaque branchement passe par les aides sûres : un identifiant disparu
  // lors d'une refonte ne doit plus empêcher la modale de s'ouvrir.
  const lie = (id, cle, defaut, apres) => {
    valeur(id, prefs.lire(cle, defaut));
    surChangement(id, (e) => {
      prefs.ecrire(cle, e.target.value);
      if (apres) apres();
    });
  };

  lierSegment('r-cote', 'cote', 'droite', appliquerCote);
  lierSegment('r-affichage', 'affichage', 'rang', rafraichirProfil);
  // La langue écoutée est la moitié de ce que la présence riche annonce :
  // changer de piste sans la reposer laisserait « VF » affiché à tout le vocal.
  // Le choix de version pose aussi le rôle Discord — Toji : « quand le client
  // choisit la langue / sous-titres sur l'interface il obtient les rôles aussi,
  // comme ça c'est bien organisé. » Un choix est un choix, quel que soit
  // l'écran où il est fait : le faire ici et devoir le refaire sur les boutons
  // du salon serait deux fois la même question.
  lierSegment('r-langue', 'langue', 'auto', () => { majPresence(); poserGoutDiscord(); });
  lierSegment('r-qualite', 'qualite', 'auto');
  // La fiche ouverte se repeint : le réglage se juge sur ce qu'on regarde.
  lierSegment('r-presentation', 'presentation-saisons', 'menu', () => {
    try { if (oeuvre) peindreFicheOeuvre(); } catch { /* aucune fiche ouverte */ }
  });
  lierSegment('r-ouverture', 'ouverture-lecteur', 'ici');
  // Les rangées peignent autant de cartes que le réglage en montre : l'accueil
  // se repeint, la grille suit la feuille de style.
  lierSegment('r-series-ligne', CLE_PAR_LIGNE, DEFAUT_PAR_LIGNE, () => {
    appliquerSeriesParLigne();
    try { if (!$('v-accueil')?.hidden) peindreAccueil(); } catch { /* pas encore peint */ }
  });
  // Le catalogue et l'accueil se repeignent : un réglage qui demande de
  // ressortir de l'écran pour se voir n'a pas l'air d'avoir été pris.
  lierSegment('r-dispo', 'dispo-seulement', 'non', () => {
    // ⚠️ LE CLASSEMENT DE L'ACCUEIL SE FAIT SUR LE SERVEUR, QUI FILTRE AUSSI.
    //
    // Repeindre avec les rangees deja recues montrerait l'ancien filtre : la
    // rangee doit etre reclassee, pas seulement redessinee.
    chargerAccueil().then(() => {
      try { if (!$('v-accueil')?.hidden) peindreAccueil(); } catch { /* pas encore peint */ }
    });
    try {
      // Le critere part au serveur : on invalide l'empreinte pour que la grille
      // reparte de sa premiere page au lieu de retrier ce qu'elle a deja.
      grilleCriteres = '';
      if (!$('v-catalogue')?.hidden) peindreCatalogueOeuvres();
    } catch { /* idem */ }
  });
  // Le bandeau se repeint tout de suite : sinon il faudrait relancer une lecture
  // pour voir l'effet du réglage.
  lierSegment('r-membres', 'bouton-membres', 'oui', () => { try { peindreSeance(); } catch { /* pas de séance */ } });
  lierSegment('r-langue-anime', 'langue-anime', 'auto');
  lierSegment('r-langue-serie', 'langue-serie', 'auto');
  // ⚠️ LE SEGMENT DES FILMS N'EXISTE QUE SI LA CLÉ EXISTE.
  //
  // Tant que le serveur ne sert pas `langue-film`, les films suivent
  // `langue-serie` — et deux segments branchés sur une même clé, c'est très
  // exactement le défaut du 14/09 : `lierSegment` ne repeint QUE son groupe,
  // donc régler les films laisserait les séries allumées sur l'ancienne valeur
  // alors que la valeur enregistrée vient de changer. On ne le branche donc pas,
  // et on le DIT : un groupe muet dans la page se remarque, une divergence
  // d'affichage non.
  if (cleDeLangue('film') === 'langue-film') lierSegment('r-langue-film', 'langue-film', 'auto');
  else if ($('r-langue-film')) tracer('reglages', 'segment « films » sans clé servie — les films suivent les séries');
  monterReglageDesRangees();
  monterLanguesDeSousTitres();
  lierSegment('r-ancrage', 'ancrage', 'ancre', appliquerAncrage);
  preparerOnglets();
  preparerCompte();
  preparerNavigateur();

  // Sous-titres : curseurs et sélecteurs, avec la valeur affichée à côté.
  const st = [
    ['r-st-taille', 'st-taille', '88', 'o-taille', (v) => `${v} %`],
    ['r-st-contour', 'st-contour-epaisseur', '1.5', 'o-contour', (v) => `${v} px`],
    ['r-st-fond', 'st-fond-opacite', '62', 'o-fond', (v) => `${v} %`],
    ['r-st-position', 'st-position', '6', 'o-position', (v) => `${v} %`],
    ['r-st-interligne', 'st-interligne', '135', 'o-interligne', (v) => `${Number(v) / 100}`],
  ];
  for (const [id, cle, defaut, sortie, format] of st) {
    const el = $(id);
    if (!el) continue;
    el.value = prefs.lire(cle, defaut);
    texte(sortie, format(el.value));
    el.oninput = () => {
      prefs.ecrire(cle, el.value);
      texte(sortie, format(el.value));
      appliquerSousTitres();
    };
  }

  for (const [id, cle, defaut] of [
    ['r-st-graisse', 'st-graisse', '400'],
    ['r-st-couleur', 'st-couleur', '#ffffff'],
    ['r-st-contour-couleur', 'st-contour-couleur', '#000000'],
    ['r-st-fond-couleur', 'st-fond-couleur', '0,0,0'],
  ]) {
    lie(id, cle, defaut, appliquerSousTitres);
  }

  // La police est à part : sa liste se construit à chaque ouverture — elle
  // dépend du compte ET de l'appareil — et la CHOISIR, c'est la retenir.
  // C'est la demande, mot pour mot : on la sélectionne une fois sur le PC et
  // elle est là, ensuite, partout.
  garnirListeDePolices();
  const listePolices = $('r-st-police');
  if (listePolices) {
    // Une police retenue puis retirée du compte, ou installée puis
    // désinstallée, n'a plus d'entrée : sans ce repli, le sélecteur
    // s'afficherait vide alors que les sous-titres, eux, ont bien un style.
    const memo = prefs.lire('st-police', 'zen');
    listePolices.value = [...listePolices.options].some((o) => o.value === memo) ? memo : 'zen';
    listePolices.onchange = () => {
      prefs.ecrire('st-police', listePolices.value);
      if (!POLICES[listePolices.value]) reglagesDuCompte.retenirPolice(listePolices.value);
      appliquerSousTitres();
    };
  }

  // Ajouter une police par son NOM, et seulement si elle existe ici.
  //
  // La liste proposée vient d'un sondage d'une trentaine de familles courantes :
  // qui a installé autre chose ne la verrait jamais apparaître. Ce champ répond
  // à ce cas, sans rien changer aux garanties — on retient un NOM après avoir
  // vérifié que l'appareil sait vraiment la dessiner. Rien n'est téléversé,
  // rien n'est chargé depuis une adresse.
  const champPolice = $('st-police-ajout');
  const boutonPolice = $('st-police-ajouter');
  const etatPolice = $('st-police-etat');
  if (champPolice && boutonPolice) {
    const dire = (mot) => { if (etatPolice) etatPolice.textContent = mot; };
    const ajouter = async () => {
      const nom = nomDePolice(champPolice.value);
      if (!nom) return dire('Ce nom ne ressemble pas à une police.');
      if (!policeInstallee(nom)) {
        return dire(`« ${nom} » n'est pas installée sur cet appareil.`);
      }
      const retenue = await reglagesDuCompte.retenirPolice(nom);
      if (retenue === false) return dire("Carnet plein : retire une police avant d'en ajouter une autre.");
      champPolice.value = '';
      garnirListeDePolices();
      listePolices && (listePolices.value = nom);
      prefs.ecrire('st-police', nom);
      appliquerSousTitres();
      dire(`« ${nom} » ajoutée et retenue sur ton compte.`);
    };
    boutonPolice.onclick = ajouter;
    champPolice.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } };
  }

  // --- animation du blason ---------------------------------------------------
  // Les curseurs agissent aussitôt sur tous les blasons de la page, y compris
  // l'aperçu : c'est le seul moyen de juger un mouvement. Rien n'est conservé
  // tant qu'« Enregistrer » n'a pas été utilisé, et fermer les réglages sans
  // enregistrer remet ce qui était en place.
  const CURSEURS_BLASON = [
    ['r-bl-brillance', 'brillance', 'o-bl-brillance'],
    ['r-bl-vitesse', 'vitesse', 'o-bl-vitesse'],
    ['r-bl-grosseur', 'grosseur', 'o-bl-grosseur'],
  ];

  let blasonEnCours = lireBlason(prefs);
  // La bascule finale se règle ici aussi, mais elle ne touche pas au blason :
  // elle vit dans sa propre préférence, en millisecondes.
  // Le meme defaut et le meme plafond que la bascule elle-meme : un curseur
  // qui propose une valeur que la bascule refusera ensuite ment sur ce qu'il
  // regle. Voir `basculeFinale` pour la mesure qui a fixe ces 500 ms.
  let transition = Math.min(700, Number(prefs.lire('blason-transition', '500')) || 0);

  const peindreBlason = () => {
    for (const [id, cle, sortie] of CURSEURS_BLASON) {
      valeur(id, String(blasonEnCours[cle]));
      texte(sortie, `${blasonEnCours[cle]} %`);
    }
    appliquerBlason(blasonEnCours);
    valeur('r-bl-transition', String(transition));
    texte('o-bl-transition', transition === 0 ? 'aucune' : `${(transition / 1000).toFixed(1)} s`);
  };

  for (const [id, cle, sortie] of CURSEURS_BLASON) {
    const el = $(id);
    if (!el) continue;
    el.oninput = () => {
      blasonEnCours = { ...blasonEnCours, [cle]: Number(el.value) };
      texte(sortie, `${el.value} %`);
      appliquerBlason(blasonEnCours);
      texte('bl-etat', 'Modifié — pense à enregistrer.');
    };
  }

  const curseurTransition = $('r-bl-transition');
  if (curseurTransition) {
    curseurTransition.oninput = () => {
      transition = Number(curseurTransition.value);
      texte('o-bl-transition', transition === 0 ? 'aucune' : `${(transition / 1000).toFixed(1)} s`);
      texte('bl-etat', 'Modifié — pense à enregistrer.');
    };
  }

  surClic('bl-essai', () => {
    const zone = document.querySelector('.apercu-blason');
    if (!zone) return;
    // Les durées viennent du curseur : l'essai montre exactement ce qui sera
    // joué, pas une approximation.
    const total = Math.max(200, transition);
    const sortie = Math.round(total * 0.36);
    zone.style.setProperty('--st-bascule', `${total - sortie}ms`);
    zone.style.setProperty('--st-sortie', `${sortie}ms`);

    zone.classList.remove('joue', 'sort');
    void zone.offsetWidth; // force la reprise de l'animation
    zone.classList.add('joue');
    setTimeout(() => zone.classList.add('sort'), total - sortie);
    setTimeout(() => {
      zone.classList.remove('joue', 'sort');
      appliquerBlason(blasonEnCours);
    }, total + 400);
  });

  surClic('bl-defaut', () => {
    blasonEnCours = { ...BLASON_DEFAUTS };
    transition = 1560;
    peindreBlason();
    texte('bl-etat', "Valeurs d'origine rétablies — pense à enregistrer.");
  });

  surClic('bl-enregistrer', () => {
    prefs.ecrire('blason-brillance', String(blasonEnCours.brillance));
    prefs.ecrire('blason-vitesse', String(blasonEnCours.vitesse));
    prefs.ecrire('blason-grosseur', String(blasonEnCours.grosseur));
    prefs.ecrire('blason-transition', String(transition));
    texte('bl-etat', 'Enregistré.');
  });

  peindreBlason();

  surClic('st-defaut', () => {
    for (const cle of ['st-taille', 'st-contour-epaisseur', 'st-fond-opacite', 'st-position',
                       'st-interligne', 'st-police', 'st-graisse', 'st-couleur',
                       'st-contour-couleur', 'st-fond-couleur']) {
      prefs.ecrire(cle, '');
    }
    fermerModale();
    ouvrirModale();
  });

  appliquerSousTitres();
  if (!enPage && $('voile')) {
    appliquerAncrage();
    $('voile').hidden = false;
    // La rangée de sections se cale sous la barre de titre : sa hauteur ne se
    // lit qu'une fois la pop-up affichée, et change avec la largeur d'écran.
    const tete = document.querySelector('.modale-tete');
    const modale = document.querySelector('.modale');
    if (tete && modale) {
      modale.style.setProperty('--h-tete', `${tete.offsetHeight}px`);
    }
  }
  // ⚠️ HORS DU BLOC `owner`, ET C'EST TOUT L'INTÉRÊT.
  //
  // Brancher ses propres sources n'est pas un geste d'administration : sur une
  // instance en mode « ensemble », la maison ne prête rien et c'est la SEULE
  // façon de voir quoi que ce soit. Rangé avec les sections d'admin, l'écran
  // serait invisible pour exactement les gens qui en ont besoin.
  preparerMesSources();
  chargerMesSources();

  // ⚠️ ONZE PRÉPARATIONS D'ADMINISTRATION VIVAIENT ICI, ET N'Y VIVENT PLUS.
  //
  // `ouvrirModale()` est la pop-up des réglages PERSONNELS. Tant que les trois
  // onglets d'administration y logeaient, préparer leurs blocs à cette
  // ouverture-là avait du sens. Ils ont déménagé dans le tableau de bord le
  // 16/09 : les préparations sont parties avec eux, dans
  // `preparerLAdministration()`, appelée quand le tableau de bord s'ouvre.
  //
  // Ce que ça corrigeait, mesuré par le parcours automatique au premier essai :
  // la section « Sources et add-ons » faisait ZÉRO PIXEL DE HAUT. `#bloc-addons`
  // et `#bloc-scan` portent `hidden` dans le balisage — c'est `chargerAddons()`
  // qui les démasque. Plus personne ne l'appelait : la section existait, le rail
  // y menait, le titre s'affichait, et il n'y avait RIEN. Une page vide qui ne
  // lève pas est exactement le genre de défaut qu'on ne trouve qu'en cliquant.
  //
  // Et l'inverse était vrai aussi : ouvrir ses réglages personnels déclenchait
  // onze chargements d'administration — la liste des accès, les add-ons, les
  // photos, le journal — pour quelqu'un qui venait changer sa langue.
}

/**
 * Tout ce que le tableau de bord doit avoir préparé — appelé par `src/admin.js`
 * une fois la porte passée, jamais avant.
 *
 * ⚠️ APPELÉE UNE FOIS PAR OUVERTURE, PAS UNE FOIS PAR SECTION. Les `preparer*`
 * posent des gestionnaires de clic (`surClic` remplace `onclick`, il ne
 * s'empile pas) et les `charger*` remplissent : les rejouer est sans danger,
 * mais recharger neuf listes à chaque changement de section rendrait le rail
 * poussif pour rien.
 */
function preparerLAdministration() {
  if (role !== 'owner') return;
  chargerAddons();
  chargerPhotos();
  preparerScan();
  // L'envers des rôles : ce que MOI j'ai prêté, à des personnes nommées.
  chargerMesPrets();
  preparerMaintenance();
  preparerJournal();
  preparerNotifications();
  preparerGouts();
  preparerDiagnostic();
  // La règle du jeu de la soirée : elle n'existe que pour qui peut la
  // changer, et le serveur refuse de toute façon les autres.
  preparerReglagesSeance();
  // ⚠️ `chargerAcces()` N'EST PAS DANS CETTE LISTE : `src/admin.js` l'appelle
  // lui-même à la fin de `peupler()`, après avoir peint les personnes et la
  // matrice. L'ajouter ici la ferait partir deux fois, et la seconde réponse
  // écraserait un rendu déjà bon avec des colonnes d'un chargement précédent.
}

function fermerModale() {
  const voile = $('voile');
  voile.hidden = true;
  // Le contenu retourne à sa place APRÈS le fondu de sortie (160 ms) : le
  // rendre tout de suite viderait la pop-up sous les yeux pendant qu'elle
  // s'efface. Rouverte entre-temps, elle le garde — `ouvrirModale` ne déplace
  // le bloc que s'il est encore dans la vue des réglages.
  setTimeout(rendreLesReglages, 200);
}

/** Remet les réglages dans leur vue, si la pop-up est bien fermée. */
function rendreLesReglages() {
  if (!$('voile')?.hidden) return;
  const bloc = $('modale-corps')?.firstElementChild;
  if (bloc) $('v-reglages').appendChild(bloc); // remis en place pour la prochaine ouverture
}

let moi = null;

async function appliquerCote() {
  $('profil').classList.toggle('droite', prefs.lire('cote', 'droite') === 'droite');
}

function rafraichirProfil() {
  // Tant qu'on ne sait pas qui est là, le bloc n'existe pas : un rond gris et
  // un pseudo vide n'apprennent rien et donnent l'impression d'un chargement
  // qui n'aboutit jamais.
  const bloc = $('profil');
  if (bloc) bloc.hidden = !moi?.pseudo;
  if (!moi) return;
  const mode = prefs.lire('affichage', 'rang');
  texte('pseudo', moi.pseudo ?? '');
  texte('login-nom', mode === 'rang' ? (moi.rang ?? '—') : moi.username ? `@${moi.username}` : '');
  const pp = $('pp');
  if (pp && moi.avatar && pp.getAttribute('src') !== moi.avatar) pp.src = moi.avatar;
}

// ---------------------------------------------------------------------------
// UNE CARTE D'ADD-ON — comme dans Stremio : une icône, un nom, ce qu'il sert
//
// Toji : « à la place de noter directement l'URL, tu me la caches une fois
// qu'on l'a mis. Tu me mets le nom du profil, l'icône à gauche. Après on peut
// choisir si c'est pour du catalogue ou si c'est pour des liens. »
//
// L'adresse n'est plus affichée : celle d'AIOStreams porte la configuration
// entière encodée dedans — clés de débrideur comprises — et elle n'apprenait
// rien à personne. Reste l'hôte, en petit, parce qu'il faut bien distinguer
// deux instances du même add-on.
const NOM_ROLE = { catalogue: 'Catalogue', flux: 'Liens', soustitres: 'Sous-titres' };

// ⚠️ L'ORDRE DES LISTES D'ADD-ONS, PARTOUT LE MEME.
//
// Toji : « le catalogue, il se met tout en haut. Stream, ça se met en dessous,
// et les sous-titres, ça se met encore en dessous. » C'est l'ordre dans lequel
// les choses se passent, pas un rangement : le catalogue donne les titres, les
// liens donnent les flux, les sous-titres viennent par-dessus. Un add-on qui
// tient plusieurs rôles se range sur le plus haut qu'il porte.
const RANG_ROLE = { catalogue: 0, flux: 1, soustitres: 2 };
const rangDesRoles = (s) => Math.min(...(s.roles?.length ? s.roles : ['flux']).map((r) => RANG_ROLE[r] ?? 9));

function carteAddon(s) {
  const offres = s.offres?.length ? s.offres : Object.keys(NOM_ROLE);
  const roles = s.roles ?? [];
  const pastilles = offres.map((r) => `<button type="button" class="role-addon${roles.includes(r) ? ' tenu' : ''}"
      data-role="${esc(r)}" data-id="${esc(s.id)}"
      aria-pressed="${roles.includes(r)}">${esc(NOM_ROLE[r] ?? r)}</button>`).join('');

  // Sans logo — add-on injoignable à l'ajout, ou manifeste sans image — on
  // affiche l'initiale plutôt qu'un carré vide : la ligne garde sa forme, et
  // le bouton « actualiser » à côté dit quoi faire.
  const initiale = esc((s.nom ?? s.label ?? '?').trim().charAt(0).toUpperCase());
  const embleme = s.icone
    ? `<img class="addon-icone" src="${esc(s.icone)}" alt="" loading="lazy" />`
    : `<span class="addon-icone vide">${initiale}</span>`;

  return `<div class="addon">
    <label class="addon-actif"><input type="checkbox" data-actif="${esc(s.id)}" ${s.enabled ? 'checked' : ''} /></label>
    ${embleme}
    <span class="nom-addon">
      <span class="addon-champs">
        <input class="addon-nom" data-nom="${esc(s.id)}" value="${esc(s.label)}"
               placeholder="${esc(s.nom || hoteDe(s.manifest))}" aria-label="Nom de l’add-on" />
        <!-- ⚠️ UN CHAMP LIBRE NE DIT PAS CE QU'IL ATTEND.
             Toji : « ce que je te demandais pour le tag, c'était de pouvoir
             sélectionner entre trois tags comme Stremio et AIOStreams : le tag
             catalogue, le tag liens, et sous-titres. »
             Le champ était une étiquette libre, à côté de pastilles qui, elles,
             portaient déjà les rôles — deux choses qui se ressemblent pour deux
             usages différents. Il ne reste que les rôles, et ils sont là où on
             les cherchait. -->
        <span class="roles-addon" role="group" aria-label="Ce que cet add-on sert">${pastilles}</span>
      </span>
      <code>${esc([s.nom, hoteDe(s.manifest), s.version ? `v${s.version}` : ''].filter(Boolean).join(' · '))}</code>
      ${s.description ? `<em class="addon-detail">${esc(s.description)}</em>` : ''}
      <!-- ⚠️ L'ADRESSE D'UN ADD-ON CHANGE, ET IL N'Y AVAIT AUCUN ENDROIT OÙ
           LA CHANGER.

           Le serveur savait pourtant le faire depuis toujours : updateSource
           accepte un nouveau manifeste et le valide. Mais l'écran ne montrait
           que le NOM et la langue : pour corriger une adresse, il fallait
           retirer l'add-on et le rajouter, donc perdre son rôle, sa langue,
           son étiquette, et le retaper de mémoire.

           Le cas est arrivé le 09/09 : « Cache Multi » rendait 404 sur tous
           ses liens, et la réparation passait par une URL régénérée ailleurs.
           Il faut pouvoir la coller ici, tout de suite, sans rien perdre.

           Le champ est en pleine largeur et en petite police : c'est une
           adresse longue, qu'on relit avant de valider. -->
      <input class="addon-url" data-url="${esc(s.id)}" value="${esc(s.manifest)}"
             spellcheck="false" autocomplete="off"
             aria-label="Adresse du manifeste" title="Adresse du manifeste — Entrée pour enregistrer" />
    </span>
    <select data-langue="${esc(s.id)}">
      <option value="">Langue non précisée</option>
      <option value="vo"${s.langue === 'vo' ? ' selected' : ''}>VO / VOSTFR</option>
      <option value="vf"${s.langue === 'vf' ? ' selected' : ''}>VF</option>
      <option value="multi"${s.langue === 'multi' ? ' selected' : ''}>Les deux</option>
    </select>
    <button type="button" class="ghost mince" data-actualiser="${esc(s.id)}" title="Redemander son nom, son icône et ce qu’il sait faire">↻</button>
    <button type="button" class="ghost mince" data-retirer="${esc(s.id)}" title="Retirer cet add-on">✕</button>
    <span class="etat-lang" id="lang-${esc(s.id)}"></span>
  </div>`;
}

/** L'hôte d'une adresse, ou l'adresse elle-même si elle ne s'analyse pas. */
function hoteDe(url) {
  try { return new URL(url).host; } catch { return String(url ?? '').slice(0, 40); }
}

// Gestion des add-ons : nom, adresse, et la langue qu'ils servent.
async function chargerAddons() {
  const bloc = $('bloc-addons');
  if (!bloc) return;
  bloc.hidden = false;
  const cible = $('liste-addons');
  cible.innerHTML = '<p class="muted">Chargement…</p>';

  let cfg;
  try {
    cfg = await api('/api/admin/access');
  } catch (err) {
    cible.innerHTML = `<p class="muted">${esc(err.message)}</p>`;
    return;
  }

  // ⚠️ L'ORDRE DE LA LISTE EST CELUI DE LA CHAINE.
  //
  // Toji : « on est censé avoir dans la liste, tout en haut, le catalogue, et
  // en dessous les trois AIOStreams. » Ce n'est pas un caprice de rangement :
  // c'est l'ordre dans lequel les choses se passent — le catalogue donne les
  // titres, les liens donnent les flux, les sous-titres viennent par-dessus.
  // Un add-on qui tient plusieurs rôles se range sur le plus haut qu'il porte.
  const sources = [...(cfg.sources ?? [])].sort((a, b) => rangDesRoles(a) - rangDesRoles(b)
    || String(a.label).localeCompare(String(b.label), 'fr'));

  cible.innerHTML = sources.length
    ? sources.map(carteAddon).join('')
    : '<p class="muted">Aucun add-on enregistré.</p>';

  for (const el of cible.querySelectorAll('[data-role]')) {
    el.onclick = async () => {
      const { id, role } = el.dataset;
      const source = sources.find((s) => s.id === id);
      if (!source) return;
      const roles = new Set(source.roles ?? []);
      if (roles.has(role)) roles.delete(role); else roles.add(role);
      const etat = $(`lang-${id}`);
      if (etat) etat.textContent = '…';
      try {
        await api(`/api/admin/sources/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ roles: [...roles] }),
        });
        chargerAddons();
      } catch (err) {
        // Le refus le plus courant : décocher le dernier rôle. Il se dit ici
        // plutôt que de laisser la pastille repartir sans explication.
        if (etat) etat.textContent = err.message;
      }
    };
  }

  // ⚠️ ON ENREGISTRE QUAND LE CHAMP EST QUITTE, PAS A CHAQUE TOUCHE.
  //
  // Une requete par frappe, ce sont quinze ecritures pour renommer « Cache
  // FR » — et un ordre d'arrivee qui n'est pas garanti, donc un nom final
  // imprevisible. On part au `change` (quitter le champ, ou Entree), et la
  // liste ne se repeint PAS : elle emporterait le champ en cours de saisie si
  // deux modifications se suivent.
  const enregistrer = async (id, patch, champ) => {
    try {
      await api(`/api/admin/sources/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
      champ.classList.remove('en-faute');
    } catch (err) {
      champ.classList.add('en-faute');
      texte(`lang-${id}`, err.message);
    }
  };
  for (const el of cible.querySelectorAll('[data-nom]')) {
    el.onchange = () => enregistrer(el.dataset.nom, { label: el.value }, el);
    el.onkeydown = (e) => { if (e.key === 'Enter') el.blur(); };
  }

  // ⚠️ UNE ADRESSE NE SE CORRIGE PAS PAR MÉGARDE.
  //
  // Le nom s'enregistre au `change` — perdre le focus suffit, et ce n'est pas
  // grave : un nom se retape. Une adresse, non. La coller de travers coupe la
  // source pour tout le monde, et le seul indice serait un lecteur qui ne
  // trouve plus rien. On demande donc un geste franc : Entrée, ou le bouton.
  //
  // Le serveur valide de son côté (schéma, `/manifest.json`, refus des
  // adresses internes) et renvoie sa raison en clair : on l'affiche telle
  // quelle plutôt que de la traduire, elle est écrite pour être lue.
  for (const el of cible.querySelectorAll('[data-url]')) {
    const poser = async () => {
      const id = el.dataset.url;
      const source = sources.find((x) => x.id === id);
      const voulu = el.value.trim();
      if (!voulu || voulu === source?.manifest) return;
      el.classList.remove('en-faute');
      texte(`lang-${id}`, 'enregistrement…');
      try {
        await api(`/api/admin/sources/${id}`, {
          method: 'PUT', body: JSON.stringify({ manifest: voulu }),
        });
        // On redemande son identité dans la foulée : une adresse neuve, c'est
        // souvent un add-on qui a changé de nom, d'icône ou de capacités. Un
        // échec ici n'annule rien — l'adresse est déjà posée.
        await api(`/api/admin/sources/${id}/actualiser`, { method: 'POST', body: '{}' })
          .catch(() => {});
        chargerAddons();
      } catch (err) {
        el.classList.add('en-faute');
        texte(`lang-${id}`, err.message);
      }
    };
    el.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); poser(); } };
    // Le champ retrouve sa valeur si on le quitte sans valider : laisser une
    // adresse à moitié tapée à l'écran ferait croire qu'elle est en service.
    el.onblur = () => {
      const source = sources.find((x) => x.id === el.dataset.url);
      if (source && el.value.trim() !== source.manifest) el.value = source.manifest;
      el.classList.remove('en-faute');
    };
  }

  for (const el of cible.querySelectorAll('[data-actualiser]')) {
    el.onclick = async () => {
      const etat = $(`lang-${el.dataset.actualiser}`);
      if (etat) etat.textContent = 'lecture du manifeste…';
      try {
        await api(`/api/admin/sources/${el.dataset.actualiser}/actualiser`, { method: 'POST', body: '{}' });
        chargerAddons();
      } catch (err) {
        if (etat) etat.textContent = err.message;
      }
    };
  }

  // Retirer est sans retour (l'adresse n'est plus affichée nulle part) : d'où la confirmation.
  const signaler = (id, message) => texte(`lang-${id}`, message);
  brancherRetraits(cible, {
    api, signaler, confirmer: (q) => confirm(q), recharger: chargerAddons,
    nomDe: (id) => sources.find((s) => s.id === id)?.label,
  });

  for (const el of cible.querySelectorAll('[data-langue]')) {
    el.onchange = async () => {
      const etat = $(`lang-${el.dataset.langue}`);
      etat.textContent = '…';
      try {
        await api(`/api/admin/sources/${el.dataset.langue}`, {
          method: 'PUT',
          body: JSON.stringify({ langue: el.value }),
        });
        etat.textContent = 'enregistré';
        setTimeout(() => { etat.textContent = ''; }, 1800);
      } catch (err) {
        etat.textContent = err.message;
      }
    };
  }

  brancherCases(cible, { api, signaler });

  // Remplir le catalogue depuis les catalogues de cette base. Sans elle, il n'y
  // a rien à interroger — d'où l'ordre des deux boutons.
  surClic('meta-amorcer', async () => {
    texte('meta-etat', 'lecture des catalogues…');
    texte('meta-bilan', '');
    try {
      const bilan = await api('/api/admin/catalogue/amorcer', { method: 'POST', body: '{}' });
      texte('meta-etat', '');
      texte('meta-bilan', bilan.fait
        ? `${bilan.ajoutees} titre(s) ajouté(s) — ${bilan.total} au catalogue.`
        : `Rien ajouté : ${bilan.raison}.`);
    } catch (err) {
      texte('meta-etat', err.message);
    }
  });

  // Ajout d'un add-on. Un seul appel : la langue part avec la création, et le
  // nom s'en déduit côté serveur — il n'y a rien d'autre à saisir.
  surClic('addon-ajouter', async () => {
    const champ = $('addon-manifest');
    const manifest = champ ? champ.value.trim() : '';
    if (!manifest) {
      texte('addon-etat', 'adresse vide');
      return;
    }

    texte('addon-etat', 'ajout…');
    try {
      // Sans rôle choisi, on prend tout ce que le manifeste annonce savoir
      // faire : c'est le geste attendu quand on branche un add-on, et les
      // pastilles de la carte restent là pour affiner.
      const role = $('addon-role')?.value || '';
      await api('/api/admin/sources', {
        method: 'POST',
        body: JSON.stringify({
          manifest,
          langue: $('addon-langue')?.value || '',
          ...(role ? { roles: [role] } : {}),
        }),
      });
      if (champ) champ.value = '';
      texte('addon-etat', '');
      chargerAddons();
    } catch (err) {
      texte('addon-etat', err.message);
    }
  });
}

// ---------------------------------------------------------------------------
// MES SOURCES — ce que J'AI branché, et ce que le reste apporte
//
// ⚠️ CET ÉCRAN EXISTE D'ABORD POUR EXPLIQUER UN CATALOGUE VIDE.
//
// Sur une instance en mode « ensemble », la maison ne prête RIEN : tant que
// personne n'a branché de source, il n'y a littéralement rien à voir, et un
// catalogue vide se lit comme une panne. La question « pourquoi je ne vois
// rien ? » partait alors dans le salon vocal, où personne n'a la réponse non
// plus. Elle se lit maintenant sur la première ligne de cet écran.
//
// Les deux phrases du haut sont écrites ici plutôt que dans la page parce
// qu'elles dépendent de deux choses que le balisage ne connaît pas : le MODE de
// l'instance, et ce que ce compte-ci possède réellement.
//
// ⚠️ AUCUNE ADRESSE QUI N'EST PAS LA SIENNE N'ARRIVE JAMAIS ICI. `/api/mes-sources`
// ne rend que `sourcesDe(userId)` ; ce que la maison et les autres comptes
// apportent n'arrive qu'en NOMBRE. Une adresse de manifeste porte le plus
// souvent une clé d'API dans son chemin : l'afficher, c'est donner l'add-on.
// ---------------------------------------------------------------------------

const MOT_LANGUE_SOURCE = { vo: 'VOSTFR', vf: 'VF', multi: 'VOSTFR + VF' };

/** « une source » / « trois sources » — sans le « 1 » qui se lit mal. */
const compteSources = (n) => (n === 0 ? 'aucune source' : n === 1 ? 'une source' : `${n} sources`);

function peindreMesSources(d) {
  const siennes = d.siennes ?? [];
  const maison = d.maison ?? 0;
  const pretees = d.pretees ?? 0;
  const total = d.total ?? 0;

  // LE BILAN : d'où vient ce que je vois. On énumère les trois provenances,
  // dans l'ordre où le lecteur les interroge — les siennes d'abord, ce qui
  // n'est pas cosmétique : c'est celle-là qui répond quand deux sources ont le
  // même épisode.
  const parts = [];
  const allumees = siennes.filter((s) => s.enabled !== false).length;
  if (allumees) parts.push(`${compteSources(allumees)} à toi`);
  if (pretees) parts.push(`${pretees} ${pluriel(pretees, 'prêtée')} par d'autres comptes`);
  if (maison) parts.push(`${maison} ${pluriel(maison, 'fournie')} par l'instance`);

  const bilan = $('ms-bilan');
  if (bilan) {
    bilan.innerHTML = total === 0
      ? "<strong>Ton catalogue est vide.</strong> Aucune source ne répond pour ce compte."
      : `<strong>${esc(compteSources(total))}</strong> alimente${total > 1 ? 'nt' : ''} ton catalogue`
        + `${parts.length ? ` — ${esc(parts.join(', '))}` : ''}.`;
  }

  // L'EXPLICATION : pourquoi c'est comme ça, et ce qu'il y a à faire. Le mode
  // de l'instance décide de la phrase, parce qu'il décide de ce que l'instance
  // EST — pas d'un réglage qu'on pourrait changer d'un clic.
  const dires = [];
  if (d.mode === 'ensemble') {
    dires.push(
      "Cette instance ne fournit aucune source : elle synchronise la lecture, "
      + "elle ne tient pas de bibliothèque. Branche la tienne ci-dessous — "
      + "elle n'est visible que de toi.",
    );
  } else if (maison) {
    dires.push(
      `L'instance prête ${compteSources(maison)} à tous les comptes qu'elle héberge : `
      + "tu n'as rien à brancher pour voir son catalogue. Une source à toi passe "
      + "avant la sienne quand les deux ont l'épisode.",
    );
  } else {
    dires.push(
      "L'instance n'a pour l'instant aucune source à elle : tant que personne "
      + "n'en branche, le catalogue reste vide.",
    );
  }
  if (pretees) {
    dires.push(
      `D'autres comptes te prêtent ${compteSources(pretees)}. Un prêt s'éteint quand celui `
      + "qui l'a ouvert quitte le salon — sauf s'il a choisi de l'affranchir.",
    );
  }
  texte('ms-explication', dires.join(' '));

  // LA LISTE. Nom, adresse en entier, et de quoi retirer — rien d'autre :
  // activer/désactiver et renommer sont des gestes d'exploitant, ils vivent
  // dans la section admin et n'ont pas leur place sur l'écran d'un compte.
  const liste = $('ms-liste');
  if (!liste) return;
  // ⚠️ LE MEME ORDRE QUE PARTOUT : LE CATALOGUE, PUIS LES LIENS, PUIS LES
  // SOUS-TITRES.
  //
  // Toji : « le catalogue, il se met tout en haut. Stream, ça se met en
  // dessous, et les sous-titres, ça se met encore en dessous. » C'est l'ordre
  // dans lequel les choses se passent — le catalogue donne les titres, les
  // liens donnent les flux, les sous-titres viennent par-dessus. La liste de
  // l'administration le suivait déjà ; celle-ci restait dans l'ordre d'ajout,
  // et c'est pourtant ici que vivent les add-ons de chacun.
  const triees = [...siennes].sort((a, b) => rangDesRoles(a) - rangDesRoles(b)
    || String(a.label ?? '').localeCompare(String(b.label ?? ''), 'fr'));

  liste.innerHTML = triees.length
    ? triees.map((s) => {
      // ⚠️ « VF · VF ». Le nom se DÉDUIT de la langue quand on n'en donne pas
      // (`addSource`), et la pastille répétait donc mot pour mot le libellé
      // qu'elle était censée compléter. On ne l'affiche que si elle ajoute
      // quelque chose.
      // ⚠️ L'ADRESSE DU MANIFESTE NE S'AFFICHE PLUS. NULLE PART.
      //
      // Toji : « là, pour l'instant, il y a encore les URLs en clair. Je veux
      // que ça soit enlevé. » Cet écran-ci était reste en arrière : la liste de
      // l'administration montre l'icône et le nom depuis la refonte, celle-ci
      // affichait toujours l'adresse ENTIÈRE — et c'est l'écran qu'on ouvre le
      // plus souvent, puisque chacun y branche les siennes. Le serveur ne
      // l'envoie même plus : il donne l'hôte (`sourceLisible`).
      //
      // ⚠️ « VF · VF ». Le nom se DÉDUIT de la langue quand on n'en donne pas
      // (`addSource`), et la pastille répétait donc mot pour mot le libellé
      // qu'elle était censée compléter. On ne l'affiche que si elle ajoute
      // quelque chose.
      const nom = s.nom || s.label || s.hote || 'add-on';
      const langue = MOT_LANGUE_SOURCE[s.langue] ?? s.langue ?? '';
      const pastille = langue && langue !== nom
        ? ` <span class="muted">· ${esc(langue)}</span>` : '';
      // ⚠️ LES ROLES SE CHOISISSENT ICI AUSSI, PLUS SEULEMENT DANS L'ADMIN.
      //
      // Toji : « ce que je te demandais pour le tag, c'était de pouvoir
      // sélectionner entre trois tags comme Stremio et AIOStreams : catalogue,
      // liens, sous-titres. » Cet écran-ci — celui où chacun branche ses
      // propres add-ons, donc le plus ouvert — se contentait de les ÉCRIRE, à
      // côté d'un champ libre qui, lui, se cliquait. C'était l'inverse de ce
      // qu'il fallait.
      //
      // `offres` borne ce qu'on peut cocher : un add-on ne tient que les rôles
      // que son manifeste annonce. Sans cette information, on propose les trois
      // — le serveur refusera ce qui ne tient pas.
      const offres = s.offres?.length ? s.offres : Object.keys(NOM_ROLE);
      const tenus = s.roles ?? [];
      const pastillesRoles = offres.map((r) => `<button type="button" class="role-addon${tenus.includes(r) ? ' tenu' : ''}"
          data-ms-role="${esc(r)}" data-ms-id="${esc(s.id)}"
          aria-pressed="${tenus.includes(r)}">${esc(NOM_ROLE[r] ?? r)}</button>`).join('');
      const embleme = s.icone
        ? `<img class="addon-icone" src="${esc(s.icone)}" alt="" loading="lazy" />`
        : `<span class="addon-icone vide">${esc(String(nom).trim().charAt(0).toUpperCase())}</span>`;
      return `<div class="ms-source">
        ${embleme}
        <span class="ms-nom">
          <span class="addon-champs">
            <input class="addon-nom" data-ms-nom="${esc(s.id)}" value="${esc(s.label ?? '')}"
                   placeholder="${esc(nom)}" aria-label="Nom de l’add-on" />
            <span class="roles-addon" role="group" aria-label="Ce que cet add-on sert">${pastillesRoles}</span>
          </span>
          <code>${esc([s.nom, s.hote, s.version ? `v${s.version}` : ''].filter(Boolean).join(' · '))}${pastille}</code>
          ${s.description ? `<em class="addon-detail">${esc(s.description)}</em>` : ''}
        </span>
        ${s.enabled === false ? `<button type="button" class="ms-retirer" data-ms-rallumer="${esc(s.id)}" title="Désactivée : elle ne sert plus rien">Réactiver</button>` : ''}
        <button type="button" class="ms-retirer" data-retirer="${esc(s.id)}">Retirer</button>
      </div>`;
    }).join('')
    : '<p class="ms-vide">Tu n\'as branché aucune source.</p>';

  // Renommer et etiqueter : au `change`, comme dans l'ecran d'administration.
  // La liste ne se repeint pas — elle emporterait le champ en cours de saisie.
  const modifier = async (id, patch, champ) => {
    try {
      await api(`/api/mes-sources/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) });
      champ.classList.remove('en-faute');
      calmerLeSouci();
    } catch (err) {
      champ.classList.add('en-faute');
      souciDeSource(err.message);
    }
  };
  for (const el of liste.querySelectorAll('[data-ms-nom]')) {
    el.onchange = () => modifier(el.dataset.msNom, { label: el.value }, el);
    el.onkeydown = (e) => { if (e.key === 'Enter') el.blur(); };
  }
  for (const el of liste.querySelectorAll('[data-ms-role]')) {
    el.onclick = async () => {
      const carte = el.closest('.ms-source');
      const tenus = [...(carte?.querySelectorAll('[data-ms-role][aria-pressed="true"]') ?? [])]
        .map((b) => b.dataset.msRole);
      const role = el.dataset.msRole;
      const suite = tenus.includes(role) ? tenus.filter((r) => r !== role) : [...tenus, role];
      // ⚠️ LE SERVEUR REFUSE DE TOUT DECOCHER, ET IL A RAISON : un add-on sans
      // role reste affiche sans plus rien servir. On ne fait donc pas semblant
      // de l'accepter ici — on laisse le refus remonter, avec son message.
      el.disabled = true;
      await modifier(el.dataset.msId, { roles: suite }, el);
      el.disabled = false;
      chargerMesSources();
    };
  }

  for (const el of liste.querySelectorAll('[data-ms-rallumer]')) {
    el.onclick = async () => {
      el.disabled = true;
      await modifier(el.dataset.msRallumer, { enabled: true }, el);
      chargerMesSources();
    };
  }

  for (const el of liste.querySelectorAll('[data-retirer]')) {
    el.onclick = async () => {
      el.disabled = true;
      texte('ms-etat', 'Retrait…');
      try {
        await api(`/api/mes-sources/${encodeURIComponent(el.dataset.retirer)}`, { method: 'DELETE' });
        // On relit TOUT plutôt que de retirer la ligne sur place : les trois
        // nombres du bilan changent avec le retrait, et c'est le serveur qui
        // sait ce que le lecteur interrogera vraiment ensuite.
        calmerLeSouci();
        chargerMesSources();
      } catch (err) {
        el.disabled = false;
        souciDeSource(err.message);
      }
    };
  }
}

/** Un refus se lit en rouge et ne s'efface pas tout seul : il attend qu'on agisse. */
function souciDeSource(message) {
  const el = $('ms-etat');
  if (!el) return;
  el.textContent = message;
  el.classList.add('ms-souci');
}

function calmerLeSouci(message = '') {
  const el = $('ms-etat');
  if (!el) return;
  el.textContent = message;
  el.classList.remove('ms-souci');
}

async function chargerMesSources() {
  const bilan = $('ms-bilan');
  if (!bilan) return;
  try {
    peindreMesSources(await api('/api/mes-sources'));
  } catch (err) {
    bilan.textContent = err.message || 'Tes sources sont illisibles.';
    texte('ms-explication', '');
  }
}

function preparerMesSources() {
  surClic('ms-retour', quitterLesReglages);

  surClic('ms-ajouter', async () => {
    const champ = $('ms-manifest');
    const manifest = champ ? champ.value.trim() : '';
    if (!manifest) return souciDeSource("Il manque l'adresse du manifeste.");

    // ⚠️ ON VALIDE ICI AUSSI, ET CE N'EST PAS UNE REDITE DU SERVEUR.
    //
    // Le serveur refuse de toute façon — c'est lui l'autorité, et la page ne
    // peut pas la lui prendre. Mais un aller-retour pour apprendre qu'il
    // manque `/manifest.json` à la fin de l'adresse, c'est une seconde perdue
    // et un doute (« est-ce que ça a marché ? ») là où la réponse était
    // immédiate. Les mêmes trois règles que `validateManifest`, dans le même
    // ordre, avec les mêmes mots : deux messages différents pour un même refus
    // feraient croire à deux problèmes différents.
    const souci = souciDeLAdresse(manifest);
    if (souci) return souciDeSource(souci);

    calmerLeSouci('Ajout…');
    try {
      await api('/api/mes-sources', {
        method: 'POST',
        body: JSON.stringify({ manifest, langue: $('ms-langue')?.value || '' }),
      });
      if (champ) champ.value = '';
      calmerLeSouci('Branchée.');
      chargerMesSources();
    } catch (err) {
      souciDeSource(err.message);
    }
  });

  // Retaper après un refus efface le refus : le message d'erreur porte sur ce
  // qui était écrit, pas sur ce qu'on est en train d'écrire.
  const champ = $('ms-manifest');
  if (champ) {
    champ.oninput = () => calmerLeSouci();
    champ.onkeydown = (e) => { if (e.key === 'Enter') $('ms-ajouter')?.click(); };
  }
}

/**
 * Le même refus que `validateManifest` d'`access.js`, dit avant l'aller-retour.
 *
 * Trois règles, trois phrases distinctes : « invalide » n'apprend rien à
 * personne, et c'est justement la phrase qu'on lit quand on ne sait pas quoi
 * corriger.
 */
function souciDeLAdresse(url) {
  let lue;
  try {
    lue = new URL(url);
  } catch {
    return "Ce n'est pas une adresse : il faut une URL complète, https:// compris.";
  }
  if (!/^https?:$/.test(lue.protocol)) {
    return `Seuls http et https sont acceptés — celle-ci commence par « ${lue.protocol.replace(':', '')} ».`;
  }
  if (!lue.pathname.endsWith('/manifest.json')) {
    return "L'adresse doit se terminer par /manifest.json — c'est le fichier que l'add-on publie.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// CE QUE J'AI PRÊTÉ — qui tient un accès de moi, et jusqu'à quand
//
// ⚠️ « CE SOIR » ET « POUR DE BON » NE DOIVENT PAS SE RESSEMBLER.
//
// C'est toute la différence entre le geste courant — « viens regarder ce soir »,
// qui n'engage rien parce qu'il s'éteint tout seul — et la décision qu'on prend
// exprès. Sur une liste de douze lignes, personne ne relit douze dates : le
// filet de gauche est pointillé pour ce qui expire, plein et blanc pour ce qui
// dure, et l'étiquette de droite s'inverse. On sait ce qu'on regarde avant
// d'avoir lu un mot.
//
// « Affranchir » n'apparaît que sur les accès temporaires : proposer d'affranchir
// ce qui l'est déjà ferait douter de ce qu'on lit.
// ---------------------------------------------------------------------------

const jourEtHeure = (ms) => dateFr(ms, {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});
const heureSeule = (ms) => dateFr(ms, { hour: '2-digit', minute: '2-digit' });

/** L'échéance, dite comme on la dirait : l'heure si c'est pour bientôt, la date sinon. */
function motDeLEcheance(jusqu) {
  const reste = jusqu - Date.now();
  if (reste <= 0) return 'expiré';
  if (reste < 12 * 3600_000) return `jusqu'à ${heureSeule(jusqu)}`;
  return `jusqu'au ${jourEtHeure(jusqu)}`;
}

async function chargerMesPrets() {
  const bloc = $('pr-bloc');
  if (!bloc) return;
  bloc.hidden = false;

  const liste = $('pr-liste');
  if (!liste) return;
  liste.textContent = 'Lecture des accès prêtés…';
  texte('pr-etat', '');

  let donnees;
  try {
    donnees = await api('/api/mes-prets');
  } catch (err) {
    liste.textContent = err.message || 'Les accès prêtés sont illisibles.';
    return;
  }

  const prets = donnees.prets ?? [];
  const noms = donnees.noms ?? {};
  texte('pr-etat', prets.length
    ? `${prets.length} accès en cours`
    : '');

  if (!prets.length) {
    liste.innerHTML = '<p class="pr-vide">Personne ne tient d\'accès de toi en ce moment.</p>';
    return;
  }

  liste.innerHTML = prets.map((p) => `<div class="pr-ligne" data-permanent="${p.permanent ? 1 : 0}">
      <span class="pr-qui">
        <strong>${esc(noms[p.invite] ?? p.invite)}</strong>
        <small>${p.depuis ? `depuis le ${esc(jourEtHeure(p.depuis))}` : 'depuis une date inconnue'}</small>
      </span>
      <span class="pr-duree">${p.permanent ? 'Pour de bon' : esc(motDeLEcheance(p.jusqu))}</span>
      <span class="pr-gestes">
        ${p.permanent ? '' : `<button type="button" class="ghost" data-affranchir="${esc(p.invite)}">Affranchir</button>`}
        <button type="button" class="ghost" data-reprendre="${esc(p.invite)}">Reprendre</button>
      </span>
    </div>`).join('');

  // ⚠️ AUCUN DES DEUX GESTES N'ENVOIE LE NOM DU PRÊTEUR. Il vient du jeton,
  // côté serveur, et le serveur refuse tout ce qui n'a pas été prêté par la
  // session qui appelle. Une page ne décide pas de qui elle parle.
  const agir = async (el, chemin, options, motDAttente) => {
    for (const autre of liste.querySelectorAll('button')) autre.disabled = true;
    texte('pr-etat', motDAttente);
    try {
      await api(chemin, options);
      chargerMesPrets();
    } catch (err) {
      texte('pr-etat', err.message);
      for (const autre of liste.querySelectorAll('button')) autre.disabled = false;
    }
  };

  for (const el of liste.querySelectorAll('[data-affranchir]')) {
    el.onclick = () => agir(el,
      `/api/mes-prets/${encodeURIComponent(el.dataset.affranchir)}/affranchir`,
      { method: 'POST' }, 'Affranchissement…');
  }
  for (const el of liste.querySelectorAll('[data-reprendre]')) {
    el.onclick = () => agir(el,
      `/api/mes-prets/${encodeURIComponent(el.dataset.reprendre)}`,
      { method: 'DELETE' }, 'Reprise…');
  }
}

/**
 * Rechargement de l'interface depuis la section admin.
 *
 * Dans l'activité Discord, recharger demande de fermer puis rouvrir : plusieurs
 * gestes pour vérifier un déploiement. Un bouton suffit. L'adresse reçoit un
 * jeton d'horodatage pour que rien ne soit servi depuis le cache.
 */
/**
 * Journal des lectures.
 *
 * Les lignes viennent des deux côtés — serveur et client — sous un même
 * identifiant. Les étapes qui parlent d'échec sont teintées : dans une
 * chronologie de vingt lignes, c'est la seule qu'on cherche.
 */
async function chargerJournal() {
  const bloc = $('bloc-journal');
  if (!bloc) return;
  bloc.hidden = false;

  const liste = $('liste-journal');
  liste.textContent = 'Lecture du journal…';
  let donnees;
  try {
    donnees = await api('/api/admin/journal');
  } catch (err) {
    liste.textContent = err.message || 'Journal illisible.';
    return;
  }

  texte('journal-etat', `${donnees.traces.length} lecture(s)`);
  liste.textContent = '';
  if (!donnees.traces.length) {
    liste.textContent = 'Aucune lecture enregistrée. Lance un épisode, puis relis.';
    return;
  }

  for (const [rang, trace] of donnees.traces.entries()) {
    const bloc_ = document.createElement('details');
    bloc_.className = 'trace';
    if (rang === 0) bloc_.open = true; // la dernière est celle qu'on vient de faire

    const tete = document.createElement('summary');
    const depart = trace.lignes.find((l) => l.ou === 'depart' || l.ou === 'client:depart');
    const titre = document.createElement('b');
    titre.textContent = depart?.message ?? trace.trace;
    const quand = document.createElement('span');
    quand.className = 'quand';
    const ecoule = Math.round((Date.now() - trace.debut) / 1000);
    quand.textContent = `${trace.duree} ms · il y a ${ecoule < 60 ? `${ecoule} s` : `${Math.round(ecoule / 60)} min`}`;
    tete.append(titre, quand);

    const lignes = document.createElement('div');
    lignes.className = 'lignes';
    for (const l of trace.lignes) {
      const ligne = document.createElement('div');
      ligne.className = 'ligne';
      if (/echec|erreur|impossible|refus|ecarte|expir|injoignable|indisponible|aucun/i.test(l.message)) {
        ligne.classList.add('alerte');
      }

      const ms = document.createElement('span');
      ms.className = 'ms';
      ms.textContent = `+${l.ms} ms`;

      const ou = document.createElement('span');
      ou.className = 'ou';
      ou.textContent = l.ou;

      const quoi = document.createElement('span');
      quoi.className = 'quoi';
      quoi.textContent = l.message;
      if (l.detail) {
        const petit = document.createElement('small');
        petit.textContent = l.detail;
        quoi.appendChild(petit);
      }

      ligne.append(ms, ou, quoi);
      lignes.appendChild(ligne);
    }

    bloc_.append(tete, lignes);
    liste.appendChild(bloc_);
  }
}

function preparerJournal() {
  surClic('journal-relire', chargerJournal);
  surClic('journal-vider', async () => {
    texte('journal-etat', 'nettoyage…');
    try {
      await api('/api/admin/journal', { method: 'DELETE' });
      await chargerJournal();
    } catch (err) {
      texte('journal-etat', err.message);
    }
  });
  chargerJournal();
}

// ---------------------------------------------------------------------------
// Premiere visite
//
// Trois ecrans, pas un de plus : de quoi comprendre qu'on regarde ensemble,
// dire dans quelle langue, et savoir ou retrouver le reste. Tout est deja
// reglable dans la pop-up — ce guide ne fait que montrer le chemin une fois.
//
// Il se declenche a l'absence de la marque, pas a sa presence : une nouvelle
// version ne doit pas rejouer l'accueil a des gens qui connaissent la maison.
// ---------------------------------------------------------------------------
const MARQUE_GUIDE = 'guide-vu';

function peutEtreGuider() {
  const bloc = $('guide');
  if (!bloc || prefs.lire(MARQUE_GUIDE, '')) return;

  // ⚠️ ON NE FAIT PAS VISITER LA MAISON À QUELQU'UN QUI ARRIVE EN PLEINE FÊTE.
  //
  // Toji, capture à l'appui : « d'un côté on a le bienvenue, de l'autre côté on a
  // la séance en cours. Techniquement, ça, c'est pas bon. »
  //
  // Deux raisons de se taire, et elles ne se recouvrent pas :
  //
  //   · UNE SÉANCE EST EN COURS. On vient pour la rejoindre, pas pour lire une
  //     présentation. Le guide se pose alors par-dessus l'épisode qui démarre —
  //     deux écrans qui se disputent le même instant.
  //
  //   · LE COMPTE N'A PAS ENCORE PARLÉ. « Déjà vu » voyage avec le compte depuis
  //     que les réglages le suivent : au premier battement, il n'est pas encore
  //     arrivé, et le lire trop tôt revient à croire que TOUT LE MONDE débarque
  //     pour la première fois. C'est exactement ce qui se passait — le guide
  //     revenait à chaque appareil, à chaque navigateur, sur un compte qui l'avait
  //     déjà lu dix fois.
  //
  // On repousse plutôt que d'annuler : dès que le compte a répondu, la question
  // se repose. Quelqu'un qui découvre vraiment le service verra bien son guide,
  // une seconde plus tard, et une seule fois dans sa vie.
  if (etatSeance?.actif || etatSeance?.ouvrir) return;
  // En attente d'autorisation, rien ne s'ouvre : le guide masquait la sonnette.
  if (ecranCourant === 'v-attente') return;

  // ⚠️ TROISIÈME RAISON DE SE TAIRE : ON ADMINISTRE.
  //
  // Trouvée par le parcours automatique, au tout premier essai (16/09) : sur
  // `/admin`, la carte du guide se pose PAR-DESSUS la porte et intercepte les
  // clics. Playwright l'a dit mot pour mot — « <div role="dialog" class=
  // "guide-carte"> from <div id="guide"> subtree intercepts pointer events » —
  // après trente secondes à essayer d'appuyer sur « Entrer ».
  //
  // Ce n'est pas un défaut d'administration, c'est le même défaut que celui de
  // la séance en cours : « d'un côté on a le bienvenue, de l'autre côté on a la
  // séance en cours. Techniquement, ça, c'est pas bon. » Un guide qui explique
  // comment choisir sa langue de sous-titres n'a rien à dire à quelqu'un qui
  // vient rebrancher une source — et il l'empêche de le faire.
  //
  // ⚠️ ON REPOUSSE, ON N'ANNULE PAS : la marque « déjà vu » n'est PAS posée.
  // La poser ici priverait de son guide quelqu'un dont le tout premier geste
  // aurait été d'ouvrir l'administration — il ne le reverrait jamais. Il le
  // verra en revenant au lecteur, ce qui est l'endroit où il sert.
  const enAdministration = location.pathname === '/admin'
    || !document.getElementById('v-admin')?.hidden;
  if (enAdministration) return;

  if (!reglagesDuCompte.disponible) {
    setTimeout(peutEtreGuider, 1200);
    return;
  }

  const etapes = [...bloc.querySelectorAll('.guide-etape')];
  const points = $('guide-points');
  if (points) points.innerHTML = etapes.map(() => '<i></i>').join('');
  const pastilles = [...(points?.children ?? [])];

  let courante = 0;
  const peindre = () => {
    etapes.forEach((e, i) => { e.hidden = i !== courante; });
    pastilles.forEach((p, i) => p.classList.toggle('actif', i === courante));
    texte('guide-suivant', courante === etapes.length - 1 ? "C'est parti" : 'Continuer');
    const passer = $('guide-passer');
    // Au dernier ecran, « Passer » n'a plus de sens : il n'y a plus rien a
    // passer, et deux boutons qui font la meme chose sement le doute. Il
    // s'efface sans se retirer, pour que le pied ne saute pas.
    if (passer) passer.classList.toggle('invisible', courante === etapes.length - 1);
  };

  const fermer = () => {
    prefs.ecrire(MARQUE_GUIDE, '1');
    bloc.hidden = true;
  };

  // ⚠️ CE QU'ON REPOND ICI EST LE MEME REGLAGE QUE DANS LES REGLAGES.
  //
  // Chaque question du guide écrit la préférence que le segment correspondant
  // lit — et coche ce segment au passage. Sans ça, on répondrait à l'accueil
  // puis on trouverait la question à nouveau, non répondue, dans les réglages :
  // l'impression que l'onboarding n'a servi à rien.
  //
  // ⚠️ LA QUESTION « FILMS » A MAINTENANT SA CLÉ — défaut payé le 15/09.
  //
  // Elle écrivait `langue-serie`, comme la question « Séries » : répondre
  // « Séries : VOSTFR » puis « Films : VF » ne laissait qu'une réponse, la
  // dernière, pour les deux. L'écran posait donc une question dont il jetait la
  // réponse — et le commentaire qui était ici l'assumait (« la dernière réponse
  // tranche »), ce qui en faisait un défaut documenté plutôt qu'un défaut
  // réparé.
  //
  // `cleDeLangue('film')` rend `langue-film` dès que le SERVEUR la sert, et
  // `langue-serie` tant qu'il ne la sert pas : jamais une clé que `/api/reglages`
  // écarterait en silence, pour se voir remettre à `auto` au chargement suivant.
  // Le segment repeint suit la même clé, sinon l'écran des réglages montrerait
  // la réponse d'une question à côté de l'autre.
  //
  // ⚠️ DÉFAUT PAYÉ LE 14/09 : DEUX QUESTIONS SUR UNE CLÉ, UN SEUL GROUPE REPEINT.
  //
  // « Séries » et « Films » écrivent tous les deux `langue-serie` — c'est voulu,
  // voir juste au-dessus — mais `cocher` ne repeignait QUE les boutons du groupe
  // cliqué. Répondre « Séries : VOSTFR » puis « Films : VF » laissait donc
  // VOSTFR allumé sous « Séries » alors que la valeur enregistrée venait de
  // passer à VF : l'écran montrait deux réponses là où il n'en restait qu'une,
  // et c'est l'écran qu'on croit. On ne le répare pas question par question —
  // un registre par CLÉ le ferme une fois pour toutes : qui écrit une clé
  // repeint tout ce qui est câblé dessus, groupes du guide et segments des
  // réglages compris.
  const groupesParCle = new Map();
  const lierQuestion = (idGuide, cle, defaut, idSegment = null) => {
    const boutons = [...bloc.querySelectorAll(`#${idGuide} button[data-v]`)];
    if (!boutons.length) return;
    if (!groupesParCle.has(cle)) groupesParCle.set(cle, []);
    groupesParCle.get(cle).push({ boutons, idSegment });
    const cocher = (valeur) => {
      // TOUTES les entrées de cette clé, pas seulement celle qu'on vient de
      // cliquer : c'est exactement le défaut décrit au-dessus.
      for (const entree of groupesParCle.get(cle) ?? []) {
        for (const b of entree.boutons) b.classList.toggle('actif', b.dataset.v === valeur);
        for (const s of $(entree.idSegment)?.querySelectorAll('button[data-v]') ?? []) {
          s.classList.toggle('actif', s.dataset.v === valeur);
        }
      }
    };
    for (const b of boutons) {
      b.addEventListener('click', () => {
        prefs.ecrire(cle, b.dataset.v);
        cocher(b.dataset.v);
      });
    }
    cocher(prefs.lire(cle, defaut));
  };

  const cleFilm = cleDeLangue('film');
  lierQuestion('guide-langue-serie', 'langue-serie', 'auto', 'r-langue-serie');
  lierQuestion('guide-langue-film', cleFilm, 'auto', `r-${cleFilm}`);
  lierQuestion('guide-langue-anime', 'langue-anime', 'auto', 'r-langue-anime');
  lierQuestion('guide-dispo', 'dispo-seulement', 'non', 'r-dispo');

  // ⚠️ LA QUESTION ET LA PREFERENCE SONT A L'ENVERS L'UNE DE L'AUTRE.
  //
  // On demande « des sous-titres ? — oui », et la préférence que le lecteur
  // consulte s'appelle `st-sans`, « sans sous-titres ». Répondre « oui » en
  // écrivant la réponse telle quelle les aurait coupés. Cette question ne passe
  // donc pas par `lierQuestion`, qui recopie la valeur du bouton.
  const boutonsST = [...bloc.querySelectorAll('#guide-soustitres button[data-v]')];
  const cocherST = () => {
    const veut = prefs.lire('st-sans', 'non') === 'oui' ? 'non' : 'oui';
    for (const b of boutonsST) b.classList.toggle('actif', b.dataset.v === veut);
  };
  for (const b of boutonsST) {
    b.addEventListener('click', () => {
      prefs.ecrire('st-sans', b.dataset.v === 'non' ? 'oui' : 'non');
      cocherST();
    });
  }
  cocherST();

  // Toji : « si elle veut faire des réglages dédiés, elle a un bouton pour
  // ouvrir une nouvelle page dédiée à la gestion des sous-titres ». Le guide se
  // ferme — il a fait son travail — et l'onglet s'ouvre directement.
  surClic('guide-vers-soustitres', () => {
    prefs.ecrire(MARQUE_GUIDE, '1');
    bloc.hidden = true;
    vue('v-reglages');
    $('onglets-reglages')?.querySelector('[data-onglet="soustitres"]')?.click();
  });

  surClic('guide-suivant', () => {
    if (courante >= etapes.length - 1) return fermer();
    courante += 1;
    peindre();
  });
  surClic('guide-passer', fermer);

  peindre();
  bloc.hidden = false;
}

// ---------------------------------------------------------------------------
// Diagnostic d'un épisode
//
// Quand quelqu'un dit « la VF me sert du japonais », il n'y a aujourd'hui aucun
// moyen de savoir OÙ ça s'est joué : dans ce que l'add-on a renvoyé, dans le
// tri, ou dans le nom du fichier qui ment. Vues du lecteur, ces trois choses
// se ressemblent. Cet écran les sépare.
// ---------------------------------------------------------------------------
function preparerDiagnostic() {
  const bloc = $('bloc-diagnostic');
  if (!bloc) return;
  const dire = (m) => texte('diag-etat', m);
  const zone = $('diag-resultat');

  const dessiner = (d) => {
    const pastille = (c) => [
      c.annonce.vf ? '<span class="diag-tag vf">VF</span>' : '',
      c.annonce.vostfr ? '<span class="diag-tag vostfr">VOSTFR</span>' : '',
      c.annonce.cache ? '<span class="diag-tag cache">cache</span>' : '',
      c.annonce.lisibleTelQuel ? '<span class="diag-tag">sans conversion</span>' : '',
    ].join('');

    const classeNote = (n) => (n >= 60 ? 'bon' : n > 0 ? 'faible' : 'nul');

    // Ce que ffprobe a lu dans le fichier, quand on le lui a demandé. Le
    // verdict d'abord et en évidence : c'est la seule ligne qu'on lit vraiment,
    // et une contradiction entre le nom et le contenu doit sauter aux yeux.
    const reel = (c) => {
      if (!c.reel) return '<span class="muted">—</span>';
      if (c.reel.erreur) return `<span class="muted">${esc(c.reel.erreur)}</span>`;
      const contradit = /mais/.test(c.reel.verdict ?? '');
      const detail = (c.reel.pistes ?? [])
        .map((p) => `${esc(p.langue ?? '?')}${p.codec ? ` ${esc(p.codec)}` : ''}${p.canaux ? ` ${p.canaux}ch` : ''}`)
        .join(' · ');
      return `<span class="diag-verdict ${contradit ? 'contredit' : 'confirme'}">${esc(c.reel.verdict)}</span>`
        + (detail ? `<br><span class="muted">${detail}</span>` : '');
    };
    const horloge = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '—');

    zone.innerHTML = `
      <div class="diag-bloc">
        <b>Ce qu'on sait</b>
        <div class="diag-resume">
          <span>épisode <b>${esc(d.catalogue?.titre ?? '—')}</b></span>
          <span>rang absolu <b>${d.absolu || '—'}</b></span>
          <span>série récente <b>${d.serieRecente ? 'oui' : 'non'}</b></span>
          <span>durée <b>${d.fichier?.duree ? horloge(d.fichier.duree) : 'inconnue'}</b></span>
          <span>audio <b>${esc((d.fichier?.audio ?? []).join(' + ') || '—')}</b></span>
          <span>aperçu <b>${d.apercu ? `${d.apercu.total} vignettes` : 'aucun'}</b></span>
          <span>générique <b>${d.bornes ? `${d.bornes.intro?.debut ?? '—'}s (${d.bornes.source})` : 'inconnu'}</b></span>
        </div>
      </div>

      <div class="diag-bloc">
        <b>Ce que les add-ons ont répondu</b>
        <table class="diag-table">
          <tr><th>Source</th><th>Flux</th><th>Temps</th><th></th></tr>
          ${d.sources.map((s) => `<tr>
            <td>${esc(s.source)}</td><td>${s.flux}</td><td>${s.ms} ms</td>
            <td class="muted">${esc(s.erreur ?? '')}</td></tr>`).join('')}
        </table>
      </div>

      <div class="diag-bloc">
        <b>Ce que le tri en a fait — ${d.tri.retenus} retenus sur ${d.tri.total}${d.tri.correspondanceFranche ? '' : ', aucune correspondance franche'}</b>
        <table class="diag-table">
          <tr><th>Note</th><th>Fichier</th><th>Ce que le nom annonce</th><th>Ce que le fichier contient</th></tr>
          ${d.candidats.map((c) => `<tr>
            <td class="diag-note ${classeNote(c.note)}">${c.note}</td>
            <td class="diag-fichier">${esc(c.fichier ?? c.nom ?? '—')}<br><span class="muted">${esc(c.source)}</span></td>
            <td>${pastille(c)}</td>
            <td>${reel(c)}</td></tr>`).join('')}
        </table>
      </div>

      ${d.balayage ? `<div class="diag-bloc">
        <b>Ce que le balayage avait retenu</b>
        <div class="diag-resume">
          <span>langues <b>${esc((d.balayage.langues ?? []).join(' + ') || '—')}</b></span>
          <span>en cache <b>${d.balayage.cache ? 'oui' : 'non'}</b></span>
          <span>sources <b>${d.balayage.sources ?? 0}</b></span>
        </div>
      </div>` : ''}`;
  };

  // `sonder` déclenche ffprobe sur les trois premiers candidats. C'est lent —
  // plusieurs secondes par fichier — donc c'est un second bouton, et non le
  // comportement par défaut : la question ordinaire (« pourquoi ce fichier ? »)
  // doit rester instantanée.
  const examiner = async (sonder) => {
    const brut = ($('diag-episode')?.value ?? '').trim();
    const m = /^(\d{1,2})\s*[x:.\-]\s*(\d{1,3})$/.exec(brut);
    if (!m) { dire('écris par exemple 17x3'); return; }

    dire(sonder ? 'lecture des pistes des fichiers… (plusieurs secondes)' : 'interrogation des add-ons…');
    zone.innerHTML = '';
    try {
      const id = serie?.id || document.body.dataset.defaultSerie;
      const d = await api(`/api/admin/diagnostic/${encodeURIComponent(id)}/${m[1]}/${m[2]}${sonder ? '?sonder=1' : ''}`);
      dessiner(d);
      dire(`${d.ms} ms`);
    } catch (err) {
      dire(err.message);
    }
  };

  surClic('diag-lancer', () => examiner(false));
  surClic('diag-sonder', () => examiner(true));
}

// ---------------------------------------------------------------------------
// Quel rôle veut dire quoi
//
// La liste vient de Discord, la correspondance du fichier local. Au premier
// passage on propose une correspondance devinée d'après les noms des rôles —
// « ST VF » pour les sous-titres français, « Japanais » pour la version
// originale. C'est une proposition, jamais une décision : rien ne s'enregistre
// sans un clic.
// ---------------------------------------------------------------------------
async function preparerGouts() {
  const bloc = $('liste-gouts');
  if (!bloc) return;
  const dire = (m) => texte('gouts-etat', m);

  let d;
  try {
    d = await api('/api/admin/gouts');
  } catch (err) {
    bloc.textContent = err.message || 'Les rôles du serveur sont illisibles.';
    return;
  }

  if (!d.roles?.length) {
    bloc.textContent = 'Aucun rôle lisible sur le serveur.';
    return;
  }

  const dessiner = (table) => {
    bloc.innerHTML = Object.entries(d.familles).map(([famille, f]) => `
      <div class="champ">
        <span class="etiquette">${esc(f.nom)}</span>
        ${f.choix.map((c) => `
          <label class="ligne-gout">
            <span>${esc(c.nom)}</span>
            <select data-role="${esc(c.role)}">
              <option value="">—</option>
              ${d.roles.map((r) => `<option value="${esc(r.id)}"${table[c.role] === r.id ? ' selected' : ''}>@${esc(r.nom)}</option>`).join('')}
            </select>
          </label>`).join('')}
      </div>`).join('');

    for (const champ of bloc.querySelectorAll('select[data-role]')) {
      champ.addEventListener('change', async () => {
        dire('enregistrement…');
        try {
          const { table: neuve } = await api('/api/admin/gouts', {
            method: 'PUT', body: JSON.stringify({ [champ.dataset.role]: champ.value }),
          });
          dessiner(neuve);
          dire('enregistré');
        } catch (err) {
          dire(err.message);
        }
      });
    }
  };

  dessiner(d.table ?? {});

  // La proposition ne s'applique que si on la demande : deviner et enregistrer
  // dans la foulée poserait des rôles que personne n'a validés.
  const bouton = $('gouts-proposer');
  if (bouton && d.proposition) {
    bouton.hidden = false;
    const lisible = Object.entries(d.proposition)
      .filter(([, id]) => id)
      .map(([, id]) => d.roles.find((r) => r.id === id)?.nom)
      .filter(Boolean);
    dire(lisible.length ? `proposition : ${lisible.join(', ')}` : 'aucune correspondance évidente');
    surClic('gouts-proposer', async () => {
      dire('enregistrement…');
      try {
        const { table } = await api('/api/admin/gouts', { method: 'PUT', body: JSON.stringify(d.proposition) });
        dessiner(table);
        bouton.hidden = true;
        dire('proposition appliquée');
      } catch (err) {
        dire(err.message);
      }
    });
  }
}

// ---------------------------------------------------------------------------
// Notifications de nouveaute
//
// Quatre reglages, et rien d'autre a saisir : les salons et les roles viennent
// du serveur Discord, on les choisit dans une liste. Recopier un identifiant a
// dix-huit chiffres est une source d'erreur qu'on peut simplement supprimer.
//
// Chaque changement s'enregistre seul, comme les cases de l'onglet Acces.
// ---------------------------------------------------------------------------
async function preparerNotifications() {
  const bloc = $('bloc-notifs');
  if (!bloc) return;

  const dire = (m) => texte('notif-etat', m);

  const [salons, roles] = await Promise.all([
    api('/api/admin/salons').then((d) => d.salons ?? []).catch(() => null),
    api('/api/admin/roles').then((d) => d.roles ?? []).catch(() => null),
  ]);

  // Sans liste, autant le dire : des selecteurs vides laisseraient croire que
  // le serveur n'a ni salon ni role.
  if (!salons || !roles) {
    dire('Les salons ou les rôles du serveur sont illisibles.');
    return;
  }

  const remplir = (id, entrees, vide) => {
    const champ = $(id);
    if (!champ) return;
    champ.innerHTML = '';
    champ.append(new Option(vide, ''));
    for (const e of entrees) champ.append(new Option(e.nom, e.id));
  };

  remplir('notif-salon', salons.map((s) => ({ id: s.id, nom: s.categorie ? `${s.categorie} › #${s.nom}` : `#${s.nom}` })), '—');
  remplir('notif-salon-choix', salons.map((s) => ({ id: s.id, nom: s.categorie ? `${s.categorie} › #${s.nom}` : `#${s.nom}` })), '—');
  remplir('notif-role-vo', roles.map((r) => ({ id: r.id, nom: `@${r.nom}` })), 'Personne');
  remplir('notif-role-vf', roles.map((r) => ({ id: r.id, nom: `@${r.nom}` })), 'Personne');

  const champs = { salon: 'notif-salon', roleVo: 'notif-role-vo', roleVf: 'notif-role-vf', salonChoix: 'notif-salon-choix' };

  const peindreVeille = (v) => {
    if (!v) return;
    const morceaux = [];
    morceaux.push(v.active ? 'Veille en marche' : 'Veille arrêtée');
    // Tant que l'amorcage n'a pas eu lieu, rien ne partira : le dire evite
    // d'attendre une annonce qui, par construction, n'arrivera pas.
    if (!v.amorce) morceaux.push('la première passe ne fait que photographier l’existant');
    else morceaux.push(`${v.journal} épisode(s) déjà connus`);
    if (v.derniereAnnonce) morceaux.push(`dernière annonce ${dateFr(v.derniereAnnonce, null, true)}`);
    texte('notif-veille', morceaux.join(' · '));
  };

  const appliquer = (n) => {
    for (const [cle, id] of Object.entries(champs)) {
      const champ = $(id);
      if (champ) champ.value = n[cle] ?? '';
    }
    const groupe = $('notif-actif');
    for (const b of groupe?.querySelectorAll('button[data-v]') ?? []) {
      b.classList.toggle('actif', b.dataset.v === (n.actif ? 'oui' : 'non'));
    }
  };

  const enregistrer = async (patch) => {
    dire('enregistrement…');
    try {
      const { notifications, veille } = await api('/api/admin/notifications', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      appliquer(notifications);
      peindreVeille(veille);
      dire('enregistré');
    } catch (err) {
      dire(err.message);
    }
  };

  try {
    const { notifications, veille } = await api('/api/admin/notifications');
    appliquer(notifications);
    peindreVeille(veille);
  } catch (err) {
    dire(err.message);
  }

  for (const [cle, id] of Object.entries(champs)) {
    $(id)?.addEventListener('change', (e) => enregistrer({ [cle]: e.target.value }));
  }
  for (const b of $('notif-actif')?.querySelectorAll('button[data-v]') ?? []) {
    b.addEventListener('click', () => enregistrer({ actif: b.dataset.v === 'oui' }));
  }

  surClic('notif-essai', async () => {
    dire('envoi…');
    try {
      const r = await api('/api/admin/notifications/essai', { method: 'POST', body: JSON.stringify({ langue: 'vo' }) });
      dire(r.ok ? 'message envoyé' : `refusé : ${r.raison}${r.detail ? ` — ${r.detail}` : ''}`);
    } catch (err) {
      dire(err.message);
    }
  });

  surClic('notif-passe', async () => {
    dire('relevé en cours…');
    try {
      const b = await api('/api/admin/notifications/passe', { method: 'POST' });
      dire(b.amorce
        ? `photographie prise : ${b.ignorees} épisode(s) retenus, rien d’annoncé`
        : `${b.annoncees} annonce(s)${b.erreurs?.length ? ` · ${b.erreurs.length} échec(s)` : ''}`);
      const { veille } = await api('/api/admin/notifications');
      peindreVeille(veille);
    } catch (err) {
      dire(err.message);
    }
  });

  surClic('notif-oubli', async () => {
    // Geste sans retour : la prochaine passe ne rattrapera pas l'arriere, elle
    // le classera comme deja vu. Mieux vaut une question qu'un regret.
    if (!confirm('Oublier tout ce qui a déjà été annoncé ?\n\nLa prochaine passe repartira d’une page blanche : elle photographiera l’existant sans rien annoncer.')) return;
    dire('remise à zéro…');
    try {
      await api('/api/admin/notifications/journal', { method: 'DELETE' });
      const { veille } = await api('/api/admin/notifications');
      peindreVeille(veille);
      dire('journal vidé');
    } catch (err) {
      dire(err.message);
    }
  });
}

function preparerMaintenance() {
  const bloc = $('bloc-maintenance');
  if (!bloc) return;
  bloc.hidden = false;

  // La version du bundle est déjà dans l'adresse du script : elle change à
  // chaque déploiement, donc elle dit tout de suite si l'on regarde du neuf.
  const script = document.querySelector('script[src*="app.js"]');
  const version = script ? new URL(script.src, location.href).searchParams.get('v') : null;
  texte('version-interface', version ? `version ${version}` : '');

  surClic('recharger-interface', () => {
    texte('version-interface', 'rechargement…');
    // Un jeton d'horodatage dans l'adresse : rien ne doit être servi depuis
    // le cache, c'est justement ce qu'on vient vérifier.
    const u = new URL(location.href);
    u.searchParams.set('r', Date.now().toString(36));
    location.replace(u.toString());
  });
}

// ---------------------------------------------------------------------------
// L'ECRAN DES ACCES : LES SERVEURS, PUIS LEURS ROLES
//
// Toji : « je veux qu'on ait la liste des serveurs, après je peux cliquer
// directement sur un des serveurs, j'ai la liste des rôles, je peux cliquer
// Maintien pour en sélectionner plusieurs et faire des réglages multiples pour
// tous » — et « c'est un peu le bordel avec les quatre boutons à sélectionner à
// chaque fois ».
//
// Ce qu'il y avait : UN serveur, celui de la configuration, et quatre cases par
// ligne. Régler dix rôles demandait quarante clics, et les rôles des autres
// serveurs étaient inatteignables alors que `allowedGuilds` en accepte plusieurs
// depuis toujours.
//
// Ce qu'il y a : on choisit un serveur, on sélectionne des rôles — clic, Ctrl
// pour ajouter, Maj pour une plage, comme partout ailleurs — et les quatre
// boutons agissent UNE fois sur toute la sélection.
// ---------------------------------------------------------------------------
let serveurCourant = null;
let rolesAffiches = [];
const rolesChoisis = new Set();
let dernierClicRole = -1;
// Ouvert par un appui long, refermé quand la sélection se vide.
let modeSelectionRoles = false;

/** Ce que porte un rôle, en clair — plutôt que quatre cases à déchiffrer. */
function resumeDesDroits(role, colonnes) {
  const actifs = colonnes.filter((c) => role.droits?.[c.cle]).map((c) => c.nom);
  return actifs.length ? actifs.join(' · ') : 'aucun droit';
}

function peindreBarreDesRoles(colonnes) {
  const barre = $('barre-roles');
  if (!barre) return;
  barre.hidden = rolesChoisis.size === 0;
  if (barre.hidden) return;

  texte('roles-compte', `${rolesChoisis.size} ${pluriel(rolesChoisis.size, 'rôle')} ${pluriel(rolesChoisis.size, 'sélectionné')}`);
  const actions = $('roles-actions');
  if (!actions) return;

  // Deux boutons par droit — donner, retirer — plutôt qu'une case à trois états
  // pour une sélection où les rôles ne sont pas d'accord entre eux. Ce qu'on
  // demande est alors sans ambiguïté, et c'est ce qui compte quand l'action
  // porte sur dix rôles d'un coup.
  actions.innerHTML = colonnes.map((c) => `<button type="button" data-donne="${esc(c.cle)}">+ ${esc(c.nom)}</button>`
    + `<button type="button" class="ghost" data-retire="${esc(c.cle)}">− ${esc(c.nom)}</button>`).join('');

  const appliquer = async (cle, valeur) => {
    const ids = [...rolesChoisis];
    texte('role-etat', 'enregistrement…');
    try {
      await api('/api/admin/roles', {
        method: 'PUT',
        body: JSON.stringify({ roles: ids, droits: { [cle]: valeur } }),
      });
      texte('role-etat', `${ids.length} ${pluriel(ids.length, 'rôle')} mis à jour`);
      // Le serveur fait foi : « Section admin » allume les trois autres, et
      // l'écran doit le montrer sans qu'on ait à recharger.
      await ouvrirServeur(serveurCourant, { garderSelection: true });
    } catch (err) {
      texte('role-etat', err.message);
    }
  };
  for (const b of actions.querySelectorAll('[data-donne]')) b.onclick = () => appliquer(b.dataset.donne, true);
  for (const b of actions.querySelectorAll('[data-retire]')) b.onclick = () => appliquer(b.dataset.retire, false);
}

function peindreRoles(colonnes) {
  const bloc = $('liste-acces');
  if (!bloc) return;
  bloc.innerHTML = rolesAffiches.map((role, i) => `<div class="role-ligne${rolesChoisis.has(role.id) ? ' choisi' : ''}" data-role="${esc(role.id)}" data-rang="${i}">
      <span class="role-pastille"${role.couleur ? ` style="background:${esc(role.couleur)}"` : ''}></span>
      <span class="role-nom">${esc(role.nom)}</span>
      <span class="role-droits">${esc(resumeDesDroits(role, colonnes))}</span>
    </div>`).join('');

  for (const ligne of bloc.querySelectorAll('[data-role]')) {
    const rang = Number(ligne.dataset.rang);
    const id = ligne.dataset.role;

    const etendreJusqua = (jusqua) => {
      const [a, b] = [Math.min(dernierClicRole, jusqua), Math.max(dernierClicRole, jusqua)];
      for (let i = a; i <= b; i += 1) rolesChoisis.add(rolesAffiches[i].id);
    };

    // ⚠️ LE CLIC MAINTENU OUVRE LE MODE SELECTION.
    //
    // Toji : « je clique sur l'un et je maintiens, ça me met en mode sélection
    // et ça entoure l'élément ; je maintiens Maj et je clique sur un autre
    // rôle, et ça sélectionne tout ce qui est entre les deux ».
    //
    // Ctrl+clic faisait déjà la même chose, mais il faut le savoir, il n'existe
    // pas au doigt, et rien à l'écran ne l'annonçait. L'appui long, lui,
    // s'essaie : c'est le geste qu'on tente quand on veut « prendre » quelque
    // chose. Une fois le mode ouvert, un clic simple ajoute ou retire au lieu
    // de tout remettre à zéro — sinon on perdrait sa sélection au premier clic
    // de trop.
    let minuteurAppui = null;
    const annuler = () => { clearTimeout(minuteurAppui); minuteurAppui = null; };
    ligne.addEventListener('pointerdown', () => {
      minuteurAppui = setTimeout(() => {
        minuteurAppui = null;
        modeSelectionRoles = true;
        rolesChoisis.add(id);
        dernierClicRole = rang;
        peindreRoles(colonnes);
      }, 350);
    });
    for (const fin of ['pointerup', 'pointerleave', 'pointercancel']) ligne.addEventListener(fin, annuler);

    ligne.onclick = (e) => {
      // L'appui long vient de sélectionner : le clic qui le suit ne doit pas
      // défaire ce qu'on vient de faire.
      if (rolesChoisis.has(id) && modeSelectionRoles && dernierClicRole === rang && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        return;
      }
      if (e.shiftKey && dernierClicRole >= 0) {
        etendreJusqua(rang);
      } else if (e.ctrlKey || e.metaKey || modeSelectionRoles) {
        if (rolesChoisis.has(id)) rolesChoisis.delete(id); else rolesChoisis.add(id);
        dernierClicRole = rang;
        // Vider la sélection referme le mode : le contour disparaît, et un clic
        // redevient un clic.
        if (!rolesChoisis.size) modeSelectionRoles = false;
      } else {
        const seul = rolesChoisis.size === 1 && rolesChoisis.has(id);
        rolesChoisis.clear();
        if (!seul) rolesChoisis.add(id);
        dernierClicRole = rang;
      }
      peindreRoles(colonnes);
    };
  }
  peindreBarreDesRoles(colonnes);
}

async function ouvrirServeur(guildId, { garderSelection = false } = {}) {
  if (!guildId) return;
  serveurCourant = guildId;
  if (!garderSelection) { rolesChoisis.clear(); dernierClicRole = -1; }

  for (const carte of document.querySelectorAll('[data-serveur]')) {
    carte.classList.toggle('actif', carte.dataset.serveur === guildId);
  }

  const bloc = $('liste-acces');
  if (bloc && !garderSelection) bloc.textContent = 'Lecture des rôles…';
  try {
    const donnees = await api(`/api/admin/roles?guild=${encodeURIComponent(guildId)}`);
    // Deux clics rapides, deux réponses : la seconde arrive parfois la
    // première. On écarte ce qui ne concerne plus le serveur affiché.
    if (donnees.guild && String(donnees.guild) !== String(serveurCourant)) return;
    rolesAffiches = donnees.roles ?? [];
    // Une sélection gardée ne doit pas désigner des rôles qui ne sont plus là.
    for (const id of [...rolesChoisis]) {
      if (!rolesAffiches.some((r) => r.id === id)) rolesChoisis.delete(id);
    }
    if (!rolesAffiches.length) {
      if (bloc) bloc.textContent = 'Aucun rôle sur ce serveur, en dehors de ceux que Discord gère lui-même.';
      peindreBarreDesRoles(donnees.colonnes ?? []);
      return;
    }
    peindreRoles(donnees.colonnes ?? []);
  } catch (err) {
    if (bloc) bloc.textContent = err.message || 'Les rôles de ce serveur sont illisibles.';
  }
}

// ---------------------------------------------------------------------------
// QUI A LE DROIT — TOUT AU MEME ENDROIT
//
// Toji : « quand on clique dans Accès, on a la vision sur les serveurs Discord,
// en dessous on a les membres supplémentaires avec un bouton Ajouter ».
//
// Ces trois listes vivaient dans l'onglet Admin, entre les add-ons et le
// balayage : on réglait les serveurs d'un côté, on autorisait les personnes de
// l'autre, et la même question — qui entre ? — se posait à deux endroits. Elles
// rejoignent les serveurs et les rôles, sous eux, dans l'ordre où l'on s'en
// sert : où chacun arrive, qui attend une réponse, qui est déjà nommé.
// ---------------------------------------------------------------------------
async function chargerQuiADroit() {
  let cfg;
  try {
    cfg = await api('/api/admin/access');
  } catch (err) {
    texte('invite-etat', err.message);
    return;
  }

  // Où chacun arrive. Trois lignes, trois menus : c'est la forme la plus courte
  // qui dise à la fois la population et sa destination.
  const peindreArrivee = (table) => {
    const zone = $('liste-arrivee');
    if (!zone) return;
    const POPULATIONS = [
      ['proprietaire', 'Moi (propriétaire)'],
      ['invite', 'Les invités du site'],
      // Plus de ligne « Depuis l'activité Discord » : l'activité arrive toujours
      // sur les séances (Toji, 21/10) — un menu sans effet serait une UI fausse.
    ];
    const ECRANS = [['accueil', 'Accueil'], ['catalogue', 'Catalogue'], ['seances', 'Les séances']];
    zone.innerHTML = POPULATIONS.map(([cle, nom]) => `<div class="addon">
        <span class="nom-addon"><strong>${esc(nom)}</strong></span>
        <select data-arrivee="${cle}">${ECRANS.map(([v, t]) => `<option value="${v}"${(table?.[cle] ?? '') === v ? ' selected' : ''}>${esc(t)}</option>`).join('')}</select>
      </div>`).join('');

    for (const el of zone.querySelectorAll('[data-arrivee]')) {
      el.onchange = async () => {
        texte('invite-etat', 'enregistrement…');
        try {
          const apres = await api('/api/admin/access', {
            method: 'PUT',
            body: JSON.stringify({ accueilPar: { ...table, [el.dataset.arrivee]: el.value } }),
          });
          texte('invite-etat', '');
          peindreArrivee(apres.accueilPar);
        } catch (err) { texte('invite-etat', err.message); }
      };
    }
  };

  // Les demandes en attente. Elles se peignent AVANT les invités : autoriser
  // fait passer une ligne de la première liste à la seconde, et les deux se
  // repeignent d'un coup avec ce que le serveur vient d'écrire.
  const peindreDemandes = (liste) => {
    const zone = $('liste-demandes');
    if (!zone) return;
    zone.innerHTML = liste.length
      ? liste.map((d) => `<div class="addon">
          <span class="nom-addon">
            <strong>${esc(d.pseudo || 'sans nom')}</strong>
            <code>${esc(d.id)}</code>
          </span>
          <button type="button" data-oui="${esc(d.id)}">Autoriser</button>
          <button type="button" class="ghost" data-non="${esc(d.id)}">Refuser</button>
        </div>`).join('')
      : '<p class="muted">Personne n’attend.</p>';

    const repeindre = (apres) => {
      peindreDemandes(apres.demandes ?? []);
      peindreInvites(apres.allowedUsers ?? []);
    };
    for (const el of zone.querySelectorAll('[data-oui]')) {
      el.onclick = async () => {
        el.disabled = true;
        try { repeindre(await api(`/api/admin/demandes/${el.dataset.oui}`, { method: 'POST', body: '{}' })); }
        catch (err) { texte('invite-etat', err.message); el.disabled = false; }
      };
    }
    for (const el of zone.querySelectorAll('[data-non]')) {
      el.onclick = async () => {
        el.disabled = true;
        try { repeindre(await api(`/api/admin/demandes/${el.dataset.non}`, { method: 'DELETE' })); }
        catch (err) { texte('invite-etat', err.message); el.disabled = false; }
      };
    }
  };

  // Les invités nommés. Le serveur fait foi : chaque geste renvoie la liste
  // telle qu'elle est APRÈS écriture, et c'est elle qu'on repeint — un
  // identifiant refusé (mal recopié) disparaît donc de lui-même de l'écran.
  const peindreInvites = (liste) => {
    const zone = $('liste-invites');
    if (!zone) return;
    zone.innerHTML = liste.length
      ? liste.map((u) => `<div class="addon">
          <span class="nom-addon"><code>${esc(u)}</code></span>
          <button type="button" class="ghost" data-invite="${esc(u)}">Retirer</button>
        </div>`).join('')
      : '<p class="muted">Personne d\'invité — seuls les propriétaires entrent.</p>';
    for (const el of zone.querySelectorAll('[data-invite]')) {
      el.onclick = async () => {
        el.disabled = true;
        try {
          const apres = await api('/api/admin/access', {
            method: 'PUT',
            body: JSON.stringify({ allowedUsers: liste.filter((u) => u !== el.dataset.invite) }),
          });
          peindreInvites(apres.allowedUsers ?? []);
        } catch (err) {
          texte('invite-etat', err.message);
          el.disabled = false;
        }
      };
    }
  };
  peindreInvites(cfg.allowedUsers ?? []);
  peindreDemandes(cfg.demandes ?? []);
  peindreArrivee(cfg.accueilPar);

  surClic('invite-ajouter', async () => {
    const champ = $('invite-id');
    const id = champ ? champ.value.trim() : '';
    // Le serveur refiltre de toute façon ; le dire ici évite un aller-retour
    // qui rendrait la liste inchangée sans expliquer pourquoi.
    if (!/^\d{17,20}$/.test(id)) {
      texte('invite-etat', 'un identifiant Discord fait 17 à 20 chiffres');
      return;
    }
    texte('invite-etat', 'ajout…');
    try {
      const actuels = (await api('/api/admin/access')).allowedUsers ?? [];
      const apres = await api('/api/admin/access', {
        method: 'PUT',
        body: JSON.stringify({ allowedUsers: [...actuels, id] }),
      });
      if (champ) champ.value = '';
      texte('invite-etat', '');
      peindreInvites(apres.allowedUsers ?? []);
    } catch (err) {
      texte('invite-etat', err.message);
    }
  });
}

// La liste des serveurs lue par `chargerAcces`, que l'écran du registre
// réutilise au lieu de refaire la même requête.
let serveursAdmin = null;
// Une lecture ratée ne reste pas en mémoire : la suivante retente.
const lireServeursAdmin = () => serveursAdmin ?? (serveursAdmin = api('/api/admin/serveurs')
  .catch((err) => { serveursAdmin = null; throw err; }));

async function chargerAcces() {
  // ⚠️ IL N'Y A PLUS DE BLOC À RAPATRIER — voir `src/admin.js`.
  //
  // `ecranAdmin.rendreLeBloc()` s'appelait ici : `#acces-discord` vivait dans
  // l'onglet « Accès » et l'écran d'administration l'empruntait le temps d'être
  // ouvert. Quitter l'écran par un autre chemin — une séance qui démarre
  // bascule la vue sans passer par « ← Réglages » — laissait le bloc dans une
  // section masquée, et l'onglet s'ouvrait sans ses serveurs ni ses rôles.
  //
  // Le bloc a UN SEUL domicile depuis le 16/09 (la section « Rôles » du tableau
  // de bord), donc plus d'emprunt, plus de rapatriement, et plus ce défaut-là.

  // Les listes du bas ne dépendent pas de Discord : elles s'affichent même si
  // la liste des serveurs échoue, et c'est justement le jour où l'on en a le
  // plus besoin.
  chargerQuiADroit();

  const zone = $('liste-serveurs');
  if (!zone) return;
  zone.textContent = 'Lecture des serveurs…';
  texte('role-etat', '');

  // Le panneau de réglages fait 480 px : on n'y gère pas vingt personnes et
  // dix rôles. Le bouton mène à l'écran entier, qui reprend ce bloc-ci en
  // déménageant son balisage plutôt qu'en le recopiant.
  surClic('roles-rien', () => {
    rolesChoisis.clear();
    dernierClicRole = -1;
    modeSelectionRoles = false;
    ouvrirServeur(serveurCourant, { garderSelection: true });
  });

  let donnees;
  try {
    donnees = await (serveursAdmin = api('/api/admin/serveurs'));
  } catch (err) {
    serveursAdmin = null;
    zone.textContent = err.message || 'La liste des serveurs est illisible.';
    return;
  }

  const serveurs = donnees.serveurs ?? [];
  if (!serveurs.length) {
    zone.textContent = "Le bot n'est sur aucun serveur.";
    return;
  }

  zone.innerHTML = `<div class="serveurs-liste">${serveurs.map((g) => `<button type="button" class="serveur-carte${g.autorise ? ' ouvert' : ''}" data-serveur="${esc(g.id)}">
      ${g.icone ? `<img src="${esc(g.icone)}" alt="" />` : ''}
      <span>${esc(g.nom)}</span>
      <span class="serveur-etat">${g.autorise ? 'ouvert' : 'fermé'}${g.principal ? ' · principal' : ''}</span>
    </button>`).join('')}</div>`;

  for (const carte of zone.querySelectorAll('[data-serveur]')) {
    carte.onclick = () => ouvrirServeur(carte.dataset.serveur);
  }

  // Le serveur principal s'ouvre tout seul : c'est celui qu'on vient régler neuf
  // fois sur dix, et un écran qui demande un clic pour montrer quelque chose est
  // un écran qui n'a rien montré.
  const depart = serveurs.find((g) => g.principal) ?? serveurs.find((g) => g.autorise) ?? serveurs[0];
  if (depart) await ouvrirServeur(depart.id);
}

// ---------------------------------------------------------------------------
// L'ECRAN D'ADMINISTRATION, PLEINE PAGE (`#v-admin`)
// ---------------------------------------------------------------------------
// Toji (13/09) : « je veux avoir une gestion dans le panel d'accès où je peux
// ouvrir en grand et avoir vraiment une interface d'administration, parce que
// là c'est un peu galère. »
//
// Le module `src/admin.js` porte l'écran ; il ne connaît rien du lecteur et
// reçoit ici ses outils. `guildCourant` lui donne le serveur affiché dans
// l'étage des rôles Discord : la liste des membres du serveur se demande pour
// CELUI qu'on regarde, pas pour celui de la configuration.
// ---------------------------------------------------------------------------
const ecranAdmin = monterEcranAdmin({
  $, esc, texte, api, vue, surClic, tracer,
  ouvrirModale, fermerModale, chargerAcces, lireServeursAdmin,
  // Les onze préparations des blocs qui ont déménagé dans le tableau de bord.
  // Elles vivent ici parce qu'elles touchent au reste du lecteur ; `admin.js`
  // n'importe rien et reçoit ses outils (voir son en-tête).
  preparerLAdministration,
  guildCourant: () => serveurCourant,
  points: reglagesDesPoints,
});

/** Ouvre l'écran d'administration — depuis le bouton, la route, ou la reprise. */
function ouvrirEcranAdmin() { return ecranAdmin.ouvrir(); }

// ---------------------------------------------------------------------------
// Catalogue des photos — un écran entier.
//
// Le panneau de réglages fait 480 px : on n'y juge pas une image, et on n'en
// compare pas deux. Le catalogue prend donc toute la page, montre l'arc en
// cours à gauche, l'image en place au-dessus, et les propositions en grand.
//
// Les propositions viennent du serveur : les vignettes des épisodes de l'arc
// d'abord — ce sont les seules dont on sait qu'elles montrent le bon moment —
// puis les visuels larges de la série. Plus besoin d'aller chercher une adresse
// ailleurs et de la coller à la main.
// ---------------------------------------------------------------------------
let catalogueArcs = null;
let catalogueArc = null;
let cataloguePropositions = [];
let catalogueFiltre = 'tout';
let catalogueChoisie = null;
let vueAvantCatalogue = 'v-accueil';

async function ouvrirCatalogue() {
  // ⚠️ CE CATALOGUE-CI EST CELUI DES IMAGES, PAS CELUI DES ŒUVRES.
  //
  // Les deux portaient le même identifiant d'écran, `v-catalogue`, parce qu'il
  // n'existait qu'un seul « catalogue » du temps où il n'y avait qu'une série.
  // Celui des œuvres a pris le nom — c'est ce que le mot veut dire pour tout le
  // monde — et celui-ci vit désormais sous `v-photos`. `ECRAN_PHOTOS` tranche
  // au moment du clic : les deux fichiers changent en même temps.
  vueAvantCatalogue = document.querySelector('.view:not([hidden])')?.id ?? 'v-accueil';
  fermerModale();

  const cote = $('cat-arcs');
  if (!cote) {
    // Le balisage n'a pas (encore) d'écran de photos : on le dit une fois
    // plutôt que de lever et d'emporter la modale de réglages avec soi.
    tracer('photos', 'ecran des photos absent du balisage');
    direMot('Le catalogue des photos n’est pas disponible sur cette page.');
    return;
  }
  vue(ECRAN_PHOTOS());
  cote.textContent = 'Lecture des arcs…';
  try {
    catalogueArcs = await arcsPourPhotos(true);
  } catch (err) {
    cote.textContent = err.message || 'Les arcs sont illisibles.';
    return;
  }

  const posees = catalogueArcs.filter((a) => a.image).length;
  texte('cat-sous', `${catalogueArcs.length} arcs · ${posees} avec une image`);
  dessinerArcsCatalogue();
  if (catalogueArcs.length) ouvrirArcCatalogue(catalogueArcs[0]);
}

function dessinerArcsCatalogue() {
  const cote = $('cat-arcs');
  cote.textContent = '';
  for (const arc of catalogueArcs ?? []) {
    const bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.className = 'cat-arc' + (catalogueArc?.cle === arc.cle ? ' actif' : '');

    const puce = document.createElement('span');
    puce.className = 'puce';
    if (arc.image) {
      const img = document.createElement('img');
      img.src = arc.image;
      img.alt = '';
      puce.appendChild(img);
    }

    const lib = document.createElement('span');
    lib.className = 'lib';
    const nom = document.createElement('b');
    nom.textContent = arc.nom;
    const plage = document.createElement('small');
    plage.textContent = arc.plage + (arc.filler ? ' · filler' : '');
    lib.append(nom, plage);

    bouton.append(puce, lib);
    bouton.onclick = () => ouvrirArcCatalogue(arc);
    cote.appendChild(bouton);
  }
}

async function ouvrirArcCatalogue(arc) {
  catalogueArc = arc;
  dessinerArcsCatalogue();

  const props = $('cat-props');
  props.textContent = 'Recherche de propositions…';
  dessinerArcCourant(arc, null);

  let reponse;
  try {
    const id = serie?.id || document.body.dataset.defaultSerie;
    reponse = await api(`/api/admin/photos/${encodeURIComponent(id)}/${encodeURIComponent(arc.cle)}`);
  } catch (err) {
    props.textContent = err.message || 'Aucune proposition.';
    return;
  }

  // Le serveur connaît l'état réel de l'image posée : il prime sur la liste
  // gardée en mémoire, qui peut dater d'avant une pose.
  catalogueArc = { ...arc, ...reponse.arc };
  cataloguePropositions = reponse.propositions ?? [];
  dessinerArcCourant(catalogueArc, cataloguePropositions.length);
  dessinerPropositions();
}

function dessinerArcCourant(arc, combien) {
  const bloc = $('cat-courant');
  bloc.textContent = '';

  const vue_ = document.createElement('span');
  vue_.className = 'vue' + (arc.image ? '' : ' vide');
  if (arc.image) {
    const img = document.createElement('img');
    img.src = arc.image;
    img.alt = '';
    vue_.appendChild(img);
  }

  const infos = document.createElement('span');
  infos.className = 'infos';
  const nom = document.createElement('b');
  nom.textContent = arc.nom;
  const plage = document.createElement('span');
  plage.className = 'muted';
  plage.textContent = arc.plage + (arc.filler ? ' · filler' : '');
  const etat = document.createElement('span');
  etat.className = 'muted';
  etat.id = 'cat-etat';
  etat.textContent = arc.image
    ? 'Image posée — elle est servie par le lecteur.'
    : combien === null ? '' : `Aucune image. ${combien} ${pluriel(combien, 'proposition')}.`;
  infos.append(nom, plage, etat);

  const actions = document.createElement('span');
  actions.className = 'actions';
  if (arc.image) {
    const retirer = document.createElement('button');
    retirer.type = 'button';
    retirer.className = 'ghost';
    retirer.textContent = "Retirer l'image";
    retirer.onclick = async () => {
      texte('cat-etat', 'retrait…');
      try {
        await api(`/api/admin/arcs/${encodeURIComponent(arc.cle)}`, { method: 'DELETE' });
        await rechargerCatalogue();
      } catch (err) {
        texte('cat-etat', err.message);
      }
    };
    actions.appendChild(retirer);
  }

  // Le collage d'adresse reste possible : une image trouvée ailleurs n'a pas à
  // devenir inaccessible parce qu'on propose mieux.
  const champ = document.createElement('input');
  champ.type = 'text';
  champ.placeholder = 'ou colle une adresse…';
  champ.className = 'cat-url';
  const poser = document.createElement('button');
  poser.type = 'button';
  poser.textContent = 'Poser';
  poser.onclick = () => {
    const url = champ.value.trim();
    if (!url) return texte('cat-etat', 'adresse vide');
    poserImageCatalogue(url);
  };
  actions.append(champ, poser);
  infos.appendChild(actions);

  bloc.append(vue_, infos);
}

function brancherFiltreCatalogue() {
  const groupe = $('cat-filtre');
  if (!groupe) return;
  const boutons = [...groupe.querySelectorAll('button[data-v]')];
  const peindre = () => {
    for (const b of boutons) b.classList.toggle('actif', b.dataset.v === catalogueFiltre);
  };
  for (const b of boutons) {
    b.onclick = () => {
      catalogueFiltre = b.dataset.v;
      peindre();
      dessinerPropositions();
    };
  }
  peindre();
}

function dessinerPropositions() {
  const grille = $('cat-props');
  grille.textContent = '';

  const liste = catalogueFiltre === 'tout'
    ? cataloguePropositions
    : cataloguePropositions.filter((p) => p.source === catalogueFiltre);

  texte('cat-titre-props', `Propositions · ${liste.length}`);
  if (!liste.length) {
    grille.textContent = catalogueFiltre === 'tout'
      ? "Aucune proposition pour cet arc. Colle une adresse ci-dessus."
      : 'Rien dans cette origine.';
    return;
  }

  for (const prop of liste) {
    const carte = document.createElement('button');
    carte.type = 'button';
    carte.className = 'cat-prop';

    const vue_ = document.createElement('span');
    vue_.className = 'vue';
    const img = document.createElement('img');
    img.src = prop.apercu || prop.url;
    img.alt = '';
    img.loading = 'lazy';
    vue_.appendChild(img);

    const pied = document.createElement('span');
    pied.className = 'pied';
    const titre = document.createElement('b');
    titre.textContent = prop.titre ?? '';
    const source = document.createElement('span');
    source.textContent = prop.source ?? '';
    pied.append(titre, source);

    carte.append(vue_, pied);
    carte.onclick = () => ouvrirLoupe(prop);
    grille.appendChild(carte);
  }
}

function ouvrirLoupe(prop) {
  catalogueChoisie = prop;
  const loupe = $('cat-loupe');
  $('cat-loupe-img').src = prop.url;
  texte('cat-loupe-txt', [catalogueArc?.nom, prop.source, prop.titre].filter(Boolean).join(' · '));
  loupe.hidden = false;
}

function fermerLoupe() {
  $('cat-loupe').hidden = true;
  catalogueChoisie = null;
}

async function poserImageCatalogue(url) {
  if (!catalogueArc) return;
  texte('cat-etat', 'téléchargement…');
  try {
    const r = await api(`/api/admin/arcs/${encodeURIComponent(catalogueArc.cle)}`, {
      method: 'POST', body: JSON.stringify({ url }),
    });
    fermerLoupe();
    await rechargerCatalogue();
    texte('cat-etat', `Image posée · ${Math.round(r.octets / 1024)} Ko`);
  } catch (err) {
    texte('cat-etat', err.message);
  }
}

/** Après une pose ou un retrait : on relit tout, et on reste sur le même arc. */
async function rechargerCatalogue() {
  const cle = catalogueArc?.cle;
  catalogueArcs = await arcsPourPhotos(true);
  dessinerArcsCatalogue();
  const arc = catalogueArcs.find((a) => a.cle === cle) ?? catalogueArcs[0];
  if (arc) await ouvrirArcCatalogue(arc);
}

// ---------------------------------------------------------------------------
// Séance commune : l'interface
//
// Le module `seance.js` tient la connexion et la synchronisation ; ici on ne
// fait que peindre ce qu'il rapporte — qui pilote, qui regarde, et les
// demandes en attente.
// ---------------------------------------------------------------------------
let seance = null;
let etatSeance = null;
// Qui est ouvert dans le volet de droite de la console. Retenu ici plutôt que
// lu dans le DOM : la liste est repeinte au rythme des messages, et une classe
// posée sur une ligne détruite ne survivrait pas au battement suivant.
let choisiSeance = null;

/**
 * Ce qu'une demande réclame, en français.
 *
 * La NATURE d'abord, `action` seulement ensuite : « parole » et « main » n'ont
 * pas d'action, et s'en tenir à celle-ci annonçait une demande de parole comme
 * un déplacement dans l'épisode. Un message faux coûte plus cher qu'un message
 * absent — on ne devine jamais ce que quelqu'un a demandé.
 */
function phraseDemande(d) {
  if (d?.nature === 'parole') return 'demande à reprendre la parole.';
  if (d?.nature === 'main') return 'demande la main sur la lecture.';
  // La nature 'pause' couvre toute la famille des gestes de lecture ; c'est
  // `action` qui dit lequel exactement.
  if (d?.action === 'play') return 'veut reprendre la lecture.';
  if (d?.action === 'seek') return 'veut se déplacer dans l’épisode.';
  return 'veut mettre en pause.';
}

/**
  * Identifiant de la séance, vu depuis l'activité.
  *
  * `instanceId` désigne UN lancement de l'activité : refermer puis rouvrir
  * dans le même vocal en produit un nouveau, et les pages déjà ouvertes se
  * retrouveraient orphelines. Le salon, lui, ne change pas — c'est le repère
  * qui correspond à ce qu'on attend : « la séance de ce vocal ».
  */
function identifiantSeance() {
  const salon = sdk?.channelId;
  if (salon) return `salon-${salon}`;

  // ⚠️ PAS DE REPLI SUR `instanceId`. UNE SÉANCE, C'EST UN SALON.
  //
  // Ce retour valait `sdk.instanceId` — l'identifiant de CE lancement
  // d'activité, différent pour chaque personne et à chaque ouverture. Deux
  // personnes du même vocal, si le SDK n'avait pas encore donné le salon,
  // ouvraient donc deux séances distinctes. Chacune se croyait seule : aucun
  // participant en face, aucune main partagée, aucune synchronisation — et
  // rien pour le signaler, puisque les deux séances marchaient très bien,
  // séparément. C'est exactement la panne qu'on ne diagnostique pas.
  //
  // Sans salon, il n'y a rien à rejoindre : `null` fait attendre le SDK
  // plutôt que d'inventer une séance où l'on sera seul. Le serveur applique la
  // même règle de son côté — c'est le registre vocal qui tranche, pas ce que
  // le client annonce — mais mieux vaut ne pas lui envoyer une erreur à
  // corriger.
  return null;
}

/**
 * La séance en cours, quel que soit l'endroit d'où on regarde.
 *
 * Dans l'activité, l'instance vient du SDK. Dans le navigateur, elle vient de
 * l'adresse : c'est l'activité qui l'y a mise en ouvrant le lien. Les deux
 * désignent la MÊME séance, et c'est ce qui permet à la gestion de rester dans
 * Discord pendant que la lecture se joue dehors.
 *
 * ⚠️ CETTE FONCTION EXISTE PARCE QUE LE CALCUL ÉTAIT ENFERMÉ DANS
 * `demarrerSeance` (défaut payé le 14/09). Partout ailleurs — billets, carte
 * d'annonce — on appelait `identifiantSeance()` tout court, qui rend `null` hors
 * activité : le lecteur renonçait avant même d'appeler le serveur, et une
 * séance pilotée depuis le SITE n'atteignait jamais le bot.
 */
function instanceCourante() {
  return DANS_DISCORD ? identifiantSeance() : (instanceDeSeance || params.get('seance'));
}

/**
 * Traduit l'alias de l'adresse en instance de séance, si l'adresse en porte un.
 *
 * Ne lève jamais : un alias inconnu — séance terminée, lien recopié de
 * travers — doit laisser la page s'ouvrir sur le catalogue, pas la bloquer sur
 * un écran vide.
 */
/**
 * L'alias de séance porté par l'adresse, ou `null`.
 *
 * Sert à savoir si la séance qu'on propose de rejoindre est celle qu'on
 * regardait déjà : dans ce cas il n'y a rien à recharger, seulement un écran à
 * remettre.
 */
function aliasCourant() {
  return /^\/lecteur\/([^/?#]+)/.exec(location.pathname)?.[1] ?? null;
}

function resoudreAlias() {
  const alias = /^\/lecteur\/([^/?#]+)/.exec(location.pathname)?.[1];
  if (!alias) return Promise.resolve(null);
  return api(`/api/seance/ou/${encodeURIComponent(alias)}`)
    .then((r) => { instanceDeSeance = r.instance; return r.instance; })
    .catch(() => { tracer('seance', 'alias inconnu', alias); return null; });
}

function demarrerSeance() {
  // En attente : le serveur refuserait (seance.js), inutile de frapper en boucle.
  if (!session || seance || role === 'attente') return;

  const instance = instanceCourante();
  if (!instance) {
    tracer('seance', 'aucun identifiant de seance');
    return;
  }
  tracer('seance', `seance ${instance}`, DANS_DISCORD ? 'depuis l activite' : 'depuis le navigateur');

  seance = creerSeance({
    base, $, lecteur: lecteur(), jeton: session, instance,
    // ⚠️ UNE LECTURE AU MOMENT DE L'ENVOI, PAS UNE COPIE PRISE ICI.
    //
    // `poserProfil` REMPLACE `moi` par un objet neuf au lieu de le muter, et
    // c'est par ce remplacement qu'arrivent l'identifiant, le rang et sa
    // couleur : ils ne viennent que de `/api/moi`, dont la réponse tombe APRÈS
    // ce point. Passer l'objet le figeait donc dans l'état qu'il avait à
    // l'ouverture de la séance — le bonjour partait sans rang, et un pseudo
    // changé depuis restait périmé pour tous les autres, à chaque reconnexion.
    //
    // Des accesseurs relisent la variable à chaque lecture. La FORME ne change
    // pas — `seance.js` continue d'écrire `moi?.pseudo` et n'a rien à savoir
    // de ceci.
    moi: {
      get id() { return moi?.id ?? null; },
      get pseudo() { return moi?.pseudo ?? null; },
      get username() { return moi?.username ?? null; },
      get avatar() { return moi?.avatar ?? null; },
      get rang() { return moi?.rang ?? null; },
      get rangCouleur() { return moi?.rangCouleur ?? null; },
      // La hauteur du rôle part avec son nom et sa couleur : c'est sur elle
      // que la console trie, et elle vient du même relevé.
      get rangPosition() { return moi?.rangPosition ?? null; },
    },
    // La séance fait foi : si plus personne n'est devant l'épisode, l'onglet
    // est bien parti et le prochain choix devra en rouvrir un.
    // Une requête qui n'est pas partie, et pourquoi. Voir `direMot`.
    surRefus: (m) => direMot(m?.message ?? 'Cette demande n’est pas partie.'),
    // Ses propres requêtes coupées, ou rendues.
    surSourdine: (m) => direMot(m?.actif
      ? 'Tes demandes et tes votes sont suspendus pour cette séance.'
      : 'Tu peux de nouveau demander et voter.'),
    surParticipants: (participants) => {
      if (DANS_DISCORD && ongletOuvert && !participants.some((p) => p.regarde)) {
        ongletOuvert = false;
      }
    },
    dOu: DANS_DISCORD ? 'activite' : 'navigateur',
    // La langue est un choix personnel : elle n'entre pas dans l'état commun,
    // mais la gestion doit pouvoir compter qui regarde en français.
    langueCourante: () => prefs.lire('langue', 'auto'),
    // Dans le navigateur, un ordre d'ouverture lance l'épisode ; dans
    // l'activité, il n'y a rien à lire, seulement à afficher.
    surOuvrir: (cible) => {
      // Retenu MÊME dans l'activité, où il n'y a rien à lire : c'est par ce
      // message qu'on apprend l'épisode ouvert par quelqu'un d'autre, et la
      // présence riche n'a pas d'autre source quand ce n'est pas nous qui
      // avons lancé.
      episodeDeLaSeance = cible;
      majPresence();
      // ⚠️ On retient CE QU'ON VIENT DE RECEVOIR comme si on l'avait publié.
      // Sans cette ligne, appliquer l'écho relancerait `lancer()`, qui
      // republierait l'épisode, que les autres appliqueraient à leur tour :
      // deux navigateurs se renverraient le même épisode sans fin.
      marquerOuvertureConnue(cible);
      if (DANS_DISCORD) return;
      tracer('seance', 'ouverture demandee', cible);
      if (cible.film) {
        const fiche = Object.values(collections).flat().find((f) => f.id === cible.film);
        if (fiche) lancerFilm(fiche);
        return;
      }
      const suite = () => lancer(cible.saison, cible.episode);
      if (serie?.id === cible.serie) suite();
      else ouvrirSerie(cible.serie, { enFond: true }).then(suite);
    },
    surChangement: (lecture) => {
      etatSeance = lecture;
      rejoindreCeQuiEstDejaOuvert(lecture);
      peindreSeance();
      peindreGestion();
      peindreVignette();
      // La règle du jeu vient du serveur : les boutons de l'onglet Admin
      // montrent ce qui tranche vraiment, pas ce qu'on a cliqué en dernier.
      peindreReglagesSeance();
      // Une pause, une reprise, un saut : tout passe par ici. La présence s'y
      // branche plutôt que sur un minuteur à elle, et se charge seule de ne
      // pas repartir quand rien n'a bougé.
      majPresence();
    },
  });
}

// ---------------------------------------------------------------------------
// LA PORTE D'ENTRÉE DE LA CONSOLE
//
// La console de séance (`v-gestion`) n'était atteinte que par les deux chemins
// qui LANCENT un épisode : l'ouverture d'un onglet dehors, et la pousse vers
// un onglet déjà ouvert. Autrement dit, en sortir — par l'accueil, par le
// catalogue, par un rechargement de l'activité — n'y ramenait plus, et le seul
// moyen de revenir était de relancer un épisode pour toute la salle.
//
// Le bouton vit dans l'en-tête parce que c'est la seule barre présente sur
// TOUTES les vues sauf le lecteur, où elle s'efface d'elle-même (`body.plein`
// et la règle sur `#v-lecture`). Il est fabriqué ici plutôt qu'écrit dans la
// page : rien d'autre n'a besoin de son balisage, et il ne doit exister que
// pendant une séance — un bouton toujours là qui mène à un écran vide vaut
// moins qu'un bouton absent.
// ---------------------------------------------------------------------------

// Le mot que lit celui qui survole l'image d'une présence. C'est le seul endroit
// de la présence qui parle à quelqu'un d'EXTÉRIEUR à la séance.
// Le texte lui-même vient de la page quand elle en porte un : chaque
// communauté a sa formule, et la sienne n'a pas à passer par un dépôt.
const INVITATION_PRESENCE = document.body.dataset.invitation
  || `Rejoignez le serveur Discord de ${MARQUE}`;

const ID_ACCES_CONSOLE = 'acces-console';
const ID_ACCES_SEANCES = 'acces-seances';


/** Fabrique le bouton une fois pour toutes, et le branche. */
function preparerAccesConsole() {
  const entete = document.querySelector('header');
  if (!entete || $(ID_ACCES_CONSOLE)) return;

  const bouton = document.createElement('button');
  bouton.id = ID_ACCES_CONSOLE;
  bouton.type = 'button';
  // `ghost` est le bouton discret de la feuille de style : l'en-tête porte une
  // marque et un profil, pas un bouton plein qui se disputerait le regard.
  bouton.className = 'ghost';
  bouton.title = 'Revenir à la console de séance';
  bouton.hidden = true;

  // Le même point vert que le bandeau de séance, et pour la même raison : il
  // ne dit pas « clique ici », il dit « une séance est ouverte ».
  const pastille = document.createElement('span');
  pastille.style.cssText = 'display:inline-block;width:7px;height:7px;border-radius:50%;'
    + 'background:#4ade80;margin-right:7px;vertical-align:middle';
  bouton.append(pastille, document.createTextNode('Séance'));

  // ⚠️ DANS LE GROUPE DE DROITE, PAS DANS L'EN-TÊTE.
  //
  // L'en-tête est une GRILLE À TROIS COLONNES. Y insérer un bouton en faisait un
  // quatrième enfant, donc une rangée implicite sous la première : le bouton et
  // le profil descendaient sous la recherche, décalés au milieu de nulle part.
  // C'est la capture de Toji. La grille compte donc trois enfants pour de bon —
  // un groupe à gauche, la recherche, un groupe à droite — et les boutons
  // rejoignent un groupe.
  //
  // Toji : « mon pseudo avec le reste, c'est censé être collé à droite de
  // Séance » — ce bouton et le profil sont voisins, dans cet ordre.
  const aDroite = entete.querySelector('.tete-droite') ?? entete;
  aDroite.insertBefore(bouton, $('profil') ?? null);

  // ⚠️ DEUX PORTES DANS LA BARRE, ET PAS LES MÊMES POUR TOUT LE MONDE.
  //
  // Toji : « il faudrait qu'il y ait deux boutons dans la barre en haut. Un des
  // deux, c'est la page de gestion — cette page va être commune à tous les
  // utilisateurs, mais QUE POUR DISCORD. Pour les administrateurs, ils l'ont du
  // côté du web, mais c'est que les admins. Du côté des utilisateurs, ils vont
  // avoir que la gestion des séances et la gestion du lecteur, c'est tout. »
  //
  // La console est un poste de commande : la liste des membres, les demandes,
  // les votes, la main, les micros. Dans l'activité elle a sa place pour tout le
  // monde — c'est là qu'on gère, et la lecture se passe ailleurs. Sur le site,
  // l'invité vient REGARDER : lui ouvrir un poste de commande ne lui sert pas et
  // lui montre ce qui ne le regarde pas.
  //
  // D'où la seconde porte, celle qui manquait : « Les séances ». Elle ramène à
  // la liste, depuis n'importe où, y compris depuis le lecteur — et c'est elle
  // que voit l'invité, là où l'administrateur voit les deux.
  const versSeances = document.createElement('button');
  versSeances.id = ID_ACCES_SEANCES;
  versSeances.type = 'button';
  versSeances.className = 'ghost';
  versSeances.title = 'Revenir à la liste des séances en cours';
  versSeances.textContent = 'Les séances';
  versSeances.hidden = true;
  versSeances.onclick = () => {
    // Le lecteur reste PRÊT derrière : on le met de côté, on ne le jette pas.
    lecteur().mettreDeCote();
    ouvrirLesSeances();
  };
  // ⚠️ LA GESTION VA À GAUCHE, COLLÉE À LA MARQUE.
  //
  // Toji : « mon bouton à moi pour la gestion, tu le mets collé avec le bouton
  // d'accueil, donc à droite de Tsudoi, du logo du site ». Revenir à l'accueil
  // et aller à la gestion sont deux gestes du même ordre — on quitte ce qu'on
  // regarde — et ils se cherchent au même endroit.
  (entete.querySelector('.tete-gauche') ?? entete).append(versSeances);

  bouton.onclick = () => {
    // Le même geste que les deux chemins qui y menaient déjà. `vue()` empile
    // la vue dans le chemin et coupe la source au passage ; `peindreGestion` sort
    // d'elle-même tant que la console est masquée, donc on la rappelle APRÈS
    // plutôt que d'attendre le battement de séance suivant devant un écran
    // encore vide.
    //
    // On ne se retrouve pas enfermé : le retour de la console est masqué pour
    // l'hôte, mais la marque de l'en-tête ramène à l'accueil depuis n'importe
    // quelle vue.
    vue('v-gestion');
    peindreGestion();
  };
}

/**
 * Montre la porte quand il y a quelque chose derrière, et pas autrement.
 *
 * Deux conditions, et les deux comptent : sans séance active la console
 * n'affiche qu'un écran vide, et une fois dessus le bouton ne mènerait qu'à
 * l'endroit où l'on est déjà.
 */
function peindreAccesConsole() {
  const bouton = $(ID_ACCES_CONSOLE);
  if (bouton) {
    // Trois conditions : une séance ouverte, ne pas y être déjà, et — sur le
    // site seulement — en avoir le droit. Dans l'activité, tout le monde gère.
    // Le serveur tranche, jamais le client : `role` vient de la session, et le
    // drapeau `admin` du participant vient des rôles Discord résolus côté
    // serveur. On lit les deux — un propriétaire l'est partout, un
    // administrateur l'est sur ce serveur-là.
    // Un compte en attente n'a rien à gérer : la sonnette est son seul écran.
    const jyAiDroit = role !== 'attente' && (DANS_DISCORD || role === 'owner' || jeSuisAdmin(etatSeance));
    bouton.hidden = !etatSeance?.actif || !$('v-gestion')?.hidden || !jyAiDroit;
  }

  // La liste des séances n'existe que sur le site : dans l'activité, il n'y a
  // qu'une séance possible — celle du salon où l'on se trouve. Elle s'efface
  // quand on y est déjà, comme l'autre porte.
  // ⚠️ « LES SEANCES » EST RETIREE DE L'EN-TETE, POUR L'INSTANT.
  //
  // Toji : « les séances, je veux que tu caches pour l'instant les séances et
  // cherches un film ou une série anime. » La recherche prend le milieu de la
  // barre, et une porte de plus a cote d'elle en ferait un couloir. La liste
  // reste atteignable — c'est un ecran a part entiere — simplement plus depuis
  // l'en-tete tant que la recherche s'y installe.
  const seances = $(ID_ACCES_SEANCES);
  if (seances) seances.hidden = true;
}

function peindreSeance() {
  const bloc = $('seance-bloc');
  if (!bloc) return;

  const e = etatSeance;
  // Le bandeau n'a de sens qu'à plusieurs, et pendant la lecture : seul, il
  // ne dirait rien que l'on ne sache déjà.
  const plusieurs = (e?.participants?.length ?? 0) > 1;
  bloc.hidden = !e?.actif || !plusieurs || $('v-lecture').hidden;

  // ⚠️ DÉFAUT PAYÉ LE 14/09 : CES DEUX PEINTURES ÉTAIENT SOUS UN RÉGLAGE
  // D'AFFICHAGE QUI NE LES CONCERNE PAS.
  //
  // Elles vivaient plus bas, après le test de `bouton-membres`. Le commentaire
  // d'alors prenait soin de les placer avant le `return` du bandeau masqué…
  // mais pas avant celui du réglage. Couper « le bouton de membres » — un
  // réglage qui voyage avec le compte, donc réglable depuis le site et appliqué
  // dans l'activité — fermait alors la SEULE porte vers la console de séance
  // depuis l'iframe : « Les séances » y est masqué sans condition, et les
  // entrées par l'adresse (`?gestion=1`, `/lecteur/<alias>`) n'existent pas
  // quand `location.pathname` vaut `/`. On restait enfermé sur le catalogue.
  //
  // Elles remontent donc AVANT tout retour. Aucune des deux ne lit `bloc.hidden`
  // ni l'ajustement Discord ci-dessous : `peindreAccesConsole` ne regarde que
  // `etatSeance`, `v-gestion`, `DANS_DISCORD`/`role`/`jeSuisAdmin`, et
  // `peindreOngletSeance` que `etatSeance`.
  peindreOngletSeance();
  peindreAccesConsole();

  // ⚠️ CERTAINS NE VEULENT RIEN PAR-DESSUS L'IMAGE.
  //
  // Toji : « on a le choix d'afficher ou pas le bouton de membre ». Le réglage
  // ne coupe QUE l'affichage : la séance continue de tourner derrière, la
  // synchronisation ne change pas, et on la retrouve en sortant du lecteur.
  if (prefs.lire('bouton-membres', 'oui') === 'non') {
    bloc.hidden = true;
    return;
  }

  // Dans l'activité, la lecture se passe ailleurs : le bandeau y reste, c'est
  // la seule chose qui dise ce qui est en train d'être regardé.
  if (DANS_DISCORD && e?.actif && plusieurs) bloc.hidden = false;

  if (bloc.hidden) return;

  const bandeau = $('seance-bandeau');
  bandeau.textContent = '';
  const pastille = document.createElement('span');
  pastille.className = 'pastille';
  const texteBandeau = document.createElement('span');
  const hote = e.participants.find((p) => p.id === e.hote);
  texteBandeau.append(
    document.createTextNode(`${e.participants.length} personnes · `),
    Object.assign(document.createElement('b'), { textContent: hote?.pseudo ?? 'séance' }),
    document.createTextNode(e.jAiLaMain ? ' · tu pilotes' : ' pilote'),
  );
  bandeau.append(pastille, texteBandeau);

  if (!e.jAiLaMain) {
    const demander = document.createElement('button');
    demander.type = 'button';
    demander.textContent = 'Demander la pause';
    demander.onclick = () => seance?.demander('pause');
    bandeau.appendChild(demander);
  }

  // Les demandes en attente, pour ceux qui peuvent y répondre.
  const zone = $('seance-demandes');
  zone.textContent = '';
  if (!e.jAiLaMain) return;
  for (const d of e.demandes ?? []) {
    const carte = document.createElement('div');
    carte.className = 'seance-demande';
    const p = document.createElement('p');
    p.append(
      Object.assign(document.createElement('span'), { className: 'qui', textContent: d.pseudo }),
      document.createTextNode(` ${phraseDemande(d)}`),
    );

    const choix = document.createElement('div');
    choix.className = 'choix';
    const oui = document.createElement('button');
    oui.type = 'button';
    oui.className = 'primary';
    oui.textContent = 'Accepter';
    oui.onclick = () => seance?.repondre(d.id, true);
    const non = document.createElement('button');
    non.type = 'button';
    non.className = 'ghost';
    non.textContent = 'Refuser';
    non.onclick = () => seance?.repondre(d.id, false);
    choix.append(oui, non);

    carte.append(p, choix);
    zone.appendChild(carte);
  }
}

/** La section « Séance » des réglages : qui est là, et qui peut piloter. */
/** Minutage lisible, comme dans le lecteur. */
function horlogeCourte(s) {
  if (!Number.isFinite(s) || s < 0) return '--:--';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Écran de gestion : ce que l'activité montre pendant que la lecture se joue
 * dans les navigateurs.
 *
 * Elle ne lit plus rien elle-même, mais le serveur sait tout de la séance —
 * qui est là, qui a réellement ouvert le lecteur, où en est l'épisode, quelles
 * demandes attendent. L'activité devient le poste de commande.
 */
// ---------------------------------------------------------------------------
// L'avancee de la seance
//
// A part du reste, et repeinte a chaque image plutot qu'une fois par seconde :
// une barre qui n'avance qu'au rythme des messages du serveur saute d'un cran
// a l'autre, et le lissage CSS qui masquait ces sauts la faisait trainer
// derriere le lecteur. Ici la position est CALCULEE — le dernier point connu,
// plus le temps ecoule depuis, a l'heure du serveur — donc elle court, et elle
// se recale sans a-coup a chaque message.
//
// Cout : quelques ecritures de style par image, uniquement quand la vue de
// gestion est a l'ecran.
// ---------------------------------------------------------------------------
function peindreAvancee() {
  const e = etatSeance;
  const bandeau = $('g-bandeau');
  if (!bandeau) return;

  const duree = e?.duree ?? 0;
  const enLecture = !!e?.lecture;

  // LA POSITION EST DATÉE, et il faut rattraper le temps écoulé depuis l'envoi.
  //
  // L'état de séance arrive une fois par seconde ; l'afficher tel quel fait un
  // compteur qui saute d'une seconde entière au lieu d'avancer, et surtout qui
  // RETARDE de tout le trajet réseau. C'était écrit dans le contrat du serveur
  // (« le client rattrape le temps écoulé ») et personne ne le faisait ici :
  // la console et le lecteur affichaient donc deux chiffres différents pour le
  // même instant, ce qui donne l'impression que l'un des deux ment.
  //
  // En pause, on ne rattrape rien : le temps ne passe pas.
  const brute = typeof e?.position === 'function' ? e.position() : (e?.position ?? 0);
  const depuis = enLecture && e?.majA ? Math.max(0, (Date.now() - e.majA) / 1000) : 0;
  const position = duree > 0 ? Math.min(duree, brute + depuis) : brute + depuis;

  bandeau.classList.toggle('arret', !!e?.actif && !enLecture);

  // ⚠️ « EN PAUSE » N'EST PAS LA MÊME CHOSE QU'« EN COURS DE CHARGEMENT ».
  //
  // Le bandeau affichait « en pause » tant qu'aucune lecture n'avait commencé
  // — c'est-à-dire pendant tout le chargement, qui dure quarante secondes
  // quand la source est lente. Or c'est CET écran que Discord montre en
  // miniature au salon : ceux qui n'ouvrent jamais l'activité ne voient que
  // lui, et ils lisaient « en pause » sur un épisode qui se cherchait encore.
  //
  // La séance ne connaît que lecture vrai/faux : elle ne peut pas distinguer
  // les deux. Le signe qui les sépare est local — l'écran d'attente est
  // visible tant qu'aucune image n'a été montrée.
  const charge = !$('v-chargement')?.hidden;
  const rienEncore = charge && !enLecture && (position || 0) === 0;

  const barre = $('g-progres');
  if (barre) {
    // Bornee des deux cotes : sans duree connue la barre resterait a zero, et
    // une position qui depasse la duree annoncee la ferait deborder.
    const part = duree > 0 ? Math.max(0, Math.min(1, position / duree)) : 0;
    const large = `${(part * 100).toFixed(3)}%`;
    if (barre.style.width !== large) barre.style.width = large;
  }

  texte('g-position', horlogeCourte(position));
  texte('g-duree', duree ? horlogeCourte(duree) : '--:--');
  texte('g-lecture', !e?.actif ? ''
    : enLecture ? ''
      : rienEncore ? 'chargement…'
        : 'en pause');
  // La barre n'a rien à montrer tant que rien n'a joué : à zéro, elle laisse
  // croire qu'on est au début d'une lecture en cours.
  const av = $('g-avancee');
  if (av) av.hidden = !!rienEncore;
}

let boucleAvancee = 0;
/** Fait courir l'avancee tant que la vue de gestion est affichee. */
function suivreAvancee() {
  const tour = () => {
    boucleAvancee = requestAnimationFrame(tour);
    if ($('v-gestion')?.hidden) return;
    peindreAvancee();
  };
  if (!boucleAvancee) tour();
}

// ---------------------------------------------------------------------------
// LA TÊTE DE LA CONSOLE : les deux menus de qui tient la séance.
//
// La règle du vote et la carte du salon ne se réglaient que dans l'onglet
// Admin — un autre écran, trois clics, et il fallait revenir ici pour voir
// l'effet. Ce qui se règle EN SÉANCE doit être à portée de la séance, sinon
// personne ne le règle et tout le monde subit le défaut.
//
// Rien ne se décide dans ces menus. Ils envoient `reglages` par la séance, et
// le serveur seul valide le mode, borne la valeur, rejoue les votes en cours
// et rediffuse. Ils repeignent ensuite ce que l'ÉTAT renvoie, jamais ce qu'on
// vient de cliquer : deux pilotes verraient sinon chacun leur dernier clic
// plutôt que la règle qui tranche.
// ---------------------------------------------------------------------------

const MENUS_TETE = {
  embed: { menu: 'g-menu-embed', bouton: 'g-embed-bouton', vol: 'g-embed-vol' },
  vote: { menu: 'g-menu-vote', bouton: 'g-vote-bouton', vol: 'g-vote-vol' },
};

/** Les deux familles de vote, et le groupe de rangées qui porte chacune. */
const GROUPES_VOTE = [['saut', 'g-vote-saut'], ['pause', 'g-vote-pause']];

let menuTeteOuvert = null;
let teteBranchee = false;

/**
 * Allume une rangée d'un volet, et elle seule.
 *
 * Le même geste sert à peindre l'écho du serveur et à répondre au clic tant
 * que cet écho n'est pas revenu : deux boucles séparées finiraient par cocher
 * différemment pour la même règle. Renvoie si quelque chose a été reconnu.
 */
function cocherRangees(groupe, estActive) {
  let une = false;
  for (const b of $(groupe)?.querySelectorAll('button[data-mode], button[data-cle]') ?? []) {
    const est = !!estActive(b);
    if (est) une = true;
    b.setAttribute('aria-checked', est ? 'true' : 'false');
    const coche = b.querySelector('.g-vol-coche');
    if (coche) coche.textContent = est ? '●' : '';
  }
  return une;
}

/** Ce qu'un seuil exige, en clair — pour le dire quand aucune rangée ne le porte. */
function resumeSeuil(r) {
  if (!r?.mode) return '';
  if (r.mode === 'pourcent') return `${r.valeur} %`;
  if (r.mode === 'fixe') return `${r.valeur} voix`;
  if (r.mode === 'tous') return 'unanimité';
  return 'tout le monde sauf un';
}

/**
 * Place un volet de tête sous son bouton, ou au-dessus s'il n'y a pas la place.
 *
 * `position: fixed` et placement mesuré, comme le menu des épisodes : la tête
 * vit dans une vue qui défile, et un volet en `absolute` s'y ferait rogner par
 * le premier `overflow` rencontré. Le calcul est écrit ici plutôt que partagé
 * avec celui du menu des épisodes : celui-là s'ancre sur un bouton unique et
 * s'aligne à gauche, ces deux-ci vivent au bord DROIT de la fenêtre et en
 * sortiraient aussitôt.
 */
function placerVoletTete(bouton, vol) {
  if (!bouton || !vol) return;
  const b = rectPage(bouton);
  const large = Math.min(300, largeurPage() - 24);
  vol.style.width = `${large}px`;
  vol.style.left = `${Math.max(12, Math.min(b.right - large, largeurPage() - large - 12))}px`;

  const dispoBas = hauteurPage() - b.bottom - 16;
  const dispoHaut = b.top - 16;
  if (dispoBas < 200 && dispoHaut > dispoBas) {
    vol.style.top = 'auto';
    vol.style.bottom = `${hauteurPage() - b.top + 6}px`;
    vol.style.maxHeight = `${Math.max(160, dispoHaut)}px`;
  } else {
    vol.style.bottom = 'auto';
    vol.style.top = `${b.bottom + 6}px`;
    vol.style.maxHeight = `${Math.max(160, dispoBas)}px`;
  }
}

function fermerMenusTete(sauf = null) {
  for (const [nom, r] of Object.entries(MENUS_TETE)) {
    if (nom === sauf) continue;
    const vol = $(r.vol);
    if (vol) vol.hidden = true;
    $(r.menu)?.classList.remove('ouvert');
    $(r.bouton)?.setAttribute('aria-expanded', 'false');
  }
  if (sauf === null) menuTeteOuvert = null;
}

function basculerMenuTete(nom) {
  const r = MENUS_TETE[nom];
  if (!r) return;
  if (menuTeteOuvert === nom) { fermerMenusTete(); return; }
  // Un seul volet ouvert : deux panneaux côte à côte dans une tête de 40 px de
  // haut se recouvriraient, et le second cacherait le bouton du premier.
  fermerMenusTete(nom);
  menuTeteOuvert = nom;
  const vol = $(r.vol);
  if (vol) vol.hidden = false;
  $(r.menu)?.classList.add('ouvert');
  $(r.bouton)?.setAttribute('aria-expanded', 'true');
  placerVoletTete($(r.bouton), vol);
  peindreTeteSeance();
}

/** Ce que le serveur applique, repeint à chaque battement de la console. */
function peindreTeteSeance(e = etatSeance) {
  const pilote = !!(e?.jeSuisHote || jeSuisAdmin(e));
  for (const r of Object.values(MENUS_TETE)) {
    const menu = $(r.menu);
    if (menu) menu.hidden = !pilote;
  }
  // Perdre la main referme : un volet dont plus aucune rangée n'obtiendrait
  // quoi que ce soit du serveur ne montre qu'une promesse.
  if (!pilote) { fermerMenusTete(); return; }

  const reglages = e?.reglages ?? null;

  // Un seul endroit sait comment le serveur range la règle — ici comme dans
  // l'onglet Admin. Deux lectures écrites séparément avaient fini par ne plus
  // lire le même champ, et les deux écrans se contredisaient.
  const seuils = seuilsDeLaSeance(reglages);

  for (const [famille, groupe] of GROUPES_VOTE) {
    const regle = seuils[famille];
    const reconnu = cocherRangees(groupe, (b) => !!regle
      && b.dataset.mode === regle.mode
      && (b.dataset.mode === 'pourcent' || b.dataset.mode === 'fixe'
        ? Number(b.dataset.valeur) === Number(regle.valeur)
        : true));
    // Le curseur de l'onglet Admin peut poser 66 % — aucune rangée ne le
    // porte. Le taire ferait lire « rien de réglé » à un groupe qui l'est.
    texte(`${groupe}-compte`, !regle || reconnu ? '' : resumeSeuil(regle));
  }

  // La carte du salon. `remplacer` vaut vrai par défaut côté serveur : le lire
  // comme un booléen brut ferait passer une séance jamais réglée pour une
  // séance qui empile.
  const emb = reglages?.embed ?? {};
  const etats = {
    auto: emb.auto === true,
    remplacer: emb.remplacer !== false,
    mentionner: emb.mentionner === true,
  };
  cocherRangees('g-embed-options', (b) => !!etats[b.dataset.cle]);
  // Le détail dit ce que l'état COURANT fait, pas ce que ferait le clic : sur
  // ces trois-là, éteint ne veut pas dire « rien » — ça empile, ça se tait, ça
  // attend qu'on l'envoie.
  texte('g-embed-det-auto', etats.auto ? 'auto' : 'on propose');
  texte('g-embed-det-remplacer', etats.remplacer ? 'remplace' : 'empile');
  texte('g-embed-det-mentionner', etats.mentionner ? 'ping' : 'sans ping');

  const envoi = $('g-embed-envoyer');
  if (envoi) {
    // Rien à annoncer : le bouton le dit d'avance plutôt que d'échouer en
    // silence une fois cliqué.
    //
    // ⚠️ PLUS DE CONDITION SUR `sdk?.channelId` — défaut payé le 14/09. Le
    // bouton était éteint sur tout le site, avec un titre qui affirmait que
    // l'annonce ne partait que depuis l'activité : c'était faux, et c'est
    // précisément ce que la console du site existe pour faire. Le salon vient
    // du serveur, qui refuse qui ne pilote pas de séance.
    const cible = carteAProposer ?? episodeEnCours();
    envoi.disabled = !cible;
    envoi.title = cible ? '' : 'Aucun épisode ouvert : il n’y a rien à annoncer.';
  }

  // Le volet suit son bouton : la tête bouge avec la fenêtre, et un volet en
  // `fixed` posé une fois pour toutes resterait là où on l'a ouvert.
  const ouvert = menuTeteOuvert ? MENUS_TETE[menuTeteOuvert] : null;
  if (ouvert) placerVoletTete($(ouvert.bouton), $(ouvert.vol));
}

/** Les branchements des deux menus, posés une seule fois. */
function preparerTeteSeance() {
  if (teteBranchee) return;
  teteBranchee = true;

  for (const [nom, r] of Object.entries(MENUS_TETE)) {
    const bouton = $(r.bouton);
    const vol = $(r.vol);
    if (!bouton || !vol) continue;
    // `stopPropagation` des deux côtés : sans lui, le clic qui ouvre atteint le
    // document et referme aussitôt ce qu'il vient d'ouvrir — et le clic sur une
    // rangée refermerait le volet avant qu'on ait pu en régler une seconde.
    bouton.addEventListener('click', (evt) => { evt.stopPropagation(); basculerMenuTete(nom); });
    vol.addEventListener('click', (evt) => evt.stopPropagation());
  }

  for (const [famille, groupe] of GROUPES_VOTE) {
    for (const b of $(groupe)?.querySelectorAll('button[data-mode]') ?? []) {
      b.onclick = () => {
        // UNE SEULE FAMILLE PAR MESSAGE. `seuil` — la forme historique — règle
        // les DEUX d'un coup côté serveur : l'employer ici écraserait le seuil
        // de pause en réglant celui du saut, ce que ces deux groupes existent
        // précisément pour éviter.
        const parti = envoyerReglagesSeance({
          seuils: { [famille]: { mode: b.dataset.mode, valeur: Number(b.dataset.valeur) } },
        });
        texte('g-vote-etat', parti
          ? 'Transmis à la séance.'
          : 'Séance injoignable : la règle n’a pas été transmise.');
        // L'écho ne revient qu'au battement suivant. Sans cette coche, le clic
        // reste sans effet visible pendant une seconde et on reclique.
        if (parti) {
          cocherRangees(groupe, (autre) => autre === b);
          texte(`${groupe}-compte`, '');
        }
      };
    }
  }

  for (const b of $('g-embed-options')?.querySelectorAll('button[data-cle]') ?? []) {
    b.onclick = () => {
      const vers = b.getAttribute('aria-checked') !== 'true';
      // Le serveur ne retient que les clés booléennes qu'on lui passe : envoyer
      // la seule qui change évite d'écraser les deux autres avec ce que cette
      // fenêtre croyait savoir d'elles.
      const parti = envoyerReglagesSeance({ embed: { [b.dataset.cle]: vers } });
      texte('g-embed-etat', parti
        ? 'Transmis à la séance.'
        : 'Séance injoignable : le réglage n’a pas été transmis.');
      if (parti) {
        b.setAttribute('aria-checked', vers ? 'true' : 'false');
        const coche = b.querySelector('.g-vol-coche');
        if (coche) coche.textContent = vers ? '●' : '';
      }
    };
  }

  surClic('g-embed-envoyer', () => {
    // LE MÊME CHEMIN que le bouton de la barre de commandes. `envoyerLaCarte`
    // est le seul endroit où se décide ce qui part, ce qui remplace et ce qui
    // ping ; une seconde fonction d'envoi serait un second endroit où oublier
    // la mention, et c'est elle qui réveille le serveur.
    //
    // Ce qui attend d'être annoncé d'abord — il porte le lien d'ouverture, que
    // l'épisode de la séance n'a pas. À défaut, ce qui se joue maintenant : ce
    // bouton doit pouvoir annoncer une séance déjà commencée.
    const cible = carteAProposer ?? episodeEnCours();
    if (!cible) { texte('g-embed-etat', 'Aucun épisode ouvert : il n’y a rien à annoncer.'); return; }
    texte('g-embed-etat', 'Envoi…');
    const bouton = $('g-embed-envoyer');
    if (bouton) bouton.disabled = true;
    envoyerLaCarte({
      saison: cible.saison ?? null,
      episode: cible.episode ?? null,
      titre: cible.titre ?? '',
      film: cible.film ?? null,
      lien: cible.lien ?? null,
    }).then((r) => {
      texte('g-embed-etat', motDAnnonce(r));
      // Le bouton se rouvre — ou reste fermé si l'envoi a consommé la carte et
      // qu'il n'y a plus rien derrière.
      peindreTeteSeance();
    });
  });

  document.addEventListener('click', () => { if (menuTeteOuvert) fermerMenusTete(); });
  document.addEventListener('keydown', (evt) => {
    if (menuTeteOuvert && evt.key === 'Escape') fermerMenusTete();
  });
  // Redimensionner déplace le bouton sous un volet posé en `fixed` : il le suit
  // au lieu de rester en l'air.
  window.addEventListener('resize', () => {
    const r = menuTeteOuvert ? MENUS_TETE[menuTeteOuvert] : null;
    if (r) placerVoletTete($(r.bouton), $(r.vol));
  });

  peindreTeteSeance();
  preparerInvitations();
}

// ---------------------------------------------------------------------------
// LES DEUX INVITATIONS
//
// Toji : « quelqu'un qui n'est pas sur le serveur, depuis mon activité, va voir
// que je suis dans tel serveur, et il peut rejoindre. Et il y a aussi un
// deuxième bouton pour la vocale directement. »
//
// ⚠️ CE N'EST PAS LA PRÉSENCE RICHE QUI PORTE CES BOUTONS. Le SDK installé
// (@discord/embedded-app-sdk 2.5.0) n'expose aucun champ `buttons` dans
// `setActivity` — vérifié dans `output/commands/setActivity.d.ts`, la charge
// utile s'arrête à `name`, `type`, `details`, `state`, `assets`, `timestamps`,
// `party`, `secrets`, `instance`. Les deux boutons vivent donc dans la tête de
// la console, à l'endroit où l'on se trouve déjà quand on veut faire venir
// quelqu'un.
//
// Le même SDK expose bien `openInviteDialog` — la fenêtre d'invitation native
// de Discord — et ce serait le plus joli des deux chemins. Il n'est pas retenu :
// le lien qu'elle partage est fabriqué par Discord, pas par le bot, et ce sont
// les invitations DU BOT, elles seules, qui attribuent « Shinigami Suppléant » à
// l'arrivée — il les reconnaît en comparant leurs compteurs d'utilisation. Un
// lien natif ferait entrer quelqu'un sans rôle, et personne ne s'en apercevrait
// avant de chercher pourquoi il ne voit rien. (Le SDK la donne d'ailleurs pour
// refusée hors serveur et sans `CREATE_INSTANT_INVITE`, ce qui demanderait un
// `getChannelPermissions` de plus pour savoir s'il faut la proposer.)
//
// `SELECT_VOICE_CHANNEL` figure bien dans l'énumération `Commands` du schéma,
// mais n'a AUCUN emballage dans `output/commands/` : il n'apparaît pas sur
// `sdk.commands`, et il n'y a rien à en tirer sans écrire soi-même dans le
// transport du SDK.
//
// Le lecteur ne fabrique rien : il demande au serveur, qui demande au bot. Un
// lien absent reste absent — le bouton ne paraît pas. Donner à quelqu'un une
// adresse qui ne mène nulle part se découvre trop tard, une fois qu'il a
// cliqué.
// ---------------------------------------------------------------------------

// Le fond de cadence : deux liens qui ne bougent presque jamais ne valent pas
// une requête par seconde. Le bot les sert de son cache, mais l'aller-retour
// reste payé par le VPS et par la séance en cours.
const CADENCE_INVITATIONS = 180_000;

// La marge avant l'échéance de l'invitation vocale, qui elle expire. Un lien
// recopié à la seconde où il meurt ne s'ouvre plus, et c'est celui qui l'a REÇU
// qui le découvre — trop tard, et sans savoir à qui le dire. On redemande donc
// une minute avant plutôt qu'après.
const MARGE_INVITATION = 60_000;

let invitations = { serveur: null, vocal: null, expire: null, salon: null, quand: 0, enVol: false, echecs: 0 };
let invitationsBranchees = false;

/**
 * L'échéance de l'invitation vocale, en millisecondes — ou rien.
 *
 * Le bot compte en millisecondes, mais son banc d'essai la pose en ISO et rien
 * n'interdit à Discord de la rendre ainsi : les deux formes se lisent, plutôt
 * qu'une seule et un lien qu'on croirait éternel.
 */
function finInvitationVocale() {
  const brut = invitations.expire;
  if (brut == null) return null;
  const nombre = typeof brut === 'number' ? brut : Number(brut);
  if (Number.isFinite(nombre) && nombre > 0) return nombre;
  const lu = Date.parse(String(brut));
  return Number.isFinite(lu) ? lu : null;
}

/**
 * Le salon vocal de la séance, d'où qu'on regarde.
 *
 * Le SDK le donne dans l'activité. Dans le navigateur il n'y a pas de SDK,
 * mais l'identifiant de séance EST le salon — `salon-<id>`, voir
 * `identifiantSeance`. Sans cette seconde lecture, l'invitation au vocal
 * manquerait justement à celui qui a ouvert le lecteur dehors, alors que c'est
 * la même séance et le même salon.
 */
function salonDeLaSeance() {
  if (seanceSansSalon()) return null;
  const direct = sdk?.channelId;
  if (direct) return String(direct);
  return /^salon-(\d{17,20})$/.exec(String(etatSeance?.instance ?? ''))?.[1] ?? null;
}

/**
 * La séance tourne HORS de tout salon vocal : le serveur l'a rangée sous
 * `libre-…` (droit `lecture.sans_vocal`, activité lancée hors vocal). Micros,
 * relevé et invitation vocale restent alors inertes au lieu d'échouer.
 */
function seanceSansSalon() { return String(etatSeance?.instance ?? '').startsWith('libre-'); }

/** Le salon vocal sur lequel l'activité peut agir, ou `null`. */
function salonVocalDeLActivite() { return seanceSansSalon() ? null : sdk?.channelId ?? null; }

/** Un lien utilisable, ou rien : `https://discord.gg/...` et pas autre chose. */
function lienInvitation(quel) {
  const lien = invitations[quel];
  if (typeof lien !== 'string' || !/^https:\/\//.test(lien)) return null;
  // Seule celle du vocal expire. Périmée, elle vaut un lien mort : le bouton
  // en moins coûte moins cher que l'adresse qu'on a déjà donnée.
  const fin = quel === 'vocal' ? finInvitationVocale() : null;
  return fin !== null && Date.now() >= fin ? null : lien;
}

/**
 * Va chercher les deux liens, et se borne elle-même.
 *
 * Même mécanique que le relevé vocal : une seule requête en vol, une cadence,
 * et l'abandon après deux échecs plutôt que de marteler. Tant que la réponse
 * ne porte pas de lien, les deux boutons restent cachés — ce qui est aussi le
 * comportement obtenu si la route venait à disparaître.
 */
function rafraichirInvitations(force = false) {
  const salon = salonDeLaSeance();
  if (invitations.enVol) return;
  // Changer de salon vocal change l'invitation vocale : on repart de zéro
  // plutôt que de proposer celle du salon précédent, qui mènerait ailleurs.
  if (salon !== invitations.salon) {
    invitations = { serveur: null, vocal: null, expire: null, salon, quand: 0, enVol: false, echecs: 0 };
    force = true;
  }
  if (!force && invitations.echecs >= ECHECS_AVANT_ABANDON) return;
  const fin = finInvitationVocale();
  const perime = fin !== null && Date.now() + MARGE_INVITATION >= fin;
  if (!force && !perime && Date.now() - invitations.quand < CADENCE_INVITATIONS) return;

  invitations = { ...invitations, enVol: true };
  api(`/api/invitations${salon ? `?salon=${encodeURIComponent(salon)}` : ''}`)
    .then((r) => {
      invitations = {
        // Le serveur rend les deux adresses à plat, ou `null` : rien ici ne
        // fabrique de lien, et une chaîne vide ne passe pas pour une adresse.
        serveur: typeof r?.serveur === 'string' ? r.serveur : null,
        vocal: typeof r?.vocal === 'string' ? r.vocal : null,
        expire: r?.expire ?? null,
        salon, quand: Date.now(), enVol: false, echecs: 0,
      };
      // Le motif part dans la trace, pas à l'écran : « permission-manquante »
      // se règle dans les rôles du serveur Discord, et celui qui regarde la
      // console n'y peut rien. Sans lui, un bouton absent n'aurait
      // d'explication nulle part.
      // ⚠️ L'INVITATION DU SERVEUR MANQUE PAR CONSTRUCTION, PAS PAR PANNE.
      //
      // Le bot ne la rend plus, et c'est la décision de Toji : un bouton
      // « rejoindre le serveur » ne sert à rien là où il s'affiche, puisqu'il
      // faut déjà être entré pour le voir. La vraie porte est l'invitation
      // VOCALE, qui dépose la personne dans le salon de la séance ; l'entrée
      // définitive arrive APRÈS, en privé, avec le message d'accueil.
      //
      // Se plaindre de son absence à chaque cycle remplissait donc le journal
      // d'un « lien manquant · motif-inconnu » qui décrivait un fonctionnement
      // normal. Un journal qui crie à tort finit par ne plus être lu : seul le
      // vocal, dont l'absence est un vrai problème, mérite encore une trace.
      if (!lienInvitation('vocal')) {
        tracer('invitations', 'invitation vocale indisponible', r?.raisons?.vocal ?? null);
      }
      peindreInvitations();
    })
    .catch((err) => {
      invitations = { ...invitations, quand: Date.now(), enVol: false, echecs: invitations.echecs + 1 };
      tracer('invitations', 'liens indisponibles', err.message);
      peindreInvitations();
    });
}

/** Ce que le bouton dit après un clic, et jusqu'à quand. */
const motInvitation = new Map();

function peindreInvitations() {
  for (const [id, quel, quoi] of [
    ['g-inviter-serveur', 'serveur', 'du serveur, permanente'],
    ['g-inviter-vocal', 'vocal', 'du salon vocal, temporaire'],
  ]) {
    const bouton = $(id);
    if (!bouton) continue;
    const lien = lienInvitation(quel);
    bouton.hidden = !lien;
    if (!lien) continue;
    bouton.title = `Copie l’invitation ${quoi}. Elle donne « Shinigami Suppléant » à l’arrivée.`;
    const mot = motInvitation.get(id);
    const el = $(`${id}-mot`);
    if (el && mot && Date.now() < mot.jusqua) el.textContent = mot.texte;
    else if (el && mot) { motInvitation.delete(id); el.textContent = mot.origine; }
  }
}

/**
 * Ce qu'un clic fait du lien.
 *
 * On COPIE, on n'ouvre pas : le geste attendu est de coller l'adresse à
 * quelqu'un qui n'est pas là. L'ouvrir n'aurait de sens que pour soi-même, or
 * on est déjà dans le serveur au moment où l'on clique.
 *
 * Le presse-papier peut être refusé — l'iframe de l'activité n'a pas toujours
 * la permission, et le navigateur l'interdit hors d'un geste utilisateur. Le
 * repli est le vieux champ de texte que l'on sélectionne : moins beau, mais il
 * marche partout, et surtout il ne laisse pas le clic sans effet.
 */
async function copierInvitation(id, lien) {
  const el = $(`${id}-mot`);
  const origine = motInvitation.get(id)?.origine ?? el?.textContent ?? '';
  let copie = false;
  try {
    await navigator.clipboard.writeText(lien);
    copie = true;
  } catch {
    copie = copierParLaVieilleMethode(lien);
  }
  motInvitation.set(id, {
    origine,
    texte: copie ? 'Lien copié' : lien.replace(/^https:\/\//, ''),
    jusqua: Date.now() + VIE_MESSAGE,
  });
  tracer('invitations', copie ? 'lien copie' : 'copie refusee', id);
  peindreInvitations();
}

/** Le repli du presse-papier : un champ hors champ, sélectionné puis copié. */
function copierParLaVieilleMethode(texte_) {
  const champ = document.createElement('textarea');
  champ.value = texte_;
  champ.setAttribute('readonly', '');
  champ.style.position = 'fixed';
  champ.style.opacity = '0';
  document.body.appendChild(champ);
  champ.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  champ.remove();
  return ok;
}

/** Les deux clics, posés une seule fois. */
function preparerInvitations() {
  if (invitationsBranchees) return;
  invitationsBranchees = true;
  for (const [id, quel] of [['g-inviter-serveur', 'serveur'], ['g-inviter-vocal', 'vocal']]) {
    surClic(id, () => {
      const lien = lienInvitation(quel);
      // Le bouton est caché sans lien : y arriver quand même veut dire que
      // l'invitation vient d'expirer entre le rendu et le clic.
      if (!lien) { rafraichirInvitations(true); return; }
      copierInvitation(id, lien);
    });
  }
}

// ---------------------------------------------------------------------------
// LA CONSOLE SUR TÉLÉPHONE : trois onglets, et quatre commandes qui déménagent
// ---------------------------------------------------------------------------
//
// Toji : « pour le téléphone, l'interface, c'est pas du tout adapté pour
// l'espace de gestion. […] À la limite tu peux faire des tabs plutôt que de
// proposer comme ça — tu fais des tabs, et les boutons pratiques tu les mets à
// des emplacements plus logiques pour un téléphone. »
//
// Presque tout se fait dans la feuille de style (voir « LA CONSOLE DE SÉANCE,
// REFAITE POUR LE TÉLÉPHONE » dans index.html) : la grille nommée met les
// commandes en bas, `display: contents` supprime les deux volets et leurs
// défilements imbriqués, et les onglets ne font qu'éteindre ce qu'ils ne
// montrent pas. RIEN N'EST DÉMONTÉ NI RECONSTRUIT — un onglet qui refabriquerait
// son contenu perdrait la position de défilement, la personne désignée et le
// vote en cours.
//
// IL RESTE UNE CHOSE QUE LA FEUILLE NE PEUT PAS FAIRE : sortir un bouton de la
// tête pour le poser ailleurs. La tête réclamait six commandes sur 362 px
// utiles — trois rangées avant même d'arriver au bandeau. Un élément ne se
// déplace pas d'un parent à l'autre en CSS ; on le déplace donc ici, et
// SEULEMENT quand la disposition téléphone est active.
//
// ⚠️ LA REQUÊTE EST LA MÊME QUE CELLE DE LA FEUILLE, MOT POUR MOT.
// Deux conditions qui se ressemblent sans être identiques, c'est le pire des
// deux mondes : un appareil qui reçoit la mise en page de téléphone sans le
// déplacement des boutons, ou l'inverse. Elle est double parce qu'un iPhone 13
// couché mesure 844 × 390 : `max-width: 760px` ne l'attrape pas, et `hover:
// none` garantit qu'aucune fenêtre de bureau aplatie ne reçoit tout ceci —
// c'est ce qui fait que le bureau ne bouge pas d'un pixel.
const REQUETE_TELEPHONE = '(max-width: 760px), (hover: none) and (orientation: landscape) and (max-height: 520px)';
const ecranTelephone = typeof matchMedia === 'function' ? matchMedia(REQUETE_TELEPHONE) : null;

// Qui déménage, et où. L'ordre compte pour le RETOUR : on remet en place à
// l'envers, si bien que le voisin sur lequel chacun se réinsère est déjà rentré.
const DEMENAGEMENTS_TELEPHONE = [
  // L'identifiant de séance rejoint la barre d'onglets : c'est un repère, pas
  // une commande, et il ne mérite pas une rangée à lui.
  ['g-session', 'cs-barre'],
  // Les deux invitations et les deux menus de qui tient la séance : quatre
  // choses qu'on règle une fois, dans l'onglet qui porte ce nom.
  ['g-inviter-serveur', 'cs-panneau-reglages'],
  ['g-inviter-vocal', 'cs-panneau-reglages'],
  ['g-menu-embed', 'cs-panneau-reglages'],
  ['g-menu-vote', 'cs-panneau-reglages'],
  // « Lecteur » descend dans la barre du pouce : c'est le bouton par lequel on
  // retourne à l'image, et il vivait dans le coin le plus dur à atteindre.
  ['g-rouvrir', 'sc-commandes'],
];

let placesOrigine = null;
let dispositionTelephone = false;

/** Où chacun vit sur grand écran. Relevé une seule fois, avant tout déplacement. */
function memoriserPlacesConsole() {
  if (placesOrigine) return;
  placesOrigine = new Map();
  for (const [id] of DEMENAGEMENTS_TELEPHONE) {
    const n = $(id);
    if (n?.parentElement) placesOrigine.set(id, { parent: n.parentElement, avant: n.nextElementSibling });
  }
}

/**
 * Pose ou défait la disposition téléphone de la console.
 *
 * Appelée à chaque battement de `peindreGestion` et à chaque changement de la
 * requête d'affichage : elle sort immédiatement quand il n'y a rien à faire,
 * ce qui est le cas de tous les battements sauf celui qui suit une rotation.
 */
function disposerConsolePourTelephone() {
  const voulu = !!ecranTelephone?.matches;
  memoriserPlacesConsole();
  if (voulu === dispositionTelephone) return;
  dispositionTelephone = voulu;

  if (voulu) {
    for (const [id, vers] of DEMENAGEMENTS_TELEPHONE) {
      const n = $(id); const cible = $(vers);
      if (n && cible) cible.appendChild(n);
    }
    return;
  }
  // Le retour à grand écran remet EXACTEMENT là où c'était, voisin par voisin.
  // À l'envers, pour que le voisin de référence soit déjà rentré ; et si le
  // voisin manque quand même, on remet à la fin plutôt que de jeter une
  // exception qui laisserait le bouton nulle part.
  for (const [id] of [...DEMENAGEMENTS_TELEPHONE].reverse()) {
    const n = $(id); const place = placesOrigine.get(id);
    if (!n || !place?.parent) continue;
    if (place.avant && place.avant.parentElement === place.parent) place.parent.insertBefore(n, place.avant);
    else place.parent.appendChild(n);
  }
}

// Quel onglet est ouvert. Retenu ici et non lu dans le DOM : la console est
// repeinte au rythme des messages, et l'attribut se repose à chaque tour.
let ongletConsole = 'seance';

/** Ouvre un onglet. C'est la feuille de style qui éteint ce qu'il ne montre pas. */
function poserOngletConsole(quel) {
  const vg = $('v-gestion');
  if (!vg) return;
  ongletConsole = quel;
  vg.dataset.onglet = quel;
  for (const b of document.querySelectorAll('#cs-onglets .cs-onglet')) {
    b.setAttribute('aria-selected', b.dataset.onglet === quel ? 'true' : 'false');
  }
}

/**
 * L'onglet « Réglages » disparaît quand il n'a rien à montrer.
 *
 * Ses quatre commandes sont conditionnelles : les invitations n'existent que si
 * le bot en a rendu une, les deux menus n'appartiennent qu'à qui tient la
 * séance. Un onglet vide sur lequel on appuie et où l'on ne trouve rien coûte
 * plus cher qu'un onglet absent — et la place gagnée revient aux deux autres.
 */
function peindreOngletsConsole() {
  const rien = ['g-inviter-serveur', 'g-inviter-vocal', 'g-menu-embed', 'g-menu-vote']
    .every((id) => $(id)?.hidden !== false);
  const onglet = document.querySelector('#cs-onglets .cs-onglet[data-onglet="reglages"]');
  if (onglet) onglet.hidden = rien;
  // On ne laisse personne coincé sur un onglet qui vient de s'en aller.
  if (rien && ongletConsole === 'reglages') poserOngletConsole('seance');
}

/** Les branchements des onglets, posés une seule fois. */
function preparerOngletsConsole() {
  for (const b of document.querySelectorAll('#cs-onglets .cs-onglet')) {
    b.onclick = () => poserOngletConsole(b.dataset.onglet);
  }
  // La sortie de la feuille : sur téléphone la fiche monte PAR-DESSUS la liste,
  // et la rangée sur laquelle on a appuyé — le geste qui la refermait — se
  // retrouve dessous. Le bouton n'existe que là (`display: none` ailleurs).
  surClic('cs-fermer-fiche', () => {
    choisiSeance = null;
    motFiche = { texte: '', jusqua: 0 };
    peindreConsole(etatSeance);
  });
  // Une rotation change la disposition sans recharger la page : les six boutons
  // doivent suivre, sinon ils restent dans un onglet que la feuille n'affiche
  // plus.
  ecranTelephone?.addEventListener?.('change', disposerConsolePourTelephone);
  poserOngletConsole(ongletConsole);
}

function peindreGestion() {
  if ($('v-gestion')?.hidden) return;
  const e = etatSeance;

  // La disposition téléphone d'abord : les commandes qui déménagent doivent
  // être à leur place avant qu'on ne peigne ce qu'elles portent.
  disposerConsolePourTelephone();

  const retour = $('g-retour');
  if (retour) retour.hidden = !!e?.jeSuisHote;
  texte('g-session', e?.instance ? String(e.instance).replace(/^salon-/, '#') : '');
  // Les deux menus de la tête : ils ne s'ouvrent qu'à qui tient la séance, et
  // ils montrent la règle que le SERVEUR applique, pas le dernier clic local.
  peindreTeteSeance(e);
  // Les deux invitations, elles, sont pour tout le monde : faire venir
  // quelqu'un n'est pas un geste de pilote. L'appel se borne tout seul, et il
  // n'a lieu que tant que la console est à l'écran.
  rafraichirInvitations();
  peindreInvitations();
  // Les deux appels ci-dessus viennent de décider qui voit quoi dans la tête :
  // c'est seulement maintenant qu'on sait si l'onglet « Réglages » a quelque
  // chose à montrer.
  peindreOngletsConsole();

  // --- ce qu'on regarde ------------------------------------------------------
  // Celui qui rejoint doit le voir avant de lire quoi que ce soit.
  //
  // ⚠️ `episodeEnCours()` et NON `e.ouvrir` : ce sont deux sources, et elles
  // ne disaient pas la même chose. La barre juste en dessous affichait
  // « Épisode 412 · changer » pendant que ce bandeau montrait « Épisode en
  // cours » sans titre, sans numéro et sans vignette — capture à l'appui.
  //
  // `e.ouvrir` est ce que la séance a diffusé ; il n'arrive qu'après l'aller-
  // retour, et il manque entièrement quand le message n'est jamais parti (une
  // ouverture de lecteur qui échoue, par exemple). `episodeEnCours()` prend ce
  // que la séance dit SI elle l'a dit, et ce qu'on vient de lancer sinon. Un
  // bandeau qui se vide parce qu'un message n'est pas revenu ne décrit rien.
  const ouvert = episodeEnCours();
  const episode = ouvert && serie?.arcs
    ? (serie.arcs.flatMap((a) => a.episodes ?? [])
        .find((v) => v.season === ouvert.saison && v.episode === ouvert.episode) ?? null)
    : null;

  const bandeau = $('g-bandeau');
  if (bandeau) bandeau.hidden = !ouvert && !e?.actif;

  const src = episode?.thumbnail || '';
  const image = $('g-image');
  if (image) {
    if (src && image.getAttribute('src') !== src) image.src = src;
    image.parentElement.hidden = !src;
  }
  // La meme vignette, etalee et floutee derriere tout le bandeau : elle donne
  // sa couleur a la barre sans qu'on ait a la lire.
  const fond = $('g-fond');
  if (fond) {
    const voulu = src ? `url("${src}")` : 'none';
    if (fond.style.backgroundImage !== voulu) fond.style.backgroundImage = voulu;
  }

  // --- le surtitre : LA COLLECTION, et sa partie dans une étiquette à droite --
  //
  // Cette ligne écrivait « Saison 17 · Épisode 46 » : deux nombres qu'on lisait
  // AVANT de savoir ce qu'on regarde, alors que la question posée en ouvrant la
  // console est « où en est la soirée ». On y répond par un nom, pas par des
  // coordonnées.
  //
  // ⚠️ ICI, DEUX REPLIS ET NON UN — Séance n'est pas mono-série.
  //
  // La collection d'abord (l'arc, la partie, le cycle) : c'est le contexte le
  // plus fin. À défaut, le titre de l'ŒUVRE, qui reste utile quand une série
  // n'a aucun découpage — cas ordinaire, pas cas dégradé. Et seulement si l'on
  // ne sait rien, les coordonnées : elles restent vraies, et un surtitre vide
  // pendant les premières secondes de chargement se prendrait pour une panne.
  const collection = (serie?.arcs ?? []).find((a) => (a.episodes ?? []).some(
    (v) => v.season === ouvert?.saison && v.episode === ouvert?.episode,
  )) ?? null;

  texte('g-numero', collection?.nom
    || serie?.titre
    || serie?.name
    || (ouvert ? `Saison ${ouvert.saison} · Épisode ${ouvert.episode}` : ''));

  // « Part » et non « Partie » : le menu de changement d'épisode, juste en
  // dessous, écrit déjà « Part N ». Deux abréviations pour le même objet sur le
  // même écran se lisent comme deux objets. Le jour où l'on préférera
  // « Partie », c'est partout à la fois.
  const etiquettePartie = $('st-g-partie');
  if (etiquettePartie) {
    etiquettePartie.hidden = !collection?.partie;
    etiquettePartie.textContent = collection?.partie ? `Part ${collection.partie}` : '';
  }
  texte('g-nom', ouvert?.titre || episode?.title || (e?.actif ? 'Épisode en cours' : 'Aucun épisode'));
  texte('g-resume', episode?.resume ?? '');

  // Les reperes : la note d'abord — c'est ce qu'on cherche en premier sur un
  // episode qu'on ne connait pas. Puis la duree, la diffusion, les langues.
  const jalons = $('g-lignes');
  if (jalons) {
    // La note de l'episode quand elle existe, celle de la serie sinon : une
    // note de serie vaut mieux que pas de note, tant qu'on ne la fait pas
    // passer pour celle de l'episode.
    const note = Number(episode?.note ?? NaN);
    const noteSerie = Number(serie?.note ?? NaN);
    const morceaux = [];
    if (Number.isFinite(note) && note > 0) {
      morceaux.push(`<span class="j"><span class="g-note">${note.toFixed(1).replace('.', ',')}<small>IMDb</small></span></span>`);
    } else if (Number.isFinite(noteSerie) && noteSerie > 0) {
      morceaux.push(`<span class="j" title="Note de la série — cet épisode n’en a pas"><span class="g-note">${noteSerie.toFixed(1).replace('.', ',')}<small>IMDb</small></span></span>`);
    }
    // ⚠️ LA VRAIE DURÉE, PAS UN ARRONDI À LA MINUTE.
    //
    // C'était `${Math.round(duree_ / 60)} min` : 24 min 33 devenait « 25 min »,
    // et 24 min 29 devenait « 24 min ». Deux épisodes à quatre secondes d'écart
    // s'affichaient donc avec une minute de différence — et surtout, la présence
    // Discord du MÊME épisode montre « 00:14 / 24:33 », la valeur mesurée. Le
    // même épisode annonçait deux durées différentes à deux centimètres d'écart.
    //
    // `e.duree` est la durée mesurée du fichier, en secondes. On ne l'arrondit
    // pas : `dureeLisible` la découpe (voir l'incident consigné au-dessus
    // d'elle, dans oeuvres.js). La durée du CATALOGUE prend le relais tant que
    // la séance n'a rien mesuré — mieux vaut la durée annoncée que rien.
    const duree_ = e?.duree || episode?.duree || 0;
    if (duree_ > 0) morceaux.push(`<span class="j">${esc(dureeLisible(duree_))}</span>`);
    if (episode?.released) {
      morceaux.push(`<span class="j">${dateFr(episode.released, { day: 'numeric', month: 'long', year: 'numeric' })}</span>`);
    }
    // ⚠️ `episode.langues` ÉTAIT LU EN DIRECT ICI, et par `versionsDeLEpisode`
    // dans la grille : deux endroits qui répondaient à « puis-je le regarder en
    // français ? » par deux chemins différents, donc deux réponses possibles
    // pour le même épisode à deux centimètres d'écart. Un seul chemin.
    const versions = versionsDeLEpisode(episode);
    if (versions.langues.length) {
      // ⚠️ DEUX DRAPEAUX SÉPARÉS, jamais le carré coupé en diagonale : le Japon
      // à gauche, la France à droite. Voir l'incident au-dessus de `MIXTE` dans
      // drapeaux.js — un drapeau hybride ne répond pas à la question posée,
      // « puis-je le regarder en français ? ».
      morceaux.push(`<span class="j j-langues">${drapeauxDe(versions.langues, { type: oeuvre?.type, mesure: versions.mesure })}</span>`);
    }
    const html = morceaux.join('');
    if (jalons.innerHTML !== html) jalons.innerHTML = html;
  }

  peindreAvancee();

  // --- ce que la ligne d'état disait, et pourquoi elle n'est plus là ---------
  //
  // « Séance en cours » et « X tient la séance » redisaient le bandeau juste
  // au-dessus, qui porte déjà l'affiche, le titre, le résumé et l'avancée. Deux
  // textes pour un même fait ne se lisent pas deux fois : ils se peignent à deux
  // battements différents, et l'écran se contredit lui-même le temps d'une
  // seconde.
  //
  // ⚠️ LES DEUX `texte()` SONT PARTIS AVEC LE BALISAGE, volontairement.
  // `texte()` avale l'élément absent sans un mot : les laisser aurait donné deux
  // écritures qui ne peignent rien, et personne pour s'en apercevoir avant le
  // jour où l'on remet la ligne. Le nœud du compte des versions, lui, EST resté
  // (sous `.st-legs`) — c'est pour cela que `combien` survit ici.
  const combien = e?.participants?.length ?? 0;

  // Les langues : le drapeau d'abord, le compte dessous — on lit la langue
  // avant le nombre. Le détail des noms vient au survol.
  const zoneLangues = $('g-langues');
  if (zoneLangues) {
    zoneLangues.hidden = !combien;
    const par = (test) => (e?.participants ?? []).filter(test);
    const vf = par((p) => p.langue === 'vf');
    const vo = par((p) => p.langue && p.langue !== 'vf');
    for (const [id, gens, cle] of [['g-langue-vf', vf, 'vf'], ['g-langue-vo', vo, 'vo']]) {
      const el = $(id);
      if (!el) continue;
      // Les emoji drapeaux ne rendent rien sous Windows : on prend les traces
      // du projet, dessines une fois aux proportions officielles.
      const dessin = el.querySelector('i');
      if (dessin && !dessin.firstChild) dessin.innerHTML = DRAPEAUX[cle] ?? '';
      el.querySelector('b').textContent = String(gens.length);
      // Un drapeau que personne n'écoute DISPARAÎT, il ne se grise pas.
      // Affiché avec un zéro, il annonce une version qui n'existe pas
      // forcément : constaté sur l'épisode 412, dont aucun fichier n'est en VF
      // — le drapeau français était là quand même, et il n'y avait rien
      // derrière. Une pastille vide se lit comme une promesse.
      el.hidden = gens.length === 0;
      el.dataset.vide = gens.length ? '0' : '1';
      el.title = gens.length ? gens.map(pseudoServeur).join(', ') : 'personne';
    }
  }

  // La console prend le relais : commandes globales, demandes, votes, et la
  // fiche de qui est designe. Elle ne recalcule rien de ce que le serveur
  // tranche deja.
  peindreConsole(e);
}

/**
 * La fiche de l'œuvre que la séance regarde, pour la vignette : celle déjà en
 * mémoire, sinon demandée UNE fois, et la vignette se repeint à son arrivée.
 */
const fichesDemandeesVignette = new Set();
function ficheDeLaVignette(id) {
  const f = fichesEnMemoire.get(id)?.fiche ?? null;
  if (f || fichesDemandeesVignette.has(id)) return f;
  fichesDemandeesVignette.add(id);
  api(`/api/oeuvre/${encodeURIComponent(id)}`)
    .then((fiche) => { fichesEnMemoire.set(id, { fiche, quand: Date.now() }); peindreVignette(); })
    .catch(() => fichesDemandeesVignette.delete(id));
  return null;
}

/**
 * La vignette, quand Discord réduit l'activité.
 *
 * Trois lignes, pas une de plus : le cadre fait 320 x 180. Ce qui compte est
 * ce qu'on ne peut pas deviner de l'extérieur — quel épisode, où il en est,
 * combien de personnes sont devant.
 */
function peindreVignette() {
  // La vignette est peinte des que le cadre en a la taille : l evenement de
  // disposition de Discord, lui, n arrive pas toujours.
  if (!$('vignette')) return;

  const e = etatSeance;
  const regardent = e?.participants?.filter((p) => p.regarde).length ?? 0;

  // ⚠️ L'ÉTAT ACTUEL, PAS LE DERNIER ÉPISODE CONNU (VisionQuest, 21/10).
  //
  // La séance garde `ouvrir` quand le dernier spectateur part (pour qu'on la
  // rejoigne), et `episodeDeLaSeance` ne s'oublie jamais : la vignette montrait
  // donc l'œuvre d'avant — avec la saison de la fiche ouverte comme titre et
  // « 0 devant l'épisode ». Un épisode ne s'affiche que si quelqu'un le
  // regarde, ou s'il joue ICI (lecture sur place dans l'activité).
  // `episodeEnCours()` et non `e.ouvrir` : même divergence de sources que le
  // bandeau de la console (vignette sur « 14ème Division » en pleine lecture).
  const lectureIci = !$('v-lecture')?.hidden;
  const ou = e?.actif && (regardent > 0 || lectureIci) ? episodeEnCours() : null;
  // La fiche de L'ŒUVRE DE LA SÉANCE — la fiche chargée ici peut en être une autre.
  const ficheSerie = ou?.serie ? ficheDeLaVignette(ou.serie) : null;
  const ici = !!ou?.serie && serie?.id === ou.serie;
  const fiche = ou?.saison
    ? ((ici ? episodeCatalogue(ou.saison, ou.episode) : null)
      ?? ficheSerie?.saisons?.find((s) => s.numero === ou.saison)?.episodes
        ?.find((v) => v.numero === ou.episode) ?? null)
    : null;

  // L'IMAGE EN FOND : l'épisode, sinon la série (fond, puis affiche). Le 集 de
  // la tête ne reste qu'en dernier repli, quand il n'y a aucune image.
  const fond = $('vg-fond');
  const src = !ou ? null
    : (fiche?.thumbnail ?? fiche?.vignette ?? null)
      || fondDeLOeuvre(ficheSerie) || (ici ? (fondDeLOeuvre(oeuvre?.id === ou.serie ? oeuvre : null) || serie?.background || serie?.poster) : null)
      || null;
  if (fond) {
    const voulu = src ? `url("${src}")` : '';
    if (fond.style.backgroundImage !== voulu) fond.style.backgroundImage = voulu;
    fond.hidden = !src;
  }
  const tete = document.querySelector('#vignette .vg-tete');
  if (tete) tete.hidden = !!src;

  // ⚠️ DEUX LIGNES, ET LA PARTIE EN ÉTIQUETTE.
  //
  // Toji : « tu me mets la guerre sanglante de mille ans, tu me mets le tag de
  // l'épisode — le tag, c'est simplement Part 4 —, tu me mets le nom de
  // l'épisode, et là il y a un doublon : ça note Arc 17 Épisode 412. Je ne suis
  // pas fan. »
  //
  // Il avait raison : le titre disait déjà « … — Part 4 — Épisode 412 », et la
  // ligne du dessous répétait « ARC 17 · ÉPISODE 412 ». Deux fois la même chose
  // dans six centimètres de hauteur, au prix d'une ligne qu'on n'a pas.
  //
  // La partie sort du nom de l'arc et devient une étiquette : c'est une
  // précision, pas un titre, et elle n'a pas à manger la largeur de la phrase.
  const arcs = ici ? (serie?.arcs ?? []) : [];
  const i = ou?.saison
    ? arcs.findIndex((a) => (a.episodes ?? []).some(
      (v) => v.season === ou.saison && v.episode === ou.episode,
    ))
    : -1;
  // « Saison 2 » n'est pas un arc : c'est le nom de l'œuvre qui titre alors.
  const brut = i >= 0 ? (arcs[i].nom ?? arcs[i].titre ?? null) : null;
  const nomArc = brut && !/^(Saison\s*\d+|Épisodes spéciaux)$/i.test(brut.trim()) ? brut : null;
  const coupe = nomArc ? /^(.*?)[\s—–-]+((?:Part|Partie)\s*\d+)\s*$/i.exec(nomArc) : null;
  const nomOeuvre = ficheSerie?.titre ?? (ici ? (oeuvre?.id === ou.serie ? oeuvre?.titre : null) ?? serie?.name : null);

  texte('vg-titre', (ou ? (coupe?.[1] ?? nomArc ?? nomOeuvre ?? ou.titre ?? MARQUE) : MARQUE).trim());

  const part = $('vg-part');
  if (part) {
    const etiquette = coupe?.[2]?.trim() ?? '';
    part.textContent = etiquette;
    part.hidden = !etiquette;
  }

  // « S1E2 · titre » (Toji, 22/10). Dans un vrai arc, c'est le numéro absolu
  // qui compte (412, jamais S17E46).
  const nomEpisode = fiche?.title ?? fiche?.titre ?? null;
  const absolu = fiche?.absolu > 0 ? fiche.absolu : null;
  const numero = !ou?.saison ? null
    : (nomArc && absolu ? `Épisode ${absolu}` : `S${ou.saison}E${ou.episode}`);
  texte('vg-episode', [numero, nomEpisode].filter(Boolean).join(' · '));

  const position = !ou ? 0 : (typeof e?.position === 'function' ? e.position() : (e?.position ?? 0));
  const duree = ou ? (e?.duree ?? 0) : 0;
  const barre = $('vg-progres');
  if (barre) barre.style.width = duree ? `${Math.min(100, (position / duree) * 100)}%` : '0';

  texte('vg-ecoule', ou ? horlogeCourte(position) : '');
  texte('vg-totale', duree ? horlogeCourte(duree) : '');

  // LES VISAGES DE CEUX QUI REGARDENT, sous la barre. Un chiffre seul ne dit
  // pas qui est là ; à cette taille, trois visages en disent plus qu'une phrase.
  // Au-delà de quatre on compte, on n'aligne plus : la vignette fait six
  // centimètres.
  // ⚠️ LA RANGÉE S'ÉTEND VERS LA DROITE, ET LE BLOC RESTE CENTRÉ.
  //
  // Toji : « une fois qu'il y a 50 personnes, les PP s'ajoutent sur la gauche —
  // c'est un bloc centré au milieu, et la PP s'ajoute vers la droite en fonction
  // des arrivées. Une fois qu'il y a trop de personnes, tu mets un rond avec un
  // +, et le chiffre en dehors du rond : + tant de personnes. Et en dessous, le
  // nombre total. Le total n'apparaît qu'à partir du moment où il y a le +. »
  //
  // Cinq visages tiennent dans la largeur d'une vignette de six centimètres. Au
  // sixième, on cesse d'aligner : un rond de plus ne dit plus qui est là, il ne
  // fait que rétrécir les autres. Le rond ne porte que le SIGNE — le nombre vit
  // dehors, dans le texte, où il se lit sans loupe.
  const VISAGES_MAX = 5;
  const visages = $('vg-visages');
  const tous = (e?.participants ?? []).filter((p) => p.regarde);
  const avecPhoto = tous.filter((p) => p.avatar);
  const montres = avecPhoto.slice(0, VISAGES_MAX);
  // Le « + » dit un dépassement, pas un visage sans photo : un seul spectateur
  // sans avatar affichait « + 1 personne · 1 au total » (22/10).
  const enPlus = Math.max(0, regardent - VISAGES_MAX);

  if (visages) {
    const signature = montres.map((p) => p.avatar).join('|') + `|+${enPlus}`;
    if (visages.dataset.signature !== signature) {
      visages.dataset.signature = signature;
      visages.textContent = '';
      for (const p of montres) {
        const img = document.createElement('img');
        img.src = p.avatar;
        img.alt = '';
        img.loading = 'lazy';
        visages.append(img);
      }
      if (enPlus > 0) {
        const rond = document.createElement('span');
        rond.className = 'vg-plus';
        rond.textContent = '+';
        visages.append(rond);
      }
    }
  }

  // Le texte de la ligne : ce que le rond ne dit pas.
  // ⚠️ JAMAIS « 0 devant l'épisode » (Toji, 22/10) : sans personne devant,
  // il n'y a pas de lecture en cours, et la vignette le dit.
  texte('vg-etat', !ou
    ? 'Aucune lecture en cours'
    : (enPlus > 0
      ? `+ ${enPlus} ${pluriel(enPlus, 'personne')}`
      : `${regardent} devant l'épisode`));

  // Le total ne s'écrit que lorsqu'un « + » l'appelle : sans dépassement, il
  // répéterait le chiffre déjà posé juste à côté.
  const total = $('vg-total');
  if (total) {
    total.hidden = !(e?.actif && enPlus > 0);
    if (!total.hidden) total.textContent = `${regardent} au total`;
  }
}

// ---------------------------------------------------------------------------
// La console de séance
//
// Deux volets : la liste des personnes à gauche, la fiche de celle qu'on
// désigne à droite. On désigne d'abord, on agit ensuite — un tableau à huit
// colonnes obligerait à retrouver la ligne de quelqu'un avant de pouvoir agir
// sur lui.
//
// RIEN N'EST RECALCULÉ ICI. Le seuil d'un vote, le compte des voix, le droit
// de piloter et le nombre de spectateurs viennent tels quels de l'état que le
// serveur publie. Deux comptes vaudraient un désaccord, et c'est le sien qui
// déclenche.
//
// Et AUCUN écart de synchronisation n'est affiché, nulle part : seance.js
// rattrape en continu (0,25 s ignoré, ±7 % de vitesse jusqu'à 1,2 s, saut
// au-delà). Montrer « +3 s » ferait croire qu'il reste un geste à faire sur ce
// qui se répare tout seul. La seule anomalie qui mérite d'être dite est la
// CONNEXION PERDUE, que le serveur publie par participant.
// ---------------------------------------------------------------------------

/** Un gabarit de la page, cloné. Le balisage reste du balisage. */
const gabarit = (id) => $(id)?.content?.firstElementChild?.cloneNode(true) ?? null;

// Ce qu'un vote propose, en français. Les trois seules actions sur lesquelles
// le serveur accepte d'ouvrir un vote.
const PROPOSITIONS_VOTE = {
  intro: 'propose de passer l’intro.',
  pause: 'propose une pause pour tout le monde.',
  suivant: 'propose de passer à l’épisode suivant.',
};

// Combien de temps un compte rendu d'action reste affiché avant que la ligne
// ne redevienne l'explication permanente. Assez pour être lu sans revenir,
// assez court pour ne pas rester en travers de la suite.
const VIE_MESSAGE = 7000;

let motConsole = { texte: '', jusqua: 0 };
let motFiche = { texte: '', jusqua: 0 };
const direConsole = (t) => { motConsole = { texte: t, jusqua: Date.now() + VIE_MESSAGE }; };
const direFiche = (t) => { motFiche = { texte: t, jusqua: Date.now() + VIE_MESSAGE }; };
const encoreDit = (mot) => (Date.now() < mot.jusqua ? mot.texte : '');

// Les micros que Discord refuse DÉFINITIVEMENT de couper : le bot est sous la
// personne dans la hiérarchie des rôles, ou il n'a pas MUTE_MEMBERS. Retenu
// par personne, parce que le bouton doit se présenter éteint la fois suivante
// au lieu de proposer puis échouer — cela se règle dans les réglages du
// serveur Discord, jamais en recliquant.
const microsInterdits = new Map();

// ---------------------------------------------------------------------------
// Qui est dans le salon vocal, et quel micro y est coupé
//
// L'état de séance ne le dit pas : il ne connaît que les pages ouvertes. La
// réponse vient du bot, qui la tient de mémoire. Quand le bot ne répond pas,
// le serveur retombe sur Discord et doit énumérer tout le serveur compte par
// compte — c'est pourquoi on n'interroge pas en boucle serrée, et qu'après
// deux échecs on cesse d'y revenir tout seul plutôt que de marteler.
// ---------------------------------------------------------------------------
const CADENCE_VOCAL = 15_000;
const ECHECS_AVANT_ABANDON = 2;
let vocal = { membres: null, quand: 0, enVol: false, echecs: 0 };

function rafraichirVocal(force = false) {
  const salon = salonVocalDeLActivite();
  if (!salon || vocal.enVol) return;
  if (!force && vocal.echecs >= ECHECS_AVANT_ABANDON) return;
  if (!force && Date.now() - vocal.quand < CADENCE_VOCAL) return;

  vocal = { ...vocal, enVol: true };
  api(`/api/vocal/membres?salon=${encodeURIComponent(salon)}`)
    .then((r) => {
      vocal = {
        membres: Array.isArray(r.membres) ? r.membres : [],
        quand: Date.now(), enVol: false, echecs: 0,
      };
      if (!$('v-gestion')?.hidden) peindreGestion();
    })
    .catch((err) => {
      vocal = { ...vocal, quand: Date.now(), enVol: false, echecs: vocal.echecs + 1 };
      tracer('vocal', 'liste du salon indisponible', err.message);
    });
}

/** Ce que le vocal sait de cette personne, ou rien tant qu'il n'a pas répondu. */
const auVocal = (id) => (vocal.membres ? vocal.membres.find((m) => m.id === id) ?? null : null);

/** Suis-je administrateur ? La réponse est dans l'état, pas dans mes rôles. */
const jeSuisAdmin = (e) => !!(e?.participants ?? []).find((p) => p.id === moi?.id)?.admin;

/**
 * Le pseudo tel que le SERVEUR le connaît, pas le compte Discord.
 *
 * La séance retient le pseudo que chaque client lui a annoncé en arrivant, et
 * ce client-là n'avait pas forcément mieux que son nom de compte sous la main :
 * la socket s'ouvre avant que `/api/moi` ait répondu, et le pseudo annoncé
 * n'est corrigé qu'à la reconnexion suivante. C'est ce qui faisait apparaître
 * des noms de compte là où le serveur affiche un surnom.
 *
 * Le relevé du salon vocal, lui, vient du backend qui interroge Discord : son
 * pseudo est le `nick` du serveur, celui sous lequel les autres connaissent la
 * personne. Il est DÉJÀ en mémoire — la console le sonde toutes les 15 s pour
 * l'état des micros — donc le préférer ne coûte pas une requête de plus. Pour
 * soi-même, `/api/moi` fait mieux encore : il répond même hors du salon vocal.
 */
function pseudoServeur(p) {
  if (p.id === moi?.id && moi?.pseudo) return moi.pseudo;
  return auVocal(p.id)?.pseudo || p.pseudo;
}

/**
 * Le rang, sur UNE ligne.
 *
 * Ce qui tenait ici avant — « administrateur · la main en permanence ·
 * connexion perdue » — disait trois choses que la fiche dit déjà mieux, chacune
 * en face de son intitulé. Sous un pseudo, il n'y a la place que pour une, et
 * c'est le rang.
 *
 * `rang` et `rangCouleur` sont le rôle le plus haut sur le serveur Discord et
 * sa couleur. Ils viennent de `/api/moi`, partent avec le bonjour de la séance
 * et reviennent sur chaque participant — le client ne peut pas les déduire, et
 * le serveur ne les redemande pas à Discord à chaque arrivée.
 *
 * Le repli — le rang DANS LA SÉANCE, en un mot — sert quand ils manquent : une
 * session ouverte avant ce changement, ou un serveur Discord injoignable. Il
 * reste juste, simplement moins intéressant que « Fondateur ».
 */
const COULEUR_HEXA = /^#[0-9a-f]{6}$/i;

function rangDe(p, e) {
  if (p.rang) {
    return {
      mot: String(p.rang),
      // La couleur vient du serveur et part dans un style en ligne : on ne
      // laisse passer qu'un hexadécimal, jamais une valeur CSS quelconque.
      couleur: COULEUR_HEXA.test(String(p.rangCouleur ?? '')) ? p.rangCouleur : null,
    };
  }
  const mot = p.admin ? 'Administrateur'
    : p.id === e?.hote ? 'Hôte'
      : p.main ? 'Pilote'
        : p.regarde ? 'Spectateur' : 'Dans la gestion';
  return { mot, couleur: null };
}

/** Pose le rang et sa couleur ; sans couleur connue, le gris de la feuille. */
function poserRang(el, p, e) {
  if (!el) return;
  const { mot, couleur } = rangDe(p, e);
  el.textContent = mot;
  el.style.color = couleur ?? '';
}

/**
 * Le drapeau de la version écoutée — et RIEN quand on ne la connaît pas.
 *
 * Même règle que les compteurs de langue de la gestion : un drapeau affiché à
 * vide annonce une version qui n'existe pas forcément. Ici l'absence est même
 * le cas courant — tant que la personne laisse le lecteur choisir, sa langue
 * vaut « auto », et il n'y a rien à annoncer.
 *
 * Le dessin vient de `drapeaux.js` : les emoji drapeaux ne rendent rien sous
 * Windows, et leur chasse ne s'aligne sur rien.
 */
function poserDrapeau(el, p) {
  if (!el) return;
  // Une personne n'écoute qu'UNE langue : `drapeauxDe` rend donc ici un seul
  // drapeau. C'est bien la même fonction qu'ailleurs — un seul endroit sait
  // traduire `vf`/`vostfr`/`ja` en dessin, et il n'y a pas deux façons de le
  // faire selon l'écran.
  const dessin = drapeauxDe([p.langue], { type: oeuvre?.type });
  el.hidden = !dessin;
  el.innerHTML = dessin;
}

/** L'étiquette de la version écoutée, ou rien quand la personne laisse choisir. */
const tagLangue = (p) => (!p.langue || p.langue === 'auto' ? null : p.langue === 'vf' ? 'VF' : 'VO');
/**
 * Celle des sous-titres, distincte : on peut écouter en VO sans en avoir.
 *
 * Les fichiers annoncent leur langue dans trois normes qui coexistent —
 * « fr », « fre », « fra » désignent la même chose, et un tag qui affiche
 * « FRE » ne se lit pas, il se déchiffre. On les ramène donc toutes aux deux
 * lettres qu'on lit d'un coup d'œil, à côté d'un VO ou d'un VF qui font la
 * même longueur.
 */
const DEUX_LETTRES = {
  fre: 'FR', fra: 'FR', fr: 'FR', french: 'FR', francais: 'FR', 'français': 'FR',
  eng: 'EN', en: 'EN', english: 'EN', anglais: 'EN',
  jpn: 'JA', jap: 'JA', ja: 'JA', japanese: 'JA', japonais: 'JA',
  spa: 'ES', es: 'ES', ger: 'DE', deu: 'DE', de: 'DE', ita: 'IT', it: 'IT',
};
function tagSousTitres(p) {
  if (!p.sousTitres) return null;
  const brut = String(p.sousTitres).trim().toLowerCase();
  // La forme longue d'abord — « français (forcés) » commence par « fra », et
  // couper à deux lettres donnerait « FR » sans qu'on sache si c'est un
  // hasard. On cherche donc le code complet, puis sa racine.
  const connu = DEUX_LETTRES[brut] ?? DEUX_LETTRES[brut.slice(0, 3)] ?? DEUX_LETTRES[brut.slice(0, 2)];
  return connu ?? brut.slice(0, 2).toUpperCase();
}

/** Pose ou efface une étiquette sans laisser un cadre vide derrière elle. */
function poserTag(el, valeur) {
  if (!el) return;
  el.hidden = !valeur;
  el.textContent = valeur ?? '';
}

// ---------------------------------------------------------------------------
// L'ORDRE DE LA LISTE : la hiérarchie du serveur, pas l'ordre d'arrivée
//
// Toji : « le rôle est à tel emplacement, du coup tu places à tel
// emplacement ». `rangPosition` est la hauteur du rôle le plus haut de la
// personne dans le serveur Discord — le plus grand nombre est en haut de la
// liste des rôles, `@everyone` vaut zéro. Elle voyage depuis `/api/moi` avec le
// nom du rôle et sa couleur, par le bonjour de la séance : le client ne peut
// pas la déduire d'un nom.
//
// La coupure entre ceux qui pilotent et les autres RESTE au-dessus du rang :
// c'est elle qu'on vient chercher quand on ouvre la console, et un fondateur
// qui ne pilote pas n'est pas la personne à qui s'adresser.
//
// À rang égal, le pseudo tranche, puis l'identifiant. La console est repeinte
// une fois par seconde : sans départage stable, deux personnes du même rôle
// échangeraient leur place à chaque battement et l'on cliquerait sur la
// mauvaise. Tant que le serveur ne publie pas la hauteur, tout le monde est à
// égalité et la liste est simplement alphabétique — un ordre qui tient debout,
// contrairement à l'ordre d'arrivée qu'il remplace.
// ---------------------------------------------------------------------------
function hauteurDeRang(p) {
  const n = Number(p?.rangPosition);
  return p?.rangPosition == null || !Number.isFinite(n) ? -1 : n;
}

function parRangDiscord(a, b) {
  const ecart = hauteurDeRang(b) - hauteurDeRang(a);
  if (ecart) return ecart;
  const nom = pseudoServeur(a).localeCompare(pseudoServeur(b), 'fr', { sensitivity: 'base', numeric: true });
  return nom || String(a.id).localeCompare(String(b.id));
}

// ---------------------------------------------------------------------------
// LES TROIS PRÉSENCES : dans le vocal, dans l'activité, devant le lecteur
//
// ⚠️ ÊTRE DANS LE VOCAL NE SUFFIT PAS À PARTICIPER. Toji : « ce n'est pas juste
// que quelqu'un est dans le vocal, il faut qu'il ait lancé l'activité aussi. Le
// suivi démarre à partir du moment où la personne a lancé l'activité. »
// Quelqu'un dans le salon sans l'activité discute à côté : il n'entre ni dans
// les votes ni dans cette liste. C'est déjà le découpage du serveur — la séance
// ne connaît que ceux qui lui ont dit bonjour — et ces trois marques ne servent
// donc pas à décider qui compte, mais à voir où en est chacun de ceux qui
// comptent.
//
// `null` veut dire « on ne sait pas », et ne se dessine JAMAIS comme un « non ».
// C'est la nuance qui coûte cher ici : le registre du bot met un moment à
// répondre, et pendant ce temps personne n'est hors du vocal. Deux façons de le
// taire, selon la portée :
//   · la présence n'est renseignée pour PERSONNE — vieux serveur, salon vocal
//     jamais relevé : la colonne entière disparaît, toutes les rangées perdent
//     la même marque et restent alignées ;
//   · la présence existe mais manque à UNE personne — elle vient d'arriver, son
//     `vocal` n'a pas encore été relevé : sa marque garde sa place et reste
//     invisible. La retirer décalerait sa rangée seule, et une liste dont les
//     icônes sautent d'un cran par personne se relit à chaque battement.
// ---------------------------------------------------------------------------
const MARQUES_ETAT = [
  ['vocal', 'dans le vocal', 'pas dans le vocal'],
  ['activite', 'dans l’activité', 'pas dans l’activité'],
  ['lecteur', 'devant le lecteur', 'pas devant le lecteur'],
];

function etatsDe(p) {
  return {
    // Le champ de la séance d'abord ; sinon le relevé du salon, que la console
    // sonde déjà pour l'état des micros — le demander deux fois ne dirait rien
    // de plus.
    vocal: typeof p.vocal === 'boolean' ? p.vocal : (vocal.membres ? !!auVocal(p.id) : null),
    activite: typeof p.activite === 'boolean' ? p.activite : null,
    lecteur: !!p.regarde,
  };
}

/**
 * Les colonnes que cette séance sait renseigner — la question porte sur la
 * LISTE, pas sur une personne.
 *
 * `registreVocal.fiable` est la réponse du serveur à « ma colonne vocale
 * vaut-elle quelque chose ? » : c'est elle qu'on lit, plutôt que de la
 * redéduire. Le relevé du salon que la console sonde déjà la remplace quand la
 * séance est trop ancienne pour la publier, et un seul participant renseigné
 * suffit à prouver que le serveur sait le faire.
 */
function etatsConnus(e) {
  const gens = e?.participants ?? [];
  return {
    vocal: !!vocal.membres
      || !!e?.registreVocal?.fiable
      || gens.some((p) => typeof p.vocal === 'boolean'),
    activite: gens.some((p) => typeof p.activite === 'boolean'),
    lecteur: true,
  };
}

/**
 * Pose les trois marques, et la phrase complète au survol de la rangée.
 *
 * ⚠️ L'ATTRIBUT, PAS `hidden` : ces marques sont des `SVGElement`, et `hidden`
 * n'appartient qu'à `HTMLElement`. Lui écrire `.hidden = true` poserait une
 * propriété JavaScript que le sélecteur `[hidden]` ne verrait jamais — la
 * marque resterait à l'écran, éteinte, c'est-à-dire en train d'annoncer « non »
 * pour une présence qu'on ne connaît pas.
 */
function poserEtats(zone, p, connus) {
  if (!zone) return;
  const etats = etatsDe(p);
  const dits = [];
  for (const [cle, oui, non] of MARQUES_ETAT) {
    const marque = zone.querySelector(`[data-etat="${cle}"]`);
    if (!marque) continue;
    // Pas d'attribut : la colonne n'existe pas ici. « ? » : elle existe, mais
    // pas encore pour cette personne — la place est gardée, l'encre retirée.
    if (!connus[cle]) { marque.removeAttribute('data-vrai'); continue; }
    const su = typeof etats[cle] === 'boolean';
    marque.dataset.vrai = !su ? '?' : etats[cle] ? '1' : '0';
    if (su) dits.push(etats[cle] ? oui : non);
  }
  zone.title = dits.join(' · ');
}

/** Une ligne de personne, depuis le gabarit de la page. */
function ligneConsole(p, e, connus) {
  const ligne = gabarit('sc-tpl-ligne');
  if (!ligne) return null;
  ligne.dataset.id = p.id;
  // La connexion perdue s'affiche sans prendre de mots : la feuille éteint la
  // photo. L'écrire sous le pseudo coûtait la ligne du rang pour redire ce que
  // la fiche annonce déjà en face de « Connexion ».
  ligne.dataset.perdu = p.perdu ? '1' : '0';
  ligne.classList.toggle('choisi', p.id === choisiSeance);

  const photo = ligne.querySelector('.sc-avatar');
  if (p.avatar) photo.src = p.avatar; else photo.removeAttribute('src');
  // La couronne se pose sur la photo de TOUS ceux qui ont la main — l'hôte, un
  // administrateur, quelqu'un à qui on l'a donnée. `main` les couvre tous les
  // trois : le serveur y met déjà l'administrateur (voir `aLaMain`).
  //
  // ⚠️ `toggleAttribute` ET NON `.hidden` : la couronne est un `<svg>`, et
  // `hidden` n'appartient qu'à `HTMLElement` — sur un `SVGElement`, `.hidden =
  // false` posait un champ JavaScript sans jamais retirer l'attribut écrit dans
  // le gabarit. La couronne restait donc éteinte pour tout le monde, y compris
  // pour qui tient la séance. Même piège que les marques de présence ci-dessus.
  ligne.querySelector('.sc-couronne')?.toggleAttribute('hidden', !p.main);

  // ⚠️ COUPER LES REQUÊTES DE QUELQU'UN — le bouton de l'arbitre.
  //
  // Toji : « imaginons quelqu'un qui spamme les demandes de pause, ça va juste
  // devenir casse-pied […] un bouton pour un admin, évidemment pas pour un
  // membre standard, pour empêcher quelqu'un de spammer les requêtes. La
  // personne peut rester là, elle peut regarder, mais par contre elle ne peut
  // pas spammer les requêtes. »
  //
  // C'est volontairement la plus petite sanction possible : elle ne touche ni
  // à la présence, ni au micro, ni à la lecture. Seuls les boutons de demande
  // et de vote cessent de partir.
  //
  // Il ne s'affiche que pour qui tient la séance, et jamais sur soi-même ni sur
  // un autre pilote — le serveur refuse les deux de toute façon, mais un bouton
  // qui ne fait rien est pire qu'un bouton absent.
  const bouton = ligne.querySelector('.sc-sourdine');
  if (bouton) {
    const musele = (e?.musellees ?? []).includes(p.id);
    const proposable = !!e?.jAiLaMain && p.id !== moi?.id && !p.main;
    bouton.hidden = !proposable;
    bouton.dataset.actif = musele ? '1' : '0';
    bouton.textContent = musele ? 'Rendre' : 'Couper';
    bouton.title = musele
      ? 'Rendre à cette personne le droit de demander et de voter.'
      : 'Couper ses demandes et ses votes. Elle reste dans la séance et continue de regarder.';
    const agir = (ev) => {
      // La ligne entière est un bouton : sans cela, couper les requêtes
      // sélectionnerait aussi la personne.
      ev.stopPropagation();
      ev.preventDefault();
      seance?.sourdine?.(p.id, musele);
    };
    bouton.onclick = agir;
    bouton.onkeydown = (ev) => { if (ev.key === 'Enter' || ev.key === ' ') agir(ev); };
  }

  ligne.querySelector('.sc-pseudo').textContent = pseudoServeur(p) + (p.id === moi?.id ? ' (toi)' : '');
  poserRang(ligne.querySelector('.sc-role'), p, e);
  poserDrapeau(ligne.querySelector('.sc-drapeau'), p);
  poserEtats(ligne.querySelector('.sc-etats'), p, connus);
  return ligne;
}

/** La jauge d'un vote : des cases qu'on compte du regard, pas un pourcentage. */
function remplirJauge(cases, voix, seuil) {
  cases.textContent = '';
  for (let i = 0; i < seuil; i += 1) {
    const c = gabarit('sc-tpl-case');
    if (!c) return;
    if (i < voix) c.classList.add('pleine');
    cases.appendChild(c);
  }
}

/**
 * Les demandes et les votes, en TÊTE de la liste.
 *
 * Ce sont les deux seules choses de cet écran qui attendent une réponse : plus
 * bas, on les découvrirait après coup, une fois la scène passée.
 */
function peindreAlertes(zone, e) {
  zone.textContent = '';

  // Les demandes ne parviennent qu'à ceux qui peuvent y répondre — le serveur
  // ne les diffuse pas aux autres.
  for (const d of (e?.jAiLaMain ? e.demandes : []) ?? []) {
    const carte = gabarit('sc-tpl-demande');
    if (!carte) break;
    carte.querySelector('.sc-qui').textContent = d.pseudo;
    carte.querySelector('.sc-quoi').textContent = phraseDemande(d);
    carte.querySelector('.sc-accepter').onclick = () => repondreDemande(d, true);
    carte.querySelector('.sc-refuser').onclick = () => repondreDemande(d, false);
    zone.appendChild(carte);
  }

  // Qui tranche seul ne vote pas : son clic exécute, et le bouton doit le dire.
  const seul = !!e?.jAiLaMain && e?.reglages?.exemptionPilotes !== false;
  // Le compte du serveur ne retient QUE les voix de ceux qui regardent — sinon
  // l'intro deviendrait insautable dès que deux personnes discutent à côté.
  // Proposer « se joindre » à qui gère depuis l'activité lui ferait donner une
  // voix qui serait retirée du compte sans que rien ne le dise. Ouvrir un vote
  // reste permis, lui : demander au groupe ne suppose pas d'être devant.
  const moiIci = (e?.participants ?? []).find((p) => p.id === moi?.id) ?? null;
  // Tant qu'on ne sait pas qui l'on est, on ne bloque rien.
  const jeRegarde = moiIci ? !!moiIci.regarde : true;
  for (const v of e?.votes ?? []) {
    const carte = gabarit('sc-tpl-vote');
    if (!carte) break;
    carte.querySelector('.sc-qui').textContent = v.par ?? 'quelqu’un';
    carte.querySelector('.sc-quoi').textContent = PROPOSITIONS_VOTE[v.action] ?? 'propose une action commune.';
    // Le seuil porté par le vote prime sur celui de l'état : c'est celui avec
    // lequel les voix ont été comptées.
    const seuil = Math.max(1, Number(v.seuil ?? e.seuil ?? 0) || 1);
    const voix = Math.max(0, Number(v.voix ?? 0));
    remplirJauge(carte.querySelector('.sc-cases'), voix, seuil);
    carte.querySelector('.sc-compte').textContent = `${voix} / ${seuil}`;

    // Le vote se referme SEUL au seuil : le serveur l'efface et l'action part.
    // Aucun bouton ne le valide, sans quoi ce ne serait plus un vote.
    const bouton = carte.querySelector('.sc-voter');
    const donnee = (e.mesVoix ?? []).includes(v.action);
    bouton.textContent = seul ? 'Décider maintenant'
      : !jeRegarde ? 'Il faut regarder pour voter'
        : donnee ? 'En attente' : 'Se joindre';
    bouton.disabled = !seul && (donnee || !jeRegarde);
    bouton.onclick = () => seance?.voter(v.action);
    zone.appendChild(carte);
  }
}

/**
 * Accepter une demande, et poser le geste que l'accord suppose.
 *
 * `dire` désigne l'endroit où le compte rendu s'affiche : la ligne de la
 * console quand on répond depuis les alertes, la note de la fiche quand on
 * répond depuis la personne. Un résultat écrit ailleurs que là où l'on a
 * cliqué est un résultat que personne ne lit.
 */
function repondreDemande(d, accepte, dire = direConsole) {
  seance?.repondre(d.id, accepte);
  if (!accepte) return dire(`Demande de ${d.pseudo} refusée.`);
  // Rendre la parole, c'est lever un server mute chez Discord : la séance
  // annonce la décision (vocal.js le dit explicitement), elle ne sait pas la
  // poser. C'est ici que ça se fait, sinon l'accord reste sans effet.
  if (d.nature === 'parole') return microDe(d.de, false, d.pseudo, dire);
  return dire(`Demande de ${d.pseudo} acceptée.`);
}

/**
 * Coupe ou rend le micro d'UNE personne.
 *
 * Le serveur distingue `cause: 'interdit'` — le bot est sous la personne dans
 * la hiérarchie des rôles, ou il n'a pas le droit — du reste : ce refus-là ne
 * se répare pas en recliquant, et le bouton doit s'éteindre pour de bon.
 */
async function microDe(id, muet, pseudo, dire = direFiche) {
  const salon = salonVocalDeLActivite();
  if (!salon || !id) return dire('Salon vocal inconnu.');
  dire(muet ? 'Coupure en cours…' : 'Rétablissement en cours…');
  peindreConsole(etatSeance);
  try {
    const bilan = await api('/api/vocal/muter-membre', {
      method: 'POST', body: JSON.stringify({ salon, membre: id, muet }),
    });
    if (bilan.ok) {
      microsInterdits.delete(id);
      dire(`${pseudo ?? 'Micro'} — ${muet ? 'micro coupé' : 'micro rendu'}.`);
    } else if (bilan.cause === 'interdit') {
      microsInterdits.set(id, bilan.raison ?? 'le bot ne peut pas agir sur cette personne');
      dire(`Impossible : ${bilan.raison ?? 'le rôle du bot est sous le sien'}.`);
    } else {
      dire(bilan.raison ?? 'Le micro n’a pas bougé.');
    }
    tracer('vocal', muet ? 'micro coupe' : 'micro rendu', bilan);
  } catch (err) {
    dire(err.message);
  }
  // L'état des micros vient de changer : on le redemande sans attendre le
  // rythme de croisière, sinon la fiche affiche encore l'état d'avant.
  rafraichirVocal(true);
  peindreConsole(etatSeance);
}

/** La fiche de la personne désignée : ce qu'elle écoute, et ce qu'on peut faire. */
function peindreFiche(e) {
  const fiche = $('sc-detail');
  const vide = $('sc-detail-vide');
  if (!fiche || !vide) return;

  const p = (e?.participants ?? []).find((x) => x.id === choisiSeance) ?? null;
  // Quelqu'un qui s'en va emporte sa fiche : garder la dernière laisserait des
  // boutons qui agissent sur une personne absente.
  if (!p) choisiSeance = null;
  fiche.hidden = !p;
  vide.hidden = !!p;
  if (!p) return;

  poserEtats($('sc-detail-etats'), p, etatsConnus(e));
  const photo = $('sc-detail-avatar');
  if (photo) { if (p.avatar) photo.src = p.avatar; else photo.removeAttribute('src'); }
  const couronne = $('sc-detail-couronne');
  // L'attribut, pas la propriété — voir `ligneConsole` : sur un `<svg>`,
  // `.hidden` n'existe pas et la couronne ne se rallumait jamais.
  couronne?.toggleAttribute('hidden', !p.main);
  texte('sc-detail-pseudo', pseudoServeur(p) + (p.id === moi?.id ? ' (toi)' : ''));
  // Même règle qu'en liste : sous le pseudo, le rang et rien d'autre. Ce que
  // la personne fait dans la séance a sa propre ligne plus bas (« Pilotage »),
  // et sa connexion aussi — les répéter ici en ferait deux sources.
  poserRang($('sc-detail-role'), p, e);

  poserTag($('sc-detail-langue'), tagLangue(p));
  texte('sc-detail-version', p.langue === 'vf' ? 'française'
    : p.langue && p.langue !== 'auto' ? 'originale'
      : 'au choix du lecteur');
  poserTag($('sc-detail-st'), tagSousTitres(p));
  texte('sc-detail-soustitres', p.sousTitres ? 'une piste affichée' : p.regarde ? 'aucun' : '—');

  const micro = auVocal(p.id);
  texte('sc-detail-micro', !vocal.membres ? '—'
    : !micro ? 'hors du salon vocal'
      : micro.mute ? 'coupé' : 'ouvert');

  texte('sc-detail-pilotage', p.admin ? 'la main en permanence'
    : p.id === e.hote ? 'tient la séance'
      : p.main ? 'peut piloter' : 'ne pilote pas');

  const connexion = $('sc-detail-connexion');
  if (connexion) {
    connexion.textContent = p.perdu ? 'perdue' : 'en ligne';
    connexion.classList.toggle('perdu', !!p.perdu);
  }

  peindreActionsFiche(p, e, micro);
}

/**
 * Les boutons de la fiche.
 *
 * Une commande qu'on sait ne PAS pouvoir aboutir se présente éteinte, avec sa
 * raison écrite en dessous : la proposer puis échouer ferait passer une règle
 * de Discord ou du serveur pour une panne du lecteur, et l'on chercherait le
 * défaut du mauvais côté.
 */
function peindreActionsFiche(p, e, micro) {
  const moiMain = !!e?.jAiLaMain;
  const distribue = !!e?.jeSuisHote || jeSuisAdmin(e);
  const raisons = [];

  // --- la main ---------------------------------------------------------------
  const donner = $('sc-donner-main');
  const retirer = $('sc-retirer-main');
  if (donner && retirer) {
    donner.hidden = !!p.main;
    retirer.hidden = !p.main;
    let bloque = null;
    if (!distribue) bloque = 'Seuls l’hôte et les administrateurs distribuent la main.';
    else if (p.id === e.hote) bloque = 'L’hôte tient la séance : sa main ne se retire pas.';
    else if (p.admin) bloque = 'Un administrateur porte la main : elle ne lui a pas été donnée.';
    donner.disabled = !!bloque;
    retirer.disabled = !!bloque;
    if (bloque) raisons.push(bloque);
    donner.onclick = () => { seance?.donnerLaMain(p.id, true); direFiche(`${p.pseudo} peut piloter.`); };
    retirer.onclick = () => { seance?.donnerLaMain(p.id, false); direFiche(`${p.pseudo} ne pilote plus.`); };
  }

  // --- le micro --------------------------------------------------------------
  const couper = $('sc-muter');
  const rendre = $('sc-demuter');
  if (couper && rendre) {
    // Tant que le vocal n'a pas répondu, on ne connaît pas l'état du micro : on
    // propose de couper, qui est le geste qu'on vient chercher.
    const coupe = !!micro?.mute;
    couper.hidden = coupe;
    rendre.hidden = !coupe;
    const interdit = microsInterdits.get(p.id) ?? null;
    let bloque = null;
    if (seanceSansSalon()) bloque = 'Séance hors vocal : aucun micro à toucher.';
    else if (!sdk?.channelId) bloque = 'Salon vocal inconnu : les micros ne se touchent que depuis l’activité.';
    else if (!moiMain) bloque = 'Il faut piloter pour toucher au micro de quelqu’un.';
    else if (vocal.membres && !micro) bloque = `${p.pseudo} n’est pas dans le salon de la séance.`;
    else if (interdit) bloque = `Micro hors de portée du bot : ${interdit}. Cela se règle dans les rôles du serveur Discord.`;
    couper.disabled = !!bloque;
    rendre.disabled = !!bloque;
    if (bloque) raisons.push(bloque);
    couper.onclick = () => microDe(p.id, true, p.pseudo);
    rendre.onclick = () => microDe(p.id, false, p.pseudo);
  }

  // --- la parole -------------------------------------------------------------
  // Ce bouton n'existe que s'il y a quelque chose à accorder : une demande de
  // parole en attente. Sans elle, il ferait double emploi avec « Rétablir le
  // micro » sans le dire.
  const parole = $('sc-donner-parole');
  if (parole) {
    const demande = ((e?.jAiLaMain ? e.demandes : []) ?? [])
      .find((d) => d.de === p.id && d.nature === 'parole') ?? null;
    parole.hidden = !demande;
    parole.disabled = false;
    parole.onclick = () => { if (demande) repondreDemande(demande, true, direFiche); };
  }

  const note = $('sc-detail-note');
  if (note) note.textContent = encoreDit(motFiche) || raisons.join(' ');
}

/**
 * L'empreinte de ce que la liste et les alertes montrent.
 *
 * La console est repeinte une fois par seconde. Reconstruire des lignes
 * identiques à chaque battement ferait clignoter le survol et couperait un
 * clic en cours : on ne redessine que lorsque le contenu a réellement changé.
 */
function empreinteConsole(e) {
  return JSON.stringify([
    choisiSeance,
    e?.hote ?? null,
    !!e?.jAiLaMain,
    // Mon identifiant arrive après coup (`/api/moi`) : sans lui dans
    // l'empreinte, « (toi) » ne serait jamais posé sur ma propre ligne.
    moi?.id ?? null,
    // `rangPosition` décide de l'ORDRE des deux listes, `vocal` et `activite`
    // des marques posées sur chaque rangée : les taire ici ferait garder à
    // l'écran une liste rangée par une hiérarchie qui a changé, ou des marques
    // d'il y a une minute.
    (e?.participants ?? []).map((p) => [p.id, p.pseudo, p.rang, p.rangCouleur, p.rangPosition, p.avatar, p.main, p.admin, p.regarde, p.vocal, p.activite, p.perdu, p.langue, p.sousTitres]),
    // Le surnom du serveur arrive par le relevé du salon vocal, quinze
    // secondes après le reste : sans lui ici, la liste garderait les noms
    // affichés au premier battement et ne les corrigerait jamais.
    //
    // ⚠️ LE BOOLÉEN À CÔTÉ DE LA LISTE N'EST PAS UN DOUBLON. « Je ne sais pas »
    // (`null`) et « personne » (`[]`) donnent la même liste vide une fois
    // repliés, et c'est exactement la distinction qui commande les marques :
    // sans lui, un salon qui répond enfin « personne » laisserait les marques
    // du vocal masquées pour toujours.
    !!vocal.membres,
    // Et la même distinction, côté séance : c'est ce drapeau qui ouvre ou
    // ferme la colonne du vocal sur toute la liste.
    !!e?.registreVocal?.fiable,
    (vocal.membres ?? []).map((m) => [m.id, m.pseudo]),
    (e?.demandes ?? []).map((d) => [d.id, d.de, d.nature, d.action, d.pseudo]),
    (e?.votes ?? []).map((v) => [v.action, v.voix, v.seuil, v.par]),
    e?.mesVoix ?? [],
    e?.reglages?.exemptionPilotes !== false,
  ]);
}
let empreinteVue = null;

function peindreConsole(e) {
  const zoneAlertes = $('sc-alertes');
  const zonePilotes = $('sc-liste-pilotes');
  const zoneSpectateurs = $('sc-liste-spectateurs');
  if (!zoneAlertes || !zonePilotes || !zoneSpectateurs) return;

  // Deux groupes, et l’ordre compte : ceux qui peuvent agir d’abord. Une liste
  // unique triée par pseudo obligerait à lire chaque ligne pour savoir à qui
  // s’adresser. DANS chaque groupe, la hiérarchie du serveur (voir
  // `parRangDiscord`) : `filter` rend un tableau neuf, le trier ne touche pas
  // à l’état que le serveur publie.
  const gens = e?.participants ?? [];
  const peuventPiloter = gens.filter((p) => p.main).sort(parRangDiscord);
  const spectateurs = gens.filter((p) => !p.main).sort(parRangDiscord);

  // --- les compteurs sont partis, et voici pourquoi on ne les remet pas ------
  //
  // « N dans le vocal · N dans l'activité · N regardent · N pilotent » disait en
  // quatre nombres ce que les deux colonnes juste en dessous disent déjà rangée
  // par rangée, avec un nom en face de chaque marque.
  //
  // ⚠️ UN TOTAL NE SE VÉRIFIE PAS. Quand il ne tombe pas juste, on ne sait pas
  // laquelle des vingt rangées il compte de travers — et c'est arrivé deux fois
  // sur cet écran : « Regardent » a chapeauté des gens qui ne regardaient pas,
  // puis le compteur du vocal a donné la taille du SALON pendant que les marques
  // comptaient les participants, affichant « 0 dans le vocal » au-dessus de
  // plusieurs micros verts. Une liste nominative n'a pas ce défaut : elle se
  // relit ligne à ligne, et l'erreur porte un nom.
  //
  // Les marques de présence de chaque rangée restent la seule source (voir
  // `poserEtats` et `etatsDe`). `e.regardent`, `e.dansLActivite` et `e.enVocal`
  // continuent d'arriver du serveur — cet écran ne les lit simplement plus.

  // La pause a quitté cette barre. Elle passe désormais par le même chemin que
  // « passer l'intro » — le lecteur pour qui pilote, un vote pour les autres —
  // et ce vote s'affiche dans les alertes, juste en dessous. Deux entrées pour
  // le même geste, dont une seule se voyait par le reste du groupe, faisaient
  // croire que la pause n'avait pas pris.
  peindreMenuEpisode(e);

  // UN SEUL BOUTON, qui dit ce qu'il va faire.
  //
  // « Couper les micros » et « Rétablir » côte à côte laissaient toujours l'un
  // des deux sans objet, et il fallait lire les deux pour deviner l'état
  // courant. Un bouton qui s'inverse porte l'information dans son libellé.
  const micros = $('sc-micros');
  if (micros) {
    // L'état vient du relevé vocal, pas de la séance : c'est Discord qui sait
    // qui est coupé. « Tout le monde est coupé » se lit sur ceux qui ne
    // pilotent PAS — qui tient la séance n'est jamais coupé, le compter
    // rendrait le bouton faux dès la première coupure.
    const aCouper = (vocal.membres ?? []).filter((m) => m.id !== e?.hote);
    const coupes = aCouper.length > 0 && aCouper.every((m) => m.mute);
    micros.disabled = !salonVocalDeLActivite() || !e?.jAiLaMain;
    micros.textContent = coupes ? 'Rétablir les micros' : 'Couper les micros';
    micros.onclick = () => microsDuSalon(!coupes);
  }

  // L'annonce : visible seulement quand un épisode attend d'être annoncé.
  peindreInviteCarte();

  texte('sc-etat', encoreDit(motConsole) || (
    !e?.actif ? 'Aucune séance en cours.'
      : !e.jAiLaMain
        ? 'Tu ne pilotes pas : la pause et les sauts passent par un vote, depuis le lecteur.'
        : e.reglages?.exemptionPilotes === false
          ? 'Tu pilotes, mais tout le monde vote : ta pause ouvre un vote comme les autres.'
          : 'Tu pilotes : changer d’épisode le lance chez tout le monde.'
  ));

  // --- la fiche, repeinte à chaque fois : elle est faite d'éléments fixes ----
  peindreFiche(e);

  const empreinte = empreinteConsole(e);
  if (empreinte === empreinteVue) return;
  empreinteVue = empreinte;

  peindreAlertes(zoneAlertes, e);
  texte('sc-nb-groupe-pilotes', String(peuventPiloter.length));
  texte('sc-nb-groupe-spectateurs', String(spectateurs.length));
  // Les mêmes marques sur les deux groupes : ce qui est inconnu l'est pour tout
  // le monde, et la décision se prend une fois pour la liste entière.
  const connus = etatsConnus(e);
  for (const [zone, liste] of [[zonePilotes, peuventPiloter], [zoneSpectateurs, spectateurs]]) {
    zone.textContent = '';
    for (const p of liste) {
      const ligne = ligneConsole(p, e, connus);
      if (ligne) zone.appendChild(ligne);
    }
  }
}

/** Couper ou rendre les micros de TOUT le salon. Réservé à qui pilote. */
async function microsDuSalon(muet) {
  const salon = salonVocalDeLActivite();
  if (!salon) return direConsole('Salon vocal inconnu.');
  direConsole(muet ? 'Coupure en cours…' : 'Rétablissement en cours…');
  peindreConsole(etatSeance);
  try {
    const bilan = await api('/api/vocal/muter', {
      method: 'POST', body: JSON.stringify({ salon, muet }),
    });
    // Le bilan compte : un échec de permission passerait sinon pour un silence
    // obéissant.
    direConsole(bilan.echecs?.length
      ? `${bilan.ok.length}/${bilan.demandes} — ${bilan.echecs[0].raison}`
      : `${bilan.ok.length} micro(s) ${muet ? 'coupé(s)' : 'rendu(s)'}.`);
    tracer('vocal', muet ? 'coupure du salon' : 'micros rendus', bilan);
  } catch (err) {
    direConsole(err.message);
  }
  rafraichirVocal(true);
  peindreConsole(etatSeance);
}

/**
 * Les branchements qui ne dépendent pas de l'état : ils sont posés une fois.
 *
 * Le choix d'une personne passe par la LISTE, pas par chaque ligne : les
 * lignes sont refaites à chaque changement, et un écouteur par ligne serait
 * reposé autant de fois.
 */
function preparerConsoleSeance() {
  for (const id of ['sc-liste-pilotes', 'sc-liste-spectateurs']) {
    const zone = $(id);
    if (!zone) continue;
    zone.onclick = (evt) => {
      const ligne = evt.target.closest('.sc-ligne');
      if (!ligne) return;
      choisiSeance = ligne.dataset.id === choisiSeance ? null : ligne.dataset.id;
      motFiche = { texte: '', jusqua: 0 };
      // L'empreinte change avec la sélection : le tour suivant redessinera les
      // deux listes avec la bonne ligne allumée.
      peindreConsole(etatSeance);
    };
  }
  preparerMenuEpisode();
  // Les trois onglets du téléphone. Posés ici parce qu'ils sont du même ordre
  // que le choix d'une personne : un branchement qui ne dépend pas de l'état,
  // et qui n'a donc à être fait qu'une fois.
  preparerOngletsConsole();
}

// ---------------------------------------------------------------------------
// Le menu de changement d'épisode
//
// Il remplace « Pause pour tous » dans la barre de commandes, et il ne
// remplace pas seulement un bouton : la pause était le seul geste de cette
// barre qui existait déjà ailleurs, sous une autre forme (le lecteur, un
// vote). Ce menu, lui, n'existe nulle part ailleurs depuis la console.
//
// UNE SEULE SOURCE DE VÉRITÉ : `collections` pour les planches et `serie.arcs`
// pour la suite — exactement ce dont l'écran des épisodes se sert. Recopier
// ici une hiérarchie parallèle la ferait diverger au premier arc ajouté dans
// `arcs.js`.
//
// CE QUE ÇA COÛTE EN RÉSEAU : rien, ou presque. Les collections sont déjà dans
// la page (`window.__COLLECTIONS__`), les arcs sont déjà en mémoire dès qu'une
// série a été ouverte. Le menu ne déclenche au plus qu'un seul appel — le
// `/api/meta/series/:id` que l'écran de la série fait de toute façon — et
// seulement si la console est ouverte sans qu'aucune série ait été chargée
// (quelqu'un qui rejoint une séance déjà partie). Les vignettes sont les mêmes
// adresses que la grille des épisodes, servies par notre `/api/img` en
// `cache-control: public, max-age=86400`, et posées en `loading="lazy"` : une
// vignette déjà vue ne repart pas sur le réseau, et une liste de soixante
// épisodes n'en demande que ce qui tient à l'écran.
// ---------------------------------------------------------------------------

// Où l'on est dans le menu. `pile` porte les niveaux traversés, ce qui donne
// le bouton de retour sans avoir à recalculer d'où l'on vient.
//
// `quete` est ce qui est tapé dans la recherche, `index` le catalogue préparé
// pour elle. L'index est refait à l'ouverture et à chaque changement de série,
// jamais à la frappe : normaliser quatre cents titres par touche pressée
// coûterait pour rien ce qu'une seule fois suffit à payer.
let menuEp = { pile: [], ouvert: false, quete: '', index: [], indexDe: null };

// Ce que la recherche pose dans la liste au maximum. Une lettre seule ramène
// des centaines d'épisodes ; le volet en montre six à la fois, et fabriquer
// quatre cents lignes à chaque touche pour n'en regarder jamais plus d'une
// vingtaine coûterait le rendu sans rien apporter.
const MAX_RESULTATS_MENU = 60;

/** Les planches qui ont réellement quelque chose derrière elles. */
const planchesGarnies = () => Object.entries(PLANCHES)
  .filter(([cle]) => (collections[cle] ?? []).length)
  .map(([cle, def]) => ({ cle, libelle: def.libelle }));

/**
 * L'arc où l'on est, dans la série chargée.
 *
 * La série est vérifiée : deux séries peuvent avoir un S1E5, et allumer le
 * mauvais arc parce que les numéros coïncident serait pire que ne rien
 * allumer. Une cible sans série nommée est acceptée — les anciens messages de
 * séance n'en portaient pas.
 */
const arcCourantDe = (ou) => (ou && serie && (!ou.serie || ou.serie === serie.id)
  ? arcDeLEpisode(serie.arcs, ou.saison, ou.episode)
  : null);

/** L'épisode que la séance regarde, quel que soit celui qui l'a lancé. */
const episodeEnCours = () => etatSeance?.ouvrir ?? episodeDeLaSeance ?? null;

/**
 * CE QU'IL FAUT ROUVRIR, ET AVEC QUELLE ŒUVRE — PURE, éprouvée sans navigateur.
 *
 * ⚠️ LE DÉFAUT QUE CETTE FONCTION TIENT : le bouton « Lecteur » lisait bien
 * l'épisode de la séance, puis appelait `lancer(saison, episode)` — une
 * fonction qui prend l'ŒUVRE dans la variable globale `serie`, et ne regarde
 * JAMAIS celle que la séance a nommée. Deux façons d'échouer, muettes toutes
 * les deux :
 *
 *   · `serie` vaut `null` — on vient d'arriver sur la console, on a rechargé
 *     l'onglet, on a rejoint une séance déjà commencée, on n'a encore ouvert
 *     aucune fiche. `lancer()` pose alors son verrou (`lancementEnCours = true`)
 *     PUIS lève sur `serie.id` en écrivant sa trace de lancement. Le clic ne
 *     rendait rien à l'écran — la promesse rejetée n'était attrapée nulle part —
 *     et surtout le verrou ne redescendait jamais : tout lancement ultérieur,
 *     y compris depuis le menu des épisodes, repartait en silence sur
 *     « clic ignore — un lancement est deja en cours ». UN SEUL CLIC
 *     CONDAMNAIT LE LECTEUR POUR LE RESTE DE LA SESSION.
 *
 *   · `serie` est chargée, mais ce n'est pas celle de la séance — on a
 *     parcouru le catalogue depuis la console entre-temps : on ouvrait le
 *     S2E5 d'une AUTRE œuvre, parce que les numéros coïncident. C'est le même
 *     piège qu'`arcCourantDe` documente quelques lignes plus haut.
 *
 * Tous les autres chemins d'ouverture chargent l'œuvre d'abord — `surOuvrir`
 * (`ouvrirSerie(cible.serie).then(suite)`), `preparerLeRetour`
 * (`serieChargee(cible.serie)`), la liste des séances. Celui-ci était le SEUL
 * à s'en passer, et c'est le seul bouton dont le nom promet d'ouvrir le
 * lecteur.
 *
 * @param {object} ouvert   ce que la séance regarde (`episodeEnCours()`)
 * @param {object} memoire  ce que CE navigateur a retenu (`positionMemorisee()`)
 * @param {string} serieCourante l'œuvre déjà chargée, `null` si aucune
 * @returns {{quoi:'episode'|'film'|'rien', serie:?string, saison:number,
 *           episode:number, film:?string, charger:boolean, raison:string}}
 */
export function cibleDeRouverture({ ouvert = null, memoire = null, serieCourante = null } = {}) {
  const rien = (raison) => ({
    quoi: 'rien', serie: null, saison: 0, episode: 0, film: null, charger: false, raison,
  });
  // Les deux sources ne parlent pas le même dialecte : la séance rend des
  // nombres, la mémoire d'onglet des chaînes (`URLSearchParams`). Sans cette
  // conversion, `lancer()` recevait du texte et `saison < seuilVF.saison` se
  // comparait sur des chaînes.
  const nombre = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0; };

  const versEpisode = (oeuvreId, saison, ep, raison) => {
    const s = oeuvreId ?? serieCourante ?? null;
    // ⚠️ SANS ŒUVRE, ON NE LANCE PAS — et on le dit. C'est très exactement
    // l'appel qui posait le verrou avant de lever : ne rien faire en
    // l'annonçant vaut mieux qu'un clic qui tue tous les suivants.
    if (!s) return rien('épisode sans œuvre nommée, et aucune œuvre chargée');
    return {
      quoi: 'episode', serie: s, saison: nombre(saison), episode: nombre(ep),
      film: null, charger: s !== serieCourante, raison,
    };
  };

  // L'ÉPISODE DE LA SÉANCE D'ABORD, la position mémorisée seulement ensuite.
  //
  // Ce bouton ne lisait que `positionMemorisee()` — ce que CE navigateur a
  // retenu de sa dernière lecture réussie. Or on rouvre le lecteur précisément
  // quand la lecture n'a PAS eu lieu : rien n'était mémorisé, le bouton ne
  // lançait rien et répondait « choisis un épisode dans le catalogue » à
  // quelqu'un qui venait d'en choisir un. La séance, elle, savait parfaitement
  // qu'on était sur le 412.
  if (nombre(ouvert?.saison) > 0 && nombre(ouvert?.episode) > 0) {
    return versEpisode(ouvert.serie ?? null, ouvert.saison, ouvert.episode, 'épisode de la séance');
  }
  if (ouvert?.film) {
    return {
      quoi: 'film', serie: null, saison: 0, episode: 0,
      film: ouvert.film, charger: false, raison: 'film de la séance',
    };
  }

  // La mémoire locale reste utile pour un cas qu'elle seule couvre : revenir
  // sur une séance qu'on a quittée et qui n'a plus d'épisode ouvert.
  if (memoire?.serie && nombre(memoire.saison) > 0 && nombre(memoire.episode) > 0) {
    return versEpisode(memoire.serie, memoire.saison, memoire.episode, 'dernière lecture de cet onglet');
  }
  return rien('ni épisode de séance ni lecture mémorisée');
}

/**
 * Rouvre la fenêtre de lecture sur ce que la séance regarde.
 *
 * ⚠️ RIEN NE SORT D'ICI SANS SE VOIR. L'ancien écouteur rendait la promesse de
 * `lancer()` au gestionnaire de clic, qui la jette : un rejet ne laissait ni
 * message à l'écran, ni ligne de trace. « Je clique et il ne se passe rien »
 * était littéralement vrai — il se passait quelque chose, et ce quelque chose
 * échouait sans témoin.
 *
 * Les dépendances sont des paramètres pour que le banc éprouve CE chemin-là et
 * pas une imitation : `essais/rouvrir-le-lecteur.mjs` lui passe une œuvre
 * absente, une œuvre étrangère, un chargement qui échoue, et vérifie à chaque
 * fois qu'on charge AVANT de lancer et qu'on ne rejette jamais.
 */
export async function rouvrirLeLecteur({
  ouvert = episodeEnCours(),
  memoire = positionMemorisee(),
  serieCourante = serie?.id ?? null,
  charger = serieChargee,
  lancerEpisode = lancer,
  lancerLeFilm = lancerFilm,
  filmDuCatalogue = (id) => Object.values(collections).flat().find((f) => f.id === id) ?? null,
  dire = (m) => texte('g-etat-action', m),
  noter = (quoi, detail) => tracer('lecteur', quoi, detail),
} = {}) {
  const cible = cibleDeRouverture({ ouvert, memoire, serieCourante });
  noter('rouverture demandee', cible);

  if (cible.quoi === 'film') {
    const fiche = filmDuCatalogue(cible.film);
    if (!fiche) {
      dire('film de la séance introuvable au catalogue');
      return { fait: 'echec', raison: 'film introuvable', cible };
    }
    await lancerLeFilm(fiche);
    return { fait: 'film', raison: cible.raison, cible };
  }

  if (cible.quoi !== 'episode') {
    dire('choisis un épisode dans le catalogue');
    return { fait: 'rien', raison: cible.raison, cible };
  }

  // L'ŒUVRE D'ABORD, LE LANCEMENT ENSUITE. `serieChargee` et non `ouvrirSerie` :
  // la seconde bascule la vue sur la fiche de l'œuvre, ce qui ferait
  // disparaître la séance sous les yeux de qui la tient.
  if (cible.charger) {
    const chargee = await charger(cible.serie).catch(() => null);
    if (!chargee) {
      dire('œuvre de la séance illisible');
      noter('rouverture abandonnee — oeuvre non chargee', cible.serie);
      return { fait: 'echec', raison: 'œuvre non chargée', cible };
    }
  }

  await lancerEpisode(cible.saison, cible.episode);
  return { fait: 'episode', raison: cible.raison, cible };
}

/**
 * Charge la série sans changer d'écran.
 *
 * `ouvrirSerie` ferait basculer la vue sur la fiche de la série : depuis la
 * console, cela ferait disparaître la séance sous les yeux de qui la tient.
 * On ne reprend donc que la partie qui nous intéresse — la même requête, sans
 * le rendu.
 */
async function serieChargee(id) {
  if (!id) return null;
  if (oeuvre?.id === id) return serie;
  // Le contrat du catalogue, comme partout ailleurs. C'était
  // `/api/meta/series/:id` — la route de l'unique série — et elle n'aurait rien
  // rendu pour un film ni pour une œuvre ajoutée depuis.
  const lue = normaliserOeuvre(await api(`/api/oeuvre/${encodeURIComponent(id)}`));
  if (!lue) return null;
  oeuvre = lue;
  serie = enSerie(lue);
  return serie;
}

/**
 * Le chemin d'où l'on part quand le menu s'ouvre.
 *
 * L'épisode n'est passé que s'il appartient à la série chargée : sinon le menu
 * s'ouvrirait sur un arc choisi par une coïncidence de numéros.
 */
const cheminDouvertureMenu = () => cheminDeDepart(
  serie?.arcs,
  arcCourantDe(episodeEnCours()) === null ? null : episodeEnCours(),
);

/** Ce que la tête du volet annonce, et ce que le retour promet de retrouver. */
function titreDuNiveau(niveau) {
  // Le premier niveau montre le CATALOGUE depuis ce chantier — toutes les
  // œuvres, à plat. « Collections » était le nom des trois planches de
  // l'ancien accueil (Films / Anime / Live action), un découpage qui disait
  // « il y a une série et deux tas de films ». Il ne reste que pour les pages
  // qui n'ont pas encore de catalogue.
  if (niveau.genre === 'collections') return catalogue.length ? 'Catalogue' : 'Collections';
  if (niveau.genre === 'titres') return PLANCHES[niveau.cle]?.libelle ?? 'Titres';
  if (niveau.genre === 'arcs') return oeuvre?.titre ?? serie?.name ?? 'Arcs';
  if (niveau.genre === 'parties') return niveau.entree?.nom ?? 'Parties';
  const arc = (serie?.arcs ?? [])[niveau.arc];
  return arc ? `${arc.nom}${arc.partie ? ` — Part ${arc.partie}` : ''}` : 'Épisodes';
}

/** Une rangée qui mène ailleurs : collection, arc, partie, film. */
function rangeeMenu({ nom, detail, courant, surChoix }) {
  const el = gabarit('sc-tpl-menu-rangee');
  if (!el) return null;
  el.querySelector('.sc-menu-nom').textContent = nom;
  el.querySelector('.sc-menu-det').textContent = detail ?? '';
  if (courant) el.setAttribute('aria-current', 'true');
  el.onclick = surChoix;
  return el;
}

/**
 * Une ligne d'épisode : vignette à gauche, nom en haut, rang en dessous.
 *
 * `ou` n'est rempli que par la recherche. Elle traverse tout le catalogue :
 * sans le nom de l'arc, deux résultats voisins par le titre mais éloignés de
 * deux cents épisodes se ressembleraient trait pour trait.
 */
function ligneMenuEpisode(v, courant, ou = null) {
  const el = gabarit('sc-tpl-menu-episode');
  if (!el) return null;

  const contexte = el.querySelector('.sc-menu-ou');
  if (contexte) {
    contexte.textContent = ou ?? '';
    contexte.hidden = !ou;
  }

  const img = el.querySelector('img');
  // `src` n'est posé que s'il existe : une balise sans source afficherait
  // l'icône d'image cassée du navigateur là où l'on veut un simple aplat.
  if (v.thumbnail) img.src = v.thumbnail; else img.remove();
  el.querySelector('.sc-menu-num').textContent = String(v.absolu ?? v.episode);
  el.querySelector('.sc-menu-titre-ep').textContent = v.title || `Épisode ${v.episode}`;

  const reste = joursAvant(v.released);
  el.querySelector('.sc-menu-rang').textContent = reste
    ? `Saison ${v.season} · Épisode ${v.episode} · dans ${reste} ${pluriel(reste, 'jour')}`
    : `Saison ${v.season} · Épisode ${v.episode}`;

  if (reste) {
    el.disabled = true;
    el.setAttribute('aria-disabled', 'true');
  } else {
    el.onclick = () => lancerDepuisMenu(() => lancer(v.season, v.episode));
  }
  if (courant) el.setAttribute('aria-current', 'true');
  return el;
}

/**
 * Lance ce qu'on vient de choisir, POUR TOUT LE MONDE.
 *
 * `lancer` et `lancerFilm` savent déjà le faire : depuis l'activité, ils
 * poussent l'épisode aux pages ouvertes et l'annoncent dans le salon. Ce menu
 * n'a donc rien à réinventer — il referme, et il laisse faire.
 */
function lancerDepuisMenu(geste) {
  fermerMenuEpisode();
  // `lancer` et `lancerFilm` sont asynchrones : un `try` seul laisserait passer
  // le rejet, et le menu se refermerait sur un lancement qui n'a jamais eu
  // lieu, sans un mot. Ils disent déjà leurs propres échecs à l'écran ; ce
  // filet ne sert qu'à ce qu'il en reste une trace dans la console.
  try {
    Promise.resolve(geste()).catch((err) => {
      direConsole(err?.message ?? 'Lancement impossible.');
      peindreConsole(etatSeance);
    });
  } catch (err) {
    direConsole(err?.message ?? 'Lancement impossible.');
    peindreConsole(etatSeance);
  }
}

/**
 * Les parties frères du niveau ouvert, quand il y en a plus d'une.
 *
 * Rendu à part de la peinture parce que trois endroits en ont besoin : la tête
 * qui affiche « Part 4 », le panneau qui propose les autres, et la bascule qui
 * décide s'il y a quelque chose à proposer.
 */
function partiesDuNiveau(niveau) {
  if (!niveau || niveau.genre !== 'episodes') return { nom: null, parties: [], courante: null };
  return partiesDeLArc(serie?.arcs, niveau.arc);
}

/** Le panneau de choix de partie, refermé. */
function fermerParties() {
  const panneau = $('sc-menu-parties');
  if (panneau) { panneau.hidden = true; panneau.textContent = ''; }
  $('sc-menu-partie')?.setAttribute('aria-expanded', 'false');
}

/**
 * Ouvre ou referme le choix de partie.
 *
 * Choisir une partie REMPLACE le niveau ouvert au lieu d'en empiler un : on ne
 * descend pas d'un cran en passant de la partie 4 à la partie 1, on se déplace
 * de côté. Empiler aurait fait grossir le chemin de retour à chaque essai, et
 * il aurait fallu autant de retours que d'hésitations pour ressortir.
 */
function basculerParties() {
  const panneau = $('sc-menu-parties');
  const bouton = $('sc-menu-partie');
  if (!panneau || !bouton) return;
  if (!panneau.hidden) { fermerParties(); return; }

  const niveau = menuEp.pile[menuEp.pile.length - 1];
  const { parties, courante } = partiesDuNiveau(niveau);
  if (parties.length < 2) return;

  panneau.textContent = '';
  for (const p of parties) {
    const arc = (serie?.arcs ?? [])[p.index];
    const el = rangeeMenu({
      nom: `Part ${p.partie}`,
      detail: arc ? `${arc.total ?? (arc.episodes?.length ?? 0)} ép.` : '',
      courant: p.index === courante?.index,
      surChoix: () => {
        fermerParties();
        menuEp.pile[menuEp.pile.length - 1] = { genre: 'episodes', arc: p.index };
        peindreVoletMenu();
      },
    });
    if (el) panneau.appendChild(el);
  }
  panneau.hidden = false;
  bouton.setAttribute('aria-expanded', 'true');
}

/** Redessine le volet pour le niveau où l'on se trouve. */
function peindreVoletMenu() {
  const liste = $('sc-menu-liste');
  const niveau = menuEp.pile[menuEp.pile.length - 1];
  if (!liste || !niveau) return;

  const enQuete = Boolean(menuEp.quete.trim());
  const parent = menuEp.pile[menuEp.pile.length - 2] ?? null;
  const { nom: nomArc, parties, courante: partieCourante } = partiesDuNiveau(niveau);
  // La partie ne s'affiche que là où il y a un choix : sur un arc d'un seul
  // tenant, « Part 1 » sans « Part 2 » derrière ferait croire à une suite.
  // Pendant une recherche, on n'est plus dans un arc : la commande n'aurait
  // plus de sens, et resterait pourtant cliquable.
  const montrerParties = !enQuete && parties.length > 1;

  const retour = $('sc-menu-retour');
  if (retour) {
    // En recherche, le retour défait la recherche : c'est le geste qu'on fait
    // pour revenir en arrière, et on vient de chercher.
    retour.hidden = !parent && !enQuete;
    texte('sc-menu-retour-mot', enQuete ? 'Quitter la recherche' : (parent ? titreDuNiveau(parent) : ''));
  }

  const boutonPartie = $('sc-menu-partie');
  if (boutonPartie) {
    boutonPartie.hidden = !montrerParties;
    texte('sc-menu-partie-mot', montrerParties ? `Part ${partieCourante?.partie ?? '?'}` : '');
  }
  if (!montrerParties) fermerParties();

  liste.textContent = '';
  const ajouter = (el) => { if (el) liste.appendChild(el); };
  const ou = episodeEnCours();

  if (enQuete) {
    const trouves = chercherDansCatalogue(menuEp.index, menuEp.quete, MAX_RESULTATS_MENU);
    // « 60 résultats » quand il y en a trois cents serait un compte faux. À la
    // borne, on dit que la liste est coupée plutôt que d'annoncer un total.
    texte('sc-menu-titre', trouves.length
      ? (trouves.length === MAX_RESULTATS_MENU
        ? `${MAX_RESULTATS_MENU} premiers résultats`
        : `${trouves.length} ${pluriel(trouves.length, 'résultat')}`)
      : `Aucun épisode pour « ${menuEp.quete.trim()} »`);
    for (const t of trouves) {
      const ici = !!ou && t.saison === ou.saison && t.episode === ou.episode;
      ajouter(ligneMenuEpisode(t.video, ici, t.contexte));
    }
    // Pas de `scrollIntoView` ici : la liste est déjà classée par pertinence,
    // et la faire sauter sur l'épisode en cours l'enverrait sous le pli.
    return;
  }

  // Le nom de l'arc n'est écrit qu'une fois. Quand le retour le porte déjà,
  // le redire à droite prenait la place où « Part 4 » a quelque chose à dire.
  const redite = montrerParties && parent && titreDuNiveau(parent) === nomArc;
  texte('sc-menu-titre', redite ? '' : titreDuNiveau(niveau));

  if (niveau.genre === 'collections') {
    // ⚠️ LE MENU DE SÉANCE CHOISIT UNE ŒUVRE, PUIS UN ÉPISODE.
    //
    // Il partait des trois planches, donc d'un monde à une seule série : « Anime »
    // descendait droit dans `collections.anime[0]`, l'unique titre. Avec un
    // catalogue, le premier niveau liste les œuvres — c'est le même geste que
    // sur la grille, au même endroit, et qui pilote n'a pas à quitter la console
    // pour changer d'œuvre.
    if (catalogue.length) {
      for (const o of chercherOeuvres(catalogue, { tri: 'titre' })) {
        ajouter(rangeeMenu({
          nom: o.titre,
          detail: [typeLisible(o), o.annee ? String(o.annee) : '']
            .filter(Boolean).join(' · '),
          courant: o.id === oeuvre?.id,
          // Un film se lance : il n'a rien en dessous. Une série descend d'un
          // niveau — l'ouvrir sur sa fiche ferait quitter la console à qui tient
          // la séance, en pleine séance.
          surChoix: estUnFilm(o)
            ? () => lancerDepuisMenu(() => lancerFilm({
              id: o.id, nom: o.titre, fond: o.fond, affiche: o.affiche,
            }))
            : () => descendreMenu({ genre: 'arcs', serie: o.id }),
        }));
      }
      return;
    }
    for (const p of planchesGarnies()) {
      const fiches = collections[p.cle] ?? [];
      ajouter(rangeeMenu({
        nom: p.libelle,
        detail: detailPlanche(p.cle, fiches),
        courant: p.cle === 'anime' && !!serie,
        surChoix: () => descendreMenu(
          p.cle === 'anime'
            ? { genre: 'arcs', serie: fiches[0]?.id ?? null }
            : { genre: 'titres', cle: p.cle },
        ),
      }));
    }
    return;
  }

  if (niveau.genre === 'titres') {
    for (const f of collections[niveau.cle] ?? []) {
      // Un film se lance : il n'a rien en dessous. Une série descend d'un
      // niveau — l'envoyer à `ouvrirTitre` la ferait ouvrir sur la fiche de
      // la série, donc quitter la console pendant la séance.
      const film = f.type === 'movie';
      ajouter(rangeeMenu({
        nom: f.nom,
        detail: f.annee ? String(f.annee).slice(0, 4) : '',
        surChoix: film
          ? () => lancerDepuisMenu(() => lancerFilm(f))
          : () => descendreMenu({ genre: 'arcs', serie: f.id }),
      }));
    }
    return;
  }

  if (niveau.genre === 'arcs') {
    const arcCourant = arcCourantDe(ou);
    for (const e of entreesArcs(serie?.arcs)) {
      const dedans = arcCourant !== null
        && (e.index === arcCourant || e.parties.some((p) => p.index === arcCourant));
      ajouter(rangeeMenu({
        nom: e.nom,
        detail: e.filler ? 'filler' : '',
        courant: dedans,
        surChoix: () => descendreMenu(e.parties.length > 1
          ? { genre: 'parties', entree: e }
          : { genre: 'episodes', arc: e.index }),
      }));
    }
    return;
  }

  if (niveau.genre === 'parties') {
    const arcCourant = arcCourantDe(ou);
    for (const p of niveau.entree?.parties ?? []) {
      const arc = (serie?.arcs ?? [])[p.index];
      ajouter(rangeeMenu({
        nom: `Part ${p.partie}`,
        detail: arc ? `${arc.total ?? (arc.episodes?.length ?? 0)} ép.` : '',
        courant: p.index === arcCourant,
        surChoix: () => descendreMenu({ genre: 'episodes', arc: p.index }),
      }));
    }
    return;
  }

  const arc = (serie?.arcs ?? [])[niveau.arc];
  let courante = null;
  for (const v of arc?.episodes ?? []) {
    const ici = !!ou && v.season === ou.saison && v.episode === ou.episode;
    const el = ligneMenuEpisode(v, ici);
    ajouter(el);
    if (ici) courante = el;
  }
  // On ne fait pas chercher des yeux l'épisode en cours dans une liste de
  // soixante : le volet s'ouvre dessus. `block: 'center'` plutôt que `start`,
  // pour qu'on voie aussi ce qui précède — c'est souvent là qu'on veut aller.
  if (courante) courante.scrollIntoView({ block: 'center' });
}

/**
 * Descend d'un niveau. La série est chargée en chemin si elle manque.
 *
 * Le chargement pose la série GLOBALE, et c'est voulu : `lancer()` s'en sert
 * pour résoudre l'épisode choisi. Descendre dans un titre par ce menu, c'est
 * annoncer qu'on va y lire quelque chose.
 */
function descendreMenu(niveau) {
  if (niveau.genre === 'arcs' && niveau.serie && serie?.id !== niveau.serie) {
    texte('sc-menu-titre', 'Chargement…');
    serieChargee(niveau.serie)
      .then(() => {
        // Refermé entre-temps : repeindre rouvrirait un volet que personne
        // n'attend plus, sur un niveau que personne n'a demandé.
        if (!menuEp.ouvert) return;
        // La série qui vient d'arriver est celle où la recherche doit chercher.
        rafraichirIndexMenu();
        menuEp.pile.push({ genre: 'arcs' });
        peindreVoletMenu();
      })
      .catch((err) => texte('sc-menu-titre', err?.message ?? 'Série illisible.'));
    return;
  }
  menuEp.pile.push(niveau);
  peindreVoletMenu();
}

/**
 * Un cran en arrière, quel que soit le dernier geste posé.
 *
 * La recherche passe AVANT la pile : elle recouvre le niveau ouvert sans le
 * quitter, donc c'est elle qu'on défait d'abord. Remonter la pile sous une
 * recherche encore active n'aurait rien changé à l'écran — le bouton aurait
 * paru cassé.
 */
function remonterMenu() {
  fermerParties();
  if (menuEp.quete.trim()) { poserQueteMenu(''); return; }
  if (menuEp.pile.length > 1) menuEp.pile.pop();
  peindreVoletMenu();
}

/** Le catalogue préparé pour la recherche, refait seulement s'il a changé. */
function rafraichirIndexMenu() {
  if (menuEp.indexDe === (serie?.id ?? null) && menuEp.index.length) return;
  menuEp.index = indexerCatalogue(serie?.arcs);
  menuEp.indexDe = serie?.id ?? null;
}

/** Ce qui est tapé dans la recherche, et ce que la liste en fait. */
function poserQueteMenu(valeur) {
  menuEp.quete = valeur ?? '';
  const champ = $('sc-menu-q');
  if (champ && champ.value !== menuEp.quete) champ.value = menuEp.quete;
  const vider = $('sc-menu-vider');
  if (vider) vider.hidden = !menuEp.quete;
  if (menuEp.quete.trim()) { fermerParties(); rafraichirIndexMenu(); }
  peindreVoletMenu();
}

/**
 * Place le volet sous le bouton, ou au-dessus s'il n'y a pas la place.
 *
 * `position: fixed` et placement mesuré, comme le menu des arcs : la console
 * vit dans une colonne qui défile, et un volet en `absolute` s'y ferait rogner
 * par le premier `overflow` rencontré.
 */
function placerVoletMenu() {
  const bouton = $('sc-menu-bouton');
  const vol = $('sc-menu-vol');
  if (!bouton || !vol) return;

  const b = rectPage(bouton);
  const large = Math.min(Math.max(b.width, 340), largeurPage() - 24);
  vol.style.width = `${large}px`;
  vol.style.left = `${Math.max(12, Math.min(b.left, largeurPage() - large - 12))}px`;

  const dispoBas = hauteurPage() - b.bottom - 16;
  const dispoHaut = b.top - 16;
  if (dispoBas < 240 && dispoHaut > dispoBas) {
    vol.style.top = 'auto';
    vol.style.bottom = `${hauteurPage() - b.top + 6}px`;
    vol.style.maxHeight = `${Math.max(160, dispoHaut)}px`;
  } else {
    vol.style.bottom = 'auto';
    vol.style.top = `${b.bottom + 6}px`;
    vol.style.maxHeight = `${Math.max(160, dispoBas)}px`;
  }
}

function fermerMenuEpisode() {
  menuEp.ouvert = false;
  fermerParties();
  const vol = $('sc-menu-vol');
  if (vol) vol.hidden = true;
  $('sc-menu')?.classList.remove('ouvert');
  $('sc-menu-bouton')?.setAttribute('aria-expanded', 'false');
}

function ouvrirMenuEpisode() {
  // L'index survit à la fermeture : rouvrir le menu pour affiner une recherche
  // est le geste le plus fréquent, et le refaire à chaque ouverture reviendrait
  // à repayer le catalogue entier pour un menu qu'on vient de fermer.
  menuEp = {
    pile: cheminDouvertureMenu(), ouvert: true, quete: '',
    index: menuEp.index, indexDe: menuEp.indexDe,
  };
  const vol = $('sc-menu-vol');
  if (vol) vol.hidden = false;
  $('sc-menu')?.classList.add('ouvert');
  $('sc-menu-bouton')?.setAttribute('aria-expanded', 'true');
  fermerParties();
  rafraichirIndexMenu();
  const champ = $('sc-menu-q');
  if (champ) champ.value = '';
  const vider = $('sc-menu-vider');
  if (vider) vider.hidden = true;
  // Placer AVANT de peindre : c'est le placement qui fixe la hauteur du volet,
  // et le rendu s'y déroule pour amener l'épisode en cours au milieu. Dans
  // l'autre sens, le défilement se calculait sur une hauteur qui changeait
  // juste après, et l'épisode en cours retombait hors de vue.
  placerVoletMenu();
  peindreVoletMenu();

  // ⚠️ AUCUNE ŒUVRE CHARGÉE : ON VA CHERCHER CELLE QUE LA SÉANCE REGARDE.
  //
  // C'était « la seule série qu'il y ait » (`collections.anime[0]`) — l'unique
  // titre du monde à une série. Quelqu'un qui rejoint une séance déjà partie
  // se retrouvait donc avec le menu d'une AUTRE œuvre que celle projetée, et
  // ses épisodes ne correspondaient à rien de ce qui se passait à l'écran.
  //
  // La séance sait ce qu'elle porte : son ordre d'ouverture nomme l'œuvre. À
  // défaut — une séance sans épisode encore ouvert — on ne devine rien, le
  // premier niveau du menu montre le catalogue entier et le choix se fait là.
  const voulue = episodeEnCours()?.serie ?? null;
  if (!oeuvre && voulue) {
    serieChargee(voulue)
      .then(() => {
        if (!menuEp.ouvert) return;
        rafraichirIndexMenu();
        menuEp.pile = cheminDouvertureMenu();
        peindreVoletMenu();
      })
      .catch(() => {});
  }
}

/**
 * L'autre chemin : l'écran des épisodes en vignettes, celui du catalogue.
 *
 * Le menu déroulant sert à qui sait ce qu'il cherche. Ici on RECONNAÎT
 * l'épisode à son image, et c'est parfois le seul repère qu'on ait. Les deux
 * mènent au même geste — un clic lance pour toute la séance — et aucun ne peut
 * remplacer l'autre.
 *
 * On ouvre l'arc où la séance se trouve, pas le premier de la liste : arriver
 * sur les vingt premiers épisodes de la série quand on en regarde le 412e
 * obligerait à retraverser tout le découpage pour revenir où l'on était.
 */
/**
 * ⚠️ LA CONSOLE N'OUVRE PLUS LA GRILLE DES EPISODES — et c'est pour cela qu'il
 * n'y a plus de memoire de retour ici.
 *
 * « Parcourir les episodes » ouvrait la grille PAR-DESSUS la console. Or la
 * grille n'a qu'un retour, « ← Arcs », qui mene a la liste des arcs et jamais a
 * la console : qui pilotait, cliquait puis se ravisait, perdait la console pour
 * le reste de la soiree — participants, micros, votes, bandeau, menus Vote et
 * Embed — et sa seule sortie etait de lancer un episode a toute la salle,
 * c'est-a-dire d'interrompre la seance pour recuperer son ecran. On avait
 * repare cela par une memoire (`vueAvantEpisodes`) ; le bouton retire, il n'y a
 * plus rien a memoriser, et le retour de la grille redevient ce qu'il a toujours
 * annonce : la fiche de l'oeuvre.
 *
 * ⛔ SI UNE PORTE VERS LA GRILLE EST UN JOUR REOUVERTE DEPUIS UN ECRAN QUI N'EST
 * PAS LA FICHE, elle doit revenir avec sa memoire : sans elle, c'est de nouveau
 * une porte a sens unique. Meme mecanique que `vueAvantCatalogue`, qui resout
 * deja ce probleme pour le catalogue.
 */

/**
 * L'état du bouton, à chaque battement de la console.
 *
 * Changer d'épisode le change pour TOUT LE MONDE : c'est le geste de qui
 * pilote, et le bouton se présente éteint aux autres plutôt que de proposer
 * puis d'échouer en silence. Hors séance, il n'y a personne à emmener : le
 * menu reste ouvert à qui est là.
 */
function peindreMenuEpisode(e) {
  const bouton = $('sc-menu-bouton');
  if (!bouton) return;
  const permis = !e?.actif || !!e.jAiLaMain;
  bouton.disabled = !permis;
  bouton.title = permis ? '' : 'Il faut piloter la séance pour changer d’épisode.';
  if (!permis && menuEp.ouvert) fermerMenuEpisode();

  // Le bouton dit où l'on est, pas ce qu'il fait : cette barre n'a plus que ce
  // menu, et « Changer d'épisode » seul n'apprenait rien. La fiche n'est
  // cherchée que si l'arc est reconnu — cette ligne repasse une fois par
  // seconde, et parcourir quatre cents épisodes pour rien n'a aucun intérêt.
  const ou = episodeEnCours();
  const arc = arcCourantDe(ou);
  const fiche = arc === null ? null : episodeCatalogue(ou.saison, ou.episode);
  texte('sc-menu-libelle', fiche
    ? `Épisode ${fiche.absolu ?? fiche.episode} · changer`
    : 'Changer d’épisode');
}

/** Les branchements du menu, posés une seule fois. */
function preparerMenuEpisode() {
  const bouton = $('sc-menu-bouton');
  const vol = $('sc-menu-vol');
  if (!bouton || !vol || bouton.dataset.branche === '1') return;
  bouton.dataset.branche = '1';

  // `stopPropagation` sur les deux : sans lui, le même clic atteindrait le
  // document et refermerait ce qu'il vient d'ouvrir, ou ce dans quoi on est en
  // train de naviguer.
  bouton.addEventListener('click', (evt) => {
    evt.stopPropagation();
    if (menuEp.ouvert) fermerMenuEpisode(); else ouvrirMenuEpisode();
  });
  vol.addEventListener('click', (evt) => evt.stopPropagation());
  surClic('sc-menu-retour', remonterMenu);

  // La partie ouvre son choix sans refermer le volet : le clic est déjà arrêté
  // par le volet, il n'y a que le panneau à basculer.
  surClic('sc-menu-partie', basculerParties);
  surClic('sc-menu-vider', () => {
    poserQueteMenu('');
    // Le focus revient au champ : vider, c'est repartir sur une autre idée, pas
    // quitter la recherche. Sans ça il fallait recliquer dans le champ.
    $('sc-menu-q')?.focus();
  });

  const champ = $('sc-menu-q');
  if (champ) {
    champ.addEventListener('input', () => poserQueteMenu(champ.value));
    // La frappe ne remonte pas au document : plusieurs écouteurs y attendent
    // Échap — celui du menu juste en dessous, celui des modales — et le champ
    // serait vidé PUIS le menu refermé par le même appui.
    champ.addEventListener('keydown', (evt) => {
      evt.stopPropagation();
      // Un seul Échap défait une seule chose : la recherche s'il y en a une, le
      // menu sinon. C'est la même règle que le bouton de retour.
      if (evt.key !== 'Escape') return;
      if (champ.value) poserQueteMenu(''); else fermerMenuEpisode();
    });
  }

  document.addEventListener('click', () => { if (menuEp.ouvert) fermerMenuEpisode(); });
  document.addEventListener('keydown', (evt) => {
    if (!menuEp.ouvert) return;
    // Échap remonte d'un niveau tant qu'il y en a un : c'est le geste qu'on
    // fait quand on s'est trompé d'arc, et refermer tout obligerait à
    // redescendre depuis le début.
    if (evt.key === 'Escape') {
      const enQuete = Boolean(menuEp.quete.trim());
      if (enQuete || menuEp.pile.length > 1) remonterMenu(); else fermerMenuEpisode();
    }
  });
  addEventListener('resize', () => { if (menuEp.ouvert) placerVoletMenu(); });
}

/**
 * L'onglet « Séance » des réglages.
 *
 * Il ne tient plus la console — elle vit sur l'écran de gestion, où l'activité
 * atterrit une fois la lecture partie. Ne reste ici que ce qui est PERSONNEL :
 * ce qui est partagé, ce qui ne l'est pas, et ce que l'on peut faire de sa
 * place. L'onglet lui-même n'apparaît que pendant une séance.
 *
 * ⚠️ Aucun `return` avant la ligne qui démasque l'onglet : c'est ce qui le
 * rendait introuvable, la fonction s'arrêtant sur un élément disparu.
 */
function peindreOngletSeance() {
  const e = etatSeance;
  const onglet = document.querySelector('#onglets-reglages button[data-seance]');
  if (onglet) onglet.hidden = !e?.actif;
  if (!e?.actif) return;

  texte('seance-note', e.jeSuisHote
    ? 'Tu tiens la séance : les demandes te sont adressées, et tu les traites depuis l’écran de gestion.'
    : e.jAiLaMain
      ? 'Tu peux piloter la lecture, et répondre aux demandes depuis l’écran de gestion.'
      : 'Tu peux demander une pause depuis le bandeau, en haut de l’écran.');
}

// ---------------------------------------------------------------------------
// Les réglages de séance, dans l'onglet Admin
//
// Ce qui se décide une fois pour la soirée, et qui n'a rien à faire dans la
// console : la place que prend le bandeau, et la façon dont le groupe tranche.
//
// Deux natures différentes, et c'est pour cela qu'elles ne partent pas au même
// endroit. La HAUTEUR du bandeau est un confort de poste : elle reste ici, en
// mémoire locale, et ne pose qu'une variable de style. Le SEUIL de vote et
// l'exemption des pilotes sont la règle du jeu : ils appartiennent à la
// séance, donc au serveur, qui seul les fait respecter — et qui n'accepte de
// les changer que de l'hôte ou d'un administrateur.
// ---------------------------------------------------------------------------

/** La hauteur du bandeau, posée pour toute la page. Zéro le fait disparaître. */
function appliquerHauteurBandeau(px) {
  const h = Math.max(0, Math.min(260, Number(px) || 0));
  document.documentElement.style.setProperty('--sc-bandeau-h', `${h}px`);
  // À zéro, la variable ne suffit pas : un bandeau de zéro pixel garde ses
  // bordures et son fond. On le retire de la page.
  const bandeau = $('g-bandeau');
  if (bandeau) bandeau.classList.toggle('sans-bandeau', h === 0);
  return h;
}

/**
 * Envoie la règle du jeu au serveur.
 *
 * Elle voyage par la connexion de la séance, sur le message `reglages` que le
 * serveur attend. Rien ne se décide ici : il valide le mode, borne la valeur,
 * rejoue les votes en cours au nouveau seuil et rediffuse. Si la séance n'est
 * pas là pour porter le message, on le DIT plutôt que de laisser croire au
 * réglage — un réglage qu'on croit posé est pire qu'un réglage refusé.
 */
function envoyerReglagesSeance(charge) {
  if (typeof seance?.reglages !== 'function') {
    texte('sc-reglages-etat', 'Séance injoignable : la règle n’a pas été transmise.');
    return false;
  }
  seance.reglages(charge);
  texte('sc-reglages-etat', 'Transmis à la séance.');
  return true;
}

/** Les bornes du curseur de valeur suivent le mode : ce ne sont pas les mêmes. */
function calerValeurVote(mode) {
  const bloc = $('sc-valeur-vote');
  const curseur = $('r-sc-valeur');
  if (bloc) bloc.hidden = mode !== 'pourcent' && mode !== 'fixe';
  if (!curseur) return;
  if (mode === 'pourcent') { curseur.min = '5'; curseur.max = '100'; curseur.step = '5'; }
  // Le serveur plafonne une séance à 40 personnes : demander plus de voix
  // qu'il ne peut y en avoir rendrait le vote indécidable.
  else if (mode === 'fixe') { curseur.min = '1'; curseur.max = '40'; curseur.step = '1'; }
  texte('o-sc-valeur', mode === 'fixe' ? `${curseur.value} voix` : `${curseur.value} %`);
}

/**
 * Reflète ce que le serveur applique VRAIMENT.
 *
 * L'état de séance fait foi : peindre depuis un souvenir local afficherait la
 * règle de celui qui l'a réglée en dernier, pas celle qui tranche.
 */
/** Allume un bouton d’un groupe, et lui seul. */
function allumerSegment(id, valeur) {
  for (const b of $(id)?.querySelectorAll('button[data-v]') ?? []) {
    const est = b.dataset.v === valeur;
    b.classList.toggle('actif', est);
    b.setAttribute('aria-pressed', est ? 'true' : 'false');
  }
}

/**
 * La règle du jeu telle que le SERVEUR la range aujourd'hui : par famille.
 *
 * Il tient le saut et la pause séparément dans `seuils`, et ne garde `seuil`
 * que comme forme d'avant la séparation — forme qu'il ne remet PLUS à jour dès
 * qu'un réglage arrive par famille. Ce panneau lisait `seuil` : il affichait
 * donc la règle d'avant, et contredisait la console de séance sur le même
 * réglage. Le repli reste, pour une séance ouverte à l'ancienne qui ne porte
 * que l'ancien champ.
 */
function seuilsDeLaSeance(r) {
  const ancien = r?.seuil ?? null;
  return { saut: r?.seuils?.saut ?? ancien, pause: r?.seuils?.pause ?? ancien };
}

function peindreReglagesSeance() {
  const bloc = $('bloc-seance-reglages');
  if (!bloc) return;
  const r = etatSeance?.reglages ?? null;
  const { saut, pause } = seuilsDeLaSeance(r);
  const mode = saut?.mode ?? 'moins1';

  allumerSegment('sc-mode-vote', mode);
  allumerSegment('sc-exemption', r?.exemptionPilotes === false ? '0' : '1');

  const curseur = $('r-sc-valeur');
  // Pendant qu'on tire le curseur, l'écho du serveur ne doit pas le reprendre
  // sous le doigt : on ne repose sa valeur que lorsqu'il n'est pas en main.
  if (curseur && document.activeElement !== curseur && saut?.valeur) {
    curseur.value = String(saut.valeur);
  }
  calerValeurVote(mode);

  // UN SEUL RÉGLAGE POUR DEUX FAMILLES : il faut le dire quand elles diffèrent.
  //
  // Ce panneau ne porte qu'un mode et qu'une valeur, mais la console de séance
  // règle le saut et la pause séparément. Quand elle l'a fait, il ne peut
  // montrer que l'une des deux — et le prochain clic ici les alignera. Le
  // taire ferait passer la règle du saut pour celle de la pause, puis
  // écraserait la seconde sans que personne l'ait demandé.
  const note = $('sc-seuils-separes');
  if (note) {
    const separes = JSON.stringify(saut) !== JSON.stringify(pause);
    note.hidden = !separes;
    note.textContent = separes
      ? `Réglés séparément depuis la console de séance : ${resumeSeuil(saut)} pour le saut,`
        + ` ${resumeSeuil(pause)} pour la pause. Ce réglage-ci porte sur les deux à la fois.`
      : '';
  }
}

/**
 * Pose la même règle sur LES DEUX familles, dans la forme qu'emploie la console.
 *
 * ⚠️ Ce panneau envoyait `seuil`, la forme historique. Côté serveur elle règle
 * bien les deux familles d'un coup — l'effet voulu ici, puisqu'il n'y a qu'un
 * réglage à l'écran — mais elle écrit dans un AUTRE champ que la console, et
 * elle laisse `seuils` tel quel dans l'état rediffusé. Les deux écrans se
 * contredisaient donc dès qu'on touchait celui-ci : le vote appliquait la
 * nouvelle règle, la console continuait d'afficher l'ancienne.
 *
 * Un seul champ écrit par les deux chemins, et la contradiction n'a plus où
 * se loger. La validation ne change pas : le serveur borne chaque famille
 * exactement comme il bornait la forme unique.
 */
function poserSeuilPourLesDeux(mode, valeur) {
  return envoyerReglagesSeance({
    seuils: { saut: { mode, valeur }, pause: { mode, valeur } },
  });
}

function preparerReglagesSeance() {
  const bloc = $('bloc-seance-reglages');
  if (!bloc) return;

  // --- la hauteur du bandeau : un confort de poste, rien de partagé ----------
  const hauteur = $('r-sc-bandeau');
  if (hauteur) {
    hauteur.value = prefs.lire('sc-bandeau-h', '125');
    texte('o-sc-bandeau', `${appliquerHauteurBandeau(hauteur.value)} px`);
    hauteur.oninput = () => {
      prefs.ecrire('sc-bandeau-h', hauteur.value);
      texte('o-sc-bandeau', `${appliquerHauteurBandeau(hauteur.value)} px`);
    };
  }

  // --- la règle du jeu : elle appartient à la séance -------------------------
  //
  // La ligne qui avoue que ce panneau ne montre qu'une des deux familles.
  // Fabriquée ici plutôt qu'écrite dans la page : elle n'a de sens que dans ce
  // cas-là, et `sc-reglages-etat` — la seule autre ligne de ce bloc — sert à
  // confirmer les envois. Y écrire effacerait la confirmation qu'on vient d'y
  // poser, une fois par seconde.
  const champMode = $('sc-mode-vote');
  if (champMode?.parentElement && !$('sc-seuils-separes')) {
    const note = document.createElement('span');
    note.id = 'sc-seuils-separes';
    note.className = 'aide';
    note.hidden = true;
    champMode.parentElement.appendChild(note);
  }

  const modeCourant = () => $('sc-mode-vote')?.querySelector('button.actif')?.dataset.v ?? 'moins1';

  for (const b of $('sc-mode-vote')?.querySelectorAll('button[data-v]') ?? []) {
    b.onclick = () => {
      const mode = b.dataset.v;
      // Allume tout de suite : l’écho du serveur ne revient qu’au battement
      // suivant, et le curseur de valeur relit ce groupe pour savoir de quel
      // mode il porte la valeur.
      allumerSegment('sc-mode-vote', mode);
      calerValeurVote(mode);
      // 'moins1' et 'tous' ne portent pas de valeur : le serveur la remet à
      // zéro de lui-même, on n'en invente pas une.
      const valeur = mode === 'pourcent' || mode === 'fixe' ? Number($('r-sc-valeur')?.value ?? 0) : 0;
      poserSeuilPourLesDeux(mode, valeur);
    };
  }

  const valeur = $('r-sc-valeur');
  if (valeur) {
    valeur.oninput = () => calerValeurVote(modeCourant());
    // À la relâche seulement : un message par pixel parcouru ferait rejouer
    // les votes en cours à chaque cran du curseur.
    valeur.onchange = () => poserSeuilPourLesDeux(modeCourant(), Number(valeur.value));
  }

  for (const b of $('sc-exemption')?.querySelectorAll('button[data-v]') ?? []) {
    b.onclick = () => {
      allumerSegment('sc-exemption', b.dataset.v);
      envoyerReglagesSeance({ exemptionPilotes: b.dataset.v === '1' });
    };
  }

  peindreReglagesSeance();
}

// Pilotage du balayage : lancement, avancement, arrêt.
let minuteurScan = null;

function preparerScan() {
  const bloc = $('bloc-scan');
  if (!bloc) return;
  bloc.hidden = false;

  surClic('scan-lancer', async () => {
    texte('scan-etat', 'démarrage…');
    try {
      const r = await api('/api/admin/scan', {
        method: 'POST',
        body: JSON.stringify({ serie: serie?.id }),
      });
      if (!r.demarre) {
        texte('scan-etat', r.raison ?? 'refusé');
        return;
      }
      suivreScan();
    } catch (err) {
      texte('scan-etat', err.message);
    }
  });

  surClic('scan-arreter', async () => {
    await api('/api/admin/scan', { method: 'DELETE' }).catch(() => {});
    texte('scan-etat', 'arrêt demandé');
  });

  suivreScan();
}

async function suivreScan() {
  clearInterval(minuteurScan);
  const rafraichir = async () => {
    let e;
    try {
      e = await api('/api/admin/scan');
    } catch {
      clearInterval(minuteurScan);
      return;
    }
    const jauge = $('scan-jauge');
    const barre = jauge?.firstElementChild;
    if (jauge) jauge.hidden = !e.total;
    if (barre && e.total) barre.style.width = `${Math.round((e.faits / e.total) * 100)}%`;

    if ($('scan-arreter')) $('scan-arreter').hidden = !e.enCours;
    if ($('scan-lancer')) $('scan-lancer').hidden = e.enCours;

    texte(
      'scan-etat',
      e.total
        ? `${e.faits}/${e.total} · ${e.trouves} avec source${e.dernier ? ` · ${e.dernier}` : ''}`
        : 'jamais lancé',
    );

    // On cesse d'interroger dès que le balayage est fini : rien ne bougera plus.
    if (!e.enCours) clearInterval(minuteurScan);
  };
  await rafraichir();
  minuteurScan = setInterval(rafraichir, 2500);
}

// ---------------------------------------------------------------------------
// Photos : une catégorie, puis ses vignettes.
//
// Trois origines cohabitent, et il faut les distinguer d'un coup d'œil :
//   · posée  — une image téléchargée une fois, puis servie par le lecteur ;
//   · auto   — l'affiche que le catalogue a résolue, rien à régler ici ;
//   · aucune — la place est vide.
// D'où la pastille de couleur sur chaque vignette. Le champ d'adresse
// n'apparaît que pour la vignette désignée : vingt champs affichés ensemble,
// c'était la version d'avant, et on ne savait plus à quoi ils appartenaient.
// ---------------------------------------------------------------------------
const CATEGORIES_PHOTOS = [
  { cle: 'accueil', nom: 'Accueil' },
  { cle: 'films', nom: 'Films' },
  { cle: 'anime', nom: 'Anime' },
  { cle: 'live', nom: 'Live action' },
];

const visuelsDe = (cle) => (window.__VISUELS__ ?? {})[cle] ?? [];

let photosArcs = null;
let photoChoisie = null;

/** Les arcs et leur visuel. Relus après chaque pose, sinon gardés en mémoire. */
async function arcsPourPhotos(force = false) {
  if (photosArcs && !force) return photosArcs;
  const id = serie?.id || document.body.dataset.defaultSerie;
  ({ arcs: photosArcs } = await api(`/api/admin/arcs/${encodeURIComponent(id)}`));
  return photosArcs;
}

/** Écran d'entrée : les quatre catégories, chacune avec son état. */
async function chargerPhotos() {
  const racine = $('photos-categories');
  if (!racine) return;
  $('photos-racine').hidden = false;
  $('photos-detail').hidden = true;

  // Le nombre d'arcs déjà pourvus n'est connu qu'après l'appel : la carte se
  // dessine tout de suite et se complète, plutôt que de faire attendre les
  // trois autres.
  const dessiner = (arcs) => {
    racine.textContent = '';
    for (const categorie of CATEGORIES_PHOTOS) {
      const carte = document.createElement('button');
      carte.type = 'button';
      carte.className = 'cat-carte';

      const vue = document.createElement('span');
      vue.className = 'vue';
      const image = categorie.cle === 'accueil'
        ? (visuelsDe('anime')[0] ?? visuelsDe('films')[0] ?? null)
        : (visuelsDe(categorie.cle)[0] ?? null);
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        vue.appendChild(img);
      } else {
        vue.classList.add('vide');
        vue.textContent = 'vide';
      }

      const nom = document.createElement('span');
      nom.className = 'nom';
      nom.textContent = categorie.nom;

      const det = document.createElement('span');
      det.className = 'det';
      det.textContent = detailCategoriePhotos(categorie.cle, arcs);

      carte.append(vue, nom, det);
      carte.onclick = () => ouvrirCategoriePhotos(categorie);
      racine.appendChild(carte);
    }
  };

  dessiner(photosArcs);
  arcsPourPhotos().then(dessiner).catch(() => {});
}

function detailCategoriePhotos(cle, arcs) {
  if (cle === 'accueil') return 'trois cases · automatique';
  if (cle === 'anime') {
    if (!arcs) return 'arcs…';
    const posees = arcs.filter((a) => a.image).length;
    return `${arcs.length} ${pluriel(arcs.length, 'arc')} · ${posees} ${pluriel(posees, 'posée')}`;
  }
  const n = (collections[cle] ?? []).length;
  return `${n} ${pluriel(n, 'titre')} · automatique`;
}

/** Deuxième écran : l'image de base de la catégorie, puis ses vignettes. */
async function ouvrirCategoriePhotos(categorie) {
  $('photos-racine').hidden = true;
  $('photos-detail').hidden = false;
  $('photos-editeur').hidden = true;
  photoChoisie = null;
  texte('photos-titre', categorie.nom);

  const base = $('photos-base');
  const liste = $('photos-liste');
  base.textContent = '';
  liste.textContent = '';

  // L'image de base : celle que l'accueil montre pour cette catégorie.
  if (categorie.cle !== 'accueil') {
    const visuels = visuelsDe(categorie.cle);
    base.appendChild(carteBasePhoto(visuels[0] ?? null, categorie.nom, visuels.length));
  }

  if (categorie.cle === 'anime') {
    let arcs;
    liste.textContent = 'Lecture des arcs…';
    try {
      arcs = await arcsPourPhotos();
    } catch (err) {
      liste.textContent = err.message || 'Les arcs sont illisibles.';
      return;
    }
    liste.textContent = '';
    for (const arc of arcs) {
      liste.appendChild(vignettePhoto({
        image: arc.image,
        origine: arc.image ? 'posee' : null,
        nom: arc.nom,
        sous: arc.plage + (arc.filler ? ' · filler' : ''),
        surChoix: (el) => choisirPhoto(arc, el),
      }));
    }
    return;
  }

  // Accueil, films, live action : ce que le catalogue a résolu. Rien à régler
  // ici — le dire est plus honnête qu'afficher un champ sans effet.
  const entrees = categorie.cle === 'accueil'
    ? CATEGORIES_PHOTOS.filter((c) => c.cle !== 'accueil').map((c) => ({
        nom: c.nom, sous: 'case de l’accueil', image: visuelsDe(c.cle)[0] ?? null,
      }))
    : (collections[categorie.cle] ?? []).map((f) => ({
        nom: f.nom, sous: f.annee ?? '', image: f.affiche ?? f.fond ?? null,
      }));

  for (const e of entrees) {
    liste.appendChild(vignettePhoto({ ...e, origine: e.image ? 'auto' : null }));
  }
}

function carteBasePhoto(image, nom, combien) {
  const bloc = document.createElement('div');
  bloc.className = 'photo-base';
  const vue = document.createElement('span');
  vue.className = 'vue';
  if (image) {
    const img = document.createElement('img');
    img.src = image;
    img.alt = '';
    vue.appendChild(img);
  }
  const infos = document.createElement('span');
  infos.className = 'infos';
  const titre = document.createElement('b');
  titre.textContent = `Image de la case « ${nom} »`;
  const detail = document.createElement('span');
  detail.textContent = combien > 1
    ? `${combien} affiches, montrées à tour de rôle · mapping automatique`
    : 'mapping automatique, depuis le catalogue';
  infos.append(titre, detail);
  bloc.append(vue, infos);
  return bloc;
}

function vignettePhoto({ image, origine, nom, sous, surChoix }) {
  const carte = document.createElement(surChoix ? 'button' : 'div');
  if (surChoix) carte.type = 'button';
  carte.className = 'vign';

  const vue = document.createElement('span');
  vue.className = 'vue';
  if (image) {
    const img = document.createElement('img');
    img.src = image;
    img.alt = '';
    img.loading = 'lazy';
    vue.appendChild(img);
  } else {
    vue.classList.add('vide');
  }
  const pastille = document.createElement('span');
  pastille.className = `pastille${origine ? ` ${origine}` : ''}`;
  pastille.title = origine === 'posee' ? 'image posée' : origine === 'auto' ? 'résolue par le catalogue' : 'aucune image';
  vue.appendChild(pastille);

  const titre = document.createElement('span');
  titre.className = 'nom';
  const libelle = document.createElement('span');
  libelle.className = 't';
  libelle.textContent = nom;
  titre.appendChild(libelle);
  if (sous) {
    const petit = document.createElement('small');
    petit.textContent = sous;
    titre.appendChild(petit);
  }

  carte.append(vue, titre);
  if (surChoix) carte.onclick = () => surChoix(carte);
  return carte;
}

/** Ouvre l'éditeur sous la grille, pour l'arc désigné. */
function choisirPhoto(arc, carte) {
  for (const el of $('photos-liste').querySelectorAll('.vign')) el.classList.remove('choisie');
  carte.classList.add('choisie');
  photoChoisie = arc;

  const editeur = $('photos-editeur');
  editeur.hidden = false;
  editeur.textContent = '';

  const tete = document.createElement('div');
  tete.className = 'tete';
  const nom = document.createElement('b');
  nom.textContent = arc.nom;
  const etat = document.createElement('span');
  etat.className = 'etat-lang';
  tete.append(nom, etat);

  const ligne = document.createElement('div');
  ligne.className = 'ligne';
  const champ = document.createElement('input');
  champ.type = 'text';
  champ.placeholder = 'https://…/image.jpg';
  const poser = document.createElement('button');
  poser.type = 'button';
  poser.textContent = 'Poser';
  ligne.append(champ, poser);

  if (arc.image) {
    const retirer = document.createElement('button');
    retirer.type = 'button';
    retirer.className = 'ghost';
    retirer.textContent = 'Retirer';
    retirer.onclick = async () => {
      etat.textContent = 'retrait…';
      try {
        await api(`/api/admin/arcs/${encodeURIComponent(arc.cle)}`, { method: 'DELETE' });
        await rafraichirPhotos();
      } catch (err) {
        etat.textContent = err.message;
      }
    };
    ligne.appendChild(retirer);
  }

  poser.onclick = async () => {
    const url = champ.value.trim();
    if (!url) { etat.textContent = 'adresse vide'; return; }
    etat.textContent = 'téléchargement…';
    poser.disabled = true;
    try {
      const r = await api(`/api/admin/arcs/${encodeURIComponent(arc.cle)}`, {
        method: 'POST', body: JSON.stringify({ url }),
      });
      etat.textContent = `posée · ${Math.round(r.octets / 1024)} Ko`;
      await rafraichirPhotos();
    } catch (err) {
      etat.textContent = err.message;
    } finally {
      poser.disabled = false;
    }
  };

  editeur.append(tete, ligne);
  // La grille peut être longue : l'éditeur s'ouvre sous elle, donc on l'amène
  // dans le champ de vision plutôt que de laisser chercher.
  editeur.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  champ.focus();
}

/** Après une pose ou un retrait : on relit, et on rouvre là où on était. */
async function rafraichirPhotos() {
  await arcsPourPhotos(true);
  await ouvrirCategoriePhotos(CATEGORIES_PHOTOS.find((c) => c.cle === 'anime'));
}

// Le lecteur est monté une seule fois, au premier usage : ses écouteurs
// vivent aussi longtemps que la page, seule la source change.
let _lecteur = null;
let varianteFiche = null;
/** Le signal de variante passe par `annoncer` du lecteur (exposé `signaler`), pas posé brut. */
function annoncerVariante() {
  const phrase = phraseVariante(varianteFiche);
  if (phrase) lecteur().signaler(phrase);
}
// Réglages par personne du registre commun (langue, flou), servis avec la fiche.
let reglagesFiche = null;
const lecteur = () => {
  if (!_lecteur) {
    // `journal` manquait, et le lecteur appelait pourtant `journal(...)` en
    // clair : chaque tentative d'affiner les bornes d'intro/outro levait un
    // ReferenceError qui coupait la boucle. Le gabarit de `tracer` est
    // exactement celui attendu — `(ou, message, detail)`.
    _lecteur = creerLecteur({
      base, $, trace: () => traceCourante, jeton: () => session, journal: tracer,
    });
  }
  return _lecteur;
};

// ---------------------------------------------------------------------------
// Lecture : un clic sur l'épisode suffit. Le serveur résout, vérifie chaque
// candidat et ne renvoie que celui qui a réellement répondu.
// ---------------------------------------------------------------------------
/**
 * Épisodes encadrant celui qui va être lu, tels que la grille les présente.
 * Aucune liste séparée à tenir à jour : la grille EST la source d'ordre.
 */
// ---------------------------------------------------------------------------
// Préparer l'épisode suivant pendant qu'on regarde celui-ci
//
// Résoudre une source prend une à trois secondes : les add-ons sont
// interrogés, les liens vérifiés un à un. Faire ce travail au moment où
// l'épisode se termine, c'est offrir ce temps mort à chaque fin d'épisode.
//
// On le fait donc AVANT, pendant la dernière minute — là où personne ne
// regarde plus vraiment. Quand la fin arrive, la source est déjà là.
//
// ⚠️ Une référence de lecture expire (six heures) et un lien de débridage bien
// avant. Préparer dix épisodes d'avance ne servirait donc à rien : un seul,
// et seulement quand la fin approche.
// ---------------------------------------------------------------------------
const AVANT_LA_FIN = 60; // secondes : filet quand on arrive tard (reprise près de la fin)
// Toji (21/10) : préparer dès qu'on a une source viable, pas à la dernière
// minute — un échec à 60 s de la fin ne laissait plus le temps de réessayer.
const APRES_LE_DEBUT = 20; // secondes de lecture réelle avant de résoudre le suivant
const DERNIERES = 120; // dans ces secondes, un lien raté ou vieux se refait (une fois)
// La référence vit 6 h (PLAYABLE_TTL, server.js) mais le lien de débridage
// derrière elle peut tomber avant : passé ce délai, on le redemande.
const PERIME_MS = 30 * 60_000;
const PRECHAUFFE = 90; // secondes avant la fin : on remplit le tampon du suivant

let prepare = null; // { cible, promesse, quand, essais, fini, choix }

/**
 * Que faire du lien du suivant à cet instant (pure) : `'resoudre'` dès
 * `APRES_LE_DEBUT` secondes lues (ou dans la dernière minute si on arrive
 * tard), `'refaire'` UNE fois dans les `DERNIERES` secondes si la résolution a
 * échoué ou si son lien a vieilli, sinon `null`.
 */
export function quoiPreparer({ lu, reste, prepare: p, cible, maintenant = Date.now() }) {
  if (!(lu >= APRES_LE_DEBUT || (reste > 0 && reste < AVANT_LA_FIN))) return null;
  if (p?.cible !== cible) return 'resoudre';
  const aRefaire = p.fini && (!p.choix || maintenant - p.quand > PERIME_MS);
  return aRefaire && reste > 0 && reste < DERNIERES && p.essais < 2 ? 'refaire' : null;
}

/** Lance la résolution du suivant sans rien bloquer (voir `quoiPreparer`). */
function preparerSuivant(saison, episode, { lu = 0, reste = Infinity } = {}) {
  const suivant = episodeApres(saison, episode);
  if (!suivant || !serie) return;

  const cible = `${serie.id}:${suivant.saison}:${suivant.episode}`;
  const quoi = quoiPreparer({ lu, reste, prepare, cible });
  if (!quoi) return;
  if (quoi === 'refaire') tracer('prechargement', 'nouvel essai', { cible, raison: prepare.choix ? 'lien vieilli' : 'échec' });

  const voulue = langueVoulueDe(oeuvre);
  const p = { cible, quand: Date.now(), essais: prepare?.cible === cible ? prepare.essais + 1 : 1, fini: false, choix: null };
  // On garde la PROMESSE, pas son résultat : si la fin arrive avant que la
  // résolution soit finie, on l'attend au lieu d'en relancer une seconde.
  // L'episode suivant est de la MEME oeuvre : meme type, donc meme regle.
  p.promesse = api(`/api/play-now/series/${encodeURIComponent(cible)}`
    + parametresDeGout(voulue, sousTitresVoulus(oeuvre)))
    .then((choix) => {
      tracer('prechargement', `source prête pour S${suivant.saison}E${suivant.episode}`, { ref: choix.ref });
      p.choix = choix?.ref ? choix : null;
      p.fini = true;
      return p.choix;
    })
    .catch((err) => {
      // Un échec ici ne se voit pas : la lecture normale refera le travail.
      tracer('prechargement', 'échec', err.message);
      p.fini = true;
      return null;
    });
  prepare = p;
}

// Les octets du suivant ne se tirent ni en économie de données, ni pour un
// onglet oublié en arrière-plan.
let cacheDepuis = 0;
document.addEventListener('visibilitychange', () => { cacheDepuis = document.hidden ? Date.now() : 0; });
export function prechargementPermis(nav = navigator, depuis = cacheDepuis, maintenant = Date.now()) {
  return !nav.connection?.saveData && !(depuis && maintenant - depuis > 5 * 60_000);
}

/** Ce qui a été préparé pour cette cible, ou `null`. */
async function sourcePreparee(cible) {
  if (prepare?.cible !== cible) return null;
  const choix = await prepare.promesse;
  prepare = null; // une référence ne sert qu'une fois
  return choix;
}

/** L'épisode qui suit, dans la même saison ou la suivante. */
function episodeApres(saison, episode) {
  // Un épisode pas encore sorti n'a pas de source : le préparer, c'était un
  // « aucun lien renvoyé » à chaque fin du dernier épisode diffusé.
  const s = voisinsCatalogue(serie?.arcs, saison, episode, (v) => !joursAvant(v.released)).suivant;
  return s ? { saison: s.season, episode: s.episode } : null;
}

/**
 * L'épisode d'avant et celui d'après, dans l'ordre de l'histoire.
 *
 * ⚠️ Cette fonction lisait les GRILLES de la page (`ep-grille`, `s-episodes`)
 * et n'y trouvait souvent rien. Voici pourquoi, et c'est mesurable : depuis
 * Discord, la lecture part dans un onglet du navigateur ouvert sur un billet
 * (`/s/<billet>`). Cette adresse ne porte AUCUN fragment, donc
 * `positionMemorisee()` est vide, donc `ouvrirSerie` retombe sur l'arc 0 et
 * dessine « L'agent des Shinigami » — les épisodes 1 à 20 — pendant que
 * `lancer()` ouvre l'épisode 412. Aucune des deux grilles ne contenait donc
 * l'épisode joué : les deux voisins revenaient nuls, et les boutons
 * « précédent » et « suivant » du lecteur restaient éteints pour de bon.
 *
 * On lit donc le CATALOGUE, qui est la même source que `episodeApres` et que
 * l'écran des épisodes. Il ne dépend d'aucun rendu, il est juste quel que soit
 * le chemin emprunté, et il franchit les frontières d'arc — revenir du premier
 * épisode d'une partie au dernier de la précédente marche enfin.
 */
function voisinsDe(saison, episode) {
  // Un épisode à venir n'est pas jouable : on l'écarte plutôt que d'y mener.
  // La date de diffusion dit la même chose que la classe « a-venir » de la
  // grille, sans avoir besoin que la grille existe.
  const { precedent, suivant } = voisinsCatalogue(
    serie?.arcs, saison, episode, (v) => !joursAvant(v.released),
  );
  const aller = (v) => (v ? () => lancer(v.season, v.episode) : null);
  // La carte de fin montre le suivant ; sans suivant jouable, l'infobulle du
  // bouton grisé dit quand sort l'épisode annoncé, si on le sait.
  const annonce = suivant ? null : voisinsCatalogue(serie?.arcs, saison, episode).suivant;
  const date = annonce?.released ? dateFr(annonce.released, { day: 'numeric', month: 'short' }) : '';
  return {
    precedent: aller(precedent),
    suivant: aller(suivant),
    infoSuivant: suivant
      ? { titre: intituleCourtEpisode(suivant.season, suivant.episode), vignette: suivant.thumbnail ?? null }
      : null,
    aVenir: annonce && date ? `Épisode ${annonce.absolu ?? annonce.episode} · ${date}` : null,
  };
}

/** « 24 min », « 1h 45min »… en secondes. Renvoie 0 si rien d'exploitable. */
function secondesDe(texte) {
  const t = String(texte ?? '');
  const h = /(\d+)\s*h/i.exec(t);
  const min = /(\d+)\s*m/i.exec(t);
  const total = (Number(h?.[1]) || 0) * 3600 + (Number(min?.[1]) || 0) * 60;
  return total || 0;
}

/**
 * Retourne l'écran d'attente en négatif par-dessus la lecture qui démarre.
 *
 * L'écran est marqué « garde » : il survit au changement de vue et reste au
 * premier plan pendant que la vidéo se lance derrière. La fin de l'animation
 * découvre donc une image déjà en cours, au lieu de s'ajouter à l'attente.
 * Rend la main tout de suite — c'est le point : rien ne doit l'attendre.
 */

/** Rend le nombre de millisecondes a laisser passer avant d'ouvrir. */
// Instant où l'écran d'attente est apparu : sert à laisser la vague achever
// son passage avant que l'inversion ne s'enclenche.
let debutAttente = 0;

/**
 * Pose deux bandes blanches sur les côtés de l'image, un peu plus larges que le
 * vide réel et dégradées vers l'intérieur.
 *
 * Le fond blanc du lecteur suffisait à couvrir ce vide, mais sa limite tombait
 * au pixel près sur le bord de l'image : on voyait la jonction. En mordant de
 * quelques pixels et en s'estompant, les bandes se raccordent sans couture.
 */
function poserBandes() {
  const video = $('video');
  const cadre = $('lecteur');
  if (!video || !cadre) return;

  const largeur = () => {
    const { videoWidth: vw, videoHeight: vh } = video;
    const { clientWidth: cw, clientHeight: ch } = cadre;
    if (!vw || !vh || !cw || !ch) return 0;
    // `object-fit: contain` : l'image occupe la plus petite des deux échelles.
    const affichee = Math.min(cw / vw, ch / vh) * vw;
    return Math.max(0, (cw - affichee) / 2);
  };

  const l = largeur();
  if (l < 2) {
    // Dimensions pas encore connues : on repassera dès que la vidéo les
    // annonce, plutôt que de renoncer en silence.
    if (!video.videoWidth) {
      video.addEventListener('loadedmetadata', () => poserBandes(), { once: true });
    }
    return;
  }

  retirerBandes(); // jamais deux jeux superposés

  for (const cote of ['left', 'right']) {
    const b = document.createElement('div');
    b.className = 'bande-blanche';
    Object.assign(b.style, {
      position: 'absolute', top: '0', bottom: '0', [cote]: '0',
      width: `${l + 52}px`, // déborde franchement sur l'image
      background: `linear-gradient(to ${cote === 'left' ? 'right' : 'left'},
        #fff 0, #fff ${l + 12}px, rgba(255,255,255,0) ${l + 52}px)`,
      pointerEvents: 'none', zIndex: '2',
    });
    cadre.appendChild(b);
  }
}

/** Retire les bandes posées le temps de la transition. */
function retirerBandes() {
  for (const b of document.querySelectorAll('.bande-blanche')) b.remove();
}

/**
 * Pose la vignette de ce qu'on va regarder derrière l'écran d'attente.
 *
 * L'écran ne montrait que le blason sur du noir : de quoi reconnaître la 14ème
 * Division, pas l'épisode. Or Discord affiche cette vue en miniature dans le
 * salon, sous le nom de l'activité — c'est ce que voit tout le vocal, y compris
 * ceux qui ne l'ouvriront jamais. La vignette dit les deux à la fois.
 *
 * L'image arrive telle quelle : c'est le CSS (`.v-fond`) qui la floute et
 * l'assombrit, exactement comme `.g-fond` le fait pour le bandeau de la
 * console. Le blason reste donc lisible par-dessus sans qu'on ait à choisir
 * une teinte par épisode.
 *
 * @param {string|null} image adresse de la vignette, ou rien pour revenir au
 *   fond du thème.
 */
/**
 * Ce que l'écran d'attente annonce : l'épisode, son arc, son numéro.
 *
 * Posé À CÔTÉ du fond et jamais ailleurs : les deux décrivent la même chose,
 * et les séparer garantissait qu'un jour l'un dirait un épisode et l'autre en
 * montrerait un autre.
 */
function peindreTitreAttente(cible) {
  const fiche = cible?.saison ? episodeCatalogue(cible.saison, cible.episode) : null;
  const nom = cible?.titre || fiche?.title || null;

  const t = $('chargement-episode');
  if (t) { t.textContent = nom ?? ''; t.hidden = !nom; }

  // L'arc et le vrai numéro d'épisode. Aucun rang inventé quand l'arc n'est pas
  // reconnu : « Arc 0 » serait faux, et un repère faux est pire qu'absent.
  const arcs = serie?.arcs ?? [];
  const i = cible?.saison
    ? arcs.findIndex((a) => (a.episodes ?? []).some(
      (v) => v.season === cible.saison && v.episode === cible.episode,
    ))
    : -1;
  const bouts = [];
  if (i >= 0) bouts.push(`Arc ${i + 1}`);
  if (fiche?.absolu > 0) bouts.push(`Épisode ${fiche.absolu}`);
  else if (cible?.saison) bouts.push(`S${cible.saison}E${cible.episode}`);

  const a = $('chargement-arc');
  if (a) { a.textContent = bouts.join(' · '); a.hidden = bouts.length === 0; }
}

/**
 * @param {string|null} image l'image en grand — c'est elle qu'on veut voir
 * @param {string|null} repli la même en petit, posée DERRIÈRE en attendant
 *
 * ⚠️ LE REPLI N'EST PAS UNE PRECAUTION, C'EST CE QUI SUPPRIME LE CLIGNOTEMENT.
 *
 * Cet ecran s'ouvre en une frame et l'image en grand (1920 px) met le temps
 * qu'elle met : sans couche de dessous, on regardait un cadre noir puis, d'un
 * coup, un decor. La petite version est celle que la carte ou la vignette
 * affichait a l'instant d'avant — donc deja dans le cache — et elle tient la
 * place, cadree pareil, jusqu'a ce que la grande la recouvre.
 */
function peindreFondAttente(image, repli = null) {
  const fond = $('v-fond');
  if (!fond) return;
  const couches = [image, repli && repli !== image ? repli : null].filter(Boolean);
  const voulu = couches.length ? couches.map(urlCss).join(', ') : 'none';
  if (fond.style.backgroundImage !== voulu) fond.style.backgroundImage = voulu;
  // Sans image, l'élément s'efface au lieu de poser un voile sur rien : l'écran
  // redevient celui d'avant, au pixel près.
  fond.hidden = !image;
  // La bascule précédente a laissé le fond à zéro et sa transition en place :
  // sans cette remise à neuf, le deuxième épisode d'une soirée retombait sur le
  // cadre noir. On ne touche pas au reste — l'image, elle, survit d'un épisode
  // à l'autre tant qu'on la repose.
  fond.style.removeProperty('opacity');
  fond.style.removeProperty('transition');
}

// ⚠️ LA TRANSITION SE JOUE QUAND L'IMAGE EST PRÊTE, PAS AVANT.
//
// Toji : « on a calé l'animation de transition juste avant que le lien termine
// de charger … dès que la vidéo est prête, le lecteur, bam, il pop. »
//
// L'enchaînement était : transition blanche → lecteur → le voile du lecteur
// (« Chargement en cours… ») → image. Deux écrans d'attente à la suite, avec la
// transition AU MILIEU : elle annonçait une image qui n'arrivait que douze
// secondes plus tard, et ce qu'elle découvrait était un second chargement.
//
// On attend donc que la balise puisse jouer, et c'est À CET INSTANT que la
// transition part. Elle ne masque plus une attente : elle la conclut.
//
// ⚠️ LE REPÈRE N'EST PAS `readyState >= 3`. MESURÉ : IL N'ARRIVE JAMAIS.
//
// Première version de ce code : on attendait HAVE_FUTURE_DATA. Relevé sur la
// production, le lecteur reste à `readyState: 1` du début à la fin — parce que
// le MKV n'est pas lu par la balise mais DÉMUXÉ EN JAVASCRIPT et poussé dans un
// `MediaSource`. Dans ce mode, la balise n'annonce pas ce qu'elle « pourra »
// jouer : elle prend ce qu'on lui donne, morceau par morceau, et son
// `readyState` reflète mal l'état réel.
//
// Attendre 3 revenait donc à attendre l'échéance de secours — vingt secondes
// devant un blason, exactement ce qu'on cherchait à supprimer.
//
// Deux repères, et le premier qui répond gagne :
//   · `canplay`, que la balise émet aussi en mode `MediaSource` dès qu'un
//     segment est décodable ;
//   · `buffered.length > 0`, qui dit qu'il y a de la matière à peindre — vrai
//     quel que soit le chemin, démuxage local ou remux serveur.
//
// L'échéance n'est plus qu'un filet, et elle est LONGUE à dessein.
//
// À huit secondes, elle basculait avant que l'image soit là : on quittait
// l'écran d'attente et sa vignette pour tomber sur le voile du lecteur — un
// blason gris et « Chargement en cours… ». C'est exactement l'écran dont Toji
// ne veut plus, et c'est mon échéance qui le faisait apparaître.
//
// Elle ne sert donc qu'au cas désespéré : une source qui ne rendra jamais rien.
// Trente secondes laissent le temps au démuxage le plus lent, et au-delà le
// lecteur prend la main pour dire lui-même que ça ne démarre pas.
const ATTENTE_MAX_IMAGE = 30_000;

const imagePrete = (v) => !!v && (v.readyState >= 2 || v.buffered?.length > 0);

// ⚠️ « DÉCODABLE » N'EST PAS « PEINTE ». LA DERNIÈRE MARCHE SE VOIT.
//
// Toji : « quand le lecteur apparaît, d'abord il y a du noir complet, puis
// l'épisode qui pop. Tu peux faire de façon que les deux poppent en même temps ?
// Ça doit se compter en moins de 20 ms. »
//
// Le tampon dit qu'il y a de quoi décoder ; il ne dit pas qu'une image a été
// présentée à l'écran. Entre les deux il reste le décodage de la première image
// et un cycle de composition — quelques dizaines de millisecondes pendant
// lesquelles la balise est là, vide et noire. C'est ce noir-là qu'on voit.
//
// `requestVideoFrameCallback` répond à la seule question qui compte : une image
// a-t-elle été présentée ? Le navigateur l'appelle APRÈS avoir composé la frame.
// Basculer à ce moment-là, c'est découvrir une balise qui affiche déjà quelque
// chose — l'écran d'attente et l'image se croisent sans intervalle visible.
//
// Deux précautions, parce que cette API n'est pas partout :
//   · Firefox ne la connaît pas — on bascule alors sur l'ancien repère, qui
//     reste juste, simplement moins précis ;
//   · une balise en PAUSE peut ne jamais présenter de frame. On ne l'attend donc
//     qu'un court instant : au-delà, mieux vaut un lecteur avec un noir de
//     quelques images qu'un blason qui s'éternise.
const GRACE_PREMIERE_IMAGE = 400;

function quandUneImageEstPeinte(v, faire) {
  if (typeof v.requestVideoFrameCallback !== 'function') return faire();
  let rendu = false;
  const une_fois = () => { if (!rendu) { rendu = true; faire(); } };
  try { v.requestVideoFrameCallback(une_fois); } catch { return faire(); }
  setTimeout(une_fois, GRACE_PREMIERE_IMAGE);
}

// ⚠️ LE LECTEUR SE COMPOSE SOUS LE VOILE, PAS APRÈS LUI.
//
// Toji : « il y a encore un mini-délai entre le moment où l'épisode est là et le
// lecteur déjà présent. C'est très très léger, mais regarde ce que tu peux
// faire. »
//
// Il restait un cycle de rendu, et il était structurel : la balise vidéo vit
// dans une vue CACHÉE jusqu'à la bascule. Un élément caché n'est pas composé par
// le navigateur — au moment où on l'affiche, il lui faut une image pour peindre,
// et c'est elle qu'on voyait passer.
//
// On inverse donc : le lecteur est montré TOUT DE SUITE, sous l'écran d'attente
// qui le recouvre. La classe `garde` existe exactement pour ça — `vue()` ne
// masque pas une vue qui la porte. La vidéo se compose derrière, tranquillement,
// pendant qu'on regarde encore le blason ; quand une image est peinte, il ne
// reste plus qu'à lever le voile. Rien à rendre, rien à attendre.
function basculerQuandPret() {
  const v = $('video');
  const attente = $('v-chargement');

  // Le voile reste au-dessus le temps que l'image arrive.
  if (attente) attente.classList.add('garde');
  vue('v-lecture');

  const aller = () => {
    const balise = $('video');
    // On ne lève le voile qu'une fois l'image réellement à l'écran : sinon il
    // s'efface sur une balise encore noire.
    if (balise) quandUneImageEstPeinte(balise, basculeFinale);
    else basculeFinale();
  };
  if (!v || imagePrete(v)) return aller();

  let fait = false;
  let sondage = 0;
  const y_aller = () => {
    if (fait) return;
    fait = true;
    v.removeEventListener('canplay', y_aller);
    v.removeEventListener('loadeddata', surDonnees);
    v.removeEventListener('progress', surDonnees);
    clearInterval(sondage);
    clearTimeout(garde);
    // Retour pressé entre-temps : la réponse arrive, mais elle ne rappelle plus
    // personne à un écran qu'on a quitté.
    if ($('v-chargement')?.hidden) return;
    aller();
  };
  const surDonnees = () => { if (imagePrete(v)) y_aller(); };
  const garde = setTimeout(y_aller, ATTENTE_MAX_IMAGE);
  // Le démuxeur remplit le tampon sans que la balise émette toujours un
  // événement : on regarde aussi de nous-mêmes, quatre fois par seconde.
  sondage = setInterval(surDonnees, 250);
  v.addEventListener('canplay', y_aller);
  v.addEventListener('loadeddata', surDonnees);
  v.addEventListener('progress', surDonnees);
}

// Toji (23/10) : « mesurer le préchargement et le réduire ». Une ligne par
// lecture au journal (donc à Loki) : attente → première image, et depuis le clic
// sur une des quatre versions (`/v/x/`, `tsudoi.v.clic`) quand on en vient.
let attenteMesuree = 0;
function mesurerPremiereImage() {
  if (!debutAttente || attenteMesuree === debutAttente) return;
  attenteMesuree = debutAttente;
  let clic = 0;
  try { clic = Number(sessionStorage.getItem('tsudoi.v.clic')) || 0; sessionStorage.removeItem('tsudoi.v.clic'); } catch { /* confort */ }
  const ms = Date.now() - debutAttente;
  tracer('lecture', `premiere image en ${ms} ms`, { ms, depuisClicVersion: clic ? Date.now() - clic : null });
}

function basculeFinale() {
  const v = $('v-chargement');
  if (!v) return 0;
  mesurerPremiereImage();
  // Un fondu précédent encore en vol ne doit pas ranger celui-ci après coup.
  interrompreRangement?.();

  // ⚠️ PLUS DE BLANC. L'ÉCRAN D'ATTENTE S'EFFACE, L'ÉPISODE EST DÉJÀ DERRIÈRE.
  //
  // Toji : « enlève le blanc avec l'effet qu'on a fait et toute la transition de
  // noir à blanc. Laisse le fond avec la preview de l'épisode et, en fait, juste
  // ça, switch sur l'épisode. »
  //
  // La transition virait au blanc, puis du blanc à l'image : deux mouvements, un
  // flash au milieu, et un écran qui ne ressemblait à rien de ce qu'il y avait
  // avant ni après. Elle valait quand elle recouvrait un démarrage ; elle ne
  // vaut plus rien maintenant que la bascule attend que l'image soit prête (voir
  // `basculerQuandPret`) — il n'y a plus rien à cacher, seulement un voile à
  // lever.
  //
  // Un fondu, donc. La preview reste jusqu'au bout : c'est la même image que
  // celle de l'épisode qui commence, et elle fait la liaison toute seule.
  const total = Math.min(
    700,
    Math.max(0, Number(prefs.lire('blason-transition', '500')) || 0),
  );

  // Le nettoyage est le même dans les deux chemins : ce qu'on a posé en style
  // inline doit repartir, sinon le chargement suivant hérite d'un voile déjà
  // transparent et ne se voit jamais.
  // `cacher` à faux : l'écran se rouvre pour un autre chargement — on retire
  // le fondu sans le masquer.
  const ranger = (cacher = true) => {
    if (cacher) v.hidden = true;
    v.removeAttribute('style');
    v.classList.remove('garde', 'lecture', 'inverse', 'efface');
    for (const el of v.querySelectorAll('[style]')) el.removeAttribute('style');
    const fond = $('v-fond');
    if (fond) {
      fond.style.removeProperty('opacity');
      fond.style.removeProperty('transition');
    }
    // Le corps ne garde aucune trace de la transition : la classe qui posait le
    // fond blanc a disparu avec elle.
    document.body.classList.remove('bascule');
  };

  if (total === 0) { ranger(); return 0; }

  // `garde` protège l'écran du masquage automatique le temps du fondu : sans
  // elle, `vue()` le cacherait net et l'on perdrait la transition qu'on vient de
  // demander.
  v.classList.add('garde');
  v.style.transition = `opacity ${total}ms cubic-bezier(.4, 0, .2, 1)`;
  v.style.opacity = '0';
  v.style.pointerEvents = 'none';

  // Un filet, comme partout ailleurs : si la fin de transition ne se déclenche
  // pas, l'écran ne doit pas rester en travers pour autant.
  const minuteur = setTimeout(() => { interrompreRangement = null; ranger(); }, total + 60);
  interrompreRangement = () => { clearTimeout(minuteur); interrompreRangement = null; ranger(false); };
  return total;
}

/** Ce vers quoi le retour pendant l'attente ramène. */
let retourDepuisAttente = null;

/**
 * Rejoue le même épisode depuis un fichier d'une autre langue, à la seconde où
 * l'on en était. Utilisé quand le fichier courant ne porte qu'une bande-son.
 */
/**
 * Réclame l'écart mesuré entre deux montages et recale la lecture dessus.
 *
 * Le calcul dure une minute environ : on repasse à intervalles espacés, et on
 * renonce plutôt que d'interroger sans fin. Un écart négligeable est ignoré —
 * déplacer la lecture d'un dixième de seconde se verrait plus que le défaut.
 */
function reclamerAlignement(avant, apres, seconde, essai = 0) {
  if (!avant || !apres || essai > 10) return;
  api(`/api/align/${avant}/${apres}`)
    .then(({ decalage }) => {
      if (decalage === null || decalage === undefined) {
        setTimeout(() => reclamerAlignement(avant, apres, seconde, essai + 1), 12_000);
        return;
      }
      const l = lecteur();
      if (l.ref() !== apres || Math.abs(decalage) < 0.4) return;
      l.recaler(Math.max(0, seconde + decalage));
      l.signaler(`Recalé de ${decalage > 0 ? '+' : ''}${decalage.toFixed(1)} s`);
    })
    // Sans mesure, la lecture reprend décalée de plusieurs secondes : ça se dit.
    .catch((err) => tracer('alignement', 'écart entre montages non mesuré', err?.message ?? String(err)));
}

// ---------------------------------------------------------------------------
// L'AUTRE LANGUE EST PREPAREE PENDANT QU'ON REGARDE
//
// Toji : « ça met du temps, en fait, pour les deux, le changement de langue. »
//
// Ce n'est pas un defaut de code, c'est le cout du geste : basculer relance une
// resolution complete — interroger les add-ons, obtenir un lien debride,
// verifier qu'il repond, rouvrir le conteneur. Plusieurs secondes, pendant
// lesquelles l'ecran de chargement remplace l'episode.
//
// Sauf qu'on sait des le debut de la lecture qu'on POURRAIT basculer, et on a
// tout le temps de le preparer : la resolution part en fond, une fois, et le
// resultat attend. Au clic, il n'y a plus qu'a ouvrir — ce que le lecteur fait
// deja en une fraction de seconde.
//
// ⚠️ UNE SEULE PREPARATION A LA FOIS, ET JAMAIS DEUX FOIS LA MEME. Une
// resolution coute des requetes aux add-ons et une verification de lien : la
// lancer a chaque `timeupdate` reviendrait a marteler les sources pour un clic
// qui ne viendra peut-etre jamais.
let minuteurAutreLangue = null;

/** Ce que le catalogue sait de cette langue pour l'episode ouvert. */
function etatDoublage(vers) {
  try {
    const fiche = episodeCatalogue(episodeEnCours()?.saison, episodeEnCours()?.episode);
    if (Array.isArray(fiche?.langues) && fiche.langues.length) {
      return fiche.langues.includes(vers === 'vf' ? 'vf' : 'vo');
    }
  } catch { /* on ne sait pas */ }
  return null;
}
let langueEnAvance = null; // { cible, vers, choix }
let preparationLangue = null;

function preparerLautreLangue(cible, vers) {
  if (!cible || !vers) return;
  if (langueEnAvance?.cible === cible && langueEnAvance?.vers === vers) return;
  if (preparationLangue === `${cible}|${vers}`) return;
  preparationLangue = `${cible}|${vers}`;

  api(`/api/langue/${cible}?vers=${vers}&depuis=${lecteur().ref() ?? ''}`)
    .then((choix) => {
      if (choix?.ref) {
        langueEnAvance = { cible, vers, choix, quand: Date.now() };
        tracer('langue', `${vers.toUpperCase()} prete en avance`, choix.source ?? null);
      }
    })
    // Une langue qui n'existe pas n'est pas une panne : le bouton s'en
    // apercevra au clic, comme avant.
    .catch((err) => tracer('langue', 'préparation en avance impossible', err?.message ?? String(err)))
    .finally(() => { preparationLangue = null; });
}

async function changerLangue(cible, vers, seconde) {
  const l = lecteur();

  // Preparee pendant la lecture : on ouvre sans passer par l'ecran d'attente.
  // Le lien debride vit six heures — bien au-dela de ce qu'on garde ici.
  //
  // ⚠️ C'EST CE CHEMIN-CI QUI DONNAIT L'ÉCRAN NOIR, ET IL EST LE PLUS COURANT.
  //
  // Toji (14/09) : « pourquoi il y a des écrans noirs quand on change de
  // langue ? » — la langue préparée d'avance saute l'écran d'attente, à juste
  // titre (le lien est déjà résolu, il n'y a plus rien à chercher). Mais
  // RÉSOLU N'EST PAS CHARGÉ : `ouvrirAutreLangue` vide quand même la balise et
  // rouvre le fichier depuis zéro, et rien ne couvrait plus ce trou-là depuis
  // que le voile du lecteur a été retiré. Mesuré : 479 ms au cran direct,
  // 3 309 ms quand le démuxeur rouvre le fichier — voir
  // `essais/bascule-de-source.mjs`.
  //
  // C'est le lecteur qui répond maintenant, et pas un second écran d'attente
  // — celui-là, Toji l'a fait retirer deux fois : il GÈLE la dernière image et
  // ne la lâche qu'à la première image peinte de la nouvelle source (voir
  // « GELER L'IMAGE PENDANT QU'ON CHANGE DE FICHIER », lecteur.js). Ne pas
  // rajouter `vue('v-chargement')` ici : ce serait remettre exactement l'écran
  // qui a été supprimé.
  const avance = langueEnAvance;
  if (avance && avance.cible === cible && avance.vers === vers && Date.now() - avance.quand < 20 * 60_000) {
    langueEnAvance = null;
    l.ouvrirAutreLangue(avance.choix, vers, seconde);
    return;
  }

  // D'abord l'écran, ensuite la recherche : l'inverse laisse croire que le
  // bouton n'a pas répondu.
  l.video.pause();
  document.body.classList.add('plein');
  $('v-chargement')?.classList.add('lecture');
  debutAttente = Date.now();
  // Renoncer pendant ce chargement rend le lecteur, pas l'écran du lancement
  // précédent (audit 14e 21/10, P10).
  retourDepuisAttente = 'v-lecture';
  vue('v-chargement');
  appliquerBlason(lireBlason(prefs));
  // Même épisode, autre bande-son : la vignette est la même. Il faut pourtant
  // la reposer — la bascule qui a lancé la lecture l'a laissée effacée.
  peindreFondAttente(imageEpisodeEnGrand(episodeEnCours()), imageEpisode(episodeEnCours()));
  peindreTitreAttente(episodeEnCours());

  let choix;
  try {
    choix = await api(`/api/langue/${cible}?vers=${vers}&depuis=${lecteur().ref() ?? ''}`);
  } catch (err) {
    // ⚠️ « AUCUNE SOURCE VF » SUR TOUTE ERREUR, MEME UNE VRAIE PANNE.
    //
    // Ce catch affichait le meme message que la langue demandee n'existe
    // pas — sans rien tracer — que le serveur ait vraiment cherche et
    // trouve zero fichier (404, `err.status`, voir `api()`) OU qu'il n'ait
    // jamais pu repondre (reseau coupe, 500...). Le premier cas est normal
    // et attendu ; le second cachait une vraie panne derriere un message
    // qui laissait croire que le film n'a simplement pas cette langue.
    // `chargerSousTitres`, juste a cote, trace deja son propre echec — on
    // fait pareil ici.
    if ($('v-chargement').hidden) {
      tracer('langue', `abandon : écran quitté (${err.message})`, { ecran: ecranCourant });
      return;
    }
    vue('v-lecture');
    if (err.status === 404) {
      tracer('langue', `aucune source ${vers.toUpperCase()} pour ${cible}`);
      l.signaler(`Aucune source ${vers.toUpperCase()} disponible`);
    } else {
      tracer('langue', `echec du changement de langue — ${err.message}`, cible);
      l.signaler('Erreur réseau — réessaie');
    }
    return;
  }
  // Retour pressé pendant la recherche : même garde que `lancerLEpisode`. Sans
  // elle, la réponse rouvrait la source et rappelait le lecteur à l'écran.
  if ($('v-chargement').hidden) {
    tracer('langue', 'abandon : écran quitté', { ecran: ecranCourant });
    return;
  }

  // La transition attend l'image : voir `basculerQuandPret`.
  // La reprise tient compte de l'écart mesuré entre les deux montages : sans
  // lui, on reprendrait à la même seconde d'un fichier qui ne commence pas au
  // même endroit.
  const avant = l.ref();
  l.ouvrirAutreLangue(choix, vers, seconde);

  // L'écart entre les deux montages se mesure en arrière-plan : il demande de
  // lire l'amorce des deux fichiers. On recale dès qu'il est connu, plutôt que
  // de retarder la lecture d'une minute pour l'obtenir.
  reclamerAlignement(avant, choix.ref, seconde);
  basculerQuandPret();
}

/**
 * Attache les pistes de texte d'un fichier donné.
 *
 * Appelée à l'ouverture d'un épisode ET à chaque changement de fichier : des
 * répliques venues d'un autre montage ne correspondraient à rien.
 */
/**
 * Va chercher des sous-titres dans un autre fichier du même épisode.
 *
 * La recherche interroge plusieurs sources et ouvre chacune avec ffprobe : elle
 * prend du temps, et le serveur répond « en cours » tant qu'elle n'a pas
 * abouti. On repasse donc, sans jamais retarder la lecture.
 */
/**
 * Dit — ou cesse de dire — qu'une recherche de sous-titres est en cours.
 *
 * ⚠️ LE PANNEAU ANNONCAIT « AUCUN SOUS-TITRE » PENDANT QU'ON EN CHERCHAIT.
 *
 * La recherche de secours dure jusqu'a quarante-huit secondes ; le panneau,
 * lui, se peint a l'ouverture avec ce qu'il voit — c'est-a-dire rien. Le
 * verdict tombait donc avant la reponse, et rien ne venait le corriger.
 *
 * Le drapeau vit sur la balise video plutot que dans une variable de module :
 * c'est le seul objet que les deux cotes partagent, et `st-panneau.js`
 * l'observe (`MutationObserver`) pour se repeindre tout seul des qu'il bouge.
 */
function marquerRechercheST(encours) {
  const v = $('video');
  if (!v) return;
  if (encours) v.dataset.stCherche = '1';
  else delete v.dataset.stCherche;
}

function chargerSousTitresDeSecours(cible, depuis, essai = 0) {
  // Huit essais sans rien : c'est fini, et le panneau doit pouvoir le dire.
  if (essai > 8) { marquerRechercheST(false); return; }
  api(`/api/subs-secours/${cible}?depuis=${depuis}`)
    .then(({ pistes, enCours, ref }) => {
      // ⚠️ AUCUNE VERIFICATION DE FRAICHEUR — cette recherche peut poller
      // jusqu'a 48 s (8 essais * 6 s). Pendant ce temps, l'episode pour
      // lequel elle a ete lancee peut avoir change : suivant, precedent,
      // changement de langue. Une reponse tardive posait alors les
      // sous-titres d'un fichier ABANDONNE sur la video en cours — meme
      // patron que `reclamerAlignement` et `preparationEncoreUtile` : on
      // verifie que `depuis` (le fichier pour lequel on cherchait) est
      // toujours celui que le lecteur joue avant d'agir OU de reprogrammer
      // un nouvel essai qui ne servirait plus a rien.
      if (lecteur().ref() !== depuis) {
        tracer('soustitres', 'secours perimes — le fichier a change', depuis);
        marquerRechercheST(false);
        return;
      }
      // Toujours en cours : le drapeau RESTE pose, c'est tout son objet.
      if (enCours) {
        setTimeout(() => chargerSousTitresDeSecours(cible, depuis, essai + 1), 6000);
        return;
      }
      marquerRechercheST(false);
      tracer('soustitres', `secours : ${pistes?.length ?? 0} piste(s)`, ref ?? null);
      if (!pistes?.length || !ref) return;
      // Les pistes viennent d'un AUTRE fichier : c'est sa référence qui doit
      // servir à les récupérer, pas celle du fichier joué.
      attacherPistes(ref, pistes, true);
      lecteur().signaler(`${pistes.length} sous-titre(s) empruntés à une autre source`);
    })
    // ⚠️ Muet jusqu'ici : la recherche echouait sans jamais retirer son
    // drapeau, et le panneau serait reste bloque sur « recherche en cours »
    // pour le reste de la lecture.
    .catch((err) => {
      marquerRechercheST(false);
      tracer('soustitres', 'recherche de secours interrompue', err?.message ?? err);
    });
}

// ---------------------------------------------------------------------------
// LES SOUS-TITRES SE CHARGENT A LA MAIN, PAS PAR UNE BALISE <track>
//
// Toji : « j'ai aucun sous-titre qui s'affiche au niveau du lecteur, rien du
// tout. J'ai beau mettre FRE ou le reste, y a rien qui s'affiche. »
//
// Le diagnostic, en production : ffprobe listait bien la piste (« 1 piste(s) ·
// ffprobe · 0:fre »), le menu la proposait, on la choisissait — et le serveur
// n'a JAMAIS vu passer la requete du fichier .vtt. Le navigateur ne l'avait pas
// demandee.
//
// C'est le defaut de la balise `<track>` : elle ne telecharge sa piste que
// lorsque le navigateur le decide, et il ne le dit pas quand il s'en abstient.
// Une piste posee sur une video en cours de remux, une origine qui ne convient
// pas au CORS, un `mode` change trop tot ou trop tard — tous ces cas donnent le
// meme resultat visible : un menu qui propose une piste vide, sans erreur, sans
// trace, sans rien.
//
// On ne lui laisse donc plus la main. Le fichier est demande par `fetch` — donc
// avec le jeton de session, comme tout le reste — les repliques sont lues, et
// posees une par une dans une piste creee a la main. Ce qui echoue se voit :
// une reponse HTTP a un code, et un fichier vide se compte.
// ---------------------------------------------------------------------------

/**
 * Les repliques d'un fichier WebVTT.
 *
 * Volontairement tolerant : les add-ons servent du SRT converti, parfois avec
 * des numeros de bloc, des `

`, ou des marqueurs de position accroches au
 * timing. Refuser un fichier pour un detail de forme, c'est perdre le seul
 * sous-titre disponible.
 */
/**
 * Le texte d'une replique, debarrasse de ce qui n'est pas du texte.
 *
 * Toji : « les sous-titres ne s'affichent pas correctement. » Ce que les
 * fichiers portent vraiment :
 *   · des directives ASS entre accolades — position, italique, karaoke. ffmpeg
 *     les laisse passer dans le WebVTT qu'il produit, et le peintre les affiche
 *     telles quelles, en toutes lettres, devant la replique.
 *   · du balisage WebVTT que le peintre echappe (il n'autorise que i, b et u) :
 *     `<c.italic>`, `<v Narrateur>`, `<font color>`. Echappe, donc VISIBLE.
 *   · des entites — `&amp;`, `&nbsp;` — que rien ne decode.
 *
 * On garde l'italique, le gras et le souligne : ce sont les seuls qui portent
 * du sens dans un sous-titre (une pensee, un titre, une voix off).
 */
export function nettoyerReplique(brut) {
  return String(brut ?? '')
    .replace(/\{[^{}]*\}/g, '')
    // ⚠️ LE REGARD EN AVANT SE POSE APRES `<`, ET COUVRE LE `/`.
    //
    // Écrit `<\/?(?![ibu]\s*>)`, le moteur reprend la main : il essaie avec le
    // `/` consommé (le regard voit « i> », donc il refuse), PUIS sans — et là
    // le regard voit « /i> », qui n'est pas une de nos trois balises. `</i>`
    // partait, `<i>` restait : une réplique en italique s'ouvrait sans jamais
    // se fermer, et tout le reste du sous-titre passait en italique.
    .replace(/<(?!\/?[ibu]\s*>)[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    // Les lignes vidées par le nettoyage ne doivent pas laisser un blanc au
    // milieu de la réplique.
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}

/** L'horloge WebVTT d'une seconde : `01:02:03.500`. */
const horlogeVTT = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const d2 = (n) => String(n).padStart(2, '0');
  return `${d2(h)}:${d2(m)}:${d2(sec)}.${String(ms).padStart(3, '0')}`;
};

/** Des repliques vers un fichier WebVTT — celui qu'on servira a la balise. */
export function enTexteVTT(repliques) {
  const corps = (repliques ?? [])
    .map((c) => `${horlogeVTT(c.debut)} --> ${horlogeVTT(c.fin)}\n${c.texte}`)
    .join('\n\n');
  return `WEBVTT\n\n${corps}\n`;
}

export function lireVTT(texte) {
  const secondes = (h, m, sec, ms) => Number(h) * 3600 + Number(m) * 60 + Number(sec) + Number(ms) / 1000;
  const HORLOGE = /(?:(\d{1,3}):)?(\d{1,2}):(\d{1,2})[.,](\d{1,3})\s*-->\s*(?:(\d{1,3}):)?(\d{1,2}):(\d{1,2})[.,](\d{1,3})/;

  const repliques = [];
  const lignes = String(texte ?? '').replace(/\r\n?/g, '\n').split('\n');
  for (let i = 0; i < lignes.length; i += 1) {
    const m = HORLOGE.exec(lignes[i]);
    if (!m) continue;
    const debut = secondes(m[1] ?? 0, m[2], m[3], m[4]);
    const finR = secondes(m[5] ?? 0, m[6], m[7], m[8]);
    const corps = [];
    for (let j = i + 1; j < lignes.length && lignes[j].trim() !== ''; j += 1) corps.push(lignes[j]);
    i += corps.length;
    // Une replique sans texte, ou qui finit avant de commencer, ne s'affiche
    // pas : `addCue` la refuserait et emporterait tout le reste avec elle.
    const contenu = nettoyerReplique(corps.join('\n'));
    if (!contenu || !(finR > debut)) continue;
    repliques.push({ debut, fin: finR, texte: contenu });
  }
  return repliques;
}

/**
 * Attache des pistes de texte à la vidéo et en active une.
 *
 * ⚠️ `seulementSiMieux` EXISTE PARCE QUE CETTE FONCTION EST APPELÉE PLUSIEURS
 * FOIS POUR LE MÊME FICHIER.
 *
 * Depuis que chaque source de sous-titres s'affiche dès qu'elle répond, les
 * lots arrivent les uns après les autres. Sans ce drapeau, le dernier arrivé
 * gagnait : une source lente qui ne propose que du portugais aurait remplacé le
 * français déjà posé par la première, quatre secondes après que la personne a
 * commencé à le lire. On n'active donc un nouveau lot QUE s'il apporte une
 * langue mieux placée que celle en cours.
 */
function attacherPistes(ref, pistes, emprunt = false, { seulementSiMieux = false } = {}) {
  const video = $('video');
  if (!video || !pistes?.length) return 0;

  // ⚠️ UNE PISTE CREEE PAR `addTextTrack` NE SE RETIRE JAMAIS.
  //
  // Releve en production : apres deux ouvertures, `video.textTracks` en portait
  // VINGT-SIX — treize par episode, empilees. Le nettoyage du lecteur
  // (`querySelectorAll('track')`) ne trouvait plus rien a retirer, puisqu'il
  // n'y avait plus de balise. Et le menu, qui numerote ce qu'il voit, envoyait
  // donc l'indice d'une piste FANTOME du chargement precedent : choisir
  // « Francais » ne chargeait rien, en silence. C'est exactement ce que Toji
  // voyait.
  //
  // On revient donc a une balise `<track>` — retirable, elle — mais SANS lui
  // confier le telechargement : sa source est un `blob:` fabrique ici, a partir
  // du fichier qu'on a demande, lu et nettoye nous-memes. On garde le controle
  // (et les traces) sans perdre le menage.
  // ⚠️ ON N'ATTACHE QUE CE QU'ON VEUT LIRE.
  //
  // Toji : « il faut que tu fasses un reglage pour afficher que ceux qui sont
  // selectionnes : francais, anglais. Le reste, tu me les filtres
  // automatiquement. » Un episode bien servi propose treize pistes — turc,
  // thai, hebreu — et il fallait les faire defiler pour trouver la sienne.
  //
  // Le filtre agit ICI, a l'attachement, et pas dans le menu : une piste qu'on
  // ne veut pas voir n'a aucune raison d'exister dans la page, ni de se faire
  // telecharger si on la touche par erreur.
  // ⚠️ `typeEnLecture` ET NON LE REGLAGE GENERAL — defaut paye le 15/09.
  //
  // Toji : « pour les films de l'anglais avec des sous-titres francais tout le
  // temps ». Cette fonction ne connait que `ref`, une reference de FICHIER :
  // elle lisait donc `st-langues`, unique pour tout le catalogue, et satisfaire
  // les films imposait la meme chose aux animes. Le type de ce qui joue est
  // pose par les deux seules fonctions qui lancent une lecture.
  const voulues = clesDepuisCodes(languesPreferees(typeEnLecture));
  const { pistes: retenues, filtre, ecartees } = pistesRetenues(pistes, voulues);
  if (filtre && ecartees) {
    tracer('sous-titres', `${ecartees} piste(s) ecartee(s) par le reglage de langues`);
  } else if (!filtre && voulues.length && pistes.length) {
    // Aucune des langues voulues n'est la : on montre tout plutot que rien.
    // Un episode dont les seuls sous-titres sont en japonais ne doit pas se
    // retrouver muet parce qu'on a coche « francais, anglais ».
    tracer('sous-titres', 'aucune langue voulue ici — toutes les pistes restent');
  }

  // ⚠️ UNE PISTE ASS OU IMAGE N'EST PAS PEINTE PAR LE CALQUE TEXTE.
  //
  // Elle passe par une bibliothèque (JASSUB, libpgs — voir `st-riches.js`)
  // que le lecteur monte quand la piste est choisie. Mais le panneau ne liste
  // que `video.textTracks` : pour qu'un ASS y apparaisse et s'y choisisse
  // comme les autres, on lui donne une balise `<track>` FACTICE — la même
  // balise que les autres, donc retirable (voir l'avertissement sur
  // `addTextTrack` ci-dessus : une piste créée par lui ne part JAMAIS) —, un
  // VTT vide en `blob:` pour que le navigateur n'aille rien chercher, et
  // `chargee: true` pour qu'`activerPiste` ne télécharge rien. C'est
  // l'observateur de `change` (`surChangementDePiste`) qui, en la voyant
  // passer active, demande le rendu riche au lecteur.
  const capacites = capacitesDuNavigateur();
  observerPistesRiches(video);
  const posees = [];
  for (const p of retenues) {
    const rendu = formatDeRendu(p, capacites);
    if (rendu === null) {
      // Une image sans Worker : rien ne sait la peindre. Une entrée vide dans
      // le menu ferait croire à une piste qui ne marche pas.
      tracer('sous-titres', 'piste image sans Worker — écartée', p.langue);
      continue;
    }
    if (p.format === 'ass' && rendu === 'texte') {
      // Le VTT du serveur prend le relais : les mots restent, les styles non.
      tracer('sous-titres', 'ASS rendu en texte — WebAssembly indisponible', p.langue);
    }
    const t = document.createElement('track');
    t.kind = 'subtitles';
    t.srclang = p.langue;
    t.label = (p.titre || p.langue.toUpperCase()) + (emprunt ? ' (autre source)' : '');
    video.appendChild(t);
    // `t.track` existe des l'ajout : c'est l'objet que le menu et le peintre
    // manipulent, et il porte deja le mode.
    t.track.mode = 'disabled';
    if (rendu === 'ass' || rendu === 'image') {
      t.dataset.riche = rendu;
      t.src = URL.createObjectURL(new Blob(['WEBVTT\n'], { type: 'text/vtt' }));
      const urls = urlsDePiste({ ref, piste: p, base, avecAcces: avecJeton });
      // Les polices du conteneur ne se demandent QUE pour un ASS qu'on va
      // vraiment rendre en ASS — jamais pour une image, jamais pour un repli.
      const polices = rendu === 'ass' ? policesDe(ref) : Promise.resolve([]);
      posees.push({ balise: t, piste: t.track, entree: p, chargee: true, ref, riche: { format: rendu, urls, polices } });
      continue;
    }
    posees.push({ balise: t, piste: t.track, entree: p, chargee: false, ref });
  }
  // La liste repart de zero a chaque episode : les balises de l'episode
  // precedent ont ete retirees par le lecteur, et leurs pistes avec. Leurs
  // `blob:` doivent partir aussi — une URL d'objet retient son contenu en
  // memoire tant que personne ne la revoque, et un sous-titre de serie fait
  // quelques dizaines de kilo-octets par episode.
  const anciennes = pistesChargeables.get(video) ?? [];
  for (const x of anciennes) {
    if (x.balise?.isConnected) continue;
    if (x.balise?.src?.startsWith('blob:')) URL.revokeObjectURL(x.balise.src);
  }
  pistesChargeables.set(video, [...anciennes.filter((x) => x.balise?.isConnected), ...posees]);

  // Les pistes sont deja dans l'ordre voulu : la premiere est la bonne. On
  // garde tout de meme la recherche par code, pour le cas ou le filtre a rendu
  // la main (aucune langue voulue disponible).
  const memoST = choixSTDe(lectureCourante?.oeuvreId);
  const codes = memoST && memoST !== 'aucun'
    ? [memoST, ...languesPreferees(typeEnLecture)]
    : languesPreferees(typeEnLecture);
  let choisi = null;
  for (const code of codes) {
    choisi = posees.find((x) => String(x.entree.langue || '').toLowerCase().startsWith(code));
    if (choisi) break;
  }

  // « Aucun » n'est pas une langue de repli : sans ce test, quelqu'un qui a
  // demandé à ne pas voir de sous-titres verrait quand même la première piste
  // du fichier, faute d'en avoir trouvé une dans ses langues.
  // `sansSousTitres` et non `st-sans` : « aucun » peut aussi etre dit pour ce
  // type-la seulement (`st-anime = 'aucun'`), et il doit valoir la meme chose.
  if (memoST === 'aucun' || (!memoST && sansSousTitres(typeEnLecture))) {
    lecteur().pistesPretes();
    return pistes.length;
  }

  const actif = choisi ?? posees[0];
  // ⚠️ UN LOT QUI ARRIVE APRÈS NE DÉTRÔNE QUE S'IL FAIT MIEUX.
  //
  // Le rang d'une langue dans `languesPreferees()` est ce qui départage. Une
  // piste déjà en cours dans une langue mieux placée reste : c'est celle que
  // la personne est en train de lire.
  if (seulementSiMieux && actif) {
    const rangDe = (code) => {
      const i = codes.findIndex((c) => String(code || '').toLowerCase().startsWith(c));
      return i < 0 ? 99 : i;
    };
    const enCours = [...(video.textTracks ?? [])].find((t) => t.mode === 'showing');
    if (enCours && rangDe(enCours.language) <= rangDe(actif.entree.langue)) {
      tracer('sous-titres', 'lot tardif gardé au menu, sans bascule',
        `${enCours.language} reste devant ${actif.entree.langue}`);
      lecteur().pistesPretes();
      return retenues.length;
    }
  }
  if (actif) activerPiste(video, actif.piste);
  lecteur().pistesPretes();
  return retenues.length;
}

// Ce qui reste a telecharger, par element video. Une `WeakMap` : la video
// remplacee emporte ses pistes avec elle, sans qu'on ait a faire le menage.
const pistesChargeables = new WeakMap();

/**
 * Ce qu'une balise <track> ou une bibliothèque porterait en en-tête, dans
 * l'adresse : le jeton de session. Même geste qu'`activerPiste` pour le VTT,
 * et qu'`avecAcces` dans le lecteur pour le flux vidéo.
 */
function avecJeton(chemin) {
  if (!session) return chemin;
  return chemin + (chemin.includes('?') ? '&' : '?') + 't=' + encodeURIComponent(session);
}

// Les polices du conteneur, par référence — une seule demande par fichier,
// quel que soit le nombre de pistes ASS qu'il porte.
let policesMemo = { ref: null, promesse: null };

/**
 * Les URL des polices embarquées dans le conteneur `ref` (les pièces jointes
 * d'un Matroska de fansub). Une liste illisible n'empêche rien : JASSUB rend
 * avec sa police par défaut, et la trace dit pourquoi.
 */
function policesDe(ref) {
  if (policesMemo.ref === ref && policesMemo.promesse) return policesMemo.promesse;
  const promesse = api(`/api/subs/${ref}/polices`)
    .then(({ polices }) => urlsDesPolices({ ref, polices, base, avecAcces: avecJeton }))
    .catch((err) => {
      tracer('sous-titres', 'polices du conteneur illisibles — rendu sans elles', err?.message ?? String(err));
      return [];
    });
  policesMemo = { ref, promesse };
  return promesse;
}

/**
 * La piste active a changé : si c'est une piste riche, le lecteur monte son
 * rendu ; sinon — une piste texte, ou aucune — il le retire.
 *
 * ⚠️ LE PANNEAU POSE `hidden`, JAMAIS `showing` : c'est le mode qui fait
 * décoder sans afficher, celui que le calque texte attend (voir
 * `st-panneau.js`). L'un comme l'autre veut dire « choisie ».
 */
async function surChangementDePiste(video) {
  const active = [...(video.textTracks ?? [])].find((p) => p.mode === 'hidden' || p.mode === 'showing') ?? null;
  const connues = pistesChargeables.get(video) ?? [];
  const riche = active ? (connues.find((x) => x.piste === active)?.riche ?? null) : null;
  if (!riche) {
    lecteur().retirerPisteRiche();
    return;
  }
  const polices = await riche.polices;
  // Le choix a pu changer pendant que les polices arrivaient : on ne monte
  // pas le rendu d'une piste qu'on vient de quitter.
  const encore = [...(video.textTracks ?? [])].find((p) => p.mode === 'hidden' || p.mode === 'showing') ?? null;
  if (encore !== active) return;
  lecteur().poserPisteRiche({ format: riche.format, urls: riche.urls, polices });
}

/** Branche l'observateur ci-dessus, une seule fois par balise. */
function observerPistesRiches(video) {
  if (!video?.dataset || video.dataset.stRiches === '1' || !video.textTracks?.addEventListener) return;
  video.dataset.stRiches = '1';
  video.textTracks.addEventListener('change', () => {
    surChangementDePiste(video).catch((err) => tracer('sous-titres', 'rendu riche : observateur en erreur', err?.message ?? String(err)));
  });
}

// ⚠️ LE TELECHARGEMENT LE PLUS LENT GAGNAIT, PAS LE CLIC LE PLUS RECENT.
//
// `activerPiste` pose les modes tout de suite, puis va chercher les repliques.
// Deux clics rapproches lancaient deux allers-retours, et celui qui revenait en
// DERNIER reposait son `mode = 'hidden'` : on se retrouvait avec la piste
// abandonnee a l'ecran, ou avec les deux. Ce compteur donne un rang a chaque
// demande — seule la derniere a le droit d'agir en revenant.
let generationPiste = 0;

/**
 * Rend une piste visible — en allant chercher ses repliques si c'est la
 * premiere fois.
 *
 * Exportee par `window` plus bas : c'est le menu des sous-titres du lecteur qui
 * l'appelle quand on change de piste, et il vit dans un autre module.
 */
async function activerPiste(video, piste) {
  const ma = ++generationPiste;
  for (const p of video.textTracks) p.mode = p === piste ? 'hidden' : 'disabled';
  const connues = pistesChargeables.get(video) ?? [];
  const cible = connues.find((x) => x.piste === piste);
  if (!cible || cible.chargee) return;
  cible.chargee = true;

  const adresse = base(`/api/subs/${cible.ref}/${cible.entree.rang}.vtt`)
    + (session ? `?t=${encodeURIComponent(session)}` : '');
  try {
    const r = await fetch(adresse, { credentials: 'include', signal: AbortSignal.timeout(20000) });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const repliques = lireVTT(await r.text());
    tracer('soustitres', `${repliques.length} replique(s) chargee(s)`, cible.entree.langue);
    if (!repliques.length) {
      lecteur().signaler('cette piste de sous-titres est vide');
      return;
    }
    // On reecrit un WebVTT propre — repliques nettoyees, horloges normalisees —
    // et on le sert a la balise depuis la memoire. Le navigateur ne va nulle
    // part : il n'a plus l'occasion de renoncer sans le dire.
    const blob = new Blob([enTexteVTT(repliques)], { type: 'text/vtt' });
    // La balise recoit ses repliques dans TOUS les cas : le telechargement a
    // eu lieu, et `chargee` empeche deja de le refaire — les jeter laisserait
    // une piste marquee « chargee » et definitivement vide.
    cible.balise.src = URL.createObjectURL(blob);

    // En revanche l'AFFICHAGE appartient au dernier clic, pas au dernier
    // telechargement arrive. Une demande doublee pose ses repliques et s'en
    // tient la : les modes restent ceux qu'a choisis le clic le plus recent.
    if (ma !== generationPiste) {
      tracer('soustitres', 'piste doublee par un choix plus recent', cible.entree.langue);
      return;
    }
    piste.mode = 'hidden';
    lecteur().pistesPretes();
  } catch (err) {
    cible.chargee = false;
    tracer('soustitres', 'piste illisible', err.message);
    lecteur().signaler('sous-titres indisponibles');
  }
}

// Le menu des sous-titres vit dans `lecteur.js` et doit pouvoir changer de
// piste : sans ce passe-plat, il reposerait `mode` a la main et la piste
// resterait vide, faute d'avoir ete telechargee.
// ⚠️ `detacherLecteur` ETAIT ECRITE EN ENTIER, ET APPELEE NULLE PART.
//
// Fenetre de document detachee emportant le lecteur COMPLET — commandes et
// sous-titres compris —, repli sur la vignette image seule, remise en place
// au retour, message honnete si les deux sont refuses : tout etait la, et
// aucun bouton, aucune touche ne l'appelait. Recherche faite sur tout le
// projet : une seule occurrence, sa definition.
//
// Le lecteur vit dans son propre module et ne connait pas cette fonction —
// meme situation que pour les pistes de sous-titres, meme passe-plat.
window.__detacherLecteur = () => { detacherLecteur(); };

window.__activerPisteST = (piste) => {
  const video = $('video');
  if (piste) retenirChoixST(piste.language || piste.label);
  if (video && piste) activerPiste(video, piste);
};

/**
 * Interroge chaque source externe de son côté, et accroche ses pistes au menu
 * dès qu'elles arrivent.
 *
 * Rend le NOMBRE de pistes trouvées, toutes sources confondues — c'est lui qui
 * décide s'il faut aller emprunter les sous-titres d'un autre montage. Une
 * source qui échoue (502) ne compte pas comme « elle n'a rien » : la
 * distinction est tout l'objet du `pistes: null` que le serveur renvoie.
 */
function chargerSourcesExternes(ref, externes) {
  const sources = Array.isArray(externes) ? externes : [];
  if (!sources.length) return Promise.resolve(0);
  let trouvees = 0;
  return Promise.all(sources.map((src) => api(`/api/subs/${ref}/externes/${src.i}`)
    .then(({ pistes }) => {
      // Même garde que partout ailleurs : le temps de l'aller-retour, le
      // lecteur a pu changer de fichier. Poser les pistes d'un fichier
      // abandonné sur la vidéo en cours est le défaut qu'on répare depuis
      // trois jours, sous trois formes différentes.
      if (lecteur().ref() !== ref) {
        tracer('soustitres', `${src.nom} : réponse périmée`, ref);
        return;
      }
      if (!pistes?.length) {
        tracer('soustitres', `${src.nom} : rien`, null);
        return;
      }
      trouvees += attacherPistes(ref, pistes, false, { seulementSiMieux: true });
      tracer('soustitres', `${src.nom} : ${pistes.length} piste(s)`,
        pistes.map((p) => p.langue).join(','));
    })
    .catch((err) => {
      // ⚠️ UNE SOURCE MUETTE SE DIT. Sans ce journal, « il n'y a pas le
      // français » ne distinguerait pas une source qui n'en a pas d'une
      // source qui n'a pas répondu — et les deux appellent des gestes
      // opposés.
      tracer('soustitres', `${src.nom} : injoignable`, err?.message ?? err);
    })))
    .then(() => trouvees);
}

/**
 * Charge les sous-titres du fichier joué. S'il n'en porte aucun, va les
 * emprunter à un autre montage du même épisode plutôt que de s'en passer.
 */
function chargerSousTitres(ref, cible) {
  api(`/api/subs/${ref}`)
    .then(({ pistes, externes }) => {
      // ⚠️ MEME GARDE QUE `chargerSousTitresDeSecours`, JUSTE EN DESSOUS.
      //
      // L'aller-retour reseau suffit, a lui seul, a ce que le fichier pour
      // lequel `ref` a ete demande ne soit plus celui que le lecteur joue —
      // episode suivant, changement de langue pendant que la liste des
      // pistes etait encore en vol. Sans cette verification, les pistes
      // d'un fichier abandonne pouvaient s'attacher a la video en cours.
      if (lecteur().ref() !== ref) {
        tracer('soustitres', 'reponse perimee — le fichier a change', ref);
        return;
      }
      const n = attacherPistes(ref, pistes);
      tracer('soustitres', `${n} piste(s) attachee(s)`, {
        langues: (pistes ?? []).map((p) => p.langue),
        actives: [...($('video')?.textTracks ?? [])].map((p) => `${p.language}:${p.mode}`),
      });
      // ⚠️ CHAQUE SOURCE EXTERNE A SON PROPRE TEMPS — ON NE LES ATTEND PLUS.
      //
      // Toji, 14/09 : « tu affiches de façon indépendante chaque piste. La
      // piste intégrée peut pop directement, la V3 peut pop directement, la V3
      // Pro arrive après. Chacune a son timing. Dès qu'il y en a une qui est
      // dispo, hop, on l'affiche. »
      //
      // Mesuré en production le même jour : OpenSubtitles v3 rend 45 pistes en
      // 40 MILLISECONDES, la « Pro » met de 0,7 à 4,5 s pour en rendre zéro à
      // huit, et le ffprobe du fichier coûte 1,47 s. Tout partait dans une
      // seule réponse : le menu attendait donc la plus lente des trois.
      //
      // Elles partent maintenant toutes en même temps, et chacune s'accroche
      // au menu quand elle arrive. Le tri par langue préférée reste fait à
      // l'intérieur de chaque lot par le serveur.
      return chargerSourcesExternes(ref, externes)
        .then((externesVues) => {
          if (n || externesVues) return;
          tracer('soustitres', 'aucune piste nulle part — recherche ailleurs');
          // Le drapeau se pose AVANT le premier appel : sans lui, le panneau
          // ouvert dans la seconde qui suit annoncerait « aucun sous-titre »
          // alors que la recherche vient tout juste de partir.
          if (cible) { marquerRechercheST(true); chargerSousTitresDeSecours(cible, ref); }
        });
    })
    .catch((err) => tracer('soustitres', 'liste des pistes illisible', err.message));
}

/**
 * Intitulé d'un épisode tel que la page le présente : son arc et son rang dans
 * cet arc. La numérotation par saison n'apparaît nulle part ailleurs, et
 * « S17E41 » ne dit rien de la place réelle de l'épisode dans l'histoire.
 */
function intituleEpisode(saison, episode) {
  for (const arc of serie?.arcs ?? []) {
    const i = (arc.episodes ?? []).findIndex(
      (v) => v.season === saison && v.episode === episode,
    );
    if (i >= 0) {
      const v = arc.episodes[i];
      const partie = arc.partie ? ` — Part ${arc.partie}` : '';
      // Le numéro absolu prime : c'est celui dont on parle. Le rang dans l'arc
      // ne sert que si le catalogue ne l'a pas fourni.
      const numero = v.absolu ?? v.episode ?? (i + 1);
      return `${arc.nom}${partie} — Épisode ${numero}`;
    }
  }
  // Épisode hors arc connu : on garde la référence brute plutôt que rien.
  return `${serie?.name ?? ''} — S${saison}E${episode}`;
}

/**
 * Le même épisode, mais tel que la BARRE DU LECTEUR doit le nommer.
 *
 * Toji : « tu vas m'enlever la guerre sanglante de mille ans et Part 4 ; tu me
 * mets plutôt le nom de l'épisode, simplement le nom de l'épisode et l'épisode
 * en question. »
 *
 * La barre affichait `intituleEpisode()` — « La guerre sanglante de mille ans
 * — Part 4 — Épisode 412 ». Trois informations, dont deux qu'on a déjà : on
 * sait quelle série on regarde, on est dans la séance depuis le début, et le
 * nom de l'arc mange la ligne sans rien apprendre. Ce qu'on ne peut PAS
 * deviner, c'est le nom de l'épisode — et il n'y était pas.
 *
 * ⚠️ POURQUOI UNE SECONDE FONCTION PLUTÔT QUE CHANGER LA PREMIÈRE.
 *
 * `intituleEpisode` sert AILLEURS, et l'arc y a du sens : l'annonce dans le
 * salon Discord, le billet d'ouverture, la présence riche, le titre d'onglet.
 * Ces textes-là sont lus par des gens qui ne sont PAS devant l'écran et qui
 * n'ont aucun autre contexte — leur retirer le nom de l'arc les rendrait
 * illisibles. Le besoin est donc local à la barre du lecteur, et le
 * changement l'est aussi.
 *
 * Le numéro d'abord parce que c'est sous celui-là qu'on en parle (412, jamais
 * S17E46), le nom ensuite. Sans nom connu, le numéro seul — jamais une chaîne
 * vide ni un repli sur le nom de l'arc.
 */
function intituleCourtEpisode(saison, episode) {
  const fiche = episodeCatalogue(saison, episode);
  const numero = fiche?.absolu ?? fiche?.episode ?? episode;
  const marque = numero > 0 ? `Épisode ${numero}` : `S${saison}E${episode}`;
  const nom = fiche?.title ?? null;
  return nom ? `${marque} — ${nom}` : marque;
}

/**
 * Adresse publique du lecteur, telle que le serveur l'annonce.
 *
 * Dans l'iframe, `location.host` vaut <app-id>.discordsays.com : la page ne
 * peut pas deviner seule où elle est réellement servie.
 */
const ORIGINE_LECTEUR = document.body.dataset.lecteur || location.origin;

/** La fiche d'un épisode, telle que le catalogue en mémoire la connaît. */
function episodeCatalogue(saison, episode) {
  return (serie?.arcs ?? [])
    .flatMap((a) => a.episodes ?? [])
    .find((v) => v.season === saison && v.episode === episode) ?? null;
}

/** La vignette d un episode, quand le catalogue la connait deja. */
function imageEpisode(cible) {
  if (!cible?.saison) return null;
  return episodeCatalogue(cible.saison, cible.episode)?.thumbnail ?? null;
}

/**
 * La même vignette, mais dans la plus grande taille que le fournisseur propose.
 *
 * Les catalogues servent des images calibrées pour des LISTES — 780 px de
 * large chez Metahub, un suffixe `_t` chez TheTVDB. Étalée en fond d'un écran
 * de chargement plein cadre, une vignette de liste est molle : on voit
 * l'agrandissement avant de voir l'épisode. Le flou la sauve en partie, il ne
 * la remplace pas.
 *
 * Les deux réécritures sont exactement celles que le serveur applique déjà aux
 * affiches (`hauteResolution` dans server.js) — même fournisseurs, mêmes
 * motifs. Ici on ne peut pas l'appeler : l'adresse a déjà été enveloppée dans
 * notre proxy, donc on réécrit la partie amont, à l'intérieur.
 *
 * Une adresse qu'on ne reconnaît pas repart telle quelle : mieux vaut une
 * vignette molle qu'une adresse inventée qui ne répondra pas.
 */
function imageEpisodeEnGrand(cible) {
  const src = imageEpisode(cible);
  if (typeof src !== 'string' || !src) return src;
  return src
    // Metahub : la largeur est dans le chemin.
    .replace(/\/w\d{3,4}(\.jpg|\.png|\.webp)/i, '/w1920$1')
    // TheTVDB : le suffixe `_t` désigne la vignette, son absence l'original.
    .replace(/(artworks\.thetvdb\.com[^"']*?)_t(\.[a-z]{3,4})/i, '$1$2');
}

// ---------------------------------------------------------------------------
// Présence riche Discord
//
// Ce que le vocal apprend d'une personne sans avoir à le lui demander : le nom
// de l'app, l'épisode, depuis combien de temps il tourne, sa vignette.
//
// Le timer commande tout le reste. Discord ne l'affiche PAS à partir d'une
// position qu'on lui pousserait seconde par seconde : il l'affiche à partir de
// l'heure à laquelle la lecture a commencé, et le fait courir lui-même. Il n'y
// a donc rien à rafraîchir tant que cette heure ne bouge pas — et elle ne
// bouge qu'au changement d'épisode, à la pause, à la reprise et au saut. Cette
// section ne pose aucun minuteur : elle est appelée à ces moments-là, et
// refuse tout ce qui redirait la même chose.
// ---------------------------------------------------------------------------

// Discord met déjà le nom de l'app en tête de la carte, tout seul. Ici il ne
// sert qu'au survol du petit rond.
const NOM_APP = document.body.dataset.nomApp || MARQUE;

/**
 * Le petit rond de la présence.
 *
 * C'est la seule image que la page ne sait pas déduire : le hash de l'icône de
 * l'application n'existe nulle part côté client, et le lecteur ne sert aucun
 * logo. Discord accepte les deux formes dans ce champ — la CLÉ d'une image
 * déposée dans le portail développeur (Rich Presence → Art Assets), ou une URL
 * publique. Une clé inconnue ne fait pas échouer la pose : la grande image
 * reste, le rond ne s'affiche pas.
 */
const ICONE_APP = document.body.dataset.iconeApp || 'seance';

// Discord coupe `details`, `state` et les textes de survol à 128 caractères.
const MAX_TEXTE = 128;

// Écart, entre l'heure de départ annoncée et celle qu'on recalcule, au-delà
// duquel on repose la présence. Le seuil est choisi AU-DESSUS de ce que la
// synchro répare seule — elle ignore 0,25 s, corrige à ±7 % jusqu'à 1,2 s, et
// saute au-delà : sous 5 s, on ne verrait que son travail, et la reposer à ce
// rythme ferait clignoter la présence de tout le vocal pour rien.
const TOLERANCE_TIMER = 5000;

// « auto » ne dit pas ce qui a été servi : c'est le serveur qui tranche selon
// les sources trouvées. Une ligne lue par tout le vocal ne devine pas — sans
// choix explicite, elle se passe de langue plutôt que d'en annoncer une fausse.
const LANGUES_PRESENCE = { vf: 'VF', vostfr: 'VOSTFR' };

const borner = (t) => (typeof t === 'string' && t.length > MAX_TEXTE
  ? `${t.slice(0, MAX_TEXTE - 1)}…`
  : t);

/**
 * L'adresse d'une image telle que les serveurs de Discord peuvent aller la
 * chercher : absolue, publique, et de préférence pas chez nous.
 *
 * Le catalogue sert ses vignettes derrière `/api/img/<adresse>` — un chemin
 * relatif, donc inutilisable ici, mais qui PORTE l'adresse d'origine. On la
 * ressort telle quelle : l'image vient déjà d'un CDN, et faire passer les
 * récupérations de Discord par le VPS n'ajouterait qu'un intermédiaire de plus
 * sur le chemin d'une image qu'il va chercher pour chaque personne du vocal.
 */
export function adressePourDiscord(image, origine) {
  if (typeof image !== 'string' || !image) return null;
  if (/^https:\/\//i.test(image)) return image;
  // Tout autre schéma écrit en toutes lettres — `http:` le premier — n'est pas
  // suivi par la proxy d'images de Discord : autant ne rien poser.
  if (/^[a-z][a-z0-9+.-]*:/i.test(image)) return null;
  if (!image.startsWith('/')) return null;

  const parNous = /^\/api\/img\/([^/?#]+)$/.exec(image);
  if (parNous) {
    try {
      const amont = decodeURIComponent(parNous[1]);
      if (/^https:\/\//i.test(amont)) return amont;
    } catch { /* chemin mal encodé : on retombe sur notre propre adresse */ }
  }

  if (!origine) return null;
  try {
    const u = new URL(image, origine);
    return u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

/**
 * La durée de l'épisode en cours, une fois connue et POUR DE BON.
 *
 * `etat.duree` va et vient : le serveur la remet à zéro à chaque changement
 * d'épisode, et elle ne revient qu'au prochain état poussé par quelqu'un qui
 * lit vraiment — une seconde plus tard, parfois dix. Entre les deux, la
 * présence repartait sans `timestamps.end`, et Discord repassait d'une barre
 * « 00:14 — 24:33 » à un simple compteur qui monte. D'une présence à l'autre,
 * l'affichage changeait de forme sous les yeux.
 *
 * On retient donc la dernière durée connue POUR CET ÉPISODE-LÀ. Changer
 * d'épisode l'oublie — c'est tout l'intérêt de la ranger par clé plutôt que de
 * garder un nombre qui traînerait d'un épisode au suivant.
 */
const dureesVues = new Map();

function dureeRetenue(cible, e) {
  const cle = cleOuverture(cible ?? {});
  const fraiche = Number(e?.duree) || 0;
  if (fraiche > 0) {
    dureesVues.set(cle, fraiche);
    // Une seule entrée suffit : on ne regarde qu'un épisode à la fois, et la
    // garder pleine ferait une fuite lente sur une longue soirée.
    for (const k of dureesVues.keys()) if (k !== cle) dureesVues.delete(k);
    return fraiche;
  }
  return dureesVues.get(cle) ?? 0;
}

/**
 * La présence à poser pour un épisode donné, ou `null` quand il n'y a rien à
 * dire. Fonction pure : c'est elle que le banc d'essai éprouve, sans SDK ni
 * DOM (essais/presence.mjs).
 *
 * `type` n'est volontairement pas posé : Discord met déjà celui d'une
 * activité, et un champ absent ne peut pas être refusé.
 */
export function presenceDe({
  oeuvre = null, serie = null, film = null, type = null,
  saison = null, episode = null, titre = null, survol = null,
  langue = null, image = null, icone = null, application = null,
  arc = null, rangArc = null, absolu = null, duree = 0,
  seance = null, combien = 0, maxSeance = 0,
  resume = null, invitation = null,
  lienVocal = null, lienServeur = null,
  lecture = false, position = 0, maintenant = Date.now(),
} = {}) {
  // ⚠️ L'ŒUVRE, ET NON PLUS « LA » SÉRIE.
  //
  // Le paramètre s'appelait `serie` du temps où le lecteur n'en connaissait
  // qu'une. Il reste accepté — les bancs d'essai et les appels existants s'en
  // servent, et un renommage silencieux aurait fait disparaître la présence
  // sans un mot le jour où l'un d'eux serait passé à côté. `oeuvre` prime quand
  // les deux sont donnés : c'est le nom du contrat actuel.
  const nom = oeuvre || serie;

  // UN FILM N'A NI SAISON NI ÉPISODE, et c'est la deuxième hypothèse qui tombe
  // ici. La garde exigeait les deux : un film ne produisait donc AUCUNE
  // présence — le vocal voyait quelqu'un ouvrir l'activité et n'apprenait
  // jamais ce qu'il regardait. Un film s'identifie par son identifiant, une
  // série par sa saison et son épisode ; l'un ou l'autre suffit, aucun des deux
  // ne suffit pas.
  const estFilm = !!film || type === 'film';
  if (!estFilm && (!(saison > 0) || !(episode > 0))) return null;

  // ⚠️ SANS NOM D'ŒUVRE, AUCUNE PRÉSENCE — et surtout pas un repli en dur.
  //
  // La ligne du nom retombait sur « Bleach » quand `serie` était nul, ce qui
  // arrive deux fois : au tout début, avant que le catalogue ait répondu, et
  // dès que la séance porte une AUTRE série que celle chargée ici (le nom
  // n'est alors volontairement pas transmis — voir `presenceVoulue`). Sur un
  // film ou une autre série, tout le vocal lisait donc « Regarde Bleach ».
  // Une présence est vue par des gens qui ne sont pas devant l'écran : c'est
  // le seul endroit où ils apprennent ce qui se passe, et une mauvaise
  // réponse y coûte plus cher que pas de réponse. Le silence de quelques
  // secondes se rattrape au battement suivant, dès que le nom est connu.
  if (!nom) return null;

  // TROIS LIGNES, ET UN TYPE QUI DIT LA VÉRITÉ.
  //
  // Discord affichait « Joue à The Almighty » — le nom de l'application et le
  // type PLAYING, tous deux par défaut. On ne joue pas, on regarde : `type: 3`
  // (WATCHING) donne « Regarde », et c'est ce type-là que Discord accompagne du
  // compteur de lecture.
  //
  // `name` remplace le nom de l'application par celui de la série. « The
  // Almighty » est le nom de l'app dans le portail Discord — sur une soirée
  // Bleach, il ne dit rien à personne.
  // LE NUMÉRO VOYAGE AVEC LE NOM DE LA SÉRIE.
  //
  // Il n'a pas de champ à lui — une présence Discord n'a que le nom, `details`
  // et `state`, et aucune zone « à droite du nom ». Le mettre au survol du
  // petit rond revenait à ne pas l'afficher : personne ne survole une présence.
  // Sur la première ligne, à côté de la série, il est là où on le cherche :
  // « Regarde Bleach · E412 ».
  //
  // ⚠️ UN FILM N'A PAS DE NUMÉRO, et il ne faut surtout pas lui en fabriquer un :
  // `S${saison}E${episode}` sur un film s'écrivait « SnullEnull », posé en toutes
  // lettres sur la première ligne de la présence de tout un vocal.
  const marque = estFilm
    ? ''
    : (absolu > 0 ? `E${absolu}` : `S${saison}E${episode}`);

  // ⚠️ LA PAUSE SE DIT SUR LA PREMIÈRE LIGNE, PAS À LA PLACE D'AUTRE CHOSE.
  //
  // Discord n'a pas d'état « en pause » : une présence joue ou n'existe pas. On
  // le signalait donc en retirant la barre, puis en écrivant le temps à la place
  // de la description — deux façons de faire disparaître quelque chose pour dire
  // qu'il ne se passe rien.
  //
  // La ligne du nom porte déjà le contexte (« Bleach · E412 ») et personne ne la
  // lit deux fois : un mot de plus s'y remarque sans rien coûter. La description
  // garde sa place, la barre reste, et l'on sait quand même que c'est arrêté.
  const marqueur = lecture ? '' : ' · en pause';

  const activite = {
    name: borner([nom, marque].filter(Boolean).join(' · ') + marqueur),
    type: 3,
    // La deuxième ligne porte le titre de l'épisode. Un film n'en a pas : elle
    // porte alors son propre titre — la première ligne le donne déjà court,
    // celle-ci le donne entier, et l'on n'affiche jamais « Snull Enull ».
    details: borner(titre || (estFilm ? nom : `S${saison}E${episode}`)),
  };

  // L'arc, en un mot. « Arc 9 » se lit d'un coup d'œil ; le nom complet de
  // l'arc tiendrait la ligne entière sans rien apprendre à qui suit la série,
  // et n'apprendrait rien non plus à qui ne la suit pas.
  // ⚠️ LA DEUXIÈME LIGNE DIT CE QUI SE PASSE, PAS OÙ L'ON EST DANS LA SÉRIE.
  //
  // Toji : « Arc 17, je ne suis pas fan. À la limite, une petite description de
  // l'épisode, simplement. »
  //
  // « Arc 17 » n'apprend rien : le numéro d'épisode est déjà sur la première
  // ligne, et le rang de l'arc ne dit ni ce qu'on regarde ni où l'on en est. Le
  // résumé, lui, donne à quelqu'un qui voit passer la présence une raison de
  // demander à rejoindre.
  //
  // ⚠️ ET EN PAUSE, C'EST LE TEMPS QUI PREND LA PLACE.
  //
  // Toji : « même si on est en pause, ça note qu'on est en pause, mais ça reste
  // la donnée qu'on a mise. Je suis à 2 secondes sur 24 minutes 33, je veux
  // vraiment voir les 2 secondes et les 24 minutes 33. Ça ne bouge pas. »
  //
  // Discord ne sait pas figer sa barre : `timestamps` fait TOUJOURS courir un
  // compteur, il n'existe aucun moyen de l'arrêter sur une valeur. En pause on
  // retire donc les horodatages — sinon le compteur continuerait de monter
  // devant une image arrêtée, ce qui est pire que pas de temps du tout — et l'on
  // ÉCRIT le temps, qui lui ne bouge plus : « ⏸ 0:02 / 24:33 ».
  // La description reste À SA PLACE, en lecture comme en pause. Elle avait cédé
  // la ligne au temps quand on met en pause — Toji : « quand tu mets pause, ça
  // enlève la description. Je veux voir que la barre en dessous et la description
  // restent toujours au même endroit. » Une présence qui se réorganise à chaque
  // pause se lit deux fois : une ligne qui bouge coûte plus qu'elle n'apporte.
  const ligneDeux = resume || (rangArc > 0 ? `Arc ${rangArc}` : arc) || null;
  if (ligneDeux) activite.state = borner(ligneDeux);

  // ⚠️ LES DEUX INVITATIONS, SUR LE PROFIL.
  //
  // Toji : « les deux boutons, ils sont bien pour pouvoir le faire à la main,
  // mais je veux que ça soit dispo au niveau de la riche présence. Via l'acti. »
  //
  // ⚠️ IL N'Y AURA JAMAIS DE BOUTON. LA QUESTION EST TRANCHÉE, VOICI PAR QUOI.
  //
  // La Rich Presence classique a bien un champ `buttons` — deux entrées
  // `{ label, url }`, celles qu'on voit sous les présences de jeux. Il n'est
  // atteignable par AUCUN chemin ouvert à une activité embarquée :
  //
  //  • Le SDK ne l'a pas. `Activity` (node_modules/@discord/embedded-app-sdk/
  //    output/schema/common.mjs) énumère name, type, url, created_at,
  //    timestamps, application_id, details, details_url, state, state_url,
  //    emoji, party, assets, secrets, instance, flags — et rien d'autre. Puis
  //    `SET_ACTIVITY` n'en retient qu'une part encore plus courte (setActivity.
  //    mjs : state, state_url, details, details_url, timestamps, assets, party,
  //    secrets, instance, type). `buttons` n'est dans ni l'une ni l'autre.
  //  • Le SDK ne VALIDE PAS ce qu'on lui donne : `commandFactory` ne relit que
  //    la RÉPONSE, jamais les arguments. Un `buttons` posé ici partirait donc
  //    tel quel jusqu'au client Discord — qui l'ignore, faute de champ. Rien ne
  //    le signalerait : pas d'erreur, pas de trace, juste un profil sans bouton.
  //    (C'est aussi ce qui explique que le refus du secret soit venu de Discord
  //    et non d'une validation locale — voir plus bas.)
  //  • Là où `buttons` existe, c'est en LECTURE : le SDK le décrit dans la
  //    réponse de `GET_RELATIONSHIPS` (output/generated/schemas.mjs) comme un
  //    simple tableau de chaînes — les intitulés, sans les adresses. C'est la
  //    forme Gateway, celle qu'on REÇOIT des présences des autres.
  //  • Et là où il s'ÉCRIT, c'est ailleurs : le Discord Social SDK (jeux natifs,
  //    `Activity::AddButton`, mai 2025) et la présence Gateway d'un bot. Deux
  //    surfaces qui ne sont pas la nôtre, et rien n'a jamais été porté au SDK
  //    embarqué — aucun changelog ne le mentionne.
  //
  // Sources : docs.discord.com/developers/rich-presence/using-with-the-embedded-app-sdk
  // et docs.discord.com/developers/events/gateway-events#activity-object
  // (le `buttons` reçu par la Gateway est « an array of strings »).
  //
  // CE QUI EXISTE À LA PLACE, et qui donne le résultat demandé : Discord rend
  // CLIQUABLE ce que la présence porte déjà, dès qu'on donne une adresse à
  // chaque morceau. Quatre champs, arrivés le 17 juillet 2025 côté Gateway et
  // portés au SDK embarqué le 11 août 2025 (versions 2.1/2.2, changelog
  // « Embedded App SDK Version 2.1 & 2.2 ») : `details_url` sur la ligne du
  // titre, `state_url` sur celle du dessous, `assets.large_url` sur la grande
  // image et `assets.small_url` sur le petit rond. Ce n'est pas un bouton —
  // c'est du texte et des images qui mènent quelque part — mais c'est la seule
  // chose atteignable, et elle fait le travail : quatre portes sur le profil.
  //
  // Le partage se fait donc dans le bon sens : la ligne de l'ÉPISODE mène au
  // vocal, là où l'épisode se regarde en ce moment ; celle de l'ARC mène au
  // serveur, qui dure plus longtemps qu'une séance. Les deux passent par les
  // invitations DU BOT, les seules qui attribuent « Shinigami Suppléant » à
  // l'arrivée — un lien fabriqué à la main ferait entrer sans rôle, donc sans
  // accès aux salons.
  //
  // Une adresse absente ne pose rien : la ligne reste du texte, comme avant.
  // C'est le cas tant que le bot n'a pas répondu, et le cas définitif si le bot
  // est muet — mieux vaut une ligne inerte qu'un lien mort.
  //
  // ⚠️ Aucun de ces liens n'ouvre le LECTEUR, et c'est délibéré : une présence
  // se voit de partout, y compris hors du serveur. Ils mènent à la porte —
  // le vocal, le serveur — et c'est la présence dans le salon qui décide de la
  // suite, comme pour tout le reste.
  const https = (u) => (typeof u === 'string' && /^https:\/\//.test(u) ? u : null);
  const versVocal = https(lienVocal);
  const versServeur = https(lienServeur);
  if (versVocal) activite.details_url = versVocal;
  if (versServeur && activite.state) activite.state_url = versServeur;



  // ⚠️ LA BARRE RESTE, EN PAUSE COMME EN LECTURE — ELLE SE RECALE.
  //
  // Toji : « fais de façon à ce que la barre reste affichée constamment dès
  // qu'on est sur un épisode. Ça met un timer au niveau de l'épisode. »
  //
  // On la retirait en pause, pour une raison qui se tenait : Discord ne sait pas
  // arrêter son compteur, il l'aurait fait courir devant une image figée. Mais
  // la retirer coûtait plus cher que la dérive qu'elle évitait — la présence
  // perdait sa barre ET sa position, et se réorganisait sous les yeux.
  //
  // La sortie est ailleurs : on REPOSE les horodatages à chaque battement, avec
  // la position réelle. Discord fait courir son compteur entre deux poses ; on
  // le remet à l'heure avant qu'il n'ait dérivé de plus de quelques secondes.
  // À l'écran, la barre paraît immobile et affiche la bonne valeur.
  //
  // Le seul cas où l'on n'affiche rien reste celui où l'on ne sait pas : sans
  // durée, une barre serait un dessin sans échelle.
  {
    const depuis = Math.max(0, Number(position) || 0);
    const debut = Math.round(maintenant - depuis * 1000);
    activite.timestamps = { start: debut };

    // AVEC LA FIN, Discord affiche « 00:20 / 24:33 » au lieu d'un compteur qui
    // monte indéfiniment. C'est ce qu'on veut voir d'une vidéo : où l'on en
    // est ET combien il reste. Sans `end`, il n'a aucun moyen de connaître la
    // durée — elle ne se déduit pas d'un instant de départ.
    //
    // Une durée absente ou absurde ne pose rien : un `end` antérieur au
    // `start` ferait afficher un temps négatif, ce qui est pire que pas de
    // durée du tout.
    const totale = Number(duree) || 0;
    if (totale > 0 && totale > depuis) {
      activite.timestamps.end = Math.round(debut + totale * 1000);
    }
  }

  // ⚠️ LE BOUTON « REJOINDRE » NE SE POSE PAS : IL EST ACCORDÉ, ET PAS À NOUS.
  //
  // On croyait le tenir — le schéma du SDK accepte `party` et `secrets.join`,
  // l'événement `ACTIVITY_JOIN` existe, et depuis que le secret ne vaut plus
  // l'identifiant de party la présence est acceptée sans un mot de reproche.
  // Elle l'est, et il n'y a toujours aucun bouton. La raison est écrite noir
  // sur blanc dans la page que Discord consacre à la présence riche DES
  // ACTIVITÉS (docs.discord.com/developers/rich-presence/
  // using-with-the-embedded-app-sdk) :
  //
  //   « If the user viewing the profile has the ability to join, an
  //     "Ask to Join" button will be displayed as well. »
  //
  // Ce n'est pas nous qui posons ce bouton — c'est Discord, et il le décide en
  // regardant CELUI QUI CONSULTE LE PROFIL, pas celui qui regarde l'épisode.
  // Il n'apparaît qu'à quelqu'un qui peut réellement entrer : membre de la
  // 14ème Division, avec l'accès au salon vocal où tourne l'activité. Pour
  // tous les autres — c'est-à-dire exactement le public qu'on cherche à faire
  // venir — il n'y a rien à afficher, et Discord n'affiche rien. Aucun réglage
  // du portail développeur n'ouvre cette porte : l'approbation qu'il fallait
  // autrefois pour « Ask to Join » a été supprimée en 2019, il n'en reste
  // aucune case à cocher.
  //
  // La même page donne la liste des champs qui « ont un effet » pour une
  // activité — type, state, details, timestamps, assets, party — avec la note
  // « Some were left out since they don't have an effect for Activities ».
  // `secrets` et `instance` n'y sont pas.
  //
  // ⚠️ ON LES GARDE QUAND MÊME, et ce n'est pas de l'entêtement. Cette page est
  // DÉMONTRABLEMENT EN RETARD : elle ne mentionne pas non plus `details_url` ni
  // `state_url`, qui existent, sont dans le SDK installé et fonctionnent. Elle
  // dit donc ce qui marche, pas tout ce qui marche. Et en face, le SDK retient
  // explicitement `secrets` et `instance` dans `SET_ACTIVITY`, le client
  // Discord les VALIDE (c'est lui, et non une vérification locale, qui a refusé
  // le secret égal au party id), et `ACTIVITY_JOIN` figure toujours dans
  // l'énumération des événements. Les retirer coûterait la seule chance qui
  // reste et n'apporterait rien : posés correctement, ils ne gênent pas.
  //
  // ⚠️ LE SECRET N'OUVRE RIEN À LUI SEUL, et c'est délibéré : il voyage chez
  // tous ceux qui voient la présence, y compris hors du serveur. Il désigne une
  // séance, il ne donne aucun droit — c'est la présence dans le salon vocal qui
  // garde la porte, exactement comme pour le lien du lecteur.
  //
  // `size` sert à Discord pour afficher « 2 sur 40 » et pour griser le bouton
  // quand c'est plein. Une taille absente ferait disparaître le compte sans
  // qu'on sache pourquoi.
  //
  // ⚠️ ET LE PREMIER NOMBRE NE PEUT PAS ÊTRE ZÉRO.
  //
  // Il valait `Math.max(0, combien)`, où `combien` compte les participants que
  // le serveur dit « devant l'épisode ». Ce compte est à zéro dans deux cas
  // parfaitement ordinaires : au tout début, avant le premier état de séance,
  // et chaque fois que la liste arrive sans le drapeau. La présence annonçait
  // alors « 0 sur 40 » — une salle vide, alors que quelqu'un est manifestement
  // en train de regarder, puisque c'est SA présence qu'on lit. Un groupe sans
  // personne dedans n'est pas un groupe : Discord n'a plus rien à rejoindre, et
  // l'exemple de sa propre documentation ne montre jamais autre chose que deux
  // nombres pleins (`party: { size: [2, 4] }`).
  //
  // Un au minimum, donc : celui dont on lit la présence. C'est le seul chiffre
  // qu'on puisse affirmer sans rien demander à personne.
  if (seance) {
    activite.party = { id: String(seance) };
    if (maxSeance > 0) activite.party.size = [Math.min(Math.max(1, combien), maxSeance), maxSeance];
    // ⚠️ LE SECRET NE PEUT PAS ÊTRE L'IDENTIFIANT DE PARTY.
    //
    // Mesuré en séance, dans les traces : « presence refusee (1) — secrets
    // cannot match the party id ». Discord rejette la présence ENTIÈRE quand
    // `secrets.join` vaut exactement `party.id`, et le client la reposait alors
    // sans rien — donc pas de bouton « Rejoindre », et pas davantage les deux
    // lignes cliquables. Tout ce qui vit sur le profil tombait avec.
    //
    // La règle a du sens : le party id est PUBLIC (il sert à grouper les
    // présences), le secret est censé être l'information qu'on ne devine pas.
    // Les faire égaux revient à publier le secret, et Discord refuse plutôt que
    // de laisser croire à une protection qui n'existe pas.
    //
    // Un préfixe suffit : même séance désignée, valeur différente. Le secret
    // n'ouvre toujours rien à lui seul — c'est la présence dans le salon vocal
    // qui garde la porte — donc rien ne se perd à le rendre lisible.
    activite.secrets = { join: `rejoindre:${seance}` };
    // « Cette séance-ci », par opposition à « cette application » : sans ce
    // drapeau, Discord traite toutes les séances comme une seule.
    activite.instance = true;
  }

  // Discord n'affiche le petit rond que par-dessus une grande image : sans
  // vignette, poser l'icône seule ne donnerait rien du tout.
  if (image) {
    activite.assets = { large_image: image };
    // ⚠️ LE SURVOL INVITE, IL NE RÉPÈTE PAS.
    //
    // Toji : « quand on passe sur la PP, ce que je veux que tu mettes, c'est un
    // texte qu'on peut éditer, mais ce ne serait pas forcément des infos sur
    // l'épisode. Ce serait plutôt "rejoignez le serveur Discord de la 14ème
    // Division". »
    //
    // Il portait l'intitulé de l'épisode — c'est-à-dire ce que les deux lignes
    // du dessus disaient déjà. Or ce texte est le SEUL endroit de la présence
    // qui s'adresse à celui qui REGARDE la présence, pas à celui qui joue :
    // autant qu'il lui dise comment nous rejoindre.
    activite.assets.large_text = borner(invitation || survol || '');
    if (!activite.assets.large_text) delete activite.assets.large_text;

    // ⚠️ ET L'IMAGE MÈNE LÀ OÙ SON SURVOL PROMET D'ALLER.
    //
    // `assets.large_url` est arrivé avec les deux autres adresses (SDK 2.1/2.2,
    // 11 août 2025) et n'était pas utilisé. C'est pourtant la plus grande
    // surface cliquable de toute la présence, et la seule qu'on remarque sans
    // lire : une vignette d'épisode occupe plus de place que les deux lignes
    // réunies. Quatre portes valent mieux que deux quand aucune ne peut être un
    // bouton.
    //
    // Elle mène au SERVEUR, et pas ailleurs, parce que son survol dit déjà
    // « Rejoignez le serveur Discord de la 14ème Division ». Un texte qui
    // annonce une destination et un clic qui en ouvre une autre, c'est le genre
    // de petit mensonge qu'on ne remarque qu'après avoir cliqué.
    if (versServeur) activite.assets.large_url = versServeur;

    if (icone) {
      activite.assets.small_image = icone;
      // Le numéro d'épisode plutôt que le nom de l'application : celui-ci est
      // déjà en gros au-dessus, celui-là n'a nulle part ailleurs où aller.
      activite.assets.small_text = borner(marque || application || '');
      // Même règle pour le petit rond : il porte le numéro de l'épisode, donc
      // il mène au VOCAL, là où cet épisode passe en ce moment. Le survol dit
      // « E412 », le clic emmène à l'endroit où E412 se regarde.
      if (versVocal) activite.assets.small_url = versVocal;
    }
  }

  return activite;
}

/** Ce qui distingue deux présences à l'œil : tout, sauf l'heure de départ. */
function empreintePresence(a) {
  return JSON.stringify([
    // `name` en tête : il porte la série ET le numéro d'épisode depuis qu'ils
    // partagent la première ligne. L'omettre voulait dire qu'un changement
    // d'épisode passait inaperçu tant que le titre restait le même.
    a.name ?? null, a.type ?? null,
    a.details ?? null, a.state ?? null,
    a.assets?.large_image ?? null, a.assets?.large_text ?? null,
    a.assets?.small_image ?? null, a.assets?.small_text ?? null,
    // ⚠️ LES QUATRE ADRESSES, SANS QUOI ELLES N'ARRIVENT JAMAIS CHEZ DISCORD.
    //
    // Elles manquaient ici, et c'est ce qui les rendait invisibles quoi qu'on
    // écrive dans `presenceDe`. La séquence, immanquable : la présence est
    // posée dès le premier épisode, AVANT que le bot ait répondu, donc sans
    // aucune adresse. Les invitations arrivent une seconde plus tard ; la
    // présence voulue en porte alors quatre. Mais l'empreinte, elle, n'avait
    // pas bougé d'un caractère — même série, même titre, même vignette — donc
    // `presenceAChanger` répondait « non », et la présence sans liens restait
    // en place pour toute la durée de l'épisode. Rien ne le signalait : la
    // présence était juste, complète, et muette.
    //
    // Le retour est vrai aussi : l'invitation VOCALE expire. `lienInvitation`
    // la met à `null` passé l'échéance, les deux adresses du vocal disparaissent
    // de la présence voulue — et sans cette ligne, celle qui est POSÉE gardait
    // un lien mort, que seul celui qui a cliqué découvrait.
    //
    // On compare les adresses elles-mêmes, pas leur simple présence : le bot
    // renouvelle l'invitation vocale, et la nouvelle mène à un autre lien que
    // l'ancienne.
    a.details_url ?? null, a.state_url ?? null,
    a.assets?.large_url ?? null, a.assets?.small_url ?? null,
    // Le groupe, pour la même raison : « 2 sur 40 » restait figé sur le compte
    // du premier battement. Quelqu'un qui rejoint ou qui s'en va est un vrai
    // changement, et c'est le seul chiffre de la présence qui parle des AUTRES.
    // C'est rare — on ne rejoint pas une séance dix fois par minute — donc rien
    // à craindre du côté de la cadence que ce filtre est là pour tenir.
    a.party?.id ?? null, (a.party?.size ?? []).join('/'),
    a.timestamps ? 1 : 0,
    // La PRÉSENCE d'une fin, pas sa valeur. Elle arrive après coup, quand la
    // durée est enfin connue, et c'est elle qui fait passer Discord du
    // compteur qui monte à la barre « 00:14 — 24:33 » : sans elle ici, la
    // présence n'était jamais reposée pour ce seul changement.
    //
    // Sa VALEUR, en revanche, dérive avec le départ — un rattrapage de synchro
    // d'une seconde la déplace d'autant. La comparer ferait reposer la présence
    // à chaque micro-correction, ce que la tolérance sur `start` existe
    // justement pour éviter.
    a.timestamps?.end ? 1 : 0,
  ]);
}

/**
 * Faut-il reposer la présence ?
 *
 * L'état de séance arrive une fois par seconde. Sans ce filtre, chaque message
 * repartirait vers le client Discord puis la Gateway pour y redire la même
 * chose. Seul un vrai changement passe : épisode, pause, reprise, langue — ou
 * une heure de départ qui a bougé de plus que ce que la synchro corrige seule.
 */
export function presenceAChanger(avant, apres, tolerance = TOLERANCE_TIMER) {
  if (!apres) return !!avant;
  if (!avant) return true;
  if (empreintePresence(avant) !== empreintePresence(apres)) return true;
  const a = avant.timestamps?.start;
  const b = apres.timestamps?.start;
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  return Math.abs(a - b) > tolerance;
}

// Le plafond d'une seance, tel que le serveur l'applique (`MAX_PAR_INSTANCE`
// dans seance.js). Recopie ici pour que la presence puisse afficher « 2 sur
// 40 » : Discord a besoin des deux nombres, et un plafond absent fait
// disparaitre le compte sans qu'on sache pourquoi.
const MAX_PAR_SEANCE = 40;

// L'épisode sur lequel la séance est posée. Le serveur le tient déjà (`ouvrir`
// dans l'état de séance) mais le client de séance ne le relaie pas encore : on
// le prend s'il arrive, sinon on retient ce qu'on a vu passer.
let episodeDeLaSeance = null;

let presencePosee = null;
let presenceAutorisee = true;
let echecsPresence = 0;

/** Ce que la présence devrait dire, maintenant. */
function presenceVoulue() {
  const e = etatSeance;
  if (!e?.actif) return null;
  const cible = e.ouvrir ?? episodeDeLaSeance;
  if (!cible) return null;

  // ⚠️ LE CATALOGUE EN MÉMOIRE NE PARLE QUE DE L'ŒUVRE OUVERTE.
  //
  // Y chercher le titre et la vignette d'une AUTRE œuvre rendrait l'épisode
  // d'à côté — l'épisode 12 de ce qu'on a sous la main, pendant que la séance
  // en regarde un autre. Le champ `serie` de l'ordre d'ouverture porte
  // l'identifiant de l'œuvre (le nom du champ vient du protocole de séance, et
  // il ne change pas ici : le serveur le lit).
  //
  // Le catalogue, lui, connaît TOUTES les œuvres depuis ce chantier : quand la
  // séance en porte une qu'on n'a pas ouverte, on peut au moins la NOMMER. Le
  // titre exact, tiré du catalogue, plutôt que le silence d'avant.
  const memeOeuvre = !cible.serie || oeuvre?.id === cible.serie;
  const fiche = memeOeuvre ? episodeCatalogue(cible.saison, cible.episode) : null;
  const autre = memeOeuvre ? null : catalogue.find((o) => o.id === cible.serie) ?? null;
  const position = typeof e.position === 'function' ? e.position() : (e.position ?? 0);

  // ⚠️ LE NOM ANNONCÉ EST CELUI DE L'ŒUVRE EN COURS, ET DE RIEN D'AUTRE.
  //
  // Il retombait sur « Bleach », écrit en dur, dès que `serie` était nul : au
  // démarrage, et sur toute œuvre autre que celle chargée. Tout un vocal lisait
  // donc « Regarde Bleach » pendant qu'un film passait. Depuis, `presenceDe`
  // refuse de poser une présence sans nom — c'est le bon réflexe, mais il
  // laissait la présence MUETTE dans les mêmes cas.
  //
  // Trois sources, dans l'ordre de fiabilité : l'œuvre ouverte, l'œuvre que le
  // catalogue reconnaît à son identifiant, et le titre que la séance a joint à
  // son ordre d'ouverture. La quatrième réponse est le silence, et elle est
  // toujours préférable à un nom emprunté.
  //
  // Le nom est COURT : le catalogue rend le titre complet du fournisseur —
  // « Bleach: Thousand-Year Blood War - The Almighty » — dont il ne restait que
  // la queue une fois borné à 128 caractères : « TheAlmighty », qui ne désigne
  // rien pour personne.
  const titreOeuvre = nomCourt(
    (memeOeuvre ? oeuvre?.titre : autre?.titre) ?? (memeOeuvre ? serie?.name : null) ?? null,
  );

  // Le type décide de la forme : un film n'a ni saison ni épisode, et sa
  // présence ne doit donc pas être refusée pour cela.
  const type = (memeOeuvre ? oeuvre?.type : autre?.type) ?? (cible.film ? 'film' : 'serie');

  // Le rang de l'arc dans la liste de l'œuvre, de 1 à N. Rien à voir avec une
  // saison TVDB : c'est la place de l'arc parmi les autres, telle qu'on la
  // compte en parlant — « on est sur l'arc 9 ».
  const arcs = memeOeuvre ? (serie?.arcs ?? []) : [];
  const i = arcs.findIndex((a) => (a.episodes ?? []).some(
    (v) => v.season === cible.saison && v.episode === cible.episode,
  ));

  return presenceDe({
    oeuvre: titreOeuvre,
    type,
    film: cible.film ?? null,
    saison: cible.saison,
    episode: cible.episode,
    titre: fiche?.title ?? cible.titre ?? (type === 'film' ? titreOeuvre : null),
    arc: i >= 0 ? (arcs[i].nom ?? arcs[i].titre ?? null) : null,
    rangArc: i >= 0 ? i + 1 : null,
    absolu: fiche?.absolu ?? null,
    survol: memeOeuvre && cible.saison > 0
      ? intituleEpisode(cible.saison, cible.episode)
      : (cible.titre ?? null),
    // La description de l'épisode remplace le rang de l'arc sur la deuxième
    // ligne : elle donne une raison de demander à rejoindre. Un film n'a pas
    // d'épisode : c'est le résumé de l'œuvre qui prend la place, sans quoi la
    // deuxième ligne resterait vide sur tout un long métrage.
    resume: fiche?.resume ?? (type === 'film' ? ((memeOeuvre ? oeuvre : autre)?.resume ?? null) : null),
    // Le texte du survol. Une constante pour l'instant — le jour où il se règle
    // dans les paramètres de séance, il n'y aura que cette ligne à changer.
    invitation: INVITATION_PRESENCE,
    // La langue écoutée est un réglage de poste : c'est celui d'ici, pas celui
    // de la séance — deux personnes devant le même épisode n'affichent donc
    // pas la même ligne, et c'est le but.
    langue: prefs.lire('langue', 'auto'),
    // La vignette de l'épisode, et à défaut le visuel de l'œuvre : un film n'a
    // aucune vignette d'épisode, et une présence sans image perd sa plus grande
    // surface cliquable (voir `assets.large_url`).
    image: adressePourDiscord(
      fiche?.thumbnail ?? (memeOeuvre ? oeuvre : autre)?.fond ?? (memeOeuvre ? oeuvre : autre)?.affiche ?? null,
      ORIGINE_LECTEUR,
    ),
    icone: ICONE_APP,
    application: NOM_APP,
    lecture: !!e.lecture,
    position,
    // La durée réelle, pour que Discord affiche « 00:14 —— 24:33 » plutôt qu'un
    // compteur qui monte seul.
    duree: dureeRetenue(cible, e),
    // De quoi poser le bouton « Rejoindre ». L'instance de séance identifie le
    // salon ; le compte de ceux qui regardent remplit « 2 sur 40 ».
    seance: e?.instance ?? identifiantSeance() ?? null,
    combien: e?.participants?.filter((p) => p.regarde).length ?? 0,
    maxSeance: MAX_PAR_SEANCE,
    // Les mêmes liens que les deux boutons de l'écran de gestion, relayés du
    // bot. `lienInvitation` rend `null` sur une invitation vocale périmée : la
    // ligne redevient alors du texte plutôt que de mener nulle part.
    lienVocal: lienInvitation('vocal'),
    lienServeur: lienInvitation('serveur'),
  });
}

/**
 * Pose la présence si elle a changé.
 *
 * Hors de Discord — le lecteur ouvert dans un navigateur, la fenêtre déportée
 * — `sdk` est nul : on sort avant tout, et rien de ce qui suit n'est tenté.
 */
/**
 * Quelqu'un a cliqué « Rejoindre » sur une présence.
 *
 * Discord a déjà lancé l'activité chez lui et nous remet le secret — c'est-à-
 * dire l'instance de séance à rejoindre. Il n'y a donc pas de lien à ouvrir ni
 * de page à charger : on branche la séance et le reste suit.
 *
 * ⚠️ EN PRATIQUE, CET ÉVÉNEMENT N'EST JAMAIS VENU, et il faut savoir pourquoi
 * avant de chercher la panne ici. Le bouton qui le déclenche n'est pas posé par
 * nous : Discord l'affiche de lui-même, et seulement « if the user viewing the
 * profile has the ability to join » (docs.discord.com/developers/rich-presence/
 * using-with-the-embedded-app-sdk). Quelqu'un d'extérieur au serveur ne voit
 * donc aucun bouton, et rien n'arrive ici. Ce branchement reste en place parce
 * qu'il ne coûte qu'un abonnement, que `ACTIVITY_JOIN` figure toujours dans les
 * événements du SDK, et que le jour où Discord ouvrira le bouton plus largement
 * il n'y aura rien à écrire. Ce n'est pas par lui que les gens arrivent
 * aujourd'hui — ce sont les quatre adresses de la présence.
 *
 * ⚠️ Le secret ne donne AUCUN droit. Il dit quelle séance, pas qui peut y
 * entrer : c'est la présence dans le salon vocal qui garde la porte, et elle
 * est vérifiée côté serveur comme pour n'importe quelle autre entrée. Traiter
 * ce secret comme une autorisation serait ouvrir la séance à quiconque a vu
 * passer un profil.
 */
function brancherRejoindre() {
  if (!DANS_DISCORD || !sdk) return;
  try {
    sdk.subscribe('ACTIVITY_JOIN', ({ secret } = {}) => {
      // Le préfixe posé à l'émission repart ici. Il est retiré s'il est là et
      // ignoré s'il ne l'est pas : une présence posée par une version
      // précédente porte encore l'ancien format, et elle doit continuer à
      // fonctionner le temps qu'elle vive.
      const voulue = String(secret || '').replace(/^rejoindre:/, '').slice(0, 120);
      if (!voulue || voulue === identifiantSeance()) return;
      tracer('seance', 'rejoint depuis une presence', voulue);
      instanceDeSeance = voulue;
      demarrerSeance();
    });
  } catch (err) {
    // Le scope peut manquer : la présence reste posée, seul le bouton
    // n'aboutira pas. Mieux vaut le dire une fois que d'échouer en silence.
    tracer('presence', 'ACTIVITY_JOIN non ecoute', err?.message ?? String(err));
  }
}

function majPresence() {
  if (!DANS_DISCORD || !sdk || !presenceAutorisee) return;

  let voulue = null;
  try {
    voulue = presenceVoulue();
  } catch (err) {
    // Un catalogue à moitié chargé ne doit pas emporter le peintre qui appelle.
    tracer('presence', 'calcul impossible', err?.message ?? String(err));
    return;
  }
  if (!presenceAChanger(presencePosee, voulue)) return;
  presencePosee = voulue;

  // Une remise à zéro échoue sur la seule validation de la RÉPONSE : le SDK y
  // attend un objet Activity complet (output/schema/responses.mjs —
  // `SetActivityResponse = Activity`, `name` et `type` obligatoires) là où
  // Discord répond « plus rien ». Cet échec-là ne dit rien du droit de poser :
  // il ne compte pas dans les trois.
  const remiseAZero = voulue === null;
  const rate = (quoi, err) => {
    if (!remiseAZero) {
      echecsPresence += 1;
      // Le refus attendu ici, c'est le scope : il ne se répare pas tout seul.
      // Trois essais laissent passer un hoquet ; au-delà on arrête, plutôt que
      // de rejouer le même refus à chaque épisode jusqu'à la fin de la séance.
      if (echecsPresence >= 3) presenceAutorisee = false;
    }
    tracer('presence', `${quoi} (${echecsPresence})`, err?.message ?? String(err));
  };

  try {
    sdk.commands.setActivity({ activity: voulue })
      .then(() => { echecsPresence = 0; })
      .catch((err) => {
        // `type` et `name` sont acceptés par le schéma du SDK, mais Discord
        // peut les refuser sur une activité embarquée — il y impose parfois les
        // siens. Plutôt que de perdre la présence entière pour ces deux
        // champs, on repose sans eux : mieux vaut « Joue à » que rien.
        if (!voulue || (!voulue.type && !voulue.name)) return rate('refusee', err);
        const sansType = { ...voulue };
        delete sansType.type;
        delete sansType.name;
        tracer('presence', 'type refuse — repose sans', err?.message ?? String(err));
        sdk.commands.setActivity({ activity: sansType })
          .then(() => { echecsPresence = 0; })
          .catch((e2) => rate('refusee', e2));
      });
  } catch (err) {
    // setActivity peut lever avant même de rendre une promesse (canal fermé) :
    // la présence est un confort, elle ne casse jamais la page.
    rate('pose impossible', err);
  }
}

/**
 * Ouvre la lecture dehors, dans le navigateur.
 *
 * Le détachement a été essayé et ne donne rien d'utilisable ici : la fenêtre
 * complète est interdite depuis une iframe, et l'image seule laisse les
 * sous-titres et les commandes derrière. L'activité garde donc le catalogue,
 * et confie la lecture au navigateur, où le lecteur a l'écran entier.
 *
 * Renvoie `true` si la lecture est partie ailleurs.
 */
/**
 * Dit à tout le monde ce qu'on vient d'ouvrir : à la séance, et au salon.
 *
 * Les deux vont ensemble et se faisaient séparément — l'ouverture initiale
 * annonçait sans prévenir la séance (d'où un bandeau sans titre ni vignette),
 * le changement d'épisode prévenait la séance sans annoncer (d'où un salon qui
 * n'apprenait rien). Les réunir ici garantit qu'aucun des deux chemins n'en
 * oublie la moitié.
 */
/**
 * Le dernier épisode déjà annoncé à la séance.
 *
 * Le serveur REMET LA SÉANCE À ZÉRO sur chaque `ouvrir` — position, votes —
 * pour que personne ne se retrouve au milieu du nouvel épisode. Envoyer deux
 * fois le même ferait donc sauter au début quelqu'un qui vient de commencer.
 * Depuis que `lancer()` prévient la séance lui-même, les deux chemins peuvent
 * se croiser : cette garde les rend inoffensifs l'un pour l'autre.
 */
let dernierePoussee = null;

function cleOuverture({ serie: idSerie = null, saison = null, episode = null, film = null }) {
  const ref = idSerie ?? serie?.id ?? null;
  return film ? `film:${film}` : `${ref ?? ''}:${saison}:${episode}`;
}

/** Retenir une ouverture reçue comme si on l'avait publiée : elle ne repart pas. */
// ⚠️ ARRIVER DANS UNE SÉANCE EN COURS OUVRE LE LECTEUR, TOUT DE SUITE.
//
// Toji : « quand je sélectionne un épisode et que ça ouvre le lecteur, après,
// n'importe qui qui ouvre l'activité, s'il est dans le même salon que moi, ça
// lui ouvre directement la session et ça lui ouvre le lecteur. Il n'y a pas de
// délai. »
//
// C'est la bonne manière de rejoindre : la séance existe déjà, l'épisode est
// choisi, la question « que regarde-t-on ? » ne se pose plus. L'arrivant
// tombait pourtant sur le catalogue, et devait retrouver lui-même l'épisode
// que les autres regardaient déjà — avec le risque d'en ouvrir un autre.
//
// L'ordre `ouvrir` ne servait qu'à ceux qui ÉTAIENT LÀ quand il est passé. Un
// nouvel arrivant reçoit l'état, pas l'ordre : c'est donc l'état qui doit
// l'emmener. `e.ouvrir` porte exactement ce qu'il faut.
//
// Trois gardes, parce qu'une ouverture automatique qui se répète est pire que
// pas d'ouverture du tout :
//   - une seule fois par activité (`rejointAuto`), sinon chaque battement
//     rouvrirait un onglet ;
//   - jamais si un onglet est déjà ouvert — c'est nous qui avons lancé ;
//   - `marquerOuvertureConnue` AVANT, pour que `lireDehors` ne republie pas
//     l'épisode qu'il vient de recevoir : rejoindre n'est pas décider.
//
// Discord peut refuser d'ouvrir la fenêtre ; `lireDehors` le dit en rendant
// `false`, et l'on retombe alors sur le catalogue, comme avant.
let rejointAuto = false;

function rejoindreCeQuiEstDejaOuvert(e) {
  if (rejointAuto || ongletOuvert) return;
  const cible = e?.ouvrir;
  if (!cible || (!cible.film && !(cible.saison > 0 && cible.episode > 0))) return;

  // L'épisode est déjà dans le lecteur (à l'écran ou mis de côté) : le relancer
  // repartirait de zéro sous les yeux de qui regarde (audit 14e 21/10, P2).
  if (!cible.film && lecteur().episodeCharge() === `${cible.serie}:${cible.saison}:${cible.episode}`) return;

  // ⚠️ REJOINDRE N'EST PAS DEVINER. DEUX CONTEXTES, DEUX RÈGLES.
  //
  // Dès que l'épisode ouvert a commencé à traverser jusqu'ici, cette reprise
  // s'est mise à partir TOUT DE SUITE à l'ouverture de l'activité. Toji : « on
  // n'a même pas le temps de choisir un épisode qu'on est sur le dernier épisode
  // en date. » C'est juste : l'épisode reste en mémoire de séance vingt-quatre
  // heures, donc ouvrir l'activité le lendemain relançait la veille.
  //
  // Ce qu'il voulait, c'est : « n'importe qui qui ouvre l'activité, S'IL EST
  // DANS LE MÊME SALON QUE MOI, ça lui ouvre directement le lecteur ». Le mot
  // qui compte est « que moi » : il y a déjà quelqu'un devant l'écran. Une
  // séance dont plus personne ne regarde n'est pas en cours, c'est un souvenir.
  //
  //   · DANS L'ACTIVITÉ, on ouvre pour GÉRER : on ne rejoint que si quelqu'un
  //     d'AUTRE regarde déjà. Sinon on laisse le catalogue faire son travail.
  //   · SUR UNE ADRESSE DE SÉANCE (`/lecteur/<alias>`), on est venu POUR
  //     regarder : on reprend ce qui est ouvert, même seul — c'est le cas de
  //     l'onglet rechargé, et c'est justement celui qu'il fallait réparer.
  //
  // Ailleurs dans le navigateur (le catalogue, une fiche), on ne reprend rien :
  // personne n'a demandé à regarder quoi que ce soit.
  // ⚠️ ON NE SORT JAMAIS QUELQU'UN DE L'ÉCRAN QU'IL A CHOISI.
  //
  // Toji : « j'étais dans l'onglet des séances. Ça m'a redirigé vers
  // l'épisode. »
  //
  // La garde regardait l'ADRESSE, et l'adresse ne bouge pas : naviguer du
  // lecteur vers la liste des séances reste sur `/lecteur/<alias>`. Un
  // battement de séance suffisait alors à rappeler la reprise, qui rouvrait le
  // lecteur par-dessus la liste qu'on venait d'ouvrir.
  //
  // Ce qu'il faut regarder, c'est l'ÉCRAN, pas l'adresse : être sur la liste des
  // séances ou sur la console, c'est avoir décidé d'être ailleurs que devant
  // l'image. Une reprise automatique sert à celui qui ARRIVE, jamais à celui qui
  // vient de partir.
  // Deux façons d'être ailleurs, et il faut les deux.
  //
  //   · L'ÉCRAN AFFICHÉ — on vient de naviguer vers la liste ou la console.
  //   · L'ÉCRAN RETENU — on vient de RECHARGER depuis l'une des deux. Au premier
  //     instant, aucune vue n'est encore posée : tout est caché, et regarder
  //     l'écran affiché ne dit rien. C'est ce trou qui laissait la reprise
  //     ouvrir le lecteur avant que l'écran retenu n'ait pu être restauré —
  //     Toji : « j'ai reload dans la section gestion de session, et ça a refait
  //     ce qu'il faisait tout à l'heure. »
  //
  // Le retenu ne dépend d'aucun ordre d'exécution : il est écrit avant le
  // rechargement, donc il est vrai dès la première milliseconde.
  //
  // ⚠️ MAIS L'ÉCRAN DE CET ONGLET, PAS CELUI QU'UN AUTRE A RETENU (audit 14e
  // 21/10, P4). `14d.ecran` est commun à tous les onglets et n'est pas réécrit
  // pendant un chargement : lancer depuis la liste le laissait sur `v-sessions`,
  // et la reprise préparait en fond en parallèle du lancement. Le retenu ne sert
  // plus qu'avant le premier écran posé par cet onglet (`ecranCourant` nul).
  // ⚠️ DANS L'ACTIVITÉ, LES SÉANCES D'ARRIVÉE NE SONT PAS UN CHOIX (Toji, 21/10).
  // Le lancement y pose « Les séances » d'office : tant qu'on n'en a pas bougé,
  // une séance en cours dans le salon se rejoint quand même. La mémoire durable
  // ne compte pas non plus : seul l'onglet dit d'où l'on recharge.
  const surLaPorte = DANS_DISCORD && seancesDArrivee;
  const retenu = DANS_DISCORD ? (ecranCourant ?? ecranRetenuDeLOnglet()) : (ecranCourant ?? prefs.lire('ecran', ''));
  const retenuAilleurs = retenu === 'v-gestion' || (retenu === 'v-sessions' && !surLaPorte);
  const ailleursVolontairement = (!$('v-sessions')?.hidden && !surLaPorte) || !$('v-gestion')?.hidden;
  if (ailleursVolontairement || retenuAilleurs) {
    // ⚠️ ÊTRE AILLEURS N'EST PAS N'AVOIR RIEN À FAIRE.
    //
    // On ne sort personne de l'écran qu'il a choisi — mais on peut préparer, en
    // silence, le lecteur qu'il rouvrira d'un clic. C'est exactement le
    // symétrique de ce que fait le reste de cette fonction : là on OUVRE parce
    // que la personne est venue regarder, ici on se TIENT PRÊT parce qu'elle
    // est venue gérer et qu'elle reviendra.
    preparerLeRetour(cible);
    return;
  }

  if (DANS_DISCORD) {
    const monId = moi?.id ?? null;
    const dAutresRegardent = (e?.participants ?? []).some((p) => p.regarde && p.id !== monId);
    if (!dAutresRegardent) return;
  } else {
    // ⚠️ DANS LE NAVIGATEUR, C'EST LE BILLET QUI DIT « JE VIENS REGARDER ».
    //
    // La garde regardait l'ADRESSE : `/lecteur/<alias>`. Mais cette adresse
    // reste dans la barre une fois qu'on est entré, et elle y reste après avoir
    // quitté le lecteur. Recharger l'onglet — le geste le plus banal qui soit —
    // relançait donc la séance par-dessus tout le reste : Toji, deux fois,
    // « quand je reload, ça m'envoie sur séance en cours ».
    //
    // Le billet, lui, ne survit pas au rechargement : la page l'efface de
    // l'adresse dès qu'elle l'a résolu. Il désigne donc exactement le geste
    // qu'on veut suivre — on vient de cliquer le lien de la séance — et rien
    // d'autre. Sans lui, on arrive : l'accueil est la bonne réponse.
    //
    // ⚠️ ET C'EST LE BILLET QUI LANCE, PAS CETTE REPRISE (audit 14e 21/10, P5) :
    // les deux lançaient le même épisode, le second départ abandonnant le
    // premier. Le chemin du billet pose `rejointAuto` avant de démarrer la
    // séance ; dans le navigateur, cette fonction ne fait donc que préparer.
    return;
  }

  rejointAuto = true;
  episodeDeLaSeance = cible;
  marquerOuvertureConnue(cible);
  const intitule = cible.titre
    ?? (cible.saison ? intituleEpisode(cible.saison, cible.episode) : null)
    ?? 'la séance';
  tracer('seance', 'rejoint une seance deja ouverte', cible);

  lireDehors(cible, intitule).then((ouvert) => {
    if (ouvert) ongletOuvert = true;
    else tracer('seance', 'ouverture automatique refusee — retour au catalogue', cible);
  });
}

// ---------------------------------------------------------------------------
// LE LECTEUR SE TIENT PRÊT PENDANT QU'ON REGARDE LA LISTE
//
// Toji : « quand je reviens dans la page gestion des sessions, QUE JE
// RAFRAÎCHIS, et que je retourne de nouveau dans "revenir au lecteur", ça doit
// être instantané comme de base. »
//
// Ce qui se passait, et pourquoi c'était lent :
//
//   · sortir du lecteur ne jette plus la source (`mettreDeCote`), donc y
//     revenir ne coûtait déjà plus rien — TANT QU'ON NE RECHARGEAIT PAS ;
//   · un rechargement, lui, détruit la balise et son tampon. C'est le
//     navigateur, on ne peut pas l'en empêcher. Le bouton « Revenir au
//     lecteur » retombait donc sur `lancer()`, qui refait TOUT au moment du
//     clic : résoudre une source (une à trois secondes, les add-ons sont
//     interrogés et les liens vérifiés un à un), ouvrir le conteneur, remplir
//     le tampon. Trois à quatre secondes pour revenir sur un écran qu'on
//     n'avait jamais vraiment quitté.
//
// La réponse n'est pas de rendre ce chemin plus rapide : c'est de ne pas
// l'emprunter au moment du clic. Dès que la séance dit quel épisode elle porte
// et qu'on est sur son adresse, le travail part EN FOND pendant qu'on lit la
// liste. Le clic ne fait alors plus que changer d'écran — c'est la branche
// `estPret()` du bouton, celle qui servait déjà à qui n'avait pas rechargé.
//
// ⚠️ CE QUE ÇA COÛTE, ET COMMENT C'EST BORNÉ.
//
// Une préparation ouvre un flux : c'est de la bande passante, et le serveur
// compte les octets par personne (`garde.js`). Trois bornes, dans cet ordre :
//
//   1. UNE SEULE À LA FOIS, et une seule par épisode : `preparationEnFond`
//      retient ce qui est en route et refuse un second départ.
//   2. LE MÊME ÉPISODE QUE LA SÉANCE, jamais un autre : on ne prépare pas
//      « l'épisode suivant au cas où ». Un seul flux, donc, là où la garde ne
//      s'inquiète qu'à partir de quatre épisodes tirés en parallèle.
//   3. LE TAMPON DU NAVIGATEUR, qui s'arrête tout seul : une balise EN PAUSE
//      cesse de tirer dès qu'elle a de l'avance, et le démuxeur local ne
//      produit que trente secondes devant la tête (`AVANCE_S` dans mkv.js).
//      Un onglet laissé sur la liste ne tire donc pas l'épisode : il tire une
//      dizaine de mégaoctets, une fois, puis se tait.
//
// Rien de tout cela n'approche les seuils de la garde — 4 Go/h avant de
// seulement ralentir, 6 Mo/s soutenus sur cinq minutes, quatre épisodes en
// parallèle. Une préparation vaut le début d'un épisode qu'on aurait de toute
// façon regardé.
// ---------------------------------------------------------------------------

/**
 * Ce qui est en cours de préparation, ou `null`.
 *
 * Sert à deux choses et pas une de plus : ne pas repartir deux fois sur le
 * même épisode, et périmer une préparation dont l'épisode n'est plus celui de
 * la séance. Elle vit ici plutôt que dans le lecteur parce que c'est main.js
 * qui sait ce que la séance porte — le lecteur, lui, ne connaît que des
 * fichiers.
 */
let preparationEnFond = null;

/**
 * La préparation lancée pour cet épisode a-t-elle encore un sens ?
 *
 * Appelée par `lancer` juste avant de poser la source, c'est-à-dire APRÈS la
 * résolution — une à trois secondes pendant lesquelles tout a pu changer :
 *
 *   · la séance est passée à un autre épisode, et `preparerLeRetour` a déjà
 *     lancé une préparation plus récente : celle-ci pose une source périmée
 *     par-dessus la bonne ;
 *   · la personne est revenue au lecteur entre-temps — par le bouton, ou parce
 *     qu'un ordre d'ouverture est arrivé. `lancer()` travaille alors pour de
 *     vrai, et poser une seconde source par-dessus la sienne couperait l'image
 *     sous ses yeux.
 */
function preparationEncoreUtile(saison, episode) {
  if (!preparationEnFond) return false;
  if (preparationEnFond.saison !== saison || preparationEnFond.episode !== episode) return false;
  return !!$('v-lecture')?.hidden;
}

/**
 * Prépare, sans rien montrer, le lecteur que ce clic rouvrira.
 *
 * @param {object} cible l'épisode que la séance porte (`etat.ouvrir`).
 */
function preparerLeRetour(cible) {
  // ⚠️ JAMAIS DANS L'ACTIVITÉ, et c'est la garde la plus importante du lot.
  //
  // L'activité ne lit RIEN : la lecture se passe dans les navigateurs, sa
  // balise reste vide en permanence, et la séance s'appuie là-dessus pour
  // savoir qui regarde vraiment (`jeLisVraiment`). Y poser une source ferait
  // passer la fenêtre de gestion pour une fenêtre de lecture — et un
  // administrateur qui tient la séance depuis Discord écraserait la position
  // réelle des spectateurs.
  if (DANS_DISCORD) return;

  // Hors d'une adresse de séance, personne n'a demandé à regarder quoi que ce
  // soit : sur le catalogue ou une fiche, préparer serait tirer un épisode
  // dans le dos de quelqu'un qui furète.
  if (!/^\/lecteur\//.test(location.pathname)) return;

  // Une séance sans épisode ouvert n'a rien à préparer. Les films non plus,
  // délibérément : leur chemin d'ouverture est distinct (`lancerFilm`) et le
  // gain ne justifie pas d'en doubler la mécanique. Ils gardent donc le
  // comportement d'avant — le clic refait le chemin complet.
  if (!cible || cible.film) return;
  if (!cible.serie || !(cible.saison > 0) || !(cible.episode > 0)) return;

  const cle = `${cible.serie}:${cible.saison}:${cible.episode}`;

  // Déjà prêt : c'est le cas de celui qui n'a PAS rechargé — la source est
  // restée dans la balise, mise de côté. Il n'y a rien à refaire.
  if (lecteur().estPret() && lecteur().episodeCharge() === cle) return;

  // Déjà en route. Cette fonction est appelée à CHAQUE battement de séance,
  // c'est-à-dire une fois par seconde : sans ce garde-fou, on rouvrirait un
  // flux par seconde, ce qui est très exactement le profil d'un aspirateur.
  if (preparationEnFond?.cle === cle) return;

  preparationEnFond = { cle, saison: cible.saison, episode: cible.episode };
  tracer('prechargement', 'le lecteur se prépare en fond', cible);

  // `serieChargee` et non `ouvrirSerie` : la seconde bascule la vue sur la
  // fiche de la série, ce qui sortirait de la liste des séances quelqu'un qui
  // n'a rien demandé. On ne veut que la requête, pas le rendu.
  serieChargee(cible.serie)
    .then(() => {
      if (!preparationEncoreUtile(cible.saison, cible.episode)) return null;
      return lancer(cible.saison, cible.episode, { enFond: true });
    })
    .catch((err) => {
      // Un échec ne se voit pas : le clic refera le chemin complet, comme
      // avant. On libère seulement la place pour un prochain essai.
      tracer('prechargement', 'préparation impossible', err.message);
      if (preparationEnFond?.cle === cle) preparationEnFond = null;
    });
}

// ---------------------------------------------------------------------------
// LES SÉANCES EN COURS, SUR LE SITE
//
// Toji : « la bande qu'on a faite pour l'affichage Discord avec l'épisode, et à
// droite les boutons pour rejoindre le vocal / rejoindre le lecteur si on est
// déjà dedans. »
//
// Une bande par séance, bâtie comme la carte du salon : vignette, épisode, où
// il en est. Les deux entrées sont à droite, et elles ne disent pas la même
// chose selon où l'on se trouve déjà — c'est le serveur qui tranche
// (`jySuis`), le navigateur n'ayant aucun moyen de savoir qui est dans quel
// salon vocal.
// ---------------------------------------------------------------------------

const mmss = (s) => {
  const n = Math.max(0, Math.round(s || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const reste = n % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(reste).padStart(2, '0')}`
    : `${m}:${String(reste).padStart(2, '0')}`;
};

function peindreSeancesDuSite(liste, gestion = false) {
  const zone = $('ss-liste');
  const vide = $('ss-vide');
  const compte = $('ss-compte');
  if (!zone) return;

  zone.textContent = '';
  const n = liste.length;
  if (compte) compte.textContent = n ? `${n} ${pluriel(n, 'séance')}` : '';
  if (vide) vide.hidden = n > 0;

  for (const s of liste) {
    const bande = document.createElement('article');
    bande.className = 'ss-bande';

    const vignette = document.createElement('div');
    vignette.className = 'ss-vignette';
    if (s.vignette) {
      const img = document.createElement('img');
      img.src = s.vignette;
      img.alt = '';
      img.loading = 'lazy';
      vignette.append(img);
    }
    // Le point ne dit pas « en ligne » mais « ça joue » : c'est la seule chose
    // qui distingue une séance en pause d'une séance en cours.
    if (s.lecture) {
      const point = document.createElement('span');
      point.className = 'ss-joue';
      vignette.append(point);
    }

    const texte = document.createElement('div');
    texte.className = 'ss-texte';

    const sur = document.createElement('div');
    sur.className = 'ss-sur';
    const gens = s.regardent > 0
      ? `${s.regardent} devant l’écran`
      : `${s.participants} ${pluriel(s.participants, 'connecté')}`;
    sur.append(document.createTextNode(gens));
    if (s.hote) {
      const b = document.createElement('b');
      b.textContent = s.hote;
      sur.append(document.createTextNode(' · '), b);
    }

    const titre = document.createElement('h2');
    titre.className = 'ss-titre';
    const marque = s.absolu > 0 ? `E${s.absolu}` : `S${s.ouvrir?.saison}E${s.ouvrir?.episode}`;
    titre.textContent = s.titre ? `${marque} — ${s.titre}` : marque;

    texte.append(sur, titre);

    // La barre ne s'affiche que si la durée est connue : une jauge sans échelle
    // ne mesure rien et ferait croire à une progression.
    if (s.duree > 0) {
      const barre = document.createElement('div');
      barre.className = 'ss-barre';
      const jauge = document.createElement('span');
      jauge.style.width = `${Math.min(100, (s.position / s.duree) * 100).toFixed(1)}%`;
      barre.append(jauge);
      const temps = document.createElement('div');
      temps.className = 'ss-temps';
      temps.textContent = `${mmss(s.position)} / ${mmss(s.duree)}`;
      texte.append(barre, temps);
    }

    const actions = document.createElement('div');
    actions.className = 'ss-actions';

    const versLecteur = document.createElement('a');
    versLecteur.className = 'ss-bouton primaire';
    versLecteur.href = `/lecteur/${encodeURIComponent(s.alias)}`;
    versLecteur.textContent = s.jeRegarde ? 'Revenir au lecteur' : 'Rejoindre le lecteur';

    // ⚠️ REVENIR À LA SÉANCE QU'ON N'A JAMAIS QUITTÉE NE RECHARGE RIEN.
    //
    // Ce lien est une vraie navigation : suivre son adresse rechargeait la page
    // entière — le paquet, la connexion de séance, la résolution du lien, le
    // démuxage. Pour revenir à un épisode qu'on regardait dix secondes plus tôt.
    //
    // Quand c'est LA MÊME séance et que le lecteur a gardé sa source, il n'y a
    // rien à refaire : on rebascule l'écran, et c'est instantané. L'adresse
    // reste dans le `href` — elle sert au clic milieu, au « ouvrir dans un
    // nouvel onglet », et à tous les cas où la reprise à chaud n'est pas
    // possible.
    versLecteur.addEventListener('click', (ev) => {
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
      // Une AUTRE séance : c'est un vrai déplacement, le lien fait son travail.
      if (s.alias !== aliasCourant()) return;
      ev.preventDefault();

      // Le lecteur a gardé sa source : rien à refaire, on remet l'écran.
      if (lecteur().estPret()) {
        document.body.classList.add('plein');
        vue('v-lecture');
        lecteur().reveiller?.();
        return;
      }

      // ⚠️ PAS DE SOURCE : IL FAUT L'OUVRIR, PAS RECHARGER LA PAGE.
      //
      // Toji : « j'avais cliqué sur Gérer la séance, j'ai recliqué sur Les
      // séances et sur Revenir au lecteur. Ça plante. Je ne peux même plus
      // revenir au lecteur. »
      //
      // Et c'était une boucle sans sortie. Arriver par « Gérer la séance » ouvre
      // la console sans jamais toucher au lecteur : aucune source n'est posée.
      // Le clic retombait donc sur le lien, qui pointe vers l'adresse OÙ L'ON EST
      // DÉJÀ — le navigateur rechargeait, la reprise voyait « écran retenu :
      // les séances », et rendait la liste. Reclic, même chemin, même résultat.
      // Le lecteur devenait inatteignable.
      //
      // On ne recharge pas : on est déjà dans la bonne séance, elle dit quel
      // épisode est ouvert, il n'y a qu'à le lancer.
      const cible = etatSeance?.ouvrir;
      if (!cible) {
        tracer('seance', 'rien a rouvrir : la seance ne porte aucun episode', s.alias);
        return;
      }
      if (cible.film) {
        const fiche = Object.values(collections).flat().find((f) => f.id === cible.film);
        if (fiche) lancerFilm(fiche);
        return;
      }
      const suite = () => lancer(cible.saison, cible.episode);
      if (serie?.id === cible.serie) suite();
      else ouvrirSerie(cible.serie, { enFond: true }).then(suite).catch((err) => {
        tracer('seance', 'serie illisible au retour au lecteur', err.message);
      });
    });

    actions.append(versLecteur);

    // ⚠️ LES DEUX ENTRÉES D'UN ADMIN : gérer, ou regarder.
    //
    // Toji : « côté admin, le lecteur va avoir deux choix : l'interface de
    // gestion de session, et l'interface standard. » Le bouton du dessus ouvre
    // le lecteur ; celui-ci ouvre la même séance sur la console — la liste des
    // membres, les demandes, les votes, la main.
    //
    // C'est le serveur qui dit si on y a droit : un client ne décide jamais de
    // ses propres pouvoirs.
    if (gestion) {
      const versGestion = document.createElement('a');
      versGestion.className = 'ss-bouton';
      versGestion.href = `/lecteur/${encodeURIComponent(s.alias)}?gestion=1`;
      versGestion.textContent = 'Gérer la séance';
      actions.append(versGestion);
    }

    // L'invitation vocale ne se propose qu'à qui n'est pas déjà dans le salon :
    // à celui qui y est, elle ne proposerait que d'y entrer une seconde fois.
    if (s.invitationVocale && !s.jySuis) {
      const versVocal = document.createElement('a');
      versVocal.className = 'ss-bouton';
      versVocal.href = s.invitationVocale;
      versVocal.target = '_blank';
      versVocal.rel = 'noopener noreferrer';
      versVocal.textContent = 'Rejoindre le vocal';
      actions.append(versVocal);
    }

    bande.append(vignette, texte, actions);
    zone.append(bande);
  }
}

let rafraichirSeances = null;

/**
 * Ouvre la liste et la tient à jour tant qu'on la regarde.
 *
 * Le rafraîchissement s'arrête dès qu'on la quitte : cette page n'a aucune
 * raison d'interroger le serveur pendant qu'on regarde un épisode.
 */
function ouvrirLesSeances() {
  vue('v-sessions');
  // La préparation du lecteur pendant la liste, c'est `preparerLeRetour` (appelé
  // par la reprise de séance) : le doublon qui vivait ici ouvrait la fiche par
  // `ouvrirSerie` et sortait de la liste (audit 14e 21/10, P3).
  const relire = () => {
    const ecran = $('v-sessions');
    if (!ecran || ecran.hidden) {
      if (rafraichirSeances) { clearInterval(rafraichirSeances); rafraichirSeances = null; }
      return;
    }
    api('/api/seances')
      .then((r) => peindreSeancesDuSite(r?.seances ?? [], !!r?.gestion))
      .catch((err) => tracer('seances', 'liste indisponible', err.message));
  };
  relire();
  if (!rafraichirSeances) rafraichirSeances = setInterval(relire, 10_000);
}

// ---------------------------------------------------------------------------
// UN MOT QUI S'EFFACE
//
// Pour ce qui doit être lu une fois : une requête refusée, une sourdine posée.
// Il se pose au-dessus de tout, se lit d'un coup, et s'en va seul.
//
// ⚠️ Il DOIT s'effacer. Un refus laissé à l'écran se lit comme un état
// permanent — « tes demandes sont suspendues » sur une séance où elles ne le
// sont plus, c'est la même faute que le « chargement en cours » qui survivait
// à son écran.
// ---------------------------------------------------------------------------

let minuteurMot = null;

function direMot(texte, duree = 4200) {
  const zone = $('mot-requete');
  if (!zone || !texte) return;
  zone.textContent = texte;
  zone.dataset.vu = '1';
  clearTimeout(minuteurMot);
  minuteurMot = setTimeout(() => {
    zone.dataset.vu = '0';
    // Le texte part avec l'opacité : sinon un lecteur d'écran continuerait de
    // lire un message invisible.
    setTimeout(() => { if (zone.dataset.vu !== '1') zone.textContent = ''; }, 250);
  }, duree);
}

/**
 * Fait poser le rôle Discord qui correspond à la version choisie.
 *
 * « Automatique » ne pose rien : ce n'est pas un choix de version, c'est
 * l'absence de choix — et attribuer un rôle à quelqu'un qui n'a rien décidé
 * serait décider à sa place. Les rôles existants ne sont pas retirés non plus :
 * revenir sur « automatique » ne dit pas « je ne veux plus être prévenu ».
 *
 * Ne bloque rien et n'annonce rien : la préférence de lecture, elle, est déjà
 * enregistrée dans le navigateur. Un rôle qui ne se pose pas — bot muet,
 * hiérarchie de rôles mal réglée, personne pas encore sur le serveur — ne doit
 * pas empêcher quelqu'un de régler son lecteur.
 */
function poserGoutDiscord() {
  const choisi = prefs.lire('langue', 'auto');
  const audio = choisi === 'vf' ? 'vf' : (choisi === 'vostfr' ? 'vo' : null);
  if (!audio) return;
  api('/api/mes-gouts', { method: 'POST', body: JSON.stringify({ audio }) })
    .then((r) => tracer('gouts', r?.pose ? 'role pose' : 'role non pose', audio))
    .catch((err) => tracer('gouts', 'role refuse', err.message));
}

function marquerOuvertureConnue(cible) {
  dernierePoussee = cleOuverture(cible ?? {});
}

function pousserOuverture({ serie: idSerie = null, saison = null, episode = null, film = null, titre = null }) {
  const ref = idSerie ?? serie?.id ?? null;
  const cle = cleOuverture({ serie: ref, saison, episode, film });
  if (cle === dernierePoussee) return;
  dernierePoussee = cle;
  // L'épisode que la séance porte déjà ne se republie jamais : le serveur
  // remettrait la séance à zéro pour tous (« Revenir au lecteur », audit 21/10 P1).
  if (etatSeance?.ouvrir && cle === cleOuverture(etatSeance.ouvrir)) return;
  seance?.ouvrir?.({ serie: ref, saison, episode, film, titre });
}

/**
 * Envoie la carte d'activité dans le salon vocal. À la main, toujours.
 *
 * Elle partait à chaque lancement, en mentionnant le rôle de la langue :
 * lancer un épisode en japonais réveillait le serveur entier sans que personne
 * ait rien demandé. Un message qui ping ne doit jamais être un effet de bord —
 * il se décide. Cette fonction est donc le SEUL chemin, et elle n'est appelée
 * que par un clic ou par un réglage explicitement mis sur « automatique ».
 */
/**
 * Le lien d'ouverture de cet épisode, fabriqué si besoin.
 *
 * ⚠️ C'EST LA GARANTIE QUI MANQUAIT. Une carte sans lien est refusée par le
 * serveur — à juste titre : un embed dont le bouton ne mène nulle part ne sert
 * personne. Mais le refus tombait au moment de l'envoi, sous la forme d'un code
 * interne (« Non envoyé — lien-manquant ») dont rien ne disait qu'il suffisait
 * de fabriquer un billet.
 *
 * Trois chemins mènent à l'annonce et deux d'entre eux n'avaient pas de lien à
 * donner : le CHANGEMENT d'épisode quand un onglet est déjà ouvert, et le
 * bouton « Envoyer » de la console pour une séance commencée. Plutôt que de
 * réparer chacun — et d'oublier le quatrième — le lien se fabrique ici, à
 * l'unique endroit par lequel tout passe.
 *
 * Un billet ne donne aucun droit : c'est la présence dans le salon vocal qui
 * garde la porte. Le fabriquer ne coûte qu'un aller-retour.
 */
function lienDOuverture(cible, titre, dejaLa = null) {
  if (dejaLa) return Promise.resolve(dejaLa);
  return api('/api/billet', {
    method: 'POST',
    body: JSON.stringify({
      // ⚠️ `instanceCourante()` ET NON `identifiantSeance()` : depuis le site,
      // le second rend toujours `null` (pas de SDK, pas de salon), le serveur
      // répondait 400 « billet incomplet », et la carte partait sans lien —
      // donc `/rejoindre` n'avait rien à donner. Le salon, lui, peut rester
      // nul : le serveur le retrouve seul depuis l'instance.
      instance: instanceCourante(),
      serie: cible.serie ?? serie?.id ?? null,
      saison: cible.saison ?? null,
      episode: cible.episode ?? null,
      film: cible.film ?? null,
      titre,
      salon: sdk?.channelId ?? null,
    }),
  })
    .then((r) => r?.lien ?? null)
    .catch((err) => { tracer('annonce', 'billet refuse', err.message); return null; });
}

/**
 * Ce qu'un refus veut dire, en français.
 *
 * Les codes sont utiles dans les journaux, illisibles à l'écran : « Non envoyé
 * — permissions » n'apprend rien à qui doit décider quoi faire. Chaque cas dit
 * donc ce qui s'est passé ET ce qui le débloquerait.
 */
const MOT_ANNONCE = {
  'salon-manquant': 'Cette séance n’est pas rattachée à un salon vocal.',
  'lien-manquant': 'Le lien d’ouverture n’a pas pu être fabriqué — réessaie dans un instant.',
  'token-absent': 'Le bot n’a pas de jeton Discord : à régler côté serveur.',
  permissions: 'Le bot n’a pas le droit d’écrire dans ce salon.',
  'limite-taux': 'Discord nous demande de ralentir — réessaie dans quelques secondes.',
  discord: 'Discord a refusé le message.',
  exception: 'Le message n’est pas parti — la connexion a lâché.',
  inconnue: 'Le message n’est pas parti, sans raison connue.',
};

function motDAnnonce(r) {
  // ⚠️ CE REPLI DISAIT UNE CHOSE FAUSSE — défaut payé le 14/09.
  //
  // Il annonçait « l'annonce part depuis l'activité Discord » parce que
  // `envoyerLaCarte` renonçait hors activité. Elle ne renonce plus : depuis le
  // site aussi, la carte part. `null` ne veut donc plus dire « pas le bon
  // endroit », il veut dire « l'appel n'a pas abouti » — la connexion, ou un
  // refus du serveur, tous deux déjà dans le journal (`annonce · echec`).
  if (!r) return 'Le message n’est pas parti — réessaie dans un instant.';
  if (r?.poste) return 'Envoyé dans le salon.';
  // ⚠️ « Déjà annoncé » N'EST PAS UN ÉCHEC — le serveur rend `ok: true`. Le
  // présenter comme un refus fait chercher une panne là où tout va bien.
  if (r?.raison === 'doublon') return 'Déjà annoncé pour cet épisode.';
  return MOT_ANNONCE[r?.raison] ?? `Non envoyé${r?.raison ? ` — ${r.raison}` : ''}.`;
}

function envoyerLaCarte({ saison, episode, titre, lien = null, film = null } = {}) {
  // ⚠️ DÉFAUT PAYÉ LE 14/09 : `if (!sdk?.channelId) return` COUPAIT LE FIL
  // ENTRE LE SITE ET LE BOT.
  //
  // `sdk` vaut `null` hors activité PAR CONSTRUCTION. Cette garde faisait donc
  // renoncer le client avant même d'appeler le serveur, et c'est le seul
  // appelant de `/api/seance/annonce` — la seule route qui écrive la mémoire du
  // bot. Une séance pilotée depuis le site n'existait pas pour Discord :
  // `/annonce` et `/rejoindre` répondaient « Aucune séance connue » pendant que
  // six personnes regardaient.
  //
  // Le salon ne vient plus du client depuis longtemps : le serveur le tire de
  // `salonOuElleALaMain(session.userId)` et refuse (403) qui ne pilote pas de
  // séance. Il n'y a donc rien à garder ici — la garde ne protégeait rien, elle
  // empêchait.
  const cible = { serie: serie?.id ?? null, saison, episode, film };
  const reglages = etatSeance?.reglages?.embed ?? {};
  // La mention du rôle est ce qui ping : elle ne part que si on l'a demandée.
  const choisie = prefs.lire('langue', 'auto');
  // Le lien d'abord : sans lui, l'appel partirait pour se faire refuser.
  return lienDOuverture(cible, titre, lien).then((adresse) => api('/api/seance/annonce', {
    method: 'POST',
    body: JSON.stringify({
      // Informatif seulement : le serveur ignore ce champ et prend le salon de
      // la séance qu'on pilote. `sdk?.` — il n'y a pas de SDK sur le site.
      salon: sdk?.channelId ?? null, serie: cible.serie, saison, episode, titre,
      image: imageEpisode(cible),
      langue: reglages.mentionner && choisie !== 'auto' ? choisie : null,
      resume: episodeCatalogue(saison, episode)?.resume ?? null,
      remplacer: reglages.remplacer !== false,
      lien: adresse,
    }),
  })).then((r) => {
    tracer('annonce', r?.poste ? 'publiee dans le vocal' : 'non publiee', r?.raison ?? null);
    // ⚠️ UN REFUS NE CONSOMME PAS LA CARTE — défaut mesuré le 15/09.
    //
    // La carte était effacée dans TOUS les cas, y compris quand le serveur
    // venait de répondre « non publiée ». Or c'est elle qui allume « Annoncer
    // dans le salon » et qui porte le lien d'ouverture : un refus faisait donc
    // disparaître le bouton, et il n'y avait plus rien à recliquer. C'est le
    // symptôme exact de « la console depuis le site ne marche pas » — depuis le
    // site, `lien-manquant` et le 403 « tu ne pilotes aucune séance » sont
    // précisément les refus qu'on rencontre, et ils sont tous deux réparables
    // en quelques secondes. La carte ne part donc que si le message est parti.
    //
    // ⚠️ `doublon` COMPTE COMME PARTI : le serveur rend `ok: true` avec cette
    // raison — l'épisode EST annoncé, garder la carte ferait recliquer pour
    // rien (voir `motDAnnonce`, qui le dit déjà à l'écran).
    if (r?.poste || r?.raison === 'doublon') {
      carteAProposer = null;
      peindreInviteCarte();
    }
    return r;
  }).catch((err) => { tracer('annonce', 'echec', err.message); return null; });
}

/** Ce qu'on propose d'annoncer, tant que personne n'a tranché. */
let carteAProposer = null;

function peindreInviteCarte() {
  const b = $('sc-carte');
  if (!b) return;
  const proposee = !!carteAProposer;
  b.hidden = !proposee || !(etatSeance?.jeSuisHote || jeSuisAdmin(etatSeance));
  b.classList.toggle('propose', proposee);
  b.textContent = proposee ? 'Annoncer dans le salon' : 'Annoncer';
}

function annoncerOuverture({ saison, episode, titre, lien = null }) {
  // La séance d'abord : c'est elle qui peint le bandeau de la console chez
  // tout le monde, et elle n'a pas à attendre que Discord réponde.
  pousserOuverture({ saison, episode, titre });

  // ⚠️ PLUS DE `if (!sdk?.channelId) return;` ICI — défaut payé le 14/09.
  //
  // C'était lui qui empêchait `carteAProposer` d'exister hors activité : depuis
  // le site, le bouton « Annoncer dans le salon » ne s'allumait jamais, et
  // l'envoi automatique (`embed.auto`) ne partait pas non plus. Le salon est
  // retrouvé côté serveur, à partir de la séance qu'on pilote.

  // ⚠️ ON PROPOSE, ON N'ENVOIE PAS.
  //
  // Sauf si la séance a explicitement choisi l'envoi automatique. Le bouton
  // s'allume dans la barre de commandes et attend un clic ; sans clic, rien ne
  // part et personne n'est réveillé.
  // Le lien est fabriqué si besoin — voir `lienDOuverture`. On ne pose la carte
  // qu'une fois qu'elle mène quelque part : un bouton d'envoi allumé sur une
  // carte sans lien ne promettrait qu'un refus.
  lienDOuverture({ serie: serie?.id ?? null, saison, episode }, titre, lien)
    .then((adresse) => {
      carteAProposer = { saison, episode, titre, lien: adresse };
      peindreInviteCarte();
      if (adresse && etatSeance?.reglages?.embed?.auto) envoyerLaCarte(carteAProposer);
    });
}

async function lireDehors(cible, intitule) {
  if (!DANS_DISCORD || !sdk) return false;

  // Le lien ne dit rien de ce qu'il ouvre : un billet opaque, que seul le
  // serveur sait résoudre. On ne peut donc pas le bricoler pour entrer dans
  // la séance d'un autre salon.
  let adresse;
  try {
    const { lien } = await api('/api/billet', {
      method: 'POST',
      body: JSON.stringify({
        instance: identifiantSeance(), ...cible, titre: intitule,
        // LE SALON VOCAL VOYAGE AVEC LE BILLET, et c'est lui qui ouvre la
        // porte à l'autre bout : le serveur vérifie que celui qui présente le
        // lien est bien dans ce salon-là, maintenant. Sans ce champ, le billet
        // ne prouve rien d'autre qu'une identité, et le lecteur redemanderait
        // une connexion Discord — ce qu'on est justement en train de retirer.
        salon: sdk?.channelId ?? null,
      }),
    });
    adresse = lien;
    // Le retour, côté navigateur, rendra l'écran d'où l'on part (voir `DEPUIS_ACTIVITE`).
    // Une origine qu'on ne sait pas écrire ne doit jamais faire passer le billet pour refusé.
    try {
      const origine = new URLSearchParams({ depuis: ecranDOrigineDansLActivite(), oeuvre: cible.serie ?? cible.film ?? '' });
      if (adresse) adresse = `${String(adresse).split('#')[0]}#${origine}`;
    } catch { /* sans origine, le retour retombe sur la liste des séances */ }
  } catch (err) {
    tracer('sortie', 'billet refuse', err.message);
    return false;
  }

  // La SEANCE d'abord : c'est elle qui peint le bandeau de la console chez
  // tout le monde — titre, numero, vignette, resume. Sans ce message, l'etat
  // publie n'a pas de champ `ouvrir` et la console affiche « Episode en
  // cours » sans rien d'autre, ce qui etait le cas jusqu'ici au PREMIER
  // lancement : le seul qui compte vraiment.
  pousserOuverture({
    serie: cible.serie ?? serie?.id ?? null,
    saison: cible.saison ?? null,
    episode: cible.episode ?? null,
    film: cible.film ?? null,
    titre: intitule,
  });

  // L'episode peut partir dans le chat du salon vocal : ceux qui n'ont pas
  // l'activite ouverte y trouvent le lien, et le bouton les amene sur la meme
  // seance. Mais ON PROPOSE, on n'envoie pas — c'est un message qui peut
  // reveiller tout un serveur, il se decide. Le meme chemin que partout
  // ailleurs, pour qu'il n'y ait qu'un seul endroit ou ce choix se fasse.
  if (sdk?.channelId) {
    carteAProposer = {
      saison: cible.saison ?? null, episode: cible.episode ?? null,
      titre: intitule, lien: adresse, film: cible.film ?? null,
    };
    peindreInviteCarte();
    if (etatSeance?.reglages?.embed?.auto) envoyerLaCarte(carteAProposer);
  }
  try {
    const retour = await sdk.commands.openExternalLink({ url: adresse });
    // ⚠️ DÉFAUT PAYÉ LE 14/09 : « ANNULER » N'EST PAS UNE ERREUR, ET ON LE
    // PRENAIT POUR UN SUCCÈS.
    //
    // Le SDK ne REJETTE pas quand la personne répond « Annuler » à « tu quittes
    // Discord », ou quand le navigateur bloque la fenêtre : il RÉSOUT avec
    // `{ opened: false }`. Le retour était jeté, le `catch` ci-dessous ne voyait
    // donc que les pannes de transport, et l'activité basculait sur la console
    // de séance en jurant que la lecture avait commencé — alors qu'il n'y avait
    // de lecteur nulle part, et que le repli « lecture sur place » n'était
    // jamais atteint.
    //
    // ⚠️ `=== false` STRICT, JAMAIS UN `!retour.opened`. Le schéma du SDK porte
    // `.default({ opened: null })` pour les clients anciens qui répondent une
    // trame vide : `null` veut dire « on ne sait pas », pas « ça a échoué ».
    // Le traiter comme un échec ferait lire DANS l'iframe pendant que l'onglet
    // joue dehors — deux bandes-son et une séance à deux positions, pire que le
    // défaut qu'on corrige.
    if (retour?.opened === false) {
      tracer('sortie', 'ouverture annulee — lecture sur place', intitule);
      return false;
    }
    tracer('sortie', 'lecture ouverte dans le navigateur', intitule);
    // L ecran d attente ne doit pas rester : la lecture se passe ailleurs, et
    // l activite revient a ce qu elle fait — le catalogue.
    document.body.classList.remove('plein');
    $('v-chargement')?.classList.remove('lecture');
    // L'activité reste sur la gestion : c'est là qu'on suit ce qui se passe
    // maintenant que la lecture est ailleurs.
    vue('v-gestion');
    peindreGestion();
    return true;
  } catch (err) {
    // Refus de Discord : plutôt que de ne rien faire, on lit sur place.
    tracer('sortie', 'ouverture refusee — lecture sur place', err.message);
    return false;
  }
}

/**
 * Ouvre un épisode.
 *
 * ⚠️ DEUX MODES, ET UN SEUL CHEMIN — c'est tout l'intérêt.
 *
 * `enFond` ne fait pas « moins » : il fait la MÊME chose sans rien montrer ni
 * rien lancer. Le lecteur préparé reçoit donc exactement ce que reçoit un
 * lecteur ouvert normalement — durée, voisins, aperçu au survol, pistes de
 * sous-titres, bouton de langue, surveillance de la fin. Une préparation
 * écrite à côté aurait fatalement divergé, et le retour aurait rendu un
 * lecteur amputé de ce qu'on aurait oublié d'y remettre.
 *
 * Ce que `enFond` retire, et rien d'autre :
 *   · l'écran d'attente et le plein cadre — on regarde la liste des séances ;
 *   · l'annonce à la séance (`pousserOuverture`) — on ne DÉCIDE rien, on se
 *     contente de préparer ce que la séance a déjà décidé ; l'annoncer
 *     relancerait l'épisode chez tout le monde ;
 *   · la bascule d'écran et le `play()` — voir `preparerEnFond`.
 */
/**
 * @param {number} reprise seconde où reprendre. Vient de « Reprendre » sur
 *   l'accueil, et de nulle part ailleurs : une lecture ordinaire commence au
 *   début, et une lecture de séance se cale sur la séance, pas sur nous.
 * @param {boolean} relance appel interne, déclenché par `video.onerror` plus
 *   bas pour retenter une seule fois. Jamais posé par un appelant.
 */
// Un seul lancement de premier plan a la fois — voir le commentaire sur le
// verrou, juste dans la fonction.
let lancementEnCours = false;

/**
 * ⚠️ LE VERROU NE REDESCENDAIT QUE SUR LES CHEMINS PRÉVUS.
 *
 * `lancerLEpisode` remet `lancementEnCours` à `false` sur chacune de ses
 * sorties normales et sur chacun de ses échecs ATTRAPÉS. Mais une exception
 * non prévue — `serie` à `null` au moment d'écrire la trace de lancement, un
 * nœud absent, un module qui lève — sortait de la fonction en laissant le
 * verrou posé. À partir de là, PLUS AUCUN lancement ne partait de la session :
 * chaque clic tombait sur « clic ignore — un lancement est deja en cours », et
 * rien à l'écran ne disait pourquoi. Le défaut n'était pas dans le clic qui
 * vient d'échouer, il était dans tous ceux d'après.
 *
 * Cette enveloppe rend le verrou et laisse l'erreur remonter à l'appelant, qui
 * lui l'affiche (`lancerDepuisMenu`, `rouvrirLeLecteur`).
 */
async function lancer(saison, episode, options = {}) {
  try {
    return await lancerLEpisode(saison, episode, options);
  } catch (err) {
    if (!options.enFond) lancementEnCours = false;
    throw err;
  }
}

async function lancerLEpisode(saison, episode, { enFond = false, reprise = 0, relance = false } = {}) {
  // ⚠️ PAS D'ŒUVRE, PAS DE LANCEMENT — ET ON LE DIT AVANT DE PRENDRE LE VERROU.
  // `serie.id` est lu sans précaution une dizaine de lignes plus bas (la trace
  // de lancement) puis à chaque appel de résolution : y arriver avec `serie` à
  // `null` levait APRÈS avoir posé le verrou et APRÈS avoir basculé sur l'écran
  // d'attente — écran de chargement figé, et lecteur mort pour la session.
  if (!serie?.id) throw new Error('Aucune œuvre chargée pour cet épisode.');
  // La caisse des groupes : ouvrir un groupe se paie AVANT d'annoncer l'épisode
  // à la séance (les membres qui le reçoivent le rejoignent gratuitement).
  if (!enFond && !relance) {
    const titreEp = [oeuvre?.titre, intituleEpisode(saison, episode)].filter(Boolean).join(' · ');
    if (!(await caisseUI.porteDuPrix('series', `${serie.id}:${saison}:${episode}`, { titre: titreEp, qualite: prefs.lire('qualite', 'auto') }))) return;
  }
  // ⚠️ DEUX FACONS D'OUVRIR, ET C'EST UN REGLAGE — PAS UNE DEVINETTE.
  //
  // Toji : « soit ça ouvre directement la séance et le lecteur part dans une
  // nouvelle page, de façon qu'on puisse gérer intégralement depuis internet ;
  // soit ça ouvre directement le lecteur, et la séance est en fond ».
  //
  // Dans le navigateur seulement : l'activité Discord vit dans une iframe dont
  // le mapping d'URL n'ouvre pas d'onglet, et où « une deuxième page » ne veut
  // rien dire. Et jamais en fond : préparer un épisode n'est pas l'ouvrir.
  if (!enFond && !DANS_DISCORD && prefs.lire('ouverture-lecteur', 'ici') === 'onglet' && serie?.id) {
    const adresse = `${location.origin}/?id=${encodeURIComponent(serie.id)}&ep=${saison}:${episode}`;
    const onglet = window.open(adresse, '_blank', 'noopener');
    // Un bloqueur de fenêtres a le dernier mot : plutôt que de ne rien faire —
    // le clic resterait sans effet, et personne ne saurait pourquoi — on ouvre
    // ici, comme avant.
    if (onglet) {
      vue('v-gestion');
      peindreGestion();
      return;
    }
  }

  // Changement d'épisode : la dernière seconde du précédent part AVANT que la
  // balise ne change de fichier.
  try { noterOuOnEnEst({ force: true }); } catch { /* une note ne bloque pas un lancement */ }
  const dejaLa = $('video');
  if (dejaLa) {
    dejaLa.onerror = null;
    // Le lecteur tient le même rappel de l'autre côté (voir `brancherReprise`) :
    // le laisser pendant la résolution, c'est laisser l'épisode PRÉCÉDENT se
    // relancer par-dessus celui qu'on vient de demander, si sa source meurt
    // juste maintenant. `_lecteur` et non `lecteur()` : rien à créer ici.
    _lecteur?.surPanneSource(null);
    // ⚠️ CE MARQUEUR NE SE REMETTAIT JAMAIS A ZERO.
    //
    // `v.dataset.reprise` sert plus bas a n'autoriser qu'UNE tentative de
    // reprise par ouverture — voir `v.onerror`. L'intention etait « une
    // tentative par episode » ; l'effet reel etait « une tentative pour
    // toute la session, tous episodes confondus », parce que la balise
    // <video> vit toute la session et que rien ne remettait jamais ce
    // marqueur a '0'. Des la premiere erreur rencontree apres l'ouverture
    // de la page, plus AUCUNE reprise ne se declenchait nulle part, meme
    // sur un episode qui n'avait encore jamais echoue.
    //
    // On le remet a zero a l'ouverture d'un nouvel episode — pas quand
    // `enFond` prepare simplement une source en arriere-plan sans toucher
    // a la balise visible, et pas quand cet appel EST la relance interne
    // declenchee par `onerror` juste plus bas : celle-ci doit au contraire
    // trouver le marqueur a '1' pour ne retenter qu'une seule fois.
    if (!enFond && !relance) dejaLa.dataset.reprise = '0';
  }
  if (!enFond) {
    // ⚠️ VERROU CONTRE LA RE-ENTRANCE.
    //
    // Deux appels concurrents — double-clic sur « précédent »/« suivant », ou
    // clic pendant qu'une résolution précédente tourne encore — lançaient
    // deux résolutions en parallèle. `nouvelleTrace()` juste plus bas écrase
    // `traceCourante` (~798) à chaque appel : le second appel réattribue donc
    // toute la télémétrie du premier (encore en vol, côté serveur) à SON
    // identifiant à lui, et l'admin qui relit la trace du premier clic y
    // trouve les étapes du second. Un verrou simple suffit : un seul
    // lancement de premier plan à la fois, le second est ignoré plutôt que de
    // partir en parallèle.
    //
    // `enFond` n'est pas concerné : `preparerLeRetour` a déjà son propre
    // garde-fou (`preparationEnFond?.cle`) et ne touche pas `traceCourante`.
    if (lancementEnCours) {
      tracer('lancement', 'clic ignore — un lancement est deja en cours', { saison, episode });
      return;
    }
    lancementEnCours = true;
    // Une ouverture visible fait céder la préparation en fond (audit 14e 21/10,
    // P2) : pendant l'attente le lecteur est caché, et la préparation se
    // croirait encore utile — elle poserait sa source par-dessus celle-ci.
    preparationEnFond = null;

    texte('chargement-txt', `Recherche d'une source pour S${saison}E${episode}…`);
    appliquerBlason(lireBlason(prefs));
    peindreFondAttente(imageEpisodeEnGrand({ saison, episode }), imageEpisode({ saison, episode }));
    peindreTitreAttente({ saison, episode });
    // Pas de destination imposée : le retour suit le chemin (voir `revenir`).
    retourDepuisAttente = null;
    const bRetour = $('retour-attente');
    if (bRetour) bRetour.hidden = false;
    document.body.classList.add('plein');
    $('v-chargement')?.classList.add('lecture');
    debutAttente = Date.now();
    vue('v-chargement');

    nouvelleTrace(`episode S${saison}E${episode} · ${serie.id}`);
  }

  // Ce que l'activité vient de lancer. Le PREMIER épisode part par un lien
  // (un billet ouvert dans le navigateur), pas par un message 'ouvrir' : sans
  // cette ligne, la présence n'apprendrait l'épisode qu'au suivant.
  //
  // ⚠️ En fond, on ne touche à rien de tout cela : préparer n'est pas
  // regarder. La séance sait déjà quel épisode elle porte — c'est elle qui
  // nous l'a dit — et la présence annoncerait un visionnage qui n'a pas lieu.
  if (!enFond) {
    episodeDeLaSeance = {
      serie: serie?.id ?? null, saison, episode, titre: intituleEpisode(saison, episode),
    };
    majPresence();
  }

  // LA SÉANCE APPREND L'ÉPISODE ICI, quel que soit l'endroit d'où on lance.
  //
  // C'était réservé à l'activité. Résultat : cliquer « épisode précédent »
  // depuis le lecteur changeait l'épisode chez soi et NULLE PART ailleurs — la
  // console Discord restait sur le 412 pendant que le lecteur jouait le 411,
  // et les autres participants ne suivaient pas.
  //
  // Or lancer un épisode est un fait en soi, pas la conséquence d'un onglet
  // ouvert avec succès : les deux chemins qui prévenaient la séance plus bas
  // peuvent échouer tous les deux, et alors personne n'apprenait rien.
  //
  // Pas de boucle : `surOuvrir` retient la clé reçue AVANT d'appliquer l'écho,
  // et `pousserOuverture` la retrouve identique ici, donc se tait.
  //
  // ⚠️ JAMAIS EN FOND. Préparer, c'est se rendre capable de montrer ; ce n'est
  // pas ouvrir l'épisode. Annoncer ici relancerait chez tout le monde un
  // épisode que personne n'a demandé — et l'annonce partirait à chaque
  // rechargement de la liste des séances.
  if (!enFond) pousserOuverture({ saison, episode, titre: intituleEpisode(saison, episode) });

  // Depuis l'activité, la lecture se passe dans les navigateurs.
  if (DANS_DISCORD) {
    // LA SÉANCE APPREND L'ÉPISODE ICI, avant toute tentative d'ouverture.
    //
    // Elle ne l'apprenait que plus bas : soit par `lireDehors` quand un onglet
    // s'ouvrait, soit par la branche « une page est déjà ouverte ». Les deux
    // chemins peuvent échouer — un billet refusé, une page qui redemande une
    // connexion — et alors PERSONNE n'apprenait l'épisode : ni le bandeau, ni
    // les autres participants, ni la présence riche des autres.
    //
    // Or lancer un épisode est un fait en soi, pas la conséquence d'un onglet
    // ouvert avec succès. On le dit donc tout de suite, et ce qui suit ne fait
    // plus que la lecture.
    //
    // Un onglet vient peut-être d'être ouvert sans avoir eu le temps de
    // rejoindre la séance : le voyant serait encore éteint, et l'on en
    // ouvrirait un deuxième. On tient donc l'onglet pour vivant tant que la
    // séance ne dit pas le contraire.
    const voyant = (etatSeance?.participants ?? []).some((p) => p.regarde);
    const suivent = voyant || ongletOuvert;
    if (suivent && seance) {
      // Des pages sont déjà ouvertes : on les met à jour plutôt que d'en
      // ouvrir une de plus. Trois épisodes, sinon, c'est trois lecteurs.
      // Prévient la séance ET le salon : changer d'épisode est un évènement
      // pour ceux qui ne sont pas devant l'activité autant que pour ceux qui
      // y sont.
      annoncerOuverture({
        saison, episode,
        titre: intituleEpisode(saison, episode),
      });
      tracer('sortie', 'episode pousse a l onglet ouvert', {
        episode: `S${saison}E${episode}`, voyant, suppose: !voyant,
      });
      document.body.classList.remove('plein');
      $('v-chargement')?.classList.remove('lecture');
      vue('v-gestion');
      peindreGestion();
      lancementEnCours = false;
      return;
    }
    if (await lireDehors(
      { serie: serie.id, saison, episode },
      intituleEpisode(saison, episode),
    )) {
      ongletOuvert = true;
      lancementEnCours = false;
      return;
    }
  }

  let choix;
  const depart = Date.now();

  // Déjà préparé pendant la fin de l'épisode précédent : on enchaîne sans le
  // temps mort de la résolution.
  const avance = await sourcePreparee(`${serie.id}:${saison}:${episode}`);
  if (avance) {
    tracer('resolution', 'source déjà prête (préchargée)', { ref: avance.ref });
  }

  if (avance) choix = avance;
  else try {
    // La langue voulue part avec la demande : c'est elle qui décide de la
    // source servie, et chacun reçoit donc la sienne pour le même épisode.
    const voulue = langueVoulueDe(oeuvre);
    choix = await api(`/api/play-now/series/${encodeURIComponent(`${serie.id}:${saison}:${episode}`)}`
      + parametresDeGout(voulue, sousTitresVoulus(oeuvre)));
    if (choix.langueVoulue && choix.langueServie === 'autre') {
      tracer('resolution', `pas de source ${choix.langueVoulue} pour cet episode`);
    }
    tracer('resolution', `source retenue en ${Date.now() - depart} ms`, {
      ref: choix.ref, direct: choix.direct, duree: choix.duree, source: choix.source ?? null,
    });
  } catch (err) {
    tracer('resolution', `echec en ${Date.now() - depart} ms`, err.message);
    // Une préparation qui échoue ne se voit pas et ne doit pas se voir : le
    // clic sur « Revenir au lecteur » refera le chemin complet, comme avant.
    // Poser l'écran d'erreur par-dessus la liste des séances rendrait visible
    // un travail que personne n'a demandé.
    if (enFond) { tracer('prechargement', 'préparation en fond abandonnée'); return; }
    lancementEnCours = false;
    // ⚠️ Voir le commentaire dans `api()` : une « attente d'autorisation » a
    // deja pose son propre ecran (`v-attente`) — le rebasculer ici sur
    // l'ecran d'erreur generique l'effacerait aussitot.
    if (err.silencieux) return;
    return erreur(err.message, err.essais ?? []);
  }

  // Retour pressé pendant la recherche : la réponse arrive quand même, mais
  // elle ne doit plus rien déclencher.
  //
  // En fond il n'y a pas d'écran d'attente à interroger : ce qui périme une
  // préparation, c'est que la séance ait changé d'épisode ou que la personne
  // soit revenue au lecteur entre-temps. C'est `preparerLeRetour` qui le sait,
  // et qui le vérifie avant de laisser poser la source.
  // La trace dit POURQUOI : sans elle, un abandon n'apparaissait que comme
  // « départ + source retenue », sans suite (audit 14e 21/10, P7).
  if (!enFond && $('v-chargement').hidden) {
    lancementEnCours = false;
    tracer('lancer', 'abandon : écran quitté', { ecran: ecranCourant });
    return;
  }
  if (enFond && !preparationEncoreUtile(saison, episode)) {
    tracer('prechargement', 'préparation périmée avant la pose', { saison, episode });
    return;
  }

  // ⚠️ `choix.ref` N'ETAIT JAMAIS VALIDE.
  //
  // `api()` rend `{}` — sans lever — quand le corps recu ne parse pas en JSON
  // ET que le statut HTTP est 2xx (voir `api()`, ~836) : un succes de
  // transport qui ne porte aucune reference exploitable. Sans ce garde-fou,
  // `poser.ref` valait `undefined` et `lecteur().ouvrir(...)` tentait
  // d'ouvrir une source qui n'existe pas, avec un echec confus cote lecteur
  // au lieu d'un message clair ici, la ou toutes les autres pannes de
  // resolution atterrissent deja.
  if (!choix?.ref) {
    tracer('resolution', 'reponse sans reference exploitable', choix);
    if (!enFond) lancementEnCours = false;
    if (enFond) { tracer('prechargement', 'préparation en fond abandonnée — reponse vide'); return; }
    return erreur('Aucune source exploitable pour cet épisode.');
  }

  // La bascule pose son ecran blanc de facon synchrone : au retour, il couvre
  // deja tout. On enchaine donc sans attendre — le chargement du flux part
  // pendant que la transition se joue par-dessus, au lieu d'apres.
  if (!enFond) basculerQuandPret();
  const poser = {
    ref: choix.ref,
    direct: choix.direct,
    duree: choix.duree,
    dureeApprox: secondesDe(serie.runtime),
    // La barre du lecteur dit le NUMÉRO et le NOM de l'épisode, rien d'autre —
    // voir `intituleCourtEpisode`. L'arc et sa partie restent partout ailleurs.
    titre: intituleCourtEpisode(saison, episode),
    // Pour l'écran verrouillé et le casque (Media Session, voir lecteur.js).
    oeuvre: oeuvre?.titre ?? serie?.name ?? null,
    affiche: oeuvre?.affiche || serie?.poster || null,
    episode: `${serie.id}:${saison}:${episode}`,
    voisins: voisinsDe(saison, episode),
    surSousTitres: (ref) => chargerSousTitres(ref, `series/${serie.id}:${saison}:${episode}`),
    // Le type decide du drapeau de la version originale : japonais pour un
    // anime, anglais pour le reste (voir `drapeaux.js`).
    typeOeuvre: oeuvre?.type ?? null,
    changerLangue: (vers, position) => changerLangue(
      `series/${serie.id}:${saison}:${episode}`, vers, position,
    ),
    // La préférence (dernier choix de l'œuvre, sinon réglages) : le lecteur s'en sert pour la PISTE d'un fichier
    // multi-pistes, et rend compte de tout choix fait à la main.
    langueVoulue: langueVoulueDe(oeuvre),
    surChoixLangue: (l) => retenirChoixDeLangue(oeuvre?.id ?? serie?.id, l),
    // Le menu « Voix » (autres fichiers) et le panneau de sources du
    // propriétaire — la même fabrique que pour les films, voir
    // `rappelsDeVoixEtSources`.
    ...rappelsDeVoixEtSources('series', `${serie.id}:${saison}:${episode}`),
    // `doublageConnu` n'est plus passé : le lecteur ne l'a JAMAIS lu (audit du
    // 10/09 — `installer` ne le déstructurait pas), et le menu « Voix » demande
    // désormais au serveur (/api/voix) ce qu'un autre fichier porte.
  };
  // ⚠️ LA SECONDE OÙ LA SÉANCE EN EST, ET NON ZÉRO.
  //
  // Une balise préparée mais restée au générique n'est pas neutre : dès qu'elle
  // tient des métadonnées, la séance la considère comme lisante et publie sa
  // position une fois par seconde. Depuis qui pilote, ce zéro RAMÈNERAIT TOUTE
  // LA SALLE au début de l'épisode pendant qu'on lit la liste des séances.
  // On prépare donc là où la séance est, pas là où le fichier commence.
  // ⚠️ AUSSI EN FOND : un membre de séance dont l'épisode a été préparé en fond
  // rejoint SANS relancer (`rejoindreCeQuiEstDejaOuvert`) — sa progression
  // partait sous l'épisode d'avant, ou pas du tout.
  {
    // De quoi noter la reprise. Posé ici, une fois la source retenue : plus tôt
    // on retiendrait un épisode qui n'a peut-être aucune source.
    const fiche = episodeCatalogue(saison, episode);
    lectureCourante = {
      oeuvreId: oeuvre?.id ?? serie?.id ?? null,
      saison, episode,
      titre: oeuvre?.titre ?? serie?.name ?? null,
      episodeTitre: fiche?.title ?? null,
      vignette: fiche?.thumbnail ?? null,
    };
    // Meme geste que dans `lancerFilm`, et hors de `lectureCourante` pour la
    // meme raison. `oeuvre` est nul quand on arrive par un lien direct : le
    // repli de `typeDOeuvre` est « serie », qui est ce que le lecteur faisait.
    typeEnLecture = typeDOeuvre(oeuvre);
  }
  if (enFond) lecteur().preparerEnFond(poser, etatSeance?.position ?? 0);
  else { lecteur().ouvrir(poser); annoncerVariante(); }
  if (!enFond) retenirLectureDeLOnglet(saison, episode);

  if (!enFond) retenirLeLancement(reprise);

  const v = $('video');

  // ⚠️ REPRENDRE : ON SE PLACE DÈS QUE LA BALISE TIENT QUELQUE CHOSE, PAS
  // QUAND ELLE SE DÉCLARE « PRÊTE ».
  //
  // Le repère de « prêt » ne peut PAS être `Number.isFinite(video.duration)` ni
  // `readyState >= 3` : sur un MKV démuxé dans la page (voir mkv.js), la durée
  // reste `Infinity` — le conteneur fragmenté n'en annonce aucune — et
  // `readyState` plafonne à 1 ou 2. Les deux repères habituels ne sont donc
  // JAMAIS atteints, et la reprise ne serait jamais posée : la lecture
  // repartirait du générique, en silence.
  //
  // `imagePrete` est le repère du projet, et le seul juste ici :
  // `readyState >= 2 || buffered.length > 0`. Si la balise n'y est pas encore,
  // on attend `loadedmetadata`, qui arrive dans les deux chemins.
  if (!enFond && reprise > 0) {
    const placer = () => {
      try { lecteur().recaler(reprise); } catch (err) { tracer('reprise', 'recalage refusé — balise pas déplaçable', err?.message ?? String(err)); }
      tracer('reprise', `reprise à ${Math.round(reprise)} s`, { saison, episode });
    };
    if (imagePrete(v)) placer();
    else v.addEventListener('loadedmetadata', placer, { once: true });
  }

  surveillerVideo(v);
  surveillerFin(v, saison, episode);
  if (!enFond) {
    // L onglet porte l episode : parmi dix onglets, celui-ci se retrouve.
    marquerOnglet(intituleEpisode(saison, episode));
    mesurerMiseEnPage('ouverture du lecteur');
  }

  // Un lien debrid peut expirer ou tomber — ou le démuxeur mourir sans que la
  // balise le dise : on redemande une source plutôt que de laisser un lecteur
  // noir, une seule fois pour ne pas boucler. Voir `brancherReprise`.
  //
  // La seconde où l'on reprenait voyage avec la nouvelle tentative : sans
  // elle, une source qui lâche à la reprise ramènerait au générique celui qui
  // venait justement de demander à reprendre.
  //
  // `relance: true` protège le marqueur que la reprise vient de poser : sans
  // lui, ce nouvel appel le remettrait à '0' avant même qu'elle ait pu le
  // lire, et une seconde erreur sur ce même épisode retenterait indéfiniment
  // au lieu de s'arrêter une fois.
  brancherReprise(v, lecteur(), {
    enFond,
    relancer: () => lancer(saison, episode, { enFond, reprise, relance: true }),
    contexte: { saison, episode },
  });

  // Les sous-titres arrivent apres : les chercher d'abord retarderait la lecture
  // pour une piste qui n'existe peut-etre pas.
  chargerSousTitres(choix.ref, `series/${serie.id}:${saison}:${episode}`);

  // ⚠️ L'AUTRE LANGUE SE PREPARE PENDANT QU'ON REGARDE, PAS AU CLIC.
  //
  // Vingt secondes apres le depart : assez pour que la lecture soit lancee et
  // que la resolution en cours soit finie — on ne se dispute pas la ligne avec
  // ce qui sert l'image — et bien avant qu'on pense a basculer.
  //
  // On ne prepare que ce qui EXISTE : `doublageConnu` sait deja, sans rien
  // couter, si l'episode a une VF. Preparer une langue absente, ce serait des
  // requetes aux add-ons pour une reponse qu'on connait.
  const autre = (choix.langue === 'vf') ? 'vo' : 'vf';
  // `doublageConnu` est le rappel que le lecteur a recu a l'ouverture : il
  // repond depuis le catalogue, sans rien couter. `false` = cette langue
  // n'existe pas pour cet episode ; `null` = on ne sait pas, on prepare.
  if (etatDoublage(autre) !== false) {
    clearTimeout(minuteurAutreLangue);
    minuteurAutreLangue = setTimeout(() => {
      if (!$('v-lecture')?.hidden) preparerLautreLangue(`series/${serie.id}:${saison}:${episode}`, autre);
    }, 20_000);
  }

  // La source est posee : ce lancement de premier plan est termine, le
  // suivant (un autre clic sur precedent/suivant) peut partir. Voir le
  // verrou en tete de fonction.
  if (!enFond) lancementEnCours = false;
}

/**
 * Suit les étapes de la balise vidéo une fois par ouverture.
 *
 * Ce sont elles qui situent l'arrêt : rien du tout, métadonnées reçues mais
 * pas d'image, ou lecture partie puis coupée. Chaque étape n'est notée qu'une
 * fois — un `waiting` se répète à chaque hoquet, et vingt lignes identiques
 * n'apprennent rien de plus que la première.
 */
/**
 * Dimensions réelles au moment où l'on regarde.
 *
 * Une mise en page qui se tient sur mon écran peut se replier sur un autre :
 * la fenêtre d'une activité change de taille sans rechargement, et une largeur
 * figée ne se voit qu'à ce moment-là. Ces chiffres viennent donc de l'écran de
 * celui qui regarde, pas d'une simulation.
 */
function mesurerMiseEnPage(quand) {
  const b = (s) => {
    const e = document.querySelector(s);
    if (!e) return null;
    const r = e.getBoundingClientRect();
    return [Math.round(r.width), Math.round(r.height), Math.round(r.left)];
  };
  tracer('mise-en-page', quand, {
    fenetre: [window.innerWidth, window.innerHeight],
    main: b('main'),
    vue: b('#v-lecture'),
    lecteur: b('.lecteur'),
    video: b('video'),
    bas: b('.l-bas'),
    classes: document.body.className || '(aucune)',
  });
}

// Un redimensionnement de la fenêtre d'activité ne recharge rien : c'est
// exactement là qu'une mise en page se casse sans qu'on le voie.
let minuteurMesure = null;
addEventListener('resize', () => {
  if (!traceCourante || $('v-lecture')?.hidden) return;
  clearTimeout(minuteurMesure);
  minuteurMesure = setTimeout(() => mesurerMiseEnPage('apres redimensionnement'), 400);
});

/**
 * L onglet dit ce qu il joue.
 *
 * Un onglet parmi dix ne se retrouve pas a son titre par defaut : celui qui a
 * lance un episode doit pouvoir revenir dessus d un coup d oeil. Le titre
 * porte donc l episode, et la pastille de l onglet prend sa vignette.
 */
function marquerOnglet(intitule) {
  document.title = intitule ? `${intitule} — ${MARQUE}` : MARQUE;
  // ⚠️ L'ICÔNE NE BOUGE PAS. JAMAIS.
  //
  // Elle prenait la vignette de l'épisode. Le titre suffit largement à
  // retrouver l'onglet, et une icône qui change à chaque épisode cesse d'être
  // une marque : on ne reconnaît plus la 14ème Division dans une barre
  // d'onglets, on y voit une image de plus, différente à chaque fois.
  //
  // Le blason est posé une seule fois dans le balisage et plus rien ne le
  // touche — ni ici, ni le serveur, qui injectait l'icône du serveur Discord
  // par-dessus.
}

/**
 * Prépare l'épisode suivant quand la fin approche. Branché sur la vidéo, parce
 * que c'est elle qui sait où on en est.
 */
function surveillerFin(v, saison, episode) {
  const regarder = () => {
    // ⚠️ ON DEMANDAIT SA DURÉE À LA BALISE, QUI NE LA CONNAÎT PAS TOUJOURS.
    //
    // Sur le chemin du remux, ffmpeg produit un flux fragmenté sans durée :
    // `v.duration` vaut `Infinity`, donc `reste` valait `Infinity`, donc la
    // condition était fausse à chaque battement et **`preparerSuivant`
    // n'était jamais appelé**. Exactement là où il sert le plus, puisque
    // c'est le chemin où ffmpeg doit redémarrer — celui dont l'attente est
    // la plus longue. Le lecteur promet pourtant, dans `executerSaut`, que
    // « la source du suivant a été préparée pendant les dernières minutes ».
    //
    // Et même avec une durée finie, `v.currentTime` ignorait `etat.decalage`
    // : en mode remux le flux repart de zéro alors que la lecture est peut-
    // être à la vingtième minute. Le lecteur, lui, sait les deux — on le lui
    // demande.
    const T = lecteur().totale();
    const p = lecteur().position();
    const reste = T > 0 ? T - p : 0;
    // Secondes réellement LUES (un déplacement ne compte pas) : le suivant se
    // prépare dès que celui-ci tient, pas à la dernière minute.
    if (!v.paused && dernierePosition !== null && p > dernierePosition && p - dernierePosition < 2) lu += p - dernierePosition;
    dernierePosition = p;
    preparerSuivant(saison, episode, { lu, reste: reste > 0 ? reste : Infinity });
    const apres = episodeApres(saison, episode);
    if (reste > 0 && reste < PRECHAUFFE && prepare?.choix && prechargementPermis()
      && prepare.cible === `${serie?.id}:${apres?.saison}:${apres?.episode}`) {
      lecteur().prechauffer(prepare.choix, reste);
    }
  };
  let lu = 0;
  let dernierePosition = null;
  v.removeEventListener('timeupdate', v.__surFin ?? (() => {}));
  v.__surFin = regarder;
  v.addEventListener('timeupdate', regarder);
}

function surveillerVideo(v) {
  if (!v || v.dataset.surveille === '1') return;
  v.dataset.surveille = '1';

  const vues = new Set();
  const noter1 = (nom, detail) => {
    if (vues.has(nom)) return;
    vues.add(nom);
    tracer('video', nom, detail);
  };

  v.addEventListener('loadstart', () => { vues.clear(); noter1('chargement demarre'); });
  v.addEventListener('loadedmetadata', () => noter1('metadonnees recues', { duree: Math.round(v.duration || 0) }));
  v.addEventListener('canplay', () => noter1('pret a jouer'));
  // ⚠️ LA SECONDE DE L'ŒUVRE, ICI AUSSI — c'est ce qu'on relit dans Loki.
  //
  // Au cran remux la balise repart de zéro : « en attente de donnees à 12 s »
  // pouvait vouloir dire la douzième minute d'un film. Toutes les analyses
  // faites sur ces lignes portaient donc sur des positions fausses, et c'est
  // par elles qu'on cherche les pauses.
  const ouDansLOeuvre = () => Math.round(lecteur()?.position?.() ?? v.currentTime ?? 0);
  v.addEventListener('playing', () => noter1('lecture partie', { position: ouDansLOeuvre() }));
  v.addEventListener('waiting', () => noter1('en attente de donnees', { position: ouDansLOeuvre() }));
  v.addEventListener('stalled', () => noter1('flux au point mort'));
  v.addEventListener('abort', () => noter1('chargement abandonne'));
}

// ---------------------------------------------------------------------------
// La hauteur disponible est calculée depuis celle, réelle, de l'en-tête —
// elle varie avec le zoom, la police système et la taille de l'avatar.
// Le blason prend ses réglages dès l'ouverture : sans cela, l'écran d'attente
// jouerait le profil d'origine jusqu'à ce qu'on passe par les réglages.
appliquerBlason(lireBlason(prefs));

function ajusterHauteur() {
  const entete = document.querySelector('header');
  if (!entete) return;
  document.documentElement.style.setProperty('--h-entete', `${entete.offsetHeight}px`);
}

// Pile de navigation : le retour doit fonctionner partout, y compris quand une
// vue s'ouvre depuis un chemin inhabituel.
const pile = [];
const vueOriginale = typeof vue === 'function' ? vue : null;

// UNE IMAGE QUI ÉCHOUE NE RESTE PAS CASSÉE.
// Toji (20/10) : des affiches du catalogue en icône d'image cassée, revenues à
// l'actualisation. Le relais avait répondu 502 pendant une coupure passagère,
// et rien ne redemandait l'image. On retente deux fois (1,5 s puis 3 s), puis
// on cache la balise : le fond de la carte vaut mieux que l'icône cassée.
// En capture : `error` ne remonte pas, et un seul écouteur couvre aussi les
// cartes que la grille virtuelle peint plus tard.
document.addEventListener('error', (e) => {
  const img = e.target;
  if (!(img instanceof HTMLImageElement) || !/^https?:/.test(img.src)) return;
  const essais = Number(img.dataset.reessai || 0);
  if (essais >= 2) { img.style.visibility = 'hidden'; return; }
  img.dataset.reessai = String(essais + 1);
  const voulue = img.src;
  setTimeout(() => {
    if (!img.isConnected || img.src !== voulue) return;
    const u = new URL(voulue);
    u.searchParams.set('r', String(essais + 1));
    img.src = u.href;
  }, 1500 * (essais + 1));
}, true);
document.addEventListener('load', (e) => {
  const img = e.target;
  if (img instanceof HTMLImageElement && img.dataset.reessai) {
    delete img.dataset.reessai;
    img.style.visibility = '';
  }
}, true);

document.addEventListener('DOMContentLoaded', () => {
  surClic('retour-global', () => {
    const precedente = pile.length > 1 ? (pile.pop(), pile[pile.length - 1]) : 'v-accueil';
    vue(precedente);
  });
  ajusterHauteur();
  new ResizeObserver(ajusterHauteur).observe(document.querySelector('header'));
  addEventListener('resize', ajusterHauteur);
  texte('chargement-txt', DANS_DISCORD ? 'Connexion à Discord…' : 'Vérification de la session…');
  surClic('login', connexionDiscord);
  // La sonnette : elle ne s'affiche que si le serveur refuse pour cause
  // d'attente, mais son bouton doit être branché avant, comme les autres.
  brancherAttente();
  suivreLaTailleDesRangees();
  // La recherche est branchee des le demarrage : elle vit dans l'en-tete, donc
  // sur tous les ecrans, et ne depend d'aucun catalogue deja charge.
  preparerRechercheTete();
  preparerMapping();
  // Le clic droit sur une affiche. Un seul écouteur pour toute la page — les
  // grilles se repeignent sans arrêt, brancher carte par carte laisserait
  // muettes toutes celles peintes ensuite.
  brancherMenuDesCartes();
  brancherBulleEpisode();
  surClic('accueil', () => {
    marquerPosition({});
    const resultats = $('zone-resultats');
    if (resultats) resultats.hidden = true;
    $('v-accueil')?.classList.remove('libre');
    const champ = $('q');
    if (champ) champ.value = '';
    ouvrirAccueil();
  });
  surClic('retour', () => revenir('v-accueil'));
  // Les deux portes du catalogue d'œuvres. Deux identifiants pour la même
  // chose : `nav-catalogue` est celui de la nouvelle interface, `tout-voir` le
  // lien de fin de rangée. Un bouton absent ne coûte rien — `surClic` se garde.
  surClic('rech-retour', ouvrirAccueil);
  surClic('nav-catalogue', ouvrirCatalogueOeuvres);
  surClic('tout-voir', ouvrirCatalogueOeuvres);
  surClic('nav-accueil', ouvrirAccueil);
  // Chaque retour mène là où son libellé le promet : la fiche annonce
  // « ← Catalogue », la grille annonce « ← Accueil ». Un bouton qui dit une
  // destination et en ouvre une autre est un petit mensonge qu'on ne remarque
  // qu'après avoir cliqué.
  // ⚠️ LE RETOUR SUIT LE CHEMIN, IL NE VISE PLUS UN ECRAN FIXE.
  //
  // Toji : « imaginons qu'on soit dans l'accueil et qu'on clique directement
  // pour le choix d'un épisode d'une série. À la place d'avoir le retour vers
  // le catalogue, on a le retour vers l'accueil. »
  //
  // Le bouton ouvrait le catalogue quoi qu'il arrive, et son libellé l'annonçait
  // — ce qui était honnête, mais faux pour qui venait de l'accueil : on se
  // retrouvait dans une grille de douze mille titres après avoir cliqué sur une
  // affiche de la première rangée.
  // Venu de la recherche, il rend la recherche (il rendait le catalogue, matrice
  // des menus 21/10). Sans chemin connu — un lien ouvert directement sur une
  // fiche — le catalogue s'il est là, sinon l'accueil.
  surClic('oeu-retour', () => revenir($('v-catalogue') && catalogueComplet ? 'v-catalogue' : 'v-accueil'));
  surClic('oeuvres-retour', ouvrirAccueil);
  surClic('retour2', () => {
    // ⚠️ ON MET DE CÔTÉ, ON NE JETTE PAS.
    //
    // C'était `pause()` puis `removeAttribute('src')` : la source était perdue,
    // et revenir coutait tout le chemin — résoudre un lien, ouvrir le
    // conteneur, remplir le tampon. Plusieurs secondes pour revoir une image
    // qu'on venait de quitter.
    //
    // Toji : « le lecteur, dans tous les cas, il est tout le temps ouvert en
    // fond … quand l'utilisateur revient, c'est instantané. Ça ne lui fait ça
    // que quand il change de session. »
    lecteur().mettreDeCote();
    // ⚠️ SUR LE SITE, SORTIR DU LECTEUR REND À LA LISTE DES SÉANCES.
    //
    // Toji : « quand la personne quitte le lecteur, mettre les différentes
    // sessions · pour le client, l'interface web c'est vraiment juste lecteur /
    // choix de session, pas le reste. »
    //
    // Le retour ramenait au catalogue — la grille des épisodes, les
    // collections. C'est juste dans l'activité, où l'on choisit ce qu'on va
    // lancer. Sur le site, l'invité n'a rien à choisir : il vient rejoindre ce
    // qui se regarde déjà, et le catalogue lui proposait au contraire d'ouvrir
    // un épisode dans son coin, hors de toute séance.
    // ⚠️ ON REND LA PAGE D'OÙ L'ON VIENT, PAS UNE PAGE DÉCIDÉE D'AVANCE.
    //
    // C'était `if (!DANS_DISCORD) ouvrirLesSeances()` : sur le site, sortir du
    // lecteur ouvrait la liste des séances quoi qu'il arrive. Juste du temps où
    // le site n'était qu'un lecteur ; depuis qu'il porte l'accueil, les rayons
    // et les fiches, ça renvoyait quelqu'un venu de l'accueil sur un écran qu'il
    // n'avait jamais demandé — le reproche exact de Toji.
    //
    // Quelqu'un qui n'a traversé aucune page du site est, lui, bien arrivé par
    // une séance : c'est à elle qu'on le rend.
    // `ecranDeRetourLecture` vérifie que l'écran visé EXISTE. `vue()` sur un
    // identifiant absent masque tout et ne montre rien : on sortait du lecteur
    // sur un écran noir, et le bouton paraissait cassé.
    revenir(ecranDeRetourLecture());
  });
  surClic('ss-mes-reglages', () => { vue('v-reglages'); ouvrirModale({ enPage: true }); });
  surClic('ss-demarrer', () => ouvrirAccueil());
  surClic('retour-attente', () => {
    // La requête en cours n'est pas annulable, mais son résultat sera ignoré :
    // `lancer()` vérifie qu'on est toujours en attente avant d'ouvrir.
    //
    // ⚠️ On garde la destination AVANT de l'effacer : elle était remise à zéro
    // ici puis relue vingt lignes plus bas, si bien que le repli — l'accueil —
    // gagnait à tous les coups et que l'écran mémorisé n'a jamais servi.
    const destination = retourDepuisAttente;
    retourDepuisAttente = null;

    // ⚠️ ON RANGE TOUT CE QUE LA TRANSITION AVAIT POSÉ, ET D'ABORD LE BLANC.
    //
    // Toji : « quand on clique dessus, l'interface est buguée à cause du blanc.
    // Il faut vraiment que j'appuie, hop, ça se switch sur le bon design. »
    //
    // La bascule de fin de chargement passe le fond en blanc — c'est voulu, c'est
    // elle qui donne le « hop » quand l'image apparaît. Mais partir PENDANT cette
    // bascule laissait la classe posée : on se retrouvait sur la liste des
    // séances avec un fond blanc et des textes clairs, illisibles.
    //
    // Un écran qu'on quitte doit rendre ce qu'il a emprunté. Les quatre classes
    // partent ensemble, avant tout changement de vue, pour que la page suivante
    // s'affiche déjà dans son propre décor.
    document.body.classList.remove('plein', 'bascule', 'plein-cadre');
    $('v-chargement')?.classList.remove('lecture');

    // Renoncer à une attente rend l'écran d'où l'on est parti — la fiche, le
    // rayon, l'accueil. Seul quelqu'un qui n'a traversé aucune page du site est
    // arrivé par une séance : pour lui, et pour lui seul, c'est la liste des
    // séances qui a un sens (voir le retour du lecteur, même raison).
    // Une destination imposée gagne : le changement de langue rend le lecteur.
    if (destination === 'v-lecture') {
      document.body.classList.add('plein');
      vue('v-lecture');
      lecteur().reveiller?.();
      return;
    }
    revenir(ecranDeRetourLecture());
  });
  surClic('retour3', () => revenir(oeuvre ? ECRAN_OEUVRE() : 'v-accueil'));
  surClic('profil', () => ouvrirModale());
  // L'entrée de l'administration, dans les réglages. Elle n'était branchée que
  // par `chargerAcces`, c'est-à-dire une fois l'administration DÉJÀ ouverte :
  // le premier clic ne faisait rien (matrice des menus, 21/10).
  surClic('acces-en-grand', () => ouvrirEcranAdmin());
  surClic('voile', (e) => { if (e.target === $('voile')) fermerModale(); });
  surClic('fermer-modale', fermerModale);
  // Sur téléphone, la croix n'est plus affichée : le retour en haut à gauche
  // est la seule sortie, et il doit donc fermer aussi bien qu'elle.
  surClic('retour-modale', fermerModale);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if ($('cat-loupe') && !$('cat-loupe').hidden) return fermerLoupe();
    if ($('voile') && !$('voile').hidden) fermerModale();
  });
  surChangement('r-soustitres', (e) => { prefs.ecrire('soustitres', e.target.value); appliquerSousTitres(); });
  // Un seul écouteur pour les trois cartes, posé une fois pour toutes.
  // ⚠️ SANS GARDE, CE SEUL NŒUD ABSENT TUAIT TOUT LE DÉMARRAGE.
  //
  // `$('planches')` rend `null` dès que l'accueil est réécrit sans les trois
  // planches (Films / Anime / Live action), et ce chantier les remplace
  // justement par des rangées. Un `addEventListener` sur `null` lève, et comme
  // on est à l'intérieur de `DOMContentLoaded`, TOUT ce qui suit ne s'exécute
  // plus : pas de connexion, pas de séance, pas de lecteur. C'est très
  // exactement l'incident consigné au-dessus de `surClic`.
  $('planches')?.addEventListener('click', (e) => {
    const carte = e.target.closest('.planche[data-cat]');
    if (carte) ouvrirPlanche(carte.dataset.cat);
  });

  const curseurLigne = $('r-par-ligne');
  if (curseurLigne) {
    curseurLigne.value = prefs.lire('par-ligne', '9');
    curseurLigne.oninput = () => {
      prefs.ecrire('par-ligne', curseurLigne.value);
      appliquerParLigne();
    };
  }
  appliquerParLigne();

  surClic('col-retour', ouvrirAccueil);
  surClic('col-editer', basculerEdition);
  // La grille des episodes ne s'ouvre plus que depuis la fiche de l'oeuvre : le
  // retour y ramene, et il n'a plus a deviner d'ou l'on vient. Lire le
  // commentaire au-dessus de `peindreMenuEpisode` AVANT de rouvrir une autre
  // porte vers cette grille.
  surClic('retour-arcs', () => vue(ECRAN_OEUVRE()));
  // En modale, le retour la ferme ; en PAGE (« Mes réglages » depuis la liste),
  // fermer une modale absente ne changeait pas d'écran (audit 14e 21/10, P8).
  surClic('retour4', quitterLesReglages);
  surClic('photos-retour', chargerPhotos);
  surClic('g-retour', () => revenir(oeuvre ? ECRAN_OEUVRE() : 'v-accueil'));
  // Les micros du salon, les demandes, les votes et la fiche de chacun : tout
  // cela vit maintenant dans la console de seance, sur ce meme ecran.
  preparerConsoleSeance();
  // La porte d'entrée de la console, dans l'en-tête. Posée ici, elle existe
  // avant le premier état de séance : `peindreSeance` n'a plus qu'à la montrer
  // quand il y a une séance derrière.
  preparerAccesConsole();
  // La hauteur du bandeau se regle dans l'onglet Admin, mais elle s'applique a
  // tout le monde : sans cette ligne, seul celui qui ouvre les reglages verrait
  // sa propre valeur, et les autres garderaient celle de la feuille de style.
  appliquerHauteurBandeau(prefs.lire('sc-bandeau-h', '125'));
  surClic('sc-carte', () => { if (carteAProposer) envoyerLaCarte(carteAProposer); });

  // La règle du vote et la carte du salon, à portée de la séance.
  preparerTeteSeance();

  // ⚠️ LE SEUL BOUTON QUI PROMET D'OUVRIR LE LECTEUR, ET LE SEUL QUI NE
  // CHARGEAIT PAS L'ŒUVRE. Voir `cibleDeRouverture` pour le détail du défaut.
  // Le `.catch` n'est pas décoratif : sans lui, un échec de `rouvrirLeLecteur`
  // repart dans le vide — c'est ce qui faisait dire « je clique et il ne se
  // passe rien ».
  surClic('g-rouvrir', () => {
    rouvrirLeLecteur().catch((err) => {
      texte('g-etat-action', err?.message ?? 'lecteur indisponible');
      tracer('lecteur', 'rouverture en echec', err?.message ?? String(err));
    });
  });

  // L'avancee court a chaque image, dans sa propre boucle : c'est elle qu'on
  // regarde, et une seconde de retard s'y voit.
  suivreAvancee();

  // Le reste de la vue — qui est la, quelle langue, quel episode — ne change
  // qu'au rythme des messages : une fois par seconde suffit largement.
  setInterval(() => {
    if (!$('v-gestion')?.hidden) {
      peindreGestion();
      // L'etat des micros ne vient pas de la seance : il se demande au vocal.
      // L'appel se borne lui-meme (voir rafraichirVocal) — ici on ne fait que
      // lui donner l'occasion, et seulement quand la console est a l'ecran.
      rafraichirVocal();
    }
    peindreVignette();
    // Où l'on en est, retenu sur le compte. La fonction se borne elle-même :
    // elle n'écrit qu'une fois par quinze secondes, et seulement si la lecture
    // avance vraiment.
    noterOuOnEnEst();
  }, 1000);
  // Les moments où la position compte le plus : pause (et fin), saut, sortie.
  $('video')?.addEventListener('pause', () => noterOuOnEnEst({ force: true }));
  $('video')?.addEventListener('seeked', () => noterOuOnEnEst({ force: true }));
  window.addEventListener('pagehide', () => noterOuOnEnEst({ force: true, partant: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') noterOuOnEnEst({ force: true, partant: true });
  });
  surClic('ouvrir-catalogue', ouvrirCatalogue);
  surClic('cat-retour', () => {
    vue(vueAvantCatalogue);
    ouvrirModale();
  });
  surClic('cat-loupe-fermer', fermerLoupe);
  surClic('cat-loupe', (e) => { if (e.target === $('cat-loupe')) fermerLoupe(); });
  surClic('cat-loupe-utiliser', () => {
    if (catalogueChoisie) poserImageCatalogue(catalogueChoisie.url);
  });
  brancherFiltreCatalogue();
  appliquerSousTitres();
  appliquerCote();



  // La fenêtre de l'activité n'a ni la taille ni les libertés d'un
  // navigateur : la feuille de style doit pouvoir s'y adapter, donc elle doit
  // savoir où elle se trouve.
  document.body.classList.toggle('dans-discord', DANS_DISCORD);
  suivreTaille();
  // Le téléphone : la rotation et le plein écran qui la suit, puis le déblocage
  // de la première image. Les deux ne font rien ailleurs qu'au doigt — ils se
  // désarment eux-mêmes dès la première ligne.
  verifierRotation = suivreRotation() ?? null;
  debloquerAmorce();

  // Avant tout appel : l'en-tete se remplit avec ce que l'on sait deja.
  peindreProfilRetenu();

  connecter()
    .then(() => {
      // En même temps que les réglages : ce que l'accueil va lire (voir `amorcer`).
      // Hors d'une adresse de séance ou d'admin, l'écran d'arrivée est l'accueil.
      amorcer('/api/catalogue?limite=300');
      // Le solde (menu profil) et l'interrupteur de la caisse : sans lui, la porte du prix reste ouverte.
      caisseUI.chargerMoi().then(() => { try { if (!$('v-accueil')?.hidden) peindreEnCeMomentAccueil(); } catch { /* pas encore peint */ } });
      if (!DANS_DISCORD && location.pathname === '/') {
        amorcer('/api/reprendre'); amorcer('/api/bibliotheque');
        // Avec le filtre de ce navigateur ; si les réglages du compte le changent,
        // l'adresse diffère et l'accueil refait l'appel (rien de faux ne sert).
        amorcer(`/api/accueil?dispo=${queDuDispo() ? 'oui' : 'non'}`);
      }
    })
    // LES RÉGLAGES DU COMPTE JUSTE APRÈS LA CONNEXION, ET AVANT LE RESTE.
    //
    // C'est ici et pas ailleurs pour deux raisons. D'abord il faut la session
    // — la route ne répond qu'à un compte identifié. Ensuite `connecter()`
    // vient d'appliquer les goûts déduits des rôles Discord : ce que la
    // personne a réglé elle-même doit passer PAR-DESSUS, sinon les rôles
    // regagneraient à chaque ouverture. Enfin, tout ce qui suit — la séance,
    // la reprise de l'épisode — se peint avec les bons réglages du premier
    // coup au lieu de sauter sous les yeux une seconde plus tard.
    //
    // Ne rejette jamais : compte injoignable, hors ligne, stockage muet, on
    // garde les réglages de ce navigateur et le lecteur démarre comme avant.
    .then(() => reglagesDuCompte.charger())
    // La traduction du guide ICI et pas plus tôt — voir `migrerVocabulaireDesLangues`.
    .then((recus) => {
      migrerVocabulaireDesLangues(); if (recus) repeindreLesReglages();
      // Les réglages du compte ont pu changer le filtre « disponible seulement ».
      if (!DANS_DISCORD && location.pathname === '/') amorcer(`/api/accueil?dispo=${queDuDispo() ? 'oui' : 'non'}`);
    })
    // ⚠️ L'INSTANCE AVANT LA SÉANCE, et c'est un ordre, pas une préférence.
    //
    // `demarrerSeance()` était appelé ici, puis l'alias de l'adresse était
    // traduit bien plus bas. Or la fonction se garde d'un second appel
    // (`if (seance) return`) : le premier gagnait, avec `instanceDeSeance` à
    // `null`. Le lecteur rejoignait donc une séance qui n'était pas la sienne
    // au PREMIER chargement, et paraissait se réparer au rechargement — les
    // deux appels s'étaient alors croisés dans l'autre sens.
    //
    // Une adresse de séance porte un alias que seul le serveur sait traduire :
    // il n'y a rien à démarrer avant d'avoir sa réponse.
    .then(() => resoudreAlias())
    .then(() => {
      demarrerSeance();
      // Ecouter le « Rejoindre » d'une presence : quelqu'un peut arriver par
      // ce chemin a tout moment, et l'ecoute doit etre en place avant lui.
      brancherRejoindre();
      // Sans attendre : l'en-tête se complète quand le nom arrive, l'accueil ne l'attend pas.
      appliquerIdentite();
    })
    // ⚠️ LE CATALOGUE AVANT TOUT AFFICHAGE.
    //
    // Les trois écrans en vivent, mais surtout : la présence riche et le
    // bandeau de séance doivent pouvoir NOMMER l'œuvre dès le premier
    // battement, y compris quand la séance en porte une qu'on n'a pas ouverte.
    // Sans lui, `presenceVoulue` n'a pas de nom à donner et `presenceDe` se
    // tait — c'est le bon réflexe, mais autant ne pas l'y contraindre.
    //
    // Il ne rejette jamais : un catalogue injoignable rend une grille vide qui
    // le dit, pas un démarrage cassé.
    .then(() => chargerCatalogue())
    .then(() => preparerPlanches())
    .then((suite) => { peutEtreGuider(); return suite; })
    .then(() => {
      // Reprise à l'endroit quitté, quand l'adresse en garde la trace.
      //
      // Deux formes coexistent, et la seconde remplace la première :
      // · `/s/<billet>` — le lien tel qu'on le reçoit, que le serveur résout
      //   avant même que la page charge ; il n'arrive plus ici que si le
      //   serveur n'a pas su rediriger ;
      // · `/lecteur/<séance>?b=<billet>` — ce que le serveur sert désormais.
      //   Il a déjà ouvert la session à partir du billet : la page n'a plus
      //   qu'à retenir la séance, lancer l'épisode, et FAIRE DISPARAÎTRE le
      //   billet de la barre d'adresse. Un code laissé dans l'URL se lit
      //   par-dessus l'épaule et reste dans l'historique.
      //
      // Ce qui doit rester sous les yeux, c'est `/lecteur/<séance>` — rien
      // d'autre.
      const billet = /^\/s\/([\w-]{6,40})$/.exec(location.pathname)?.[1]
        || (/^\/lecteur\//.test(location.pathname) ? params.get('b') : null);

      // L'adresse porte un ALIAS, pas l'instance : huit caractères qui ne
      // disent rien du salon Discord. Le serveur seul sait ce qu'ils
      // désignent, et c'est ce qui permet à l'adresse d'être publique sans
      // révéler l'identifiant d'un salon réel.
      const alias = /^\/lecteur\/([^/?#]+)/.exec(location.pathname)?.[1];
      const dansUneSeance = !!alias;

      // ⚠️ `?admin=1` OUVRE L'ADMINISTRATION DES ACCES, ET RIEN D'AUTRE.
      //
      // Même mécanique que `?gestion=1` juste en dessous : l'adresse choisit
      // l'écran d'arrivée, puis DISPARAIT de la barre. Un paramètre laissé là
      // voyagerait dans un lien copié, et rouvrirait l'administration chez
      // quelqu'un qui n'a rien demandé — chez qui elle ne s'ouvrira pas, la
      // condition ci-dessous et les routes la refusant toutes les deux, mais
      // qui verrait quand même passer un écran vide.
      //
      // La condition est un confort d'affichage, pas une garde : le serveur
      // exige `admin` sur les six routes, et un écran ouvert de force ne
      // rendrait que des messages d'erreur.
      if (params.get('admin') === '1' && role === 'owner') {
        history.replaceState(null, '', location.pathname);
        return ouvrirEcranAdmin();
      }

      // ⚠️ `/admin` NE S'EFFACE PAS DE LA BARRE, LUI — et c'est la difference.
      //
      // `?admin=1` disparait de l'adresse : c'est un paramètre, il voyagerait
      // dans un lien copié et rouvrirait l'administration chez quelqu'un qui n'a
      // rien demandé. `/admin` est une adresse a part entiere : on la tape, on
      // la met en favori, on la partage entre administrateurs, et recharger doit
      // rendre la meme page. L'effacer reviendrait a casser le rechargement.
      //
      // Ce n'est pas une garde non plus : le serveur exige `owner` PUIS le mot
      // de passe sur les vingt-six routes du tableau de bord. Quelqu'un qui
      // ouvre `/admin` sans droit voit la porte, tape ce qu'il veut, et n'obtient
      // rien — c'est le serveur qui refuse, pas cette ligne.
      if (location.pathname === '/admin') return ouvrirEcranAdmin();

      if (billet) {
        // Le geste est là : on vient de cliquer le lien de la séance. C'est ce
        // que `rejoindreCeQuiEstDejaOuvert` attend pour reprendre la lecture —
        // et c'est le seul cas où elle le fait, dans le navigateur.
        // ⚠️ LE LIEN LANCE L'ÉPISODE LUI-MÊME : la reprise de séance ne le
        // relance pas au premier état reçu (deux départs, audit 14e 21/10 P5).
        rejointAuto = true;
        return api(`/api/billet/${billet}`)
          .then((b) => {
            instanceDeSeance = b.instance;
            // Pas de `marquerOuvertureConnue(b)` : une séance qui ne porterait
            // pas encore l'épisode doit l'apprendre. S'il y est déjà, ni
            // `pousserOuverture` (P1) ni le serveur (seance.js) ne le republient.
            demarrerSeance();
            // Le billet a fait son travail : l'adresse redevient lisible, et
            // ne garde que l'alias — celui par lequel on vient d'arriver.
            history.replaceState(null, '', location.pathname);
            // Venu de l'activité : le chemin reprend l'écran qu'on y avait, pour
            // que le retour du lecteur y ramène (et non à la liste des séances).
            const retourActivite = ecranDeRetourDepuisActivite(DEPUIS_ACTIVITE, Boolean(b.serie && !b.film));
            if (retourActivite) noterEtape(retourActivite === 'v-oeuvre' ? ECRAN_OEUVRE() : retourActivite);
            // Un film : le catalogue d'œuvres le connaît par son identifiant,
            // et l'ancien tas de collections n'est plus la source. On retombe
            // dessus quand même — un lien fabriqué avant ce chantier désigne
            // encore un film de collection, et il doit continuer d'ouvrir.
            if (b.film) {
              const o = catalogue.find((x) => x.id === b.film);
              if (o) return lancerFilm({ id: o.id, nom: o.titre, fond: o.fond, affiche: o.affiche });
              const fiche = Object.values(collections).flat().find((f) => f.id === b.film);
              return fiche ? lancerFilm(fiche) : ouvrirAccueil();
            }
            // En fond : arrivé par le lien Discord, le retour rend la liste des
            // séances, pas une fiche qu'on n'a jamais vue (choix du 21/10).
            return ouvrirOeuvre(b.serie, { enFond: true }).then(() => lancer(b.saison, b.episode));
          })
          .catch(() => {
            erreur('Ce lien a expiré. Redemande-le depuis Discord.');
          });
      }

      // `/lecteur/<séance>` sans billet : on a déjà la session (le cookie
      // posé à la première ouverture) et l'instance vient d'être lue dans le
      // chemin. On rejoint, et la séance dira elle-même quel épisode est en
      // cours — c'est elle la source, pas l'adresse.
      if (dansUneSeance) {
        // ⚠️ `?gestion=1` OUVRE LA CONSOLE, PAS LE LECTEUR.
        //
        // Toji : « côté admin, le lecteur va avoir deux choix : l’interface
        // de gestion de session, et l’interface standard. » Les deux boutons
        // de la liste des séances mènent à la même adresse, ce paramètre près :
        // la séance rejointe est la même, seul l’écran d’arrivée change.
        //
        // Il disparaît de la barre d’adresse aussitôt lu : recharger depuis la
        // console doit rendre la console, mais un lien copié puis collé
        // ailleurs n’a aucune raison d’emporter ce choix-là.
        if (params.get('gestion') === '1') {
          history.replaceState(null, '', location.pathname);
          const versConsole = () => { vue('v-gestion'); peindreGestion(); };
          versConsole();
          return OUVRIR ? ouvrirSerie(OUVRIR, { enFond: true }).then(versConsole) : undefined;
        }
        // L'instance est résolue et la séance démarrée depuis longtemps : il ne
        // reste qu'à ouvrir la série. La séance dira elle-même quel épisode est
        // en cours — c'est elle la source, pas l'adresse.
        //
        // ⚠️ ET L'ÉCRAN OÙ L'ON ÉTAIT REVIENT AVEC.
        //
        // Ce chemin rendait l'accueil, quoi qu'on regardât avant. Recharger
        // depuis la console de séance ramenait donc au catalogue, puis la reprise
        // ouvrait le lecteur par-dessus : deux écrans traversés, et jamais celui
        // qu'on venait de quitter.
        //
        // `v-lecture` fait exception, et c'est voulu : l'écran seul ne sert à
        // rien sans son fichier. On laisse la reprise de séance l'ouvrir — elle
        // sait quel épisode et à quelle seconde, là où un simple changement de
        // vue rendrait une balise vide.
        // ⚠️ LA MÉMOIRE « CET ONGLET-CI » ÉTAIT ÉCRITE ET JAMAIS LUE (13/09).
        //
        // Toji : « je suis dans l'administration des accès. Quand je reload, je
        // veux repartir dedans, pas autre part. »
        //
        // `vue()` pose DEUX mémoires depuis longtemps, et le commentaire qui les
        // accompagne dit très bien pourquoi : `localStorage` retient l'écran
        // d'une fois sur l'autre, `sessionStorage` distingue « je recharge cet
        // onglet » de « j'arrive sur le site » — il survit au rechargement, pas
        // à la fermeture. Seulement voilà : `14d.ecran-onglet` n'était RELU
        // nulle part. L'intention était écrite, le câblage manquait.
        //
        // Conséquence exacte de ce que décrit Toji : l'administration s'ouvre
        // sans changer l'adresse (c'est une page de l'application, pas une URL),
        // donc au rechargement `location.pathname` vaut `/`, `surLeDomaineNu`
        // est vrai, et l'accueil s'affiche AVANT que `retenu` soit seulement
        // consulté. Les deux cas étaient indiscernables faute de lire ce qui les
        // distingue.
        const rechargementDeCetOnglet = ecranRetenuDeLOnglet();
        // La mémoire de l'onglet passe devant : elle répond à « où étais-je il y
        // a deux secondes », quand `localStorage` répond à « où étais-je la
        // dernière fois », ce qui peut remonter à avant-hier et à un autre poste.
        // ⚠️ LE DOMAINE NU MÈNE AU CATALOGUE, TOUJOURS.
        //
        // Toji : « quand je rafraîchis la page, ça ne me mène pas sur le menu
        // principal, ça me mène dans "séance en cours" ». Recharger l'adresse
        // racine, c'est arriver — pas revenir : on n'a rien demandé d'autre que
        // le site, et la réponse à « rien demandé » est le catalogue.
        //
        // ⚠️ LE DOMAINE NU SEULEMENT — PAS « TOUJOURS L'ACCUEIL ».
        //
        // Ce qui renvoyait Toji dans la séance à chaque rechargement n'était pas
        // la mémoire d'écran : c'était la reprise automatique, qui se fiait à
        // l'adresse `/lecteur/<alias>` (voir `rejoindreCeQuiEstDejaOuvert`, où
        // c'est réglé). Forcer l'accueil ici en plus a emporté ce qui marchait —
        // « quand je recharge une page, ça revient à la même page ». Recharger
        // depuis la grille rend la grille, depuis une fiche rend la fiche.
        //
        // Reste ce qu'il demandait au départ : taper le domaine, c'est ARRIVER.
        // Là, et là seulement, la réponse est le catalogue.
        //
        // ⚠️ …ET « ARRIVER » N'EST PAS « RECHARGER ». Les deux se présentent
        // avec la MÊME adresse `/` — c'est précisément pourquoi il faut la
        // mémoire d'onglet pour les départager, et pas l'adresse. Sans le
        // `!rechargementDeCetOnglet` ci-dessous, cette ligne rendait l'accueil à
        // chaque F5 depuis n'importe quelle page ouverte sur le domaine nu :
        // l'administration, la console, la grille. C'était le bug.
        // La décision elle-même vit dans `ecranDeDemarrage` — pure, et éprouvée
        // par `essais/ecran-de-demarrage.mjs`. Ce chemin ne laisse AUCUNE requête
        // derrière lui : sans banc, il ne se diagnostique qu'en rechargeant la
        // page à la main, ce qui est exactement comme ça qu'il s'est trompé trois
        // fois sans qu'on le voie.
        const depart = ecranDeDemarrage({
          dansDiscord: DANS_DISCORD,
          ouvrir: OUVRIR,
          chemin: location.pathname,
          memoireOnglet: rechargementDeCetOnglet,
          memoireDurable: prefs.lire('ecran', ''),
          existe: (id) => Boolean($(id)),
        });
        const reprendre = () => {
          // Une trace, parce que ce chemin s'est déjà trompé deux fois et qu'on
          // ne peut pas le voir depuis le serveur : il ne laisse aucune requête
          // derrière lui.
          tracer('demarrage', `adresse ${location.pathname} · ${depart.raison}`);
          if (depart.ecran !== 'v-accueil') {
            const retenu = depart.ecran;
            vue(retenu);
            // ⚠️ RENDRE L'ÉCRAN NE SUFFIT PAS : IL FAUT LE PEINDRE.
            //
            // `vue()` ne fait que démasquer une section. Un écran dont le
            // contenu vient d'un appel — la console, la liste des séances, et
            // depuis ce chantier l'accueil, la grille et la fiche — reviendrait
            // donc VIDE après un rechargement. C'est la même règle que celle
            // écrite plus haut : « recharger depuis un écran doit rendre CET
            // écran », et un écran vide n'est pas cet écran-là.
            if (retenu === 'v-gestion') peindreGestion();
            if (retenu === 'v-sessions') {
              if (DANS_DISCORD && !rechargementDeCetOnglet) seancesDArrivee = true;
              ouvrirLesSeances();
            }
            // Même règle : `vue()` ne fait que démasquer. L'administration
            // reviendrait avec un tableau vide et trois zones muettes.
            if (retenu === 'v-admin') ouvrirEcranAdmin();
            if (retenu === 'v-catalogue') ouvrirCatalogueOeuvres();
            // La fiche a besoin de son œuvre : `OUVRIR` l'a chargée juste avant
            // quand l'adresse en portait une, sinon on n'a rien à peindre et
            // l'accueil est la seule réponse honnête.
            if (retenu === 'v-oeuvre' || retenu === 'v-serie') {
              if (oeuvre) peindreFicheOeuvre();
              else ouvrirAccueil();
            }
            return;
          }
          // `ecranDeDemarrage` a déjà tranché : arrivée sur le domaine nu, écran
          // inconnu, ou `v-lecture` laissé à la reprise de séance. Dans les trois
          // cas la réponse est la même, et sa raison est dans la trace.
          ouvrirAccueil();
        };
        if (!OUVRIR) { reprendre(); return undefined; }
        // En fond : un écran de chargement posé ici fausserait `ecranCourant`
        // (voir `retenuAilleurs`) et noterait la fiche dans le chemin du retour.
        return ouvrirSerie(OUVRIR, { enFond: true }).then(() => {
          // L'adresse porte un épisode : c'est la page ouverte dans un nouvel
          // onglet par le réglage « le lecteur dans une nouvelle page ». Elle
          // n'a pas à faire recliquer ce qu'on venait de lancer.
          if (EPISODE_VOULU && serie?.id) {
            // On efface l'épisode de la barre d'adresse : recharger cet onglet
            // ne doit pas relancer la lecture par surprise, et le lien resterait
            // sinon dans l'historique.
            history.replaceState(null, '', `${location.pathname}?id=${encodeURIComponent(OUVRIR)}`);
            return lancer(EPISODE_VOULU.saison, EPISODE_VOULU.episode);
          }
          return reprendre();
        });
      }

      // Ancienne forme, conservée le temps que les liens en circulation
      // expirent.
      // Un rechargement pendant un épisode (et l'ancienne forme `?id=&s=&e=`) :
      // la fiche d'abord — le chemin la note, le retour y ramène — puis
      // l'épisode, à la seconde retenue.
      const rouvrir = lectureARouvrir();
      if (rouvrir) {
        tracer('demarrage', `rechargement en lecture · ${rouvrir.oeuvre} S${rouvrir.saison}E${rouvrir.episode}`);
        return ouvrirSerie(rouvrir.oeuvre)
          .then(() => repriseDeLEpisode(rouvrir.oeuvre, rouvrir.saison, rouvrir.episode))
          .then((reprise) => lancer(rouvrir.saison, rouvrir.episode, { reprise }));
      }
      // Un film lancé depuis une proposition de la refonte (`?id=tt…&lire=film`).
      if (OUVRIR && params.get('lire') === 'film') {
        return ouvrirSerie(OUVRIR).then(() => lancerFilm({ id: oeuvre.id, nom: oeuvre.titre, fond: oeuvre.fond, affiche: oeuvre.affiche }));
      }
      const film = params.get('film');
      if (film) {
        const fiche = Object.values(collections).flat().find((f) => f.id === film);
        if (fiche) return lancerFilm(fiche);
      }

      // ⚠️ SUR LE SITE, LA PORTE D'ENTREE EST LE CATALOGUE. ELLE A CHANGE.
      //
      // Elle était la liste des séances, et c'était juste à l'époque : « pour le
      // client, l'interface web c'est vraiment juste lecteur / choix de session,
      // pas le reste » — le site n'avait alors pas de catalogue. Il en a un
      // depuis, de plusieurs centaines de titres, et la demande est devenue
      // l'inverse : « quand on accède au domaine de base, je veux qu'on affiche
      // le catalogue », puis « je reload dans l'accueil et ça me revient dans
      // l'onglet séance ».
      //
      // C'était donc cette ligne, et non la mémoire d'écran ni la reprise
      // automatique — toutes deux déjà corrigées pour rien. Une décision écrite
      // en dur ne se voit pas depuis les symptômes : elle fait exactement ce
      // qu'elle dit, et c'est ce qu'elle dit qui a vieilli.
      //
      // L'écran retenu passe devant : recharger depuis la liste des séances rend
      // la liste des séances. Sans rien de retenu, on arrive — et arriver, c'est
      // le catalogue.
      //
      // ⚠️ DÉFAUT PAYÉ LE 14/09 : `if (!DANS_DISCORD)` COUPAIT L'ACTIVITÉ DE SA
      // PROPRE PORTE D'ENTRÉE.
      //
      // `ouvrirLaPorte()` est le SEUL endroit qui lise l'écran d'arrivée réglé
      // (`moi.accueilPar`, servi par `/api/moi`) — et il porte DÉJÀ la branche
      // Discord : `population = DANS_DISCORD ? 'discord' : …`, repli `'seances'`.
      // Cette garde l'empêchait d'être appelée dans l'iframe, où le flot
      // retombait sur la position mémorisée — vide dans une iframe fraîche —
      // donc sur `ouvrirAccueil()`, toujours. Lancer l'activité depuis le vocal
      // rendait le catalogue au lieu de la séance, et le menu « Depuis
      // l'activité Discord » de l'écran Accès ne changeait rien, quel que soit
      // le choix : une UI fausse.
      //
      // Rien n'est perdu au passage : le rechargement de l'onglet est traité
      // plus haut dans `ouvrirLaPorte` (`14d.ecran-onglet`, qui va rechercher
      // l'identifiant dans `positionMemorisee()` pour `v-oeuvre`/`v-serie`) —
      // c'est précisément ce que faisaient les lignes supprimées ici.
      //
      // ⚠️ `?id=` EST UNE ARRIVÉE, PAS UN RECHARGEMENT — et il était ignoré.
      //
      // Sur le domaine nu, `?id=` ne menait nulle part : le flot filait vers
      // `ouvrirLaPorte()`, qui ne le lit pas. C'est l'adresse que fabrique
      // « Ouvrir dans le navigateur » (voir `preparerNavigateur`) : le bouton
      // promettait de rouvrir l'épisode en cours et rendait l'accueil. On
      // l'honore, mais SEULEMENT sur une arrivée : sans mémoire d'onglet, la
      // page vient de s'ouvrir et l'adresse est la seule chose qu'on ait
      // demandée. Avec une mémoire d'onglet, c'est un F5 — et « recharger
      // depuis la grille rend la grille » passe devant, sinon chaque
      // rafraîchissement reviendrait à la fiche qu'on avait quittée.
      if (OUVRIR && !ecranRetenuDeLOnglet()) return ouvrirOeuvre(OUVRIR);
      return ouvrirLaPorte();
    })
    .catch((err) => {
      if (err && err.silencieux) return;
      erreur(err.message || 'Démarrage impossible.');
    });
});

// Rechargement automatique après un déploiement : la décision est dans maj-auto.js.
{
  let enPauseDepuis = Date.now();
  const v = () => document.getElementById('video');
  v()?.addEventListener('pause', () => { enPauseDepuis = Date.now(); });
  window.__majAuto = surveillerVersion({
    etat: () => ({
      lecture: ecranCourant === 'v-lecture',
      enCours: !!v() && !v().paused && !v().ended,
      pauseDepuisMs: Date.now() - enPauseDepuis,
      saisie: /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '') || !!document.activeElement?.isContentEditable,
      menu: !!document.querySelector('.l-menu:not([hidden]), .st-panneau:not([hidden])'),
    }),
  });
}
